// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import { JobSelector, type JobSetupTile } from './JobSelector';
import { TooltipTitle } from './TooltipTitle';
// Paige: review leases, many reviewers on one queue (ADR-0009, docs/engineering/workbench-port.md).
import { useReviewLease } from '../paige/useReviewLease';
import { READ_ONLY_HINT, readOnlyView } from '../paige/read-only';
import { SETUP_ROLE_HINT, changesSetup } from '../paige/setup-role';
import { ReviewLeaseNotice } from '../paige/ReviewLeaseNotice';
import { CaptureOrigin } from '../paige/CaptureOrigin';
import { ExportedOnceNotice } from '../paige/ExportedOnceNotice';
import { asTheCheckSeesIt, confirmWords, nextFlagged } from '../paige/flagged-after-export';
import {
  FlaggedAfterExportChip,
  FlaggedAfterExportFinder,
  FlaggedAfterExportNotice,
  useFlaggedAfterExport,
} from '../paige/FlaggedAfterExport';
import { HeldByChip } from '../paige/HeldByChip';
import { CONFIRM_SAVE_FAILED } from '../paige/confirm-save';
import { findingWords } from '../paige/finding-words';
import { PageDropZone } from '../paige/PageDropZone';
import { holdPage } from '../paige/page-holds';
import { nextCandidates, nextStillArrives } from '../paige/next-arrival';
import { passedOverNotice } from '../paige/review-queue';
import { computerCheckMessage } from './customer-health-report';
import { nextDocumentTypeName } from './document-type-name';
import { LazyPageThumbnail } from './LazyPageThumbnail';
import { dialogTabStops } from './dialog-tab-stops';
import { setupSnapshotKey } from './setup-snapshot';
import { jobSetupTally } from './job-setup-tally';
import {
  draftAfterChange,
  draftsStillOpen,
  draftsWithout,
  holdsSetupDraft,
  jobWithSetupDraft,
  type SetupDrafts,
} from './setup-draft';
import { bindOcrBoxSelection, selectOcrBoxes, ocrBoxText } from './ocr-box-selection';
import { fieldDisplayMatches } from './field-display-matches';
import { citedWordIndexes, inkBox, type EvidenceCitation } from './field-evidence-words';
import { useFieldDisplayOrder } from './field-display-order';
import { numericFieldDisplayEvidence } from './numeric-field-evidence';
import { isFolderPreparationPaused } from '../shared/capture-preparation';
import { isReaderProblem, readerProblemWords } from './not-read';
import './job-setup-dialog.css';
import './setup-sheet.css';
import { CorrectionsPanel } from './CorrectionsPanel';
import { LearningScoreButton } from './LearningScoreButton';
import {
  ApplicationStatusBar,
  WorkspaceNotice,
  WorkspaceNoticeContext,
  trackDocumentBarHeight,
  useNoticeAfterMove,
  useNoticeForCurrentVisit,
  useWorkspaceNotice,
  type NoticeAction,
  type PublishNotice,
} from './WorkspaceNotice';
import { landingAfterJobDeletion } from './job-deletion-landing';
import { confirmsJobName, typeableJobName } from './job-deletion-confirmation';
import {
  registerWorkspaceSupportHandler,
  supportArgument,
  workspaceSupportHandler,
} from './support-window';
import { PendingSupportConfirmation } from './support-confirmation';
import { JobIdentity } from './JobIdentity';
import { WorkspaceDialog } from './WorkspaceDialog';
import { confirmAction } from './ConfirmActionDialog';
import { DeletedDocumentsDialog } from './DeletedDocuments';
import {
  AUTOMATIC_DELETE_SENTENCE,
  DELETE_UNDO_TELEMETRY,
  deletedNotice,
} from './document-deletion';
import {
  leaveSampleOut,
  leftOutSampleFileStaysIn,
  leftOutSampleShownUnder,
} from '../shared/setup-sample-leave-out';
import { hasNoTypeLeft, isNoTypeLeftRefusal, NO_TYPE_LEFT_SENTENCE } from '../shared/no-type-left';
import { RenameJobDialog } from './RenameJobDialog';
import { JobImportDialog } from './JobImportDialog';
import { ResetLearningDialog, type ResetLearningSelection } from './ResetLearningDialog';
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AccountDoorButton, OPEN_APPEARANCE_EVENT } from './AccountDoor';
import {
  ComputerDoorButton,
  CONNECTION_ADDED_EVENT,
  ConnectionAlert,
  OPEN_COMPUTER_EVENT,
  type ComputerSection,
} from './ComputerDoor';
import { ConnectionsPanel } from './ConnectionsPanel';
import { announceExportDestinationChanged } from './connection-use';
import {
  ContentCentralCatalogStep,
  ContentCentralConnectStep,
  ContentCentralCreateWindow,
  ContentCentralMatchWindow,
  ContentCentralSteps,
  ContentCentralSummary,
  FilingPath,
  mappingChoicesFromProposal,
  REVIEW_BEFORE_RESUMING,
  type ContentCentralWaiting,
} from './ContentCentralSetup';
import {
  ExportBackupPanel,
  ExportExceptionsPanel,
  ExportFilesPanel,
  ExportHeader,
  ExportPower,
  ExportRoute,
  ExportStage,
  ExportWhenPanel,
  exportStatus,
  filesSummary,
  retentionSummary,
  triggerSummary,
  type ExportStop,
} from './ExportRoute';
import {
  AutopilotChoices,
  effectiveProcessingMode,
  pilotMode,
  useAiReviewAnswer,
  useAiReviewAvailable,
} from './Autopilot';
import { useExportReadyCount, type ExportReadyCount } from './export-ready-count';
import { bindingDeliversSomeTypes, folderExportHold } from './export-next-step';
import {
  exportBlockedReason,
  exportSaveFailure,
  type ExportSaveFailure,
} from '../shared/export-telemetry';
import { reportExportSaveFailed, useExportBlockedReport } from './export-telemetry';
import { NamingWindow } from './NamingWindow';
import { OperatorErrorText, TechnicalDetails } from './TechnicalDetails';
import { CloudDestination } from './CloudDestination';
import { CLOUD_PROVIDER_KINDS } from '../shared/cloud-export';
import {
  TECHNICAL_DETAILS_MARKER,
  operatorMessage,
  splitTechnicalDetails,
} from '../shared/error-details';
import {
  LearningImprovementActionContext,
  LearningPendingContext,
  WorkbenchNavigation,
} from './WorkbenchNavigation';
import { useWorkspaceWindowTitle } from './workspace-window-title';
import './field-workbench.css';
import './setup-fields.css';
import './export-workspace.css';
import './jobs-workspace.css';
import './review-canvas.css';
import { WorkspaceStepper } from './WorkspaceStepper';
import { TooltipButton } from './TooltipButton';
import { ReaderPageBreak } from './ReaderPageBreak';
import { ReaderVirtualFlow, virtualReaderPageAt } from './ReaderVirtualFlow';
import { ReaderNeighbour, scrollToNeighbourSeam } from './ReaderNeighbour';
import { nearestReaderPage } from './reader-page-navigation';
import { observeReaderVisibility } from './reader-visibility';
import { DocumentSearchDialog } from './DocumentSearchDialog';
import { SelectionActions } from './SelectionActions';
import { selectedTextForField } from './selection-field-value';
import { isJobAdjustment } from './learning-news';
import { useImprovementBell } from '../paige/improvement-bell'; // PAIGE: the workspace menu's count (CapturePoint removed its copy)
import { OPEN_DOCUMENT_EVENT } from '../paige/open-document'; // PAIGE: the export history's Send back for review
import { SetupOverview } from './SetupOverview'; // PAIGE: Job setup's overview, Paige's since CapturePoint removed its own (31cd71e0)
import { reviewPolicySummary } from '../paige/review-policy-summary';
import { extractionNeedsDocumentType } from './extraction-feedback';
import { useReviewPaneLayout, type ReviewPane } from './review-pane-layout';
import {
  Archive,
  ArrowRight,
  Brain,
  ClipboardList,
  Barcode,
  QrCode,
  BrainCircuit,
  Check,
  CheckCircle2,
  Clock3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Copy,
  Database,
  ExternalLink,
  Eye,
  FileCheck2,
  FileDown,
  FileUp,
  FileSearch,
  FileText,
  Files,
  FolderInput,
  FolderOpen,
  FolderOutput,
  FolderSync,
  Smartphone,
  FolderTree,
  Gauge,
  Hand,
  Handshake,
  History,
  LayoutGrid,
  Link2,
  ListChecks,
  LoaderCircle,
  Mail,
  LockKeyhole,
  ArrowUpCircle,
  MoreHorizontal,
  Maximize2,
  Minimize2,
  MoveHorizontal,
  MoveVertical,
  Paperclip,
  Plus,
  RotateCw,
  RotateCwSquare,
  Square,
  ScanLine,
  Search,
  Pencil,
  Server,
  ShieldAlert,
  SearchCheck,
  TextSearch,
  ScanBarcode,
  Scissors,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Table2,
  TextCursorInput,
  Trash2,
  TriangleAlert,
  Undo2,
  WandSparkles,
  X,
  Zap,
  Sigma,
  ScrollText,
  Minus,
  Info,
  ZoomIn,
  ZoomOut,
  UserCheck,
  Asterisk,
  Timer,
  GraduationCap,
  ScanEye,
  Lock,
  Layers,
  Target,
  Calculator,
  FileType,
  BoxSelect,
  Play,
  Bot,
  Power,
} from 'lucide-react';
import {
  TourCard,
  TourOffer,
  TOUR_LEAVE_EVENT,
  TOUR_OWN_JOB_EVENT,
  presetTourExport,
  startTourScan,
  useTour,
} from './TourCard';
import { TourReadingReport, TourStart, noteTourClicked } from './TourStart';
import { TourReadingFailed } from '../paige/TourStartFailed';
import { DocumentHint, RUN_SPEED_TEST_EVENT, TAKE_TOUR_EVENT } from './DocumentHints';
import { scheduledTraining } from './speed-advice';
import {
  TOUR_CORPUS,
  TOUR_DOCUMENTS,
  TOUR_WATCHED_KEY,
  tourDocumentType,
  tourReadAgain,
  tourReaderProblem,
  markTourFlag,
  markTourRemoval,
  noteOpenDocument,
  readTour,
  readTourFlag,
  readTourRemovals,
  requestTourReturn,
  takeTourReturn,
  tourReturnDocument,
  tourOfferLabel,
  tourOriginOf,
  tourReturn,
  writeTour,
  type TourOrigin,
  type TourReturn,
} from './tour';
import {
  appLinkWindowEvent,
  chooseAppLinkJob,
  jobAppLinkPlaceNames,
  jobAppLinkPlaces,
  stillSettingUp,
} from './app-link-routing';
import { AppLinkJobChooser } from './AppLinkJobChooser';
import { isJobAppLinkRoute, type AppLinkRoute, type JobAppLinkRoute } from '../shared/app-links';
import { tourTelemetry, type TourStartFrom } from './tour-telemetry';
import { SimulatedScannerModal } from './SimulatedScanner';
import {
  PageSourceCards,
  SimulateScannerButton,
  readLastScanner,
  rememberLastScanner,
} from './PageSourceCards';
import { SampleJobsDialog, useSampleChoices } from './WelcomeSamples';
import { rememberSampleJobSet, sampleSetOfJob } from './sample-set-icons';
import { ShortcutSheet, useShortcutSheetKeys } from './ShortcutSheet';
import { LastChange, type UndoableChange, type ValueUndo } from './last-change';
import { blockUndoBehindWindow, textBoxUntouched } from './text-undo';
import { workbenchHasDocuments } from './workbench-documents';
import {
  FirstExportCard,
  FolderImportStatus,
  OPEN_SETUP_SECTION_EVENT,
  SetupNextCardView,
  CaughtUpHero,
  CaughtUpRail,
} from './FirstReviewGuide';
// PAIGE: the export line from Paige's destinations, where the desktop shows its first-export card (tour and job).
import { HostExportPanel } from '../paige/HostExportPanel';
import { FieldsHeading, reviewItemPlace, WaitingLine } from './ToCheckLine';
import {
  agreeingUncheckedRows,
  ARRIVAL_GUARD_MS,
  BACK_OFFER_MS,
  CONFIRM_BEAT_MS,
  confirmRefusal,
  confirmRefusalMessageCode,
  mainReviewAction,
  reviewShortcutTarget,
  anyWindowOpen,
} from './review-main-action';
import { ClearedNewsLine } from './ClearedNews';
import {
  engineHoldsAt,
  engineWaitReasons,
  orderByWaitReasons,
  reasonsToCheck,
  tableHoldWords,
  tableWaitSentences,
  waitingNote,
  type WaitCue,
} from './review-wait-reasons';
import { useSteadyCount } from './use-steady-count';
import {
  addClearedDocuments,
  clearedNewsReopened,
  clearedReleaseIds,
  type ClearedDocument,
  type ClearedNews,
} from './cleared-news';
import {
  isSampleJob,
  connectMilestones,
  fillTypeCards,
  forgetReadyTypes,
  rememberReadyType,
  noteMilestone,
  requestSampleScan,
  sampleJobReadyNotice,
  sampleSetForJob,
  SAMPLE_SCAN_EVENT,
  startSampleJob,
  takeSampleScan,
  uncertainFields,
  WAIT_REASON_WORDS,
  whyDocumentWaits,
  type SampleSetChoice,
  type SampleStack,
} from './onboarding';
import './onboarding.css';
import { KeptScanDialog, LiveScanFeed, scanProblemFor, type ScanProblem } from './LiveScanFeed';
import { BreakSheetsModal } from './BreakSheets';
import {
  arithmeticValidatedAfterEdit,
  lineItemArithmetic,
  lineItemBalances,
  lineItemSum,
  money,
  shiftedLineItem,
  OPERATOR_ENTERED_ROW,
  operatorEnteredRowIsComplete,
} from '../shared/line-item-arithmetic';
import { lineItemEvidenceForCell, type LineItemCell } from '../shared/line-item-evidence';
import { findingStillHolds, valueIsUngrounded } from '../shared/finding-clearing';
import { fieldCorrectionIsCorrection, valueIsHumanAuthored } from '../shared/field-correction-kind';
import { messageAfter, messageFor } from '../shared/customer-message';
import { confirmingNothing, NOTHING_TO_CONFIRM } from '../shared/empty-confirmation';
import { LookupsPanel } from './Lookups';
import { WatchedFolderSection } from './WatchedFolderSection';
import { folderName as watchedFolderName } from './watched-folder-words';
import { LookupJourney, type FieldPatch, type LookupJourneyNavigationGuard } from './LookupJourney';
import { clearBreakSheetDraft } from './break-sheet-draft';
import {
  boundaryCorrection,
  buildMerge as buildBoundaryMerge,
  buildSplit as buildBoundarySplit,
  confirmationChangesNothing,
  confirmationEntries,
  fieldValueEdit,
  keepFileWhole,
  sourceSplitSuggestion,
  supersededCorrection,
  typeCorrection,
  typeFieldsCorrection,
  unconfirmedFields,
  type BoundaryChangeOf,
} from './review-edits';
import { markEvidenceForLines, markEvidenceOf } from './mark-evidence';
import { advanceOpeningProgress, decideOpeningScreen, openingDocumentIndex } from './opening-job';
import {
  mayConfirmShown,
  MOVED_UNDER_NOTICE,
  pageAfterRefresh,
  selectionAfterRefresh,
} from './selection-after-refresh';
import { askedWork, awaitsAiReviewer, nextAsked } from './document-work-filter';
import {
  calibrationAsks,
  calibrationStarted,
  isCalibrating,
  startsInCalibration,
} from '../shared/calibration';
import { projectRefreshedBatch } from './schema-refresh-projection';
import { captureLearningLanguage } from './capture-learning-language';
import { learningClockAnchor } from './learning-clock';
import {
  invertBoundaryMutation,
  spliceBoundaryDocuments,
  type BoundaryDocumentMutation,
} from './boundary-mutation';
import {
  BOUNDARY_FOLLOW_MS,
  BOUNDARY_RECHECK_SETTLE_MS,
  BOUNDARY_REVISION_CHANGED,
  BoundaryQueue,
  locateBatchPage,
  resolveBoundaryRequest,
  type BoundaryQueueItem,
  type BoundaryTurn,
} from './boundary-queue';
import { BoundaryPendingMark } from './BoundaryPendingMark';
import {
  applyLineItemSave,
  assertLineItemConfirmationCurrent,
  documentFieldContractToken,
  waitForPendingSaveLane,
} from './save-lane';
import { WholeBatchQueue, type WholeBatchTurn } from './whole-batch-queue';
import { FieldDraftEditor, RetainedFieldDrafts } from './FieldDraftEditor';
import { FieldReviewRow } from './FieldReviewRow';
import { FieldSourceCrop } from './FieldSourceCrop';
import { DocumentTypePreview } from './DocumentTypePreview';
import { isWithheldField, SET_ASIDE_REASON } from './withheld-field-candidate';
import { tieChoices } from './lookup-tie-choices';
import './source-review.css';
import { closePermissionFrozen, noteCloseBarrier, registerCloseBlocker } from './close-permission';
import { primaryReviewChannel } from './review-channel';
import {
  createFieldDraftCoordinator,
  submitLeavingFieldDrafts,
  FieldDraftStore,
  type FieldDraft,
  type FieldDraftBinding,
  type FieldDraftCommand,
  type FieldDraftOutcome,
} from './field-drafts';
import {
  askPageTurn,
  pageTurnBusy,
  pageTurnImage,
  pageTurnKey,
  pageTurnPendingFor,
  pageTurnSettled,
  pageTurnShown,
  pageTurnTransform,
  settlePageTurn,
  type PageTurnEntry,
} from './page-turn-queue';
import { hydrationResultIsCurrent } from './hydration-result';
import {
  ENGINE_OUTAGE_PATIENCE_MS,
  ENGINE_RESTART_COOLDOWN_MS,
  clearEngineOutage,
  engineOutageNow,
  engineQueueHeldUntil,
  holdEngineQueueUntil,
  judgeCaptureFailure,
  recordEngineOutage,
} from './engine-outage';
import type {
  DocumentTypeName,
  DocumentTypeRemovalImpact,
  FieldLookupAssignment,
  JobsChangedEvent,
  LegacyImportStatus,
  LookupRuleDefinition,
  OcrBox,
  ProcessingMode,
  RemovedDocumentType,
  StorageLocation,
  TableRegion,
} from '../shared/desktop-api';
import { filedDocumentType } from './document-type-identity';
import { spoken } from './StorageLocation';
import { fieldLookup, usesListSpelling } from '../shared/lookup-rules';
import { projectLearningSkills, typeLearningState } from '../shared/learning-skills';
import {
  CORRECTION,
  FINDING,
  MISPLACED_VALUE_SOURCE,
  processorOnlyByDesign,
  SEPARATION,
  SUGGESTION,
  SUGGESTION_ACTION,
  TYPE_FROM_BOUNDARY_REEVALUATION,
  TYPE_FROM_CONFIRMED_EXAMPLES,
  TYPE_FROM_OPERATOR_SPLIT,
  TYPE_FROM_REMOVED_TYPE,
  TYPE_FROM_REMOVED_TYPE_ANSWERS_KEPT,
  TYPE_FROM_REMOVED_TYPE_REREAD,
  TYPE_TRUST_AUTHORIZED_LEARNED,
  UNCLASSIFIED_DOCUMENT,
} from '../shared/desktop-api';
import {
  NEW_JOB_BACKUP_DAYS,
  exportHistoryBackupUntil,
  exportHistoryCleanupNote,
  exportHistoryDateRange,
  exportHistoryDayChoices,
  exportHistoryStateLabel,
  exportRunAttentionDescription,
  exportRunExceptions,
  exportRunFailures,
  exportRunNotice,
  exportRunNoticeCode,
  exportSettingsNeedSave,
  exportEnabledAfterSave,
  exportHasDestination,
  exportSwitchRequest,
  latestExportAt,
} from '../shared/export-presentation';
import { LineItemLedger, RetainedLineDrafts } from './line-item-ledger';
import { resolveRowSource } from './row-source-geometry';
import { LineItemArithmeticStatus } from './LineItemArithmeticStatus';
import {
  lineDraftEvent,
  lineDraftMatchesRow,
  lineDraftRebaseBinding,
  lineDraftSavedOutcome,
  lineDraftTableIdentity,
  lineDraftTarget,
  readLineDraftValue,
} from './line-item-drafts';
import { LearningMode, type SuggestionApplyRequest } from './LearningMode';
import {
  stanceOfferAllowed,
  stanceOfferLabel,
  stanceReceiptCopy,
  stanceStopped,
  withStanceChange,
  STANCE_AREA,
  STANCE_CHAIN_LABEL,
  type LearningStance,
  type StanceArea,
} from './learning-stance';
import { typeRemovalReviewLine, typeRemovalToast } from './type-removal-copy';
import { addedSuggestedTypeId, newTypeFieldsReceipt } from './suggested-type-fields';
import {
  readLearningSchedule,
  scheduledJobIds,
  type LearningSchedule,
  type ScheduledLearningRun,
} from './learning-schedule';
import type {
  AppearanceMode,
  BarcodeResult,
  BatchDocumentSummary,
  BatchWorkspaceOverview,
  BoundCatalogJobSummary,
  BoundaryOpeningProfile,
  BoundaryContinuationProfile,
  CaptureProcessingProgress,
  CatalogBackedJobResult,
  ClusterView,
  ContentCentralBindingProposal,
  ContentCentralCatalogDefinition,
  ContentCentralCatalogSummary,
  ContentCentralFilingPreviewResult,
  ContentCentralMappingChoice,
  ContentCentralProvisioningPlan,
  DestinationConnectionSummary,
  DocumentFilingRule,
  DocumentResult,
  DocumentTypeSuggestion,
  DeletedDocument,
  EngineHealth,
  ExportDestinationSelection,
  ExportFolderDefaults,
  ExportDocumentListRequest,
  ExportDocumentPage,
  ExportDocumentSummary,
  ExportFiles,
  ExportJobSettings,
  ExportRunResult,
  FieldResult,
  FilingExpression,
  FilingPlan,
  FilingPlanPreview,
  FilingToken,
  FolderAnalysisProgress,
  FolderAnalysisStatus,
  InterfaceZoomEvent,
  InterfaceZoomState,
  JobDefinition as StoredJobDefinition,
  JobDestinationBinding,
  JobDocumentTypeDefinition,
  JobFieldDefinition,
  JobSuggestion,
  LearningAccelerationReport,
  LearningEligibility,
  LearningFieldMeasurement,
  LearningTableMeasurement,
  LearningProgress,
  LiveCaptureProgress,
  OperatorCorrection,
  LearningScorecard,
  LineItemRow,
  PageSearchResult,
  PendingContentCentralProposal,
  PendingContentCentralProposalDecision,
  ProcessResult,
  ReviewImpactProfile,
  ReviewPanelKind,
  ReviewReleasePreview,
  ScannerDiscovery,
  ScannerSource,
  SimulatedScannerTray,
  StagedCapture,
  UpdateStatus,
  WorkstationHealthReport,
} from '../shared/desktop-api';
import { channelTitle, updateActivityLine } from '../shared/update-channels';
import { StageAnimation } from './StageAnimation';
import { type FieldKind, REVIEW_POLICY_DEFAULTS, valuePatternFromExample } from './field-shape';
import {
  activatedSetupJob,
  applyBootstrapProposal,
  isRemovedDocumentType,
  newFolderSetupJob,
  sameSerializableValue,
  type SetupDocumentType,
  type SetupField,
} from './setup-proposal';
import {
  LearningEvidencePreview,
  learningPageEvidence,
  learningPreviewIdentity,
  useLearningPreview,
} from './learning-evidence';
import { ProcessingWorkspace } from './ProcessingWorkspace';
import { useWorkingProgress } from './useWorkingProgress';
import {
  forgetSetupProgress,
  forgetSetupRun,
  readLearnFloor,
  useSetupProgressFloor,
  writeLearnFloor,
} from './setup-progress-floor';
import {
  heldSetupHandoff,
  landSetupHandoff,
  noteSetupFiles,
  noteSetupHandoffRead,
  noteSetupPage,
  noteSetupResults,
  processingTitle,
  rememberSetupHandoff,
  setupFiles,
  setupPage,
  setupTypesLine,
  useHeldSetupHandoff,
  useSetupHandoff,
  type SetupHandoff,
  type SetupImportReport,
} from './setup-handoff';
import { useSetupProcessing } from '../paige/setup-processing';
import {
  appFolderImports,
  captureDisplayName,
  discardFolderImportWave,
  folderImportLines,
  folderImportProgress,
  folderImportReadout,
  registerFolderImports,
  rememberCaptureImport,
  stampFolderImport,
  type FolderImportProgress,
  type FolderImportRegistry,
} from './folder-import-progress';
import { ReadingFinds } from './ReadingFinds';
import {
  describe as describeWorking,
  foldWaveStage,
  forgetWave,
  importStepShown,
  questionLine,
  spotPage,
  stepClock,
  wavesUnderstanding,
  workingPreviewIdentity,
  workingReadout,
  workingStageRank,
} from './working-progress';
import { readyTags, withSetupDesign } from './setup-design-progress';
import { useSetupDesign } from './use-setup-design';
import { SetupJourney, SetupTips, type JourneyFind } from './SetupJourney';
import {
  learningModel,
  learningPart,
  learningTiles,
  readingModel,
  setupReadWeight,
  timeLeft,
  type JourneyTile,
  type JourneyType,
  type LeftMemory,
} from './setup-journey-model';
import { typeShades } from './type-tints';
import {
  canceledByStop,
  setupFailureCategory,
  setupFailureMessage,
  setupFailureWords,
  setupResumeNotStartedWords,
  stoppedByPerson,
} from './setup-failure';
import {
  finishSetupStages,
  reportNoScanner,
  reportSetupFailure,
  reportSetupHandoff,
  reportSetupReady,
  reportSetupStopped,
  useNoProgressTelemetry,
  useSetupStageTelemetry,
} from './setup-signals';
import { setupHandoffHow, setupStageOf } from './setup-telemetry';
import { scanFaultFor } from './scan-problem';
import { SetupTypeName } from './SetupTypeAtlas';
import {
  FieldOnPage,
  FitText,
  JobThresholdChart,
  ReviewThresholdChart,
  evidenceBox,
  type PageBox,
} from './setup-field-visuals';
import { SetupTypeDeck, kindIcon as kindIconFor } from './SetupTypeDeck';
import { DocumentStartsArt } from './DocumentStartsArt';
import './setup-type-route.css';
import { confidenceBasisText } from './field-confidence-basis';
import {
  FIELD_REVIEW_IMPACT_SCOPE,
  fieldReviewImpactText,
  fieldReviewPolicyScope,
  selectFieldReviewImpact,
  type FieldReviewImpact,
} from './field-review-impact';
import './processing-workspace-integration.css';
import newJobLearningUrl from './assets/new-job-learning.png';
import {
  fieldDraftStillApplies,
  OwnTypingRevisions,
  reviewDocumentMutationToken,
  type ReviewWorkspaceMessage,
  type ReviewWorkspaceSnapshot,
} from './review-workspace';
import { SettingsPage, type SettingsSection } from './SpeedCheck';
import { isTrialWall, noteLicenseFrom, TrialTray, TrialWallButton } from './EngagementPanels';
import { PagesTray } from './PageAllowance';
import { CortexaClusterPage } from './CortexaCluster';
import { SHARED_PROCESSING_ENABLED } from '../shared/shared-processing';
import { useAfterPointerRelease } from './after-pointer-release';
import {
  HeldSetupProgress,
  JobProcessingButton,
  JobProcessingMonitor,
  useJobProcessingState,
} from './JobProcessingControl';
import { pointerZoomGesture } from './pointer-zoom';
import { SpeedBoostButton } from './SpeedBoostStatus';
import {
  RuntimeComponentDownload,
  runtimeDownloadVisible,
  useRuntimeComponentStatus,
} from './RuntimeComponentDownload';
import { hostHas } from '../shared/host-capabilities';
import { documentStepModifier, stepsDocument } from '../shared/document-step-keys';
import { hostWords } from '../shared/host-words';
import { PaigeExportSetup } from '../paige/ExportSetup';
import { PaigePhoneScanSetup, PHONE_SCAN_TITLE } from '../paige/PhoneScanSetup';
import { useJobDoorValues } from './job-door-values';
import {
  focusActiveWorkspaceSection,
  WorkspaceSectionNav,
  type WorkspaceSectionItem,
} from './WorkspaceSections';
import { RevisionedJobSaveCoordinator } from './job-save-coordinator';
import { reportAreaPart, useTelemetryArea, type TelemetryAreaParts } from './telemetry-area';
import { reviewInFront, reviewTally } from './review-telemetry';
import { telemetryCountBucket, type TelemetryUiErrorCode } from '../shared/telemetry-catalog';
import { reportEvent } from './telemetry';
import {
  AppLifetimeCaptureDrain,
  mayStartCaptureWave,
  nextDrainableCapture,
  CaptureDrainGateOwner,
  captureDrainBlocked,
  captureDrainGeneration,
  subscribeCaptureDrainGate,
} from './capture-background-drain';

const CONTENT_CENTRAL_CATALOG_RENDER_LIMIT = 600;
const CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE = 18;
const CONTENT_CENTRAL_SCHEMA_FIELD_PAGE_SIZE = 24;
const sameContentCentralId = (left: string | null | undefined, right: string | null | undefined) =>
  left && right ? left.toLowerCase() === right.toLowerCase() : left === right;
const mergeContentCentralCatalogs = (
  current: ContentCentralCatalogSummary[],
  incoming: ContentCentralCatalogSummary[],
) => {
  const seen = new Set(current.map((catalog) => catalog.id.toLocaleLowerCase()));
  const merged = [...current];
  for (const catalog of incoming) {
    const id = catalog.id.toLocaleLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(catalog);
    if (merged.length === CONTENT_CENTRAL_CATALOG_RENDER_LIMIT) break;
  }
  return merged;
};

/**
 * `settings` owns everything configured for this installation: appearance,
 * updates, hardware evidence, performance, storage, and external connections.
 * `cluster` owns the live group of computers that can share work. Keeping those
 * two names in the route state as well as on screen prevents yesterday's
 * retired "Computer / Preferences / Others" vocabulary from leaking back into compact
 * navigation, accessibility labels, or programmatic navigation.
 */
type Screen = 'jobs' | 'job' | 'settings' | 'cluster' | 'learning';
type JobMode = 'capture' | 'setup' | 'export';
const rendererWorkspaceSessionKey = 'capturepoint:renderer-workspace:v1';
const readRendererWorkspaceSession = (): {
  screen: Screen;
  jobMode: JobMode;
  selectedId: string;
} | null => {
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(rendererWorkspaceSessionKey) ?? 'null',
    ) as Record<string, unknown> | null;
    if (
      !value ||
      ![
        'jobs',
        'job',
        'settings',
        'learning',
        ...(SHARED_PROCESSING_ENABLED ? ['cluster'] : []),
      ].includes(String(value.screen)) ||
      !['capture', 'setup', 'export'].includes(String(value.jobMode)) ||
      typeof value.selectedId !== 'string'
    )
      return null;
    return {
      screen: value.screen as Screen,
      jobMode: value.jobMode as JobMode,
      selectedId: value.selectedId,
    };
  } catch {
    return null;
  }
};
/**
 * PAIGE (docs/engineering/workbench-port.md): the jobs home's card menu opens a job on its setup
 * (`?setup=1` on the review route); the host writes this one-shot key, the App opens on Job setup
 * when it mounts and clears it. The desktop always mounts on the documents; so does Paige otherwise.
 */
const setupRequestKey = 'capturepoint:setup-request:v1';
const setupRequested = (): boolean => {
  try {
    return window.sessionStorage.getItem(setupRequestKey) === '1';
  } catch {
    return false;
  }
};
const clearSetupRequest = (): void => {
  try {
    window.sessionStorage.removeItem(setupRequestKey);
  } catch {
    // Nothing to clear without a session store.
  }
};
type JobKind = 'invoice' | 'general' | 'hr' | 'statements';
type FolderActivityEntry = {
  id: string;
  stage: FolderAnalysisProgress['stage'] | 'starting';
  kind: string;
  status: string;
  title: string;
  detail: string;
  at: number;
};
type FolderWorkTask = {
  id: string;
  kind: string;
  status: string;
  label: string;
  detail: string;
  pageNumber: number;
  updatedAt: number;
  previewPath?: string | null;
  tags?: string[];
  document?: DocumentResult | null;
  evidence?: NonNullable<FolderAnalysisProgress['activity']>['evidence'];
};

type Field = SetupField;

type DocumentTypeDefinition = SetupDocumentType;

type Job = {
  id: string;
  name: string;
  description: string;
  kind?: JobKind;
  accent: string;
  bootstrapMode: 'scan' | 'folder' | 'sample-scan' | 'manual' | 'import' | 'content-central';
  status: 'provisional' | 'active' | 'archived';
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  documentTypes: DocumentTypeDefinition[];
  separationMode:
    | 'automatic'
    | typeof SEPARATION.automaticBarcode
    | typeof SEPARATION.existingBarcode
    | typeof SEPARATION.capturePointSeparator
    | typeof SEPARATION.filePerDocument;
  separationBarcodeText?: string | null;
  boundaryOpeningProfiles?: BoundaryOpeningProfile[];
  boundaryContinuationProfiles?: BoundaryContinuationProfile[];
  suppressedSuggestions?: string[];
  suggestionsPausedUntil?: string | null;
  reviewPolicy?: {
    documentType: number;
    fields: number;
    boundary: number;
    holdForReview?: boolean;
    documentTypeEnabled?: boolean;
    fieldsEnabled?: boolean;
    boundaryEnabled?: boolean;
    lineItemArithmeticEnabled?: boolean;
    processingMode?: ProcessingMode;
  } | null;
  learningStance?: LearningStance | null;
  /** Types the operator removed; engine-owned, so saving never changes it. */
  removedDocumentTypes?: RemovedDocumentType[] | null;
};

type LineItemsMode = 'hidden' | 'docked';
type JobReviewLayout = {
  resultsWidth: number;
  spineWidth: number;
  lineItemsMode: LineItemsMode;
  lineItemsHeight: number;
  detachedPanels: ReviewPanelKind[];
};
/**
 * How many times an outage-shaped failure re-queues itself before it is left for
 * a person. Two, because the failures this heals are a machine going away — the
 * engine dying, the application closing mid-recognition — and those clear on the
 * next start or the one after. A third identical result is not weather.
 */
const AUTOMATIC_RETRY_LIMIT = 2;
/**
 * How many recognition starts are worth telling the operator about, on the
 * stalled-intake screen.
 *
 * Its own number, deliberately, even though it currently equals the heal limit
 * above. They were one constant and that was a collision: the heal limit is a
 * DECISION the code makes, the display threshold is a SENTENCE a person reads,
 * and the drain now writes the main process's live attempt count back into the
 * queue on every automatic resume — so a count that used to move only across
 * restarts moves within a session, and one number could no longer serve both.
 */
const ATTEMPTS_WORTH_SAYING = 2;
const LINE_ITEMS_MIN_HEIGHT = 132;
const LINE_ITEMS_MAX_HEIGHT = 760;
type ViewerSelectionMenu = {
  documentId: string;
  documentMutationToken: string;
  x: number;
  y: number;
  text: string;
  copyText?: string;
  automatic?: boolean;
  lineIndexes: number[];
  pageNumber: number;
  barcode?: BarcodeResult;
};
type SuggestionApplicationScope = 'refresh-safe' | 'future-only';
type SuggestionApplicationOutcome = 'refreshed' | 'kept' | 'future-only' | 'queued';
type JobCreationIntent = {
  job: Job;
  folderToken?: string;
  folderFileCount?: number;
  folderAnalysisCount?: number;
  openSetup?: boolean;
  catalogResult?: CatalogBackedJobResult;
};
type JobBootstrapChoice = 'scan' | 'folder' | 'manual' | 'content-central';
/** New job's two steps: "How do you want to start?", then naming the job. */
type NewJobStep = 'choice' | 'name';

/**
 * One vocabulary for every place a job can begin. The first-run page and the
 * returning-operator dialog used to invent different names for the same four
 * paths, which made the latter feel like connector controls bolted onto an
 * otherwise simple action.
 */
const jobBootstrapCopy: Record<
  JobBootstrapChoice,
  { label: string; detail: string; promise: string; action: string }
> = {
  scan: {
    label: 'Start scanning',
    detail: 'No setup required',
    promise:
      'Add pages from a scanner or files. Paige builds the document types and fields from what arrives.',
    action: 'Create job',
  },
  folder: {
    label: 'Learn from a folder',
    detail: 'Propose a setup from documents you already have',
    promise:
      'Choose a representative folder. Cortexa reads a sample and proposes the setup before you start working.',
    action: 'Choose folder',
  },
  'content-central': {
    label: 'Use a Content Central catalog',
    detail: 'Start with its document types and fields',
    promise:
      'Choose an existing catalog. Its document types and fields become the job’s starting setup.',
    action: 'Create job from catalog',
  },
  manual: {
    label: 'Set up manually',
    detail: 'Define known document types and fields yourself',
    promise: 'Name the job, then continue to Job setup to define its document types and fields.',
    action: 'Continue to job setup',
  },
};

const uid = () => Math.random().toString(36).slice(2, 10);
/** A setup sample's "Leave out of this job" choice, beside its types (setup-sample-leave-out.ts). */
const LEAVE_SAMPLE_OUT = '__leave_out__';
/** "an Invoice", "a Credit Memo" — so a sentence about a type reads like a sentence. */
const withArticle = (name: string) =>
  `${/^[aeiou]/i.test(name.trim()) ? 'an' : 'a'} ${name.trim()}`;
const counted = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;
/** A document cleared by learning, as the news names it: by its type. */
const clearedDocument = (
  id: string,
  item: { documentType?: string | null } | undefined,
  releaseId: string | null = null,
): ClearedDocument => ({ id, kind: item?.documentType?.trim() || 'document', releaseId });

const friendlyExportBlocker = (blocker: string) => {
  const documentName = blocker.match(/^([^:]+):/)?.[1]?.trim();
  const checks: string[] = [];
  if (/document-type route/i.test(blocker)) checks.push('the document type');
  if (/boundary route/i.test(blocker)) checks.push('the page boundaries');
  if (/field route/i.test(blocker)) checks.push('the captured values');
  if (/line items|line-items route/i.test(blocker)) checks.push('the line items');
  if (checks.length === 0) return blocker;
  const readableChecks =
    checks.length === 1
      ? checks[0]
      : `${checks.slice(0, -1).join(', ')}, and ${checks[checks.length - 1]}`;
  return `${documentName || 'This document'} needs review before export. Paige is not yet reliable enough to approve ${readableChecks} automatically.`;
};

function ExportResultDialog({ result, onClose }: { result: ExportRunResult; onClose: () => void }) {
  // A document that reached the destination and only missed its backup copy was delivered; one sent to the
  // exceptions folder was not delivered there, and is said apart.
  const { undelivered, backupPending } = exportRunFailures(result);
  const exceptions = exportRunExceptions(result);
  const delivered = result.exported - exceptions + backupPending;
  const toExceptions =
    exceptions > 0 ? `${counted(exceptions, 'document')} sent to the exceptions folder` : '';
  return (
    <WorkspaceDialog telemetry="export-results" title="Export results" onClose={onClose}>
      <div className="export-readiness-guide">
        <p className="export-readiness-intro">
          {delivered ? `${counted(delivered, 'document')} exported` : 'No documents were exported'}
          {toExceptions && ` · ${toExceptions}`}
        </p>
        <div className="export-readiness-grid">
          <section>
            <CheckCircle2 size={26} />
            <div>
              <h3>{delivered} exported</h3>
              <p>
                Successfully delivered to your destination.
                {backupPending > 0
                  ? ` The backup copy of ${counted(backupPending, 'document')} is waiting in Export → Activity.`
                  : ''}
                {toExceptions && ` ${toExceptions}.`}
              </p>
            </div>
          </section>
          <section>
            <Info size={26} />
            <div>
              <h3>
                {result.held} held · {undelivered} failed
              </h3>
              <p>
                Held documents stay in your workspace. Failed deliveries can be checked in Export →
                Activity.
              </p>
            </div>
          </section>
        </div>
        {result.blockers.length > 0 && (
          <>
            <h3>What needs attention</h3>
            {result.blockers.slice(0, 8).map((blocker, index) => (
              <p key={index} style={{ marginTop: 12 }}>
                {friendlyExportBlocker(blocker)}
              </p>
            ))}
            {result.blockers.length > 8 && (
              <p style={{ marginTop: 12 }}>
                Showing 8 of {result.blockers.length} document explanations.
              </p>
            )}
          </>
        )}
      </div>
    </WorkspaceDialog>
  );
}

function ExportRunAttentionNotice({ result }: { result: ExportRunResult }) {
  // A delivered document whose backup copy is pending is not one: Activity's row says so.
  const total = result.held + exportRunFailures(result).undelivered;
  if (total === 0) return null;
  const heading = (
    <>
      <TriangleAlert size={15} />
      <span>
        <strong>
          {counted(total, 'document')} need{total === 1 ? 's' : ''} attention
        </strong>
        <small>{exportRunAttentionDescription(result)}</small>
      </span>
    </>
  );
  // Readiness blockers belong only to held documents. A failed delivery has
  // already frozen and left the active workspace, so an empty disclosure both
  // lies about its location and opens onto nothing.
  if (result.held === 0 || result.blockers.length === 0)
    return (
      <div className="export-run-notice static" role="status">
        <div className="export-run-notice-summary">{heading}</div>
      </div>
    );
  return (
    <details className="export-run-notice">
      <summary>
        {heading}
        <ChevronDown size={14} />
      </summary>
      <div>
        {result.blockers.slice(0, 6).map((blocker) => (
          <p key={blocker}>{friendlyExportBlocker(blocker)}</p>
        ))}
        {result.blockers.length > 6 && (
          <small>
            {counted(result.blockers.length - 6, 'additional document')} also need review.
          </small>
        )}
      </div>
    </details>
  );
}
const friendlyBarcodeSymbology = (value: string) =>
  ({
    Code39: 'Code 39',
    Code93: 'Code 93',
    Code128: 'Code 128',
    QrCode: 'QR code',
    DataMatrix: 'Data Matrix',
    Pdf417: 'PDF417',
    Ean13: 'EAN-13',
    Ean8: 'EAN-8',
    UpcA: 'UPC-A',
    UpcE: 'UPC-E',
  })[value.replaceAll(' ', '')] ?? value.replace(/([a-z])([A-Z])/g, '$1 $2');
const shortDuration = (milliseconds: number) => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};
const reviewLayoutKey = (jobId: string) => `cpv6-review-layout:${jobId}`;
const representativeAutoUseKey = (jobId: string) => `cpv6-representative-auto-use:${jobId}`;
const representativeAutoFailureKey = (jobId: string) => `cpv6-representative-auto-failure:${jobId}`;
type RepresentativeAutoFailure = { proposalKey: string; message: string };
const readRepresentativeAutoFailure = (jobId: string): RepresentativeAutoFailure | null => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(representativeAutoFailureKey(jobId)) ?? 'null',
    ) as Partial<RepresentativeAutoFailure> | null;
    return parsed && typeof parsed.proposalKey === 'string' && typeof parsed.message === 'string'
      ? { proposalKey: parsed.proposalKey, message: parsed.message }
      : null;
  } catch {
    return null;
  }
};
const writeRepresentativeAutoFailure = (
  jobId: string,
  failure: RepresentativeAutoFailure | null,
) => {
  try {
    if (failure) localStorage.setItem(representativeAutoFailureKey(jobId), JSON.stringify(failure));
    else localStorage.removeItem(representativeAutoFailureKey(jobId));
  } catch {
    /* The visible retry remains available in this session. */
  }
};
const clearRepresentativeAutoPreference = (storage: Storage, jobId: string) => {
  try {
    storage.removeItem(representativeAutoUseKey(jobId));
    storage.removeItem(representativeAutoFailureKey(jobId));
  } catch {
    /* Cleanup is best-effort; deleting/activating the job still wins. */
  }
};
/**
 * Whether two extracted values are the same field of the same document type.
 *
 * The stable id when there is one, the name when there is not — a document read
 * before its type was configured carries names only. Accent sensitivity so a
 * renamed-but-identical field is not treated as two.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
const readReviewLayout = (jobId: string): JobReviewLayout => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(reviewLayoutKey(jobId)) ?? '{}',
    ) as Partial<JobReviewLayout>;
    const width = Number(parsed.resultsWidth);
    const spineWidth = Number(parsed.spineWidth);
    const height = Number(parsed.lineItemsHeight);
    // Layouts written before the pane was resizable carry one of three presets.
    // "expanded" was the tall one, so honour it as a height rather than losing it.
    const legacyExpanded = String(parsed.lineItemsMode ?? '') === 'expanded';
    return {
      resultsWidth: Number.isFinite(width) && width >= 270 && width <= 600 ? width : 340,
      spineWidth:
        Number.isFinite(spineWidth) && spineWidth >= 132 && spineWidth <= 360 ? spineWidth : 360,
      lineItemsMode: parsed.lineItemsMode === 'hidden' ? 'hidden' : 'docked',
      lineItemsHeight:
        Number.isFinite(height) &&
        height >= LINE_ITEMS_MIN_HEIGHT &&
        height <= LINE_ITEMS_MAX_HEIGHT
          ? height
          : legacyExpanded
            ? 420
            : 215,
      detachedPanels: Array.isArray(parsed.detachedPanels)
        ? parsed.detachedPanels.filter(
            (panel): panel is ReviewPanelKind =>
              panel === 'viewer' || panel === 'fields' || panel === 'line-items',
          )
        : [],
    };
  } catch {
    return {
      resultsWidth: 340,
      spineWidth: 360,
      lineItemsMode: 'docked',
      lineItemsHeight: 215,
      detachedPanels: [],
    };
  }
};
const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
/**
 * The field of a type that already carries a name, in any case or spacing, or undefined when the name is
 * free. One name per field in a type: what a field learns is filed under its name, and the readers refuse a
 * type with two (B-19: a second Vendor on Invoice left every invoice Not read). The engine's save-job refuses
 * the same thing with the same rule (trim, ordinal ignore-case: toLowerCase, not the host locale's casing).
 */
const fieldNamed = (fields: readonly Field[], name: string): Field | undefined => {
  const wanted = name.trim().toLowerCase();
  return wanted ? fields.find((item) => item.name.trim().toLowerCase() === wanted) : undefined;
};

const field = (name: string, kind: FieldKind, description: string, barcode = false): Field => ({
  id: uid(),
  name,
  kind,
  required: false,
  readText: true,
  readBarcode: barcode,
  description,
  multiple: false,
  position: 0,
});

const defaultTypeName: Record<JobKind, string> = {
  invoice: 'Invoice',
  statements: 'Statement',
  hr: 'Employee document',
  general: 'General document',
};

function migrateJob(
  value: Job & { fields?: Field[]; documentTypes?: DocumentTypeDefinition[] },
): Job {
  const now = new Date().toISOString();
  const normalizeFields = (items: Field[]) =>
    items
      .filter((item) => item.kind !== 'document-type')
      .map((item, position) => ({
        ...item,
        semanticId: item.semanticId ?? null,
        multiple: item.multiple ?? false,
        position,
      }));
  const documentTypes = Array.isArray(value.documentTypes)
    ? value.documentTypes.map((type, position) => ({
        ...type,
        semanticId: type.semanticId ?? null,
        description: type.description ?? null,
        status: type.status ?? 'active',
        schemaVersion: type.schemaVersion ?? 1,
        position,
        fields: normalizeFields(type.fields ?? []),
      }))
    : [
        {
          id: uid(),
          name: defaultTypeName[value.kind ?? 'general'] ?? 'Document',
          semanticId: value.kind ?? 'document',
          description: null,
          status: 'active' as const,
          schemaVersion: 1,
          position: 0,
          fields: normalizeFields(value.fields ?? []),
        },
      ];
  return {
    ...value,
    description: value.description ?? '',
    accent: value.accent ?? '#167a8b',
    bootstrapMode: value.bootstrapMode ?? 'manual',
    status: value.status ?? 'active',
    schemaVersion: value.schemaVersion ?? 0,
    createdAt: value.createdAt ?? now,
    updatedAt: value.updatedAt ?? now,
    documentTypes,
    separationMode: value.separationMode ?? 'automatic',
    separationBarcodeText: value.separationBarcodeText ?? null,
    boundaryOpeningProfiles: value.boundaryOpeningProfiles ?? [],
    boundaryContinuationProfiles: value.boundaryContinuationProfiles ?? [],
    suppressedSuggestions: value.suppressedSuggestions ?? [],
    suggestionsPausedUntil: value.suggestionsPausedUntil ?? null,
    reviewPolicy: value.reviewPolicy ?? null,
    learningStance: value.learningStance ?? null,
    removedDocumentTypes: value.removedDocumentTypes ?? null,
  };
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
const schemaRequest = (job: Job) =>
  job.documentTypes.map((type) => ({
    id: type.id,
    semanticId: type.semanticId ?? null,
    schemaVersion: type.schemaVersion,
    name: type.name,
    description: type.description ?? undefined,
    /* readingInstruction was missing here, and this is the schema every SCAN is
     read with — so guidance the learner had promoted into the job reached every
     re-extraction path and not the one that processes new batches. The learner
     looked like it worked in the trial and did nothing at the scanner. The table reading plan
     that table learning adopts was missing the same way. */
    fields: type.fields.map(
      ({
        id,
        semanticId,
        name,
        kind,
        required,
        readText,
        readBarcode,
        description,
        multiple,
        barcodeSymbology,
        barcodeValuePrefix,
        valuePattern,
        formatExample,
        defaultValue,
        readingInstruction,
        tableReadingPlan,
        learnedFromCount,
        shiftLineItemColumns,
        repairUnbalancedQuantity,
        reviewBelow,
        reviewBelowSource,
      }) => ({
        id,
        semanticId: semanticId ?? null,
        name,
        kind,
        required,
        readText,
        readBarcode,
        description,
        multiple,
        barcodeSymbology,
        barcodeValuePrefix,
        valuePattern,
        formatExample,
        defaultValue,
        readingInstruction,
        tableReadingPlan,
        learnedFromCount,
        shiftLineItemColumns,
        repairUnbalancedQuantity,
        reviewBelow,
        reviewBelowSource,
      }),
    ),
  }));

/**
 * Whether a document still has anything outstanding, judged from its own state.
 *
 * Written down once because it was written down twice: setDocumentType computed
 * it inline and confirmDocument did not compute it at all, so confirming every
 * field on a document left it flagged for review for ever. An operator working
 * through a hundred documents watched the outstanding count refuse to fall and
 * concluded they were making no progress — which they were, invisibly.
 *
 * A verified field is not a field needing review. Verification used to set
 * `verified` and leave `needsReview` standing beside it, which is two flags
 * disagreeing about the same fact.
 */
/**
 * Drop a stale "does not add up" finding once the rows do, and recompute the
 * document's review state from what it now says about itself.
 *
 * Kept beside documentNeedsReview because they answer the same question and
 * drifted apart once already: one is what the screen shows, the other is what
 * the spine and the outstanding count read.
 */
const withSettledArithmetic = (batch: ProcessResult, documentId: string): ProcessResult => ({
  ...batch,
  documents: batch.documents.map((item) => (item.id === documentId ? settledDocument(item) : item)),
});

/**
 * The same settling the engine does, applied optimistically so the screen does
 * not lag a save.
 *
 * NOT an authority. The engine settles every document on its way to disk and
 * the saved document comes back, so this only decides what the operator sees in
 * the beat before that returns. It exists because a confirmed document turning
 * green a second late feels broken; it does not exist to decide anything.
 *
 * That distinction is the fix. This rule used to be one of five that each
 * decided review state for real, and they drifted the moment any one was
 * corrected.
 */
/**
 * The values an operator reviews on a document: its read field values and its
 * rows. Review and verification flags are left out — they are what a confirmation
 * changes. Two documents with the same token show the operator the same reading.
 */
const reviewedValuesToken = (item: ProcessResult['documents'][number]): string =>
  JSON.stringify({
    fields: item.fields
      .filter((field) => field.name !== 'Document type')
      .map((field) => [field.name, field.value, field.canonicalValue ?? null]),
    rows: item.lineItems.map((row) => [
      row.rowNumber,
      row.description,
      row.quantity ?? null,
      row.unitPrice ?? null,
      row.amount ?? null,
      row.pageNumber,
    ]),
  });

const settledDocument = <T extends ProcessResult['documents'][number]>(item: T): T => {
  const findings = item.validationFindings.filter((finding) => findingStillHolds(finding, item));
  const settled =
    findings.length === item.validationFindings.length
      ? item
      : { ...item, validationFindings: findings };
  // Only the engine applies the Review thresholds, so this can clear a hold the
  // operator just answered but never adds one the engine did not make.
  const needsReview = item.needsReview && documentNeedsReview(settled);
  // The engine marks a held document for Teach only when it waits for measured uncertainty alone
  // (ReviewState.Assess); once nothing holds it, it stays a teaching example as before.
  const needsTeaching = Boolean(settled.needsTeaching);
  return needsReview === settled.needsReview &&
    needsTeaching === Boolean(settled.needsTeaching) &&
    settled === item
    ? item
    : { ...settled, needsReview, needsTeaching };
};

const documentNeedsReview = (item: {
  documentTypeVerified?: boolean;
  fields: ReadonlyArray<{
    name: string;
    value?: string;
    needsReview?: boolean;
    verified?: boolean;
    source?: string;
    evidenceLineIndexes?: readonly number[] | null;
    barcodeEvidence?: unknown;
    lookupEvidence?: unknown;
  }>;
  lineItems?: ReadonlyArray<{
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
  }>;
  validationFindings: ReadonlyArray<{ code?: string; severity: string }>;
}) =>
  !item.documentTypeVerified ||
  item.fields.some(
    (field) => field.name !== 'Document type' && field.needsReview && !field.verified,
  ) ||
  item.validationFindings.some(
    (finding) =>
      (finding.severity === 'warning' || finding.severity === 'error') &&
      finding.code !== FINDING.pageRotationApplied &&
      findingStillHolds(finding, item),
  );

/**
 * Whether a finding recorded when the document was read is still true of it.
 *
 * The checklist has always re-checked these against the document rather than
 * trusting the stored sentence. The flag did not, so a purchase order could read
 * "Confirmed · Paige is learning from this one" with every field ticked
 * and thirty-eight of thirty-eight line items balanced, and still wear an orange
 * nine in the spine — the complaint outliving the fault, this time on a document
 * put right by a learned rule rather than by hand.
 *
 * The rule itself now lives in `shared/finding-clearing.ts`, beside the engine's
 * counterpart in `ReviewState.StillHolds` and the cases both of them answer.
 * It was written here and there separately, and both copies ended in "anything I
 * do not recognise holds forever" — which produced the same trapped-document bug
 * twice in one day, on two different codes.
 */

const schemaNameCollator = new Intl.Collator(undefined, { sensitivity: 'accent' });
const fieldContractSignature = (
  fields: Array<{
    id?: string;
    fieldId?: string | null;
    name: string;
    kind?: string;
    fieldKind?: string | null;
  }>,
) =>
  fields
    .filter(
      (field) =>
        schemaNameCollator.compare(field.name, 'Document type') !== 0 &&
        (field.kind ?? field.fieldKind) !== 'line-items',
    )
    .map((field) =>
      [
        (field.id ?? field.fieldId ?? '').trim().toLowerCase(),
        field.name.trim().toLowerCase(),
        (field.kind ?? field.fieldKind ?? 'text').trim().toLowerCase(),
      ].join('\u001e'),
    )
    .join('\u001f');

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
const batchUsesCurrentSchema = (job: Job, batch: ProcessResult) => {
  // Invocation-local only: a later schema edit must always be measured anew.
  const contracts = new Map<DocumentTypeDefinition, { signature: string; configured: Field[] }>();
  const loaded = new Set(batch.loadedDocumentIds ?? []);
  return batch.documents.every((document) => {
    const type = job.documentTypes.find(
      (candidate) =>
        candidate.id === document.documentTypeId ||
        schemaNameCollator.compare(candidate.name, document.documentType) === 0,
    );
    if (!type) return true;
    if (
      document.documentTypeId !== type.id ||
      schemaNameCollator.compare(document.documentType, type.name) !== 0
    )
      return false;
    let contract = contracts.get(type);
    if (!contract) {
      contract = {
        signature: fieldContractSignature(type.fields),
        configured: type.fields.filter((field) => field.kind !== 'line-items'),
      };
      contracts.set(type, contract);
    }
    if (
      (document.fieldContractSignature ?? fieldContractSignature(document.fields)) !==
      contract.signature
    )
      return false;
    if (batch.workspaceProjection === 'paged' && !loaded.has(document.id)) return true;
    const configured = contract.configured;
    const projected = document.fields.filter((field) => field.name !== 'Document type');
    if (configured.length !== projected.length) return false;
    return configured.every((field) =>
      projected.some((value) => {
        const identityMatches =
          value.fieldId === field.id ||
          (!value.fieldId && schemaNameCollator.compare(value.name, field.name) === 0);
        return (
          identityMatches &&
          schemaNameCollator.compare(value.name, field.name) === 0 &&
          (value.fieldKind ?? field.kind) === field.kind
        );
      }),
    );
  });
};

// The proposal's merge into the job and Save and use's activation: setup-proposal.ts, shared with support.
export { applyBootstrapProposal, isRemovedDocumentType };
const requireRepresentativeProposal = (result: ProcessResult): void => {
  if (result.bootstrapProposal?.documentTypes.length) return;
  throw new Error(
    'Paige finished reading these documents but could not identify a reusable document-type and field setup. Choose a more varied set, or start scanning and teach this job as you work.',
  );
};

/**
 * A once-per-job representative pass is evidence for a proposal, never the
 * operational batch. A folder and an explicit multi-file selection differ only
 * in how the operator chose the source; every later durability and acceptance
 * rule is intentionally shared.
 */
const usesRepresentativeBootstrap = (job: Pick<Job, 'bootstrapMode'>) =>
  job.bootstrapMode === 'folder' || job.bootstrapMode === 'sample-scan';

const toProjectionSkeleton = (
  document: ProcessResult['documents'][number],
): ProcessResult['documents'][number] => ({
  id: document.id,
  sourceName: document.sourceName,
  sourcePath: '',
  sourcePaths: [],
  documentType: document.documentType,
  documentTypeId: document.documentTypeId,
  documentTypeSchemaVersion: document.documentTypeSchemaVersion,
  fieldContractSignature:
    document.fieldContractSignature ?? fieldContractSignature(document.fields),
  documentTypeConfidence: document.documentTypeConfidence,
  documentTypeVerified: document.documentTypeVerified,
  pages: document.pages.map((page) => ({
    number: page.number,
    previewPath: page.previewPath,
    text: '',
    ocrConfidence: 0,
    ocrElapsedMilliseconds: 0,
    barcodeElapsedMilliseconds: 0,
    lines: [],
    words: [],
    barcodes: [],
    startsNewDocument: page.startsNewDocument,
    boundaryConfidence: page.boundaryConfidence,
    boundaryReasons: page.boundaryReasons ?? [],
  })),
  fields: [],
  lineItems: [],
  validationFindings: [],
  needsReview: document.needsReview,
  needsTeaching: document.needsTeaching,
});

const removedSeedJobIds = new Set(['ap-invoices', 'vendor-statements', 'employee-records']);

const kindLabel: Record<FieldKind, string> = {
  'document-type': 'Document type',
  text: 'Text',
  identifier: 'Identifier',
  date: 'Date',
  'date-time': 'Date and time',
  currency: 'Currency',
  number: 'Number',
  integer: 'Whole number',
  decimal: 'Decimal',
  percentage: 'Percentage',
  phone: 'Phone',
  email: 'Email',
  'postal-address': 'Address',
  barcode: 'Barcode',
  'line-items': 'Line items',
};

const valueTypeDescriptions: Record<Exclude<FieldKind, 'document-type' | 'line-items'>, string> = {
  text: 'Names, descriptions and other words.',
  identifier:
    'A reference code, such as an invoice number, account ID or part number. Use this even when the code contains only digits; it identifies something rather than an amount to calculate.',
  date: 'A calendar date, such as an invoice date or due date.',
  'date-time': 'A date together with a time, such as when a delivery arrived.',
  currency: 'A money amount, such as a price, subtotal or balance.',
  number: 'A general numeric value, with or without decimal places, such as 12 or 12.5.',
  integer: 'A whole number, such as a count of 12 items.',
  decimal:
    'A numeric measurement or amount that can have decimal places, such as 2.75. Checked the same way as Number.',
  percentage: 'A rate expressed as a percentage, such as 5% tax.',
  phone: 'A telephone number.',
  email: 'An email address.',
  'postal-address': 'A mailing or street address.',
  barcode:
    'A value encoded in a barcode. The Reading options choose whether to read barcodes, printed text or both.',
};

/**
 * Jobs an early build kept in this window's storage, before main stored them. Copied to main
 * once: once the marker says so (or the copy succeeds), an emptied job list stays empty
 * instead of bringing deleted jobs back from this old copy.
 */
function readLegacyJobs() {
  try {
    if (localStorage.getItem('cpv6-jobs-migrated')) return [];
    const stored = localStorage.getItem('cpv6-jobs');
    const parsed = stored ? (JSON.parse(stored) as Job[]) : [];
    return parsed.filter((job) => !removedSeedJobIds.has(job.id)).map(migrateJob);
  } catch {
    return [];
  }
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function useJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        // Tour jobs still being removed (their recovery copies are cleaned up first) stay off the list.
        const removing = readTourRemovals();
        const stored = ((await window.capturePointDesktop?.listJobs()) ?? [])
          .filter((job) => !removing.includes(job.id))
          .map((job) => migrateJob(job as Job));
        if (canceled) return;
        if (stored.length > 0) {
          setJobs(stored);
          return;
        }
        const legacy = readLegacyJobs();
        if (legacy.length === 0) return;
        const migrated: Job[] = [];
        for (const job of legacy) {
          const saved = await window.capturePointDesktop?.saveJob(job as StoredJobDefinition);
          if (saved) migrated.push(migrateJob(saved as Job));
        }
        if (migrated.length === legacy.length) {
          // Every job is in main now: the old copy is spent, whether or not this view is still here.
          try {
            localStorage.setItem('cpv6-jobs-migrated', new Date().toISOString());
            localStorage.removeItem('cpv6-jobs');
          } catch {
            /* The marker alone keeps it from being copied again. */
          }
        }
        if (!canceled) setJobs(migrated);
      } catch (reason) {
        if (!canceled) setLoadError(messageFor(reason, 'Jobs could not be loaded'));
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, []);
  return { jobs, setJobs, loading, loadError };
}

/** Reads of the document list before an export that keeps removing documents is said plainly. */
const WORKSPACE_LIST_READ_ATTEMPTS = 3;
/**
 * The latest batch's document index and every listed document's summary, read as one answer.
 *
 * The index and its windows are separate reads. An export ("As ready" sends documents the
 * moment they are confirmed) can remove a listed document between them, and the old single
 * pass threw "The document index is missing position N." at the person opening the job. A
 * listed document missing from the windows means the list changed: read it all again, and
 * after a few tries say so in plain words.
 */
async function readWorkspaceDocumentList(
  api: NonNullable<typeof window.capturePointDesktop>,
  jobId: string,
) {
  for (let attempt = 1; ; attempt++) {
    const overview = await api.getLatestBatchOverview(jobId);
    if (!overview) return null;
    const index = await api.getBatchDocumentIndex({ jobId, batchId: overview.batchId });
    const summaries: BatchDocumentSummary[] = [];
    for (let offset = 0; offset < index.length; offset += 100) {
      const page = await api.getBatchDocumentWindow({
        jobId,
        batchId: overview.batchId,
        offset,
        limit: 100,
      });
      summaries.push(...page.documents);
    }
    const summaryById = new Map(summaries.map((item) => [item.id, item]));
    if (index.every((item) => summaryById.has(item.id))) return { overview, index, summaryById };
    if (attempt >= WORKSPACE_LIST_READ_ATTEMPTS)
      throw new Error("This job's documents changed while opening. Try again.");
  }
}

/**
 * Read the active workspace without inflating its persisted document JSON.
 *
 * Export keeps terminal documents in immutable history and removes them from
 * this projection. Rebuilding from the bounded index/window APIs is therefore
 * safe for a one-document job and a ten-thousand-document job alike. Only the
 * document the operator was already on (or the first remaining one) is hydrated;
 * every other row stays a lightweight page-and-identity skeleton until opened.
 */
async function loadLatestWorkspaceBatch(
  jobId: string,
  preferredDocumentId?: string,
): Promise<ProcessResult | null> {
  const api = window.capturePointDesktop;
  if (!api) return null;
  const read = await readWorkspaceDocumentList(api, jobId);
  if (!read) return null;
  const { overview, index, summaryById } = read;
  const skeletons: ProcessResult['documents'][number][] = index.map((item) => {
    const summary = summaryById.get(item.id)!;
    return {
      id: summary.id,
      sourceName: summary.sourceName,
      sourcePath: '',
      sourcePaths: [],
      documentType: summary.documentType,
      documentTypeId: summary.documentTypeId,
      documentTypeSchemaVersion: summary.documentTypeSchemaVersion,
      fieldContractSignature: summary.fieldContractSignature,
      documentTypeConfidence: summary.documentTypeConfidence,
      documentTypeVerified: summary.documentTypeVerified,
      pages: summary.pages.map((page) => ({
        number: page.number,
        previewPath: page.previewPath,
        text: '',
        ocrConfidence: 0,
        ocrElapsedMilliseconds: 0,
        barcodeElapsedMilliseconds: 0,
        lines: [],
        words: [],
        barcodes: [],
        startsNewDocument: page.startsNewDocument,
        boundaryConfidence: page.boundaryConfidence,
        boundaryReasons: page.boundaryReasons ?? [],
      })),
      fields: [],
      lineItems: [],
      validationFindings: [],
      needsReview: summary.needsReview,
      needsTeaching: summary.needsTeaching,
    } satisfies ProcessResult['documents'][number];
  });
  const selectedId =
    preferredDocumentId && index.some((item) => item.id === preferredDocumentId)
      ? preferredDocumentId
      : index[0]?.id;
  let documents = skeletons;
  let hydratedSelectedId: string | undefined;
  if (selectedId) {
    const selected = await api.getBatchDocument({
      jobId,
      batchId: overview.batchId,
      documentId: selectedId,
    });
    if (selected) {
      documents = skeletons.map((item) => (item.id === selected.id ? selected : item));
      hydratedSelectedId = selected.id;
    }
  }
  return {
    batchId: overview.batchId,
    startedAt: overview.startedAt,
    completedAt: overview.completedAt,
    documents,
    warnings: overview.warnings,
    timings: overview.timings ?? undefined,
    workspaceProjection: 'paged',
    loadedDocumentIds: hydratedSelectedId ? [hydratedSelectedId] : [],
  };
}

/**
 * Continues the selected job's durable capture queue while another workspace is
 * on screen. It intentionally renders nothing: keeping the document viewer and
 * all of its keyboard, pointer and detached-window effects hidden-but-mounted
 * would trade one lifecycle defect for several UI ones.
 */
function SelectedJobBackgroundCaptureDrain({
  jobId,
  getCurrentJob,
  active,
  onResult,
  onNotice,
}: {
  jobId: string;
  getCurrentJob: (jobId: string) => Job | undefined;
  active: boolean;
  onResult: (jobId: string, result: ProcessResult) => Promise<Job | void>;
  onNotice: PublishNotice;
}) {
  const live = useRef(true);
  const activeRef = useRef(active);
  const currentJobRef = useRef(getCurrentJob);
  const resultRef = useRef(onResult);
  const noticeRef = useRef(onNotice);
  const adoptionBlocked = useRef(false);
  activeRef.current = active;
  currentJobRef.current = getCurrentJob;
  resultRef.current = onResult;
  noticeRef.current = onNotice;

  const controller = useRef<AppLifetimeCaptureDrain<StagedCapture> | null>(null);
  useEffect(() => {
    // This resource is created by the effect that owns its cleanup. React's
    // development Strict Mode deliberately runs setup, cleanup, then setup
    // again; creating it during render left the ref pointing at the first,
    // permanently disposed controller during that second setup.
    live.current = true;
    activeRef.current = active;
    const created = new AppLifetimeCaptureDrain<StagedCapture>({
      blocked: () =>
        adoptionBlocked.current || !currentJobRef.current(jobId) || captureDrainBlocked(jobId),
      list: async () => {
        const api = window.capturePointDesktop;
        if (!api) return [];
        const staged = await api.listStagedCaptures(jobId);
        const outage =
          /engine|did not start|restarted safely|stopped unexpectedly|interrupted|closed before|object reference|nullreference/i;
        return await Promise.all(
          staged.map(async (capture) => {
            if (
              capture.status !== 'failed' ||
              !outage.test(capture.error ?? 'interrupted') ||
              (capture.attempts ?? 0) >= AUTOMATIC_RETRY_LIMIT
            )
              return capture;
            try {
              const attempts = await api.retryStagedCapture(capture.id);
              return { ...capture, status: 'pending' as const, error: null, attempts };
            } catch {
              // The manifest is still failed on disk. Returning that same truth is
              // what prevents the scheduler from dispatching a retry main refused.
              return capture;
            }
          }),
        );
      },
      process: async (capture) => {
        const api = window.capturePointDesktop;
        let currentJob = currentJobRef.current(jobId);
        if (
          !api ||
          !currentJob ||
          !live.current ||
          !activeRef.current ||
          captureDrainBlocked(jobId)
        )
          return false;
        // A job with no type left reads nothing new (ADR-0096): its pages wait until a type is added.
        if (hasNoTypeLeft(currentJob)) return false;
        if (Date.now() < engineQueueHeldUntil()) return false;
        const admittedGeneration = captureDrainGeneration(jobId);
        if (capture.waveMode === 'processor-paced') {
          try {
            await api.waitForLayoutCatchUp(jobId);
          } catch {
            // Learning only accelerates a wave. Its refusal never withholds paper.
          }
          if (
            !live.current ||
            !activeRef.current ||
            captureDrainBlocked(jobId) ||
            captureDrainGeneration(jobId) !== admittedGeneration
          )
            return false;
        }
        const overview = await api.getLatestBatchOverview(jobId);
        if (
          !live.current ||
          !activeRef.current ||
          captureDrainBlocked(jobId) ||
          captureDrainGeneration(jobId) !== admittedGeneration
        )
          return false;
        // The save coordinator publishes this authoritative ref before it opens
        // the gate. Read it at dispatch, not from a prop that React may not have
        // rendered yet after a save normalization or rollback.
        currentJob = currentJobRef.current(jobId);
        if (!currentJob || hasNoTypeLeft(currentJob)) return false;
        let result: ProcessResult;
        try {
          result = await api.processStagedCapture({
            captureId: capture.id,
            jobId: currentJob.id,
            documentTypes: schemaRequest(currentJob),
            separationMode: currentJob.separationMode,
            separationBarcodeText: currentJob.separationBarcodeText,
            boundaryOpeningProfiles: currentJob.boundaryOpeningProfiles,
            boundaryContinuationProfiles: currentJob.boundaryContinuationProfiles,
            currentBatchId: overview?.batchId,
          });
        } catch (reason) {
          const message = messageFor(reason, 'Recognition failed');
          // Refused because the job has no type left (main saw it before this window did): a wait, not a failure.
          if (isFolderPreparationPaused(message) || isNoTypeLeftRefusal(message)) return false;
          const verdict = judgeCaptureFailure(message, engineOutageNow(), Date.now());
          recordEngineOutage(verdict.outageNow);
          if (verdict.outage)
            holdEngineQueueUntil(
              Date.now() +
                (verdict.resume ? ENGINE_RESTART_COOLDOWN_MS : ENGINE_OUTAGE_PATIENCE_MS),
            );
          const attemptAfterFailure = (capture.attempts ?? 0) + 1;
          if (verdict.resume && attemptAfterFailure < AUTOMATIC_RETRY_LIMIT)
            await api.retryStagedCapture(capture.id).catch(() => undefined);
          if (live.current && activeRef.current && verdict.opensOutage) {
            reportEvent('engine.outage-shown', { jobId: currentJob.id });
            noticeRef.current(
              'Recognition paused while the engine restarts. The queue resumes by itself.',
              'engine-restarting',
            );
          }
          throw reason;
        }
        clearEngineOutage();
        // The native call above has already committed the pages and removed its
        // durable manifest. A later shell/job-adoption failure is not recognition
        // failure and must never send the same paper through the engine again.
        // Hold later waves until Documents remounts and reconciles the committed
        // batch instead of inventing a retry for work that succeeded.
        if (live.current && activeRef.current) {
          try {
            await resultRef.current(currentJob.id, result);
          } catch {
            adoptionBlocked.current = true;
            noticeRef.current(
              'The pages were read and saved. Open Documents to refresh this job before more pages continue.',
              'job-refresh-failed',
            );
            return false;
          }
        }
        void api.autoTuneReviewBars(currentJob.id).catch(() => undefined);
        return true;
      },
      onError: (reason) => {
        if (!live.current || !activeRef.current) return;
        const message = messageFor(reason, 'Recognition stopped');
        // A failed manifest remains durable and visible when Documents reopens.
        // Keep this global notice short; the detailed retry card owns the error.
        if (!/engine|restarted safely|stopped unexpectedly/i.test(message))
          noticeRef.current('Some pages need attention in Documents.', 'pages-not-read');
      },
    });
    controller.current = created;
    const unsubscribe = subscribeCaptureDrainGate(jobId, () => created.wake());
    created.setBackgroundActive(activeRef.current);
    return () => {
      live.current = false;
      activeRef.current = false;
      unsubscribe();
      created.dispose();
      if (controller.current === created) controller.current = null;
    };
  }, [jobId]);

  useEffect(() => {
    if (!active) adoptionBlocked.current = false;
    controller.current?.setBackgroundActive(active);
  }, [active]);
  return null;
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function App() {
  const { jobs, setJobs, loading: jobsLoading, loadError: jobsLoadError } = useJobs();
  // A window reload comes back to the screen and job mode it left (Settings, Learning, Job setup),
  // not only to the job. A restored mode belongs to the restored job: when that job is gone (the
  // first job stands in), the stand-in opens on its documents, not on a Job setup or Export that was
  // open for another job.
  const restoredWorkspace = useRef(readRendererWorkspaceSession()).current;
  const [screen, setScreen] = useState<Screen>(restoredWorkspace?.screen ?? 'job');
  // PAIGE: a job asked for on its setup (setupRequestKey) opens there; otherwise on its documents. The session's
  // jobMode is not restored here: Paige's host pins the route's mode, which follows the job's state (a job being set
  // up is 'setup'), so restoring it opened Job setup over the tour's documents (paige/CapturePointWorkbench.tsx).
  const [requestedJobMode, setJobMode] = useState<JobMode>(() =>
    setupRequested() ? 'setup' : 'capture',
  );
  useEffect(clearSetupRequest, []);
  const [selectedId, setSelectedId] = useState(restoredWorkspace?.selectedId ?? jobs[0]?.id ?? '');
  const restoredJobGone =
    !jobsLoading &&
    restoredWorkspace !== null &&
    selectedId === restoredWorkspace.selectedId &&
    !jobs.some((job) => job.id === selectedId);
  const jobMode: JobMode = restoredJobGone ? 'capture' : requestedJobMode;
  // Async analysis belongs to the job that started it. Keep the current
  // identity outside render closures so a late completion cannot move a
  // different job to Setup after the operator has switched workspaces.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const [learningInitialSection, setLearningInitialSection] = useState<
    'overview' | 'suggestions' | 'train' | 'results'
  >('overview');
  const [learningJobId, setLearningJobId] = useState<string | undefined>(
    restoredWorkspace?.screen === 'learning'
      ? restoredWorkspace.selectedId || undefined
      : undefined,
  );
  const [learningReturnToSetup, setLearningReturnToSetup] = useState(false);
  /** Each open starts Learning afresh at its section, even over Learning already open (a link to Results over Teach). */
  const [learningOpens, setLearningOpens] = useState(0);
  const [teachingDocument, setTeachingDocument] = useState<{
    jobId: string;
    documentId: string;
  } | null>(null);
  /** Reviewed in Learning, applied by the document workspace that owns the batch. */
  const [suggestionApplyRequest, setSuggestionApplyRequest] = useState<
    ({ jobId: string } & SuggestionApplyRequest) | null
  >(null);
  /** Learning's Teach: open the job's documents on the Teach filter. */
  const [teachRequestJobId, setTeachRequestJobId] = useState<string | null>(null);
  const openLearning = useCallback(
    (
      jobId = selectedIdRef.current,
      section: 'overview' | 'suggestions' | 'train' | 'results' = 'overview',
      fromJobSetup = false,
    ) => {
      setLearningReturnToSetup(fromJobSetup);
      setLearningJobId(jobId || undefined);
      setLearningInitialSection(section);
      setLearningOpens((count) => count + 1);
      setScreen('learning');
    },
    [],
  );
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        rendererWorkspaceSessionKey,
        JSON.stringify({ screen, jobMode, selectedId }),
      );
    } catch {
      // A disabled session store only loses reload navigation; durable job and
      // proposal recovery remain owned by native state.
    }
  }, [screen, jobMode, selectedId]);
  useEffect(() => {
    if (jobsLoading || screen !== 'job' || !selectedId || jobs.some((job) => job.id === selectedId))
      return;
    setScreen('jobs');
  }, [jobsLoading, jobs, screen, selectedId]);
  // Paige holds the workbench's shape over the mount until a real screen is up: the job library,
  // a settings page, a load failure, or a job that is not here (paige/CapturePointWorkbench.tsx).
  // A job's own screens report themselves once their documents or setup map are in hand.
  useEffect(() => {
    if (jobsLoading) return;
    if (screen !== 'job' || jobsLoadError || !jobs.some((job) => job.id === selectedId))
      window.paigeWorkbench?.opened?.();
  }, [jobsLoading, jobsLoadError, jobs, screen, selectedId]);
  useEffect(() => {
    const open = (event: Event) => {
      const detail = (
        event as CustomEvent<{ jobId?: string; section?: string; fromJobSetup?: boolean }>
      ).detail;
      openLearning(
        detail?.jobId,
        detail?.section === 'suggestions' || detail?.section === 'train'
          ? detail.section
          : 'overview',
        detail?.fromJobSetup === true,
      );
    };
    window.addEventListener('capturepoint-open-learning', open);
    return () => window.removeEventListener('capturepoint-open-learning', open);
  }, [openLearning]);
  const workspaceRef = useRef<HTMLDivElement>(null);
  // Paige mounts the workbench with the new-job dialog already open (paige/CapturePointWorkbench.tsx).
  const [showCreate, setShowCreate] = useState(() => Boolean(window.paigeWorkbench?.createOnOpen));
  const [showJobImport, setShowJobImport] = useState(false);
  // Earlier-Paige imports paused or stopped before they finished: each one's job menu offers
  // Finish import, the way back to it (owner, 2026-10-10), and opens the import window on it.
  const [unfinishedImports, setUnfinishedImports] = useState<LegacyImportStatus[]>([]);
  const [jobImportResume, setJobImportResume] = useState<LegacyImportStatus | null>(null);
  const refreshUnfinishedImports = useCallback(() => {
    void window.capturePointDesktop
      ?.listLegacyImports()
      .then(setUnfinishedImports)
      .catch(() => undefined);
  }, []);
  // F1 opens the keyboard shortcut sheet from anywhere in the main window.
  const [showShortcuts, setShowShortcuts] = useState(false);
  const openJobSetupAtRef = useRef<(section: string) => void>(() => undefined);
  const [returningNewJob, setReturningNewJob] = useState<Job | null>(null);
  useEffect(() => {
    const back = (event: Event) => {
      const job = (event as CustomEvent<Job>).detail;
      setReturningNewJob(job);
      setCreateMode(
        job.bootstrapMode === 'manual'
          ? 'manual'
          : job.bootstrapMode === 'content-central'
            ? 'content-central'
            : 'scan',
      );
      setCreateStart('name');
      setShowCreate(true);
    };
    window.addEventListener('capturepoint-back-to-new-job', back);
    return () => window.removeEventListener('capturepoint-back-to-new-job', back);
  }, []);
  const [deleteTarget, setDeleteTarget] = useState<Job | null>(null);
  // Rename started from the job door (the Scan jobs window keeps its own).
  const [doorRenameJobId, setDoorRenameJobId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState<JobBootstrapChoice>('scan');
  // PAIGE: New job opened from Start my own job lands on its name step (`createStart`).
  const [createStart, setCreateStart] = useState<NewJobStep>(
    () => window.paigeWorkbench?.createStart ?? 'choice',
  );
  // New job opens on "How do you want to start?"; a start already chosen (the
  // welcome's Get started, with the samples installed) opens on its name step.
  const newJobOrigin = useRef<{ screen: Screen; jobId: string }>({ screen: 'jobs', jobId: '' });
  const [freshJob, setFreshJob] = useState<{
    jobId: string;
    origin: { screen: Screen; jobId: string };
  } | null>(null);
  const openNewJob = (mode?: JobBootstrapChoice) => {
    // PAIGE: New job on a job's own route is Paige's new-job route (workbench-port.md, `openNewJob`).
    if (window.paigeWorkbench?.newJob) return window.paigeWorkbench.newJob(mode);
    newJobOrigin.current = { screen, jobId: selectedIdRef.current };
    setCreateMode(mode ?? 'scan');
    setCreateStart(mode ? 'name' : 'choice');
    setShowCreate(true);
  };
  const [toast, setToast, toastAction] = useWorkspaceNotice(selectedId);
  // A short action under way, shown in the status bar until it answers (Downloading job setup…).
  const [workingNotice, setWorkingNotice] = useState('');
  const componentDownload = useRuntimeComponentStatus();
  const toastAfterMove = useNoticeAfterMove(setToast);
  const toastToCurrentVisit = useNoticeForCurrentVisit(setToast);
  const [appearance, setAppearance] = useState<AppearanceMode>(() =>
    window.capturePointDesktop?.initialAppearance === 'dark' ? 'dark' : 'light',
  );
  const [appearanceChanging, setAppearanceChanging] = useState(false);
  const [interfaceZoom, setInterfaceZoom] = useState<InterfaceZoomState | null>(null);
  const [interfaceZoomHud, setInterfaceZoomHud] = useState<InterfaceZoomEvent | null>(null);
  const interfaceZoomHudTimer = useRef<number | null>(null);
  const interfaceZoomRevision = useRef(0);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('appearance');
  /** The speed test hint's Run speed test, until Performance has started the test. */
  const [speedTestRequest, setSpeedTestRequest] = useState(0);
  const [showSampleJobs, setShowSampleJobs] = useState(false);
  // From the click on Take a 2-minute tour until its job exists: the start window shows at once.
  const [tourStarting, setTourStarting] = useState(false);
  const [appearanceStandalone, setAppearanceStandalone] = useState(false);
  const [jobDoorOpen, setJobDoorOpen] = useState(false);
  const [setupEntry, setSetupEntry] = useState<{
    section: JobSetupSection;
    stop?: ExportStop;
    request: number;
  } | null>(null);
  const [focusSettingsSection, setFocusSettingsSection] = useState(false);
  const [focusJobsHeading, setFocusJobsHeading] = useState(false);
  const appearanceChangeInFlight = useRef(false);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [activeBatches, setActiveBatches] = useState<Record<string, ProcessResult>>({});
  /** How many documents each job's workspace shows, live ones included (workbench-documents.ts). */
  const [shownDocumentCounts, setShownDocumentCounts] = useState<Record<string, number>>({});
  // Export runs in the engine, including the twenty-second "As ready" loop, so
  // it can retire work while the renderer is looking at another tab. Keep an
  // authoritative ref for the refresh path: a closure captured when a timer was
  // installed is not allowed to put an already-exported document back.
  const activeBatchesRef = useRef(activeBatches);
  activeBatchesRef.current = activeBatches;
  const [bootstrapSamples, setBootstrapSamples] = useState<Record<string, ProcessResult>>({});
  const [folderStatuses, setFolderStatuses] = useState<Record<string, FolderAnalysisStatus>>({});
  const [representativeReconciliations, setRepresentativeReconciliations] = useState<
    Record<string, boolean>
  >({});
  const [engineHealth, setEngineHealth] = useState<EngineHealth | null>(null);
  // For the rail dot on Settings: amber only when a stage that SHOULD be
  // on the graphics card is not. A machine on its processor by design shows
  // nothing — correct is not a state to announce.
  const [workstationHealth, setWorkstationHealth] = useState<WorkstationHealthReport | null>(null);
  const [bootstrapActivity, setBootstrapActivity] = useState<Record<string, string>>({});
  const [folderProgress, setFolderProgress] = useState<Record<string, FolderAnalysisProgress>>({});
  const [folderActivity, setFolderActivity] = useState<Record<string, FolderActivityEntry[]>>({});
  const [folderWork, setFolderWork] = useState<Record<string, FolderWorkTask[]>>({});
  const [bootstrapFailures, setBootstrapFailures] = useState<Record<string, string>>({});

  // Every destination owns its own beginning. The shell's one scroll container
  // otherwise carried a position from a long Export page into Scan jobs (or
  // Job setup into Learning), making the next screen appear to open halfway
  // through. Reset before paint whenever navigation changes the destination,
  // mode, or active job; scrolling inside a screen remains completely local.
  useLayoutEffect(() => {
    const workspace = workspaceRef.current;
    if (workspace) {
      workspace.scrollTop = 0;
      workspace.scrollLeft = 0;
    }
    // At the extreme zoom layout the document becomes the scroll container.
    // Reset that route too, or the same defect returns only for the operator
    // who enlarged the interface enough to need it most.
    const documentScroller = document.scrollingElement;
    if (documentScroller) {
      documentScroller.scrollTop = 0;
      documentScroller.scrollLeft = 0;
    }
  }, [screen, jobMode, selectedId]);

  // The running version has to be visible somewhere. Without it an operator cannot
  // tell whether an update was applied, which makes auto-update unverifiable from
  // the outside: a staged release and a failed one look exactly the same.
  const [appVersion, setAppVersion] = useState('');
  // What a manual update check found. The banner already speaks for a download
  // that is happening; this is for the two answers it cannot give — "nothing to
  // get" and "I could not reach the feed" — which are the whole reason an
  // operator presses the button rather than waiting.
  const [updateCheckNote, setUpdateCheckNote] = useState<string | null>(null);
  const [updateChecking, setUpdateChecking] = useState(false);
  /**
   * Ask for an update now.
   *
   * The logic is unchanged — only its trigger moved. It deliberately stays
   * silent for `downloading` and `ready` because the banner owns those, and it
   * routes everything else through the one place that has words for every
   * state; listing them by hand is how "ahead of your channel" ended up as a
   * click that produced nothing at all. It used to hang off a 10px version chip
   * at the foot of the rail; it is now a button in Settings that says what
   * it does.
   */
  const checkForUpdateNow = useCallback(async () => {
    if (updateChecking) return;
    setUpdateChecking(true);
    setUpdateCheckNote(null);
    try {
      const desktop = window.capturePointDesktop;
      if (!desktop) return;
      const result = await desktop.checkForUpdateNow();
      if (result.state !== 'downloading' && result.state !== 'ready')
        setUpdateCheckNote(updateActivityLine(result));
    } catch (reason) {
      setUpdateCheckNote(messageFor(reason, 'The update check could not be completed'));
    } finally {
      setUpdateChecking(false);
    }
  }, [updateChecking]);
  // A setup edit becomes a capture gate in the same event turn, before its
  // debounced native save starts. The coordinator owns the complete lifetime:
  // canceled debounces, same-object commits, rollback reloads, and deletion all
  // stay blocked until that job's newest admitted write is genuinely idle.
  const jobSaveGateOwners = useRef(new Map<string, CaptureDrainGateOwner>());
  const jobSaveGateOwner = (jobId: string) => {
    const current = jobSaveGateOwners.current.get(jobId);
    if (current) return current;
    const created = new CaptureDrainGateOwner(jobId, 'saving the job setup');
    jobSaveGateOwners.current.set(jobId, created);
    return created;
  };
  const createJobSaveCoordinator = () =>
    new RevisionedJobSaveCoordinator<Job>({
      idOf: (job) => job.id,
      save: async (job) => {
        const saved = await window.capturePointDesktop?.saveJob(job as StoredJobDefinition);
        return saved ? migrateJob(saved as Job) : job;
      },
      reload: async (jobId) => {
        const stored = await window.capturePointDesktop?.listJobs();
        const authoritative = stored?.find((job) => job.id === jobId);
        return authoritative ? migrateJob(authoritative as Job) : null;
      },
      onSaved: (saved) => {
        // The save barrier releases in the coordinator's finally block, before
        // React is required to render this state update. Publish the same
        // authoritative snapshot to the ref synchronously so a CPU background
        // wave awakened by that release cannot read the older optimistic schema.
        jobsRef.current = jobsRef.current.map((job) => (job.id === saved.id ? saved : job));
        setJobs((current) => current.map((job) => (job.id === saved.id ? saved : job)));
      },
      // A commit is the current job from the moment it is admitted: the next change
      // (a rename, the stance, a proposal) is built from jobsRef, and a save it
      // supersedes never reaches onSaved, so without this it rebuilt the job from
      // before the first change and the whole-job save undid it (chunk 28, D1).
      onCommitAdmitted: (next) => {
        jobsRef.current = jobsRef.current.map((job) => (job.id === next.id ? next : job));
        setJobs((current) => current.map((job) => (job.id === next.id ? next : job)));
      },
      // What a failed commit falls back to when the rollback's reload also fails
      // (chunk 28, R5-D1): the job as published before its first commit.
      currentOf: (jobId) => jobsRef.current.find((job) => job.id === jobId) ?? null,
      onRollback: (jobId, authoritative) => {
        jobsRef.current = authoritative
          ? jobsRef.current.map((job) => (job.id === jobId ? authoritative : job))
          : jobsRef.current.filter((job) => job.id !== jobId);
        setJobs((current) =>
          authoritative
            ? current.map((job) => (job.id === jobId ? authoritative : job))
            : current.filter((job) => job.id !== jobId),
        );
      },
      onAutosaveError: (reason) =>
        setToast(messageFor(reason, 'Job changes were not saved'), 'job-save-failed'),
      onActivityChange: (jobId, active) => jobSaveGateOwner(jobId).setBlocked(active),
    });
  const jobSaveCoordinator = useRef<RevisionedJobSaveCoordinator<Job> | null>(null);
  if (!jobSaveCoordinator.current) jobSaveCoordinator.current = createJobSaveCoordinator();
  useEffect(() => {
    // Strict Mode rehearses this lifecycle in development. Recreate during its
    // second setup instead of leaving the ref attached to the coordinator the
    // first cleanup disposed.
    const coordinator = jobSaveCoordinator.current ?? createJobSaveCoordinator();
    jobSaveCoordinator.current = coordinator;
    return () => {
      coordinator.dispose();
      if (jobSaveCoordinator.current === coordinator) jobSaveCoordinator.current = null;
    };
  }, []);

  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    let revision = 0;
    const stop = desktop.onAppearanceChanged?.((mode) => {
      revision += 1;
      setAppearance(mode);
    });
    const requestedAt = revision;
    void desktop
      .getAppearance?.()
      .then((mode) => {
        if (revision === requestedAt) setAppearance(mode);
      })
      .catch(() => undefined);
    return stop;
  }, []);

  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    let live = true;
    const stop = desktop.onInterfaceZoomChanged((event) => {
      if (!live) return;
      interfaceZoomRevision.current += 1;
      setInterfaceZoom(event);
      if (event.source === 'settings' || (event.source === 'display' && !event.limitReached)) {
        setInterfaceZoomHud(null);
        if (interfaceZoomHudTimer.current !== null)
          window.clearTimeout(interfaceZoomHudTimer.current);
        interfaceZoomHudTimer.current = null;
        return;
      }
      setInterfaceZoomHud(event);
      if (interfaceZoomHudTimer.current !== null)
        window.clearTimeout(interfaceZoomHudTimer.current);
      interfaceZoomHudTimer.current = window.setTimeout(() => {
        setInterfaceZoomHud(null);
        interfaceZoomHudTimer.current = null;
      }, 4_000);
    });
    const requestedAt = interfaceZoomRevision.current;
    void desktop
      .getInterfaceZoom()
      .then((state) => {
        if (live && interfaceZoomRevision.current === requestedAt) setInterfaceZoom(state);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      interfaceZoomRevision.current += 1;
      stop?.();
      if (interfaceZoomHudTimer.current !== null)
        window.clearTimeout(interfaceZoomHudTimer.current);
    };
  }, []);

  const changeInterfaceZoom = useCallback(async (action: 'in' | 'out' | 'reset') => {
    const requestedAt = ++interfaceZoomRevision.current;
    const state = await window.capturePointDesktop?.changeInterfaceZoom(action);
    if (state && interfaceZoomRevision.current === requestedAt) setInterfaceZoom(state);
  }, []);

  const changeAppearance = useCallback(async (mode: AppearanceMode) => {
    const desktop = window.capturePointDesktop;
    if (!desktop?.setAppearance)
      throw new Error('Paige could not save its appearance on this computer.');
    if (appearanceChangeInFlight.current) return;
    appearanceChangeInFlight.current = true;
    setAppearanceChanging(true);
    try {
      const saved = await desktop.setAppearance(mode);
      setAppearance(saved);
    } finally {
      appearanceChangeInFlight.current = false;
      setAppearanceChanging(false);
    }
  }, []);

  // Ctrl+, follows the desktop convention and opens the exact Appearance
  // section. Settings is one coherent computer-level destination now; there is
  // no second Preferences surface to make operators guess which one owns a
  // choice. Never navigate away while a modal is holding the keyboard.
  useEffect(() => {
    const openLicense = () => {
      setAppearanceStandalone(false);
      setSettingsSection('license');
      setScreen('settings');
    };
    const openPerformance = () => {
      setAppearanceStandalone(false);
      setSettingsSection('performance');
      setScreen('settings');
    };
    // The speed test hint: This computer › Performance, and the test starts there.
    const runSpeedTest = () => {
      openPerformance();
      setSpeedTestRequest((current) => current + 1);
    };
    const openComputer = (event: Event) => {
      const section = (event as CustomEvent<{ section?: ComputerSection }>).detail?.section;
      setAppearanceStandalone(false);
      setSettingsSection(
        (current) =>
          section ?? (current === 'jobs' || current === 'overview' ? 'appearance' : current),
      );
      setScreen('settings');
    };
    // Paige's account door opens Appearance on its own page, as its workspace menu did.
    const openAppearance = () => {
      setAppearanceStandalone(true);
      setSettingsSection('appearance');
      setScreen('settings');
    };
    window.addEventListener(OPEN_APPEARANCE_EVENT, openAppearance);
    // Stop setup answers with the saved status; the screen shows it at once instead of
    // polling a "running" status until the cancelled work unwinds.
    const folderStatusChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ jobId?: string; status?: FolderAnalysisStatus }>)
        .detail;
      if (detail?.jobId && detail.status)
        setFolderStatuses((current) => ({ ...current, [detail.jobId!]: detail.status! }));
    };
    window.addEventListener('capturepoint-open-license', openLicense);
    window.addEventListener('capturepoint-open-performance', openPerformance);
    window.addEventListener(RUN_SPEED_TEST_EVENT, runSpeedTest);
    window.addEventListener(OPEN_COMPUTER_EVENT, openComputer);
    window.addEventListener(FOLDER_STATUS_EVENT, folderStatusChanged);
    return () => {
      window.removeEventListener(OPEN_APPEARANCE_EVENT, openAppearance);
      window.removeEventListener(FOLDER_STATUS_EVENT, folderStatusChanged);
      window.removeEventListener('capturepoint-open-license', openLicense);
      window.removeEventListener('capturepoint-open-performance', openPerformance);
      window.removeEventListener(RUN_SPEED_TEST_EVENT, runSpeedTest);
      window.removeEventListener(OPEN_COMPUTER_EVENT, openComputer);
    };
  }, []);
  useEffect(() => {
    // On-device milestone times live in main's per-install store; follow them.
    const stopMilestones = connectMilestones();
    const openSection = (event: Event) => {
      const section = (event as CustomEvent<string>).detail;
      if (typeof section === 'string') openJobSetupAtRef.current(section);
    };
    window.addEventListener(OPEN_SETUP_SECTION_EVENT, openSection);
    return () => {
      stopMilestones();
      window.removeEventListener(OPEN_SETUP_SECTION_EVENT, openSection);
    };
  }, []);
  useShortcutSheetKeys(setShowShortcuts);
  useEffect(() => {
    const openSettings = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key !== ',') return;
      // The desktop's own settings screen; a host that hides it keeps its settings elsewhere.
      if (!hostHas('workbench-settings')) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      event.preventDefault();
      setSettingsSection('appearance');
      setFocusSettingsSection(true);
      setScreen('settings');
    };
    window.addEventListener('keydown', openSettings);
    return () => window.removeEventListener('keydown', openSettings);
  }, []);

  useEffect(() => {
    if (!focusSettingsSection || screen !== 'settings') return;
    const frame = window.requestAnimationFrame(() => {
      focusActiveWorkspaceSection(workspaceRef.current);
      setFocusSettingsSection(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusSettingsSection, screen, settingsSection]);

  useEffect(() => {
    void window.capturePointDesktop?.getRuntimeInfo().then((runtime) => {
      document.documentElement.dataset.desktopRuntime = runtime.isPackaged
        ? 'packaged'
        : 'development';
      document.documentElement.dataset.desktopVersion = runtime.appVersion;
      setAppVersion(runtime.appVersion);
    });
    const refreshHealth = () =>
      void window.capturePointDesktop
        ?.getEngineHealth()
        .then(setEngineHealth)
        .catch(() => undefined);
    refreshHealth();
    const healthTimer = window.setInterval(refreshHealth, 5000);
    return () => window.clearInterval(healthTimer);
  }, []);
  useEffect(() => {
    let live = true,
      pushed = false;
    const stop = window.capturePointDesktop?.onWorkstationHealthChanged((next) => {
      pushed = true;
      if (live) setWorkstationHealth(next);
    });
    void window.capturePointDesktop
      ?.getWorkstationHealth()
      .then((next) => {
        if (live && !pushed) setWorkstationHealth(next);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      stop?.();
    };
  }, []);
  useEffect(
    () =>
      window.capturePointDesktop?.onFolderAnalysisProgress((progress) => {
        setFolderProgress((current) => ({ ...current, [progress.jobId]: progress }));
        // A heartbeat says the engine is alive inside a long step; it is not a new activity.
        if (progress.heartbeat) return;
        // Remembered as it arrives: a type's ready event can be overtaken before a render reads it.
        rememberReadyType(progress.jobId, progress.activity);
        const activity = progress.activity;
        const title = activity
          ? activity.kind === 'source'
            ? `${activity.status === 'completed' ? 'Prepared' : activity.status === 'failed' ? 'Could not prepare' : 'Preparing'} ${activity.label}`
            : activity.kind === 'ocr'
              ? `${activity.status === 'completed' ? 'Read text' : activity.status === 'failed' ? 'Text needs review' : 'Reading text'} · ${activity.label}`
              : activity.kind === 'barcode'
                ? `${activity.status === 'completed' ? 'Checked barcodes' : activity.status === 'failed' ? 'Barcode check needs review' : 'Scanning barcodes'} · ${activity.label}`
                : activity.kind === 'page'
                  ? `${activity.status === 'completed' ? 'Finished page' : 'Processing page'} ${activity.pageNumber.toLocaleString()} · ${activity.label}`
                  : activity.kind === 'document'
                    ? `${activity.status === 'completed' ? 'Understood' : activity.status === 'failed' ? 'Flagged for review' : 'Understanding'} · ${activity.label}`
                    : activity.label
          : progress.stage === 'preparing'
            ? progress.completed
              ? `Prepared ${progress.sourceName ?? `sample ${progress.completed}`}`
              : 'Cataloging representative files'
            : progress.stage === 'recognizing'
              ? progress.completed
                ? `Read page ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
                : 'Starting page recognition'
              : progress.stage === 'organizing'
                ? 'Comparing pages and locating document starts'
                : progress.stage === 'understanding'
                  ? progress.completed
                    ? `Understood document ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
                    : 'Starting local document intelligence'
                  : progress.stage === 'finalizing'
                    ? 'Consolidating document types and shared fields'
                    : 'Proposed setup is ready';
        const detail =
          activity?.detail ??
          (progress.stage === 'recognizing'
            ? `${progress.sourceName ?? 'Current page'}${progress.barcodeCount ? ` · ${counted(progress.barcodeCount, 'barcode')} found` : ''}`
            : progress.stage === 'understanding' && progress.documentTypes.length
              ? `Patterns: ${progress.documentTypes.slice(-3).join(', ')}`
              : (progress.sourceName ??
                `${progress.pageCount.toLocaleString()} pages · ${progress.documentCount.toLocaleString()} documents`));
        setFolderActivity((current) => {
          const entries = current[progress.jobId] ?? [];
          const signature = activity
            ? `${activity.id}:${activity.status}`
            : `${progress.stage}:${progress.completed}:${progress.total}:${progress.sourceName ?? ''}`;
          if (entries.at(-1)?.id === signature) return current;
          return {
            ...current,
            [progress.jobId]: [
              ...entries,
              {
                id: signature,
                stage: progress.stage,
                kind: activity?.kind ?? progress.stage,
                status: activity?.status ?? 'started',
                title,
                detail,
                at: Date.now(),
              },
            ].slice(-80),
          };
        });
        if (activity)
          setFolderWork((current) => {
            const tasks = current[progress.jobId] ?? [];
            const next = tasks.filter((task) => task.id !== activity.id);
            next.push({
              id: activity.id,
              kind: activity.kind,
              status: activity.status,
              label: activity.label,
              detail: activity.detail ?? '',
              pageNumber: activity.pageNumber,
              updatedAt: Date.now(),
              previewPath:
                activity.previewPath ?? progress.completedDocument?.pages[0]?.previewPath,
              tags: activity.tags ?? [
                ...new Set(
                  progress.completedDocument?.pages.flatMap((page) =>
                    page.barcodes.map((barcode) => barcode.symbology),
                  ) ?? [],
                ),
              ],
              document: progress.completedDocument,
              evidence: activity.evidence,
            });
            return {
              ...current,
              [progress.jobId]: next
                .sort((left, right) => right.updatedAt - left.updatedAt)
                .slice(0, 36),
            };
          });
      }),
    [],
  );

  const selectedJob = jobs.find((job) => job.id === selectedId) ?? jobs[0];
  useWorkspaceWindowTitle(selectedJob?.name, appVersion);
  // A restored renderer session does not pass through openJob: it can remember
  // "Documents" while main still owns a provisional representative proposal.
  // Derive the safe mode during render so CapturePreview never mounts for one
  // effect cycle and begins draining activation-gated captures before evidence
  // hydration has had a chance to return this job to Setup.
  const representativeSetupRequired =
    selectedJob?.status === 'provisional' && usesRepresentativeBootstrap(selectedJob);
  const effectiveJobMode: JobMode = representativeSetupRequired ? 'setup' : jobMode;
  // Job setup's unsaved draft (setup-draft.ts): held here until Save, never saved on its
  // own. The job list, the engine and the work screen behind setup keep the saved job.
  const [setupDrafts, setSetupDrafts] = useState<SetupDrafts<Job>>({});
  const setupJob = selectedJob
    ? jobWithSetupDraft(setupDrafts, selectedJob, effectiveJobMode)
    : undefined;
  useEffect(() => {
    setSetupDrafts((current) => draftsStillOpen(current, selectedJob, effectiveJobMode));
  }, [selectedJob?.id, selectedJob?.status, effectiveJobMode]);
  useLayoutEffect(() => {
    if (representativeSetupRequired && jobMode !== 'setup') setJobMode('setup');
  }, [representativeSetupRequired, jobMode]);
  // Product telemetry (ADR-0081): the area in front (telemetry-area.ts), from the screen and the job's mode as shown,
  // so a first setup's wait is setup, not the documents behind it.
  useTelemetryArea('screen', screen);
  useTelemetryArea('noJobs', !jobsLoading && !jobsLoadError && jobs.length === 0);
  // Unknown while the job list is still loading, so the screen behind it is not counted as a visit.
  useTelemetryArea('hasJob', jobsLoading ? null : Boolean(selectedJob));
  useTelemetryArea('jobMode', effectiveJobMode);
  useTelemetryArea('settingsSection', settingsSection);
  // Trial telemetry: the job whose documents are in front. Leaving them (another job, Scan jobs, Settings, Learning,
  // Job setup, New job) sends that visit's review summary (review-telemetry.ts).
  useEffect(() => {
    reviewInFront(
      screen === 'job' && !showCreate && selectedJob && effectiveJobMode === 'capture'
        ? selectedJob.id
        : null,
    );
  }, [screen, showCreate, selectedJob?.id, effectiveJobMode]);
  const refreshActiveWorkspace = useCallback(async (jobId: string) => {
    const api = window.capturePointDesktop;
    if (!api) return;
    const previous = activeBatchesRef.current[jobId];
    const preferredDocumentId = previous?.documents[0]?.id;
    const refreshed = await loadLatestWorkspaceBatch(jobId, preferredDocumentId);
    setActiveBatches((current) => {
      if (!refreshed) {
        if (!(jobId in current)) return current;
        const next = { ...current };
        delete next[jobId];
        return next;
      }
      return { ...current, [jobId]: refreshed };
    });
  }, []);
  // A background export has no renderer request to answer. While the job's
  // documents are on screen, poll the tiny overview and re-read the paged
  // projection only when it no longer matches. The overview itself is kept in
  // no state: nothing shows it, and storing a new object every 4 seconds
  // re-rendered the whole app (chunk 23 review, C2).
  useEffect(() => {
    const jobId = selectedJob?.id;
    const api = window.capturePointDesktop;
    if (!jobId || !api || screen !== 'job') return;
    let live = true;
    let checking = false;
    const check = async () => {
      if (!live || checking) return;
      checking = true;
      try {
        const overview = await api.getLatestBatchOverview(jobId);
        if (!live) return;
        const current = activeBatchesRef.current[jobId];
        const currentPages =
          current?.documents.reduce((sum, document) => sum + document.pages.length, 0) ?? 0;
        if (
          (overview?.batchId ?? '') !== (current?.batchId ?? '') ||
          (overview?.pageCount ?? 0) !== currentPages ||
          (overview?.documentCount ?? 0) !== (current?.documents.length ?? 0)
        )
          await refreshActiveWorkspace(jobId);
      } catch {
        // Opening the workspace owns engine failures. A background freshness
        // check stays quiet and asks again after the engine recovers.
      } finally {
        checking = false;
      }
    };
    void check();
    const timer = window.setInterval(() => void check(), 4_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [selectedJob?.id, screen, refreshActiveWorkspace]);
  useEffect(() => {
    if (!selectedId && jobs[0]) setSelectedId(jobs[0].id);
  }, [jobs, selectedId]);
  const updateJob = (next: Job) => {
    jobsRef.current = jobsRef.current.map((job) => (job.id === next.id ? next : job));
    setJobs((current) => current.map((job) => (job.id === next.id ? next : job)));
    jobSaveCoordinator.current?.update(next);
  };
  const commitJob = useCallback(async (next: Job) => {
    return await jobSaveCoordinator.current!.commit(next);
  }, []);
  // A job that does not exist yet has no save lane to order, and the host may name it: in Paige
  // the API stamps the workspace job id it created over the one the dialog invented (ADR-0010), so
  // the job the renderer keeps from here on is the one the answer carries, not the one it asked
  // with. The coordinator would refuse that as an identity mismatch, and it has nothing to order
  // for a first save, so a creation goes straight to the host and enters the lanes under its
  // durable id.
  const createJob = useCallback(async (next: Job): Promise<Job> => {
    const saved = await window.capturePointDesktop?.saveJob(next as StoredJobDefinition);
    return saved ? migrateJob(saved as Job) : next;
  }, []);
  /** A change saved outside Job setup (a rename, the learning stance) is carried into its open draft. */
  const carryIntoSetupDraft = (saved: Job | undefined, patch: (draft: Job, saved: Job) => Job) => {
    if (saved)
      setSetupDrafts((current) =>
        current[saved.id] ? { ...current, [saved.id]: patch(current[saved.id], saved) } : current,
      );
  };
  const renameJob = async (jobId: string, name: string) => {
    await renameSavedJob(jobId, name, jobsRef.current, commitJob);
    carryIntoSetupDraft(
      jobsRef.current.find((job) => job.id === jobId),
      (draft, saved) => ({ ...draft, name: saved.name }),
    );
  };
  /**
   * What JobWorkspace changes. Job setup's edits to a job in use are its draft, kept here
   * until Save (setup-draft.ts): saving them as they happened let an unsaved threshold
   * re-judge and release real documents. Everything else is saved as before.
   */
  const changeWorkspaceJob = (next: Job) => {
    const saved = jobsRef.current.find((job) => job.id === next.id);
    if (saved && holdsSetupDraft(effectiveJobMode, saved)) {
      setSetupDrafts((current) => draftAfterChange(current, saved, next));
      return;
    }
    updateJob(next);
  };
  /** JobWorkspace's saves: once saved, the draft is the saved job. */
  const commitWorkspaceJob = useCallback(
    async (next: Job) => {
      const saved = await commitJob(next);
      setSetupDrafts((current) => draftsWithout(current, saved.id));
      return saved;
    },
    [commitJob],
  );
  /**
   * A change saved on its own while Job setup's draft stays open (a lookup's fields as the lookup
   * is saved): applied to the saved job, and the draft takes the saved version so it doesn't read
   * the save as one of its own changes.
   */
  const saveAlongsideSetupDraft = async (jobId: string, change: (saved: Job) => Job) => {
    const latest = jobsRef.current.find((job) => job.id === jobId);
    if (!latest) return undefined;
    const saved = await commitJob(change(latest));
    carryIntoSetupDraft(saved, (draft) => ({
      ...draft,
      schemaVersion: saved.schemaVersion,
      updatedAt: saved.updatedAt,
    }));
    return saved;
  };
  const acceptBackgroundCaptureResult = useCallback(
    async (jobId: string, result: ProcessResult) => {
      let currentJob = jobsRef.current.find((candidate) => candidate.id === jobId);
      if (!currentJob) return;
      const proposed = applyBootstrapProposal(currentJob, result);
      if (proposed !== currentJob)
        currentJob = migrateJob(
          await commitJob({
            ...proposed,
            status: currentJob.status,
          }),
        );
      // No document editor is mounted while this owner is active. Every narrow
      // save admitted before navigation is held by the shared gate above, and main
      // returns the authoritative batch after applying those saves, so the result
      // can replace the shell projection without discarding live operator work.
      if (usesRepresentativeBootstrap(currentJob) && currentJob.status === 'provisional')
        setBootstrapSamples((current) => ({ ...current, [jobId]: result }));
      else setActiveBatches((current) => ({ ...current, [jobId]: result }));
      return currentJob;
    },
    [commitJob],
  );
  /**
   * A job, out to a file and back in as a new job: its setup AND what it learned.
   *
   * Both say what happened rather than that something happened: an operator handing a
   * job to another workstation has to be able to tell what travelled, in the screens'
   * own words (jobSetupTally). What it learned travels too but is not counted here:
   * layouts are an engine concept, not a user one. Where the export had to leave
   * something behind, the warning follows the tally.
   */
  const exportJobSetup = async (job: Job) => {
    // From the moment the file is chosen until it is written: never a silent wait.
    const stopWatching = window.capturePointDesktop?.onJobSetupFileWorking?.((work) => {
      if (work.action !== 'download') return;
      setToast('');
      setWorkingNotice(`Downloading job setup for ${job.name}…`);
    });
    try {
      const written = await window.capturePointDesktop?.exportJobTemplate({
        jobId: job.id,
        jobName: job.name,
      });
      if (!written) return;
      const carried = `Downloaded job setup for ${written.jobName} - ${jobSetupTally(written)}.`;
      setToast(written.warnings.length > 0 ? `${carried} ${written.warnings.join(' ')}` : carried);
    } catch (reason: unknown) {
      setToast(
        messageFor(reason, 'The job setup could not be downloaded'),
        'job-setup-download-failed',
      );
    } finally {
      stopWatching?.();
      setWorkingNotice('');
    }
  };
  const importJobSetup = () => setShowJobImport(true);
  /**
   * "Try with sample documents": a job labelled as a sample, opened with the
   * simulated scanner on the chosen set. startSampleJob imports the set's
   * pre-trained job setup when one ships (build item 6); today none does, so it
   * creates an ordinary new job.
   */
  const startSampleSet = async (
    choice: SampleSetChoice,
    tour?: { documents: string[]; from?: TourOrigin; entry?: TourStartFrom; again?: boolean },
  ) => {
    const desktop = window.capturePointDesktop;
    if (!desktop) throw new Error('Sample documents are unavailable in this build.');
    // Fine-tunes still waiting for the sample reader download: the job reads with the standard reader until then.
    let readerPending = false;
    const started = await startSampleJob<Job>(choice.corpus, {
      label: choice.label,
      existingNames: jobsRef.current.map((job) => job.name),
      newId: () => crypto.randomUUID(),
      now: () => new Date().toISOString(),
      importPretrained: async (corpus) => {
        const imported = await desktop.importSampleJobSetup(corpus, tour ? 'tour' : undefined);
        readerPending = (imported?.pendingLayoutReaderModelCount ?? 0) > 0;
        return imported ? migrateJob(imported.job as Job) : null;
      },
      createJob: async (draft) => migrateJob(await commitJob(draft as unknown as Job)),
    });
    const durable = started.job;
    rememberSampleJobSet(durable.id, started.corpus);
    setShowSampleJobs(false);
    setJobs((current) => [durable, ...current.filter((item) => item.id !== durable.id)]);
    jobsRef.current = [durable, ...jobsRef.current.filter((item) => item.id !== durable.id)];
    if (tour) {
      // The tour's job is not the operator's first job; the tour card speaks for it, so no notice.
      // Its export folder is picked before anything is read, so Export is one click and
      // nothing leaves until that click (owner, 2026-10-04: the folder is preset for the tour).
      // A tour that cannot start leaves no half-made job behind; startTour says why.
      try {
        await presetTourExport(durable.id, durable.name);
      } catch (reason) {
        void removeTourJob(durable);
        throw reason;
      }
      writeTour({
        jobId: durable.id,
        step: 'pane',
        startedAt: new Date().toISOString(),
        ...(tour.from ? { from: tour.from } : {}),
      });
      // Sent once the tour's job exists, so main knows it as the tour's (funnel tour_started).
      tourTelemetry.started(tour.entry ?? 'other', {
        jobId: durable.id,
        again: Boolean(tour.again),
      });
      requestSampleScan(durable.id, started.corpus, tour.documents);
      openJob(durable.id);
      return;
    }
    noteMilestone('firstJob');
    requestSampleScan(durable.id, started.corpus);
    openJob(durable.id);
    toastAfterMove(
      sampleJobReadyNotice(durable.name, { pretrained: started.pretrained, readerPending }),
    );
  };
  /* The tour (owner decisions, 2026-10-03/04): the ready-made Accounts Payable job on five clean
     documents, one of each type, with the tour card. */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const startTour = async (entry: TourStartFrom) => {
    const running = readTour();
    // Watched one before (to its end or part-way), read before a running tour is ended below.
    const again = readTourFlag(TOUR_WATCHED_KEY);
    // A tour under way is resumed. A finished one (its last step has no Leave tour) makes way
    // for a fresh tour, and its job goes, as when leaving. A tour on their own job just ends.
    if (
      running &&
      !running.own &&
      running.step !== 'turn' &&
      jobsRef.current.some((job) => job.id === running.jobId)
    ) {
      openJob(running.jobId);
      return;
    }
    // Where they are now is where Leave tour brings them back (Brandy, 2026-10-05): the open job
    // (never a tour job on its way out) with its open document, or Scan jobs when that is in front.
    const removing = readTourRemovals();
    const here = jobsRef.current.find(
      (job) =>
        job.id === selectedIdRef.current && job.id !== running?.jobId && !removing.includes(job.id),
    );
    const from = tourOriginOf(here?.id, screen);
    // The window opens on the click (owner, 2026-10-04: five seconds of nothing read as broken).
    noteTourClicked();
    setTourStarting(true);
    if (running) await endTourRef.current(false);
    // How long its sample reader download took, if one was needed (tour.ready says so).
    const stopTiming = tourTelemetry.watchReaderDownload(
      TOUR_CORPUS,
      window.capturePointDesktop?.onSampleReaderDownload,
    );
    try {
      await startSampleSet(
        {
          corpus: TOUR_CORPUS,
          label: 'Accounts Payable',
          documentCount: TOUR_DOCUMENTS.length,
          description: '',
        },
        { documents: TOUR_DOCUMENTS, from, entry, again },
      );
    } catch (reason) {
      // To where they are now: ending the earlier tour removed its job and moved the workspace,
      // and a notice for the visit it started from was dropped (Watch the tour again did nothing).
      toastToCurrentVisit(
        reason instanceof Error ? reason.message : 'The tour could not start',
        'tour-could-not-start',
      );
    } finally {
      stopTiming();
      setTourStarting(false);
    }
  };
  /* Leaving the tour, or making their own job, removes the tour's job (owner, 2026-10-03).
     Leave tour (`goBack`) also returns to where the tour was started (Brandy, 2026-10-05). */
  const endTour = async (goBack: boolean) => {
    const tour = readTour();
    writeTour(null);
    // Left part-way, once it had begun: the tour is offered again as "Watch the tour again".
    if (tour?.ready) markTourFlag(TOUR_WATCHED_KEY);
    // A tour on their own job leaves their job exactly as it is.
    if (tour?.own) return;
    const tourJob = tour ? jobsRef.current.find((job) => job.id === tour.jobId) : undefined;
    // Back first, then the tour's job goes. Removing it first sent the workspace on a detour: a
    // job screen whose job vanishes falls back to Scan jobs, and New job opened over that.
    if (goBack)
      returnFromTour(
        tourReturn(
          tour?.from,
          jobsRef.current.filter((job) => job.id !== tour?.jobId).map((job) => job.id),
        ),
      );
    if (tourJob) void removeTourJob(tourJob);
  };
  /* Where Leave tour lands: the job the tour was started from, on the document that was open
     there, or Scan jobs if that was in front. With no job to go back to (the welcome), My own
     documents' name step, as before. */
  const returnFromTour = (back: TourReturn) => {
    if (back.to === 'new-job') {
      openNewJob('scan');
      return;
    }
    if (back.to === 'jobs') {
      setSelectedId(back.jobId);
      setScreen('jobs');
      return;
    }
    requestTourReturn(back.documentId ? { jobId: back.jobId, documentId: back.documentId } : null);
    openJob(back.jobId);
  };
  /* The tour's job leaves every list at once. An exported job keeps recovery copies, and the
     engine refuses its delete until cleanup has removed them ("…then delete the job again";
     about two minutes, measured October 4), so it asks again in the background. The tour's
     exported files stay where they went; only Paige's own copies go. */
  const removeTourJob = async (tourJob: Job) => {
    markTourRemoval(tourJob.id, true);
    jobsRef.current = jobsRef.current.filter((job) => job.id !== tourJob.id);
    setJobs((current) => current.filter((job) => job.id !== tourJob.id));
    // Right after its export the engine can also refuse for a moment (the export settling), so
    // any refusal is asked again while the job is still there, for up to ten minutes.
    for (let attempt = 1; ; attempt += 1) {
      try {
        await deleteJobEverywhere(tourJob);
        break;
      } catch (reason) {
        const message = reason instanceof Error ? reason.message : String(reason);
        console.warn(`Tour job removal, try ${attempt}: ${message}`);
        const stillThere =
          (await window.capturePointDesktop?.listJobs().catch(() => null))?.some(
            (job) => job.id === tourJob.id,
          ) ?? true;
        if (!stillThere) break;
        if (attempt < 60) {
          await new Promise((done) => window.setTimeout(done, 10_000));
          continue;
        }
        setToast(message || `${tourJob.name} could not be removed`, 'job-delete-failed');
        break;
      }
    }
    markTourRemoval(tourJob.id, false);
  };
  const removeTourJobRef = useRef(removeTourJob);
  removeTourJobRef.current = removeTourJob;
  // A restart picks up any tour job still on its way out.
  useEffect(() => {
    const removing = readTourRemovals();
    if (!removing.length) return;
    void (async () => {
      const stored = (await window.capturePointDesktop?.listJobs()) ?? [];
      for (const id of removing) {
        const job = stored.find((item) => item.id === id);
        if (job) void removeTourJobRef.current(migrateJob(job as Job));
        else markTourRemoval(id, false);
      }
    })();
  }, []);
  const endTourRef = useRef(endTour);
  endTourRef.current = endTour;
  const startTourRef = useRef(startTour);
  startTourRef.current = startTour;
  useEffect(() => {
    // The card's Leave tour, and its Start my own job, are heard here, where jobs are made and
    // removed. Start my own job opens My own documents; the tour ends when that job is made.
    const leave = () => void endTourRef.current(true);
    const ownJob = () => openNewJob('scan');
    // Settings › Appearance › Take the tour (beside Show hints), or a capturepoint://tour link, which says so.
    const takeTour = (event: Event) =>
      void startTourRef.current(
        (event as CustomEvent<{ from?: string } | null>).detail?.from === 'link'
          ? 'link'
          : 'settings',
      );
    window.addEventListener(TOUR_LEAVE_EVENT, leave);
    window.addEventListener(TOUR_OWN_JOB_EVENT, ownJob);
    window.addEventListener(TAKE_TOUR_EVENT, takeTour);
    return () => {
      window.removeEventListener(TOUR_LEAVE_EVENT, leave);
      window.removeEventListener(TOUR_OWN_JOB_EVENT, ownJob);
      window.removeEventListener(TAKE_TOUR_EVENT, takeTour);
    };
  }, []);
  const followAppLinkRef = useRef<(route: AppLinkRoute) => void>(() => undefined);
  // capturepoint:// links from a web page or email (docs/product/app-links.md), followed only
  // once the jobs are loaded, so the tour finds a tour already under way and resumes it.
  useEffect(() => {
    if (jobsLoading) return;
    return window.capturePointDesktop?.onAppLink((route) => {
      const event = appLinkWindowEvent(route);
      // The same event the app's own button raises, marked as a link's (the tour reports where it was started).
      if (event) window.dispatchEvent(new CustomEvent(event, { detail: { from: 'link' } }));
      // A buy that follows a link to License started from the link (checkout.start from).
      if (route === 'settings/license') noteLicenseFrom('link');
      // A place in a job: the window picks the job, or asks which (and any other link closes that question).
      followAppLinkRef.current(route);
    });
  }, [jobsLoading]);
  useEffect(() => {
    if (!jobsLoading) refreshUnfinishedImports();
  }, [jobsLoading, refreshUnfinishedImports]);
  /** Saved job setup in Import job setup. A failure is shown in that window, which stays open. */
  const importJobSetupFile = async () => {
    const created = await window.capturePointDesktop?.importJobTemplate();
    if (!created) return;
    const durable = migrateJob(created.job as Job);
    setShowJobImport(false);
    setJobs((current) => [durable, ...current.filter((job) => job.id !== durable.id)]);
    jobsRef.current = [durable, ...jobsRef.current.filter((job) => job.id !== durable.id)];
    // The job is complete when the import returns, so it opens like any new job (owner, 2026-10-10:
    // staying on the previous job's documents made them look like the new job's).
    openJob(durable.id);
    noteMilestone('firstJob');
    toastAfterMove(`Created ${durable.name} - ${jobSetupTally(created)}`);
  };
  /** Deletes a job in the engine and forgets everything this window held for it. */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const deleteJobEverywhere = async (target: Job) => {
    const coordinator = jobSaveCoordinator.current!;
    // A whole-job save is an upsert. Close this job's write lane and
    // wait for any request already across IPC before asking the engine
    // to delete, or a late autosave can silently recreate the row.
    await coordinator.retire(target.id);
    try {
      const desktop = window.capturePointDesktop;
      if (!desktop) throw new Error('Paige could not reach its local document engine.');
      await desktop.deleteJob({ jobId: target.id, confirmationName: target.name });
    } catch (reason) {
      // A live export or accepted destination setup may correctly refuse
      // deletion. Reopen autosave and republish the latest UI snapshot;
      // retiring canceled its debounce while the decision was pending.
      // Republish the job as it stands now, not the click-time copy: a
      // rename or proposal committed meanwhile must not be saved over.
      coordinator.resume(target.id);
      coordinator.update(jobsRef.current.find((job) => job.id === target.id) ?? target);
      throw reason;
    }
    coordinator.completeRetire(target.id);
    jobSaveGateOwners.current.get(target.id)?.retireAfterSettled();
    jobSaveGateOwners.current.delete(target.id);
    // A deleted job leaves no break sheet draft behind. The dialog is never
    // opened again for this job, so nothing else would ever clear it.
    clearBreakSheetDraft(window.localStorage, target.id);
    clearRepresentativeAutoPreference(window.localStorage, target.id);
    jobsRef.current = jobsRef.current.filter((job) => job.id !== target.id);
    setJobs((current) => current.filter((job) => job.id !== target.id));
    const forget = <T,>(current: Record<string, T>) => {
      const next = { ...current };
      delete next[target.id];
      return next;
    };
    setActiveBatches(forget);
    setBootstrapSamples(forget);
    setFolderStatuses(forget);
    setFolderProgress(forget);
    setFolderActivity(forget);
    setFolderWork(forget);
    forgetReadyTypes(target.id);
  };
  /* Leaving New job puts the operator back where it was opened (owner decision September 24,
     2026). Creating the job used to be a point of no return: its empty start screen's Back
     reopened the name step, and the close and Back there both left the empty job behind, so
     a new job could not be cancelled (owner, 2026-10-03). The job New job just made is
     remembered, with where New job was opened, until it has pages or the operator moves to
     another job; leaving New job while it is still empty discards it and goes back. */
  // The fresh job is an ordinary job once it has pages or the operator moves to another job.
  useEffect(() => {
    if (!freshJob) return;
    if (
      (selectedId !== freshJob.jobId && !showCreate) ||
      (activeBatches[freshJob.jobId]?.documents.length ?? 0) > 0
    )
      setFreshJob(null);
  }, [freshJob, selectedId, showCreate, activeBatches]);
  const discardFreshJob = async (land: boolean) => {
    const fresh = freshJob;
    setFreshJob(null);
    const target = fresh ? jobsRef.current.find((job) => job.id === fresh.jobId) : undefined;
    if (!fresh || !target || (activeBatches[target.id]?.documents.length ?? 0) > 0) return;
    try {
      await deleteJobEverywhere(target);
    } catch (reason) {
      setToast(
        reason instanceof Error ? reason.message : `${target.name} could not be removed`,
        'job-delete-failed',
      );
      return;
    }
    if (!land) return;
    const origin = fresh.origin;
    const back =
      origin.jobId && jobsRef.current.some((job) => job.id === origin.jobId)
        ? origin.jobId
        : (jobsRef.current[0]?.id ?? '');
    if (origin.screen === 'job' && back) openJob(back);
    else {
      setSelectedId(back);
      setScreen('jobs');
    }
  };
  const openJob = (id: string) => {
    // PAIGE (docs/engineering/workbench-port.md): another job is another Paige route; this
    // route's channel names only its own job, so switching in place was refused on every call.
    if (window.paigeWorkbench?.openJob && id !== selectedIdRef.current)
      return window.paigeWorkbench.openJob(id);
    setSelectedId(id);
    const target = jobsRef.current.find((job) => job.id === id);
    setJobMode(
      target?.status === 'provisional' &&
        (target.bootstrapMode === 'manual' || usesRepresentativeBootstrap(target))
        ? 'setup'
        : 'capture',
    );
    setScreen('job');
  };
  // PAIGE (docs/runbooks/exports.md, "Export history"): Send back for review opens that document on the job's
  // documents, the way Leave tour comes back to one (requestTourReturn, answered once its batch is on screen).
  const openJobRef = useRef(openJob);
  openJobRef.current = openJob;
  useEffect(() => {
    const open = (event: Event) => {
      const documentId = (event as CustomEvent<unknown>).detail;
      const jobId = selectedIdRef.current;
      if (typeof documentId !== 'string' || !jobId) return;
      requestTourReturn({ jobId, documentId });
      openJobRef.current(jobId);
    };
    window.addEventListener(OPEN_DOCUMENT_EVENT, open);
    return () => window.removeEventListener(OPEN_DOCUMENT_EVENT, open);
  }, []);
  // PAIGE (docs/engineering/workbench-port.md): a job's route opens its job as openJob does. The
  // desktop's new-job dialog opened the job it made; Paige's opens it on its own route, where the
  // App starts on Documents, so "Build my own setup" landed on the start screen, not Job setup.
  const routeJobOpened = useRef(false);
  useEffect(() => {
    if (routeJobOpened.current || jobsLoading || !window.paigeWorkbench?.openJob) return;
    routeJobOpened.current = true;
    const target = jobs.find((job) => job.id === selectedIdRef.current);
    if (
      target?.status === 'provisional' &&
      (target.bootstrapMode === 'manual' || usesRepresentativeBootstrap(target))
    )
      setJobMode('setup');
  }, [jobsLoading, jobs]);

  const representativeEvidenceRequests = useRef(new Set<string>());
  const representativeResumeAttempts = useRef(new Set<string>());
  // Jobs whose setup stopped with an error in front of the person this session. What main saved is
  // offered as Resume; this session never resumes it by itself, so a computer that just failed under
  // load is not pushed straight back in (it used to, saying "Continuing the setup that was running
  // when Paige closed"). Starting a setup again clears it.
  const setupsStoppedThisSession = useRef(new Set<string>());
  /** First in every setup catch, before anything is awaited: a status read in between must not resume it. */
  const noteSetupStopped = (jobId: string) => {
    setupsStoppedThisSession.current.add(jobId);
  };
  const noteSetupStarting = (jobId: string) => {
    setupsStoppedThisSession.current.delete(jobId);
  };
  // A setup this window continues by itself after a restart, until its call and what follows it are done. Stopped,
  // its Try again or Start over waits for that, as for a setup the window started (JobWorkspace, October 7, 2026).
  const continuingSetups = useRef(new Map<string, Promise<void>>());
  const hydrateRepresentativeEvidence = useCallback(
    async (jobId: string) => {
      const api = window.capturePointDesktop;
      if (!api || representativeEvidenceRequests.current.has(jobId)) return;
      representativeEvidenceRequests.current.add(jobId);
      let continuing: { done: Promise<void>; release: () => void } | null = null;
      try {
        let evidence = await api.getFolderAnalysisEvidence(jobId);
        if (evidence.state === 'recoverable' && evidence.operationId) {
          const resumeKey = `${jobId}|${evidence.operationId}`;
          if (
            !representativeResumeAttempts.current.has(resumeKey) &&
            !setupsStoppedThisSession.current.has(jobId)
          ) {
            representativeResumeAttempts.current.add(resumeKey);
            let release = () => {};
            continuing = {
              done: new Promise<void>((resolve) => {
                release = resolve;
              }),
              release: () => release(),
            };
            continuingSetups.current.set(jobId, continuing.done);
            // Reopening lands on the same job because its setup was saved mid-run and continues
            // here; say so, so a reopened app is not mistaken for one that never closed.
            if (selectedIdRef.current === jobId)
              setToast('Continuing the setup that was running when Paige closed');
            setFolderStatuses((current) => ({
              ...current,
              [jobId]: { ...evidence, state: 'running', running: true, failure: null },
            }));
            try {
              await api.resumeFolderAnalysis(jobId);
            } catch (reason) {
              // Held first: a continuation that failed is never continued again by itself, even
              // when it saved a new remainder under another operation id.
              noteSetupStopped(jobId);
              // The durable state below is authoritative. A native replacement
              // can fail after saving another resumable checkpoint, while a bad
              // request becomes a durable failed state instead.
              reportSetupFailure(jobId, reason instanceof Error ? reason.message : '');
            }
            evidence = await api.getFolderAnalysisEvidence(jobId);
          }
        }
        const { result, previousResult, progress, ...status } = evidence;
        if (progress) setFolderProgress((current) => ({ ...current, [jobId]: progress }));
        if (status.state === 'ready')
          setRepresentativeReconciliations((current) => ({ ...current, [jobId]: true }));
        setFolderStatuses((current) => ({ ...current, [jobId]: status }));
        // A continuation deliberately leaves its previous manifest readable while
        // native work runs. It is evidence, but it is not the answer to the current
        // operation. Keep polling until the main process publishes and closes that
        // identified operation; otherwise a reload silently reopens stale setup.
        if (status.state === 'running') return;
        if (status.state === 'failed' || status.state === 'conflicted') {
          setBootstrapFailures((current) => ({
            ...current,
            [jobId]:
              status.failure ??
              'Document setup analysis stopped before it could publish a proposal.',
          }));
          return;
        }
        if (status.state === 'recoverable') {
          // A failure that already explains this stop (the engine stopped mid-setup) stays.
          setBootstrapFailures((current) => ({
            ...current,
            [jobId]:
              current[jobId] ??
              'Paige kept this document setup safe, but could not resume it automatically in this session. Resume to continue from where it stopped.',
          }));
          return;
        }
        if (status.state === 'idle') return;
        if (status.state === 'no-proposal') {
          setBootstrapFailures((current) => ({
            ...current,
            [jobId]:
              'Paige finished reading these documents but could not identify a reusable document-type and field setup. Choose a more varied set, or start scanning and teach this job as you work.',
          }));
          return;
        }
        if (!result) {
          setBootstrapFailures((current) => ({
            ...current,
            [jobId]:
              'Paige found the completed setup session, but its proposal could not be reopened. Choose the documents and try again.',
          }));
          return;
        }
        requireRepresentativeProposal(result);

        setBootstrapSamples((current) => ({ ...current, [jobId]: result }));
        let currentJob = jobsRef.current.find((candidate) => candidate.id === jobId);
        if (!currentJob) return;
        // The native result is durable before the renderer receives it. If that
        // renderer reloads between those two moments, no callback remains to apply
        // the proposal. Rebuild the exact same proposal transaction from durable
        // evidence; the save coordinator serializes it behind any newer job edit.
        if (
          currentJob.status === 'provisional' &&
          usesRepresentativeBootstrap(currentJob) &&
          (currentJob.documentTypes.length === 0 ||
            previousResult?.bootstrapProposal?.documentTypes.length)
        ) {
          const reconciled = applyBootstrapProposal(
            currentJob,
            result,
            previousResult?.bootstrapProposal,
          );
          // A continuation remains durable after it has been reconciled so a
          // later renderer can still recover it. Replaying that evidence must be
          // idempotent: do not create another job revision on every reload when
          // the exact proposal is already present.
          if (!sameSerializableValue(currentJob, reconciled))
            currentJob = migrateJob(await commitJob(reconciled));
        }
        setBootstrapFailures((current) => {
          if (!(jobId in current)) return current;
          const next = { ...current };
          delete next[jobId];
          return next;
        });
        // Evidence remains useful on active jobs, but only an unfinished proposal
        // is Setup navigation. The current-id check prevents a late job A result
        // from moving job B while the operator is working in it.
        if (currentJob.status === 'provisional' && selectedIdRef.current === jobId)
          setJobMode('setup');
      } catch (reason) {
        setBootstrapFailures((current) => ({
          ...current,
          [jobId]: messageFor(reason, "Paige could not reopen this job's setup analysis."),
        }));
      } finally {
        representativeEvidenceRequests.current.delete(jobId);
        if (continuing) {
          if (continuingSetups.current.get(jobId) === continuing.done)
            continuingSetups.current.delete(jobId);
          continuing.release();
        }
        setRepresentativeReconciliations((current) => {
          if (!(jobId in current)) return current;
          const next = { ...current };
          delete next[jobId];
          return next;
        });
      }
    },
    [commitJob],
  );

  /**
   * Own a representative first-job pass above the workspace that starts it.
   *
   * Persisting `folder`/`sample-scan` deliberately replaces CapturePreview
   * with Setup before the native operation starts. The operation, its visible
   * starting state, and any pre-operation rejection therefore belong here in
   * the shell. A child-owned catch would run after that child unmounted and
   * strand the operator in an empty Setup screen with no failure or retry.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const runRepresentativeSourceBootstrap = async (startingJob: Job, source: 'files' | 'folder') => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    const jobId = startingJob.id;
    let activityStarted = false;
    try {
      const selection =
        source === 'folder'
          ? await desktop.chooseDocumentFolder()
          : await desktop.chooseRepresentativeFiles();
      // Canceling the native picker is a decision, not a failed analysis. Keep
      // any existing retry explanation in place and do not flash progress.
      if (selection.canceled || !selection.token) return;
      noteSetupFiles(jobId, selection.supportedFileCount ?? 0);
      noteSetupStarting(jobId);
      // A new run (Choose folder and retry after a failure, too): it starts from nothing, not
      // on the failed run's step, page or results.
      forgetSetupRun(jobId);

      setBootstrapFailures((current) => {
        if (!(jobId in current)) return current;
        const next = { ...current };
        delete next[jobId];
        return next;
      });
      setBootstrapActivity((current) => ({
        ...current,
        [jobId]: 'Learning from representative documents',
      }));
      setFolderActivity((current) => ({
        ...current,
        [jobId]: [
          {
            id: `starting:${Date.now()}`,
            stage: 'starting',
            kind: 'source',
            status: 'started',
            title:
              source === 'folder'
                ? 'Opening representative folder'
                : 'Opening representative documents',
            detail: 'Selecting a balanced sample for local analysis',
            at: Date.now(),
          },
        ],
      }));
      activityStarted = true;
      const representativeMode: Job['bootstrapMode'] =
        source === 'folder' ? 'folder' : 'sample-scan';
      let sampleJob = migrateJob(
        await commitJob({
          ...startingJob,
          bootstrapMode: representativeMode,
          description:
            source === 'folder'
              ? 'Proposed from a folder of representative documents'
              : 'Built from representative files, then applied to the full selected set',
          updatedAt: new Date().toISOString(),
        }),
      );
      updateJob(sampleJob);
      const result = await desktop.importFolderDocuments({
        jobId,
        token: selection.token,
        documentTypes: [],
        separationMode: sampleJob.separationMode,
        separationBarcodeText: sampleJob.separationBarcodeText,
        boundaryOpeningProfiles: sampleJob.boundaryOpeningProfiles,
        boundaryContinuationProfiles: sampleJob.boundaryContinuationProfiles,
      });
      requireRepresentativeProposal(result);
      // The pass can outlive a renderer visit. Merge into the latest committed
      // job snapshot rather than restoring the click-time one over newer setup.
      sampleJob = jobsRef.current.find((candidate) => candidate.id === jobId) ?? sampleJob;
      const proposed = applyBootstrapProposal(sampleJob, result);
      const saved = migrateJob(await commitJob(proposed));
      // Only while the proposal is still the published job. A commit is published when it is admitted
      // (onCommitAdmitted), so the automatic Save and use can activate the job while this save is out;
      // publishing (and autosaving) this answer after it put the job back to provisional and setup never
      // ended (found by Paige v2's arrived-setup journey).
      const published = jobsRef.current.find((candidate) => candidate.id === jobId);
      if (!published || published === proposed || published.schemaVersion === saved.schemaVersion)
        updateJob(saved);
      setBootstrapSamples((current) => ({ ...current, [jobId]: result }));
      // The direct result above is already validated and committed. Hydration
      // reconciles durable status before clearing the live progress; a later status-read problem must
      // not relabel this successful native operation as a preflight failure.
      await hydrateRepresentativeEvidence(jobId);
      if (selectedIdRef.current === jobId) {
        setJobMode('setup');
        setToast(
          `Found ${saved.documentTypes.length} provisional document type${saved.documentTypes.length === 1 ? '' : 's'}`,
        );
      }
    } catch (reason) {
      noteSetupStopped(jobId);
      const raw = messageFor(reason, 'The selected documents could not be analyzed');
      // What main saved decides what is offered (owner, 2026-09-29: the engine died mid-setup and
      // the only choice was "Choose folder and retry", which main then refused because the setup
      // was saved to resume). A saved place is offered as Resume; this session does not resume
      // it by itself (noteSetupStopped, above), so a machine that just failed under load is not
      // pushed straight back in.
      const status = await desktop.getFolderAnalysisStatus(jobId).catch(() => null);
      const resumable = status?.state === 'recoverable' && Boolean(status.operationId);
      if (status) setFolderStatuses((current) => ({ ...current, [jobId]: status }));
      const message = setupFailureMessage(raw, status);
      setBootstrapFailures((current) => ({ ...current, [jobId]: message }));
      const failed = activityStarted && reportSetupFailure(jobId, raw, status?.failure);
      // A stop the person made is a plain notice, whatever "stopped" reads as.
      if (selectedIdRef.current === jobId)
        setToast(
          resumable ? 'Setup stopped. Your place is saved.' : 'Document setup analysis stopped',
          failed || !activityStarted ? 'setup-failed' : 'notice',
        );
    } finally {
      if (activityStarted) {
        setBootstrapActivity((current) => {
          if (!(jobId in current)) return current;
          const next = { ...current };
          delete next[jobId];
          return next;
        });
        setFolderProgress((current) => {
          if (!(jobId in current)) return current;
          const next = { ...current };
          delete next[jobId];
          return next;
        });
      }
    }
  };

  useEffect(() => {
    if (!selectedJob || !usesRepresentativeBootstrap(selectedJob)) return;
    const jobId = selectedJob.id;
    const status = folderStatuses[jobId];
    const sampleAvailable = Boolean(bootstrapSamples[jobId]);
    if (status?.state === 'running') {
      void hydrateRepresentativeEvidence(jobId);
      // While it runs, ask main only for the small status every second. The evidence (the whole
      // manifest and sample results) is read again only once the status says something new: the
      // run ended, or another operation or evidence revision took its place.
      const desktop = window.capturePointDesktop;
      if (!desktop) return;
      let live = true;
      const timer = window.setInterval(
        () =>
          void desktop
            .getFolderAnalysisStatus(jobId)
            .then((next) => {
              if (!live) return;
              if (
                next.state !== 'running' ||
                next.operationId !== status.operationId ||
                next.evidenceRevision !== status.evidenceRevision
              ) {
                void hydrateRepresentativeEvidence(jobId);
                return;
              }
              setFolderStatuses((current) =>
                current[jobId] && sameSerializableValue(current[jobId], next)
                  ? current
                  : { ...current, [jobId]: next },
              );
            })
            .catch(() => undefined),
        1_000,
      );
      return () => {
        live = false;
        window.clearInterval(timer);
      };
    }
    if (status?.state === 'recoverable') {
      void hydrateRepresentativeEvidence(jobId);
      return;
    }
    if (status?.state === 'ready' && sampleAvailable) return;
    // Idle, failed, conflicted and no-proposal are terminal until the operator retries. An
    // undefined status has not been checked yet, while ready without a renderer
    // sample is a durable proposal waiting to be reconciled.
    if (status && status.state !== 'ready') return;
    void hydrateRepresentativeEvidence(jobId);
  }, [
    selectedJob?.id,
    selectedJob?.bootstrapMode,
    selectedJob?.status,
    selectedJob?.documentTypes.length,
    folderStatuses[selectedJob?.id ?? '']?.state,
    folderStatuses[selectedJob?.id ?? '']?.operationId,
    folderStatuses[selectedJob?.id ?? '']?.evidenceRevision,
    Boolean(bootstrapSamples[selectedJob?.id ?? '']),
    hydrateRepresentativeEvidence,
  ]);
  // A setup this window starts (a new job's first setup, choosing the documents again, Read more) runs in main
  // before this window's copy of its status says so: that copy was read before main wrote the setup's operation
  // (idle for a new job, else the last setup's ready or failed), and only a running copy is kept fresh above. Until
  // main reports the new setup running, ask it every second. Stop setup names the operation it stops, so without
  // this it appeared only after the window was reloaded (October 7, 2026).
  const startingSetupJobId =
    selectedJob &&
    bootstrapActivity[selectedJob.id] &&
    folderStatuses[selectedJob.id]?.state !== 'running'
      ? selectedJob.id
      : null;
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!startingSetupJobId || !desktop) return;
    let live = true;
    const check = () =>
      void desktop
        .getFolderAnalysisStatus(startingSetupJobId)
        .then((status) => {
          if (live && status.state === 'running')
            setFolderStatuses((current) =>
              current[startingSetupJobId]?.state === 'running'
                ? current
                : { ...current, [startingSetupJobId]: status },
            );
        })
        .catch(() => undefined);
    check();
    const timer = window.setInterval(check, 1_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [startingSetupJobId]);

  // Keep feedback available until replaced/dismissed; do not lose errors to a timer.

  // Follow the auto-updater so the operator sees a download arrive and can choose
  // to install it now, instead of it applying silently on the next quit.
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    desktop
      .getUpdateStatus()
      .then(setUpdateStatus)
      .catch(() => undefined);
    return desktop.onUpdateStatusChanged(setUpdateStatus);
  }, []);

  // A machine asking to join the cluster, said where the operator actually is.
  //
  // The request card lives on the cluster screen, but the person who walks to
  // the second machine and presses Join is standing at that machine — the one
  // who can admit it is at this one, on whatever screen their work has them on.
  // The main process already broadcasts every cluster change; this listens from
  // the shell so the ask reaches a toast with a way there, instead of waiting
  // to be discovered. The ask expires in ten minutes — a security bound, not a
  // courtesy — so waiting is losing.
  const [joinAsk, setJoinAsk] = useState<{ name: string; cluster: string } | null>(null);
  const knownJoinAsks = useRef<Set<string>>(new Set());
  const screenRef = useRef(screen);
  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !SHARED_PROCESSING_ENABLED) return;
    const digest = (view: ClusterView) => {
      // Toast once per request, and never while the cluster screen is already
      // up — the card itself is showing there, larger and with the buttons.
      const fresh = view.requests.filter((request) => !knownJoinAsks.current.has(request.id));
      knownJoinAsks.current = new Set(view.requests.map((request) => request.id));
      if (fresh.length > 0 && view.cluster && screenRef.current !== 'cluster')
        setJoinAsk({ name: fresh[0].name, cluster: view.cluster.name });
    };
    desktop
      .getCluster()
      .then(digest)
      .catch(() => undefined);
    return desktop.onClusterChanged(digest);
  }, []);
  useEffect(() => {
    if (!joinAsk) return;
    // Longer than the plain toast: this one carries a button, and a strip that
    // vanishes while the mouse is on its way to it teaches people not to try.
    const timer = window.setTimeout(() => setJoinAsk(null), 8000);
    return () => window.clearTimeout(timer);
  }, [joinAsk]);

  // Native scheduling owns the clock and saved preferences; this only edits and observes.
  const [learningSchedule, setLearningSchedule] = useState<LearningSchedule>(() =>
    readLearningSchedule(localStorage),
  );
  const [scheduledLearning, setScheduledLearning] = useState<ScheduledLearningRun | null>(null);
  const [learningScheduleNotice, setLearningScheduleNotice] = useState<string | null>(
    'Loading learning schedule…',
  );
  const [learningScheduleReady, setLearningScheduleReady] = useState(false);
  const [learningScheduleSaving, setLearningScheduleSaving] = useState(false);
  const scheduleSaving = useRef(false);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api) return;
    let disposed = false,
      polling = false;
    const refresh = async (initialize = false) => {
      if (polling || scheduleSaving.current) return;
      polling = true;
      try {
        const state = initialize
          ? await api.initializeLearningSchedule(readLearningSchedule(localStorage))
          : await api.learningScheduleState();
        if (disposed || scheduleSaving.current) return;
        if (state.schedule) {
          setLearningSchedule(state.schedule);
          setLearningScheduleReady(true);
        }
        setScheduledLearning(state.run);
        setLearningScheduleNotice(state.hold);
      } catch (error) {
        if (!disposed)
          setLearningScheduleNotice(
            `Learning schedule could not be read: ${messageFor(error, 'unknown error')}`,
          );
      } finally {
        polling = false;
      }
    };
    void refresh(true);
    const timer = window.setInterval(() => void refresh(!learningScheduleReadyRef.current), 5000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);
  const learningScheduleReadyRef = useRef(false);
  learningScheduleReadyRef.current = learningScheduleReady;
  const changeLearningSchedule = async (next: LearningSchedule) => {
    const api = window.capturePointDesktop;
    if (!api || !learningScheduleReady || scheduleSaving.current) return;
    scheduleSaving.current = true;
    setLearningScheduleSaving(true);
    try {
      const state = await api.setLearningSchedule(next);
      if (state.schedule) setLearningSchedule(state.schedule);
      setScheduledLearning(state.run);
      setLearningScheduleNotice(state.hold);
    } catch (error) {
      setLearningScheduleNotice(
        `Learning schedule was not saved: ${messageFor(error, 'unknown error')}`,
      );
    } finally {
      scheduleSaving.current = false;
      setLearningScheduleSaving(false);
    }
  };

  // On the card by any backend the engine names in its health lines: CUDA on NVIDIA, the Vulkan reader and
  // DirectML OCR on AMD/Intel (Intel plan item 12; "(CUDA)" alone missed every AMD/Intel card). The props keep
  // their "cudaActive" name for every card.
  const onCard = (detail: string | undefined) => /\((CUDA|Vulkan|DirectML)\)/.test(detail ?? '');
  const cudaActive = onCard(engineHealth?.semanticDetail) || onCard(engineHealth?.ocrDetail);
  // Learning speed is about the extraction reader specifically. OCR using CUDA
  // does not make the 4B understanding model accelerated, and presenting that
  // mixed state as "GPU ready" is exactly the surprise the first-run guidance
  // is meant to prevent.
  // The five-second engine health is the live device truth. Workstation health
  // is intentionally cached because it is expensive, so after a crowded card
  // recovers it can briefly describe the runtime that was replaced. A verified
  // live reader on the card (CUDA, or Vulkan on AMD/Intel; the name keeps "Cuda")
  // wins; until either report has named a device, the answer is
  // unknown rather than processor-only. The startup card must never turn
  // "still checking" into advice to buy hardware the workstation already has.
  const semanticRuntimeOnCuda = /\((CUDA|Vulkan)\)/.test(engineHealth?.semanticDetail ?? '');
  const semanticRuntimeOnCpu = engineHealth?.semanticDetail?.includes('(CPU)') ?? false;
  const extractionDeviceVerdict = workstationHealth?.components.find(
    (component) => component.component === 'extraction',
  )?.verdict;
  const semanticAccelerationState: boolean | null = semanticRuntimeOnCuda
    ? true
    : extractionDeviceVerdict === 'verified-gpu'
      ? true
      : semanticRuntimeOnCpu ||
          extractionDeviceVerdict === 'running-on-cpu' ||
          extractionDeviceVerdict === 'by-design-cpu'
        ? false
        : null;
  const semanticAccelerationReady = semanticAccelerationState === true;
  // This is intentionally narrower than "not accelerated". Unknown health and
  // a graphics installation that fell back to the processor need different
  // guidance; only a workstation designed to read on its processor gets the
  // CPU-only learning explanation.
  const processorOnlyExtraction = processorOnlyByDesign(workstationHealth);
  const dailyWorkbench =
    effectiveJobMode === 'capture' &&
    Boolean(selectedJob) &&
    workbenchHasDocuments(
      activeBatches[selectedJob?.id ?? ''],
      shownDocumentCounts[selectedJob?.id ?? ''],
    );

  const doorValues = useJobDoorValues(selectedJob, jobDoorOpen);
  const jobDoorTiles: JobSetupTile[] = selectedJob
    ? [
        {
          id: 'document-types',
          label: 'Document types',
          icon: <Files size={18} />,
          value: `${selectedJob.documentTypes.length} ${selectedJob.documentTypes.length === 1 ? 'type' : 'types'}`,
        },
        {
          id: 'document-starts',
          label: 'Document starts',
          icon: <Scissors size={18} />,
          value: DOCUMENT_START_LABELS[selectedJob.separationMode],
        },
        {
          id: 'thresholds',
          label: 'Review thresholds',
          icon: <SlidersHorizontal size={18} />,
          value: 'By confidence',
        },
        {
          id: 'learning',
          label: 'Learning',
          icon: <BrainCircuit size={18} />,
          value: doorValues.aiq === undefined ? undefined : `AIQ ${doorValues.aiq}`,
        },
        {
          id: 'lookups',
          label: 'Lookup lists',
          icon: <Database size={18} />,
          value:
            doorValues.lists === undefined
              ? undefined
              : doorValues.lists
                ? `${doorValues.lists} ${doorValues.lists === 1 ? 'list' : 'lists'}`
                : 'None yet',
        },
        // PAIGE: a watched folder is a folder on the operator's own computer; Paige's is Paige Scan's (hostHas).
        ...(hostHas('watched-folder')
          ? [
              {
                id: 'watched-folder',
                label: 'Watched folder',
                icon: <FolderInput size={18} />,
                value: doorValues.watched,
              },
            ]
          : []),
        {
          id: 'export',
          label: 'Export',
          icon: <FolderOutput size={18} />, // The tile has room for one short phrase: when documents leave, "Off" while the
          // switch is off (2026-10-26), or "Not set up" while nothing can export. Autopilot is not named here; it has one
          // home, Review thresholds (owner, 2026-10-03).
          // PAIGE: where Paige sends the job's documents, from its destinations (host-export.ts).
          value:
            doorValues.sendsTo ??
            (doorValues.destination === undefined
              ? undefined
              : !doorValues.destination
                ? 'Not set up'
                : doorValues.when
                  ? doorValues.when.enabled === false
                    ? 'Off'
                    : triggerSummary(
                        doorValues.when.triggerMode,
                        doorValues.when.scheduleLocalTime ?? '21:00',
                      )
                  : undefined),
        },
        // Paige: Scan from a phone stays in the setup's own sections; the job menu keeps the common ones.
      ]
    : [];
  const openJobSetupAt = (request: string) => {
    if (!selectedJob) return;
    // "export:when" opens Export at its When stop.
    const [section, stop] = request.split(':');
    if (section === 'learning') {
      openLearning(selectedJob.id);
      return;
    }
    setSetupEntry((current) => ({
      section: section as JobSetupSection,
      stop: stop as ExportStop | undefined,
      request: (current?.request ?? 0) + 1,
    }));
    setJobMode('setup');
    setScreen('job');
  };
  openJobSetupAtRef.current = openJobSetupAt;
  const doorRenameJob = jobs.find((job) => job.id === doorRenameJobId);
  const unfinishedImport = selectedJob
    ? unfinishedImports.find((item) => item.job.id === selectedJob.id)
    : undefined;
  const workspaceJobSelector = (
    <JobSelector
      jobs={jobs}
      currentJobId={selectedJob?.id}
      loading={jobsLoading}
      error={jobsLoadError}
      onOpen={openJob}
      unfinishedImport={
        unfinishedImport && {
          progress: `${unfinishedImport.analyzedImages} of ${unfinishedImport.trainingImages + unfinishedImport.recentImages} read`,
          onContinue: () => {
            // Its latest progress, so the window opens on where it stands now.
            const open = (status: LegacyImportStatus) => {
              setJobImportResume(status);
              setShowJobImport(true);
            };
            void window.capturePointDesktop
              ?.legacyImportStatus(unfinishedImport.job.id)
              .then(open)
              .catch(() => open(unfinishedImport));
          },
        }
      }
      onManage={() => {
        // Paige's jobs live on its dashboard; the job door's All jobs is the way back to it.
        if (window.paigeWorkbench) return window.paigeWorkbench.leave();
        setFocusJobsHeading(true);
        setScreen('jobs');
      }}
      onCreate={() => openNewJob()}
      setup={
        selectedJob
          ? {
              tiles: jobDoorTiles,
              onOpen: openJobSetupAt,
              // PAIGE: the whole setup opens on its Overview, which holds every section.
              onOpenAll: () => openJobSetupAt('overview'),
            }
          : undefined
      }
      onOpenChange={setJobDoorOpen}
      manage={
        selectedJob
          ? {
              onRename: () => setDoorRenameJobId(selectedJob.id),
              onDownload: () => void exportJobSetup(selectedJob),
              onDelete: () => setDeleteTarget(selectedJob),
            }
          : undefined
      }
    />
  );
  const jobLibrary = (
    <JobsHome
      jobs={jobs}
      currentJobId={selectedJob?.id}
      loading={jobsLoading}
      error={jobsLoadError}
      focusHeading={focusJobsHeading}
      onHeadingFocused={() => setFocusJobsHeading(false)}
      onOpen={openJob}
      onCreate={openNewJob}
      onDelete={setDeleteTarget}
      onRename={renameJob}
      onExport={(job) => void exportJobSetup(job)}
      // PAIGE: the tour runs on the desktop's ready-made sample job (`sample-sets`).
      onSample={hostHas('sample-sets') ? () => void startTour('welcome') : undefined}
    />
  );
  // The Scan jobs window: the same library, whose heading and actions the window header carries.
  const jobChooser = <JobsHome hideHeader {...jobLibrary.props} />;
  const leaveLearningToWork = (continueToWork: () => void) => {
    if (effectiveJobMode === 'setup') {
      setScreen('job');
      window.dispatchEvent(
        new CustomEvent('capturepoint-setup-leave', {
          detail: { jobId: selectedIdRef.current, continueToWork },
        }),
      );
    } else continueToWork();
  };
  /* A link to a place in a job (capturepoint://job/..., docs/product/app-links.md), opened the way
     the app's own buttons open it: Learning at Train or Results, Learning's Teach in Documents, or
     the job door's Lookup lists and Review thresholds tiles. A job still being set up opens as usual. */
  const openJobLink = (route: JobAppLinkRoute, jobId: string) => {
    const job = jobsRef.current.find((candidate) => candidate.id === jobId);
    if (!job) return;
    const place = jobAppLinkPlaces[route];
    const here = jobId === selectedJob?.id;
    if (stillSettingUp(job)) {
      if (here) setScreen('job');
      else leaveLearningToWork(() => openJob(jobId));
      return;
    }
    if (here && place.kind === 'setup') {
      openJobSetupAt(place.section);
      return;
    }
    if (here && place.kind === 'learning') {
      openLearning(jobId, place.section, effectiveJobMode === 'setup');
      return;
    }
    leaveLearningToWork(() => {
      if (place.kind === 'teach') setTeachRequestJobId(jobId);
      openJob(jobId);
      if (place.kind === 'learning') openLearning(jobId, place.section);
      // The door's tiles open Job setup for the job in front, so this waits until it is.
      if (place.kind === 'setup') setLinkSetupPlace({ jobId, section: place.section });
    });
  };
  const [linkJobChoice, setLinkJobChoice] = useState<{
    route: JobAppLinkRoute;
    jobIds: string[];
  } | null>(null);
  const [linkSetupPlace, setLinkSetupPlace] = useState<{
    jobId: string;
    section: 'lookups' | 'thresholds';
  } | null>(null);
  useEffect(() => {
    if (!linkSetupPlace || selectedJob?.id !== linkSetupPlace.jobId) return;
    setLinkSetupPlace(null);
    openJobSetupAt(linkSetupPlace.section);
  }, [linkSetupPlace, selectedJob?.id]);
  followAppLinkRef.current = (route) => {
    // A newer link replaces a "Which job?" still open, as main replaces a link still waiting.
    if (!isJobAppLinkRoute(route)) {
      setLinkJobChoice(null);
      return;
    }
    // Sample jobs and the tour's job are never the person's own (a tour on their own job is).
    const tour = readTour();
    const notOwn = (job: Job) => isSampleJob(job) || (!tour?.own && tour?.jobId === job.id);
    // PAIGE (paige/app-links.ts): on a job's own route the address names the job, Paige's own and signed in, so the
    // link opens that job's place; Which job? is for a link that names none.
    const routed = window.paigeWorkbench?.openJob
      ? jobsRef.current.find((job) => job.id === selectedIdRef.current)
      : undefined;
    const choice = routed
      ? notOwn(routed) || routed.status === 'archived'
        ? ({ kind: 'none' } as const)
        : ({ kind: 'one', jobId: routed.id } as const)
      : chooseAppLinkJob(jobsRef.current, selectedJob?.id, notOwn);
    setLinkJobChoice(choice.kind === 'ask' ? { route, jobIds: choice.jobIds } : null);
    if (choice.kind === 'one') openJobLink(route, choice.jobId);
  };
  const linkChoiceJobs = linkJobChoice
    ? linkJobChoice.jobIds.flatMap((id) => jobs.filter((job) => job.id === id))
    : [];
  const teachingOpenRequest = useRef(0);
  useEffect(() => {
    teachingOpenRequest.current += 1;
  }, [screen, learningJobId]);
  const openTeachingDocument = async (jobId: string, documentId: string) => {
    const request = ++teachingOpenRequest.current;
    try {
      const fresh = await loadLatestWorkspaceBatch(jobId, documentId);
      if (request !== teachingOpenRequest.current) return;
      if (!fresh?.documents.some((document) => document.id === documentId)) {
        setToast('That document is no longer in this job’s current documents.', 'document-gone');
        return;
      }
      leaveLearningToWork(() => {
        setActiveBatches((current) => ({ ...current, [jobId]: fresh }));
        setTeachingDocument({ jobId, documentId });
        openJob(jobId);
      });
    } catch (reason) {
      if (request === teachingOpenRequest.current)
        setToast(
          reason instanceof Error ? reason.message : 'The document could not be opened. Try again.',
          'document-open-failed',
        );
    }
  };
  const applySuggestionFromLearning = async (jobId: string, request: SuggestionApplyRequest) => {
    const token = ++teachingOpenRequest.current;
    try {
      // The workspace applies against the batch it shows, so hand it the current one first.
      const fresh = await loadLatestWorkspaceBatch(jobId);
      if (token !== teachingOpenRequest.current) return;
      leaveLearningToWork(() => {
        if (fresh) setActiveBatches((current) => ({ ...current, [jobId]: fresh }));
        setSuggestionApplyRequest({ jobId, ...request });
        openJob(jobId);
      });
    } catch (reason) {
      if (token === teachingOpenRequest.current)
        setToast(
          reason instanceof Error
            ? reason.message
            : 'This job’s documents could not be opened. Try again.',
          'documents-load-failed',
        );
    }
  };
  const learningProps = {
    schedule: learningSchedule,
    onScheduleChange: changeLearningSchedule,
    scheduledRun: scheduledLearning,
    scheduleNotice: learningScheduleNotice,
    scheduleDisabled: !learningScheduleReady || learningScheduleSaving,
    onOpenTeachingDocument: (jobId: string, documentId: string) =>
      void openTeachingDocument(jobId, documentId),
    onApplyJobSuggestion: (jobId: string, request: SuggestionApplyRequest) =>
      void applySuggestionFromLearning(jobId, request),
    onTeach: (jobId: string) =>
      leaveLearningToWork(() => {
        setTeachRequestJobId(jobId);
        openJob(jobId);
      }),
    onSetCalibration: async (jobId: string, on: boolean) => {
      const api = window.capturePointDesktop;
      if (!api?.setCalibration) throw new Error("Calibration can't be changed in this window.");
      const saved = await api.setCalibration(jobId, on);
      applyStoredStance(jobId, saved.learningStance ?? null);
    },
    onChangeSuggestionSettings: async (
      jobId: string,
      area: StanceArea,
      stoppedAt: string | null,
    ) => {
      const latest = jobsRef.current.find((job) => job.id === jobId);
      if (!latest) throw new Error(`This ${hostWords().job} is no longer available.`);
      const saved = await commitJob({
        ...latest,
        learningStance: withStanceChange(latest.learningStance, area, stoppedAt),
      });
      // The engine gives the saved job a new version: carried with the stance, so an open Job setup
      // doesn't read the save as its own unsaved change.
      const version = {
        learningStance: saved.learningStance,
        schemaVersion: saved.schemaVersion,
        updatedAt: saved.updatedAt,
      };
      carryIntoSetupDraft(saved, (draft) => ({ ...draft, ...version }));
      window.dispatchEvent(
        new CustomEvent('capturepoint-learning-settings-saved', { detail: { jobId, ...version } }),
      );
    },
  };
  const overlayContent =
    screen === 'learning' ? (
      <LearningMode
        {...learningProps}
        onOpenJobSetup={(id) => {
          setSelectedId(id);
          setJobMode('setup');
          setScreen('job');
        }}
        initialSection={learningInitialSection}
        key={`${learningJobId ?? 'no-job'}:${learningOpens}`}
        jobs={jobs}
        activeJobId={learningJobId}
      />
    ) : screen === 'jobs' ? (
      jobChooser
    ) : screen === 'settings' ? (
      <SettingsPage
        standalone={appearanceStandalone && settingsSection === 'appearance'}
        clusterContent={SHARED_PROCESSING_ENABLED ? <CortexaClusterPage /> : undefined}
        jobsContent={jobLibrary}
        section={settingsSection}
        onSectionChange={(next) => {
          setSettingsSection(next);
          setScreen('settings');
        }}
        training={
          learningScheduleReady
            ? scheduledTraining(
                learningSchedule,
                scheduledJobIds(learningSchedule, jobs).length > 0,
              )
            : null
        }
        onTeach={(jobId) =>
          leaveLearningToWork(() => {
            setTeachRequestJobId(jobId);
            openJob(jobId);
          })
        }
        onTrain={(jobId) => openLearning(jobId, 'train')}
        speedTestRequest={speedTestRequest}
        onSpeedTestRequestHandled={() => setSpeedTestRequest(0)}
        appearance={appearance}
        appearanceChanging={appearanceChanging}
        onAppearance={changeAppearance}
        interfaceZoom={interfaceZoom}
        onInterfaceZoom={changeInterfaceZoom}
        appVersion={appVersion}
        checking={updateChecking}
        checkNote={updateCheckNote}
        cudaActive={cudaActive}
        activeJob={
          selectedJob
            ? {
                id: selectedJob.id,
                name: selectedJob.name,
                settingUp:
                  selectedJob.status === 'provisional' || selectedJob.documentTypes.length === 0,
              }
            : null
        }
        onCheckForUpdate={() => void checkForUpdateNow()}
        onOpenJobs={() => {
          setFocusJobsHeading(true);
          setScreen('jobs');
        }}
      />
    ) : screen === 'cluster' && SHARED_PROCESSING_ENABLED ? (
      <CortexaClusterPage />
    ) : null;
  /**
   * New job's Create: the dialog's own path, and a support request's (create-job-from-folder,
   * ADR-0088), which answers once the job is saved and leaves its setup running here.
   */
  const createJobFromIntent = async (
    {
      job,
      folderToken,
      folderFileCount,
      folderAnalysisCount,
      openSetup,
      catalogResult,
    }: JobCreationIntent,
    options: {
      useAllDocuments?: boolean;
      fromSupport?: boolean;
      onJobSaved?: (jobId: string) => void;
    } = {},
  ) => {
    // PAIGE (workbench-port.md, "SetupSampleChoice.tsx deleted"): setup always uses all documents, no
    // question, so there is no sample choice to ask for or pass on (options.useAllDocuments goes unused).
    // A support request makes a job beside whatever New job dialog is open; it never takes that dialog's job over.
    const returning = options.fromSupport ? null : returningNewJob;
    const resumed = returning ? jobsRef.current.find((item) => item.id === returning.id) : null;
    if (returning && !resumed)
      throw new Error('This job is no longer available. Close this dialog and start a new job.');
    const requestedName = job.name.trim().toLocaleLowerCase();
    // A Content Central link is a new job beside the empty one New job was reopened from, so that
    // job's name counts as taken.
    if (
      jobsRef.current.some(
        (item) =>
          item.id !== (resumed && !catalogResult ? resumed.id : job.id) &&
          item.name.trim().toLocaleLowerCase() === requestedName,
      )
    )
      throw new Error('A job with this name already exists.');
    if (resumed && !catalogResult)
      job = {
        ...resumed,
        name: job.name,
        bootstrapMode: job.bootstrapMode,
        updatedAt: job.updatedAt,
      };
    // A new job starts in Calibration (owner, 2026-10-30; shared/calibration.ts), unless it is made on Autopilot
    // (owner, 2026-10-31).
    else if (!catalogResult && startsInCalibration(job.reviewPolicy?.processingMode))
      job = {
        ...job,
        learningStance: calibrationStarted(job.learningStance, new Date().toISOString()),
      };
    // PAIGE (workbench-port.md, "CPv6 sync"): a new job's first save is createJob, past jobSaveCoordinator,
    // because the API stamps its own workspace job id (ADR-0010).
    const isNew = !jobsRef.current.some((item) => item.id === job.id);
    const saved = catalogResult?.job ?? (isNew ? await createJob(job) : await commitJob(job));
    let durable = saved ? migrateJob(saved as Job) : job;
    setJobs((current) => [durable, ...current.filter((item) => item.id !== durable.id)]);
    jobsRef.current = [durable, ...jobsRef.current.filter((item) => item.id !== durable.id)];
    if (!options.fromSupport) {
      setReturningNewJob(null);
      setShowCreate(false);
    }
    if (folderToken) noteSetupFiles(durable.id, folderFileCount ?? 0);
    openJob(durable.id);
    // Choosing another way to start removes the empty job New job was reopened from.
    if (resumed && catalogResult && freshJob?.jobId === resumed.id) void discardFreshJob(false);
    options.onJobSaved?.(durable.id);
    // Making their own job ends the tour; its job is removed.
    const tourOnCreate = readTour();
    if (tourOnCreate && tourOnCreate.jobId !== durable.id) void endTourRef.current(false);
    // A plain new job stays cancellable from New job until it has pages; one that
    // starts a folder setup, a hand-built setup or a Content Central link does not.
    setFreshJob((current) =>
      folderToken || catalogResult || openSetup
        ? null
        : current?.jobId === durable.id
          ? current
          : { jobId: durable.id, origin: newJobOrigin.current },
    );
    noteMilestone('firstJob');
    if (openSetup) setJobMode('setup');
    // Opening the new job starts a new visit; its confirmations belong there.
    toastAfterMove(
      catalogResult
        ? `${durable.name} is connected to Content Central${catalogResult.warnings.length ? ` · ${catalogResult.warnings.length} note${catalogResult.warnings.length === 1 ? '' : 's'}` : ''}`
        : 'Job created',
    );
    if (folderToken && window.capturePointDesktop) {
      noteSetupStarting(durable.id);
      setBootstrapFailures((current) => {
        const next = { ...current };
        delete next[durable.id];
        return next;
      });
      setBootstrapActivity((current) => ({
        ...current,
        [durable.id]: 'Learning from representative documents',
      }));
      setFolderActivity((current) => ({
        ...current,
        [durable.id]: [
          {
            id: 'starting',
            stage: 'starting',
            kind: 'source',
            status: 'started',
            title: 'Opening representative document set',
            detail: 'Selecting a balanced sample for local analysis',
            at: Date.now(),
          },
        ],
      }));
      try {
        const result = await window.capturePointDesktop.importFolderDocuments({
          jobId: durable.id,
          token: folderToken,
          documentTypes: [],
          separationMode: durable.separationMode,
          separationBarcodeText: durable.separationBarcodeText,
          boundaryOpeningProfiles: durable.boundaryOpeningProfiles,
          boundaryContinuationProfiles: durable.boundaryContinuationProfiles,
        });
        requireRepresentativeProposal(result);
        // The native call can take hours on a processor-only computer.
        // Merge into the latest renderer job, not the create-time
        // snapshot, so no setup revision committed while it ran can be
        // rolled back by the late proposal.
        const latestJob = jobsRef.current.find((item) => item.id === durable.id) ?? durable;
        durable = applyBootstrapProposal(latestJob, result);
        durable = migrateJob(await commitJob(durable));
        setJobs((current) => current.map((item) => (item.id === durable.id ? durable : item)));
        setBootstrapSamples((current) => ({ ...current, [durable.id]: result }));
        await hydrateRepresentativeEvidence(durable.id);
        if (selectedIdRef.current === durable.id) {
          setJobMode('setup');
          toastToCurrentVisit(
            `Found ${durable.documentTypes.length} provisional document type${durable.documentTypes.length === 1 ? '' : 's'}`,
          );
        }
      } catch (reason) {
        noteSetupStopped(durable.id);
        const message = messageFor(reason, 'The sample folder could not be analyzed');
        const failed = reportSetupFailure(durable.id, message);
        setBootstrapFailures((current) => ({ ...current, [durable.id]: message }));
        if (selectedIdRef.current === durable.id)
          toastToCurrentVisit('Folder analysis stopped', failed ? 'setup-failed' : 'notice');
      } finally {
        setBootstrapActivity((current) => {
          const next = { ...current };
          delete next[durable.id];
          return next;
        });
        setFolderProgress((current) => {
          const next = { ...current };
          delete next[durable.id];
          return next;
        });
      }
    }
  };

  // Support access (ADR-0088). Work a support request did outside this window is re-read here
  // as after the same work done here, and what only this window does is run by its own
  // handlers (main/support-window-bridge.ts). Kept current every render; subscribed once.
  const supportWindowCommands = useRef<
    Record<string, (args: Readonly<Record<string, unknown>>) => Promise<unknown>>
  >({});
  supportWindowCommands.current = {
    'create-job-from-folder': (args) =>
      new Promise((resolve, reject) => {
        const now = new Date().toISOString();
        // New job's own folder job (CreateJobModal), named by support.
        // The same job support makes with no window (setup-proposal.ts).
        const job: Job = newFolderSetupJob(crypto.randomUUID(), supportArgument(args, 'name'), now);
        void createJobFromIntent(
          {
            job,
            folderToken: supportArgument(args, 'token'),
            folderFileCount: typeof args.fileCount === 'number' ? args.fileCount : 0,
            folderAnalysisCount:
              typeof args.analysisFileCount === 'number' ? args.analysisFileCount : 0,
          },
          {
            useAllDocuments: args.useAllDocuments === true,
            fromSupport: true,
            onJobSaved: (jobId) => resolve({ jobId }),
          },
        ).catch(reject);
      }),
    'open-job': async (args) => {
      const jobId = supportArgument(args, 'jobId');
      if (!jobsRef.current.some((job) => job.id === jobId))
        throw new Error("This job is not in Paige's job list.");
      setShowCreate(false);
      openJob(jobId);
      return { opened: jobId };
    },
    'rename-job': async (args) => {
      const jobId = supportArgument(args, 'jobId');
      if (!jobsRef.current.some((job) => job.id === jobId))
        throw new Error("This job is not in Paige's job list.");
      await renameJob(jobId, supportArgument(args, 'name'));
      return { renamed: jobId };
    },
    // The Delete dialog's own delete, landing where that dialog lands.
    'delete-job': async (args) => {
      const target = jobsRef.current.find((job) => job.id === supportArgument(args, 'jobId'));
      if (!target) throw new Error("This job is not in Paige's job list.");
      const jobIdsBefore = jobsRef.current.map((job) => job.id);
      await deleteJobEverywhere(target);
      if (deleteTarget?.id === target.id) setDeleteTarget(null);
      if (selectedIdRef.current === target.id) {
        const landing = landingAfterJobDeletion(jobIdsBefore, target.id, screen === 'job');
        if (landing.screen === 'job') openJob(landing.jobId);
        else {
          setSelectedId('');
          setScreen('jobs');
        }
      }
      return { deleted: target.id };
    },
    // Job setup's Autopilot choice, saved as a change outside Job setup is (a rename).
    'set-processing-mode': async (args) => {
      const jobId = supportArgument(args, 'jobId');
      // Two pilot modes (2026-10-19): the retired straight-through Autopilot is saved as Autopilot.
      const requested = supportArgument(args, 'mode') as ProcessingMode;
      const mode: ProcessingMode = requested === 'straight-through' ? 'ai-reviewed' : requested;
      const current = jobsRef.current.find((job) => job.id === jobId);
      if (!current) throw new Error("This job is not in Paige's job list.");
      const saved = await commitJob({
        ...current,
        reviewPolicy: { ...(current.reviewPolicy ?? REVIEW_POLICY_DEFAULTS), processingMode: mode },
        updatedAt: new Date().toISOString(),
      });
      carryIntoSetupDraft(saved, (draft, stored) => ({
        ...draft,
        reviewPolicy: {
          ...(draft.reviewPolicy ?? REVIEW_POLICY_DEFAULTS),
          processingMode: stored.reviewPolicy?.processingMode,
        },
      }));
      return { job: jobId, mode: saved.reviewPolicy?.processingMode ?? null };
    },
    // Confirm in the job's review: the workspace's own confirmation, once the job is open.
    'confirm-document': async (args) => {
      const jobId = supportArgument(args, 'jobId');
      if (!jobsRef.current.some((job) => job.id === jobId))
        throw new Error("This job is not in Paige's job list.");
      if (selectedIdRef.current !== jobId || screen !== 'job') {
        setShowCreate(false);
        openJob(jobId);
      }
      return (await workspaceSupportHandler('confirm-document', jobId, 30_000))(args);
    },
  };
  /* Calibration as the engine stored it (its switch, or the engine ending it once the job is ready), in
     every copy of the job held here, without touching anything else of it: no version, no unsaved setup. */
  const applyStoredStance = (jobId: string, stance: Job['learningStance']) => {
    const withStance = (item: Job) =>
      item.id !== jobId
        ? item
        : {
            ...item,
            learningStance: {
              ...item.learningStance,
              calibrationStartedAt: stance?.calibrationStartedAt ?? null,
              calibrationEndedAt: stance?.calibrationEndedAt ?? null,
              calibrationEndedBy: stance?.calibrationEndedBy ?? null,
            },
          };
    jobsRef.current = jobsRef.current.map(withStance);
    setJobs((current) => current.map(withStance));
    carryIntoSetupDraft(
      jobsRef.current.find((item) => item.id === jobId),
      (draft) => withStance(draft),
    );
  };
  // A confirmation may have ended Calibration (the engine decides as it saves one): the workspace asks for
  // the job's stance again once its confirmations settle.
  useEffect(() => {
    const reread = (event: Event) => {
      const jobId = (event as CustomEvent<string>).detail;
      void window.capturePointDesktop
        ?.listJobs()
        .then((stored) => {
          const found = stored.find((item) => item.id === jobId);
          if (found) applyStoredStance(jobId, found.learningStance ?? null);
        })
        .catch(() => undefined);
    };
    window.addEventListener('capturepoint-calibration-check', reread);
    return () => window.removeEventListener('capturepoint-calibration-check', reread);
  });
  const supportJobsChanged = useRef<(event: JobsChangedEvent) => void>(() => undefined);
  supportJobsChanged.current = (event) => {
    if (event.change === 'removed') {
      jobsRef.current = jobsRef.current.filter((job) => job.id !== event.jobId);
      setJobs((current) => current.filter((job) => job.id !== event.jobId));
      if (selectedIdRef.current === event.jobId) {
        setSelectedId('');
        setScreen('jobs');
      }
      return;
    }
    if (event.change === 'created' || event.change === 'updated') {
      // As the job import's own close does: the stored job joins (or replaces) the list.
      void window.capturePointDesktop
        ?.listJobs()
        .then((stored) => {
          const found = stored.find((job) => job.id === event.jobId);
          if (!found || readTourRemovals().includes(found.id)) return;
          const durable = migrateJob(found as Job);
          const known = jobsRef.current.some((job) => job.id === durable.id);
          if (event.change === 'created' && known) return;
          jobsRef.current = known
            ? jobsRef.current.map((job) => (job.id === durable.id ? durable : job))
            : [durable, ...jobsRef.current];
          setJobs((current) =>
            current.some((job) => job.id === durable.id)
              ? current.map((job) => (job.id === durable.id ? durable : job))
              : [durable, ...current],
          );
        })
        .catch(() => undefined);
      return;
    }
    // Documents or export changed: the open job's documents are read again, as after its own export.
    if (selectedIdRef.current === event.jobId)
      void refreshActiveWorkspace(event.jobId).catch(() => undefined);
  };
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.onJobsChanged || !api.onSupportWindowCommand) return;
    const stopChanges = api.onJobsChanged((event) => supportJobsChanged.current(event));
    const stopCommands = api.onSupportWindowCommand((request) => {
      const handler = supportWindowCommands.current[request.command];
      void (async () => {
        try {
          if (!handler) throw new Error('This Paige window cannot do that.');
          const result = await handler(request.args ?? {});
          await api.replySupportWindowCommand({ requestId: request.requestId, ok: true, result });
        } catch (reason) {
          await api
            .replySupportWindowCommand({
              requestId: request.requestId,
              ok: false,
              error:
                reason instanceof Error ? reason.message : 'The Paige window could not do that.',
            })
            .catch(() => undefined);
        }
      })();
    });
    return () => {
      stopChanges();
      stopCommands();
    };
  }, []);

  return (
    <WorkspaceNoticeContext.Provider
      value={{
        message: toast,
        working: workingNotice,
        action: toastAction,
        dismiss: () => setToast(''),
      }}
    >
      <div className={`app-shell workspace-first${dailyWorkbench ? ' daily-workbench' : ''}`}>
        {selectedJob && (
          <JobProcessingMonitor key={`processing-${selectedJob.id}`} jobId={selectedJob.id} />
        )}
        {/* Only the job chooser lives up here; the status indicators are in the bottom bar on
          every screen. With no jobs there is nothing to choose, so the welcome has no top bar. */}
        {!dailyWorkbench && jobs.length > 0 && (
          <div className="workspace-topbar">{workspaceJobSelector}</div>
        )}
        {selectedJob && (
          <SelectedJobBackgroundCaptureDrain
            key={selectedJob.id}
            jobId={selectedJob.id}
            getCurrentJob={(jobId) => jobsRef.current.find((job) => job.id === jobId)}
            active={effectiveJobMode !== 'capture' && !representativeSetupRequired}
            onResult={acceptBackgroundCaptureResult}
            onNotice={setToast}
          />
        )}
        <main className="main-shell" inert={screen !== 'job' || showCreate}>
          <div className="workspace" ref={workspaceRef}>
            {selectedJob ? (
              <JobWorkspace
                key={selectedJob.id}
                workspaceJobSelector={dailyWorkbench ? workspaceJobSelector : undefined}
                setupEntry={setupEntry}
                onJobs={() => setScreen('jobs')}
                job={setupJob ?? selectedJob}
                mode={effectiveJobMode}
                learningOpen={screen === 'learning'}
                covered={screen !== 'job' || showCreate}
                teachingDocumentId={
                  teachingDocument?.jobId === selectedJob.id
                    ? teachingDocument.documentId
                    : undefined
                }
                onTeachingDocumentOpened={() => setTeachingDocument(null)}
                learningPanel={
                  <LearningMode
                    {...learningProps}
                    key={selectedJob.id}
                    placement="setup"
                    jobs={jobs}
                    activeJobId={selectedJob.id}
                  />
                }
                suggestionApplyRequest={
                  suggestionApplyRequest?.jobId === selectedJob.id ? suggestionApplyRequest : null
                }
                onSuggestionApplyConsumed={() => setSuggestionApplyRequest(null)}
                teachRequested={teachRequestJobId === selectedJob.id}
                canReturnToNewJob={freshJob?.jobId === selectedJob.id}
                onTeachRequestConsumed={() => setTeachRequestJobId(null)}
                batch={
                  (effectiveJobMode === 'setup' &&
                  usesRepresentativeBootstrap(selectedJob) &&
                  selectedJob.status === 'provisional'
                    ? bootstrapSamples[selectedJob.id]
                    : activeBatches[selectedJob.id]) ?? null
                }
                folderAnalysisStatus={folderStatuses[selectedJob.id]}
                onFolderAnalysisStatus={(jobId, status) =>
                  setFolderStatuses((current) => ({ ...current, [jobId]: status }))
                }
                whenSetupContinued={(jobId) => continuingSetups.current.get(jobId)}
                onSetupStopped={noteSetupStopped}
                onSetupResumed={noteSetupStarting}
                onFolderAnalysisAbandoned={(jobId, previousSetupKept) => {
                  // The setup that was cleared takes what this window showed of it along, as main forgets its latest
                  // progress. Kept, its last progress read as a new setup starting and froze the screen, and the next
                  // setup opened on its count (October 7, 2026), and on its last page. Keep previous setup keeps
                  // what that setup learned (chunk 28, R4-2).
                  forgetSetupRun(jobId, { keepResults: previousSetupKept });
                  const forget = <T,>(current: Record<string, T>) => {
                    if (!(jobId in current)) return current;
                    const next = { ...current };
                    delete next[jobId];
                    return next;
                  };
                  setFolderProgress(forget);
                  setFolderActivity(forget);
                  setFolderWork(forget);
                }}
                externalActivity={
                  bootstrapActivity[selectedJob.id] ??
                  (representativeReconciliations[selectedJob.id]
                    ? 'Reopening the completed setup'
                    : undefined)
                }
                externalFailure={bootstrapFailures[selectedJob.id]}
                folderAnalysisProgress={folderProgress[selectedJob.id]}
                folderActivity={folderActivity[selectedJob.id] ?? []}
                folderWork={folderWork[selectedJob.id] ?? []}
                processorOnlyExtraction={processorOnlyExtraction}
                onDismissExternalFailure={() =>
                  setBootstrapFailures((current) => {
                    const next = { ...current };
                    delete next[selectedJob.id];
                    return next;
                  })
                }
                onChooseRepresentativeSource={runRepresentativeSourceBootstrap}
                onBatch={(result) =>
                  // Routed by what the JOB is, not which tab is up: a provisional
                  // folder job's processed result IS its bootstrap evidence. The
                  // old jobMode clause read the CURRENT render's mode, so the
                  // recovery flow's onProcessed -> onBackToSetup pair (one tick
                  // apart) filed a finished analysis into activeBatches and the
                  // proposal it had just built never showed.
                  (usesRepresentativeBootstrap(selectedJob) && selectedJob.status === 'provisional'
                    ? setBootstrapSamples
                    : setActiveBatches)((current) => ({ ...current, [selectedJob.id]: result }))
                }
                onBootstrapAnalyzed={(jobId, result) => {
                  // The analysis response arrives in the render that started it,
                  // whose job can still say `scan` for one tick. File it by the
                  // operation's declared identity, not that stale render, so
                  // representative evidence can never become an editable batch.
                  try {
                    requireRepresentativeProposal(result);
                    setBootstrapSamples((current) => ({ ...current, [jobId]: result }));
                    void hydrateRepresentativeEvidence(jobId);
                  } catch (reason) {
                    setBootstrapFailures((current) => ({
                      ...current,
                      [jobId]: messageFor(
                        reason,
                        'Document setup analysis produced no usable proposal.',
                      ),
                    }));
                  }
                }}
                onAnalyzeMore={async (currentJob, currentBatch) => {
                  if (!window.capturePointDesktop) return;
                  const ownerJobId = currentJob.id;
                  noteSetupStarting(ownerJobId);
                  // Read more is a new setup run: the finished one's learning bar and steps stay with it
                  // (they opened the new run full, on the old stage).
                  forgetSetupProgress(ownerJobId);
                  setJobMode('capture');
                  setBootstrapFailures((current) => {
                    const next = { ...current };
                    delete next[currentJob.id];
                    return next;
                  });
                  setBootstrapActivity((current) => ({
                    ...current,
                    [currentJob.id]: 'Looking for document types not represented yet',
                  }));
                  setFolderActivity((current) => ({
                    ...current,
                    [currentJob.id]: [
                      {
                        id: `continue:${Date.now()}`,
                        stage: 'starting',
                        kind: 'source',
                        status: 'started',
                        title: 'Opening documents not considered yet',
                        detail: 'Selecting a new, varied sample from the remaining folder',
                        at: Date.now(),
                      },
                    ],
                  }));
                  try {
                    const continuation = await window.capturePointDesktop.continueFolderAnalysis({
                      jobId: currentJob.id,
                      batchId: currentBatch.batchId,
                      separationMode: currentJob.separationMode,
                      separationBarcodeText: currentJob.separationBarcodeText,
                      boundaryOpeningProfiles: currentJob.boundaryOpeningProfiles,
                      boundaryContinuationProfiles: currentJob.boundaryContinuationProfiles,
                    });
                    requireRepresentativeProposal(continuation.result);
                    // As the first setup does: merge into the job as it is now, so a rename or setting
                    // saved while Read more ran is not rolled back by the click-time snapshot.
                    const latestJob =
                      jobsRef.current.find((item) => item.id === ownerJobId) ?? currentJob;
                    const nextJob = applyBootstrapProposal(
                      latestJob,
                      continuation.result,
                      currentBatch.bootstrapProposal,
                    );
                    const saved = await commitJob(nextJob);
                    setBootstrapSamples((current) => ({
                      ...current,
                      [currentJob.id]: continuation.result,
                    }));
                    const { result: _result, ...status } = continuation;
                    setFolderStatuses((current) => ({ ...current, [currentJob.id]: status }));
                    if (selectedIdRef.current === ownerJobId)
                      setToast(
                        `Now considered ${status.analyzedFileCount} of ${status.totalFileCount} files`,
                      );
                    return saved;
                  } catch (reason) {
                    noteSetupStopped(ownerJobId);
                    reportSetupFailure(
                      currentJob.id,
                      reason instanceof Error ? reason.message : '',
                    );
                    setBootstrapFailures((current) => ({
                      ...current,
                      [currentJob.id]: messageFor(reason, 'Additional analysis stopped'),
                    }));
                  } finally {
                    setBootstrapActivity((current) => {
                      const next = { ...current };
                      delete next[currentJob.id];
                      return next;
                    });
                    setFolderProgress((current) => {
                      const next = { ...current };
                      delete next[currentJob.id];
                      return next;
                    });
                    if (selectedIdRef.current === ownerJobId) setJobMode('setup');
                  }
                }}
                onFolderAccepted={() => {
                  setBootstrapSamples((current) => {
                    const next = { ...current };
                    delete next[selectedJob.id];
                    return next;
                  });
                  setActiveBatches((current) => {
                    const next = { ...current };
                    delete next[selectedJob.id];
                    return next;
                  });
                }}
                onWorkspaceRefresh={() => refreshActiveWorkspace(selectedJob.id)}
                onDocumentCount={(count) =>
                  setShownDocumentCounts((current) =>
                    current[selectedJob.id] === count
                      ? current
                      : { ...current, [selectedJob.id]: count },
                  )
                }
                onPersistedJobUpdated={(durable) => {
                  jobsRef.current = jobsRef.current.map((value) =>
                    value.id === durable.id ? durable : value,
                  );
                  setJobs((current) =>
                    current.map((value) => (value.id === durable.id ? durable : value)),
                  );
                  // The engine saved this job itself (tuned bars, a reset, a removed type): it is the new start.
                  setSetupDrafts((current) => draftsWithout(current, durable.id));
                }}
                onModeChange={(mode) => {
                  if (selectedIdRef.current === selectedJob.id) setJobMode(mode);
                }}
                onChange={changeWorkspaceJob}
                onCommit={commitWorkspaceJob}
                onSaveAlongside={saveAlongsideSetupDraft}
                onToast={setToast}
                onOpenCluster={() => setScreen('cluster')}
              />
            ) : !jobsLoading && !jobsLoadError && jobs.length === 0 ? (
              // A workstation with no jobs has none to open: its first screen is the welcome.
              <div className="first-run-workspace">{jobLibrary}</div>
            ) : (
              <div className="empty-document-workspace">
                <Files size={64} strokeWidth={1} />
                <h1>Your document workspace</h1>
                <p>Open a {hostWords().job} to begin.</p>
                <button className="primary-button" onClick={() => setScreen('jobs')}>
                  Choose a {hostWords().job}
                </button>
              </div>
            )}
          </div>
        </main>
        {/* A screen in a window (Learning, Scan jobs, Settings): counted as its area, not as a window left.
          Hidden, not closed, under Delete job's question, so Cancel finds its search and focus as they were. */}
        {screen !== 'job' && !showCreate && (
          <WorkspaceDialog
            telemetry="none"
            suspended={Boolean(deleteTarget)}
            title={
              screen === 'learning'
                ? `Learning${learningJobId ? ` for ${jobs.find((job) => job.id === learningJobId)?.name ?? 'this job'}` : ''}`
                : screen === 'cluster'
                  ? 'Shared processing'
                  : screen === 'jobs'
                    ? hostWords().jobsCapital
                    : screen === 'settings' &&
                        appearanceStandalone &&
                        settingsSection === 'appearance'
                      ? 'Appearance'
                      : 'Settings'
            }
            compact={
              screen === 'settings' && appearanceStandalone && settingsSection === 'appearance'
            }
            description={undefined}
            actions={
              screen === 'jobs' ? (
                <JobLibraryActions onCreate={() => openNewJob()} />
              ) : screen === 'learning' && learningReturnToSetup ? (
                <button className="secondary-button small" onClick={() => setScreen('job')}>
                  <ChevronLeft size={16} />
                  Back to Job setup
                </button>
              ) : undefined
            }
            onClose={() => setScreen('job')}
          >
            {overlayContent}
          </WorkspaceDialog>
        )}
        {showSampleJobs && (
          <SampleJobsDialog onClose={() => setShowSampleJobs(false)} onChoose={startSampleSet} />
        )}
        <TourStart starting={tourStarting} />
        {showShortcuts && <ShortcutSheet onClose={() => setShowShortcuts(false)} />}
        {showJobImport && (
          <JobImportDialog
            resume={jobImportResume ?? undefined}
            onClose={() => {
              setShowJobImport(false);
              setJobImportResume(null);
              refreshUnfinishedImports();
              // An import closed before it finished ("Finish later", or a stopped reading) has already
              // made its job; without this it stayed off the list until the next start.
              void window.capturePointDesktop
                ?.listJobs()
                .then((stored) => {
                  const removing = readTourRemovals();
                  setJobs((current) => [
                    ...stored
                      .filter(
                        (job) =>
                          job.status === 'active' &&
                          !removing.includes(job.id) &&
                          !current.some((known) => known.id === job.id),
                      )
                      .map((job) => migrateJob(job as Job)),
                    ...current,
                  ]);
                })
                .catch(() => undefined);
            }}
            onFile={importJobSetupFile}
            onImported={(result) => {
              const durable = migrateJob(result.job as Job);
              // A job already on the list (an import resumed from its job menu) keeps this window's copy,
              // which may hold setup changes still being saved; the import changes none of it.
              setJobs((current) =>
                current.some((job) => job.id === durable.id) ? current : [durable, ...current],
              );
              setShowJobImport(false);
              setJobImportResume(null);
              refreshUnfinishedImports();
              noteMilestone('firstJob');
              setSelectedId(durable.id);
              setScreen('job');
              setJobMode(durable.status === 'provisional' ? 'setup' : 'capture');
            }}
          />
        )}
        {interfaceZoomHud && (
          <InterfaceZoomHud
            state={interfaceZoomHud}
            onReset={() => {
              void changeInterfaceZoom('reset').then(() => setInterfaceZoomHud(null));
            }}
            onClose={() => setInterfaceZoomHud(null)}
          />
        )}
        {showCreate && (
          <CreateJobModal
            initialName={returningNewJob?.name}
            connectionSettings={
              <SettingsPage
                standalone
                clusterContent={SHARED_PROCESSING_ENABLED ? <CortexaClusterPage /> : undefined}
                jobsContent={jobLibrary}
                section="connections"
                onSectionChange={() => {}}
                appearance={appearance}
                appearanceChanging={appearanceChanging}
                onAppearance={changeAppearance}
                interfaceZoom={interfaceZoom}
                onInterfaceZoom={changeInterfaceZoom}
                appVersion={appVersion}
                checking={updateChecking}
                checkNote={updateCheckNote}
                cudaActive={cudaActive}
                activeJob={
                  selectedJob
                    ? {
                        id: selectedJob.id,
                        name: selectedJob.name,
                        settingUp:
                          selectedJob.status === 'provisional' ||
                          selectedJob.documentTypes.length === 0,
                      }
                    : null
                }
                onCheckForUpdate={() => void checkForUpdateNow()}
                onOpenJobs={() => {
                  setFocusJobsHeading(true);
                  setScreen('jobs');
                }}
              />
            }
            existingJobs={jobs}
            existingNames={jobs
              .filter((job) => createMode === 'content-central' || job.id !== returningNewJob?.id)
              .map((job) => job.name)}
            intent={createMode}
            start={createStart}
            discardsJob={Boolean(returningNewJob && freshJob?.jobId === returningNewJob.id)}
            cudaActive={semanticAccelerationReady}
            onIntentChange={setCreateMode}
            onClose={() => {
              // Mounted by Paige to create a job: closing the dialog is leaving.
              if (window.paigeWorkbench?.createOnOpen) window.paigeWorkbench.leave();
              // Closing New job reopened from the empty job it just made is cancelling that job.
              const cancelling = Boolean(returningNewJob && freshJob?.jobId === returningNewJob.id);
              setShowCreate(false);
              setReturningNewJob(null);
              if (cancelling) void discardFreshJob(true);
            }}
            onBack={() => {
              if (window.paigeWorkbench?.createOnOpen) window.paigeWorkbench.leave();
              // Back returns where New job was opened (owner decision September 24, 2026):
              // from the job door, the review screen; from Scan jobs, Scan jobs. Leaving
              // the empty job New job just made discards it on the way.
              if (returningNewJob && freshJob?.jobId === returningNewJob.id) {
                setShowCreate(false);
                setReturningNewJob(null);
                void discardFreshJob(true);
                return;
              }
              const fromJobDoor = screen === 'job' && !returningNewJob;
              setShowCreate(false);
              setReturningNewJob(null);
              if (!fromJobDoor) {
                setFocusJobsHeading(true);
                setScreen('jobs');
              }
            }}
            // Starting another way instead leaves no empty job behind either.
            onOpenExisting={(jobId) => {
              if (returningNewJob) void discardFreshJob(false);
              setShowCreate(false);
              setReturningNewJob(null);
              openJob(jobId);
            }}
            onTour={
              // PAIGE: the tour runs on the desktop's ready-made sample job (`sample-sets`).
              hostHas('sample-sets')
                ? () => {
                    if (returningNewJob) void discardFreshJob(false);
                    setShowCreate(false);
                    setReturningNewJob(null);
                    void startTour('new-job');
                  }
                : undefined
            }
            onImport={
              // PAIGE (workbench-port.md, row 18): a job setup file is the desktop's (`job-setup-files`).
              hostHas('job-setup-files')
                ? () => {
                    if (returningNewJob) void discardFreshJob(false);
                    setShowCreate(false);
                    setReturningNewJob(null);
                    importJobSetup();
                  }
                : undefined
            }
            onCreate={(intent) => createJobFromIntent(intent)}
          />
        )}
        {linkJobChoice && linkChoiceJobs.length > 0 && (
          <AppLinkJobChooser
            jobs={linkChoiceJobs}
            place={jobAppLinkPlaceNames[linkJobChoice.route]}
            onChoose={(jobId) => {
              setLinkJobChoice(null);
              openJobLink(linkJobChoice.route, jobId);
            }}
            onClose={() => setLinkJobChoice(null)}
          />
        )}
        {doorRenameJob && (
          <RenameJobDialog
            key={doorRenameJob.id}
            name={doorRenameJob.name}
            otherNames={jobs.filter((job) => job.id !== doorRenameJob.id).map((job) => job.name)}
            onRename={(name) => renameJob(doorRenameJob.id, name)}
            onClose={() => setDoorRenameJobId(null)}
          />
        )}
        {deleteTarget && (
          <DeleteJobModal
            job={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onDelete={async () => {
              const target = deleteTarget;
              // Taken before the delete: deleteJobEverywhere drops the job from jobsRef, and the
              // next job is found from where the deleted one stood.
              const jobIdsBefore = jobsRef.current.map((job) => job.id);
              await deleteJobEverywhere(target);
              setDeleteTarget(null);
              const landing = landingAfterJobDeletion(jobIdsBefore, target.id, screen === 'job');
              if (landing.screen === 'job') openJob(landing.jobId);
              else {
                if (selectedId === target.id) setSelectedId('');
                setScreen('jobs');
              }
              // Leaving the deleted job starts a new visit; the confirmation belongs to where the operator lands.
              toastAfterMove(`Deleted ${target.name}`);
            }}
          />
        )}
        {updateStatus &&
          (updateStatus.state === 'downloading' || updateStatus.state === 'ready') && (
            <div className={`update-banner ${updateStatus.state}`} role="status">
              {/* The channel comes from the STATUS — the package's own origin —
              never from the stored setting. Reading the setting is what put
              "Stable" beside a package fetched from Preview, and it would do it
              again the moment anything is held across a switch. There is no
              unlabelled case: a package whose origin could not be established is
              never staged, so it never reaches this banner. */}
              {updateStatus.state === 'downloading' ? (
                <>
                  <LoaderCircle className="spin" size={16} />
                  <span>
                    Downloading update {updateStatus.version} ({channelTitle(updateStatus.channel)})
                    - {Math.round(updateStatus.percent)}%
                  </span>
                </>
              ) : (
                <>
                  <ArrowUpCircle size={17} />
                  <span>
                    Paige {updateStatus.version} ({channelTitle(updateStatus.channel)}) is ready to
                    install.
                  </span>
                  <button
                    className="update-banner-install"
                    onClick={() =>
                      void window.capturePointDesktop
                        ?.installUpdateNow()
                        .then((result) => {
                          // Refused while documents are in flight; say why rather than
                          // leaving a button that looks broken.
                          if (result && !result.started)
                            setToast(result.reason, 'update-install-refused');
                        })
                        .catch(() => undefined)
                    }
                  >
                    Install &amp; restart
                  </button>
                </>
              )}
            </div>
          )}
        {/* Off the document screen this bar is always there, carrying the same indicators, in
          the same bottom-left place, as the document bar: Learning, This computer, Speed boost. */}
        {/* On the document screen the document bar carries the download progress; a job with no
          document open has no document bar, so this bar carries it there (hidden by CSS
          whenever a document bar is on screen). */}
        {(!dailyWorkbench ||
          ((toast || workingNotice || runtimeDownloadVisible(componentDownload)) &&
            screen !== 'job') ||
          (runtimeDownloadVisible(componentDownload) && screen === 'job')) && (
          <ApplicationStatusBar
            componentDownload={componentDownload}
            downloadOnly={dailyWorkbench && screen === 'job'}
            indicators={
              dailyWorkbench ? undefined : (
                <div className="document-bar-indicators" inert={screen !== 'job' || showCreate}>
                  {selectedJob && (
                    <>
                      <LearningScoreButton
                        job={selectedJob}
                        onClick={() =>
                          openLearning(undefined, 'overview', effectiveJobMode === 'setup')
                        }
                      />
                      <span className="document-bar-divider" aria-hidden="true" />
                    </>
                  )}
                  {hostHas('workbench-settings') ? <ComputerDoorButton /> : <AccountDoorButton />}
                  {hostHas('licensing') && <TrialTray />}
                  {hostHas('licensing') && <PagesTray />}
                  {hostHas('local-performance') && <SpeedBoostButton />}
                  {hostHas('saved-connections') && <ConnectionAlert />}
                </div>
              )
            }
          />
        )}
        {joinAsk && (
          <div className="toast toast-ask" role="status">
            <Handshake size={17} />
            <span>
              {joinAsk.name} wants to join {joinAsk.cluster}
            </span>
            <button
              type="button"
              onClick={() => {
                setJoinAsk(null);
                setScreen('cluster');
              }}
            >
              Review
            </button>
          </div>
        )}
      </div>
    </WorkspaceNoticeContext.Provider>
  );
}

export async function renameSavedJob(
  jobId: string,
  name: string,
  jobs: Job[],
  commit: (job: Job) => Promise<Job>,
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length > 160)
    throw new Error('Enter a job name of up to 160 characters.');
  const current = jobs.find((job) => job.id === jobId);
  if (!current) throw new Error('This job no longer exists.');
  if (
    jobs.some(
      (job) =>
        job.id !== jobId && job.name.trim().toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
    )
  )
    throw new Error('A job with this name already exists. Choose a different name.');
  if (current.name === trimmed) return;
  await commit({ ...current, name: trimmed });
}

export function JobLibraryActions({ onCreate }: { onCreate: () => void }) {
  // One front door: New job opens "How do you want to start?", which holds every
  // way to start a job (your documents, ready-made, Content Central, a saved job).
  return (
    <button className="primary-button" onClick={onCreate}>
      <Plus size={18} /> New job
    </button>
  );
}

/**
 * The Scan jobs window: every job as its identity, and one click to work in it.
 * A job's setup already hangs on its own door (the job name in the workspace),
 * so nothing here repeats it. The rare Rename, Download job setup and Delete
 * live on that door, with right-click and F2/Delete here as shortcuts.
 */
export function JobsHome({
  hideHeader = false,
  jobs,
  currentJobId,
  loading,
  error,
  focusHeading,
  onHeadingFocused,
  onOpen,
  onCreate,
  onDelete,
  onRename,
  onExport,
  onSample,
}: {
  hideHeader?: boolean;
  jobs: Job[];
  currentJobId?: string;
  loading: boolean;
  error: string;
  focusHeading: boolean;
  onHeadingFocused: () => void;
  onOpen: (id: string) => void;
  onCreate: (mode?: JobBootstrapChoice) => void;
  onDelete: (job: Job) => void;
  onRename?: (jobId: string, name: string) => Promise<void>;
  onExport: (job: Job) => void;
  onSample?: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [query, setQuery] = useState('');
  const [renameJobId, setRenameJobId] = useState<string | null>(null);
  const renameTarget = jobs.find((job) => job.id === renameJobId);
  useEffect(() => {
    if (!focusHeading) return;
    const frame = window.requestAnimationFrame(() => {
      headingRef.current?.focus();
      onHeadingFocused();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusHeading, onHeadingFocused]);

  if (!loading && !error && jobs.length === 0)
    return <FirstRunHome onCreate={onCreate} onSample={onSample} />;

  // The open job first, then the most recently used.
  const byRecent = [...jobs].sort(
    (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
  );
  const orderedJobs = [
    ...byRecent.filter((job) => job.id === currentJobId),
    ...byRecent.filter((job) => job.id !== currentJobId),
  ];
  // A handful of jobs does not need a search box (the job door uses the same
  // threshold). The effective query falls back to empty below it, so a list
  // that shrinks can never stay invisibly filtered by a hidden control.
  const showSearch = jobs.length >= 8;
  const effectiveQuery = showSearch ? query.trim().toLocaleLowerCase() : '';
  const matchingJobs = orderedJobs.filter(
    (job) =>
      !effectiveQuery ||
      [job.name, job.description, ...job.documentTypes.map((type) => type.name)].some((value) =>
        value.toLocaleLowerCase().includes(effectiveQuery),
      ),
  );
  const workingJobs = matchingJobs.filter((job) => job.status !== 'archived');
  const archivedJobs = matchingJobs.filter((job) => job.status === 'archived');
  const jobFace = (job: Job) => (
    <JobFace
      key={job.id}
      job={job}
      current={job.id === currentJobId}
      onOpen={() => onOpen(job.id)}
      onRename={onRename ? () => setRenameJobId(job.id) : undefined}
      onExport={() => onExport(job)}
      onDelete={() => onDelete(job)}
    />
  );
  return (
    <div className="page jobs-page jobs-workspace">
      {renameTarget && onRename && (
        <RenameJobDialog
          key={renameTarget.id}
          name={renameTarget.name}
          otherNames={jobs.filter((job) => job.id !== renameTarget.id).map((job) => job.name)}
          onRename={(name) => onRename(renameTarget.id, name)}
          onClose={() => setRenameJobId(null)}
        />
      )}
      {!hideHeader && (
        <section className="page-heading jobs-heading">
          <div>
            <h1 ref={headingRef} tabIndex={-1}>
              {hostWords().jobsCapital}
            </h1>
            <p>Choose the {hostWords().job} you want to work in.</p>
          </div>
          <div className="jobs-heading-actions">
            <JobLibraryActions onCreate={() => onCreate()} />
          </div>
        </section>
      )}

      {showSearch && !loading && !error && (
        <section className="jobs-library-tools" aria-label={`Find ${hostWords().jobs}`}>
          <label className="jobs-search">
            <Search size={18} aria-hidden="true" />
            <span className="visually-hidden">Search {hostWords().jobs}</span>
            <input
              type="search"
              aria-label={`Search ${hostWords().jobs}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search jobs or document types"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear job search">
                <X size={16} />
              </button>
            )}
          </label>
          <p className="jobs-result-count" aria-live="polite">
            {matchingJobs.length === jobs.length
              ? `${jobs.length.toLocaleString()} ${jobs.length === 1 ? 'job' : 'jobs'}`
              : `${matchingJobs.length.toLocaleString()} of ${jobs.length.toLocaleString()} jobs`}
          </p>
        </section>
      )}

      <section className="job-list" aria-label={hostWords().jobsCapital} aria-busy={loading}>
        {loading && (
          <div className="jobs-state">
            <LoaderCircle className="spin" size={20} />
            <span>Loading jobs</span>
          </div>
        )}
        {!loading && error && (
          <div className="jobs-state error">
            <TriangleAlert size={20} />
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && matchingJobs.length === 0 && (
          <div className="jobs-empty-results" role="status">
            <Search size={24} aria-hidden="true" />
            <strong>No {hostWords().jobs} match</strong>
            <span>Try another name or document type.</span>
            <button type="button" className="secondary-button small" onClick={() => setQuery('')}>
              Show all jobs
            </button>
          </div>
        )}
        {!loading && !error && workingJobs.length > 0 && (
          <div className="job-grid">{workingJobs.map(jobFace)}</div>
        )}
        {!loading && !error && archivedJobs.length > 0 && (
          <section className="job-archive" aria-label={`Archived ${hostWords().jobs}`}>
            <h2>
              <Archive size={16} aria-hidden="true" /> Archived
            </h2>
            <div className="job-grid">{archivedJobs.map(jobFace)}</div>
          </section>
        )}
      </section>
    </div>
  );
}

export function InterfaceZoomHud({
  state,
  onReset,
  onClose,
}: {
  state: InterfaceZoomEvent;
  onReset: () => void;
  onClose: () => void;
}) {
  const limitText = state.limitReached
    ? state.atMaximum
      ? `Maximum on this monitor: ${state.maximumPercent}%`
      : state.atMinimum
        ? `Minimum app size: ${state.minimumPercent}%`
        : 'App size is already at its limit'
    : null;
  return (
    <div className="interface-zoom-hud" role="group" aria-label="App text and controls">
      <span className="interface-zoom-mark">
        <Gauge size={16} />
      </span>
      <output aria-live="polite">
        <small>App text and controls</small>
        <strong>{state.percent}%</strong>
      </output>
      {limitText && <em>{limitText}</em>}
      <button
        type="button"
        className="interface-zoom-reset"
        disabled={state.level === 0}
        onClick={onReset}
      >
        Default size
      </button>
      <button
        type="button"
        className="interface-zoom-close"
        onClick={onClose}
        aria-label="Close app size controls"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function NewJobLearningIntroduction({
  intent,
  compact = false,
}: {
  intent: JobBootstrapChoice;
  compact?: boolean;
}) {
  return (
    <section
      className={`new-job-learning-intro${compact ? ' compact' : ''}`}
      aria-label="How this new job learns"
    >
      <p>
        {intent === 'content-central'
          ? 'Your catalog provides the setup. Your confirmations teach this job to recognize the documents.'
          : 'Your confirmations teach this job to recognize similar documents.'}
      </p>
    </section>
  );
}

function FirstRunHome({
  onCreate,
  onSample,
}: {
  onCreate: (mode?: JobBootstrapChoice) => void;
  onSample?: () => void;
}) {
  // One path (owner rules, 2026-09-27): one primary action and the picture of
  // the idea. It says Get started in both versions (owner, 2026-10-04: "it's
  // weird to say 'Use your own documents'"). With the sample documents installed
  // it goes straight to naming a job on their own documents, and Take a 2-minute
  // tour is the small link beside it; without them it opens New job's first
  // step, How do you want to start?, and there is no tour link. Saved jobs and
  // earlier-Paige jobs are on that first step, not here.
  const choices = useSampleChoices();
  const noSamples = !onSample || (choices !== null && choices.length === 0);
  return (
    <section className="job-library-empty" aria-label="Welcome to Paige">
      <div className="first-job-copy">
        <span className="first-job-eyebrow">WELCOME TO PAIGE</span>
        <h2>
          Your documents.
          <br />
          <em>Your intelligence.</em>
        </h2>
        <p>Turn a stack of documents into a job that learns from you.</p>
        {noSamples ? (
          <button className="primary-button" onClick={() => onCreate()}>
            Get started <ArrowRight size={21} />
          </button>
        ) : (
          <div className="first-job-paths">
            <button type="button" className="primary-button" onClick={() => onCreate('scan')}>
              Get started <ArrowRight size={21} />
            </button>
          </div>
        )}
      </div>
      {!noSamples && choices !== null && (
        <div className="first-job-tour">
          <button type="button" data-telemetry="tour-welcome" onClick={onSample}>
            {tourOfferLabel('Take a 2-minute tour')} →
          </button>
        </div>
      )}
      <div className="first-job-visual">
        <img
          src={newJobLearningUrl}
          alt="Paper documents become organized digital records through learning."
        />
      </div>
    </section>
  );
}

/**
 * One scan job in the Scan jobs window: its identity, and one click to work in
 * it. The only status shown is which job is current (owner decision, September
 * 23, 2026); the job's setup and its work appear once it is open, on its door
 * and in its workspace. Rename, Download job setup and Delete are one
 * right-click (or F2 / Delete / the Menu key) away, never a button repeated on
 * every job.
 */
function JobFace({
  job,
  current,
  onOpen,
  onRename,
  onExport,
  onDelete,
}: {
  job: Job;
  current: boolean;
  onOpen: () => void;
  onRename?: () => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const face = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const closeMenu = (restoreFocus: boolean) => {
    setMenu(null);
    if (restoreFocus) face.current?.focus({ preventScroll: true });
  };
  const openMenuAtFace = () => {
    const bounds = face.current?.getBoundingClientRect();
    if (bounds)
      setMenu({ x: bounds.left + bounds.width / 2, y: bounds.top + Math.min(bounds.height, 120) });
  };
  useLayoutEffect(() => {
    if (!menu) return;
    const panel = menuRef.current;
    if (panel) {
      // Opened near an edge, the whole menu still stays on screen.
      panel.style.left = `${Math.max(8, Math.min(menu.x, innerWidth - panel.offsetWidth - 8))}px`;
      panel.style.top = `${Math.max(8, Math.min(menu.y, innerHeight - panel.offsetHeight - 8))}px`;
      panel.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    }
    const outside = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !menuRef.current?.contains(event.target))
        closeMenu(false);
    };
    const dismiss = () => closeMenu(false);
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
    };
  }, [menu]);
  const run =
    (action: () => void, restoreFocus = true) =>
    () => {
      closeMenu(restoreFocus);
      action();
    };
  return (
    <article
      className={`job-card job-face${current ? ' current-job' : ''}${job.status === 'archived' ? ' archived' : ''}`}
      data-job-id={job.id}
      style={{ '--job-accent': job.accent } as React.CSSProperties}
    >
      <button
        ref={face}
        type="button"
        className="job-card-body"
        onClick={onOpen}
        aria-label={`Open ${job.name}`}
        aria-current={current ? 'true' : undefined}
        aria-haspopup="menu"
        aria-expanded={menu ? true : undefined}
        aria-controls={menu ? menuId : undefined}
        aria-keyshortcuts={onRename ? 'F2 Delete Shift+F10' : 'Delete Shift+F10'}
        onContextMenu={(event) => {
          event.preventDefault();
          setMenu({ x: event.clientX, y: event.clientY });
        }}
        onKeyDown={(event) => {
          if (event.key === 'F2' && onRename) {
            event.preventDefault();
            onRename();
          } else if (event.key === 'Delete' && hostHas('job-delete')) {
            // PAIGE: only where the host deletes a job from here (workbench-port.md, `job-delete`).
            event.preventDefault();
            onDelete();
          } else if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
            event.preventDefault();
            openMenuAtFace();
          }
        }}
      >
        <span className="job-face-icon" aria-hidden="true">
          <JobIdentity jobId={job.id} />
        </span>
        <span className="job-card-name">
          <h3>{job.name}</h3>
        </span>
        {(current || hasNoTypeLeft(job)) && (
          <span className="job-face-tags">
            {current && <small className="job-card-status">Current job</small>}
            {/* Every type removed: the job takes no new work until one is added (ADR-0096). */}
            {hasNoTypeLeft(job) && (
              <small className="job-card-status is-attention">{NO_TYPE_LEFT_SENTENCE}</small>
            )}
          </span>
        )}
      </button>
      {menu &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={`${job.name} options`}
            className="job-face-menu"
            onContextMenu={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              const items = [
                ...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
              ];
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              if (event.key === 'Escape' || event.key === 'Tab') {
                event.preventDefault();
                event.stopPropagation();
                closeMenu(true);
              } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                items[
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
                ]?.focus();
              }
            }}
          >
            <button type="button" role="menuitem" onClick={run(onOpen, false)}>
              <ArrowRight size={15} aria-hidden="true" />
              <span>Open</span>
              <kbd aria-hidden="true">Enter</kbd>
            </button>
            <div role="separator" />
            {onRename && (
              <button type="button" role="menuitem" aria-keyshortcuts="F2" onClick={run(onRename)}>
                <TextCursorInput size={15} aria-hidden="true" />
                <span>Rename</span>
                <kbd aria-hidden="true">F2</kbd>
              </button>
            )}
            <button type="button" role="menuitem" onClick={run(onExport)}>
              <FileDown size={15} aria-hidden="true" />
              <span>Download job setup</span>
            </button>
            {/* PAIGE: only where the host deletes a job from here (workbench-port.md, `job-delete`). */}
            {hostHas('job-delete') && (
              <>
                <div role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="danger"
                  aria-keyshortcuts="Delete"
                  onClick={run(onDelete)}
                >
                  <Trash2 size={15} aria-hidden="true" />
                  <span>Delete job…</span>
                  <kbd aria-hidden="true">Del</kbd>
                </button>
              </>
            )}
          </div>,
          face.current?.closest<HTMLElement>('[role="dialog"]') ?? document.body,
        )}
    </article>
  );
}

function DeleteJobModal({
  job,
  onClose,
  onDelete,
}: {
  job: Job;
  onClose: () => void;
  onDelete: () => Promise<void>;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const matches = confirmsJobName(confirmation, job.name);
  useEffect(() => {
    previousFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus(),
    );
    return () => {
      window.cancelAnimationFrame(frame);
      // A delete from the job door lands on the next job, whose door is the stable fallback.
      const target = previousFocus.current?.isConnected
        ? previousFocus.current
        : (document.querySelector<HTMLElement>('.jobs-heading h1') ??
          document.querySelector<HTMLElement>('.job-selector-trigger'));
      target?.focus();
    };
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !deleting) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogTabStops(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleting, onClose]);
  const remove = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await onDelete();
    } catch (reason) {
      setError(messageFor(reason, 'The job could not be deleted'));
      setDeleting(false);
    }
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !deleting && onClose()}
    >
      <section
        ref={dialogRef}
        className="delete-job-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-job-title"
        tabIndex={-1}
      >
        <header>
          <span>
            <Trash2 size={19} />
          </span>
          <div>
            <small>Permanent action</small>
            <h2 id="delete-job-title">Delete “{job.name}”?</h2>
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            disabled={deleting}
            aria-label="Cancel deletion"
          >
            <X size={17} />
          </button>
        </header>
        <p>
          {hostHas('local-storage')
            ? 'This removes the job setup, processed documents, corrections, and learning history stored on this computer. Original source files are never deleted.'
            : 'This removes the job setup, processed documents, corrections, and learning history. Original source files are never deleted.'}
        </p>
        <label>
          <span>
            Type <strong>{typeableJobName(job.name)}</strong> to confirm
          </span>
          <input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={deleting}
            spellCheck={false}
            autoComplete="off"
          />
        </label>
        {error && (
          <div className="delete-job-error">
            <TriangleAlert size={15} /> {error}
          </div>
        )}
        {deleting && (
          <p className="delete-job-progress" role="status">
            <LoaderCircle className="spin" size={13} /> Stopping this job's processing - queued work
            is discarded, the page being read right now finishes, then everything is removed.
            Usually under a minute.
          </p>
        )}
        <footer>
          <button className="secondary-button" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            className="danger-button"
            onClick={() => void remove()}
            disabled={!matches || deleting}
          >
            {deleting ? <LoaderCircle className="spin" size={15} /> : <Trash2 size={15} />} Delete
            permanently
          </button>
        </footer>
      </section>
    </div>
  );
}

/**
 * Removing a document type. Two shapes: a type added by hand in this visit and not yet
 * saved leaves the draft (as it always has); a saved type is removed by the engine, which
 * counts first what will change (DocumentStore.TypeRemoval) and says so in plain lines.
 */
function RemoveDocumentTypeDialog({
  type,
  autopilot,
  onClose,
  onRemove,
  returnFocus,
  loadImpact,
}: {
  type: DocumentTypeDefinition;
  /** The job runs on Autopilot: its documents no type fits go to the exceptions folder instead of waiting. */
  autopilot: boolean;
  onClose: () => void;
  onRemove: (impact?: DocumentTypeRemovalImpact) => void | Promise<void>;
  /** Where focus goes when the control that opened the dialog is gone. */
  returnFocus: () => HTMLElement | null;
  /** A saved type: what removing it will do, as the engine counts it. */
  loadImpact?: () => Promise<DocumentTypeRemovalImpact>;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [impact, setImpact] = useState<DocumentTypeRemovalImpact | null>(null);
  const [impactError, setImpactError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removingRef = useRef(false);
  useEffect(() => {
    if (!loadImpact) return;
    let live = true;
    void loadImpact()
      .then((value) => {
        if (live) setImpact(value);
      })
      .catch((reason: unknown) => {
        if (live) setImpactError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      live = false;
    };
  }, []);
  const close = () => {
    if (!removingRef.current) onCloseRef.current();
  };
  const remove = async () => {
    if (removingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    setRemoveError(null);
    try {
      await onRemove(impact ?? undefined);
    } catch (reason) {
      setRemoveError(reason instanceof Error ? reason.message : String(reason));
      removingRef.current = false;
      setRemoving(false);
      // Count again: a refusal often means the setup or its documents changed, and the
      // next attempt must confirm the counts (and setup version) the operator now reads.
      if (loadImpact) {
        setImpact(null);
        void loadImpact()
          .then(setImpact)
          .catch((failure: unknown) =>
            setImpactError(failure instanceof Error ? failure.message : String(failure)),
          );
      }
    }
  };
  const returnFocusRef = useRef(returnFocus);
  returnFocusRef.current = returnFocus;
  useEffect(() => {
    previousFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector<HTMLButtonElement>('.secondary-button')?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!removingRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogTabStops(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      // The options menu that opened this dialog closes as it opens, so focus
      // was usually already on the page body.
      const opener = previousFocus.current;
      const target =
        opener?.isConnected && opener !== document.body ? opener : returnFocusRef.current();
      target?.focus();
    };
  }, []);
  if (!loadImpact)
    return (
      <div
        className="modal-backdrop"
        onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      >
        <section
          ref={dialogRef}
          className="delete-job-modal remove-document-type-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-document-type-title"
          tabIndex={-1}
        >
          <header>
            <span>
              <Trash2 size={19} />
            </span>
            <div>
              <small>Remove from this setup</small>
              <h2 id="remove-document-type-title">Remove “{type.name}”?</h2>
            </div>
            <button
              className="icon-button"
              onClick={onClose}
              aria-label="Cancel removing document type"
            >
              <X size={17} />
            </button>
          </header>
          <p>
            This removes the document type and its {counted(type.fields.length, 'field')} from this
            job. It has not been saved yet, so nothing else changes.
          </p>
          <footer>
            <button className="secondary-button" onClick={onClose}>
              Keep document type
            </button>
            <button className="danger-button" onClick={() => void onRemove()}>
              <Trash2 size={15} /> Remove from setup
            </button>
          </footer>
        </section>
      </div>
    );
  const blocked = impact?.blocked
    ? (impact.blockedReason ?? "This type can't be removed right now.")
    : null;
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <section
        ref={dialogRef}
        className="delete-job-modal remove-document-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-document-type-title"
        aria-busy={(!impact && !impactError) || removing}
        tabIndex={-1}
      >
        <header>
          <span>
            <Trash2 size={19} />
          </span>
          <div>
            <small>Remove from this job</small>
            <h2 id="remove-document-type-title">Remove “{type.name}”?</h2>
          </div>
          <button
            className="icon-button"
            onClick={close}
            disabled={removing}
            aria-label="Cancel removing document type"
          >
            <X size={17} />
          </button>
        </header>
        {!impact && !impactError && (
          <p className="remove-type-status" role="status">
            <LoaderCircle className="spin" size={14} /> Checking what changes…
          </p>
        )}
        {impactError && (
          <div className="remove-type-error" role="alert" data-telemetry="error:type-remove-failed">
            <TriangleAlert size={15} />
            <span>
              <span>
                Paige couldn't check what removing this type would change.{' '}
                {operatorMessage(impactError)}
              </span>
              <TechnicalDetails detail={splitTechnicalDetails(impactError).detail ?? impactError} />
            </span>
          </div>
        )}
        {impact && (
          <ul className="remove-type-impact" aria-label="What changes">
            <li>
              <ClipboardList size={16} aria-hidden="true" />
              <span>{typeRemovalReviewLine(impact, autopilot)}</span>
            </li>
            {impact.documentsExported > 0 && (
              <li>
                <CheckCircle2 size={16} aria-hidden="true" />
                <span>
                  {counted(impact.documentsExported, 'exported document')}{' '}
                  {impact.documentsExported === 1 ? 'stays' : 'stay'} as{' '}
                  {impact.documentsExported === 1 ? 'it is' : 'they are'}
                </span>
              </li>
            )}
            <li>
              <Brain size={16} aria-hidden="true" />
              <span>
                What Cortexa learned about this type will be forgotten. Other types are not
                affected.
                {impact.confirmedDocuments > 0 && (
                  <small>
                    It learned from {counted(impact.confirmedDocuments, 'confirmed document')}.
                  </small>
                )}
              </span>
            </li>
            {/* Not on Autopilot with documents to review: the line above already says they go to the exceptions folder.
            With none, that line says only that none need a new type, so this says it was the only type, of the
            documents still to come. With one, it names that one. */}
            {impact.lastType && !(autopilot && impact.documentsToReview > 0) && (
              <li className="is-note">
                <Info size={16} aria-hidden="true" />
                <span>
                  It's this job's only type. Add another so{' '}
                  {impact.documentsToReview === 1
                    ? 'this document'
                    : impact.documentsToReview > 1
                      ? 'these documents'
                      : 'new documents'}{' '}
                  can be filed.
                </span>
              </li>
            )}
          </ul>
        )}
        {blocked && (
          <div
            className="remove-type-error"
            role="alert"
            data-telemetry="error:type-remove-refused"
          >
            <TriangleAlert size={15} />
            <span>{blocked}</span>
          </div>
        )}
        {removeError && (
          <div className="remove-type-error" role="alert" data-telemetry="error:type-remove-failed">
            <TriangleAlert size={15} />
            <span>
              <span>The type was not removed. {operatorMessage(removeError)}</span>
              <TechnicalDetails detail={splitTechnicalDetails(removeError).detail ?? removeError} />
            </span>
          </div>
        )}
        <footer>
          <button className="secondary-button" onClick={close} disabled={removing}>
            Cancel
          </button>
          <button
            className="danger-button"
            onClick={() => void remove()}
            disabled={!impact || Boolean(blocked) || removing}
          >
            {removing ? <LoaderCircle className="spin" size={15} /> : <Trash2 size={15} />}{' '}
            {removing ? 'Removing…' : 'Remove type'}
          </button>
        </footer>
      </section>
    </div>
  );
}

// 'overview' is Paige's: its sources, email intake, retention and send-results cards live there
// (SetupOverview); CapturePoint's setup opens on Document types and has no overview.
type JobSetupSection =
  | 'overview'
  | 'document-types'
  | 'document-starts'
  | 'thresholds'
  | 'learning'
  | 'lookups'
  | 'watched-folder'
  | 'export'
  | 'phones';
const DOCUMENT_START_LABELS: Record<Job['separationMode'], string> = {
  [SEPARATION.automatic]: 'Automatic',
  [SEPARATION.automaticBarcode]: 'Automatic + barcodes',
  [SEPARATION.existingBarcode]: 'Matching barcodes',
  [SEPARATION.capturePointSeparator]: 'Break sheets',
  [SEPARATION.filePerDocument]: 'One per file',
};
type SetupExitOutcome =
  { state: 'applied' | 'busy' | 'blocked' } | { state: 'failed'; message: string };

export function JobWorkspace({
  workspaceNavigation,
  workspaceJobSelector,
  setupEntry,
  job,
  mode,
  batch,
  onDocumentCount,
  onJobs,
  folderAnalysisStatus,
  onFolderAnalysisStatus,
  onFolderAnalysisAbandoned,
  whenSetupContinued,
  onSetupStopped = () => {},
  onSetupResumed = () => {},
  externalActivity,
  externalFailure,
  folderAnalysisProgress,
  folderActivity,
  folderWork,
  processorOnlyExtraction,
  onDismissExternalFailure,
  onChooseRepresentativeSource,
  onBatch,
  onBootstrapAnalyzed,
  onAnalyzeMore,
  onFolderAccepted,
  onWorkspaceRefresh,
  onPersistedJobUpdated,
  onModeChange,
  onChange,
  onCommit,
  onSaveAlongside,
  onToast,
  onOpenCluster,
  learningOpen = false,
  covered = false,
  teachingDocumentId,
  onTeachingDocumentOpened,
  learningPanel,
  suggestionApplyRequest,
  onSuggestionApplyConsumed,
  teachRequested = false,
  onTeachRequestConsumed,
  canReturnToNewJob = false,
}: {
  workspaceNavigation?: ReactNode;
  workspaceJobSelector?: ReactNode;
  setupEntry?: { section: JobSetupSection; stop?: ExportStop; request: number } | null;
  job: Job;
  mode: JobMode;
  batch: ProcessResult | null;
  onDocumentCount?: (count: number) => void;
  onJobs: () => void;
  folderAnalysisStatus?: FolderAnalysisStatus;
  onFolderAnalysisStatus: (jobId: string, status: FolderAnalysisStatus) => void;
  onFolderAnalysisAbandoned?: (jobId: string, previousSetupKept: boolean) => void;
  whenSetupContinued?: (jobId: string) => Promise<void> | undefined;
  onSetupStopped?: (jobId: string) => void;
  onSetupResumed?: (jobId: string) => void;
  externalActivity?: string;
  externalFailure?: string;
  folderAnalysisProgress?: FolderAnalysisProgress;
  folderActivity: FolderActivityEntry[];
  folderWork: FolderWorkTask[];
  processorOnlyExtraction: boolean;
  onDismissExternalFailure: () => void;
  onChooseRepresentativeSource: (job: Job, source: 'files' | 'folder') => Promise<void>;
  onBatch: (result: ProcessResult) => void;
  onBootstrapAnalyzed: (jobId: string, result: ProcessResult) => void;
  onAnalyzeMore: (job: Job, batch: ProcessResult) => Promise<Job | void>;
  onFolderAccepted: () => void;
  onWorkspaceRefresh: () => Promise<void>;
  onPersistedJobUpdated?: (job: Job) => void;
  onModeChange: (mode: JobMode) => void;
  onChange: (job: Job) => void;
  onCommit: (job: Job) => Promise<Job>;
  /** Saves `change` of the saved job beside Job setup's open draft (which keeps its edits); undefined when there is no saved job to change. */
  onSaveAlongside?: (jobId: string, change: (saved: Job) => Job) => Promise<Job | undefined>;
  onToast: PublishNotice;
  onOpenCluster: () => void;
  learningOpen?: boolean;
  /**
   * PAIGE (docs/engineering/workbench-port.md): another screen is over this job (the Jobs picker,
   * the new-job dialog). Job setup is portalled above Paige's workbench layer, so it steps aside
   * rather than covering the screen that was opened over it.
   */
  covered?: boolean;
  teachingDocumentId?: string;
  onTeachingDocumentOpened?: () => void;
  /** Learning for this job, shown inline as a Job setup section. */
  learningPanel?: ReactNode;
  suggestionApplyRequest?: SuggestionApplyRequest | null;
  onSuggestionApplyConsumed?: () => void;
  teachRequested?: boolean;
  onTeachRequestConsumed?: () => void;
  /** The empty job New job just made: its start screen offers Back to New job, where leaving discards it. */
  canReturnToNewJob?: boolean;
}) {
  const setupControlId = useId();
  const setupWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const typeActionsRef = useRef<HTMLDivElement | null>(null);
  const typeActionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const typeBackRef = useRef<HTMLButtonElement | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState(job.documentTypes[0]?.id ?? '');
  const [resetLearningSelection, setResetLearningSelection] =
    useState<ResetLearningSelection | null>(null);
  // A nonempty missing id means an asynchronous proposal refresh removed the
  // selection. Do not silently paint another type under an Add-field draft;
  // the reconciliation effect below clears that type-owned UI first.
  const selectedType =
    job.documentTypes.find((item) => item.id === selectedTypeId) ??
    (selectedTypeId ? undefined : job.documentTypes[0]);
  const [typeSearch, setTypeSearch] = useState('');
  // Document types opens on the deck of all types; a type's editor is one step in.
  const [typeView, setTypeView] = useState<'deck' | 'type'>('deck');
  useEffect(() => {
    setTypeSearch('');
    setTypeView('deck');
  }, [job.id]);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // PAIGE (docs/engineering/workbench-port.md): the desktop's main process holds a setup run to its
  // end and applies the proposal even if the window was replaced; in a browser the tab that started
  // the run applies it, so a tab closed mid-setup left the job typeless and its setup empty, with the
  // proposal sitting in the stored batch. A typeless provisional job adopts it when its setup opens,
  // as CapturePreview does for a typeless job's documents. The job is read again when the batch
  // arrives: a setup that finished meanwhile has its types and may already be saved as active, and
  // adopting into the job as it was when the effect began wrote it back as provisional, so the
  // handoff to Documents was undone (2026-10-01, workbench-port.md).
  useEffect(() => {
    if (hostHas('job-library') || job.status !== 'provisional' || job.documentTypes.length > 0)
      return;
    let live = true;
    void window.capturePointDesktop
      ?.getLatestBatch(job.id)
      .then((full) => {
        if (!live || !full?.bootstrapProposal?.documentTypes.length) return;
        const current = liveSetupJob.current;
        if (current.status !== 'provisional' || current.documentTypes.length > 0) return;
        const adopted = applyBootstrapProposal(current, full);
        if (adopted !== current) void onCommit(adopted).catch(() => undefined);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [job.id]);
  const [setupSection, setSetupSection] = useState<JobSetupSection>(
    job.status === 'provisional' ? 'document-types' : 'overview',
  );
  const [thresholdReturn, setThresholdReturn] = useState<{
    typeId: string;
    fieldId: string;
    name: string;
  } | null>(null);
  const [typeReport, setTypeReport] = useState<LearningAccelerationReport | null>(null);
  const [typeReportLoading, setTypeReportLoading] = useState(false);
  // What Learning reads for its skill states, so each type card can say what Learning says.
  const [typeLearning, setTypeLearning] = useState<{
    eligibility: LearningEligibility | null;
    fields: LearningFieldMeasurement[];
    tables: LearningTableMeasurement[];
  }>({ eligibility: null, fields: [], tables: [] });
  useEffect(() => {
    if (mode !== 'setup') return;
    let current = true;
    setTypeReport(null);
    setTypeReportLoading(true);
    setTypeLearning({ eligibility: null, fields: [], tables: [] });
    const api = window.capturePointDesktop;
    if (!api?.getLearningAcceleration) {
      setTypeReportLoading(false);
      return;
    }
    void api
      .getLearningAcceleration(job.id)
      .then((report) => {
        if (current) setTypeReport(report);
      })
      .catch(() => undefined)
      .finally(() => {
        if (current) setTypeReportLoading(false);
      });
    // A read that fails leaves its part unreported, and the card claims nothing past it.
    void Promise.all([
      Promise.resolve()
        .then(() => api.learningEligibility?.(job.id) ?? null)
        .catch(() => null),
      Promise.resolve()
        .then(() => api.learningFieldResults?.(job.id) ?? [])
        .catch(() => []),
      Promise.resolve()
        .then(() => api.learningTableResults?.(job.id) ?? [])
        .catch(() => []),
    ]).then(([eligibility, fields, tables]) => {
      if (current) setTypeLearning({ eligibility, fields, tables });
    });
    return () => {
      current = false;
    };
    // PAIGE (docs/engineering/workbench-port.md): read again when the setup's reading lands. Setup
    // from samples opens this screen before any page is read, so the one read was of an empty job
    // and every proposed type's card stayed an outline reading "No documents yet".
  }, [job.id, mode, learningOpen, batch?.batchId, batch?.completedAt]);
  const typeLearningStates = useMemo(
    () =>
      new Map(
        projectLearningSkills({
          job,
          eligibility: typeLearning.eligibility,
          report: typeReport,
          fieldResults: typeLearning.fields,
          tableResults: typeLearning.tables,
        })
          .filter((group) => group.kind === 'type')
          .map((group) => [group.key, typeLearningState(group)]),
      ),
    [job, typeLearning, typeReport],
  );
  const [exportSetupVisited, setExportSetupVisited] = useState(false);
  const exportDraftControl = useRef<{ dirty: boolean; save: () => Promise<boolean> } | null>(null);
  const setupBaseline = useRef(job);
  const setupVisit = useRef({ jobId: job.id, mode });
  // The setup map and the export screen are complete on their first render: Paige drops its held skeleton.
  useEffect(() => {
    if (mode !== 'capture') window.paigeWorkbench?.opened?.();
  }, [mode]);
  // A first setup's proposal is committed into the job while Job setup is open, after
  // this visit's baseline was taken. It is not an edit to throw away: Discard and Close
  // without saving go back to the job as the proposal landed (with its types), while
  // the proposal itself still reads as unsaved until Save and close accepts it, and
  // edits made after it landed are still changes.
  const proposalIdentity =
    job.status === 'provisional' &&
    usesRepresentativeBootstrap(job) &&
    job.documentTypes.length > 0 &&
    batch
      ? `${batch.batchId}|${batch.completedAt}|${folderAnalysisStatus?.evidenceRevision ?? 0}`
      : '';
  const proposalLanding = useRef<{ identity: string; baseline: Job; job: Job | null }>({
    identity: proposalIdentity,
    baseline: job,
    job: null,
  });
  // The workspace stays mounted while documents are being reviewed. Those
  // durable job updates are not unsaved edits on the next setup visit.
  if (setupVisit.current.jobId !== job.id || setupVisit.current.mode !== mode) {
    setupBaseline.current = job;
    setupVisit.current = { jobId: job.id, mode };
    proposalLanding.current = { identity: proposalIdentity, baseline: job, job: null };
  } else if (proposalLanding.current.identity !== proposalIdentity) {
    proposalLanding.current = {
      identity: proposalIdentity,
      baseline: setupBaseline.current,
      job: proposalIdentity ? job : null,
    };
  }
  /** What Discard restores: the landed proposal while this visit's baseline still stands (no save since), else the baseline. */
  const setupDiscardTarget = () =>
    proposalLanding.current.job && proposalLanding.current.baseline === setupBaseline.current
      ? proposalLanding.current.job
      : setupBaseline.current;
  const [closeSetupPrompt, setCloseSetupPrompt] = useState(false);
  const [discardSetupPrompt, setDiscardSetupPrompt] = useState(false);
  // Discarding remounts Export's own draft, which keeps its edits inside the card.
  const [exportDraftGeneration, setExportDraftGeneration] = useState(0);
  const journeyNavigation = useRef<LookupJourneyNavigationGuard | null>(null);
  const [journeyBusy, setJourneyBusy] = useState(false);
  const updateJourneyGuard = useCallback((guard: LookupJourneyNavigationGuard | null) => {
    journeyNavigation.current = guard;
    setJourneyBusy(guard?.busy ?? false);
  }, []);
  const setupExitDestination = useRef<(() => void) | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [exportHasChanges, setExportHasChanges] = useState(false);
  useEffect(() => {
    const saved = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          jobId: string;
          learningStance: Job['learningStance'];
          schemaVersion?: number;
          updatedAt?: string;
        }>
      ).detail;
      if (detail?.jobId !== job.id) return;
      // The stance and the version the engine gave that save; a sender without a version keeps the baseline's.
      const saved: Partial<Job> = {
        learningStance: detail.learningStance,
        ...(detail.schemaVersion === undefined ? {} : { schemaVersion: detail.schemaVersion }),
        ...(detail.updatedAt === undefined ? {} : { updatedAt: detail.updatedAt }),
      };
      const landing = proposalLanding.current;
      const landed = landing.baseline === setupBaseline.current;
      setupBaseline.current = { ...setupBaseline.current, ...saved };
      if (landed)
        proposalLanding.current = {
          ...landing,
          baseline: setupBaseline.current,
          job: landing.job && { ...landing.job, ...saved },
        };
    };
    window.addEventListener('capturepoint-learning-settings-saved', saved);
    return () => window.removeEventListener('capturepoint-learning-settings-saved', saved);
  }, [job.id]);
  const setupHasChanges =
    setupSnapshotKey(job) !== setupSnapshotKey(setupBaseline.current) || exportHasChanges;
  // PAIGE (docs/engineering/web.md, "When Paige is updated"): unsaved setup holds the page, so
  // Paige never reloads itself onto a newer version over it.
  useEffect(() => (setupHasChanges ? holdPage('setup-edits') : undefined), [setupHasChanges]);
  const closeSetupDestination = () => {
    if (setupExitDestination.current) {
      const leave = setupExitDestination.current;
      setupExitDestination.current = null;
      leave();
      return;
    }
    // A host with no job library has nowhere else to send a provisional job: closing a setup
    // that has document types in it is the person keeping it, so the job is activated (the save
    // Paige takes as the setup's acceptance) and Documents opens.
    if (job.status === 'provisional' && !hostHas('job-library') && job.documentTypes.length > 0) {
      void acceptProposalAndOpen();
      return;
    }
    // PAIGE (docs/engineering/workbench-port.md): Paige hides Scan jobs; its jobs home is the
    // dashboard. Scan jobs opened here as a Jobs picker over this same setup, and closing the
    // picker reopened it (production, 2026-09-30).
    if (job.status === 'provisional' && usesRepresentativeBootstrap(job) && window.paigeWorkbench) {
      window.paigeWorkbench.leave();
      return;
    }
    // A provisional job cannot enter Documents. Closing its modal must lead to
    // Scan jobs, not request Documents and immediately reopen the same setup.
    // The live job, not this render's: a Save and close that just accepted the
    // proposed setup has made it active (leaveSetup sets it before this runs).
    const current = liveSetupJob.current;
    if (current.status === 'provisional' && usesRepresentativeBootstrap(current)) onJobs();
    else onModeChange('capture');
  };
  const acceptProposalAndOpen = async () => {
    const outcome = await leaveSetup(true, true);
    if (outcome.state === 'applied') onModeChange('capture');
  };
  /**
   * Close Job setup, going on to `destination` when one asked (the work screen's own way
   * on). The destination travels with this one request and is set only as setup actually
   * closes or asks to save: set up front, a lookup editor's declined leave left it behind,
   * and a later plain close went there.
   */
  const requestSetupClose = (destination?: () => void) => {
    if (journeyNavigation.current) {
      journeyNavigation.current.requestLeave(() => {
        setJourneyFieldId(null);
        closeSetupAfterJourney(destination);
      });
      return;
    }
    closeSetupAfterJourney(destination);
  };
  const closeSetupAfterJourney = (destination?: () => void) => {
    setupExitDestination.current = destination ?? null;
    if (
      setupSnapshotKey(job) !== setupSnapshotKey(setupBaseline.current) ||
      exportDraftControl.current?.dirty
    )
      setCloseSetupPrompt(true);
    else closeSetupDestination();
  };
  const requestSetupCloseRef = useRef(requestSetupClose);
  requestSetupCloseRef.current = requestSetupClose;
  useEffect(() => {
    if (mode !== 'setup') return;
    const leave = (event: Event) => {
      const detail = (event as CustomEvent<{ jobId: string; continueToWork: () => void }>).detail;
      if (detail?.jobId !== job.id) return;
      requestSetupCloseRef.current(detail.continueToWork);
    };
    window.addEventListener('capturepoint-setup-leave', leave);
    return () => window.removeEventListener('capturepoint-setup-leave', leave);
  }, [job.id, mode]);
  useEffect(() => {
    if (mode !== 'setup' || setupSection !== 'thresholds') return;
    let live = true;
    let pending = false;
    const refresh = async () => {
      if (pending) return;
      pending = true;
      try {
        const profile = await window.capturePointDesktop?.getReviewImpactProfile(job.id);
        if (live && profile) setReviewImpactRead({ jobId: job.id, profile });
      } catch {
        /* Keep the last response on a transient native read failure. */
      } finally {
        pending = false;
      }
    };
    const timer = window.setInterval(() => {
      void refresh();
    }, 3000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [job.id, mode, setupSection]);
  const openSetupSection = useCallback((next: JobSetupSection) => {
    if (next === 'export') setExportSetupVisited(true);
    setSetupSection(next);
    setSampleOpen(false);
    window.requestAnimationFrame(() => focusActiveWorkspaceSection(setupWorkspaceRef.current));
  }, []);
  // Opening Job setup at one of Export's stops (Needs review's next step) asks Export for that stop.
  const [exportStopRequest, setExportStopRequest] = useState<{
    stop: ExportStop;
    request: number;
  } | null>(null);
  // Each door request opens its section once. The shell keeps its last request, so without this
  // every later visit to Job setup (All setup, a close and reopen) replayed the last door tile and
  // sent Export its stop again. One already there when this job's workspace mounts was for an
  // earlier visit.
  const handledSetupEntry = useRef(setupEntry?.request ?? 0);
  useEffect(() => {
    if (mode !== 'setup' || !setupEntry || handledSetupEntry.current === setupEntry.request) return;
    handledSetupEntry.current = setupEntry.request;
    if (setupEntry.section === 'export' && setupEntry.stop) {
      const stop = setupEntry.stop;
      setExportStopRequest((current) => ({ stop, request: (current?.request ?? 0) + 1 }));
    }
    openSetupSection(setupEntry.section);
  }, [mode, setupEntry?.request]);
  const [showAdd, setShowAdd] = useState(false);
  const [typeActionsOpen, setTypeActionsOpen] = useState(false);
  const [manuallyAddedTypeIds, setManuallyAddedTypeIds] = useState<Set<string>>(() => new Set());
  const [typePendingRemovalId, setTypePendingRemovalId] = useState<string | null>(null);
  // A saved type is removed by the engine (with its learning); an unsaved hand-added one leaves the draft.
  const [typePendingRemovalSaved, setTypePendingRemovalSaved] = useState(false);
  const typeRemovalDone = useRef(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldKind, setNewFieldKind] = useState<FieldKind>('text');
  const selectDocumentType = useCallback((type: DocumentTypeDefinition | undefined) => {
    setTypeActionsOpen(false);
    setShowAdd(false);
    setNewFieldName('');
    setSelectedTypeId(type?.id ?? '');
    setSampleOpen(false);
    setExpanded(null);
  }, []);
  useEffect(() => {
    if (
      job.documentTypes.some((type) => type.id === selectedTypeId) ||
      (!selectedTypeId && job.documentTypes.length === 0)
    )
      return;
    selectDocumentType(job.documentTypes[0]);
  }, [job.documentTypes, selectDocumentType, selectedTypeId]);
  const [setupApplying, setSetupApplying] = useState(false);
  const setupApplyingRef = useRef(false);
  const representativeHandoffRef = useRef(false);
  const [representativeStaged, setRepresentativeStaged] = useState<number | null>(null);
  // THE WIZARD WAITS FOR SETUP'S DOCUMENTS (setup-handoff.ts, owner 2026-10-03). After an
  // accepted setup the work screen runs behind the wizard; the wizard stays over it, counting
  // the documents, until none is waiting or being processed. Remembered per job, so a remount,
  // a reload or coming back to the job comes back to the wizard, never to step 1.
  // PAIGE: setup read the whole selection, so no import follows it; the job's card on the API
  // says where Paige has got with those documents (paige/setup-processing.ts).
  const heldSetup = useHeldSetupHandoff(job.id);
  const setupWizardHolds = mode === 'capture' && job.status === 'active' && Boolean(heldSetup);
  const heldImport = useSetupProcessing(job.id, setupWizardHolds ? heldSetup : null);
  const setupImportDone = Boolean(heldImport?.loaded && !heldImport.busy);
  const heldDocuments = heldSetup?.documents ?? 0;
  // Documents done, never fewer than the wizard has shown (a reload included).
  const heldRead = !heldSetup
    ? 0
    : Math.min(
        heldDocuments,
        Math.max(
          heldSetup.read ?? 0,
          !heldImport
            ? 0
            : setupImportDone && !heldImport.failed
              ? heldDocuments
              : heldImport.total !== null && heldImport.total <= heldDocuments
                ? heldDocuments - heldImport.total + heldImport.read
                : heldImport.loaded
                  ? heldDocuments - heldImport.queuedFiles
                  : 0,
        ),
      );
  useEffect(() => {
    if (setupWizardHolds && heldRead > 0) noteSetupHandoffRead(job.id, heldRead);
  }, [setupWizardHolds, heldRead, job.id]);
  // When "Ready to check · Start checking" was first offered (telemetry setup.handoff: stayed rather than auto).
  const readyOfferedAt = useRef<{ jobId: string; at: number } | null>(null);
  useEffect(() => {
    if (
      setupWizardHolds &&
      heldImport?.firstBatchReady &&
      heldImport.documents > 0 &&
      readyOfferedAt.current?.jobId !== job.id
    )
      readyOfferedAt.current = { jobId: job.id, at: Date.now() };
  }, [setupWizardHolds, heldImport?.firstBatchReady, heldImport?.documents, job.id]);
  /** The wizard hands the job over: telemetry says how (setup.handoff) and how long its documents were read. */
  const handOver = (by: 'button' | 'close' | 'done') => {
    const held = heldSetupHandoff(job.id);
    if (!held) return;
    const offered =
      readyOfferedAt.current?.jobId === job.id && Date.now() - readyOfferedAt.current.at >= 2_000;
    reportSetupHandoff(job.id, setupHandoffHow(by, offered), Date.now() - held.at);
    landSetupHandoff(job.id);
  };
  useEffect(() => {
    if (setupWizardHolds && setupImportDone) handOver('done');
  }, [setupWizardHolds, setupImportDone, job.id]);
  const openJobNow = () => handOver('close');
  const startCheckingNow = () => handOver('button');
  // PAIGE: the page of the document the count has reached, from the documents setup read.
  const heldPreviewPath =
    batch?.documents[Math.min(heldRead, Math.max(0, (batch?.documents.length ?? 1) - 1))]?.pages[0]
      ?.previewPath ?? null;
  // What setup learned, as the setup screen shows it once learning is done: the saved types and their fields.
  const learnedTypes = useMemo(
    () =>
      job.documentTypes
        .filter((type) => type.status !== 'archived')
        .map((type) => ({ name: type.name, fields: type.fields.map((field) => field.name) })),
    [job.documentTypes],
  );
  // "resume" until Try again's setup is running again, then "resumed" while it runs: stopped, it reads "Stopping…" until
  // its call comes back, where it said "Trying again…" (October 7, 2026).
  const [folderOperationDecision, setFolderOperationDecision] = useState<
    'resume' | 'resumed' | 'abandon' | null
  >(null);
  const [folderOperationDecisionError, setFolderOperationDecisionError] = useState('');
  // Try again was refused before the stopped setup ran again: the banner says that, not what it said before the press.
  const [resumeDidNotStart, setResumeDidNotStart] = useState(false);
  // Main answers Stop at once, but this window's own call for the stopped setup (a first setup, choosing the
  // documents again, Read more, a setup continued after a restart) comes back about a second later, once the setup has unwound, and until then main
  // refuses to take the setup up again or clear it. A Try again pressed in that second was refused and put the person
  // back on "Setup did not finish" (October 7, 2026). Try again and Start over wait for that call instead, at most
  // half a minute: past that main is asked anyway, and a refusal is said plainly.
  const setupCallOut = useRef<{ out: boolean; waiting: (() => void)[] }>({
    out: false,
    waiting: [],
  });
  setupCallOut.current.out = Boolean(externalActivity);
  useEffect(() => {
    if (!externalActivity) setupCallOut.current.waiting.splice(0).forEach((resume) => resume());
  }, [externalActivity]);
  const untilSetupCallBack = () =>
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 30_000);
      const continuing = whenSetupContinued?.(job.id);
      const afterCall = () => {
        if (setupCallOut.current.out) setupCallOut.current.waiting.push(resolve);
        else resolve();
      };
      if (continuing) void continuing.then(afterCall);
      else afterCall();
    });
  const folderStatusNow = useRef(folderAnalysisStatus);
  folderStatusNow.current = folderAnalysisStatus;
  /**
   * Whether the stopped setup a Try again or Start over was pressed on is still the one shown, once this window's own
   * call for it has come back. Its call may have come back finished after all, or the setup changed: then there is
   * nothing left to decide, and the decision still names only the operation and revision the person saw.
   */
  const stoppedSetupStillShown = async (shown: FolderAnalysisStatus) => {
    await untilSetupCallBack();
    const status = folderStatusNow.current;
    return (
      status?.operationId === shown.operationId &&
      status.evidenceRevision === shown.evidenceRevision &&
      ['recoverable', 'failed', 'conflicted'].includes(status.state)
    );
  };
  const folderAnalysisDecisionRequired = Boolean(
    folderAnalysisStatus?.operationId &&
    ['recoverable', 'failed', 'conflicted'].includes(folderAnalysisStatus.state),
  );
  const folderAnalysisNeedsFreshStart =
    folderAnalysisStatus?.state === 'failed' && folderAnalysisStatus.resumeAvailable === false;
  /** Try again's setup was stopped and its call is still coming back. */
  const resumedSetupStopping =
    folderOperationDecision === 'resumed' && stoppedByPerson(folderAnalysisStatus?.failure);
  const folderAnalysisOwnsSetup =
    mode === 'setup' &&
    (Boolean(externalActivity) ||
      folderAnalysisStatus?.running === true ||
      folderAnalysisDecisionRequired);
  const [autoUseFailure, setAutoUseFailure] = useState<RepresentativeAutoFailure | null>(() =>
    readRepresentativeAutoFailure(job.id),
  );
  const autoAttemptedProposalKey = useRef('');
  const proposalEvidenceSaveLane = useRef<Promise<void>>(Promise.resolve());
  const analyzeMoreStartingRef = useRef(false);
  const [analyzeMoreStarting, setAnalyzeMoreStarting] = useState(false);
  const liveSetupJob = useRef(job);
  const liveProposalBatch = useRef(batch);
  liveSetupJob.current = job;
  liveProposalBatch.current = batch;
  const setupDraftRevision = useRef(0);
  const changeJob = useCallback(
    (next: Job) => {
      // Saving/Analyze-more owns a frozen job snapshot. Every setup surface —
      // fields, boundaries, review policy, lookups, and learning stance — funnels
      // through this guard so a late gesture cannot appear to save and then vanish.
      if (setupApplyingRef.current || analyzeMoreStartingRef.current || folderAnalysisOwnsSetup)
        return;
      setupDraftRevision.current += 1;
      liveSetupJob.current = next;
      onChange(next);
    },
    [folderAnalysisOwnsSetup, onChange],
  );
  const [schemaBindingRetry, setSchemaBindingRetry] = useState(0);
  const [schemaBindingCheck, setSchemaBindingCheck] = useState<{
    jobId: string;
    state: 'loading' | 'ready' | 'error';
    binding: JobDestinationBinding | null;
    message: string;
  }>({ jobId: '', state: 'loading', binding: null, message: '' });
  const proposalReview =
    mode === 'setup' &&
    job.status === 'provisional' &&
    job.documentTypes.length > 0 &&
    batch !== null;
  // Representative evidence is setup, not a batch. Keep the safe Setup route
  // mounted while native work owns the proposal, but do not leave the operator
  // staring at the empty schema editor before that proposal exists. An
  // undefined status is the short renderer-replacement window before main's
  // durable operation state has been hydrated; showing honest "Starting"
  // progress there avoids both a blank flash and a CapturePreview mount.
  // The proposal and final ready status arrive independently. A proposal must
  // not expose the editor while native analysis still owns the operation.
  const representativeSetupProgressVisible =
    mode === 'setup' &&
    job.status === 'provisional' &&
    usesRepresentativeBootstrap(job) &&
    (folderAnalysisStatus?.running ||
      (folderAnalysisStatus?.state === 'idle' && Boolean(folderAnalysisProgress)) ||
      (folderAnalysisStatus?.state === 'ready' &&
        folderAnalysisStatus.autoAcceptEligible === true) ||
      (!proposalReview && (folderAnalysisStatus === undefined || Boolean(externalActivity))));
  // A rejection can happen before main has created a durable analysis
  // operation (licensing, expired selection, or sidecar creation). In that
  // window there is no native failed status to render, so the shell-owned
  // failure is the authoritative Setup state and must carry its own retry.
  // A setup that stopped with its place saved (the engine stopped mid-setup) is offered as Resume
  // on the same screen; choosing the folder again is the other choice, never the only one.
  const resumableAfterFailure =
    folderAnalysisStatus?.state === 'recoverable' &&
    Boolean(folderAnalysisStatus.operationId) &&
    folderAnalysisStatus.resumeAvailable !== false;
  const representativeSetupFailure =
    mode === 'setup' &&
    job.status === 'provisional' &&
    usesRepresentativeBootstrap(job) &&
    !proposalReview &&
    !externalActivity &&
    Boolean(externalFailure) &&
    (!folderAnalysisDecisionRequired || resumableAfterFailure);
  // Telemetry (setup-signals.ts): setup's last stage ends when its proposal is up for review. Timed on, the
  // "values" stage also counted the review and any Read more after it.
  const reviewingProposal = proposalReview && !representativeSetupProgressVisible;
  useEffect(() => {
    if (reviewingProposal) finishSetupStages(job.id);
  }, [reviewingProposal, job.id]);
  useEffect(() => {
    setSetupSection('document-types');
    setTypeActionsOpen(false);
    setManuallyAddedTypeIds(new Set());
    setTypePendingRemovalId(null);
  }, [job.id]);

  // A removable type belongs only to the setup visit in which it was created.
  // Clear that authority as soon as the job becomes operational or the operator
  // leaves Setup; returning later starts a new visit even though this component
  // remains mounted for the same job.
  useEffect(() => {
    if (mode === 'setup' && job.status === 'provisional') return;
    setTypeActionsOpen(false);
    setManuallyAddedTypeIds(new Set());
    setTypePendingRemovalId(null);
  }, [job.status, mode]);

  useEffect(() => {
    setTypeActionsOpen(false);
  }, [selectedType?.id]);

  useEffect(() => {
    if (!typeActionsOpen) return;
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !typeActionsRef.current?.contains(event.target))
        setTypeActionsOpen(false);
    };
    const dismissWithKeyboard = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setTypeActionsOpen(false);
      window.requestAnimationFrame(() => typeActionsButtonRef.current?.focus());
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissWithKeyboard);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissWithKeyboard);
    };
  }, [typeActionsOpen]);

  useEffect(() => {
    if (mode !== 'setup') return;
    let live = true;
    const api = window.capturePointDesktop;
    setSchemaBindingCheck({ jobId: job.id, state: 'loading', binding: null, message: '' });
    if (!api) {
      setSchemaBindingCheck({
        jobId: job.id,
        state: 'error',
        binding: null,
        message: 'Paige could not verify the destination for this job.',
      });
      return;
    }
    void api
      .getJobDestinationBinding(job.id)
      .then((binding) => {
        if (live) setSchemaBindingCheck({ jobId: job.id, state: 'ready', binding, message: '' });
      })
      .catch((reason: unknown) => {
        if (live)
          setSchemaBindingCheck({
            jobId: job.id,
            state: 'error',
            binding: null,
            message: messageFor(reason, 'Paige could not verify the destination for this job.'),
          });
      });
    return () => {
      live = false;
    };
  }, [job.id, mode, schemaBindingRetry]);

  const currentSchemaBindingCheck = schemaBindingCheck.jobId === job.id ? schemaBindingCheck : null;
  const schemaBindingPending =
    mode === 'setup' &&
    (!currentSchemaBindingCheck || currentSchemaBindingCheck.state === 'loading');
  const schemaBindingFailed = mode === 'setup' && currentSchemaBindingCheck?.state === 'error';
  const destinationOwnsSchema =
    currentSchemaBindingCheck?.binding?.schemaAuthority === 'destination';
  // Unknown ownership is locked, not guessed. A short unavailable connection
  // must not let the renderer offer a schema edit the engine will reject later.
  const schemaLocked =
    mode === 'setup' &&
    (schemaBindingPending ||
      schemaBindingFailed ||
      destinationOwnsSchema ||
      folderAnalysisOwnsSetup ||
      analyzeMoreStarting ||
      setupApplying);
  useEffect(() => {
    if (!schemaLocked) return;
    setShowAdd(false);
    setTypeActionsOpen(false);
    setTypePendingRemovalId(null);
  }, [schemaLocked]);

  const projectDocumentToType = (
    document: ProcessResult['documents'][number],
    type: DocumentTypeDefinition,
  ) => {
    const recognized = new Map<string, FieldResult>();
    const recognizedBySemantic = new Map<string, FieldResult>();
    const semanticByFieldId = new Map(
      job.documentTypes.flatMap((definition) =>
        definition.fields
          .filter((item) => item.semanticId)
          .map((item) => [item.id, item.semanticId!] as const),
      ),
    );
    for (const item of document.fields) {
      if (item.fieldId) recognized.set(item.fieldId, item);
      const semanticId = item.fieldId ? semanticByFieldId.get(item.fieldId) : null;
      if (semanticId) recognizedBySemantic.set(semanticId, item);
      recognized.set(item.name.toLocaleLowerCase(), item);
    }
    const documentTypeField = document.fields.find((item) => item.name === 'Document type');
    return {
      ...document,
      documentType: type.name,
      documentTypeId: type.id,
      documentTypeSchemaVersion: type.schemaVersion,
      documentTypeConfidence: 1,
      documentTypeVerified: true,
      needsReview: false,
      fields: [
        {
          ...(documentTypeField ?? {
            name: 'Document type',
            value: type.name,
            confidence: 1,
            source: 'operator setup',
            needsReview: false,
            evidencePageNumber: document.pages[0]?.number ?? 1,
            evidenceLineIndexes: [],
          }),
          value: type.name,
          confidence: 1,
          source: 'operator setup',
          needsReview: false,
          verified: true,
        },
        ...type.fields
          .filter((item) => item.kind !== 'line-items')
          .map((configured) => {
            const source =
              recognized.get(configured.id) ??
              (configured.semanticId
                ? recognizedBySemantic.get(configured.semanticId)
                : undefined) ??
              recognized.get(configured.name.toLocaleLowerCase());
            return source
              ? {
                  ...source,
                  fieldId: configured.id,
                  fieldKind: configured.kind,
                  name: configured.name,
                }
              : {
                  fieldId: configured.id,
                  fieldKind: configured.kind,
                  name: configured.name,
                  value: '',
                  confidence: 0,
                  source: 'Not found in sample',
                  needsReview: configured.required,
                  evidencePageNumber: document.pages[0]?.number ?? 1,
                  evidenceLineIndexes: [],
                };
          }),
      ],
    };
  };
  /** A representative setup's samples as the person left them, saved in the proposal's own lane. */
  const persistProposalEvidence = (nextBatch: ProcessResult) => {
    liveProposalBatch.current = nextBatch;
    onBatch(nextBatch);
    const previousSave = proposalEvidenceSaveLane.current;
    const nextSave = previousSave
      .catch(() => undefined)
      .then(async () => {
        const api = window.capturePointDesktop;
        if (!api) throw new Error('Paige could not save the sample assignment');
        await api.saveFolderAnalysisEvidence({ jobId: job.id, result: nextBatch });
      });
    proposalEvidenceSaveLane.current = nextSave;
    void nextSave.catch((reason: unknown) =>
      onToast(
        reason instanceof Error ? reason.message : 'The sample assignment was not saved',
        'sample-move-failed',
      ),
    );
  };
  const persistProposalTypeChange = (
    nextBatch: ProcessResult,
    documentId: string,
    type: DocumentTypeDefinition,
    beforeType: string,
  ) => {
    if (usesRepresentativeBootstrap(job)) {
      persistProposalEvidence(nextBatch);
      return;
    }
    liveProposalBatch.current = nextBatch;
    onBatch(nextBatch);
    void window.capturePointDesktop
      ?.saveCorrection({
        jobId: job.id,
        batch: nextBatch,
        correction: {
          kind: CORRECTION.documentTypeCorrected,
          targetId: documentId,
          beforeJson: JSON.stringify({ documentType: beforeType }),
          afterJson: JSON.stringify({ documentType: type.name }),
          metadataJson: JSON.stringify({ source: 'proposal-review', documentTypeId: type.id }),
        },
      })
      .catch((reason: unknown) =>
        onToast(messageFor(reason, 'The sample assignment was not saved'), 'sample-move-failed'),
      );
  };
  const drainProposalEvidenceSaves = async () => {
    // The promise is a moving tail: an operator action while an earlier save is
    // settling appends another save. Only hand the manifest to another operation
    // once the awaited promise is still the tail after it resolves.
    while (true) {
      const pendingSave = proposalEvidenceSaveLane.current;
      await pendingSave;
      if (proposalEvidenceSaveLane.current === pendingSave) return;
    }
  };
  const readMoreDocuments = async () => {
    if (!liveProposalBatch.current || analyzeMoreStartingRef.current || setupApplyingRef.current)
      return;
    analyzeMoreStartingRef.current = true;
    // Invalidate an auto-tune requested before this cumulative analysis. Its
    // eventual result is older than the frozen setup snapshot handed off below.
    setupDraftRevision.current += 1;
    setAnalyzeMoreStarting(true);
    try {
      // A sample move/merge/split is ground truth. Drain the exact serialized
      // evidence lane before continuation reads and rewrites its manifest.
      await drainProposalEvidenceSaves();
      const currentBatch = liveProposalBatch.current;
      if (!currentBatch) return;
      await onAnalyzeMore(liveSetupJob.current, currentBatch);
    } catch (reason) {
      onToast(
        messageAfter('Save the sample changes before reading more', reason),
        'setup-read-more-failed',
      );
    } finally {
      analyzeMoreStartingRef.current = false;
      setAnalyzeMoreStarting(false);
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const moveProposalDocument = (documentId: string, typeId: string) => {
    if (!batch || analyzeMoreStartingRef.current || setupApplyingRef.current) return;
    const source = batch.documents.find((item) => item.id === documentId);
    if (!source) return;
    // Leave out (setup-sample-leave-out.ts): the sample leaves the job; choosing a type again puts it back.
    if (typeId === LEAVE_SAMPLE_OUT) {
      const from =
        job.documentTypes.find((item) => item.id === source.documentTypeId) ??
        job.documentTypes.find(
          (item) =>
            item.name.localeCompare(source.documentType, undefined, { sensitivity: 'accent' }) ===
            0,
        );
      if (!from || source.leftOutOfJob || !usesRepresentativeBootstrap(job)) return;
      const nextBatch = {
        ...batch,
        documents: batch.documents.map((item) =>
          item.id === documentId ? leaveSampleOut(item, from.id, from.name) : item,
        ),
      };
      persistProposalEvidence(nextBatch);
      onToast('Sample left out of this job');
      return;
    }
    if (typeId === '__new__') {
      if (schemaLocked) return;
      const nextType: DocumentTypeDefinition = {
        id: uid(),
        name: nextDocumentTypeName(job.documentTypes),
        semanticId: null,
        description: 'Created from a representative document during setup.',
        status: 'provisional',
        schemaVersion: 0,
        position: job.documentTypes.length,
        fields: source.fields
          .filter((item) => item.name !== 'Document type')
          .map((item, position) => ({
            id: uid(),
            name: item.name,
            semanticId: null,
            kind:
              item.fieldKind && Object.hasOwn(kindLabel, item.fieldKind)
                ? (item.fieldKind as FieldKind)
                : 'text',
            required: false,
            readText: true,
            readBarcode: Boolean(item.barcodeEvidence),
            description: 'Recognized from this sample',
            multiple: false,
            position,
          })),
      };
      const nextJob = { ...job, documentTypes: [...job.documentTypes, nextType] };
      const nextBatch = {
        ...batch,
        documents: batch.documents.map((item) =>
          item.id === documentId
            ? { ...projectDocumentToType(item, nextType), leftOutOfJob: null }
            : item,
        ),
      };
      changeJob(nextJob);
      persistProposalTypeChange(nextBatch, documentId, nextType, source.documentType);
      selectDocumentType(nextType);
      onToast('Created a new type from this sample');
      return;
    }
    const type = job.documentTypes.find((item) => item.id === typeId);
    if (!type || (source.documentTypeId === type.id && !source.leftOutOfJob)) return;
    const nextBatch = {
      ...batch,
      documents: batch.documents.map((item) =>
        item.id === documentId
          ? { ...projectDocumentToType(item, type), leftOutOfJob: null }
          : item,
      ),
    };
    persistProposalTypeChange(
      nextBatch,
      documentId,
      type,
      source.leftOutOfJob?.typeName ?? source.documentType,
    );
    onToast(source.leftOutOfJob ? `Sample back in ${type.name}` : `Moved sample to ${type.name}`);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const openLearningReset = (field?: Field) => {
    if (!selectedType || job.status !== 'active' || !window.capturePointDesktop?.resetLearning)
      return;
    if (setupHasChanges) {
      onToast('Save your setup changes before resetting learning.', 'reset-learning-refused');
      return;
    }
    if (captureDrainBlocked(job.id) || setupApplyingRef.current || analyzeMoreStartingRef.current) {
      onToast('Let the current save finish, then open Reset learning.', 'reset-learning-refused');
      return;
    }
    setResetLearningSelection({
      jobId: job.id,
      typeId: selectedType.id,
      typeName: selectedType.name,
      scope: field ? (field.kind === 'line-items' ? 'line-items' : 'field') : 'document-type',
      fieldId: field?.kind === 'line-items' ? undefined : field?.id,
      fieldName: field?.name,
      expectedSchemaVersion: job.schemaVersion,
    });
  };
  const updateType = (patch: Partial<DocumentTypeDefinition>) =>
    selectedType &&
    changeJob({
      ...job,
      documentTypes: job.documentTypes.map((item) =>
        item.id === selectedType.id ? { ...item, ...patch } : item,
      ),
    });
  const updateField = (id: string, patch: Partial<Field>) =>
    selectedType &&
    updateType({
      fields: selectedType.fields.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    });
  const deleteField = (id: string) =>
    !schemaLocked &&
    selectedType &&
    updateType({ fields: selectedType.fields.filter((item) => item.id !== id) });
  // A name another field of the type already has is not added: the row stays up and says which one.
  const newFieldTaken =
    showAdd && selectedType ? fieldNamed(selectedType.fields, newFieldName) : undefined;
  const addField = () => {
    if (
      schemaLocked ||
      !newFieldName.trim() ||
      !selectedType ||
      fieldNamed(selectedType.fields, newFieldName)
    )
      return;
    const next = field(
      newFieldName.trim(),
      newFieldKind,
      'Recognized automatically from the document',
    );
    updateType({ fields: [...selectedType.fields, next] });
    setExpanded(next.id);
    setNewFieldName('');
    setShowAdd(false);
    onToast('Field added');
  };
  // A name typed in the add row is not a field yet, but the route beside it still shows
  // the field chosen before. Moving on to any other control — that route's "Looks like",
  // Required or type, the list, Save — used to edit or save the field on screen, not the
  // one being named: the owner's TIN pattern landed on Business Name (2026-10-05). So
  // leaving the row with a name (a press anywhere else, or focus moving on) adds the field
  // first; when that press or focus was on a route control, focus moves to the same
  // control of the new field's route, so what is typed next lands there. A switch
  // pressed on the old route is not flipped on either field: one more press sets it.
  const pendingFieldAdd = useRef<() => void>(() => {});
  pendingFieldAdd.current = () => {
    if (showAdd && newFieldName.trim()) addField();
  };
  useEffect(() => {
    if (!showAdd) return;
    const commitOnLeave = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('.add-field-row')) return;
      const routeControl = target.closest('.setup-route-pane')
        ? /(-[a-z]+)$/.exec(target.id)?.[1]
        : undefined;
      const before = liveSetupJob.current;
      pendingFieldAdd.current();
      if (routeControl && liveSetupJob.current !== before)
        window.requestAnimationFrame(() =>
          document.querySelector<HTMLElement>(`.setup-route-pane [id$="${routeControl}"]`)?.focus(),
        );
    };
    document.addEventListener('pointerdown', commitOnLeave, true);
    document.addEventListener('focusin', commitOnLeave, true);
    return () => {
      document.removeEventListener('pointerdown', commitOnLeave, true);
      document.removeEventListener('focusin', commitOnLeave, true);
    };
  }, [showAdd]);
  const addDocumentType = () => {
    if (schemaLocked) return;
    const next: DocumentTypeDefinition = {
      id: uid(),
      name: nextDocumentTypeName(job.documentTypes),
      semanticId: null,
      description: null,
      status: 'provisional',
      schemaVersion: 0,
      position: job.documentTypes.length,
      fields: [],
    };
    changeJob({ ...job, documentTypes: [...job.documentTypes, next] });
    setManuallyAddedTypeIds((current) => new Set(current).add(next.id));
    selectDocumentType(next);
    setTypeView('type');
  };
  const removeManuallyAddedDocumentType = () => {
    if (
      mode !== 'setup' ||
      schemaLocked ||
      !typePendingRemovalId ||
      !manuallyAddedTypeIds.has(typePendingRemovalId)
    )
      return;
    const removedIndex = job.documentTypes.findIndex((item) => item.id === typePendingRemovalId);
    if (removedIndex < 0) return;
    const remaining = job.documentTypes
      .filter((item) => item.id !== typePendingRemovalId)
      .map((item, position) => ({ ...item, position }));
    const nextSelection = remaining[Math.min(removedIndex, Math.max(0, remaining.length - 1))];
    changeJob({ ...job, documentTypes: remaining });
    setManuallyAddedTypeIds((current) => {
      const next = new Set(current);
      next.delete(typePendingRemovalId);
      return next;
    });
    typeRemovalDone.current = true;
    setTypePendingRemovalId(null);
    selectDocumentType(nextSelection);
    onToast('Document type removed from this setup');
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  // Removing a saved type goes through the engine, which retires only its learning, takes
  // the type off its unexported documents (the automatic re-read then reads each again against
  // the remaining types, never forcing one: ADR-0096) and saves the job
  // without it. The confirmed count
  // is the preview the operator read, so a setup that changed since is refused, not guessed.
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const removeSavedDocumentType = async (impact?: DocumentTypeRemovalImpact) => {
    const api = window.capturePointDesktop;
    const target = job.documentTypes.find((item) => item.id === typePendingRemovalId);
    if (!api?.removeDocumentType || !target || !impact)
      throw new Error('That document type is no longer in this job.');
    if (captureDrainBlocked(job.id) || setupApplyingRef.current || analyzeMoreStartingRef.current)
      throw new Error('Let the current save finish, then try again.');
    setupApplyingRef.current = true;
    try {
      const result = await api.removeDocumentType(job.id, {
        typeId: target.id,
        expectedSchemaVersion: impact.expectedSchemaVersion,
        confirmed: true,
      });
      const durable = migrateJob(result.job as Job);
      setupBaseline.current = durable;
      liveSetupJob.current = durable;
      if (onPersistedJobUpdated) onPersistedJobUpdated(durable);
      else onChange(durable);
      setManuallyAddedTypeIds((current) => {
        const next = new Set(current);
        next.delete(target.id);
        return next;
      });
      typeRemovalDone.current = true;
      setTypePendingRemovalId(null);
      selectDocumentType(durable.documentTypes[0]);
      setTypeView('deck');
      // Export stays mounted once visited and holds the file naming it read; the engine
      // just removed this type's. It had no unsaved changes (removal needs a saved setup),
      // so reopen it from the saved settings rather than keep a plan naming a missing type.
      setExportDraftGeneration((value) => value + 1);
      onToast(
        typeRemovalToast(
          target.name,
          result.removed,
          result.documentsStillMoving ?? 0,
          // PAIGE: no exceptions folder (NoExceptionsFolder): Autopilot holds such documents for a person, as Manual does.
          hostHas('desktop-export') &&
            pilotMode(durable.reviewPolicy?.processingMode) === 'ai-reviewed',
        ),
      );
      try {
        await onWorkspaceRefresh();
      } catch {
        onToast(
          `“${target.name}” was removed. Reopen the job to refresh its documents.`,
          'job-refresh-failed',
        );
      }
    } finally {
      setupApplyingRef.current = false;
    }
  };
  const openTypeRemoval = (type: DocumentTypeDefinition, saved: boolean) => {
    setTypeActionsOpen(false);
    if (
      saved &&
      (captureDrainBlocked(job.id) || setupApplyingRef.current || analyzeMoreStartingRef.current)
    ) {
      onToast('Let the current save finish, then remove the type.', 'type-remove-refused');
      return;
    }
    // The engine removes a saved type from the saved setup; an unsaved draft (setup-draft.ts) would be lost.
    if (saved && holdsSetupDraft(mode, job) && setupHasChanges) {
      onToast('Save your setup changes before removing this type.', 'type-remove-refused');
      return;
    }
    typeRemovalDone.current = false;
    setTypePendingRemovalSaved(saved);
    setTypePendingRemovalId(type.id);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const leaveSetup = async (activate: boolean, stayOpen = false): Promise<SetupExitOutcome> => {
    if (setupApplyingRef.current || analyzeMoreStartingRef.current) return { state: 'busy' };
    if (mode === 'setup' && (schemaBindingPending || schemaBindingFailed)) {
      onToast(
        'Paige must verify who controls this document structure before saving',
        'structure-check-failed',
      );
      return { state: 'blocked' };
    }
    // Saving or leaving is a newer operator decision than an auto-tune request
    // already in flight, even if no field happened to be changed first.
    setupDraftRevision.current += 1;
    setupApplyingRef.current = true;
    setSetupApplying(true);
    try {
      // Read the live draft before deciding which acceptance transaction owns
      // this save. Automatic use can fire in the render immediately after the
      // bootstrap mode was committed; consulting the older render's `job` here
      // could treat that representative proposal as an ordinary setup save and
      // leave the other selected files unstaged.
      const currentJob = liveSetupJob.current;
      const acceptingRepresentativeProposal =
        activate &&
        proposalReview &&
        usesRepresentativeBootstrap(currentJob) &&
        Boolean(window.capturePointDesktop);
      if (acceptingRepresentativeProposal) await drainProposalEvidenceSaves();
      // Save and use (setup-proposal.ts, shared with support): a destination-owned type's status is part of
      // that destination's schema contract, so activating the Paige job must not rewrite it.
      const requested = activate
        ? activatedSetupJob(currentJob, destinationOwnsSchema)
        : currentJob;
      if (acceptingRepresentativeProposal && window.capturePointDesktop) {
        // Commit publishes the active job before the awaiting caller switches
        // modes. Keep the same progress surface through that intermediate render.
        representativeHandoffRef.current = true;
        const staged = await window.capturePointDesktop.stageFolderImport(currentJob.id);
        // Display only: the finishing screen now shows setup as complete, and the wizard
        // stays up until these documents are processed (setup-handoff.ts).
        // PAIGE: setup read them all, so they are its documents, not files still to import.
        const setupDocuments = batch?.documents.length || staged.totalFileCount;
        rememberSetupHandoff(currentJob.id, currentJob.documentTypes.length, setupDocuments);
        setRepresentativeStaged(setupDocuments);
        const committed = await onCommit(requested);
        // The accepted setup is now the saved one. Save and close reads it from here: the
        // provisional `job` of this render would send a now-active job to Scan jobs and leave
        // Job setup open saying "Unsaved changes".
        liveSetupJob.current = committed;
        setupBaseline.current = committed;
        // The job is set up and in use, its files being read (telemetry setup.ready; own_setup_ready for an own job).
        reportSetupReady(
          currentJob,
          staged.totalFileCount,
          currentJob.documentTypes.filter((type) => type.status !== 'archived').length,
        );
        onFolderAccepted();
        if (!stayOpen) onModeChange('capture');
        clearRepresentativeAutoPreference(window.localStorage, currentJob.id);
        setAutoUseFailure(null);
        onToast(`Setup saved · processing ${setupDocuments.toLocaleString()} documents`);
        return { state: 'applied' };
      }
      const saved = await onCommit(requested);
      setupBaseline.current = saved;
      // The durable setup save is complete. Re-reading existing documents can
      // take minutes after adding fields; it is not part of saving settings.
      // CapturePreview owns the guarded schema-refresh lane on entry, including
      // processing status, edit fencing, acquisition preservation and retries.
      // Do not start a second whole-batch reader behind this modal's Saving label.
      if (!stayOpen) onModeChange('capture');
      // The same verb as the button that got them here. The button says "Save",
      // so every receipt leads with "saved" — a toast that answers in a
      // different word makes the operator wonder whether the save happened.
      onToast(
        proposalReview && activate
          ? 'Setup saved · this job now uses it'
          : batch?.workspaceProjection === 'paged'
            ? 'Job changes saved · documents will use them as they are reviewed'
            : batch
              ? 'Job changes saved · documents will update in the workspace'
              : 'Job changes saved · ready to scan',
      );
      return { state: 'applied' };
    } catch (reason) {
      const message = messageFor(reason, 'The job changes could not be saved');
      onToast(message, 'setup-save-failed');
      return { state: 'failed', message };
    } finally {
      representativeHandoffRef.current = false;
      setRepresentativeStaged(null);
      setupApplyingRef.current = false;
      setSetupApplying(false);
    }
  };
  const saveAllSetupChanges = async (closeAfter = false) => {
    if (savingAll) return;
    // The same hold as the save bar's button (setupSaveBlocked, set further down this
    // render): the close prompt's Save and close must not save past it either, or a
    // "Matching barcodes" start with no barcode is saved and reads as automatic.
    if (setupSaveBlocked) {
      if (!separationReady) {
        setCloseSetupPrompt(false);
        setupExitDestination.current = null;
        openStarts();
      }
      onToast(setupSaveBlockedReason, 'setup-save-blocked');
      return;
    }
    setSavingAll(true);
    try {
      const outcome = await leaveSetup(true, true);
      if (outcome.state !== 'applied') return;
      if (exportDraftControl.current?.dirty && !(await exportDraftControl.current.save())) {
        openSetupSection('export');
        onToast(
          'Job settings saved. Export changes still need attention before they can be applied.',
          'export-save-failed',
        );
        return;
      }
      setCloseSetupPrompt(false);
      onToast('All changes saved');
      if (closeAfter) closeSetupDestination();
    } finally {
      setSavingAll(false);
    }
  };
  const [lookupRules, setLookupRules] = useState<LookupRuleDefinition[]>([]);
  // Paige's overview card says when the lists could not be read, rather than showing none.
  const [lookupRulesFailed, setLookupRulesFailed] = useState(false);
  const [lookupRevision, setLookupRevision] = useState(0);
  /** The field whose lookup editor is open. One editor per field being filled. */
  const [journeyFieldId, setJourneyFieldId] = useState<string | null>(null);
  /** The list a new lookup starts on when it was opened from that list's sheet. */
  const [journeyTableId, setJourneyTableId] = useState<string | null>(null);
  /** Where Lookup lists draws its list sheet: the layer over the setup content. */
  const [listSheetHost, setListSheetHost] = useState<HTMLDivElement | null>(null);
  // The page behind an open lookup editor is inert, so keyboard focus moves into
  // the editor; closing it hands focus back to whatever opened it.
  const lookupSheetRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (journeyFieldId)
      window.requestAnimationFrame(() =>
        lookupSheetRef.current
          ?.querySelector<HTMLElement>('.journey-close')
          ?.focus({ preventScroll: true }),
      );
  }, [journeyFieldId]);
  // Applied in one pass rather than one call per field: every field a lookup
  // fills is pointed at the same rule, the same reading and the same behaviour
  // when nothing matches, and applying them separately over a stale `job` would
  // land the last one and lose the rest.
  //
  // Save lookup stores the rule at once, so the fields it fills are saved with it:
  // held only in the setup draft, Discard or a close without saving left the stored rule filling
  // fields the job no longer pointed at it. The saved job takes the same patch, and so does this
  // visit's baseline, so Discard keeps them in step.
  const applyFieldPatches = (patches: FieldPatch[]) => {
    if (!selectedType) return;
    const typeId = selectedType.id;
    const patchFields = (fields: Field[]) =>
      fields.map((item) => {
        const found = patches.find((entry) => entry.fieldId === item.id);
        return found ? { ...item, ...found.patch } : item;
      });
    const patchJob = (value: Job): Job => ({
      ...value,
      documentTypes: value.documentTypes.map((type) =>
        type.id === typeId ? { ...type, fields: patchFields(type.fields) } : type,
      ),
    });
    updateType({ fields: patchFields(selectedType.fields) });
    if (!onSaveAlongside || !holdsSetupDraft(mode, job)) return;
    const jobId = job.id;
    void onSaveAlongside(jobId, patchJob)
      .then((saved) => {
        if (!saved || setupVisit.current.jobId !== jobId) return;
        const landing = proposalLanding.current;
        const landed = landing.baseline === setupBaseline.current;
        setupBaseline.current = {
          ...patchJob(setupBaseline.current),
          schemaVersion: saved.schemaVersion,
          updatedAt: saved.updatedAt,
        };
        if (landed) proposalLanding.current = { ...landing, baseline: setupBaseline.current };
      })
      .catch((reason) =>
        onToast(
          reason instanceof Error ? reason.message : 'Job changes were not saved',
          'job-save-failed',
        ),
      );
  };
  useEffect(() => {
    if (mode !== 'setup' || !window.capturePointDesktop) return;
    let canceled = false;
    void window.capturePointDesktop
      .listDataTables(job.id)
      // A failed read only drops rule names from the field summary; Lookup lists
      // reports the failure and offers Refresh.
      .then((catalog) => {
        if (canceled) return;
        setLookupRules(catalog.rules);
        setLookupRulesFailed(false);
      })
      .catch(() => {
        if (canceled) return;
        setLookupRules([]);
        setLookupRulesFailed(true);
      });
    return () => {
      canceled = true;
    };
  }, [job.id, mode, lookupRevision]);
  // What each review threshold would cost, from the documents already read.
  // Fetched once per visit to setup; the sliders price themselves from these
  // histograms without another engine round-trip per drag.
  const [reviewImpactRead, setReviewImpactRead] = useState<{
    jobId: string;
    profile: ReviewImpactProfile | null;
  } | null>(null);
  const reviewImpact = reviewImpactRead?.jobId === job.id ? reviewImpactRead.profile : null;
  useEffect(() => {
    if (mode !== 'setup') return;
    setReviewImpactRead(null);
    let live = true;
    const requestedAt = setupDraftRevision.current;
    const requestedJob = setupSnapshotKey(job);
    // Bars first, then the histograms that price them: the engine sets each
    // field's bar from its own readings (never touching one an operator
    // dragged), so setup opens with the balance already struck.
    void window.capturePointDesktop
      ?.autoTuneReviewBars(job.id)
      .then((tuned) => {
        if (
          live &&
          tuned &&
          setupDraftRevision.current === requestedAt &&
          setupSnapshotKey(liveSetupJob.current) === requestedJob &&
          setupSnapshotKey(setupBaseline.current) === requestedJob
        ) {
          // This response is already persisted by AutoTuneReviewBars. It is not
          // an operator draft, and a late reply must not replace newer edits.
          const saved = migrateJob(tuned as Job);
          if (saved.id !== job.id) return;
          setupBaseline.current = saved;
          liveSetupJob.current = saved;
          // Like a learning reset, tuning has already saved this snapshot.
          // The draft callback queues another save and can return a newer
          // timestamp/schema revision that no longer matches this baseline.
          if (onPersistedJobUpdated) onPersistedJobUpdated(saved);
          else onChange(saved);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!live) return;
        void window.capturePointDesktop
          ?.getReviewImpactProfile(job.id)
          .then((profile) => {
            if (live) setReviewImpactRead({ jobId: job.id, profile });
          })
          .catch(() => undefined);
      });
    return () => {
      live = false;
    };
  }, [job.id, mode]);
  const separationComplete = (candidate: Pick<Job, 'separationMode' | 'separationBarcodeText'>) =>
    (candidate.separationMode !== SEPARATION.existingBarcode &&
      candidate.separationMode !== SEPARATION.automaticBarcode) ||
    Boolean(candidate.separationBarcodeText?.trim());
  // Only a change to how documents start can be held for a missing start barcode. A job
  // saved that way before keeps reading as it did (the engine starts no documents at a
  // barcode it was never given), so an unrelated edit, such as a field's reading
  // sources, is never blocked by it.
  const separationEdited =
    job.separationMode !== setupBaseline.current.separationMode ||
    (job.separationBarcodeText ?? '').trim() !==
      (setupBaseline.current.separationBarcodeText ?? '').trim();
  const separationReady = separationComplete(job) || !separationEdited;
  const representativeProposalKey =
    proposalReview && batch
      ? [
          job.id,
          batch.batchId,
          batch.completedAt,
          folderAnalysisStatus?.evidenceRevision ?? 0,
          folderAnalysisStatus?.operationId ?? 'legacy',
          folderAnalysisStatus?.deeplyAnalyzedDocumentCount ?? 0,
        ].join('|')
      : '';
  const currentAutoUseFailure =
    autoUseFailure?.proposalKey === representativeProposalKey ? autoUseFailure : null;
  // Keep the editor out of the automatic handoff. Missing readiness or a real
  // failure still exposes the existing editable setup and its recovery actions.
  const automaticSetupFinishing =
    (mode === 'setup' && representativeHandoffRef.current) ||
    (proposalReview &&
      usesRepresentativeBootstrap(job) &&
      folderAnalysisStatus?.state === 'ready' &&
      folderAnalysisStatus.autoAcceptEligible === true &&
      Boolean(batch?.bootstrapProposal?.documentTypes.length) &&
      separationComplete(job) &&
      !schemaBindingFailed &&
      !currentAutoUseFailure);
  const automaticSetupReady =
    changesSetup() &&
    usesRepresentativeBootstrap(job) &&
    proposalReview &&
    Boolean(representativeProposalKey) &&
    folderAnalysisStatus?.state === 'ready' &&
    folderAnalysisStatus.autoAcceptEligible === true &&
    Boolean(batch?.bootstrapProposal?.documentTypes.length) &&
    separationComplete(job) &&
    currentSchemaBindingCheck?.state === 'ready' &&
    !setupApplying &&
    !analyzeMoreStarting &&
    !setupApplyingRef.current &&
    !analyzeMoreStartingRef.current;
  const recordAutomaticStartOutcome = (proposalKey: string, outcome: SetupExitOutcome) => {
    if (outcome.state === 'failed') {
      const failure = { proposalKey, message: outcome.message };
      setAutoUseFailure(failure);
      writeRepresentativeAutoFailure(job.id, failure);
      return;
    }
    if (outcome.state !== 'applied' && autoAttemptedProposalKey.current === proposalKey)
      autoAttemptedProposalKey.current = '';
  };
  const retryAutomaticStart = () => {
    if (!automaticSetupReady || setupApplyingRef.current || analyzeMoreStartingRef.current) return;
    setAutoUseFailure(null);
    writeRepresentativeAutoFailure(job.id, null);
    autoAttemptedProposalKey.current = representativeProposalKey;
    void leaveSetup(true).then((outcome) =>
      recordAutomaticStartOutcome(representativeProposalKey, outcome),
    );
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const resumeSavedFolderAnalysis = async () => {
    const api = window.capturePointDesktop;
    const operationId = folderAnalysisStatus?.operationId;
    if (!api || !operationId || folderOperationDecision) return;
    setFolderOperationDecision('resume');
    setFolderOperationDecisionError('');
    setResumeDidNotStart(false);
    if (!(await stoppedSetupStillShown(folderAnalysisStatus))) {
      setFolderOperationDecision(null);
      return;
    }
    onDismissExternalFailure();
    onFolderAnalysisStatus(job.id, {
      ...folderAnalysisStatus,
      state: 'running',
      running: true,
      autoAcceptEligible: false,
      failure: null,
    });
    onModeChange('capture');
    setFolderOperationDecision('resumed');
    try {
      const resumed = await api.resumeFolderAnalysis(job.id, {
        jobId: job.id,
        operationId,
        evidenceRevision: folderAnalysisStatus.evidenceRevision,
      });
      requireRepresentativeProposal(resumed.result);
      // Only now, after it ran: a hold cleared before the call would let the shell continue
      // a resume that fails by itself (its status read sees the saved remainder).
      onSetupResumed(job.id);
      const { result, ...status } = resumed;
      onBootstrapAnalyzed(job.id, result);
      onFolderAnalysisStatus(job.id, status);
      onToast('Setup finished');
    } catch (reason) {
      // Held first, before anything is awaited: a resume that failed is offered again, never
      // continued by itself (with the same operation id after Try again, or a new remainder's).
      onSetupStopped(job.id);
      const message = messageFor(reason, 'The saved document analysis could not resume.');
      // Stop setup cancels the running work; the engine's "A task was canceled." is that stop,
      // not a failure to show (the saved status says "Setup stopped…").
      if (!canceledByStop(message)) setFolderOperationDecisionError(message);
      reportSetupFailure(job.id, message);
      const after = await api.getFolderAnalysisStatus(job.id).catch(() => null);
      if (after) onFolderAnalysisStatus(job.id, after);
      // Still the person's own stop, and not because they stopped it again: Try again never ran it, and says so.
      setResumeDidNotStart(
        !canceledByStop(message) && !stoppedByPerson(message) && stoppedByPerson(after?.failure),
      );
    } finally {
      setFolderOperationDecision(null);
      onModeChange('setup');
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const abandonSavedFolderAnalysis = async (chooseAgain = false) => {
    const api = window.capturePointDesktop;
    const operationId = folderAnalysisStatus?.operationId;
    if (!api || !operationId || !folderAnalysisStatus || folderOperationDecision) return;
    let discarded = false;
    let previousSetupKept = false;
    setFolderOperationDecision('abandon');
    setFolderOperationDecisionError('');
    setResumeDidNotStart(false);
    if (!(await stoppedSetupStillShown(folderAnalysisStatus))) {
      setFolderOperationDecision(null);
      return;
    }
    try {
      const status = await api.abandonFolderAnalysisOperation({
        jobId: job.id,
        operationId,
        evidenceRevision: folderAnalysisStatus.evidenceRevision,
      });
      onFolderAnalysisStatus(job.id, status);
      onFolderAnalysisAbandoned?.(job.id, status.available);
      // The discarded run's finished types must not show as finished cards in the next run.
      forgetReadyTypes(job.id);
      onDismissExternalFailure();
      if (!chooseAgain)
        onToast(status.available ? 'Kept the previous setup' : 'Cleared the unfinished setup');
      discarded = true;
      previousSetupKept = status.available;
    } catch (reason) {
      const message = messageFor(reason, 'Paige could not clear the unfinished setup.');
      setFolderOperationDecisionError(message);
      await api
        .getFolderAnalysisStatus(job.id)
        .then((status) => onFolderAnalysisStatus(job.id, status))
        .catch(() => undefined);
    } finally {
      setFolderOperationDecision(null);
    }
    if (chooseAgain && discarded)
      await onChooseRepresentativeSource(job, job.bootstrapMode === 'folder' ? 'folder' : 'files');
    else if (
      discarded &&
      !previousSetupKept &&
      job.status === 'provisional' &&
      usesRepresentativeBootstrap(job)
    ) {
      // Start over leaves the job as a brand-new job is: no setup, on its start screen (choose files, bring in a
      // folder, scan). It stayed a folder setup with nothing in it, which opens on an empty Document types editor
      // with no way to bring the documents in again (October 7, 2026).
      try {
        const fresh = migrateJob(
          await onCommit({
            ...job,
            bootstrapMode: 'scan',
            description: 'Built from documents as they are scanned or imported',
            updatedAt: new Date().toISOString(),
          }),
        );
        onChange(fresh);
        onModeChange('capture');
      } catch (reason) {
        onToast(
          reason instanceof Error ? reason.message : 'This job could not be saved',
          'job-save-failed',
        );
      }
    }
  };
  useEffect(() => {
    // Automatic setup has no operator acceptance stop. This is not confidence
    // or human confirmation: retain every existing durable readiness check.
    // Durable evidence, a real proposal, valid separation, and verified schema
    // ownership must all agree before the same Save-and-use transaction runs.
    if (!automaticSetupReady || currentAutoUseFailure) return;
    if (autoAttemptedProposalKey.current === representativeProposalKey) return;
    autoAttemptedProposalKey.current = representativeProposalKey;
    void leaveSetup(true).then((outcome) =>
      recordAutomaticStartOutcome(representativeProposalKey, outcome),
    );
  }, [
    automaticSetupReady,
    proposalReview,
    representativeProposalKey,
    folderAnalysisStatus?.state,
    folderAnalysisStatus?.autoAcceptEligible,
    batch?.bootstrapProposal?.documentTypes.length,
    job.separationMode,
    job.separationBarcodeText,
    currentSchemaBindingCheck?.state,
    setupApplying,
    analyzeMoreStarting,
    currentAutoUseFailure,
    job.bootstrapMode,
  ]);
  // Whether reading further is still turning anything up. A kind absent from the
  // documents that were read does not exist as far as the finished job is
  // concerned, and setup looked identical either way — so say it plainly.
  const discoveries = batch?.bootstrapProposal?.discoveries ?? [];
  const documentsRead = batch?.bootstrapProposal?.documentsRead ?? 0;
  const lastDiscovery = discoveries.length
    ? discoveries[discoveries.length - 1].afterDocumentsRead
    : 0;
  const sinceLastDiscovery = Math.max(0, documentsRead - lastDiscovery);
  const unread = Math.max(
    0,
    (folderAnalysisStatus?.totalFileCount ?? 0) -
      (folderAnalysisStatus?.deeplyAnalyzedDocumentCount ?? 0),
  );
  const learningStillFinding = documentsRead > 0 && sinceLastDiscovery < 5 && unread > 0;
  const learningSummary =
    documentsRead === 0
      ? `${discoveries.length} document type${discoveries.length === 1 ? '' : 's'} found`
      : unread === 0
        ? `${discoveries.length} kind${discoveries.length === 1 ? '' : 's'} found · nothing left unread`
        : sinceLastDiscovery === 0
          ? `still finding new kinds · ${unread.toLocaleString()} unread`
          : `nothing new in the last ${sinceLastDiscovery} · ${unread.toLocaleString()} unread`;
  const likelyWorkflow = batch?.bootstrapProposal?.likelyWorkflow;
  const missingWorkflowTypes = (batch?.bootstrapProposal?.suggestedTypeChecks ?? []).filter(
    (expected) =>
      !job.documentTypes.some(
        (type) =>
          expected.toLocaleLowerCase().includes(type.name.toLocaleLowerCase()) ||
          type.name.toLocaleLowerCase().includes(expected.toLocaleLowerCase()),
      ),
  );

  const configuredReviewFields = job.documentTypes.flatMap((type) => type.fields);
  const totalFieldCount = configuredReviewFields.length;
  const setupSections: readonly WorkspaceSectionItem<JobSetupSection>[] = [
    { id: 'overview', label: 'Overview', icon: <CheckCircle2 size={18} /> },
    { id: 'document-types', label: 'Document types', icon: <Files size={18} /> },
    { id: 'document-starts', label: 'Document starts', icon: <Scissors size={18} /> },
    { id: 'thresholds', label: 'Review thresholds', icon: <SlidersHorizontal size={18} /> },
    { id: 'learning', label: 'Learning', icon: <BrainCircuit size={18} /> },
    { id: 'lookups', label: 'Lookup lists', icon: <Database size={18} /> },
    // Paige: who scans into the job from a phone (PhoneScanSetup), on a Paige job only; Export stays last.
    ...(window.paigeWorkbench?.mobile
      ? [{ id: 'phones' as const, label: PHONE_SCAN_TITLE, icon: <Smartphone size={18} /> }]
      : []),
    // PAIGE: a watched folder is a folder on the operator's own computer; Paige's is Paige Scan's (hostHas).
    ...(hostHas('watched-folder')
      ? [
          {
            id: 'watched-folder' as const,
            label: 'Watched folder',
            icon: <FolderInput size={18} />,
          },
        ]
      : []),
    { id: 'export', label: 'Export', icon: <FolderOutput size={18} /> },
  ];
  const navigateSetup = (next: JobSetupSection) => {
    const navigate = () => {
      setJourneyFieldId(null);
      // Returning from another section keeps the open type; choosing Document types again goes home to the deck.
      setThresholdReturn(null);
      if (next === 'document-types' && setupSection === 'document-types') setTypeView('deck');
      openSetupSection(next);
    };
    if (journeyNavigation.current) journeyNavigation.current.requestLeave(navigate);
    else navigate();
  };
  const openTypeField = (typeId: string, fieldId?: string) => {
    openSetupSection('document-types');
    setTypeView('type');
    selectDocumentType(job.documentTypes.find((type) => type.id === typeId));
    setExpanded(fieldId ?? null);
    if (fieldId)
      window.requestAnimationFrame(() => {
        const element = setupWorkspaceRef.current?.querySelector<HTMLElement>(
          `[data-setup-field="${CSS.escape(fieldId)}"]`,
        );
        element?.scrollIntoView({ block: 'start', behavior: 'instant' });
        element?.querySelector<HTMLButtonElement>('.field-summary')?.focus({ preventScroll: true });
      });
  };
  const openStarts = () => {
    openSetupSection('document-starts');
    window.requestAnimationFrame(() => {
      const section =
        setupWorkspaceRef.current?.querySelector<HTMLElement>('.setup-document-starts');
      section?.scrollIntoView({ block: 'start', behavior: 'instant' });
      section
        ?.querySelector<HTMLElement>('#setup-document-starts-title')
        ?.focus({ preventScroll: true });
    });
  };
  const openThresholdsFromField = (field: Field) => {
    if (selectedType)
      setThresholdReturn({ typeId: selectedType.id, fieldId: field.id, name: field.name });
    openSetupSection('thresholds');
  };
  const openLookupEditor = (fieldId: string, dataTableId?: string) => {
    setJourneyTableId(dataTableId ?? null);
    setJourneyFieldId(fieldId);
  };
  const closeListJourney = () => {
    setJourneyFieldId(null);
    setJourneyTableId(null);
    if (setupSection === 'document-types' && selectedType && journeyFieldId)
      openTypeField(selectedType.id, journeyFieldId);
  };
  const requestLookupEditorClose = () => {
    if (journeyNavigation.current) journeyNavigation.current.requestLeave(closeListJourney);
    else closeListJourney();
  };
  // The field on the route: the one asked for, else the first the type captures.
  const routeField = selectedType
    ? (selectedType.fields.find((item) => item.id === expanded) ??
      selectedType.fields.find((item) => item.kind !== 'document-type') ??
      selectedType.fields[0])
    : undefined;
  // The list in the order it is shown: fields first, then the table.
  const listedFields = selectedType
    ? [
        ...selectedType.fields.filter((item) => item.kind !== 'line-items'),
        ...selectedType.fields.filter((item) => item.kind === 'line-items'),
      ]
    : [];
  // Keyboard focus stays in the field list: the chosen field's button, or the list itself when no field is left.
  const focusSetupField = (id: string | null) =>
    window.requestAnimationFrame(() => {
      const list = setupWorkspaceRef.current?.querySelector<HTMLElement>('.setup-field-list');
      const button = id
        ? list?.querySelector<HTMLButtonElement>(
            `[data-setup-field="${CSS.escape(id)}"] .field-summary`,
          )
        : null;
      (button ?? list)?.focus();
    });
  // One Tab stop for the whole list; the arrow keys move between fields, so Tab reaches the route next.
  const moveInFieldList = (key: string, id: string) => {
    const index = listedFields.findIndex((item) => item.id === id);
    const target =
      key === 'ArrowDown'
        ? listedFields[index + 1]
        : key === 'ArrowUp'
          ? listedFields[index - 1]
          : key === 'Home'
            ? listedFields[0]
            : key === 'End'
              ? listedFields[listedFields.length - 1]
              : undefined;
    if (!target) return false;
    setExpanded(target.id);
    focusSetupField(target.id);
    return true;
  };
  // The setup sample: this type's documents in the batch setup was read from.
  const sampleDocuments =
    selectedType && batch
      ? batch.documents.filter(
          (document) =>
            document.documentTypeId === selectedType.id ||
            document.documentType.localeCompare(selectedType.name, undefined, {
              sensitivity: 'accent',
            }) === 0,
        )
      : [];
  const readingOf = (document: ProcessResult['documents'][number], field: Field) =>
    document.fields.find(
      (result) =>
        result.fieldId === field.id ||
        result.name.localeCompare(field.name, undefined, { sensitivity: 'accent' }) === 0,
    );
  /** What the field read on the first sample document that has a value for it. */
  const sampleFor = (field: Field): FieldSample | undefined => {
    for (const document of sampleDocuments) {
      const reading = readingOf(document, field);
      if (!reading?.value) continue;
      const page =
        document.pages.find((candidate) => candidate.number === reading.evidencePageNumber) ??
        document.pages[0];
      return {
        value: reading.value,
        confidence: reading.confidence ?? null,
        needsReview: Boolean(reading.needsReview),
        previewPath: page?.previewPath ?? null,
        box: page ? evidenceBox(page.words ?? [], reading.evidenceLineIndexes ?? []) : null,
        from: null,
      };
    }
    return undefined;
  };
  const savedFields = new Map(
    (
      setupBaseline.current.documentTypes.find((type) => type.id === selectedType?.id)?.fields ?? []
    ).map((field) => [field.id, JSON.stringify(field)]),
  );
  // Every type can be removed from its Type options. A saved type goes through the engine and
  // needs a settled, saved setup; an unsaved one added by hand in this visit leaves the draft.
  const typeRemovalLockReason = destinationOwnsSchema
    ? "Content Central controls this job's document types."
    : schemaBindingPending
      ? 'Checking who controls this setup…'
      : schemaBindingFailed
        ? 'Setup changes are paused.'
        : schemaLocked
          ? 'Wait for setup to finish, then try again.'
          : null;
  const typeRemoval: { saved: boolean; blocked: string | null } | null =
    mode !== 'setup' || !selectedType
      ? null
      : setupBaseline.current.documentTypes.some((type) => type.id === selectedType.id)
        ? window.capturePointDesktop?.removeDocumentType
          ? {
              saved: true,
              blocked:
                typeRemovalLockReason ?? (setupHasChanges ? 'Save your changes first.' : null),
            }
          : null
        : manuallyAddedTypeIds.has(selectedType.id)
          ? { saved: false, blocked: typeRemovalLockReason }
          : { saved: false, blocked: 'Save your changes first.' };
  const fieldButton = (item: Field) => (
    <li key={item.id} data-setup-field={item.id}>
      <button
        type="button"
        className="field-summary setup-field-item"
        aria-pressed={routeField?.id === item.id}
        tabIndex={routeField?.id === item.id ? 0 : -1}
        onClick={() => setExpanded(item.id)}
        onKeyDown={(event) => {
          if (moveInFieldList(event.key, item.id)) event.preventDefault();
        }}
      >
        <span className="setup-field-item-icon">{kindIconFor(item.kind, 15)}</span>
        <span className="field-name">
          <strong>{item.name}</strong>
        </span>
        {item.required && (
          <span className="setup-field-item-required" aria-label="Required">
            *
          </span>
        )}
        {savedFields.get(item.id) !== JSON.stringify(item) && (
          <span
            className="setup-field-item-changed"
            title="Not saved yet"
            aria-label="Changed since the last save"
          />
        )}
        <span className="setup-field-item-glyphs">
          {item.kind === 'document-type' && <Lock size={12} aria-label="Always captured" />}
          {item.kind !== 'line-items' && item.kind !== 'document-type' && item.readBarcode && (
            <Barcode size={13} aria-label="Also read from a barcode" />
          )}
          {fieldLookup(item) && <Database size={13} aria-label="Uses a list" />}
          {item.reviewBelow != null && item.reviewBelowSource === 'operator' && (
            <span className="setup-field-item-threshold" title="Its own review threshold">
              <ShieldCheck size={11} aria-hidden="true" />
              {Math.round(item.reviewBelow * 100)}
            </span>
          )}
        </span>
      </button>
    </li>
  );
  const documentTypesPanel = (
    <section key={selectedType?.id} className="panel fields-panel setup-types-panel">
      <div className="panel-heading setup-type-header">
        <div className="setup-type-heading">
          <button
            ref={typeBackRef}
            type="button"
            className="quiet-button small setup-type-back"
            onClick={() => setTypeView('deck')}
          >
            <ChevronLeft size={15} />
            All types
          </button>
          {selectedType ? (
            <SetupTypeName
              name={selectedType.name}
              disabled={schemaLocked}
              onChange={(name) => updateType({ name })}
            />
          ) : (
            <h2>Document type</h2>
          )}
        </div>
        <div className="setup-type-header-actions">
          {selectedType && batch && (
            <button className="secondary-button small" onClick={() => setSampleOpen(true)}>
              <FileSearch size={16} /> View sample
            </button>
          )}
          {selectedType && job.status === 'active' && window.capturePointDesktop?.resetLearning && (
            <button className="secondary-button small" onClick={() => openLearningReset()}>
              <RotateCw size={16} /> Reset recognition
            </button>
          )}
          {selectedType && typeRemoval && (
            <div ref={typeActionsRef} className="proposal-type-actions">
              <TooltipButton
                ref={typeActionsButtonRef}
                className="icon-button"
                onClick={() => setTypeActionsOpen((current) => !current)}
                tooltip="Type options"
                aria-label="Type options"
                aria-controls={`${setupControlId}-type-actions`}
                aria-expanded={typeActionsOpen}
              >
                <MoreHorizontal size={16} />
              </TooltipButton>
              {typeActionsOpen && (
                <div
                  id={`${setupControlId}-type-actions`}
                  role="group"
                  aria-label={`Options for ${selectedType.name}`}
                >
                  <button
                    className="remove-type-action"
                    disabled={Boolean(typeRemoval.blocked)}
                    aria-describedby={
                      typeRemoval.blocked ? `${setupControlId}-type-removal-blocked` : undefined
                    }
                    onClick={() => openTypeRemoval(selectedType, typeRemoval.saved)}
                  >
                    <Trash2 size={13} /> Remove this type…
                  </button>
                  {typeRemoval.blocked && (
                    <p
                      id={`${setupControlId}-type-removal-blocked`}
                      className="remove-type-blocked"
                    >
                      {typeRemoval.blocked}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {schemaBindingPending && (
        <div className="setup-schema-owner-note checking" role="status">
          <LoaderCircle className="spin" size={15} />
          <span>
            <strong>Checking which settings you can change…</strong>
            <small>Checking the document types and fields managed by Content Central.</small>
          </span>
        </div>
      )}
      {schemaBindingFailed && (
        <div
          className="setup-schema-owner-note attention"
          role="alert"
          data-telemetry="error:structure-check-failed"
        >
          <TriangleAlert size={15} />
          <span>
            <strong>Document structure changes are paused</strong>
            <small>
              {currentSchemaBindingCheck?.message} Existing recognition settings and thresholds are
              still available.
            </small>
          </span>
          <button
            className="secondary-button small"
            onClick={() => setSchemaBindingRetry((current) => current + 1)}
          >
            Try again
          </button>
        </div>
      )}
      {destinationOwnsSchema && (
        <div className="setup-schema-owner-note">
          <Database size={15} />
          <span>
            <strong>Content Central controls this document structure</strong>
            <small>
              Change document types, field names, and required values in Content Central.
              Recognition settings and thresholds remain available here.
            </small>
          </span>
        </div>
      )}
      {selectedType ? (
        <>
          <div className="setup-type-body">
            <div
              className="field-list setup-field-list"
              role="group"
              aria-label={`${selectedType.name} fields`}
              tabIndex={-1}
            >
              <div className="setup-field-list-head">
                <span>Fields</span>
                <TooltipButton
                  className="secondary-button small"
                  onClick={() => setShowAdd(true)}
                  disabled={schemaLocked}
                  tooltip={schemaLocked ? 'Content Central controls these fields' : ''}
                >
                  <Plus size={15} /> Add field
                </TooltipButton>
              </div>
              {showAdd && !schemaLocked && (
                <div className="add-field-row">
                  <div>
                    <label htmlFor={`${setupControlId}-new-field-name`}>Field name</label>
                    <input
                      id={`${setupControlId}-new-field-name`}
                      className={newFieldTaken ? 'invalid' : ''}
                      autoFocus
                      value={newFieldName}
                      onChange={(event) => setNewFieldName(event.target.value)}
                      onKeyDown={(event) => event.key === 'Enter' && addField()}
                      placeholder="e.g. Customer number"
                      aria-invalid={Boolean(newFieldTaken)}
                      aria-describedby={
                        newFieldTaken ? `${setupControlId}-new-field-taken` : undefined
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor={`${setupControlId}-new-field-kind`}>Value type</label>
                    <select
                      id={`${setupControlId}-new-field-kind`}
                      value={newFieldKind}
                      onChange={(event) => setNewFieldKind(event.target.value as FieldKind)}
                    >
                      {Object.entries(kindLabel)
                        .filter(([key]) => key !== 'document-type')
                        .map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    className="primary-button small"
                    onClick={addField}
                    disabled={Boolean(newFieldTaken)}
                  >
                    Add
                  </button>
                  <button
                    className="icon-button"
                    onClick={() => setShowAdd(false)}
                    aria-label="Cancel adding field"
                  >
                    <X size={18} />
                  </button>
                  {newFieldTaken && (
                    <small
                      id={`${setupControlId}-new-field-taken`}
                      className="name-conflict"
                      role="status"
                      data-telemetry="error:field-name-taken"
                    >
                      <TriangleAlert size={12} /> {selectedType.name} already has a field named{' '}
                      {newFieldTaken.name}
                    </small>
                  )}
                </div>
              )}
              {/* The names scroll inside their column, under its heading and button, so a long list never makes the panel scroll. */}
              <div className="setup-field-list-scroll">
                <ul>
                  {selectedType.fields
                    .filter((item) => item.kind !== 'line-items')
                    .map(fieldButton)}
                </ul>
                {selectedType.fields.some((item) => item.kind === 'line-items') && (
                  <>
                    <div className="setup-field-group">Table</div>
                    <ul>
                      {selectedType.fields
                        .filter((item) => item.kind === 'line-items')
                        .map(fieldButton)}
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div className="setup-route-pane">
              {selectedType.fields.length === 0 && (
                <div className="setup-route-empty">
                  {/* A new type: the route its fields will take, faint, behind one way to start. */}
                  <RouteLine />
                  <div className="setup-route-ghosts">
                    {[0, 1, 2, 3].map((index) => (
                      <div key={index} className="setup-route-ghost" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                        <i />
                      </div>
                    ))}
                    <div className="setup-route-empty-call">
                      <TextCursorInput size={24} aria-hidden="true" />
                      <strong>No fields yet</strong>
                      <span>
                        {schemaLocked
                          ? 'Content Central controls the fields for this document type.'
                          : 'Add the first value this document type should capture.'}
                      </span>
                      {!schemaLocked && (
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => setShowAdd(true)}
                        >
                          <Plus size={16} aria-hidden="true" /> Add your first field
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {routeField && (
                <FieldRoute
                  key={routeField.id}
                  field={routeField}
                  sample={sampleFor(routeField)}
                  schemaLocked={schemaLocked}
                  lookupRules={lookupRules}
                  reviewDefault={job.reviewPolicy?.fields ?? REVIEW_POLICY_DEFAULTS.fields}
                  impact={selectFieldReviewImpact(reviewImpact, routeField, configuredReviewFields)}
                  onChange={(patch) => updateField(routeField.id, patch)}
                  onDelete={() => {
                    const index = listedFields.findIndex((item) => item.id === routeField.id);
                    const next = listedFields[index + 1] ?? listedFields[index - 1];
                    deleteField(routeField.id);
                    setExpanded(next?.id ?? null);
                    focusSetupField(next?.id ?? null);
                  }}
                  onOpenJourney={() => openLookupEditor(routeField.id)}
                  onOpenThresholds={() => openThresholdsFromField(routeField)}
                  onResetLearning={
                    job.status === 'active' && window.capturePointDesktop?.resetLearning
                      ? () => openLearningReset(routeField)
                      : undefined
                  }
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="setup-empty-fields">
          <Files size={24} />
          <strong>No document types yet</strong>
          <span>Add a type to define what this job recognizes.</span>
        </div>
      )}
    </section>
  );

  // Product telemetry (ADR-0081): Job setup's part of the area in front: its first setup's wait (reading, failed or
  // handing off), or the section; and the setup wizard held over the work screen while its documents import.
  useTelemetryArea(
    'setup',
    mode !== 'setup'
      ? null
      : representativeSetupProgressVisible || automaticSetupFinishing || representativeSetupFailure
        ? 'wait'
        : // PAIGE: the overview and Phones are Paige's own sections, not the catalog's areas.
          setupSection === 'overview' || setupSection === 'phones'
          ? null
          : setupSection,
  );
  useTelemetryArea('setupWizard', mode !== 'setup' && setupWizardHolds && Boolean(heldSetup));
  if (mode === 'export' && hostHas('desktop-export')) {
    return (
      <ExportWorkspace
        job={job}
        onToast={onToast}
        onSetup={() => onModeChange('setup')}
        onWorkspaceRefresh={onWorkspaceRefresh}
      />
    );
  }

  // The work screen. It keeps one place in the page (see workHost), so Job
  // setup opens over the page the operator was on without rebuilding it.
  // Behind setup it is paused and sees the last saved job, never the draft:
  // nothing it does on its own may act on unsaved setup.
  const workView = (
    <CapturePreview
      onDocumentCount={onDocumentCount}
      suspended={mode === 'setup'}
      // PAIGE: the setup wizard reads the job's card (paige/setup-processing.ts), not this import.
      teachingDocumentId={teachingDocumentId}
      onTeachingDocumentOpened={onTeachingDocumentOpened}
      workspaceNavigation={workspaceNavigation}
      workspaceJobSelector={workspaceJobSelector}
      job={mode === 'setup' ? setupBaseline.current : job}
      batch={batch}
      onJobs={onJobs}
      folderAnalysisStatus={folderAnalysisStatus}
      externalActivity={externalActivity}
      externalFailure={externalFailure}
      folderAnalysisProgress={folderAnalysisProgress}
      folderActivity={folderActivity}
      folderWork={folderWork}
      processorOnlyExtraction={processorOnlyExtraction}
      onDismissExternalFailure={onDismissExternalFailure}
      onSetupStopped={onSetupStopped}
      onChooseRepresentativeSource={onChooseRepresentativeSource}
      onRestored={onBatch}
      onJobChange={onChange}
      onCommitJob={onCommit}
      onBackToSetup={() => onModeChange('setup')}
      onProcessed={onBatch}
      onBootstrapAnalyzed={onBootstrapAnalyzed}
      onNotice={onToast}
      onOpenCluster={onOpenCluster}
      learningOpen={learningOpen}
      suggestionApplyRequest={suggestionApplyRequest}
      onSuggestionApplyConsumed={onSuggestionApplyConsumed}
      teachRequested={teachRequested}
      onTeachRequestConsumed={onTeachRequestConsumed}
      canReturnToNewJob={canReturnToNewJob}
    />
  );

  const setupContent = (
    <div
      ref={setupWorkspaceRef}
      className={`page job-workspace ${mode}-mode${mode === 'setup' && (representativeSetupProgressVisible || automaticSetupFinishing) && !representativeSetupFailure ? ' setup-processing-layout' : ''}`}
    >
      {mode === 'setup' &&
        (currentAutoUseFailure || folderAnalysisDecisionRequired || representativeSetupFailure) && (
          <section className="job-header">
            <JobProcessingButton jobId={job.id} />
            <div className="job-title-row">
              <div>
                {proposalReview && !automaticSetupFinishing && batch && (
                  <div className="proposal-documents-waiting">
                    <Files size={15} />
                    <span>
                      <strong>
                        {counted(
                          batch.documents.reduce((sum, document) => sum + document.pages.length, 0),
                          'page',
                        )}{' '}
                        ready
                      </strong>
                    </span>
                  </div>
                )}
                {proposalReview && !automaticSetupFinishing && folderAnalysisStatus?.available && (
                  <div className="proposal-coverage" aria-label="Representative folder coverage">
                    <span>
                      <strong>
                        {folderAnalysisStatus.deeplyAnalyzedDocumentCount.toLocaleString()}
                      </strong>{' '}
                      of {folderAnalysisStatus.totalFileCount.toLocaleString()} read closely enough
                      to shape this job
                    </span>
                    <i />
                    <span className={learningStillFinding ? 'coverage-open' : 'coverage-settled'}>
                      {learningSummary}
                    </span>
                    <i />
                    <span>
                      All {folderAnalysisStatus.totalFileCount.toLocaleString()} import after setup
                    </span>
                  </div>
                )}
                {proposalReview && !automaticSetupFinishing && likelyWorkflow && (
                  <div className="proposal-workflow">
                    <Sparkles size={13} />
                    <strong>{likelyWorkflow} pattern</strong>
                    {missingWorkflowTypes.length > 0 && (
                      <span>Also checking for {missingWorkflowTypes.slice(0, 4).join(', ')}</span>
                    )}
                  </div>
                )}
                {currentAutoUseFailure && (
                  <div
                    className="bootstrap-auto-failure"
                    data-telemetry="error:setup-failed"
                    role="alert"
                  >
                    <TriangleAlert size={15} />
                    <span>
                      <strong>Automatic start didn’t finish</strong>
                      <small>{currentAutoUseFailure.message}</small>
                    </span>
                    <button
                      className="secondary-button small"
                      onClick={retryAutomaticStart}
                      disabled={!automaticSetupReady}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {/* Continue, start again, a newer setup already in place, or the person's own stop: a choice, not a failure. */}
                {folderAnalysisDecisionRequired && !representativeSetupFailure && (
                  <div
                    className="bootstrap-auto-failure"
                    role="alert"
                    data-telemetry={
                      folderOperationDecisionError
                        ? 'error:setup-resume-failed'
                        : (folderAnalysisStatus?.failure ||
                              folderAnalysisStatus?.state === 'failed') &&
                            !stoppedByPerson(folderAnalysisStatus?.failure)
                          ? 'error:setup-failed'
                          : 'notice'
                    }
                  >
                    <TriangleAlert size={15} />
                    <span>
                      <strong>
                        {folderAnalysisStatus?.state === 'conflicted'
                          ? 'A newer setup is already in place'
                          : folderAnalysisNeedsFreshStart
                            ? 'Start this setup again'
                            : folderAnalysisStatus?.state === 'failed'
                              ? 'Setup did not finish'
                              : 'Continue setting up this job'}
                      </strong>
                      {/* Plain words first; the exact error behind Technical details (setup-failure.ts). */}
                      <small>
                        {resumeDidNotStart
                          ? setupResumeNotStartedWords
                          : folderOperationDecisionError || folderAnalysisStatus?.failure
                            ? setupFailureWords(
                                folderOperationDecisionError || folderAnalysisStatus?.failure,
                              )
                            : folderAnalysisStatus?.state === 'recoverable'
                              ? 'Paige saved your place. Continue where it stopped, or start over with the documents you choose.'
                              : 'Try this setup again, or keep the last setup that finished.'}
                      </small>
                      {(folderOperationDecisionError || folderAnalysisStatus?.failure) && (
                        <details className="bootstrap-failure-details">
                          <summary>Technical details</summary>
                          <small>
                            {folderOperationDecisionError || folderAnalysisStatus?.failure}
                          </small>
                        </details>
                      )}
                    </span>
                    {folderAnalysisStatus?.state !== 'conflicted' &&
                      folderAnalysisStatus?.resumeAvailable !== false && (
                        <button
                          className="secondary-button small"
                          onClick={() => void resumeSavedFolderAnalysis()}
                          disabled={folderOperationDecision !== null}
                        >
                          {folderOperationDecision === 'resume' || resumedSetupStopping ? (
                            <LoaderCircle className="spin" size={14} />
                          ) : (
                            <RotateCw size={14} />
                          )}{' '}
                          {folderOperationDecision === 'resume'
                            ? 'Trying again…'
                            : resumedSetupStopping
                              ? 'Stopping…'
                              : folderAnalysisStatus?.state === 'failed'
                                ? 'Try again'
                                : 'Continue'}
                        </button>
                      )}
                    <button
                      className="secondary-button small"
                      onClick={() => void abandonSavedFolderAnalysis(folderAnalysisNeedsFreshStart)}
                      disabled={folderOperationDecision !== null}
                    >
                      {folderOperationDecision === 'abandon' ? (
                        <LoaderCircle className="spin" size={14} />
                      ) : (
                        <Undo2 size={14} />
                      )}{' '}
                      {folderAnalysisNeedsFreshStart
                        ? 'Choose documents again'
                        : folderAnalysisStatus?.available
                          ? 'Keep previous setup'
                          : 'Start over'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

      {mode === 'setup' ? (
        representativeSetupFailure ? (
          <div
            className="capture-empty bootstrap-failed"
            role="alert"
            data-telemetry={
              folderOperationDecisionError
                ? 'error:setup-resume-failed'
                : stoppedByPerson(externalFailure)
                  ? 'notice'
                  : setupFailureCategory(externalFailure) === 'unsupported'
                    ? 'error:source-unsupported-files'
                    : 'error:setup-failed'
            }
          >
            {resumableAfterFailure ? (
              <section>
                <span>
                  <TriangleAlert size={28} />
                </span>
                <div className="eyebrow">Setup stopped</div>
                <h2>Your place is saved.</h2>
                <p>{folderOperationDecisionError || externalFailure}</p>
                <small>
                  Your source documents were not changed, and the pages already read are kept.
                  Resume continues this setup where it stopped.
                </small>
                <div>
                  <button
                    className="primary-button"
                    onClick={() => void resumeSavedFolderAnalysis()}
                    disabled={folderOperationDecision !== null}
                  >
                    {folderOperationDecision === 'resume' ? (
                      <LoaderCircle className="spin" size={15} />
                    ) : (
                      <RotateCw size={15} />
                    )}{' '}
                    {folderOperationDecision === 'resume' ? 'Resuming…' : 'Resume setup'}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => void abandonSavedFolderAnalysis(true)}
                    disabled={folderOperationDecision !== null}
                  >
                    <Undo2 size={15} />{' '}
                    {job.bootstrapMode === 'folder'
                      ? 'Choose a different folder'
                      : 'Choose different files'}
                  </button>
                </div>
              </section>
            ) : (
              <section>
                <span>
                  <TriangleAlert size={28} />
                </span>
                <div className="eyebrow">Setup analysis stopped</div>
                <h2>No work is running.</h2>
                <p>{externalFailure}</p>
                <small>
                  Your source documents were not changed. Choose them again to retry this job setup.
                </small>
                <div>
                  <button
                    className="primary-button"
                    onClick={() =>
                      void onChooseRepresentativeSource(
                        job,
                        job.bootstrapMode === 'folder' ? 'folder' : 'files',
                      )
                    }
                  >
                    <RotateCw size={15} />{' '}
                    {job.bootstrapMode === 'folder'
                      ? 'Choose folder and retry'
                      : 'Choose files and retry'}
                  </button>
                </div>
              </section>
            )}
          </div>
        ) : representativeSetupProgressVisible || automaticSetupFinishing ? (
          <RepresentativeSetupProgress
            jobId={job.id}
            status={folderAnalysisStatus}
            progress={folderAnalysisProgress}
            activity={folderActivity}
            work={folderWork}
            externalActivity={externalActivity}
            processorOnlyExtraction={processorOnlyExtraction}
            finishing={automaticSetupFinishing}
            checkingDestination={schemaBindingPending}
            stagedDocuments={representativeStaged}
            learnedTypes={learnedTypes}
            headerActions={<JobProcessingButton jobId={job.id} compact />}
          />
        ) : (
          <div className={`setup-workspace${proposalReview ? ' proposal-review-workspace' : ''}`}>
            <aside className="setup-navigation-rail">
              <WorkspaceSectionNav
                label="Job setup sections"
                items={setupSections}
                active={setupSection}
                onChange={navigateSetup}
                telemetry="setup-nav"
              />
            </aside>
            <div className="setup-content-frame">
              <div
                className={`setup-section-content${setupSection === 'learning' ? ' is-learning' : ''}`}
                inert={Boolean(journeyFieldId)}
              >
                {setupSection === 'overview' && (
                  <SetupOverview
                    types={job.documentTypes}
                    separation={DOCUMENT_START_LABELS[job.separationMode]}
                    separationReady={separationReady}
                    reviewSummary={reviewBarLabel(job)}
                    lookupCount={lookupRules.length}
                    lookupError={lookupRulesFailed}
                    onOpen={(section) =>
                      section === 'document-breaks'
                        ? openStarts()
                        : section === 'review'
                          ? openSetupSection('thresholds')
                          : openSetupSection(section)
                    }
                    onOpenType={(id) => openTypeField(id)}
                  />
                )}
                {setupSection === 'learning' && (
                  <div className="setup-focused-section setup-learning-section">
                    {learningPanel}
                  </div>
                )}
                {setupSection === 'document-types' &&
                  (typeView === 'deck' || job.documentTypes.length === 0 ? (
                    <SetupTypeDeck
                      types={job.documentTypes}
                      report={typeReport}
                      learning={typeLearningStates}
                      loading={typeReportLoading}
                      query={typeSearch}
                      onQuery={setTypeSearch}
                      onOpen={(id) => {
                        selectDocumentType(job.documentTypes.find((type) => type.id === id));
                        setTypeView('type');
                      }}
                      onAdd={
                        schemaLocked
                          ? undefined
                          : () => {
                              setTypeSearch('');
                              addDocumentType();
                            }
                      }
                      actions={
                        proposalReview &&
                        (folderAnalysisStatus?.remainingFileCount ?? 0) > 0 && (
                          <button
                            className="secondary-button"
                            onClick={() => void readMoreDocuments()}
                            disabled={
                              setupApplying || analyzeMoreStarting || Boolean(journeyFieldId)
                            }
                          >
                            <SearchCheck size={16} />
                            {analyzeMoreStarting ? 'Saving sample changes…' : 'Read more documents'}
                          </button>
                        )
                      }
                    />
                  ) : (
                    <section className="setup-type-picker">{documentTypesPanel}</section>
                  ))}
                {setupSection === 'document-starts' && (
                  <div className="setup-focused-section">
                    <SeparationCard
                      job={job}
                      onChange={changeJob}
                      onOpenThresholds={() => {
                        setThresholdReturn(null);
                        openSetupSection('thresholds');
                      }}
                    />
                  </div>
                )}
                {setupSection === 'thresholds' && (
                  <div className="setup-focused-section setup-thresholds-section">
                    {thresholdReturn && (
                      <button
                        className="quiet-button small setup-context-back"
                        onClick={() =>
                          openTypeField(thresholdReturn.typeId, thresholdReturn.fieldId)
                        }
                      >
                        <ChevronLeft size={15} />
                        Back to {thresholdReturn.name}
                      </button>
                    )}
                    <ReviewPolicyCard
                      job={job}
                      onChange={changeJob}
                      onOpenField={openTypeField}
                      onOpenStarts={openStarts}
                    />
                  </div>
                )}
                {setupSection === 'lookups' && (
                  <div className="setup-focused-section setup-lookups-section">
                    <LookupsPanel
                      jobId={job.id}
                      documentTypes={job.documentTypes}
                      revision={lookupRevision}
                      sheetHost={listSheetHost}
                      covered={Boolean(journeyFieldId)}
                      onToast={onToast}
                      onChanged={() => setLookupRevision((value) => value + 1)}
                      onOpenField={(typeId, fieldId, dataTableId) => {
                        selectDocumentType(job.documentTypes.find((type) => type.id === typeId));
                        openLookupEditor(fieldId, dataTableId);
                      }}
                    />
                  </div>
                )}
                {setupSection === 'phones' && (
                  <div className="setup-focused-section setup-phones-section">
                    <PaigePhoneScanSetup />
                  </div>
                )}
                {setupSection === 'watched-folder' && hostHas('watched-folder') && (
                  <div className="setup-watched-section">
                    <WatchedFolderSection
                      jobId={job.id}
                      jobName={job.name}
                      onOpenExport={() => navigateSetup('export')}
                    />
                  </div>
                )}
                {exportSetupVisited && (
                  <div className="setup-export-section" hidden={setupSection !== 'export'}>
                    {hostHas('desktop-export') ? (
                      <ExportSetupCard
                        key={`${job.id}:${exportDraftGeneration}`}
                        job={job}
                        onToast={onToast}
                        onWorkspaceRefresh={onWorkspaceRefresh}
                        canRun={job.status === 'active'}
                        standalone
                        embedded
                        draftControl={exportDraftControl}
                        onDirtyChange={setExportHasChanges}
                        stopRequest={exportStopRequest}
                      />
                    ) : (
                      // PAIGE: Pilot mode lives on Review thresholds (CPv6 b1b3e8f7); Export is Paige's destinations.
                      <PaigeExportSetup job={job} />
                    )}
                  </div>
                )}
              </div>
              {/* Sheets slide over the section they were opened from instead of
              replacing it: the list sheet from Lookup lists, and the one lookup
              editor from either Lookup lists or Document types. */}
              <div className="setup-sheet-layer">
                <div className="setup-sheet-slot" ref={setListSheetHost} />
                {journeyFieldId && selectedType && (
                  <div className="setup-sheet-slot">
                    <div
                      className="setup-sheet-scrim"
                      aria-hidden="true"
                      onClick={requestLookupEditorClose}
                    />
                    <section
                      ref={lookupSheetRef}
                      className="setup-sheet setup-lookup-sheet"
                      role="dialog"
                      data-telemetry-dialog="lookup"
                      aria-label={`Lookup for ${selectedType.fields.find((field) => field.id === journeyFieldId)?.name ?? 'this field'}`}
                    >
                      <LookupJourney
                        key={`${job.id}:${selectedType.id}:${journeyFieldId}`}
                        embedded
                        jobName={job.name}
                        documentTypeName={selectedType.name}
                        jobId={job.id}
                        fields={selectedType.fields}
                        targetFieldId={journeyFieldId}
                        preferredDataTableId={journeyTableId ?? undefined}
                        onClose={closeListJourney}
                        onOpenLists={
                          setupSection === 'lookups'
                            ? undefined
                            : () => {
                                setJourneyFieldId(null);
                                setJourneyTableId(null);
                                openSetupSection('lookups');
                              }
                        }
                        onToast={onToast}
                        onFieldsPatch={applyFieldPatches}
                        onRulesChanged={() => setLookupRevision((value) => value + 1)}
                        onNavigationGuardChange={updateJourneyGuard}
                      />
                    </section>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      ) : null}

      {sampleOpen && batch && selectedType && (
        <WorkspaceDialog
          telemetry="setup-sample"
          title={`Sample ${selectedType.name}`}
          onClose={() => setSampleOpen(false)}
        >
          <div className="setup-sample-view">
            <ProposalEvidence
              batch={batch}
              selectedType={selectedType}
              selectedFieldId={expanded}
              documentTypes={job.documentTypes}
              schemaLocked={schemaLocked}
              handoffLocked={analyzeMoreStarting || setupApplying}
              // PAIGE: the desktop leaves a left-out sample's file out when it imports the folder (stageFolderImport); Paige's
              // setup samples already are the job's documents, so the choice is not offered (`setup-import-phase`).
              canLeaveOut={hostHas('setup-import-phase') && usesRepresentativeBootstrap(job)}
              onMoveDocument={moveProposalDocument}
            />
          </div>
        </WorkspaceDialog>
      )}
      {resetLearningSelection && (
        <ResetLearningDialog
          selection={resetLearningSelection}
          onClose={() => setResetLearningSelection(null)}
          onReset={async (target) => {
            if (
              captureDrainBlocked(target.jobId) ||
              setupApplyingRef.current ||
              analyzeMoreStartingRef.current
            )
              throw new Error('Let the current save finish, then try again.');
            setupApplyingRef.current = true;
            try {
              const result = await window.capturePointDesktop!.resetLearning(target.jobId, {
                typeId: target.typeId,
                scope: target.scope,
                fieldId: target.fieldId,
                expectedSchemaVersion: target.expectedSchemaVersion,
                confirmed: true,
              });
              const durable = migrateJob(result.job as Job);
              setupBaseline.current = durable;
              liveSetupJob.current = durable;
              onPersistedJobUpdated?.(durable);
              try {
                await onWorkspaceRefresh();
              } catch {
                onToast(
                  'Learning was reset. Reopen the job to refresh its setup.',
                  'job-refresh-failed',
                );
              }
            } finally {
              setupApplyingRef.current = false;
            }
          }}
        />
      )}
      {mode === 'setup' &&
        typePendingRemovalId &&
        job.documentTypes.find((item) => item.id === typePendingRemovalId) && (
          <RemoveDocumentTypeDialog
            key={typePendingRemovalId}
            type={job.documentTypes.find((item) => item.id === typePendingRemovalId)!}
            // PAIGE: no exceptions folder (NoExceptionsFolder): Autopilot holds such documents for a person, as Manual does.
            autopilot={
              hostHas('desktop-export') &&
              pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed'
            }
            onClose={() => setTypePendingRemovalId(null)}
            onRemove={
              typePendingRemovalSaved ? removeSavedDocumentType : removeManuallyAddedDocumentType
            }
            loadImpact={
              typePendingRemovalSaved && window.capturePointDesktop?.previewDocumentTypeRemoval
                ? () =>
                    window.capturePointDesktop!.previewDocumentTypeRemoval(
                      job.id,
                      typePendingRemovalId,
                    )
                : undefined
            }
            // Kept: its Type options. Removed: the next type's screen (an unsaved type), or the
            // deck (a saved type, and when no type is left).
            returnFocus={() =>
              (typeRemovalDone.current ? null : typeActionsButtonRef.current) ??
              typeBackRef.current ??
              document.querySelector<HTMLElement>('.setup-deck-open') ??
              document.querySelector<HTMLElement>('.setup-deck-new')
            }
          />
        )}
    </div>
  );
  // No bottom bar: saving lives beside the close button and appears only when
  // something changed. Accepting a proposed setup also starts its import and
  // leaves setup, so that one save keeps saying so.
  const setupSaveVisible =
    mode === 'setup' &&
    !representativeSetupProgressVisible &&
    !automaticSetupFinishing &&
    !representativeSetupFailure &&
    (setupHasChanges || savingAll || setupApplying);
  const setupSaveBusy = savingAll || setupApplying;
  const setupSaveBlocked =
    setupHasChanges &&
    (!separationReady ||
      folderAnalysisOwnsSetup ||
      analyzeMoreStarting ||
      schemaBindingPending ||
      schemaBindingFailed);
  const setupSaveBlockedReason = !separationReady
    ? 'Add the barcode that starts a document'
    : schemaBindingPending
      ? 'Checking who controls this setup…'
      : schemaBindingFailed
        ? 'Setup changes are paused'
        : 'Waiting for documents to be read';
  const setupSaveCloses = proposalReview;
  const leaveJourneyThen = (next: () => void) => {
    const finish = () => {
      setJourneyFieldId(null);
      next();
    };
    if (journeyNavigation.current) journeyNavigation.current.requestLeave(finish);
    else finish();
  };
  const discardSetupChanges = () => {
    setDiscardSetupPrompt(false);
    onChange(setupDiscardTarget());
    setExportHasChanges(false);
    setExportDraftGeneration((value) => value + 1);
  };
  const setupSaveActions = setupSaveVisible ? (
    <div className="setup-save-group" role="group" aria-label="Unsaved changes">
      <span className="setup-save-state">
        <span className="setup-save-dot" aria-hidden="true" />
        {setupSaveBusy ? 'Saving…' : setupSaveBlocked ? setupSaveBlockedReason : 'Unsaved changes'}
      </span>
      {!setupSaveBusy && (
        <button
          type="button"
          className="quiet-button small"
          onClick={() => leaveJourneyThen(() => setDiscardSetupPrompt(true))}
        >
          Discard
        </button>
      )}
      <button
        type="button"
        className="primary-button small"
        disabled={
          readOnlyView() || !changesSetup() || journeyBusy || setupSaveBusy || setupSaveBlocked
        }
        title={readOnlyView() ? READ_ONLY_HINT : changesSetup() ? undefined : SETUP_ROLE_HINT}
        onClick={() => leaveJourneyThen(() => void saveAllSetupChanges(setupSaveCloses))}
      >
        {setupSaveBusy ? <LoaderCircle className="spin" size={15} /> : null}
        {setupSaveCloses ? 'Save and close' : 'Save'}
      </button>
    </div>
  ) : null;
  // Always the first child, so switching to Job setup keeps this exact work
  // screen mounted (no reload, same document and page) and only freezes it:
  // inert blocks clicks, focus and its keyboard shortcuts while setup is open.
  const workHost = (
    <div
      key="work"
      className={`page job-workspace capture-mode${mode === 'setup' ? ' job-setup-behind' : ''}${setupWizardHolds ? ' setup-wizard-behind' : ''}`}
      inert={mode === 'setup' || setupWizardHolds}
    >
      {workView}
    </div>
  );
  // The setup wizard over the running work screen until setup's documents are processed.
  if (mode !== 'setup')
    return (
      <>
        {workHost}
        {setupWizardHolds && heldSetup && (
          <WorkspaceDialog
            telemetry="setup-wait"
            title={`Job setup - ${job.name}`}
            suspended={learningOpen}
            onClose={openJobNow}
          >
            <div className="page job-workspace setup-mode setup-processing-layout">
              <RepresentativeSetupProgress
                jobId={job.id}
                activity={folderActivity}
                work={folderWork}
                processorOnlyExtraction={processorOnlyExtraction}
                finishing
                stagedDocuments={heldDocuments}
                learnedTypes={learnedTypes}
                handoff={heldSetup}
                onStartChecking={startCheckingNow}
                headerActions={<JobProcessingButton jobId={job.id} compact />}
                importing={{
                  documents: heldDocuments,
                  read: heldRead,
                  live: heldImport?.live ?? null,
                  // PAIGE: the page of the document the count has reached, from the documents setup read.
                  previewPath: heldImport?.previewPath ?? heldPreviewPath,
                  remainingMilliseconds: heldImport?.remainingMilliseconds ?? null,
                  jobDocuments: heldImport?.documents ?? 0,
                  firstBatchFiles: heldImport?.firstBatchFiles ?? null,
                  firstBatchReady: Boolean(heldImport?.firstBatchReady),
                }}
              />
            </div>
          </WorkspaceDialog>
        )}
      </>
    );
  // Only the Job setup editor opens over a work screen. A first setup (its
  // progress, handoff or failure) is the whole view, even once the job turns
  // active while that handoff finishes.
  const workBehindSetup =
    job.status === 'active' &&
    !representativeSetupProgressVisible &&
    !automaticSetupFinishing &&
    !representativeSetupFailure;
  return (
    <>
      {workBehindSetup ? workHost : null}
      <WorkspaceDialog
        telemetry="job-setup"
        title={`Job setup - ${job.name}`}
        suspended={learningOpen || covered}
        onClose={() => requestSetupClose()}
        actions={setupSaveActions}
      >
        {setupContent}
      </WorkspaceDialog>
      {discardSetupPrompt && (
        <WorkspaceDialog
          telemetry="setup-discard"
          title="Discard your changes?"
          onClose={() => setDiscardSetupPrompt(false)}
          compact
        >
          <div style={{ padding: 24 }}>
            <p>Everything you changed since the last save goes back to how it was.</p>
            <div className="export-actions">
              <button className="secondary-button" onClick={discardSetupChanges}>
                Discard changes
              </button>
              <button className="quiet-button" onClick={() => setDiscardSetupPrompt(false)}>
                Keep editing
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
      {closeSetupPrompt && (
        <WorkspaceDialog
          telemetry="setup-save-prompt"
          title="Save your changes?"
          onClose={() => {
            setupExitDestination.current = null;
            setCloseSetupPrompt(false);
          }}
          compact
        >
          <div style={{ padding: 24 }}>
            <p>Your changes to this job's setup aren't saved yet.</p>
            {setupSaveBlocked && (
              <p className="setup-save-prompt-held" role="status">
                Not ready to save:{' '}
                {setupSaveBlockedReason.replace(/^\w/, (letter) => letter.toLowerCase())}
              </p>
            )}
            <div className="export-actions">
              <button
                className="primary-button"
                disabled={savingAll || setupSaveBlocked}
                onClick={() => void saveAllSetupChanges(true)}
              >
                Save and close
              </button>
              <button
                className="secondary-button"
                disabled={savingAll}
                onClick={() => {
                  discardSetupChanges();
                  setCloseSetupPrompt(false);
                  closeSetupDestination();
                }}
              >
                Close without saving
              </button>
              <button
                className="quiet-button"
                onClick={() => {
                  setupExitDestination.current = null;
                  setCloseSetupPrompt(false);
                }}
              >
                Keep editing
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
    </>
  );
}

/** What Export now would send, said the same way wherever there is an Export now. */
function ExportReadyStatus({ count }: { count: ExportReadyCount }) {
  return count.counting ? (
    <>
      <LoaderCircle className="spin" size={15} aria-hidden="true" />
      Counting documents…
    </>
  ) : count.error ? (
    <span className="export-count-error">
      Couldn’t count documents
      <OperatorErrorText message={count.error} />
    </span>
  ) : count.ready === null ? null : count.ready === 0 ? (
    <>Nothing ready yet</>
  ) : (
    <>
      {count.ready.toLocaleString()} {count.ready === 1 ? 'document' : 'documents'} ready
      {count.toExceptions ? ` · ${count.toExceptions.toLocaleString()} to exceptions` : ''}
    </>
  );
}

/**
 * Export now beside the fields, for a job set to "Only when I say". It works like every
 * other Export now (owner decision, 2026-10-04): the engine's own count beside it, disabled
 * while nothing is ready, and one click sends. There is no review box first; the count
 * already says what will go, and the result says what went.
 */
function ManualExportAction({
  jobId,
  onNotice,
  onRefresh,
}: {
  jobId: string;
  onNotice: PublishNotice;
  onRefresh: (batch: ProcessResult) => void;
}) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExportRunResult | null>(null);
  const running = useRef(false);
  useEffect(() => {
    let live = true;
    const api = window.capturePointDesktop;
    if (!api) return;
    const read = () => {
      void api
        .getExportSettings(jobId)
        .then((settings) => {
          if (live) setEnabled(Boolean(settings?.enabled && settings.triggerMode === 'manual'));
        })
        .catch(() => {
          if (live) setEnabled(false);
        });
    };
    read();
    const timer = window.setInterval(read, 5000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId]);
  const readyCount = useExportReadyCount(jobId, enabled);
  // As on the Export screen: a failed count still lets the operator try.
  const nothingToSend = !readyCount.error && !readyCount.ready;
  if (!enabled) return null;
  const send = async () => {
    const api = window.capturePointDesktop;
    if (!api || running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const result = await api.runExport(jobId);
      setResult(result);
      onNotice(exportRunNotice(result), exportRunNoticeCode(result));
      readyCount.recount();
      // The export went; a failed reread of the documents only leaves them as shown,
      // and must not turn its notice into "could not finish".
      const batch = await api.getLatestBatch(jobId).catch(() => null);
      if (batch) onRefresh(batch);
    } catch (error) {
      onNotice(messageFor(error, 'Export could not finish. Try again.'), 'export-failed');
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      <div className="manual-export-toolbar" role="group" aria-label="Manual export">
        <small role="status">{busy ? null : <ExportReadyStatus count={readyCount} />}</small>
        <button
          type="button"
          className="secondary-button"
          data-telemetry="fields-export-now"
          disabled={busy || nothingToSend}
          onClick={() => void send()}
        >
          {busy ? (
            <>
              <LoaderCircle className="spin" size={18} aria-hidden="true" />
              Exporting…
            </>
          ) : (
            <>
              <FolderOutput size={18} aria-hidden="true" />
              Export now
            </>
          )}
        </button>
      </div>
      {result && <ExportResultDialog result={result} onClose={() => setResult(null)} />}
    </>
  );
}

function ExportWorkspace({
  job,
  onToast,
  onSetup,
  onWorkspaceRefresh,
}: {
  job: Job;
  onToast: PublishNotice;
  onSetup: () => void;
  onWorkspaceRefresh: () => Promise<void>;
}) {
  const ready = job.status === 'active';
  return (
    <div className="page export-workspace">
      <section className="job-header export-workspace-header">
        <div className="job-title-row">
          <div>
            <div className="eyebrow">{job.name}</div>
            <h1>Export</h1>
            <p>Send finished documents. Check what arrived.</p>
          </div>
        </div>
        {!ready && (
          <div className="export-proposal-note">
            <LockKeyhole size={18} />
            <span>
              <strong>Configure export now</strong>
              <small>Documents will stay here until the proposed job setup is saved.</small>
            </span>
            <button className="secondary-button small" onClick={onSetup}>
              Review job setup
            </button>
          </div>
        )}
      </section>
      <div className="export-workspace-body">
        <ExportSetupCard
          key={job.id}
          job={job}
          onToast={onToast}
          onWorkspaceRefresh={onWorkspaceRefresh}
          canRun={ready}
          standalone
        />
      </div>
    </div>
  );
}

/** A folder export that reached its folder and still needs its backup copy. */
const folderBackupPending = (document: ExportDocumentSummary) =>
  document.destinationKind !== 'content-central' &&
  document.remoteEffectState === 'verified' &&
  document.localRecoveryState !== 'verified' &&
  (document.state === 'failed' || document.state === 'uncertain');

type ExportRetryAction =
  'retry-filesystem' | 'retry-delivery' | 'complete-local-recovery' | 'reconcile-cancellation';

function exportRetryAction(document: ExportDocumentSummary): ExportRetryAction | null {
  if (document.destinationKind !== 'content-central') {
    if (document.state !== 'failed' && document.state !== 'uncertain') return null;
    // In the folder already: a retry only makes the backup copy (the engine never
    // writes a verified folder delivery twice), so it is named for that.
    return folderBackupPending(document) ? 'complete-local-recovery' : 'retry-filesystem';
  }

  // New engines own this decision. An explicit null is meaningful: the engine
  // has inspected the exact remote effect and says that no retry is safe.
  if (document.remoteRecoveryAction !== undefined) {
    return document.remoteRecoveryAction === 'retry-delivery' ||
      document.remoteRecoveryAction === 'complete-local-recovery' ||
      document.remoteRecoveryAction === 'reconcile-cancellation'
      ? document.remoteRecoveryAction
      : null;
  }

  // Preserve an actionable screen during a rolling desktop/engine upgrade.
  // These are only fallbacks for summaries produced before the discriminator
  // existed; once it is present, the renderer never second-guesses it.
  if (
    document.failureCode === 'content-central-cancel-requested' ||
    document.failureCode === 'content-central-cancel-outcome-uncertain'
  )
    return 'reconcile-cancellation';
  if (
    document.remoteEffectState === 'verified' &&
    document.localRecoveryState !== 'verified' &&
    (document.state === 'failed' || document.state === 'uncertain')
  )
    return 'complete-local-recovery';
  return (document.state === 'failed' || document.state === 'uncertain') &&
    document.remoteEffectState !== 'verified' &&
    document.remoteEffectState !== 'failed' &&
    document.remoteEffectState !== 'canceled' &&
    document.failureCode !== 'content-central-request-identity-changed'
    ? 'retry-delivery'
    : null;
}

export function ExportSetupCard({
  job,
  onToast,
  onWorkspaceRefresh,
  canRun = true,
  standalone = false,
  embedded = false,
  draftControl,
  onDirtyChange,
  stopRequest,
}: {
  job: Job;
  onToast: PublishNotice;
  onWorkspaceRefresh?: () => Promise<void>;
  canRun?: boolean;
  standalone?: boolean;
  embedded?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  draftControl?: { current: { dirty: boolean; save: () => Promise<boolean> } | null };
  stopRequest?: { stop: ExportStop; request: number } | null;
}) {
  const api = window.capturePointDesktop;
  const exportControlId = useId();
  const [stop, setStop] = useState<ExportStop>('destination');
  const [namingOpen, setNamingOpen] = useState(false);
  const [ccWindow, setCcWindow] = useState<'match' | 'create' | 'connections' | null>(null);
  const [ccChoosing, setCcChoosing] = useState<'connection' | 'catalog' | null>(null);
  // Product telemetry (ADR-0081): Export's part of the area in front: its stop, and the File naming or Content
  // Central window over it (Connections, opened from Content Central, is part of that setup).
  useTelemetryArea('exportStop', stop);
  useTelemetryArea('exportWindow', namingOpen ? 'naming' : ccWindow ? 'content-central' : null);
  // The last failed Check with Content Central and the last failed new-catalog
  // plan, each shown where it happened rather than as a passing toast.
  const [ccCheckError, setCcCheckError] = useState<string | null>(null);
  const [ccCreateError, setCcCreateError] = useState<string | null>(null);
  const [ccJustAdded, setCcJustAdded] = useState(false);
  const [createCompleted, setCreateCompleted] = useState<{
    typeCount: number;
    fieldCount: number;
    reuse: boolean;
  } | null>(null);
  const addedConnection = useRef<DestinationConnectionSummary | null>(null);
  const [exportedToday, setExportedToday] = useState<number | null>(null);
  const [lastExportAt, setLastExportAt] = useState<string | null>(null);
  // Also send to is a stop only once a cloud service is offered here or one is already set up,
  // the same rule CloudDestination shows itself by, so it never opens an empty panel.
  const [cloudOffered, setCloudOffered] = useState(false);
  // The storage folder: where the default backup folder lives, and its disk's free space.
  const [storageLocation, setStorageLocation] = useState<StorageLocation | null>(null);
  useEffect(() => {
    if (!api) return;
    let live = true;
    void (async () => {
      const location = await api.getStorageLocation();
      if (live) setStorageLocation(location);
    })().catch(() => undefined); // Unreadable: the backup path falls back to plain words, with no free space.
    return () => {
      live = false;
    };
  }, [api]);
  useEffect(() => {
    if (!api) return;
    let live = true;
    void (async () => {
      const [catalog, summary] = await Promise.all([
        api.cloudExportProviders(),
        api.cloudExportDestination(job.id),
      ]);
      if (live)
        setCloudOffered(
          Boolean(summary.destination) ||
            CLOUD_PROVIDER_KINDS.some(
              (kind) => catalog.providers.find((item) => item.kind === kind)?.available,
            ),
        );
    })().catch(() => undefined); // An engine without cloud export shows no Also send to.
    return () => {
      live = false;
    };
  }, [api, job.id]);
  // The default folder is filled in on open (owner decision 2), unsaved until a save or Turn on.
  const prefilledDestination = useRef<ExportDestinationSelection | null>(null);
  /** What the last save asked of the switch (on, or as it was), so its Try again asks the same. */
  const saveTurnsOn = useRef<boolean | undefined>(undefined);
  /**
   * The next save gives this job its first destination: the settings loaded for it had none, and
   * nothing has been saved here since. That save turns export on (owner decision, 2026-10-26);
   * every later one leaves the switch as it is. Read against what was loaded, not the settings a
   * Content Central Apply writes (which set up the destination and leave export as it was).
   */
  const firstDestinationSave = useRef(true);
  const prefillTried = useRef(false);
  const [exportResultOpen, setExportResultOpen] = useState(false);
  const cardRef = useRef<HTMLElement | null>(null);
  const liveCard = useRef(true);
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  useEffect(() => {
    liveCard.current = true;
    return () => {
      liveCard.current = false;
    };
  }, []);
  const [settings, setSettings] = useState<ExportJobSettings | null>(null);
  // Export now is enabled only when the count says something can go. It counts
  // only a saved, enabled export (the preview refuses anything else).
  const readyCount = useExportReadyCount(job.id, Boolean(settings?.enabled) && canRun);
  const [plan, setPlan] = useState<FilingPlan | null>(null);
  const [preview, setPreview] = useState<FilingPlanPreview | null>(null);
  const [previewState, setPreviewState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [previewError, setPreviewError] = useState('');
  const [destination, setDestination] = useState<ExportDestinationSelection | null>(null);
  const [destinationKind, setDestinationKind] = useState<'filesystem' | 'content-central'>(
    'filesystem',
  );
  const [connections, setConnections] = useState<DestinationConnectionSummary[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [catalogs, setCatalogs] = useState<ContentCentralCatalogSummary[]>([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogListLoading, setCatalogListLoading] = useState(false);
  const [catalogListLoaded, setCatalogListLoaded] = useState(false);
  const [catalogListError, setCatalogListError] = useState('');
  const [catalogListRetry, setCatalogListRetry] = useState(0);
  const [catalogNextCursor, setCatalogNextCursor] = useState<string | null>(null);
  const [catalogMoreLoading, setCatalogMoreLoading] = useState(false);
  const [catalogMoreError, setCatalogMoreError] = useState('');
  const [catalogId, setCatalogId] = useState('');
  const [catalogDefinition, setCatalogDefinition] =
    useState<ContentCentralCatalogDefinition | null>(null);
  const [catalogDetailLoading, setCatalogDetailLoading] = useState(false);
  const [catalogDetailError, setCatalogDetailError] = useState('');
  const [catalogDetailRetry, setCatalogDetailRetry] = useState(0);
  const [contentCentralFilingPreview, setContentCentralFilingPreview] =
    useState<ContentCentralFilingPreviewResult | null>(null);
  const [contentCentralFilingPreviewLoading, setContentCentralFilingPreviewLoading] =
    useState(false);
  const [contentCentralFilingPreviewError, setContentCentralFilingPreviewError] = useState('');
  const [
    contentCentralFilingPreviewRevisionMismatch,
    setContentCentralFilingPreviewRevisionMismatch,
  ] = useState(false);
  const [contentCentralFilingPreviewRetry, setContentCentralFilingPreviewRetry] = useState(0);
  const [catalogBindingProposal, setCatalogBindingProposal] =
    useState<ContentCentralBindingProposal | null>(null);
  const [mappingChoices, setMappingChoices] = useState<ContentCentralMappingChoice[]>([]);
  const [catalogProvisioningPlan, setCatalogProvisioningPlan] =
    useState<ContentCentralProvisioningPlan | null>(null);
  const [catalogProposalDecision, setCatalogProposalDecision] =
    useState<PendingContentCentralProposalDecision | null>(null);
  const [catalogProposalApplyState, setCatalogProposalApplyState] = useState<string | null>(null);
  const [catalogProposalOutcomeKnown, setCatalogProposalOutcomeKnown] = useState(false);
  const [catalogProposalError, setCatalogProposalError] = useState<string | null>(null);
  const [mappingDirty, setMappingDirty] = useState(false);
  const [destinationBinding, setDestinationBinding] = useState<JobDestinationBinding | null>(null);
  const [triggerMode, setTriggerMode] = useState<ExportJobSettings['triggerMode']>('continuous');
  const [retentionDays, setRetentionDays] = useState<ExportJobSettings['retentionDays']>(null);
  const [scheduleLocalTime, setScheduleLocalTime] = useState('21:00');
  const [pdfProfile, setPdfProfile] = useState<ExportJobSettings['pdfProfile']>('searchable-pdf');
  // A folder export's files (ADR-0096): the PDF with its data and text files, the default for every job, or the PDF alone.
  const [files, setFiles] = useState<ExportFiles>('pdf-data-text');
  // Another exceptions folder chosen and not saved yet (ADR-0096); the saved one is settings.exceptionsRoot.
  const [exceptionsFolder, setExceptionsFolder] = useState<ExportDestinationSelection | null>(null);
  // The folders a job with none saved yet would get, as the main process works them out (nothing made): shown in
  // place of a guess. Null until read, or when the read fails (then no exceptions folder is shown, and backup copies
  // say "In Paige’s storage folder").
  const [folderDefaults, setFolderDefaults] = useState<{
    jobId: string;
    folders: ExportFolderDefaults | null;
  } | null>(null);
  useEffect(() => {
    let live = true;
    const read = window.capturePointDesktop?.getExportFolderDefaults;
    if (!read) {
      setFolderDefaults({ jobId: job.id, folders: null });
      return;
    }
    void read(job.id)
      .then((folders) => {
        if (live) setFolderDefaults({ jobId: job.id, folders });
      })
      .catch(() => {
        if (live) setFolderDefaults({ jobId: job.id, folders: null });
      });
    return () => {
      live = false;
    };
  }, [job.id]);
  const shownFolderDefaults = folderDefaults?.jobId === job.id ? folderDefaults.folders : null;
  const [pdfAAvailable, setPdfAAvailable] = useState(false);
  const [busy, setBusy] = useState<
    | 'loading'
    | 'suggesting'
    | 'starting'
    | 'saving'
    | 'switching'
    | 'running'
    | 'history-action'
    | 'proposing-catalog'
    | 'mapping-catalog'
    | 'applying-catalog'
    | 'discarding-catalog'
    | null
  >('loading');
  const [setupLoadError, setSetupLoadError] = useState('');
  const [setupLoadRetry, setSetupLoadRetry] = useState(0);
  const [planProposal, setPlanProposal] = useState<{
    plan: FilingPlan;
    preview: FilingPlanPreview;
    kind: 'suggested' | 'starter';
  } | null>(null);
  const [runResult, setRunResult] = useState<ExportRunResult | null>(null);
  const [history, setHistory] = useState<ExportDocumentSummary[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyActiveTotal, setHistoryActiveTotal] = useState(0);
  const [historyNeedsAttentionTotal, setHistoryNeedsAttentionTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyType, setHistoryType] = useState('');
  const [historyState, setHistoryState] = useState('');
  const [historyDate, setHistoryDate] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyFiltered, setHistoryFiltered] = useState(false);
  // Automatic deletes (ADR-0096): a learned junk shape deleted these as they arrived. The engine keeps them in the
  // job's Deleted view, not in its export history, so Activity lists them beside the exports, each with Restore.
  const [automaticDeletes, setAutomaticDeletes] = useState<DeletedDocument[]>([]);
  const [restoringDeleted, setRestoringDeleted] = useState<string | null>(null);
  // Delete on an exceptions row (no type, or not a document), by export id: the one being deleted, and the ones done.
  const [deletingException, setDeletingException] = useState<string | null>(null);
  const [deletedExceptions, setDeletedExceptions] = useState<ReadonlySet<string>>(new Set());
  // Completion of a held export/recovery action refreshes the view the operator
  // is looking at now, not the filters and offset captured when it started.
  const historyView = useRef({
    showHistory,
    historyOffset,
    historyQuery,
    historyType,
    historyState,
    historyDate,
  });
  historyView.current = {
    showHistory,
    historyOffset,
    historyQuery,
    historyType,
    historyState,
    historyDate,
  };
  const [folderPickerBusy, setFolderPickerBusy] = useState<'destination' | 'exceptions' | null>(
    null,
  );
  const [historyAction, setHistoryAction] = useState<{
    exportId: string;
    kind: 'retry' | 'cancel' | 'return';
  } | null>(null);
  const previewRequest = useRef(0);
  const contentCentralFilingPreviewRequest = useRef(0);
  const catalogListRequest = useRef(0);
  const catalogMoreInFlight = useRef(false);
  const catalogListContext = `${job.id}\u0000${destinationKind}\u0000${connectionId}\u0000${catalogQuery.trim()}`;
  const catalogListContextRef = useRef(catalogListContext);
  catalogListContextRef.current = catalogListContext;
  // A bound catalog is checked once each time this screen (or an explicit
  // catalog retry) opens it. The binding returned by that authoritative engine
  // check, rather than a renderer comparison of remote schema shapes, decides
  // whether the mapping is still usable.
  const checkedBoundCatalogs = useRef(new Set<string>());
  const historyRequest = useRef(0);
  const historyPollInFlight = useRef(false);
  // An Activity load in flight, set before its first await. The 5-second poll reads this, not
  // the historyLoading state its closure holds: a poll fired between the load's start and the
  // re-render took the load's request number, the load was discarded, and the spinner stayed.
  const historyLoadingNow = useRef(false);
  const folderPickerInFlight = useRef<'destination' | 'exceptions' | null>(null);
  const historyActionInFlight = useRef<string | null>(null);
  const exportMutationInFlight = useRef(false);
  // Async recommendations and saves are answers to one exact visible draft.
  // If the operator changes that draft before an answer returns, the answer is
  // stale and must not replace the newer choices.
  const exportDraftRevision = useRef(0);
  const exportDraftTouched = useRef(false);
  // Why the last save or Turn on/off failed, said in Export itself (owner, 2026-10-04):
  // plain words, the exact error behind Technical details, and the way forward. It used to be
  // only a status-bar line in engine words. Any edit clears it. `switchTo` is a failed Turn on
  // (true) or Turn off (false), which Try again asks again.
  const [saveProblem, setSaveProblem] = useState<{
    message: string;
    naming: boolean;
    code: ExportSaveFailure['code'];
    switchTo?: boolean;
  } | null>(null);
  const markExportDraftChanged = () => {
    exportDraftTouched.current = true;
    exportDraftRevision.current++;
    setSaveProblem(null);
  };
  const beginExportMutation = () => {
    if (exportMutationInFlight.current) return false;
    exportMutationInFlight.current = true;
    return true;
  };
  const endExportMutation = () => {
    exportMutationInFlight.current = false;
  };
  // A durable proposal belongs to the connection and catalog recorded with
  // that proposal, not to an older binding which happens to be saved on the
  // job. Keep that ownership explicit until Use or Keep resolves it.
  const restoredCatalogProposal = useRef(false);
  // React state disables the buttons on the next render, but two pointer or
  // keyboard events can still arrive in the same turn. This synchronous latch
  // guarantees that Use and Keep can never start competing durable operations.
  const catalogDecisionInFlight = useRef(false);
  // Once Content Central accepts a provisioning operation, every export-policy
  // control stays still until that exact durable operation resolves. Allowing a
  // destination switch here used to let Save race an accepted remote mutation.
  const catalogOperationLocked =
    busy === 'applying-catalog' ||
    busy === 'discarding-catalog' ||
    Boolean(catalogProposalApplyState);

  const clearHistoryPage = useCallback((offset = 0) => {
    setHistory([]);
    setHistoryTotal(0);
    setHistoryActiveTotal(0);
    // The needs-attention total is the whole job's (the engine ignores the Activity
    // filters for it), so a new filter or a failed reread keeps the last known warning.
    setHistoryFiltered(false);
    setHistoryOffset(offset);
  }, []);

  const applyHistoryPage = useCallback(
    (page: ExportDocumentPage, request: ExportDocumentListRequest) => {
      setHistory(page.documents);
      // An exceptions row deleted here and since restored (Deleted documents) is the engine's again: it no longer says Deleted.
      setDeletedExceptions((current) => {
        const restored = page.documents.filter(
          (row) => current.has(row.exportId) && row.documentDeleted !== true,
        );
        if (restored.length === 0) return current;
        const next = new Set(current);
        for (const row of restored) next.delete(row.exportId);
        return next;
      });
      setHistoryTotal(page.total);
      setHistoryActiveTotal(page.activeTotal);
      setHistoryNeedsAttentionTotal(page.needsAttentionTotal);
      setHistoryOffset(page.offset);
      const filtered = Boolean(
        request.query?.trim() ||
        request.documentType ||
        request.state ||
        request.exportedFromUtc ||
        request.exportedToUtc,
      );
      setHistoryFiltered(filtered);
      if (!filtered && page.offset === 0) setLastExportAt(latestExportAt(page.documents));
    },
    [],
  );

  const requestHistoryPage = useCallback(
    async (request: ExportDocumentListRequest) => {
      if (!api) return false;
      const requestId = ++historyRequest.current;
      clearHistoryPage(request.offset ?? 0);
      setHistoryError('');
      setHistoryLoading(true);
      historyLoadingNow.current = true;
      try {
        const page = await api.listExportedDocuments(request);
        if (requestId !== historyRequest.current) return false;
        applyHistoryPage(page, request);
        setHistoryError('');
        return true;
      } catch (reason) {
        if (requestId !== historyRequest.current) return false;
        clearHistoryPage(request.offset ?? 0);
        setHistoryError(messageFor(reason, 'Export activity could not be loaded'));
        return false;
      } finally {
        if (requestId === historyRequest.current) {
          setHistoryLoading(false);
          historyLoadingNow.current = false;
        }
      }
    },
    [api, applyHistoryPage, clearHistoryPage],
  );

  const refreshPreview = useCallback(
    (next: FilingPlan) => {
      if (!api) {
        setPreview(null);
        setPreviewState('error');
        setPreviewError(
          'Filing examples are unavailable because the desktop service is not connected.',
        );
        return;
      }
      const request = ++previewRequest.current;
      setPreview(null);
      setPreviewState('loading');
      setPreviewError('');
      void api
        .previewFilingPlan(job.id, next)
        .then((value) => {
          if (request !== previewRequest.current) return;
          setPreview(value);
          setPreviewState('ready');
          setPreviewError('');
        })
        .catch((reason) => {
          if (request !== previewRequest.current) return;
          setPreview(null);
          setPreviewState('error');
          setPreviewError(messageFor(reason, 'Current filing examples could not be built.'));
        });
    },
    [api, job.id],
  );

  const decisionFromPendingProposal = (
    pending: PendingContentCentralProposal,
  ): PendingContentCentralProposalDecision => ({
    jobId: job.id,
    connectionId: pending.connectionId,
    planId: pending.plan.planId,
    planFingerprint: pending.plan.planFingerprint,
    applyIdempotencyKey: pending.applyIdempotencyKey ?? null,
    applyState: pending.applyState ?? null,
    applyOperationId: pending.applyOperationId ?? null,
  });
  const sameProposalDecision = (
    left: PendingContentCentralProposalDecision,
    right: PendingContentCentralProposalDecision,
  ) =>
    left.jobId === right.jobId &&
    left.connectionId === right.connectionId &&
    left.planId === right.planId &&
    left.planFingerprint === right.planFingerprint &&
    (left.applyIdempotencyKey ?? null) === (right.applyIdempotencyKey ?? null) &&
    (left.applyState ?? null) === (right.applyState ?? null) &&
    (left.applyOperationId ?? null) === (right.applyOperationId ?? null);
  const hydratePendingCatalogProposal = (pending: PendingContentCentralProposal) => {
    const pendingCatalogId =
      pending.plan.changes.find((change) => change.objectKind === 'catalog')?.remoteId ?? '';
    const coordinatesChanged =
      pending.connectionId !== connectionId || pendingCatalogId !== catalogId;
    restoredCatalogProposal.current = true;
    setDestinationKind('content-central');
    setConnectionId(pending.connectionId);
    setCatalogQuery('');
    setCatalogId(pendingCatalogId);
    if (coordinatesChanged) {
      setCatalogDefinition(null);
      setCatalogBindingProposal(null);
      setMappingChoices([]);
      setMappingDirty(false);
    }
    setCatalogProvisioningPlan(pending.plan);
    setCatalogProposalDecision(decisionFromPendingProposal(pending));
    setCatalogProposalApplyState(pending.applyState ?? null);
    setCatalogProposalOutcomeKnown(pending.applyOutcomeKnown ?? false);
    setCatalogProposalError(pending.applyErrorMessage ?? null);
  };
  const hydrateDurableExportSetup = (
    durableSettings: ExportJobSettings | null,
    durableBinding: JobDestinationBinding | null,
    availableConnections: DestinationConnectionSummary[] = connections,
    keepOperatorChoices = false,
  ) => {
    const contentCentralBinding =
      durableBinding?.destinationKind === 'content-central' ? durableBinding : null;
    restoredCatalogProposal.current = false;
    setSettings(durableSettings);
    if (durableSettings) {
      const durablePlan = durableSettings.filingPlan;
      setPlan(durablePlan);
      if (!keepOperatorChoices) {
        setTriggerMode(durableSettings.triggerMode);
        setPdfProfile(durableSettings.pdfProfile);
        setFiles(durableSettings.files ?? 'pdf-data-text');
        setRetentionDays(durableSettings.retentionDays);
        setScheduleLocalTime(durableSettings.scheduleLocalTime ?? '21:00');
      }
      refreshPreview(durablePlan);
    }
    setDestinationKind(
      durableSettings?.destinationKind ?? durableBinding?.destinationKind ?? 'filesystem',
    );
    setDestination(null);
    setExceptionsFolder(null);
    setDestinationBinding(durableBinding);
    setCatalogQuery('');
    setConnectionId(
      contentCentralBinding?.connectionId ??
        (availableConnections.some((item) => item.connectionId === connectionId)
          ? connectionId
          : (availableConnections[0]?.connectionId ?? '')),
    );
    setCatalogId(contentCentralBinding?.remoteCatalogId ?? '');
    setCatalogDefinition(null);
    setCatalogDetailError('');
    setCatalogDetailLoading(Boolean(contentCentralBinding?.remoteCatalogId));
    setCatalogDetailRetry((value) => value + 1);
    const restoredBindingProposal = contentCentralBinding
      ? {
          binding: contentCentralBinding,
          blockers: bindingDeliversSomeTypes(contentCentralBinding) ? [] : [REVIEW_BEFORE_RESUMING],
        }
      : null;
    setCatalogBindingProposal(restoredBindingProposal);
    setMappingChoices(
      restoredBindingProposal ? mappingChoicesFromProposal(restoredBindingProposal, job) : [],
    );
    setMappingDirty(false);
    setCatalogProvisioningPlan(null);
    setCatalogProposalDecision(null);
    setCatalogProposalApplyState(null);
    setCatalogProposalOutcomeKnown(false);
    setCatalogProposalError(null);
  };

  useEffect(() => {
    if (!api) return;
    let live = true;
    exportDraftRevision.current++;
    previewRequest.current++;
    setBusy('loading');
    setSetupLoadError('');
    setSettings(null);
    setPlan(null);
    setPreview(null);
    setPreviewState('idle');
    setPreviewError('');
    setDestination(null);
    setExceptionsFolder(null);
    setPlanProposal(null);
    setRunResult(null);
    setCatalogProvisioningPlan(null);
    setCatalogProposalDecision(null);
    setCatalogProposalApplyState(null);
    setCatalogProposalOutcomeKnown(false);
    setCatalogProposalError(null);
    setCatalogBindingProposal(null);
    setMappingChoices([]);
    setMappingDirty(false);
    setCatalogListLoading(false);
    setCatalogListLoaded(false);
    setCatalogListError('');
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    catalogMoreInFlight.current = false;
    catalogListRequest.current++;
    setCatalogDetailLoading(false);
    setCatalogDetailError('');
    contentCentralFilingPreviewRequest.current++;
    setContentCentralFilingPreview(null);
    setContentCentralFilingPreviewLoading(false);
    setContentCentralFilingPreviewError('');
    setContentCentralFilingPreviewRevisionMismatch(false);
    setContentCentralFilingPreviewRetry(0);
    checkedBoundCatalogs.current.clear();
    restoredCatalogProposal.current = false;
    clearHistoryPage();
    setHistoryError('');
    setHistoryLoading(true);
    void requestHistoryPage({ jobId: job.id });
    void Promise.all([
      api.getExportSettings(job.id),
      api.getStarterFilingPlan(job.id),
      api.getExportCapabilities(),
      api.listDestinationConnections(),
      api.getJobDestinationBinding(job.id),
      api.getPendingContentCentralProposal(job.id),
    ])
      .then(([stored, starter, capabilities, savedConnections, savedBinding, pendingProposal]) => {
        if (!live) return;
        const next = stored?.filingPlan ?? starter;
        setSettings(stored);
        firstDestinationSave.current = !exportHasDestination(stored);
        setPlan(next);
        setTriggerMode(stored?.triggerMode ?? 'continuous');
        // A new job starts at 30 days; a saved choice (Forever included) is kept exactly.
        setRetentionDays(stored ? stored.retentionDays : NEW_JOB_BACKUP_DAYS);
        setScheduleLocalTime(stored?.scheduleLocalTime ?? '21:00');
        setPdfProfile(stored?.pdfProfile ?? 'searchable-pdf');
        setFiles(stored?.files ?? 'pdf-data-text');
        setPdfAAvailable(capabilities.pdfA2u);
        // A reviewed proposal is durable engine state. Reopen its Content
        // Central context even if the last saved export still points at a
        // folder, so the operator can always Use or Keep it deliberately.
        setDestinationKind(
          pendingProposal
            ? 'content-central'
            : (stored?.destinationKind ?? savedBinding?.destinationKind ?? 'filesystem'),
        );
        setConnections(savedConnections);
        setDestinationBinding(savedBinding);
        const pendingCatalogId =
          pendingProposal?.plan.changes.find((change) => change.objectKind === 'catalog')
            ?.remoteId ?? '';
        restoredCatalogProposal.current = Boolean(pendingProposal);
        setConnectionId(
          pendingProposal?.connectionId ??
            savedBinding?.connectionId ??
            savedConnections[0]?.connectionId ??
            '',
        );
        setCatalogId(pendingProposal ? pendingCatalogId : (savedBinding?.remoteCatalogId ?? ''));
        setCatalogProvisioningPlan(pendingProposal?.plan ?? null);
        setCatalogProposalDecision(
          pendingProposal
            ? {
                jobId: job.id,
                connectionId: pendingProposal.connectionId,
                planId: pendingProposal.plan.planId,
                planFingerprint: pendingProposal.plan.planFingerprint,
                applyIdempotencyKey: pendingProposal.applyIdempotencyKey ?? null,
                applyState: pendingProposal.applyState ?? null,
                applyOperationId: pendingProposal.applyOperationId ?? null,
              }
            : null,
        );
        setCatalogProposalApplyState(pendingProposal?.applyState ?? null);
        setCatalogProposalOutcomeKnown(pendingProposal?.applyOutcomeKnown ?? false);
        setCatalogProposalError(pendingProposal?.applyErrorMessage ?? null);
        refreshPreview(next);
      })
      .catch((reason) => {
        if (!live) return;
        const message = messageFor(reason, 'Export setup could not be loaded');
        setSetupLoadError(message);
        onToast(message, 'export-setup-load-failed');
      })
      .finally(() => {
        if (live) setBusy(null);
      });
    return () => {
      live = false;
      previewRequest.current++;
      historyRequest.current++;
    };
  }, [api, clearHistoryPage, job.id, refreshPreview, requestHistoryPage, setupLoadRetry]);

  useEffect(() => {
    const request = ++catalogListRequest.current;
    const context = catalogListContext;
    setCatalogs([]);
    setCatalogListLoaded(false);
    setCatalogListError('');
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    catalogMoreInFlight.current = false;
    if (!api || destinationKind !== 'content-central' || !connectionId) {
      setCatalogListLoading(false);
      return;
    }
    let live = true;
    setCatalogListLoading(true);
    const timer = window.setTimeout(
      () =>
        void api
          .listContentCentralCatalogs(connectionId, catalogQuery.trim() || undefined)
          .then((page) => {
            if (
              !live ||
              request !== catalogListRequest.current ||
              context !== catalogListContextRef.current
            )
              return;
            setCatalogs(page.items.slice(0, CONTENT_CENTRAL_CATALOG_RENDER_LIMIT));
            setCatalogNextCursor(page.nextCursor ?? null);
            setCatalogListLoaded(true);
          })
          .catch((reason) => {
            if (
              !live ||
              request !== catalogListRequest.current ||
              context !== catalogListContextRef.current
            )
              return;
            setCatalogListLoaded(false);
            const message = messageFor(reason, 'Content Central catalogs could not be loaded');
            setCatalogListError(message);
            onToastRef.current(message, 'catalog-load-failed');
          })
          .finally(() => {
            if (
              live &&
              request === catalogListRequest.current &&
              context === catalogListContextRef.current
            )
              setCatalogListLoading(false);
          }),
      250,
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [api, catalogListRetry, catalogQuery, connectionId, destinationKind, job.id]);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const loadMoreContentCentralCatalogs = async () => {
    const cursor = catalogNextCursor;
    if (
      !api ||
      !cursor ||
      catalogMoreInFlight.current ||
      destinationKind !== 'content-central' ||
      !connectionId ||
      catalogs.length >= CONTENT_CENTRAL_CATALOG_RENDER_LIMIT
    )
      return;
    catalogMoreInFlight.current = true;
    const request = ++catalogListRequest.current;
    const context = catalogListContextRef.current;
    setCatalogMoreLoading(true);
    setCatalogMoreError('');
    try {
      const page = await api.listContentCentralCatalogs(
        connectionId,
        catalogQuery.trim() || undefined,
        cursor,
      );
      if (
        request !== catalogListRequest.current ||
        context !== catalogListContextRef.current ||
        !liveCard.current
      )
        return;
      setCatalogs((current) => mergeContentCentralCatalogs(current, page.items));
      setCatalogNextCursor(page.nextCursor ?? null);
    } catch (reason) {
      if (
        request !== catalogListRequest.current ||
        context !== catalogListContextRef.current ||
        !liveCard.current
      )
        return;
      setCatalogMoreError(messageFor(reason, 'More Content Central catalogs could not be loaded'));
    } finally {
      if (
        request === catalogListRequest.current &&
        context === catalogListContextRef.current &&
        liveCard.current
      ) {
        catalogMoreInFlight.current = false;
        setCatalogMoreLoading(false);
      }
    }
  };

  const refreshContentCentralCatalogSearch = () => {
    catalogListRequest.current++;
    catalogMoreInFlight.current = false;
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    setCatalogNextCursor(null);
    setCatalogListRetry((value) => value + 1);
  };

  useEffect(() => {
    if (!api || destinationKind !== 'content-central' || !connectionId || !catalogId) {
      setCatalogDefinition(null);
      setCatalogBindingProposal(null);
      setCatalogDetailLoading(false);
      setCatalogDetailError('');
      return;
    }
    let live = true;
    let proposalIntentId: string | null = null;
    let proposalPresented = false;
    setCatalogDefinition(null);
    setCatalogBindingProposal(null);
    setMappingChoices([]);
    setMappingDirty(false);
    setCatalogDetailLoading(true);
    setCatalogDetailError('');
    const matchesBoundCatalog =
      destinationBinding?.jobId === job.id &&
      destinationBinding.connectionId === connectionId &&
      sameContentCentralId(destinationBinding.remoteCatalogId, catalogId);
    // A restored proposal may target a different connection/catalog from the
    // job's last committed binding. Its reviewed plan must remain authoritative;
    // asking for a fresh binding proposal here could silently replace it.
    if (restoredCatalogProposal.current && !matchesBoundCatalog) {
      setCatalogDetailLoading(false);
      return;
    }
    const checkKey = `${job.id}\u0000${connectionId}\u0000${catalogId}\u0000${catalogDetailRetry}`;
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const load = async () => {
      let effectiveBinding = destinationBinding;
      // An accepted Apply owns its own reconciliation. Checking the catalog in
      // the middle of that operation could mistake a legitimate intermediate
      // schema for drift; the newly committed binding is checked after Apply.
      if (
        matchesBoundCatalog &&
        !catalogProposalApplyState &&
        !checkedBoundCatalogs.current.has(checkKey)
      ) {
        checkedBoundCatalogs.current.add(checkKey);
        let checkFailure: unknown = null;
        try {
          const checkedConnection = await api.recheckContentCentral(connectionId);
          if (live)
            setConnections((current) =>
              current.map((item) =>
                item.connectionId === checkedConnection.connectionId ? checkedConnection : item,
              ),
            );
        } catch (reason) {
          checkFailure = reason;
          try {
            const refreshedConnections = await api.listDestinationConnections();
            if (live) setConnections(refreshedConnections);
          } catch {
            /* The catalog check's original failure is the useful error. */
          }
        }
        const refreshedBinding = await api.getJobDestinationBinding(job.id);
        if (!live) return;
        setDestinationBinding(refreshedBinding);
        if (checkFailure) throw checkFailure;
        if (
          !refreshedBinding ||
          refreshedBinding.connectionId !== connectionId ||
          !sameContentCentralId(refreshedBinding.remoteCatalogId, catalogId)
        )
          throw new Error(
            "This job's saved Content Central catalog changed while it was being checked.",
          );
        effectiveBinding = refreshedBinding;
      }
      const definition = await api.getContentCentralCatalog(connectionId, catalogId);
      if (!live) return;
      const proposal = matchesBoundCatalog
        ? ({
            binding: effectiveBinding,
            blockers:
              effectiveBinding && bindingDeliversSomeTypes(effectiveBinding)
                ? []
                : [REVIEW_BEFORE_RESUMING],
          } as ContentCentralBindingProposal)
        : await (() => {
            proposalIntentId = crypto.randomUUID();
            return api.proposeContentCentralBinding(
              job.id,
              connectionId,
              catalogId,
              undefined,
              proposalIntentId,
            );
          })();
      if (!live) return;
      if (proposalIntentId) proposalPresented = true;
      return { definition, proposal };
    };
    void load()
      .then((loaded) => {
        if (!loaded || !live) return;
        const { definition, proposal } = loaded;
        if (!live) return;
        setCatalogDefinition(definition);
        setCatalogBindingProposal(proposal);
        setMappingChoices(mappingChoicesFromProposal(proposal, job));
        setMappingDirty(false);
        if (proposal.plan) {
          setCatalogProvisioningPlan(proposal.plan);
          setCatalogProposalDecision({
            jobId: job.id,
            connectionId,
            planId: proposal.plan.planId,
            planFingerprint: proposal.plan.planFingerprint,
            applyIdempotencyKey: null,
            applyState: null,
            applyOperationId: null,
          });
          setCatalogProposalApplyState(null);
          setCatalogProposalOutcomeKnown(false);
          setCatalogProposalError(null);
        }
      })
      .catch((reason) => {
        if (!live) return;
        const message = messageFor(reason, 'The Content Central catalog could not be read');
        setCatalogDetailError(message);
        onToastRef.current(message, 'catalog-load-failed');
      })
      .finally(() => {
        if (live) setCatalogDetailLoading(false);
      });
    return () => {
      live = false;
      if (proposalIntentId && !proposalPresented)
        void api
          .cancelContentCentralProposalIntent(job.id, connectionId, proposalIntentId)
          .catch(() => undefined);
    };
  }, [
    api,
    catalogDetailRetry,
    catalogId,
    catalogProposalApplyState,
    connectionId,
    destinationBinding?.bindingId,
    destinationBinding?.bindingRevision,
    destinationBinding?.connectionId,
    destinationBinding?.jobId,
    destinationBinding?.remoteCatalogId,
    destinationBinding?.status,
    destinationKind,
    job.id,
  ]);

  useEffect(() => {
    const request = ++contentCentralFilingPreviewRequest.current;
    const expectedCatalog = catalogDefinition?.catalog;
    const boundCatalogSelected = Boolean(
      api &&
      destinationKind === 'content-central' &&
      destinationBinding?.jobId === job.id &&
      destinationBinding.connectionId === connectionId &&
      sameContentCentralId(destinationBinding.remoteCatalogId, catalogId) &&
      catalogId &&
      expectedCatalog &&
      sameContentCentralId(expectedCatalog.id, catalogId) &&
      !catalogDetailLoading &&
      !catalogDetailError,
    );
    if (!boundCatalogSelected) {
      setContentCentralFilingPreview(null);
      setContentCentralFilingPreviewLoading(false);
      setContentCentralFilingPreviewError('');
      setContentCentralFilingPreviewRevisionMismatch(false);
      return;
    }
    // While an operator edits or reviews a proposal, the proposal's own
    // server-rendered previews are the only truthful examples. Keep the last
    // committed preview in memory so unchanged mappings do not blink away, but
    // never start another committed-catalog request for unreviewed choices.
    if (mappingDirty || catalogProvisioningPlan) {
      setContentCentralFilingPreviewLoading(false);
      setContentCentralFilingPreviewError('');
      setContentCentralFilingPreviewRevisionMismatch(false);
      return;
    }
    const expectedCatalogId = catalogId;
    const expectedSchemaRevision = expectedCatalog?.schemaRevision;
    const expectedFilingRevision = expectedCatalog?.filingRevision;
    if (!expectedSchemaRevision || !expectedFilingRevision) {
      setContentCentralFilingPreview(null);
      setContentCentralFilingPreviewLoading(false);
      setContentCentralFilingPreviewRevisionMismatch(false);
      setContentCentralFilingPreviewError(
        'Content Central returned an incomplete catalog revision for this filing example.',
      );
      return;
    }
    setContentCentralFilingPreview(null);
    setContentCentralFilingPreviewLoading(true);
    setContentCentralFilingPreviewError('');
    setContentCentralFilingPreviewRevisionMismatch(false);
    void api!
      .previewContentCentralFiling(job.id)
      .then((result) => {
        if (request !== contentCentralFilingPreviewRequest.current) return;
        if (result.catalogId.toLocaleLowerCase() !== expectedCatalogId.toLocaleLowerCase()) {
          setContentCentralFilingPreviewError(
            'Content Central returned an example for a different catalog.',
          );
          return;
        }
        if (
          result.schemaRevision !== expectedSchemaRevision ||
          result.filingRevision !== expectedFilingRevision
        ) {
          setContentCentralFilingPreviewRevisionMismatch(true);
          setContentCentralFilingPreviewError(
            'Content Central changed this catalog while its filing example was being prepared. Try again to load one consistent version.',
          );
          return;
        }
        setContentCentralFilingPreviewRevisionMismatch(false);
        setContentCentralFilingPreview(result);
      })
      .catch((reason) => {
        if (request !== contentCentralFilingPreviewRequest.current) return;
        setContentCentralFilingPreviewRevisionMismatch(false);
        setContentCentralFilingPreviewError(
          messageFor(reason, 'The filing example could not be loaded.'),
        );
      })
      .finally(() => {
        if (request === contentCentralFilingPreviewRequest.current)
          setContentCentralFilingPreviewLoading(false);
      });
    return () => {
      if (request === contentCentralFilingPreviewRequest.current)
        contentCentralFilingPreviewRequest.current++;
    };
  }, [
    api,
    catalogDefinition?.catalog.filingRevision,
    catalogDefinition?.catalog.id,
    catalogDefinition?.catalog.schemaRevision,
    catalogDetailError,
    catalogDetailLoading,
    catalogId,
    catalogProvisioningPlan,
    connectionId,
    contentCentralFilingPreviewRetry,
    destinationBinding?.bindingId,
    destinationBinding?.bindingRevision,
    destinationBinding?.connectionId,
    destinationBinding?.jobId,
    destinationBinding?.remoteCatalogId,
    destinationBinding?.remoteDeliverySchemaRevision,
    destinationBinding?.remoteFilingRevisionObserved,
    destinationKind,
    job.id,
    mappingDirty,
  ]);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const reviewCatalogMapping = async () => {
    if (!api || !connectionId || !catalogId || !beginExportMutation()) return;
    const revision = exportDraftRevision.current;
    const proposalIntentId = crypto.randomUUID();
    let proposalPresented = false;
    restoredCatalogProposal.current = false;
    setBusy('mapping-catalog');
    setCcCheckError(null);
    try {
      const proposal = await api.proposeContentCentralBinding(
        job.id,
        connectionId,
        catalogId,
        mappingChoices,
        proposalIntentId,
      );
      if (!liveCard.current || revision !== exportDraftRevision.current) return;
      proposalPresented = true;
      setCatalogBindingProposal(proposal);
      setMappingChoices(mappingChoicesFromProposal(proposal, job, mappingChoices));
      setCatalogProvisioningPlan(proposal.plan ?? null);
      setCatalogProposalDecision(
        proposal.plan
          ? {
              jobId: job.id,
              connectionId,
              planId: proposal.plan.planId,
              planFingerprint: proposal.plan.planFingerprint,
              applyIdempotencyKey: null,
              applyState: null,
              applyOperationId: null,
            }
          : null,
      );
      setMappingDirty(false);
      onToast(
        proposal.blockers.length
          ? 'The mapping still needs attention'
          : 'Catalog mapping ready to review',
        proposal.blockers.length ? 'catalog-plan-blocked' : undefined,
      );
    } catch (reason) {
      // Shown in the matching window with its Technical details, not as a toast.
      if (liveCard.current && revision === exportDraftRevision.current)
        setCcCheckError(messageFor(reason, 'The catalog mapping could not be reviewed'));
    } finally {
      if (!proposalPresented) {
        try {
          await api.cancelContentCentralProposalIntent(job.id, connectionId, proposalIntentId);
        } catch {
          /* A newer exact intent already owns the durable slot. */
        }
      }
      endExportMutation();
      if (liveCard.current) setBusy(null);
    }
  };

  const chooseDestination = async () => {
    if (!api || catalogOperationLocked || folderPickerInFlight.current) return;
    folderPickerInFlight.current = 'destination';
    setFolderPickerBusy('destination');
    try {
      const chosen = await api.chooseExportDestination(job.id);
      if (chosen && liveCard.current) {
        markExportDraftChanged();
        setDestination(chosen);
      }
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(reason, 'The export folder could not be chosen'),
          'export-folder-choose-failed',
        );
    } finally {
      if (folderPickerInFlight.current === 'destination') {
        folderPickerInFlight.current = null;
        if (liveCard.current) setFolderPickerBusy(null);
      }
    }
  };
  /**
   * Owner decision 2 (2026-09-25): a job with nowhere to send files opens on
   * <Documents>\Paige Jobs\<job>\exports (%ProgramData%\Ademero\Paige Jobs under the service;
   * ADR-0096), filled in exactly as the picker would.
   * It is not an edit (nothing asks to be saved) and nothing is exported until the
   * operator saves or clicks Turn on (since 2026-10-26; it was Start exporting). Change
   * picks another folder.
   */
  useEffect(() => {
    if (
      !api?.offerDefaultExportDestination ||
      prefillTried.current ||
      !plan ||
      busy !== null ||
      catalogOperationLocked ||
      destinationKind !== 'filesystem' ||
      destination
    )
      return;
    if (
      settings &&
      ((settings.destinationKind ?? 'filesystem') !== 'filesystem' || settings.destinationRoot)
    )
      return;
    prefillTried.current = true;
    void api
      .offerDefaultExportDestination(job.id, job.name)
      .then((offered) => {
        if (!liveCard.current) return;
        prefilledDestination.current = offered;
        setDestination((current) => current ?? offered);
      })
      .catch(() => {
        /* The folder stays unchosen; Choose a folder still works. */
      });
  }, [
    api,
    plan,
    busy,
    catalogOperationLocked,
    destinationKind,
    destination,
    settings,
    job.id,
    job.name,
  ]);
  /**
   * Saves what is shown: Save export, Job setup's Save and its leave-setup prompt (draftControl),
   * and Turn on before anything is saved. The switch follows `exportEnabledAfterSave` (owner
   * decision, 2026-10-26): the save that gives the job its first destination turns export on,
   * whichever panel it is made from; a later one leaves the switch as it is. It replaced
   * Start exporting, the only way export could be turned on, with Job setup's Save leaving it
   * off: "As soon as they're ready" saved from the When panel never exported. A filled-in folder
   * is asked for again either way: its permission lasts minutes, and saving the one offered when
   * Export opened said "Choose the export folder again." once setup had been open a while.
   */
  const saveShown = async (turnOn?: boolean) => {
    if (!api) return false;
    saveTurnsOn.current = turnOn;
    let chosen: ExportDestinationSelection | undefined;
    // A prefilled folder's permission is short-lived; ask for it again at the moment of saving.
    if (
      destinationKind === 'filesystem' &&
      destination &&
      destination === prefilledDestination.current
    ) {
      try {
        chosen = await api.offerDefaultExportDestination(job.id, job.name);
        if (!liveCard.current) return false;
        prefilledDestination.current = chosen;
        setDestination(chosen);
      } catch (reason) {
        const failure = exportSaveFailure({
          step: 'prepare-folder',
          wasOn: Boolean(settings?.enabled),
        });
        reportExportSaveFailed(failure, destinationKind, job.id);
        if (liveCard.current)
          setSaveProblem({
            naming: false,
            code: failure.code,
            message: `The export folder couldn’t be prepared, so export wasn’t turned on.${TECHNICAL_DETAILS_MARKER}${reason instanceof Error ? reason.message : String(reason)}`,
          });
        return false;
      }
    }
    return Boolean(await save(chosen, turnOn));
  };
  /**
   * The switch (owner decision, 2026-10-26). With a destination saved it changes only on or off:
   * the saved settings exactly, so a draft of other choices stays a draft for Save. Before
   * anything is saved, Turn on saves what is shown, which is the job's first destination.
   */
  const toggleExport = async (next: boolean) => {
    if (!api) return false;
    if (!settings || !exportHasDestination(settings))
      return next ? Boolean(await saveShown(true)) : false;
    if (catalogOperationLocked || !beginExportMutation()) return false;
    setBusy('switching');
    setSaveProblem(null);
    try {
      const saved = await api.saveExportSettings(exportSwitchRequest(settings, next));
      announceExportDestinationChanged();
      if (!liveCard.current) return false;
      setSettings(saved);
      firstDestinationSave.current = false;
      onToast(next ? 'Export is on' : 'Export is off');
      return true;
    } catch (reason) {
      if (!liveCard.current) return false;
      const failure = exportSaveFailure({ step: 'save', wasOn: !next });
      reportExportSaveFailed(failure, settings.destinationKind ?? 'filesystem', job.id);
      setSaveProblem({
        naming: false,
        code: failure.code,
        switchTo: next,
        message: `${next ? 'Export wasn’t turned on' : 'Export wasn’t turned off'}.${TECHNICAL_DETAILS_MARKER}${reason instanceof Error ? reason.message : String(reason)}`,
      });
      return false;
    } finally {
      endExportMutation();
      if (liveCard.current) setBusy(null);
    }
  };
  /** Change folder (Choose a folder while it has none) for the exceptions folder (ADR-0096): any local folder, saved with Export's save. */
  const chooseExceptionsFolder = async () => {
    if (!api?.chooseExceptionsFolder || catalogOperationLocked || folderPickerInFlight.current)
      return;
    folderPickerInFlight.current = 'exceptions';
    setFolderPickerBusy('exceptions');
    try {
      const chosen = await api.chooseExceptionsFolder(job.id);
      if (chosen && liveCard.current) {
        markExportDraftChanged();
        setExceptionsFolder(chosen);
      }
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(reason, 'The exceptions folder could not be chosen'),
          'export-folder-choose-failed',
        );
    } finally {
      if (folderPickerInFlight.current === 'exceptions') {
        folderPickerInFlight.current = null;
        if (liveCard.current) setFolderPickerBusy(null);
      }
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const proposeCatalog = async (): Promise<boolean> => {
    if (!api || !plan || !connectionId || catalogProposalApplyState || !beginExportMutation())
      return false;
    const revision = exportDraftRevision.current;
    const proposalIntentId = crypto.randomUUID();
    let proposalPresented = false;
    restoredCatalogProposal.current = false;
    setBusy('proposing-catalog');
    setCcCreateError(null);
    try {
      const proposal = await api.proposeContentCentralCatalog(
        job.id,
        connectionId,
        plan,
        proposalIntentId,
      );
      if (!liveCard.current || revision !== exportDraftRevision.current) return false;
      proposalPresented = true;
      setCatalogProvisioningPlan(proposal);
      setCatalogProposalDecision({
        jobId: job.id,
        connectionId,
        planId: proposal.planId,
        planFingerprint: proposal.planFingerprint,
        applyIdempotencyKey: null,
        applyState: null,
        applyOperationId: null,
      });
      setCatalogProposalApplyState(null);
      setCatalogProposalOutcomeKnown(false);
      setCatalogProposalError(null);
      return true;
    } catch (reason) {
      // Shown beside Create in the Catalog step with its Technical details, not as a toast.
      if (liveCard.current && revision === exportDraftRevision.current)
        setCcCreateError(messageFor(reason, 'The catalog proposal could not be prepared'));
    } finally {
      if (!proposalPresented) {
        try {
          await api.cancelContentCentralProposalIntent(job.id, connectionId, proposalIntentId);
        } catch {
          /* A newer exact intent already owns the durable slot. */
        }
      }
      endExportMutation();
      if (liveCard.current) setBusy(null);
    }
    return false;
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const applyCatalog = async (): Promise<boolean> => {
    if (
      !api ||
      !catalogProvisioningPlan ||
      !connectionId ||
      catalogProvisioningPlan.blockers.length ||
      busy !== null ||
      catalogDecisionInFlight.current
    )
      return false;
    catalogDecisionInFlight.current = true;
    const revision = exportDraftRevision.current;
    setBusy('applying-catalog');
    try {
      const result = await api.applyContentCentralCatalog(
        job.id,
        connectionId,
        catalogProvisioningPlan.planId,
        catalogProvisioningPlan.planFingerprint,
      );
      announceExportDestinationChanged();
      if (!liveCard.current || revision !== exportDraftRevision.current) return false;
      markExportDraftChanged();
      setSettings(result.settings);
      setPlan(result.settings.filingPlan);
      // Apply sets up the destination only. When, Files and Backup copy stay
      // as the operator chose them (a job without saved settings comes back
      // with the engine's inert defaults); Job setup's Save stores them.
      setDestinationKind('content-central');
      setDestination(null);
      setDestinationBinding(result.binding);
      setCatalogBindingProposal({ binding: result.binding, blockers: [] });
      setMappingDirty(false);
      setCatalogId(result.binding.remoteCatalogId ?? '');
      // The bound-catalog effect owns the post-Apply recheck and definition as
      // one revision-consistent read. Publishing a definition here first let a
      // direct filing preview start against it while that check was replacing
      // the same catalog state, producing two answers for one visible open.
      setCatalogDefinition(null);
      setCatalogDetailLoading(Boolean(result.binding.remoteCatalogId));
      restoredCatalogProposal.current = false;
      setCatalogProvisioningPlan(null);
      setCatalogProposalDecision(null);
      setCatalogProposalApplyState(null);
      setCatalogProposalOutcomeKnown(false);
      setCatalogProposalError(null);
      onToast('Content Central setup connected');
      return true;
    } catch (reason) {
      let recoveredCommittedApply = false;
      try {
        const pending = await api.getPendingContentCentralProposal(job.id);
        if (!liveCard.current || revision !== exportDraftRevision.current) return false;
        if (pending) {
          hydratePendingCatalogProposal(pending);
        } else {
          // Apply may commit its binding/settings and remove the durable pending
          // row before a response or IPC failure reaches this renderer. Reload
          // durable state, but announce success only when the engine proves that
          // this exact plan/key produced the configuration which is still active.
          const [committedSettings, committedBinding, commitProof] = await Promise.all([
            api.getExportSettings(job.id),
            api.getJobDestinationBinding(job.id),
            api.verifyContentCentralCatalogApply(
              job.id,
              connectionId,
              catalogProvisioningPlan.planId,
              catalogProvisioningPlan.planFingerprint,
            ),
          ]);
          if (!liveCard.current || revision !== exportDraftRevision.current) return false;
          const committedContentCentral = Boolean(
            commitProof &&
            committedSettings &&
            committedBinding &&
            commitProof.jobId === job.id &&
            commitProof.connectionId === connectionId &&
            commitProof.planId.toLowerCase() === catalogProvisioningPlan.planId.toLowerCase() &&
            commitProof.planFingerprint.toLowerCase() ===
              catalogProvisioningPlan.planFingerprint.toLowerCase() &&
            commitProof.applyIdempotencyKey === `apply-${catalogProvisioningPlan.planId}` &&
            committedSettings.destinationKind === 'content-central' &&
            committedBinding.destinationKind === 'content-central' &&
            committedSettings.destinationBindingId === committedBinding.bindingId &&
            committedBinding.jobId === job.id &&
            committedBinding.connectionId === connectionId &&
            committedBinding.status === 'connected' &&
            committedBinding.bindingId === commitProof.bindingId &&
            committedBinding.bindingRevision === commitProof.bindingRevision &&
            committedBinding.remoteCatalogId?.toLowerCase() === commitProof.catalogId.toLowerCase(),
          );
          // A committed Apply recovered after a lost response keeps the
          // operator's When, Files and Backup copy exactly like a direct one.
          hydrateDurableExportSetup(
            committedSettings,
            committedBinding,
            connections,
            committedContentCentral,
          );
          recoveredCommittedApply = committedContentCentral;
          // Like the direct path, a recovered commit tells the screens that read
          // export destinations (the connections alert) that this job's changed.
          if (recoveredCommittedApply) {
            markExportDraftChanged();
            announceExportDestinationChanged();
          }
          // The proposal is gone and this Apply did not commit it: nothing is
          // left for its window to show, and it must not look like success.
          else setCcWindow(null);
        }
      } catch {
        /* Preserve the Apply error; a later open resumes the durable operation. */
      }
      if (liveCard.current && (recoveredCommittedApply || revision === exportDraftRevision.current))
        onToast(
          recoveredCommittedApply
            ? 'Content Central setup connected'
            : messageFor(reason, 'Content Central could not finish the catalog setup'),
          recoveredCommittedApply ? undefined : 'catalog-setup-failed',
        );
      return recoveredCommittedApply;
    } finally {
      catalogDecisionInFlight.current = false;
      if (liveCard.current) setBusy(null);
    }
    return false;
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const discardCatalogProposal = async (): Promise<boolean> => {
    if (!api || !catalogProposalDecision || busy !== null || catalogDecisionInFlight.current)
      return false;
    if (
      catalogProposalApplyState === 'reconciliation-needed' &&
      !(await confirmAction({
        title: 'Leave this completed Content Central setup unconnected?',
        message:
          'Content Central will keep the remote setup, and Paige will retain its audit, but this job will not use that setup.',
        confirmLabel: 'Leave unconnected',
        destructive: true,
      }))
    )
      return false;
    // The question waited on the operator; only a still-open, still-idle card goes on.
    if (!liveCard.current || catalogDecisionInFlight.current) return false;
    catalogDecisionInFlight.current = true;
    const revision = exportDraftRevision.current;
    const dismissedApplyState = catalogProposalApplyState;
    setBusy('discarding-catalog');
    try {
      await api.discardPendingContentCentralProposal(catalogProposalDecision);
      const durableBinding = await api.getJobDestinationBinding(job.id);
      if (!liveCard.current || revision !== exportDraftRevision.current) return false;
      markExportDraftChanged();
      restoredCatalogProposal.current = false;
      setDestinationBinding(durableBinding);
      // "Keep current setup" means the last saved export setup, including its
      // destination kind. A durable proposal temporarily reopens Content Central
      // so it can be decided after restart; dismissing it must undo that temporary
      // presentation state rather than leave a saved folder looking switched.
      setDestinationKind(
        settings?.destinationKind ?? durableBinding?.destinationKind ?? 'filesystem',
      );
      const durableConnectionId = durableBinding?.connectionId ?? '';
      const durableCatalogId = durableBinding?.remoteCatalogId ?? '';
      const restoresSelectedCatalog = Boolean(
        durableBinding &&
        catalogDefinition &&
        durableConnectionId === connectionId &&
        durableCatalogId === catalogDefinition.catalog.id,
      );
      if (durableBinding && durableConnectionId && durableCatalogId) {
        const restoredProposal: ContentCentralBindingProposal = {
          binding: durableBinding,
          blockers: [],
        };
        setConnectionId(durableConnectionId);
        setCatalogQuery('');
        setCatalogId(durableCatalogId);
        if (!restoresSelectedCatalog) setCatalogDefinition(null);
        setCatalogBindingProposal(restoredProposal);
        setMappingChoices(mappingChoicesFromProposal(restoredProposal, job));
      } else {
        setCatalogId('');
        setCatalogDefinition(null);
        setCatalogBindingProposal(null);
        setMappingChoices([]);
      }
      setMappingDirty(false);
      setCatalogProvisioningPlan(null);
      setCatalogProposalDecision(null);
      setCatalogProposalApplyState(null);
      setCatalogProposalOutcomeKnown(false);
      setCatalogProposalError(null);
      onToast(
        dismissedApplyState === 'reconciliation-needed'
          ? 'Completed remote setup left unconnected; the audit was retained'
          : dismissedApplyState === 'needs-attention'
            ? 'Failed setup dismissed; the audit was retained'
            : 'Catalog proposal dismissed; Content Central was not changed',
        'notice',
      );
      return true;
    } catch (reason) {
      let recoveryToast: string | null = null;
      try {
        const pending = await api.getPendingContentCentralProposal(job.id);
        if (!liveCard.current || revision !== exportDraftRevision.current) return false;
        if (pending) {
          const currentDecision = decisionFromPendingProposal(pending);
          const proposalChanged = !sameProposalDecision(catalogProposalDecision, currentDecision);
          if (proposalChanged) {
            const refreshedConnections = await api.listDestinationConnections();
            if (!liveCard.current || revision !== exportDraftRevision.current) return false;
            setConnections(refreshedConnections);
            recoveryToast =
              'The proposal changed elsewhere. Review the current Content Central proposal.';
          }
          hydratePendingCatalogProposal(pending);
          if (proposalChanged) markExportDraftChanged();
        } else {
          const [durableSettings, durableBinding, refreshedConnections] = await Promise.all([
            api.getExportSettings(job.id),
            api.getJobDestinationBinding(job.id),
            api.listDestinationConnections(),
          ]);
          if (!liveCard.current || revision !== exportDraftRevision.current) return false;
          setConnections(refreshedConnections);
          hydrateDurableExportSetup(durableSettings, durableBinding, refreshedConnections);
          markExportDraftChanged();
          setCcWindow(null);
          recoveryToast =
            'This proposal was already resolved. The current export setup was reloaded.';
        }
      } catch {
        /* The original stale-decision error is the actionable one. */
      }
      if (liveCard.current && (recoveryToast || revision === exportDraftRevision.current))
        onToast(
          recoveryToast ?? messageFor(reason, 'The catalog proposal could not be dismissed'),
          recoveryToast ? undefined : 'catalog-setup-failed',
        );
    } finally {
      catalogDecisionInFlight.current = false;
      if (liveCard.current) setBusy(null);
    }
    return false;
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  /** Saves what is shown. `turnOn` true is Turn on before anything is saved; otherwise see `exportEnabledAfterSave`. */
  const save = async (destinationOverride?: ExportDestinationSelection, turnOn?: boolean) => {
    if (!api || !plan) return;
    saveTurnsOn.current = turnOn;
    const enabled = exportEnabledAfterSave({
      wasOn: Boolean(settings?.enabled),
      firstDestination: firstDestinationSave.current,
      turnOn,
    });
    const turningOn = enabled && !settings?.enabled;
    if (destinationKind === 'filesystem' && previewState !== 'ready') {
      onToast(
        previewState === 'error'
          ? 'Retry the filing examples before saving'
          : 'Wait for the current filing examples before saving',
        'export-save-refused',
      );
      return;
    }
    if (catalogOperationLocked) {
      onToast(
        'Finish checking the accepted Content Central setup before changing export',
        'export-save-refused',
      );
      return;
    }
    if (catalogProvisioningPlan) {
      onToast('Use or dismiss the catalog proposal before saving export', 'export-save-refused');
      return;
    }
    if (!beginExportMutation()) return;
    const revision = exportDraftRevision.current;
    setBusy('saving');
    setSaveProblem(null);
    // The checks here are already plain sentences; what the engine answers may not be.
    let engineAnswered = false;
    try {
      if (filesystemDestinationMissing) throw new Error('Choose the export folder before saving.');
      if (destinationKind === 'content-central') {
        if (!catalogDefinition || !catalogBindingProposal)
          throw new Error('Wait for the Content Central catalog check to finish.');
        if (catalogBindingProposal.blockers.length > 0)
          throw new Error(catalogBindingProposal.blockers[0]);
        if (
          !destinationBinding ||
          destinationBinding.connectionId !== connectionId ||
          !sameContentCentralId(destinationBinding.remoteCatalogId, catalogDefinition.catalog.id)
        )
          throw new Error('Review and accept the Content Central mapping before saving export.');
      }
      engineAnswered = true;
      const saved = await api.saveExportSettings({
        jobId: job.id,
        destinationToken:
          destinationKind === 'filesystem'
            ? (destinationOverride ?? destination)?.token
            : undefined,
        exceptionsToken: exceptionsFolder?.token,
        triggerMode,
        pdfProfile,
        files,
        retentionDays,
        filingPlan: plan,
        scheduleLocalTime: triggerMode === 'scheduled' ? scheduleLocalTime : null,
        scheduleTimeZone: triggerMode === 'scheduled' ? currentScheduleTimeZone : null,
        enabled,
        destinationKind,
        destinationBindingId:
          destinationKind === 'content-central' ? destinationBinding?.bindingId : null,
      });
      announceExportDestinationChanged();
      if (!liveCard.current) return;
      // Saved: the card shows it at once. Re-reading the Content Central binding afterwards is
      // separate, so a failed read can't report this save as failed.
      setSettings(saved);
      firstDestinationSave.current = false;
      if (revision === exportDraftRevision.current) {
        setDestination(null);
        setExceptionsFolder(null);
      }
      if (destinationKind === 'content-central') {
        try {
          const committedBinding = await api.getJobDestinationBinding(job.id);
          if (liveCard.current && revision === exportDraftRevision.current)
            setDestinationBinding(committedBinding);
        } catch {
          /* The binding shown is the one this save used; it is read again when Export next opens. */
        }
        if (!liveCard.current) return true;
      }
      onToast('Export setup saved');
      return true;
    } catch (reason) {
      if (!liveCard.current) return;
      const said = reason instanceof Error ? reason.message : String(reason);
      // A naming the engine refuses is fixed in the naming window; say so and offer it. A refusal is a naming refusal
      // only when its sentence holds the naming's own errors (the engine refuses a naming with them joined): a folder
      // refused, or a folder that couldn't be made, on a draft whose naming also has errors is said in its own words.
      const namingErrors = preview?.diagnostics.filter((item) => item.severity === 'error') ?? [];
      const refusedFor = splitTechnicalDetails(said).message;
      const naming =
        engineAnswered &&
        destinationKind === 'filesystem' &&
        namingErrors.length > 0 &&
        namingErrors.some((item) => refusedFor.includes(item.message));
      const unsaved = turningOn ? 'Export wasn’t turned on' : 'Export changes weren’t saved';
      // Telemetry: the reason as code words (the filing check's codes, never its words).
      const failure = exportSaveFailure({
        step: 'save',
        wasOn: !turningOn,
        namingErrorCodes: naming ? namingErrors.map((item) => item.code) : [],
      });
      reportExportSaveFailed(failure, destinationKind, job.id);
      // A sentence that already carries its details is shown as it is: an engine refusal of these settings (main
      // marks it, so "Choose a folder outside it." is read, not hidden) or a Content Central failure. A naming
      // refusal is still said in the naming's own words, its engine sentence behind Technical details.
      setSaveProblem({
        naming,
        code: failure.code,
        message: !engineAnswered
          ? said
          : naming
            ? `${unsaved}: the file names can’t be made from this naming yet.${TECHNICAL_DETAILS_MARKER}${splitTechnicalDetails(said).message}`
            : said.includes(TECHNICAL_DETAILS_MARKER)
              ? said
              : `${unsaved}.${TECHNICAL_DETAILS_MARKER}${said}`,
      });
    } finally {
      endExportMutation();
      if (liveCard.current) setBusy(null);
    }
  };
  /**
   * Re-reads the job's documents after an export action. The action's own outcome stands
   * whatever this does: a failed reread (such as "documents changed while opening") only
   * leaves the list as shown until the workspace's own check reads it again, and must not
   * hide the outcome or report the action as failed.
   */
  const refreshWorkspaceAfterExport = async () => {
    try {
      await onWorkspaceRefresh?.();
    } catch {
      /* The outcome is said below; the documents catch up on the next check. */
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const run = async () => {
    if (!api) return;
    if (catalogOperationLocked) {
      onToast(
        'Finish checking the accepted Content Central setup before exporting',
        'export-run-refused',
      );
      return;
    }
    if (catalogProvisioningPlan) {
      onToast('Use or dismiss the catalog proposal before exporting', 'export-run-refused');
      return;
    }
    if (exportSettingsDirty) {
      onToast('Save the export changes before exporting documents.', 'export-run-refused');
      return;
    }
    if (contentCentralPaused) {
      onToast(
        'Export is paused until the Content Central connection and catalog are ready.',
        'export-run-refused',
      );
      return;
    }
    if (!beginExportMutation()) return;
    setBusy('running');
    try {
      const result = await api.runExport(job.id);
      if (!liveCard.current) return;
      setRunResult(result);
      setExportResultOpen(true);
      readyCount.recount();
      await loadHistory();
      if (!liveCard.current) return;
      await refreshWorkspaceAfterExport();
      if (liveCard.current) onToast(exportRunNotice(result), exportRunNoticeCode(result));
    } catch (reason) {
      if (liveCard.current) onToast(messageFor(reason, 'Export could not finish'), 'export-failed');
    } finally {
      // Preflight records a newly detected catalog problem before rejecting the
      // run. Reload on failure as well as success so the screen cannot keep
      // saying Ready after the engine has paused this binding.
      if (destinationKind === 'content-central') {
        try {
          const refreshed = await api.getJobDestinationBinding(job.id);
          if (liveCard.current) setDestinationBinding(refreshed);
        } catch {
          /* Preserve the run's result; reopening Export retries this small read. */
        }
      }
      endExportMutation();
      if (liveCard.current) setBusy(null);
    }
  };
  // The filters the listed results answer. What is typed or picked in the filter boxes is a
  // draft until Find or Enter (or reopening Activity) sends it, so the 5-second refresh, a
  // finished action and the pager keep asking the question on show: sending a half-typed
  // draft at the old offset used to say "No exported documents match these filters." with
  // the pager gone.
  const historySent = useRef({ query: '', type: '', state: '', date: '' });
  const historyListRequest = (offset: number, filtered: boolean): ExportDocumentListRequest => {
    const sent = historySent.current;
    const dateRange = filtered && sent.date ? exportHistoryDateRange(sent.date) : null;
    return {
      jobId: job.id,
      offset: filtered ? offset : 0,
      query: filtered ? sent.query || undefined : undefined,
      documentType: filtered ? sent.type || undefined : undefined,
      state: filtered ? sent.state || undefined : undefined,
      exportedFromUtc: dateRange?.from,
      exportedToUtc: dateRange?.to,
    };
  };
  const loadHistory = async (offset?: number, filtered = historyView.current.showHistory) => {
    if (!api) return;
    await requestHistoryPage(
      historyListRequest(offset ?? historyView.current.historyOffset, filtered),
    );
  };
  /** Find or Enter: the drafts become the question, from the first page. */
  const findHistory = (filtered = historyView.current.showHistory) => {
    const { historyQuery, historyType, historyState, historyDate } = historyView.current;
    historySent.current = {
      query: historyQuery,
      type: historyType,
      state: historyState,
      date: historyDate,
    };
    void loadHistory(0, filtered);
  };
  const openStop = (next: ExportStop) => {
    // Leaving Activity retires its filtered results but keeps the filter drafts.
    if (next !== 'activity' && showHistory) {
      setShowHistory(false);
      void requestHistoryPage({ jobId: job.id });
    }
    if (next === 'activity' && !showHistory) {
      setShowHistory(true);
      findHistory(true);
    }
    setStop(next);
  };
  // A request from elsewhere in the app (Needs review's next step) opens its stop.
  useEffect(() => {
    if (stopRequest) openStop(stopRequest.stop);
  }, [stopRequest?.request]);
  const openConnectionsWindow = () => {
    addedConnection.current = null;
    setCcWindow('connections');
  };
  const closeConnectionsWindow = () => {
    setCcWindow(null);
    const added = addedConnection.current;
    addedConnection.current = null;
    if (!api) return;
    void api
      .listDestinationConnections()
      .then((items) => {
        if (!liveCard.current) return;
        setConnections(items);
        // The connection the operator just made is the one they went there for.
        if (
          added &&
          !proposalDecisionLocked &&
          items.some((item) => item.connectionId === added.connectionId)
        ) {
          setCcChoosing(null);
          setCcJustAdded(true);
          chooseContentCentralConnection(added.connectionId);
        }
      })
      .catch(() => undefined);
  };
  const startCreateCatalog = async () => {
    setCreateCompleted(null);
    if ((await proposeCatalog()) && liveCard.current) setCcWindow('create');
  };
  // "N exported today" on the activity pill.
  useEffect(() => {
    if (!api) return;
    let current = true;
    const read = () => {
      const today = new Date();
      const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const range = exportHistoryDateRange(day);
      void api
        .listExportedDocuments({
          jobId: job.id,
          state: 'exported',
          exportedFromUtc: range?.from,
          exportedToUtc: range?.to,
        })
        .then((page) => {
          if (current) setExportedToday(page.total);
        })
        .catch(() => {
          if (current) setExportedToday(null);
        });
    };
    read();
    const timer = window.setInterval(read, 15_000);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [api, job.id, historyTotal]);
  useEffect(() => {
    if (!api) return;
    let live = true;
    const refresh = async () => {
      if (!live || historyLoading || historyLoadingNow.current || historyPollInFlight.current)
        return;
      historyPollInFlight.current = true;
      const requestId = ++historyRequest.current;
      try {
        const request = historyListRequest(historyOffset, showHistory);
        const result = await api.listExportedDocuments(request);
        if (!live || requestId !== historyRequest.current) return;
        applyHistoryPage(result, request);
        setHistoryError('');
      } catch (reason) {
        if (!live || requestId !== historyRequest.current) return;
        clearHistoryPage(showHistory ? historyOffset : 0);
        setHistoryError(messageFor(reason, 'Export activity could not be loaded'));
      } finally {
        historyPollInFlight.current = false;
      }
    };
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [api, applyHistoryPage, clearHistoryPage, historyLoading, historyOffset, job.id, showHistory]);
  useEffect(() => {
    if (!api || destinationKind !== 'content-central') return;
    let live = true;
    // Continuous export can discover catalog drift in the background. Poll only
    // the local durable binding—not Content Central—so an open Export screen
    // replaces a stale Ready state without bringing back idle network traffic.
    const refreshBinding = async () => {
      try {
        const refreshed = await api.getJobDestinationBinding(job.id);
        if (!live) return;
        setDestinationBinding((current) =>
          current?.bindingId === refreshed?.bindingId &&
          current?.bindingRevision === refreshed?.bindingRevision &&
          current?.status === refreshed?.status &&
          current?.connectionId === refreshed?.connectionId &&
          sameContentCentralId(current?.remoteCatalogId, refreshed?.remoteCatalogId) &&
          current?.remoteDeliverySchemaRevision === refreshed?.remoteDeliverySchemaRevision &&
          current?.remoteFilingRevisionObserved === refreshed?.remoteFilingRevisionObserved
            ? current
            : refreshed,
        );
      } catch {
        /* The authoritative catalog check and manual run surface actionable failures. */
      }
    };
    const timer = window.setInterval(() => void refreshBinding(), 5_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [api, destinationKind, job.id]);
  const openExportedDocument = async (
    document: ExportDocumentSummary,
    target: 'pdf' | 'folder',
  ) => {
    if (!api) return;
    try {
      await api.openExportedDocument(document.exportId, target);
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(
            reason,
            `The exported ${target === 'pdf' ? 'PDF' : 'folder'} could not be opened`,
          ),
          'export-folder-open-failed',
        );
    }
  };
  const beginHistoryAction = (
    document: ExportDocumentSummary,
    kind: 'retry' | 'cancel' | 'return',
  ) => {
    if (busy !== null || historyActionInFlight.current) return null;
    const key = `${kind}:${document.exportId}`;
    historyActionInFlight.current = key;
    setHistoryAction({ exportId: document.exportId, kind });
    setBusy('history-action');
    return key;
  };
  const finishHistoryAction = (key: string) => {
    if (historyActionInFlight.current !== key) return;
    historyActionInFlight.current = null;
    if (!liveCard.current) return;
    setHistoryAction(null);
    setBusy(null);
  };
  const returnExportToWork = async (document: ExportDocumentSummary) => {
    if (!api || busy !== null || historyActionInFlight.current) return;
    if (
      !(await confirmAction({
        title: `Return ${document.realizedFilename || document.resolvedBaseName} to the active batch?`,
        message: 'Content Central proved that this export did not create a document.',
        confirmLabel: 'Return to batch',
      })) ||
      !liveCard.current ||
      historyActionInFlight.current
    )
      return;
    const action = beginHistoryAction(document, 'return');
    if (!action) return;
    try {
      if (!(await api.returnExportToWork(document.exportId)))
        throw new Error('This document cannot safely return to work.');
      if (!liveCard.current) return;
      await loadHistory();
      if (!liveCard.current) return;
      await refreshWorkspaceAfterExport();
      if (liveCard.current) onToast('Document returned to the active batch');
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(reason, 'The document could not return to work'),
          'export-recovery-failed',
        );
    } finally {
      finishHistoryAction(action);
    }
  };
  const cancelContentCentralExport = async (document: ExportDocumentSummary) => {
    if (!api || busy !== null || historyActionInFlight.current) return;
    if (
      !(await confirmAction({
        title: `Cancel delivery of ${document.realizedFilename || document.resolvedBaseName}?`,
        message:
          'This is available only while Content Central still has an exact uncommitted operation.',
        confirmLabel: 'Cancel delivery',
        cancelLabel: 'Keep delivering',
        destructive: true,
      })) ||
      !liveCard.current ||
      historyActionInFlight.current
    )
      return;
    const action = beginHistoryAction(document, 'cancel');
    if (!action) return;
    try {
      const canceled = await api.cancelContentCentralExport(document.exportId);
      if (!liveCard.current) return;
      await loadHistory();
      if (!liveCard.current) return;
      await refreshWorkspaceAfterExport();
      if (liveCard.current)
        onToast(
          canceled
            ? 'Content Central confirmed the uncommitted delivery was canceled'
            : 'Content Central completed the delivery before it could be canceled',
        );
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(reason, 'The delivery could not be canceled safely'),
          'export-recovery-failed',
        );
    } finally {
      finishHistoryAction(action);
    }
  };
  const retryExportedDocument = async (
    document: ExportDocumentSummary,
    recoveryAction: ExportRetryAction,
  ) => {
    if (!api || busy !== null || historyActionInFlight.current) return;
    const recoveryOnly = recoveryAction === 'complete-local-recovery';
    const reconcilingCancellation = recoveryAction === 'reconcile-cancellation';
    const contentCentral = document.destinationKind === 'content-central';
    const action = beginHistoryAction(document, 'retry');
    if (!action) return;
    try {
      const result = await api.retryExport(document.exportId);
      if (!liveCard.current) return;
      await loadHistory();
      if (!liveCard.current) return;
      await refreshWorkspaceAfterExport();
      if (liveCard.current)
        onToast(
          reconcilingCancellation
            ? result.remoteEffectState === 'verified'
              ? 'Content Central completed the delivery before it could be canceled'
              : result.remoteEffectState === 'canceled'
                ? 'Content Central confirmed the uncommitted delivery was canceled'
                : 'Content Central is still checking the cancellation'
            : recoveryOnly
              ? result.localRecoveryState === 'verified'
                ? 'Backup copy completed'
                : 'The backup copy still needs attention'
              : result.state === 'exported'
                ? contentCentral
                  ? 'Document delivered to Content Central'
                  : 'Document exported'
                : contentCentral
                  ? 'Content Central is still checking this delivery'
                  : 'The export retry is still in progress',
          !reconcilingCancellation && recoveryOnly && result.localRecoveryState !== 'verified'
            ? 'export-recovery-failed'
            : undefined,
        );
    } catch (reason) {
      if (liveCard.current)
        onToast(
          messageFor(
            reason,
            reconcilingCancellation
              ? 'The cancellation could not be confirmed yet'
              : recoveryOnly
                ? 'The backup copy could not be retried'
                : 'The export could not be retried safely',
          ),
          'export-recovery-failed',
        );
    } finally {
      finishHistoryAction(action);
    }
  };
  const savedDestinationKind = settings?.destinationKind ?? 'filesystem';
  const shownDestination =
    destination?.displayPath ??
    (savedDestinationKind === 'filesystem' ? settings?.destinationRoot : null) ??
    'No folder chosen';
  // The engine's default backup copy folder is `backup-copies` in the data folder (an upgraded install's
  // `exported-documents` until the engine has renamed it), and saved export settings carry it
  // (DocumentStore.GetExportSettings fills it in). Before a job has settings, the main process says where it is
  // (getExportFolderDefaults), so the real path is shown, never a guess; until that answer arrives (or if it fails),
  // "In Paige’s storage folder", which is always true: backup copies always have a folder.
  // Free space is said only for the disk the app measures: the folder in use, not one chosen in Settings for the
  // next start, since exports land there until the restart (inUse*).
  const defaultBackupFolder = shownFolderDefaults?.backupCopiesRoot ?? null;
  const shownLocalStore =
    settings?.localStoreRoot ?? defaultBackupFolder ?? 'In Paige’s storage folder';
  const driveOf = (folder: string) => /^[a-z]:/iu.exec(folder)?.[0].toUpperCase() ?? null;
  const backupDrive = driveOf(settings?.localStoreRoot ?? defaultBackupFolder ?? '');
  // A description without inUse* figures (the fields are optional) still has the in-use disk's
  // in freeBytes when nothing is pending; while a change is pending it has none.
  const inUseFreeBytes =
    storageLocation?.inUseFreeBytes !== undefined
      ? storageLocation.inUseFreeBytes
      : storageLocation?.restartRequired
        ? null
        : (storageLocation?.freeBytes ?? null);
  const inUseTotalBytes =
    storageLocation?.inUseTotalBytes !== undefined
      ? storageLocation.inUseTotalBytes
      : storageLocation?.restartRequired
        ? null
        : (storageLocation?.totalBytes ?? null);
  const backupSpace =
    storageLocation &&
    backupDrive &&
    backupDrive === driveOf(storageLocation.inUsePath) &&
    spoken(inUseFreeBytes) &&
    spoken(inUseTotalBytes)
      ? { free: spoken(inUseFreeBytes)!, total: spoken(inUseTotalBytes)! }
      : null;
  const clearCatalogDetailState = () => {
    setCatalogId('');
    setCatalogDefinition(null);
    setCatalogBindingProposal(null);
    setMappingChoices([]);
    setMappingDirty(false);
    setCatalogDetailLoading(false);
    setCatalogDetailError('');
    setCatalogProvisioningPlan(null);
    setCatalogProposalDecision(null);
  };
  const chooseContentCentralConnection = (nextConnectionId: string) => {
    if (nextConnectionId === connectionId) return;
    markExportDraftChanged();
    setCcCreateError(null);
    catalogListRequest.current++;
    catalogMoreInFlight.current = false;
    checkedBoundCatalogs.current.clear();
    restoredCatalogProposal.current = false;
    setConnectionId(nextConnectionId);
    setCatalogQuery('');
    setCatalogs([]);
    setCatalogListError('');
    setCatalogListLoaded(false);
    setCatalogListLoading(Boolean(nextConnectionId));
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    clearCatalogDetailState();
  };
  const changeCatalogQuery = (nextQuery: string) => {
    markExportDraftChanged();
    catalogListRequest.current++;
    catalogMoreInFlight.current = false;
    checkedBoundCatalogs.current.clear();
    restoredCatalogProposal.current = false;
    setCatalogQuery(nextQuery);
    setCatalogs([]);
    setCatalogListError('');
    setCatalogListLoaded(false);
    setCatalogListLoading(Boolean(connectionId));
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    clearCatalogDetailState();
  };
  const chooseContentCentralCatalog = (nextCatalogId: string) => {
    markExportDraftChanged();
    setCcCreateError(null);
    setCcCheckError(null);
    checkedBoundCatalogs.current.clear();
    restoredCatalogProposal.current = false;
    setCatalogId(nextCatalogId);
    setCatalogDefinition(null);
    setCatalogBindingProposal(null);
    setMappingChoices([]);
    setMappingDirty(false);
    setCatalogDetailError('');
    setCatalogDetailLoading(Boolean(nextCatalogId));
    setCatalogProvisioningPlan(null);
    setCatalogProposalDecision(null);
  };
  const selectedConnection = connections.find((item) => item.connectionId === connectionId) ?? null;
  const selectedCatalogAlreadyListed = catalogs.some((item) =>
    sameContentCentralId(item.id, catalogId),
  );
  const selectedCatalogChange = catalogProvisioningPlan?.changes.find(
    (change) => change.objectKind === 'catalog' && sameContentCentralId(change.remoteId, catalogId),
  );
  const selectedCatalogFromDefinition =
    catalogDefinition && sameContentCentralId(catalogDefinition.catalog.id, catalogId)
      ? catalogDefinition.catalog
      : null;
  const selectedCatalogFromBinding = sameContentCentralId(
    destinationBinding?.remoteCatalogId,
    catalogId,
  )
    ? destinationBinding
    : null;
  const catalogOptions: ContentCentralCatalogSummary[] =
    catalogId && !selectedCatalogAlreadyListed
      ? [
          selectedCatalogFromDefinition ?? {
            id: catalogId,
            name:
              selectedCatalogChange?.displayName ??
              selectedCatalogFromBinding?.remoteCatalogName ??
              'Selected catalog',
            documentTypeCount: selectedCatalogFromBinding?.documentTypes.length ?? 0,
            canAddDocuments: true,
          },
          ...catalogs,
        ]
      : catalogs;
  const selectedCatalogOption = catalogOptions.find((item) =>
    sameContentCentralId(item.id, catalogId),
  );
  const mappingReviewing = busy === 'mapping-catalog';
  // A reviewed durable proposal owns the catalog context until the operator
  // chooses Use or Keep. Hiding it behind another connection, catalog, search,
  // or destination used to strand an engine-side proposal with no visible way
  // to resolve it.
  const proposalDecisionLocked = catalogOperationLocked || Boolean(catalogProvisioningPlan);
  const catalogControlsLocked =
    proposalDecisionLocked || mappingReviewing || busy !== null || folderPickerBusy !== null;
  const contentCentralConnectionMissing =
    destinationKind === 'content-central' &&
    (!connectionId ||
      !selectedConnection ||
      !destinationBinding ||
      destinationBinding.connectionId !== connectionId);
  const contentCentralConnectionFailed =
    destinationKind === 'content-central' && selectedConnection?.lastFailureCode != null;
  // A binding whose preflight held only some types still delivers the rest;
  // only a whole-binding problem pauses export.
  const contentCentralMappingNeedsAttention =
    destinationKind === 'content-central' &&
    (destinationBinding?.status === 'disconnected' ||
      (destinationBinding?.status === 'needs-attention' &&
        !bindingDeliversSomeTypes(destinationBinding)));
  const contentCentralCatalogChecking =
    destinationKind === 'content-central' && Boolean(catalogId) && catalogDetailLoading;
  const contentCentralCatalogUnavailable =
    destinationKind === 'content-central' && Boolean(catalogId) && Boolean(catalogDetailError);
  const contentCentralNeedsAttention =
    contentCentralConnectionMissing ||
    contentCentralConnectionFailed ||
    contentCentralMappingNeedsAttention ||
    contentCentralCatalogUnavailable;
  // Loading the saved catalog is a safe, temporary pause—not a broken setup.
  // Keep export actions gated until validation finishes without flashing a false
  // amber warning every time the operator returns to this page.
  const contentCentralPaused = contentCentralNeedsAttention || contentCentralCatalogChecking;
  const unappliedCatalogProposal = Boolean(catalogProvisioningPlan);
  const displayCatalogProposal =
    selectedConnection && catalogDefinition ? catalogBindingProposal : null;
  const catalogProposal = !mappingDirty ? displayCatalogProposal : null;
  const currentScheduleTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const contentCentralDestinationChanged =
    destinationKind === 'content-central' &&
    (mappingDirty ||
      unappliedCatalogProposal ||
      destinationBinding?.connectionId !== connectionId ||
      !sameContentCentralId(destinationBinding?.remoteCatalogId, catalogId));
  // On or off is not a choice in the draft: the switch saves itself (owner decision, 2026-10-26), so choices
  // Job setup saved with export off are saved, not unsaved changes.
  const exportSettingsDirty = plan
    ? exportSettingsNeedSave(settings && { ...settings, enabled: true }, {
        destinationKind,
        destinationBindingId: destinationBinding?.bindingId,
        destinationChanged:
          destinationKind === 'filesystem'
            ? Boolean(destination)
            : contentCentralDestinationChanged,
        exceptionsChanged: Boolean(exceptionsFolder),
        triggerMode,
        pdfProfile,
        files,
        retentionDays,
        filingPlan: plan,
        scheduleLocalTime,
        scheduleTimeZone: currentScheduleTimeZone,
      })
    : true;
  const filesystemDestinationMissing =
    destinationKind === 'filesystem' &&
    !destination &&
    (!settings || savedDestinationKind !== 'filesystem' || !settings.destinationRoot);
  const destinationNeedsAttention =
    filesystemDestinationMissing || contentCentralNeedsAttention || unappliedCatalogProposal;
  const destinationChecking = !destinationNeedsAttention && contentCentralCatalogChecking;
  const heldTypeBindings =
    destinationKind === 'content-central' &&
    destinationBinding?.destinationKind === 'content-central'
      ? destinationBinding.documentTypes.filter((type) => type.holdReason)
      : [];
  const holdReasons: Record<string, string> = Object.fromEntries(
    heldTypeBindings.map((type) => [type.localDocumentTypeId, type.holdReason ?? '']),
  );
  // A type added after the matching has no Content Central type. Its documents
  // are not failed or skipped: they wait here, counted, until it is matched.
  const unmatchedTypes =
    destinationKind === 'content-central' &&
    destinationBinding?.destinationKind === 'content-central'
      ? job.documentTypes
          .filter(
            (type) =>
              type.status !== 'archived' &&
              !destinationBinding.documentTypes.some(
                (bound) => bound.localDocumentTypeId === type.id,
              ),
          )
          .map((type) => ({
            name: type.name,
            plural: pluralTypeName(type.name),
            count: readyCount.heldByType?.[type.id] ?? null,
          }))
      : [];
  const exportWaitingNote =
    triggerMode === 'continuous'
      ? 'Documents wait here and nothing is lost. Export picks up on its own once Content Central answers.'
      : triggerMode === 'scheduled'
        ? 'Documents wait here and nothing is lost. They leave at the next scheduled time after Content Central answers.'
        : 'Documents wait here and nothing is lost. Export now works again once Content Central answers.';
  const failureTitle = (category?: string | null) =>
    category === 'unreachable'
      ? 'can’t reach Content Central.'
      : category === 'certificate'
        ? 'this computer doesn’t trust Content Central’s certificate.'
        : category === 'key-refused'
          ? 'Content Central didn’t accept the connection key.'
          : category === 'timeout'
            ? 'Content Central didn’t answer in time.'
            : 'Content Central needs attention.';
  // The screen says exactly what the engine does: one changed type waits while
  // the rest keep exporting; a connection or whole-catalog problem stops all.
  const contentCentralWaiting: ContentCentralWaiting | null =
    destinationKind !== 'content-central' ||
    destinationBinding?.destinationKind !== 'content-central'
      ? null
      : selectedConnection?.lastFailureCode
        ? {
            kind: 'paused',
            title: `Export is paused: ${failureTitle(selectedConnection.lastFailureCategory)}`,
            detail: selectedConnection.lastFailureDetail ?? null,
            action: 'connections',
            note: exportWaitingNote,
          }
        : destinationBinding.status === 'disconnected'
          ? {
              kind: 'paused',
              title: 'Export is paused: this job’s Content Central connection was removed.',
              action: 'connection-choice',
              note: 'Choose a connection, then match the catalog again.',
            }
          : destinationBinding.status === 'needs-attention' &&
              heldTypeBindings.length > 0 &&
              heldTypeBindings.length < destinationBinding.documentTypes.length
            ? {
                kind: 'types',
                types: heldTypeBindings.map((held) => {
                  const name =
                    job.documentTypes.find((type) => type.id === held.localDocumentTypeId)?.name ??
                    held.remoteName;
                  return {
                    name,
                    plural: pluralTypeName(name),
                    remoteName: held.remoteName,
                    count: null,
                    reason: held.holdReason ?? '',
                  };
                }),
              }
            : destinationBinding.status === 'needs-attention' && heldTypeBindings.length > 0
              ? {
                  kind: 'paused',
                  title: `Export is paused: ${heldTypeBindings[0].holdReason}`,
                  action: 'matching',
                  note: 'Every document type waits until the matching is reviewed.',
                }
              : destinationBinding.status === 'needs-attention'
                ? {
                    kind: 'paused',
                    title: 'Export is paused: Content Central needs attention for this catalog.',
                    action: 'matching',
                    note: 'Check the connection in Connections, then review the matching.',
                  }
                : null;
  const exportSaveBlocker =
    busy !== null
      ? ''
      : // A disabled save says why in a few words; an enabled one needs no hover.
        catalogOperationLocked
        ? 'Wait a moment'
        : filesystemDestinationMissing
          ? 'Choose a folder first'
          : destinationKind === 'content-central' &&
              (contentCentralPaused ||
                unappliedCatalogProposal ||
                !catalogProposal ||
                catalogProposal.blockers.length > 0)
            ? 'Finish Content Central first'
            : destinationKind === 'filesystem' && previewState === 'error'
              ? 'Retry the examples first'
              : destinationKind === 'filesystem' && previewState !== 'ready'
                ? 'Still checking…'
                : '';
  // Read again with each Activity load (its 5-second refresh included), only while Activity is open. Only automatic
  // deletes are asked for, so newer ones made by hand never crowd them out; the filter stays for an engine that
  // ignores `deletedBy`.
  const readAutomaticDeletes = useCallback(async () => {
    if (!api?.listDeletedDocuments) return;
    const list = await api.listDeletedDocuments({
      jobId: job.id,
      offset: 0,
      limit: 40,
      deletedBy: 'learned-junk',
    });
    if (liveCard.current)
      setAutomaticDeletes(list.documents.filter((document) => document.automatic));
  }, [api, job.id]);
  useEffect(() => {
    if (stop !== 'activity') return;
    void readAutomaticDeletes().catch(() => undefined); // Activity still lists its exports; the next refresh tries again.
  }, [stop, history, readAutomaticDeletes]);
  const restoreAutomaticDelete = async (document: DeletedDocument) => {
    if (!api?.restoreDocuments || restoringDeleted) return;
    setRestoringDeleted(document.documentId);
    try {
      const result = await api.restoreDocuments({
        jobId: job.id,
        batchId: document.batchId,
        documentIds: [document.documentId],
      });
      if (!liveCard.current) return;
      onToast(
        result.done.includes(document.documentId)
          ? `Restored ${document.sourceName}`
          : 'That document was already restored.',
      );
      await readAutomaticDeletes().catch(() => undefined);
      await refreshWorkspaceAfterExport();
    } catch (reason) {
      if (liveCard.current)
        onToast(
          reason instanceof Error ? reason.message : 'The document could not be restored.',
          'document-restore-failed',
        );
    } finally {
      if (liveCard.current) setRestoringDeleted(null);
    }
  };
  /**
   * Delete on an exceptions row whose reason is no type or not a document, on either mode: the person's answer that
   * it is not a document, which teaches the job its page shape is junk (ADR-0096), so an Autopilot job, which has no
   * Delete elsewhere, can teach it too. Its PDF stays in the exceptions folder; the row then says Deleted.
   */
  const deleteException = async (document: ExportDocumentSummary) => {
    if (!api?.deleteDocuments || deletingException || busy !== null) return;
    setDeletingException(document.exportId);
    try {
      const result = await api.deleteDocuments({
        jobId: document.jobId,
        batchId: document.batchId,
        documentIds: [document.documentId],
      });
      if (!liveCard.current) return;
      const refusal = result.refused.find(
        (item) => item.documentId === document.documentId,
      )?.reason;
      if (result.done.includes(document.documentId) || refusal === 'deleted') {
        setDeletedExceptions((current) => new Set(current).add(document.exportId));
        if (!refusal) onToast(deletedNotice(null), 'notice');
      } else onToast('This document could not be deleted.', 'document-delete-failed');
    } catch (reason) {
      if (liveCard.current)
        onToast(
          reason instanceof Error ? reason.message : 'The document could not be deleted.',
          'document-delete-failed',
        );
    } finally {
      if (liveCard.current) setDeletingException(null);
    }
  };
  const hasExportEdits = exportSettingsDirty && exportDraftTouched.current;
  useEffect(() => {
    onDirtyChange?.(hasExportEdits);
  }, [hasExportEdits, onDirtyChange]);
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );
  // Only a committed, mounted editor may own the parent's save/close guard.
  // Section navigation keeps this editor mounted; closing/discarding retires it.
  useLayoutEffect(() => {
    if (!draftControl) return;
    // Job setup's Save and its leave-setup prompt save what is shown: the save that gives the job
    // its first destination turns export on, a later one leaves the switch as it is (2026-10-26).
    const control = { dirty: hasExportEdits, save: async () => Boolean(await saveShown()) };
    draftControl.current = control;
    return () => {
      if (draftControl.current === control) draftControl.current = null;
    };
  });
  const exportSaveButton = (
    <TooltipButton
      className="primary-button small"
      tooltip={exportSaveBlocker}
      disabled={
        busy !== null ||
        !exportSettingsDirty ||
        catalogOperationLocked ||
        (destinationKind === 'content-central'
          ? contentCentralPaused ||
            unappliedCatalogProposal ||
            !catalogProposal ||
            catalogProposal.blockers.length > 0
          : filesystemDestinationMissing || previewState !== 'ready')
      }
      onClick={() => void saveShown()}
    >
      {busy === 'saving' ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />}{' '}
      {exportSettingsDirty
        ? previewState === 'loading' && destinationKind === 'filesystem'
          ? 'Checking filing…'
          : embedded
            ? 'Apply export changes'
            : 'Save export'
        : 'Saved'}
    </TooltipButton>
  );

  // Only Autopilot uses the exceptions folder (ADR-0096, decision 8), so only Autopilot shows it; a job set back to
  // Manual while the stop is open goes back to Destination.
  const exceptionsShown = pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed';
  useEffect(() => {
    if (stop === 'exceptions' && !exceptionsShown) setStop('destination');
  }, [stop, exceptionsShown]);
  // The main process fills in and the engine saves every job's folder (<Documents>\Paige Jobs\<job>\exceptions, or under the service %ProgramData%\Ademero\Paige Jobs\<job>\exceptions) when
  // its export settings are read; before a job has any, the folder it would get is shown, as the main process works
  // it out (getExportFolderDefaults, nothing made). Saved settings without one (the default could not be made, such as
  // the service's jobs folder missing), or no answer, are null and the panel asks for one; only
  // saved settings without one hold those documents in Paige (the panel's `held`): a job with nothing saved yet
  // gets its default at its first Save.
  const shownExceptionsFolder =
    exceptionsFolder?.displayPath ??
    (settings ? settings.exceptionsRoot || null : (shownFolderDefaults?.exceptionsRoot ?? null));
  // Open folder needs a saved folder that exists: one only chosen, or only the default to come, has nothing to open.
  const exceptionsFolderOpenable = Boolean(
    !exceptionsFolder &&
    settings?.exceptionsRoot &&
    window.capturePointDesktop?.openExceptionsFolder,
  );
  const openExceptionsFolder = async () => {
    try {
      await window.capturePointDesktop!.openExceptionsFolder!(job.id);
    } catch (reason) {
      if (liveCard.current)
        onToast(
          reason instanceof Error ? reason.message : 'The folder could not be opened',
          'export-folder-open-failed',
        );
    }
  };
  const hasFolder = Boolean(
    destination || (savedDestinationKind === 'filesystem' && settings?.destinationRoot),
  );
  const folderPath = hasFolder ? shownDestination : '';
  // A folder's own name, except the default's "exports", which names nothing alone: with its job's folder before it
  // ("Accounts Payable\exports"), as the Watched folder section names folders.
  const folderLastName =
    folderPath
      .replace(/[\\/]+$/, '')
      .split(/[\\/]/)
      .filter(Boolean)
      .at(-1) ?? folderPath;
  const folderName = !hasFolder
    ? 'No folder chosen'
    : folderLastName.toLocaleLowerCase() === 'exports'
      ? watchedFolderName(folderPath)
      : folderLastName;
  // One real example per document type, every type the preview has; in Job setup the list scrolls when they don't all fit.
  const folderExamples = (preview?.examples ?? []).filter(
    (example, index, all) =>
      all.findIndex((other) => other.documentType === example.documentType) === index,
  );
  const sensitiveNaming = Boolean(
    preview?.diagnostics.some((item) => item.code === 'sensitive-field'),
  );
  const routeFlowing =
    Boolean(settings?.enabled) &&
    triggerMode === 'continuous' &&
    !exportSettingsDirty &&
    !(destinationKind === 'content-central' && contentCentralWaiting?.kind === 'paused');
  const ccStep: 'connect' | 'catalog' | 'match' | 'ready' =
    ccChoosing === 'connection' || !connectionId || !selectedConnection
      ? 'connect'
      : ccChoosing === 'catalog' || !catalogId
        ? 'catalog'
        : destinationBinding?.destinationKind === 'content-central' &&
            destinationBinding.connectionId === connectionId &&
            sameContentCentralId(destinationBinding.remoteCatalogId, catalogId) &&
            !catalogProvisioningPlan &&
            !mappingDirty
          ? 'ready'
          : 'match';
  const catalogName =
    catalogDefinition?.catalog.name ??
    selectedCatalogOption?.name ??
    destinationBinding?.remoteCatalogName ??
    'Selected';
  const planCatalogAction = catalogProvisioningPlan?.changes.find(
    (change) => change.objectKind === 'catalog',
  )?.action;
  const pendingDecision = !catalogProvisioningPlan
    ? ccStep === 'match' && !catalogDetailLoading && !catalogDetailError
      ? 'Finish matching this job to the catalog.'
      : null
    : catalogProposalApplyState
      ? 'A Content Central setup is in progress. Open it to continue.'
      : planCatalogAction === 'create'
        ? 'A new catalog from this job is ready to create.'
        : catalogProvisioningPlan.blockers.length
          ? 'The matching needs a choice before Content Central can accept it.'
          : 'Content Central accepted this matching. Use it to finish.';
  // A saved or running setup opens from its plan alone; the matching window
  // needs the live catalog, which a restored proposal deliberately doesn't reload.
  const openPendingWindow = () =>
    setCcWindow(
      planCatalogAction === 'create' || catalogProposalApplyState || !catalogDefinition
        ? 'create'
        : 'match',
    );
  const matchedTypes =
    destinationBinding?.destinationKind === 'content-central'
      ? destinationBinding.documentTypes.length
      : 0;
  const contentCentralExamples = (contentCentralFilingPreview?.previews ?? []).map((item) => ({
    typeName:
      catalogDefinition?.documentTypes.find((type) =>
        sameContentCentralId(type.id, item.externalDocumentTypeId),
      )?.name ?? 'Document',
    folders: item.logicalFolders,
    fileName: item.filename,
  }));

  const contentCentralCard = (
    <>
      {ccStep !== 'ready' && (
        <ContentCentralSteps
          step={ccStep === 'connect' ? 'connect' : ccStep === 'catalog' ? 'catalog' : 'match'}
        />
      )}
      {ccStep === 'connect' && (
        <ContentCentralConnectStep
          connections={connections}
          selectedId={connectionId}
          disabled={catalogControlsLocked}
          onChoose={(id) => {
            setCcChoosing(null);
            setCcJustAdded(false);
            chooseContentCentralConnection(id);
          }}
          onOpenConnections={openConnectionsWindow}
        />
      )}
      {ccStep === 'catalog' && (
        <ContentCentralCatalogStep
          connection={selectedConnection}
          justAdded={ccJustAdded}
          onChangeConnection={() => {
            setCcJustAdded(false);
            setCcChoosing('connection');
          }}
          query={catalogQuery}
          onQuery={changeCatalogQuery}
          catalogs={catalogs}
          loading={catalogListLoading}
          loaded={catalogListLoaded}
          error={catalogListError}
          onRetry={() => setCatalogListRetry((value) => value + 1)}
          moreError={catalogMoreError}
          onRefreshMore={refreshContentCentralCatalogSearch}
          canShowMore={
            Boolean(catalogNextCursor) && catalogs.length < CONTENT_CENTRAL_CATALOG_RENDER_LIMIT
          }
          moreLoading={catalogMoreLoading}
          onShowMore={() => void loadMoreContentCentralCatalogs()}
          limitReached={
            Boolean(catalogNextCursor) && catalogs.length >= CONTENT_CENTRAL_CATALOG_RENDER_LIMIT
          }
          renderLimit={CONTENT_CENTRAL_CATALOG_RENDER_LIMIT}
          onChoose={(id) => {
            setCcChoosing(null);
            setCcJustAdded(false);
            chooseContentCentralCatalog(id);
            setCcWindow('match');
          }}
          current={
            ccChoosing === 'catalog' && catalogId ? { id: catalogId, name: catalogName } : null
          }
          onKeepCurrent={() => setCcChoosing(null)}
          onCreate={() => void startCreateCatalog()}
          creating={busy === 'proposing-catalog'}
          createError={ccCreateError}
          locked={catalogControlsLocked}
        />
      )}
      {(ccStep === 'match' || ccStep === 'ready') && (
        <>
          {catalogDetailLoading && (
            <p className="cc-state" role="status">
              <LoaderCircle className="spin" size={14} /> Checking document types and fields…
            </p>
          )}
          {catalogDetailError && (
            <div
              className="cc-inline-error"
              role="alert"
              data-telemetry="error:catalog-load-failed"
            >
              <TriangleAlert size={14} />
              <span>
                <strong>This catalog could not be read</strong>
                <OperatorErrorText message={catalogDetailError} />
              </span>
              <button
                type="button"
                className="secondary-button small"
                disabled={catalogControlsLocked}
                onClick={() => setCatalogDetailRetry((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          )}
          <ContentCentralSummary
            connection={selectedConnection}
            catalogName={catalogName}
            matchText={
              ccStep !== 'ready'
                ? 'Matching not finished'
                : contentCentralWaiting?.kind === 'types'
                  ? `${matchedTypes - heldTypeBindings.length} of ${matchedTypes} document types exporting · ${heldTypeBindings.length === 1 ? 'one waits' : `${heldTypeBindings.length} wait`} for review`
                  : contentCentralWaiting?.kind === 'paused' &&
                      contentCentralWaiting.action === 'matching'
                    ? 'The catalog changed · review the matching'
                    : unmatchedTypes.length > 0
                      ? `${matchedTypes} of ${matchedTypes + unmatchedTypes.length} document types matched`
                      : matchedTypes === 1
                        ? '1 document type matched'
                        : `All ${matchedTypes} document types matched`
            }
            matchAttention={
              ccStep !== 'ready' ||
              unmatchedTypes.length > 0 ||
              contentCentralWaiting?.kind === 'types' ||
              (contentCentralWaiting?.kind === 'paused' &&
                contentCentralWaiting.action === 'matching')
            }
            waiting={ccStep === 'ready' ? contentCentralWaiting : null}
            unmatched={
              ccStep === 'ready' && contentCentralWaiting?.kind !== 'paused' ? unmatchedTypes : []
            }
            locked={proposalDecisionLocked}
            onChangeConnection={() => setCcChoosing('connection')}
            onChangeCatalog={() => setCcChoosing('catalog')}
            onOpenMatching={() =>
              catalogProvisioningPlan ? openPendingWindow() : setCcWindow('match')
            }
            onOpenConnections={openConnectionsWindow}
            showExamples={ccStep === 'ready'}
            examples={contentCentralExamples}
            examplesLoading={ccStep === 'ready' && contentCentralFilingPreviewLoading}
            examplesError={ccStep === 'ready' ? contentCentralFilingPreviewError : ''}
            onRetryExamples={() => {
              if (contentCentralFilingPreviewRevisionMismatch)
                setCatalogDetailRetry((value) => value + 1);
              else setContentCentralFilingPreviewRetry((value) => value + 1);
            }}
            pendingDecision={pendingDecision}
            onOpenPending={() =>
              catalogProvisioningPlan ? openPendingWindow() : setCcWindow('match')
            }
          />
        </>
      )}
    </>
  );

  // The newest page of Activity, unfiltered, holds the automatic deletes of its time span among its exports (all of
  // them when the page is the whole history), each just before the first export older than it; exports keep the
  // engine's order. Any other page or filter shows exports only.
  const shownExports = history.slice(0, 40);
  const exportedAt = (document: ExportDocumentSummary) =>
    Date.parse(document.verifiedAtUtc ?? document.frozenAtUtc);
  const oldestShownExport =
    shownExports.length > 0 ? Math.min(...shownExports.map(exportedAt)) : Number.NEGATIVE_INFINITY;
  const shownDeletes =
    historyFiltered || historyOffset > 0
      ? []
      : automaticDeletes
          .filter(
            (document) =>
              historyTotal <= shownExports.length ||
              Date.parse(document.deletedAtUtc) >= oldestShownExport,
          )
          .sort((left, right) => Date.parse(right.deletedAtUtc) - Date.parse(left.deletedAtUtc));
  const activityRows: Array<
    | { kind: 'export'; document: ExportDocumentSummary }
    | { kind: 'deleted'; document: DeletedDocument }
  > = [];
  {
    let next = 0;
    for (const document of shownExports) {
      while (
        next < shownDeletes.length &&
        Date.parse(shownDeletes[next].deletedAtUtc) > exportedAt(document)
      )
        activityRows.push({ kind: 'deleted', document: shownDeletes[next++] });
      activityRows.push({ kind: 'export', document });
    }
    while (next < shownDeletes.length)
      activityRows.push({ kind: 'deleted', document: shownDeletes[next++] });
  }
  const activityStage = (
    <ExportStage
      icon={<History size={18} aria-hidden="true" />}
      title="Activity"
      className="export-activity-stage export-activity-section"
    >
      {runResult && <ExportRunAttentionNotice result={runResult} />}
      {hasNoTypeLeft(job) && (
        <p className="export-save-note export-no-type-left" role="status">
          <TriangleAlert size={15} aria-hidden="true" /> {NO_TYPE_LEFT_SENTENCE}
        </p>
      )}
      <div className="export-history expanded">
        {!historyLoading && !historyError && historyActiveTotal > 0 && (
          <p className="export-history-summary">
            {historyActiveTotal > 0 && (
              <span>
                <Clock3 size={15} />
                {historyActiveTotal.toLocaleString()} in progress
              </span>
            )}
          </p>
        )}
        {(historyFiltered || history.length > 0) && (
          <div
            className="export-history-filters"
            role="search"
            aria-label="Export activity filters"
          >
            <input
              aria-label="Search exported documents"
              value={historyQuery}
              disabled={historyLoading || busy !== null}
              onChange={(event) => setHistoryQuery(event.target.value)}
              placeholder="Name or field value"
              onKeyDown={(event) => {
                if (event.key === 'Enter') findHistory();
              }}
            />
            <select
              aria-label="Filter by document type"
              value={historyType}
              disabled={historyLoading || busy !== null}
              onChange={(event) => setHistoryType(event.target.value)}
            >
              <option value="">All types</option>
              {job.documentTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by export status"
              value={historyState}
              disabled={historyLoading || busy !== null}
              onChange={(event) => setHistoryState(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="exported">Exported</option>
              <option value="purged">Backup copy cleaned up</option>
              <option value="failed">Problem</option>
              <option value="uncertain">Checking destination</option>
              <option value="canceled">Returned to work</option>
            </select>
            <select
              aria-label="Filter by export date"
              value={historyDate}
              disabled={historyLoading || busy !== null}
              onChange={(event) => setHistoryDate(event.target.value)}
            >
              {exportHistoryDayChoices().map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
            <button
              className="quiet-button small"
              disabled={historyLoading || busy !== null}
              onClick={() => findHistory()}
            >
              <Search size={12} /> Find
            </button>
          </div>
        )}
        {historyLoading && (
          <p className="muted-line" role="status">
            <LoaderCircle className="spin" size={13} /> Loading export activity…
          </p>
        )}
        {historyError && !historyLoading && (
          <div
            className="content-central-inline-error export-history-error"
            role="alert"
            data-telemetry="error:export-activity-load-failed"
          >
            <TriangleAlert size={14} />
            <span>
              <strong>Export activity could not be loaded</strong>
              <OperatorErrorText message={historyError} />
            </span>
            <button
              className="secondary-button small"
              onClick={() => void loadHistory(historyOffset)}
            >
              Try again
            </button>
          </div>
        )}
        {!historyLoading &&
          !historyError &&
          activityRows.map((row) =>
            row.kind === 'deleted' ? (
              <ActivityDeletedRow
                key={`deleted:${row.document.batchId}:${row.document.documentId}`}
                document={row.document}
                busy={busy !== null || restoringDeleted !== null}
                restoring={restoringDeleted === row.document.documentId}
                onRestore={() => void restoreAutomaticDelete(row.document)}
              />
            ) : (
              <ExportHistoryRow
                key={row.document.exportId}
                document={row.document}
                busy={busy !== null}
                locked={catalogOperationLocked}
                activeAction={
                  historyAction?.exportId === row.document.exportId ? historyAction.kind : null
                }
                onRetry={(action) => void retryExportedDocument(row.document, action)}
                onCancel={() => void cancelContentCentralExport(row.document)}
                onReturn={() => void returnExportToWork(row.document)}
                onOpen={(target) => void openExportedDocument(row.document, target)}
                onDelete={
                  row.document.route === 'exceptions' &&
                  (row.document.exceptionReason === 'no-type' ||
                    row.document.exceptionReason === 'ai-check-not-answered' ||
                    row.document.exceptionReason === 'not-a-document')
                    ? () => void deleteException(row.document)
                    : undefined
                }
                deleted={
                  row.document.documentDeleted === true ||
                  deletedExceptions.has(row.document.exportId)
                }
                deleting={deletingException === row.document.exportId}
              />
            ),
          )}
        {!historyLoading && !historyError && !historyFiltered && activityRows.length === 0 && (
          <div className="export-empty">
            <History size={28} aria-hidden="true" />
            <span>Nothing exported yet</span>
          </div>
        )}
        {!historyLoading && !historyError && historyFiltered && history.length === 0 && (
          <small className="export-history-empty">No exported documents match these filters.</small>
        )}
        {!historyLoading && !historyError && historyTotal > 40 && (
          <div className="export-history-pages">
            <button
              className="quiet-button small"
              disabled={historyOffset === 0}
              onClick={() => void loadHistory(Math.max(0, historyOffset - 40))}
            >
              <ChevronLeft size={12} /> Newer
            </button>
            <small>
              {historyOffset + 1}–{Math.min(historyOffset + history.length, historyTotal)} of{' '}
              {historyTotal}
            </small>
            <button
              className="quiet-button small"
              disabled={historyOffset + history.length >= historyTotal}
              onClick={() => void loadHistory(historyOffset + 40)}
            >
              Older <ChevronRight size={12} />
            </button>
          </div>
        )}
      </div>
    </ExportStage>
  );

  const exportNowBlocked =
    !settings ||
    !canRun ||
    busy !== null ||
    catalogOperationLocked ||
    contentCentralPaused ||
    unappliedCatalogProposal ||
    exportSettingsDirty;
  const nothingToSend = !readyCount.error && (readyCount.ready === null || readyCount.ready === 0);
  const exportNowStatus = <ExportReadyStatus count={readyCount} />;

  const exportOn = Boolean(settings?.enabled);
  const destinationSaved = exportHasDestination(settings);
  // What a save of the draft waits for (Start exporting waited for the same, and said why with exportSaveBlocker).
  const draftSaveBlocked =
    busy !== null ||
    catalogOperationLocked ||
    (destinationKind === 'content-central'
      ? contentCentralPaused ||
        unappliedCatalogProposal ||
        !catalogProposal ||
        catalogProposal.blockers.length > 0
      : filesystemDestinationMissing || previewState !== 'ready');
  // The switch (owner decision, 2026-10-26). With a destination saved it changes only on or off; before
  // that, Turn on saves what is shown, so it waits, with the same words, for what that save waits for.
  const switchBlocked =
    busy !== null || catalogOperationLocked || (!destinationSaved && draftSaveBlocked);
  const switchTooltip = destinationSaved
    ? busy === null && catalogOperationLocked
      ? 'Wait a moment'
      : ''
    : exportSaveBlocker;
  const switching = busy === 'switching' || (busy === 'saving' && saveTurnsOn.current === true);
  // It says the saved timing once something is saved: that is what on or off means now.
  const powerSwitch = (
    <ExportPower
      on={exportOn}
      trigger={settings?.triggerMode ?? triggerMode}
      autopilot={exceptionsShown}
      time={settings ? (settings.scheduleLocalTime ?? '21:00') : scheduleLocalTime}
      action={
        <TooltipButton
          className={`${exportOn ? 'secondary-button' : 'primary-button'} small export-power-action`}
          data-telemetry={exportOn ? 'export-turn-off' : 'export-turn-on'}
          tooltip={switchTooltip}
          disabled={switchBlocked}
          onClick={() => void toggleExport(!exportOn)}
        >
          {switching ? (
            <LoaderCircle className="spin" size={14} aria-hidden="true" />
          ) : (
            <Power size={14} aria-hidden="true" />
          )}
          {exportOn ? 'Turn off' : 'Turn on'}
        </TooltipButton>
      }
    />
  );
  // Why the switch, or Export now, is shown disabled (telemetry: export.blocked, once per session per reason).
  // The switch is on every panel; Export now only on the Destination stop of a job sent "Only when I say".
  const manualSend = exportOn && triggerMode === 'manual';
  useExportBlockedReport(
    !plan
      ? null
      : !exportOn
        ? exportBlockedReason({
            exportOn,
            destinationSaved,
            busy: busy !== null,
            operationLocked: catalogOperationLocked,
            destinationKind,
            folderMissing: filesystemDestinationMissing,
            contentCentralUnfinished: !catalogProposal || catalogProposal.blockers.length > 0,
            contentCentralNeedsAttention: contentCentralNeedsAttention,
            contentCentralChecking: contentCentralCatalogChecking,
            proposalUnapplied: unappliedCatalogProposal,
            examples: previewState,
            unsaved: exportSettingsDirty,
            canRun,
          })
        : stop === 'destination' && manualSend
          ? exportBlockedReason({
              exportOn,
              destinationSaved,
              busy: busy !== null,
              operationLocked: catalogOperationLocked,
              destinationKind,
              folderMissing: filesystemDestinationMissing,
              contentCentralUnfinished: !catalogProposal || catalogProposal.blockers.length > 0,
              contentCentralNeedsAttention: contentCentralNeedsAttention,
              contentCentralChecking: contentCentralCatalogChecking,
              proposalUnapplied: unappliedCatalogProposal,
              examples: previewState,
              unsaved: exportSettingsDirty,
              canRun,
            })
          : null,
    job.id,
  );
  // The mode's own action (owner decision, 2026-10-26): Export now with the engine's count while export is
  // on and sends "Only when I say". As soon as they're ready and Daily have none; the switch is the story.
  const primaryAction = !manualSend ? null : (
    <div className="export-primary-wrap">
      <small role="status">{exportSettingsDirty ? 'Save first' : exportNowStatus}</small>
      <button
        type="button"
        className="primary-button export-primary"
        data-telemetry="export-now"
        disabled={exportNowBlocked || nothingToSend}
        onClick={() => void run()}
      >
        {busy === 'running' ? (
          <>
            <LoaderCircle className="spin" size={16} aria-hidden="true" />
            Exporting…
          </>
        ) : (
          <>
            <FolderOutput size={16} aria-hidden="true" />
            Export now
          </>
        )}
      </button>
    </div>
  );
  // The destination is the main story (owner, 2026-09-23): the folder, its one primary action,
  // then real examples for every type, said to be examples, and the way into naming.
  const folderBody = (
    <>
      <div className="export-dest-head">
        <span className="export-dest-art" aria-hidden="true">
          <FolderOpen size={26} />
        </span>
        <span className="export-dest-name">
          <b>{hasFolder ? folderName : 'No folder yet'}</b>
          {hasFolder && <small title={folderPath}>{folderPath}</small>}
        </span>
        {hasFolder ? (
          <button
            type="button"
            className="secondary-button small export-folder-change"
            data-telemetry="export-change-folder"
            aria-busy={folderPickerBusy === 'destination'}
            disabled={catalogControlsLocked || folderPickerBusy !== null}
            onClick={() => void chooseDestination()}
          >
            {folderPickerBusy === 'destination' ? 'Choosing…' : 'Change'}
          </button>
        ) : (
          <button
            type="button"
            className="secondary-button small export-folder-change"
            data-telemetry="export-choose-folder"
            aria-busy={folderPickerBusy === 'destination'}
            disabled={catalogControlsLocked || folderPickerBusy !== null}
            onClick={() => void chooseDestination()}
          >
            {folderPickerBusy === 'destination' ? 'Choosing…' : 'Choose a folder'}
          </button>
        )}
        {primaryAction}
      </div>
      <hr />
      <span className="cc-label">Examples from your recent documents</span>
      {previewState === 'error' ? (
        <div
          className="content-central-inline-error filing-preview-error"
          role="alert"
          data-telemetry="error:filing-examples-failed"
        >
          <TriangleAlert size={14} />
          <span>
            <strong>The examples could not be built</strong>
            <OperatorErrorText message={previewError} />
          </span>
          <button
            className="secondary-button small"
            onClick={() => {
              if (plan) refreshPreview(plan);
            }}
          >
            Try again
          </button>
        </div>
      ) : previewState !== 'ready' ? (
        <p className="cc-state" role="status">
          <LoaderCircle className="spin" size={14} /> Building examples…
        </p>
      ) : folderExamples.length === 0 ? (
        <p className="cc-state">Examples appear here once documents are scanned.</p>
      ) : (
        <div className="cc-paths">
          {folderExamples.map((example) => (
            <FilingPath
              key={example.documentId}
              label={example.documentType}
              folders={example.folders}
              fileName={`${example.preferredBaseName}.pdf`}
            />
          ))}
        </div>
      )}
      {sensitiveNaming && (
        <p className="cc-warning small" title="Shows in file or folder names">
          <ShieldAlert size={14} aria-hidden="true" />
          <span className="cc-warning-text">Private value in names</span>
        </p>
      )}
      <div className="export-naming-foot">
        <button
          type="button"
          className="secondary-button small"
          data-telemetry="export-naming"
          disabled={busy !== null || catalogControlsLocked}
          onClick={() => setNamingOpen(true)}
        >
          <Pencil size={14} /> Change how files are named
        </button>
        <small>See every document type and choose its folders and file name.</small>
      </div>
    </>
  );
  const contentCentralBody = (
    <>
      <div className="export-dest-head">
        <span className="export-dest-art" aria-hidden="true">
          <Database size={26} />
        </span>
        <span className="export-dest-name">
          <b>Content Central</b>
          <small>{ccStep === 'ready' ? catalogName : 'Your catalog does the filing'}</small>
        </span>
        {primaryAction}
      </div>
      <hr />
      {contentCentralCard}
    </>
  );
  const chooseKind = (kind: 'filesystem' | 'content-central') => {
    if (destinationKind !== kind) {
      markExportDraftChanged();
      setDestinationKind(kind);
    }
  };
  const destinationStage = (
    <ExportStage
      icon={
        destinationKind === 'filesystem' ? (
          <FolderOpen size={18} aria-hidden="true" />
        ) : (
          <Database size={18} aria-hidden="true" />
        )
      }
      title="Destination"
      className="export-destination-stage"
    >
      {catalogOperationLocked && (
        <p className="export-save-note" role="status">
          <Clock3 size={15} aria-hidden="true" /> Content Central is finishing this setup.
        </p>
      )}
      <div className="export-destination-body">
        <div className="export-destination-choices" role="group" aria-label="Destination">
          <button
            type="button"
            data-telemetry="export-destination-folder"
            disabled={catalogControlsLocked}
            aria-label="A folder"
            aria-pressed={destinationKind === 'filesystem'}
            className={`export-destination-choice${destinationKind === 'filesystem' ? ' selected' : ''}`}
            onClick={() => chooseKind('filesystem')}
          >
            <span className="export-route-icon">
              <FolderOpen size={19} aria-hidden="true" />
            </span>
            <span>
              <b>A folder</b>
              <small>Files organized for you</small>
            </span>
            {destinationKind === 'filesystem' && (
              <span className="export-destination-check" aria-hidden="true">
                <Check size={13} />
              </span>
            )}
          </button>
          <button
            type="button"
            data-telemetry="export-destination-content-central"
            disabled={catalogControlsLocked}
            aria-label="Content Central"
            aria-pressed={destinationKind === 'content-central'}
            className={`export-destination-choice${destinationKind === 'content-central' ? ' selected' : ''}`}
            onClick={() => chooseKind('content-central')}
          >
            <span className="export-route-icon">
              <Database size={19} aria-hidden="true" />
            </span>
            <span>
              <b>Content Central</b>
              <small>Your catalog does the filing</small>
            </span>
            {destinationKind === 'content-central' && (
              <span className="export-destination-check" aria-hidden="true">
                <Check size={13} />
              </span>
            )}
          </button>
        </div>
        <div
          className={`export-destination-card${destinationKind === 'content-central' ? ' cc-destination' : ' folder-destination'}`}
        >
          {destinationKind === 'filesystem' ? folderBody : contentCentralBody}
        </div>
      </div>
    </ExportStage>
  );
  // Also send to: a copy of each document in a cloud service, beside the main destination.
  const elsewhereStage = (
    <ExportStage
      icon={<Server size={18} aria-hidden="true" />}
      title="Also send each document to"
      className="export-elsewhere-stage"
    >
      <CloudDestination
        jobId={job.id}
        locked={catalogControlsLocked}
        fieldNames={[
          ...new Set(
            job.documentTypes
              .filter((type) => type.status !== 'archived')
              .flatMap((type) =>
                type.fields
                  .filter((field) => field.kind !== 'line-items')
                  .map((field) => field.name),
              ),
          ),
        ]}
      />
    </ExportStage>
  );
  const contentCentralAttention =
    destinationKind === 'content-central' &&
    exportOn &&
    ((contentCentralPaused && !contentCentralCatalogChecking) ||
      unappliedCatalogProposal ||
      Boolean(contentCentralWaiting));
  const status = exportStatus({
    loading: busy === 'loading',
    enabled: exportOn,
    unsaved: hasExportEdits,
    needsAttention: historyNeedsAttentionTotal,
    attentionText: contentCentralAttention
      ? 'Content Central needs attention'
      : folderExportHold(settings),
    lastExportAt,
    exportedToday,
  });

  return (
    <section
      ref={cardRef}
      className={`panel export-setup-card export-route-card${standalone ? ' standalone' : ''}`}
    >
      {exportResultOpen && runResult && (
        <ExportResultDialog result={runResult} onClose={() => setExportResultOpen(false)} />
      )}
      {busy === 'loading' ? (
        <p className="muted-line">
          <LoaderCircle className="spin" size={14} /> Loading export setup…
        </p>
      ) : !plan ? (
        <div
          className="content-central-inline-error export-setup-load-error"
          role="alert"
          data-telemetry="error:export-setup-load-failed"
        >
          <TriangleAlert size={14} />
          <span>
            <strong>Export setup could not be loaded</strong>
            <OperatorErrorText
              message={setupLoadError || "Paige could not read this job's export settings."}
            />
          </span>
          <button
            className="secondary-button small"
            onClick={() => setSetupLoadRetry((value) => value + 1)}
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <ExportHeader
            status={status}
            active={stop === 'activity'}
            onOpenActivity={() => openStop(stop === 'activity' ? 'destination' : 'activity')}
            power={powerSwitch}
          >
            {/* Export's own Save, outside Job setup: once something is saved, or the draft is touched. */}
            {!embedded && (destinationSaved || hasExportEdits) && exportSaveButton}
          </ExportHeader>
          {/* The route stays put; only the panel under it changes. Autopilot is set on Review thresholds. */}
          <ExportRoute
            stop={stop}
            onStop={openStop}
            trigger={triggerMode}
            destinationKind={destinationKind}
            flowing={routeFlowing}
            when={triggerSummary(triggerMode, scheduleLocalTime)}
            files={filesSummary(
              destinationKind === 'filesystem' ? files : 'pdf-data-text',
              pdfProfile === 'pdfa-2u',
            )}
            destination={destinationKind === 'filesystem' ? folderName : 'Content Central'}
            backup={retentionSummary(retentionDays)}
            alsoSendTo={cloudOffered}
            exceptions={exceptionsShown}
          />
          {saveProblem && (
            <div
              className="content-central-inline-error export-save-problem"
              role="alert"
              data-telemetry={`error:${saveProblem.code}`}
            >
              <TriangleAlert size={16} aria-hidden="true" />
              <span>
                <OperatorErrorText message={saveProblem.message} />
              </span>
              {saveProblem.naming ? (
                <button
                  type="button"
                  className="secondary-button small"
                  disabled={busy !== null}
                  onClick={() => setNamingOpen(true)}
                >
                  <Pencil size={14} aria-hidden="true" /> Change how files are named
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary-button small"
                  disabled={busy !== null}
                  onClick={() =>
                    void (saveProblem.switchTo !== undefined
                      ? toggleExport(saveProblem.switchTo)
                      : saveShown(saveTurnsOn.current))
                  }
                >
                  Try again
                </button>
              )}
            </div>
          )}
          <div className="export-body">
            {stop === 'destination' && destinationStage}
            {stop === 'when' && (
              <ExportWhenPanel
                trigger={triggerMode}
                time={scheduleLocalTime}
                locked={catalogControlsLocked}
                notice={
                  unappliedCatalogProposal && !catalogOperationLocked ? (
                    <p className="export-save-note" role="status">
                      <Clock3 size={15} aria-hidden="true" /> Finish the Content Central choice
                      first.
                    </p>
                  ) : null
                }
                onTrigger={(mode) => {
                  markExportDraftChanged();
                  setTriggerMode(mode);
                }}
                onTime={(value) => {
                  markExportDraftChanged();
                  setScheduleLocalTime(value);
                }}
              />
            )}
            {stop === 'files' && (
              <ExportFilesPanel
                archive={pdfProfile === 'pdfa-2u'}
                available={pdfAAvailable}
                locked={catalogControlsLocked}
                onArchive={(archive) => {
                  markExportDraftChanged();
                  setPdfProfile(archive ? 'pdfa-2u' : 'searchable-pdf');
                }}
                files={files}
                onFiles={
                  destinationKind === 'filesystem'
                    ? (next) => {
                        if (next !== files) {
                          markExportDraftChanged();
                          setFiles(next);
                        }
                      }
                    : undefined
                }
              />
            )}
            {stop === 'backup' && (
              <ExportBackupPanel
                retentionDays={retentionDays}
                location={shownLocalStore}
                space={backupSpace}
                locked={catalogControlsLocked || folderPickerBusy !== null}
                onRetention={(days) => {
                  markExportDraftChanged();
                  setRetentionDays(days as ExportJobSettings['retentionDays']);
                }}
              />
            )}
            {stop === 'elsewhere' && elsewhereStage}
            {stop === 'exceptions' && exceptionsShown && (
              <ExportExceptionsPanel
                location={shownExceptionsFolder}
                held={Boolean(settings && !settings.exceptionsRoot)}
                contentCentral={destinationKind === 'content-central'}
                choosing={folderPickerBusy === 'exceptions'}
                locked={catalogControlsLocked || folderPickerBusy !== null}
                onChangeLocation={() => void chooseExceptionsFolder()}
                onOpen={exceptionsFolderOpenable ? () => void openExceptionsFolder() : undefined}
              />
            )}
            {stop === 'activity' && activityStage}
          </div>
        </>
      )}
      {namingOpen && plan && (
        <NamingWindow
          job={job}
          plan={plan}
          destinationLabel={hasFolder ? folderName : 'your folder'}
          onClose={() => setNamingOpen(false)}
          onUse={(next) => {
            markExportDraftChanged();
            setPlan(next);
            setPlanProposal(null);
            refreshPreview(next);
            setNamingOpen(false);
            // Said by where the save is: Job setup's Save inside Job setup, Export's own button on the Export screen.
            onToast(
              embedded
                ? 'Naming updated. Save Job setup to keep it.'
                : 'Naming updated. Save export to keep it.',
            );
          }}
        />
      )}
      {ccWindow === 'match' && catalogDefinition && selectedConnection && (
        <ContentCentralMatchWindow
          job={job}
          definition={catalogDefinition}
          proposal={displayCatalogProposal}
          choices={mappingChoices}
          dirty={mappingDirty}
          locked={catalogControlsLocked}
          reviewing={busy === 'mapping-catalog'}
          plan={catalogProvisioningPlan}
          applyState={catalogProposalApplyState}
          outcomeKnown={catalogProposalOutcomeKnown}
          applyError={catalogProposalError}
          checkError={ccCheckError}
          applying={busy === 'applying-catalog'}
          discarding={busy === 'discarding-catalog'}
          directPreview={catalogProvisioningPlan ? null : contentCentralFilingPreview}
          holdReasons={holdReasons}
          initialTypeId={heldTypeBindings[0]?.localDocumentTypeId ?? null}
          connectionName={selectedConnection.displayName}
          onChoicesChange={(choices) => {
            if (catalogControlsLocked) return;
            markExportDraftChanged();
            setCcCheckError(null);
            setMappingChoices(choices);
            setMappingDirty(true);
            setCatalogProvisioningPlan(null);
            setCatalogProposalDecision(null);
            setCatalogProposalApplyState(null);
            setCatalogProposalOutcomeKnown(false);
            setCatalogProposalError(null);
          }}
          onCheck={() => void reviewCatalogMapping()}
          onUse={() =>
            void applyCatalog().then((applied) => {
              if (applied && liveCard.current) setCcWindow(null);
            })
          }
          onKeep={() =>
            void discardCatalogProposal().then((kept) => {
              if (kept && liveCard.current) setCcWindow(null);
            })
          }
          onClose={() => setCcWindow(null)}
        />
      )}
      {ccWindow === 'create' && (
        <ContentCentralCreateWindow
          jobName={job.name}
          connectionName={selectedConnection?.displayName ?? 'Content Central'}
          plan={createCompleted ? null : catalogProvisioningPlan}
          applyState={catalogProposalApplyState}
          outcomeKnown={catalogProposalOutcomeKnown}
          errorMessage={catalogProposalError}
          applying={busy === 'applying-catalog'}
          discarding={busy === 'discarding-catalog'}
          completed={createCompleted}
          exportOn={Boolean(settings?.enabled) && !exportSettingsDirty}
          onCreate={() => {
            const plan = catalogProvisioningPlan;
            const counts = plan
              ? {
                  typeCount: plan.changes.filter((change) => change.objectKind === 'document-type')
                    .length,
                  fieldCount: plan.changes.filter((change) => change.objectKind === 'field').length,
                  reuse:
                    plan.changes.find((change) => change.objectKind === 'catalog')?.action ===
                    'reuse',
                }
              : null;
            void applyCatalog().then((applied) => {
              if (applied && liveCard.current && counts) setCreateCompleted(counts);
            });
          }}
          onKeep={() =>
            void discardCatalogProposal().then((kept) => {
              if (kept && liveCard.current) setCcWindow(null);
            })
          }
          onClose={() => {
            setCcWindow(null);
            setCreateCompleted(null);
          }}
        />
      )}
      {ccWindow === 'connections' && (
        <WorkspaceDialog
          telemetry="connections"
          title="Connections"
          description="This computer · shared by every job"
          onClose={closeConnectionsWindow}
          actions={
            <button
              type="button"
              className="secondary-button small"
              onClick={closeConnectionsWindow}
            >
              <ChevronLeft size={15} /> Back to Export
            </button>
          }
        >
          <ConnectionsPanel
            onConnectionAdded={(connection) => {
              addedConnection.current = connection;
            }}
          />
        </WorkspaceDialog>
      )}
    </section>
  );
}

function pluralTypeName(name: string): string {
  if (/s$/i.test(name)) return name;
  if (/[^aeiou]y$/i.test(name)) return `${name.slice(0, -1)}ies`;
  return `${name}s`;
}

function ExportHistoryRow({
  document,
  busy,
  locked,
  activeAction,
  onRetry,
  onCancel,
  onReturn,
  onOpen,
  onDelete,
  deleted = false,
  deleting = false,
}: {
  document: ExportDocumentSummary;
  busy: boolean;
  locked: boolean;
  activeAction: 'retry' | 'cancel' | 'return' | null;
  onRetry: (action: ExportRetryAction) => void;
  onCancel: () => void;
  onReturn: () => void;
  onOpen: (target: 'pdf' | 'folder') => void;
  /** A document sent to the exceptions folder with no type, or as not a document: Delete it, which teaches the job its shape is junk. */
  onDelete?: () => void;
  deleted?: boolean;
  deleting?: boolean;
}) {
  const state = exportHistoryStateLabel(
    document.state,
    document.verifiedAtUtc ? new Date(document.verifiedAtUtc).toLocaleString() : null,
  );
  const cleanup = exportHistoryCleanupNote(document);
  const contentCentral = document.destinationKind === 'content-central';
  const shownName = document.realizedFilename || document.resolvedBaseName;
  const shownFolders = document.realizedLogicalFolders?.join(' › ');
  const remoteVerified = contentCentral && document.remoteEffectState === 'verified';
  const localPending =
    (remoteVerified && document.localRecoveryState !== 'verified') || folderBackupPending(document);
  const truthfulState = localPending
    ? document.state === 'delivering'
      ? 'Delivered · saving backup copy'
      : 'Delivered · backup copy pending'
    : state;
  const backupUntil = exportHistoryBackupUntil(document);
  const canOpen = contentCentral
    ? Boolean(document.localPackagePath)
    : Boolean(document.destinationLocator);
  const canReturn = contentCentral && document.remoteReturnable === true;
  const retryAction = exportRetryAction(document);
  // The engine owns the exact operation id and receipt. Its explicit
  // capability avoids exposing a Cancel action for a reserve whose
  // outcome is uncertain but whose operation identity was never read.
  const canCancel =
    contentCentral &&
    (document.remoteCancelable === true || document.remoteRecoveryAction === 'cancel');
  const retryLabel =
    retryAction === 'reconcile-cancellation'
      ? 'Check cancellation'
      : retryAction === 'complete-local-recovery'
        ? 'Retry backup copy'
        : retryAction === 'retry-delivery'
          ? 'Retry delivery'
          : 'Retry export';
  const retryBusyLabel =
    retryAction === 'reconcile-cancellation'
      ? 'Checking cancellation…'
      : retryAction === 'complete-local-recovery'
        ? 'Retrying backup copy…'
        : retryAction === 'retry-delivery'
          ? 'Retrying delivery…'
          : 'Retrying export…';
  const retryAccessibleLabel =
    retryAction === 'reconcile-cancellation'
      ? `${activeAction === 'retry' ? 'Checking' : 'Check'} cancellation for ${shownName}`
      : retryAction === 'complete-local-recovery'
        ? `${activeAction === 'retry' ? 'Retrying' : 'Retry'} backup copy for ${shownName}`
        : retryAction === 'retry-delivery'
          ? `${activeAction === 'retry' ? 'Retrying' : 'Retry'} delivery of ${shownName}`
          : `${activeAction === 'retry' ? 'Retrying' : 'Retry'} export of ${shownName}`;
  // Sent to the exceptions folder (ADR-0096): the row says so with the engine's own sentence for the reason, in place
  // of the type and folders it was not filed by.
  const sentToExceptions =
    document.route === 'exceptions'
      ? `Sent to exceptions${document.exceptionReasonText ? `: ${document.exceptionReasonText.replace(/\.\s*$/u, '')}` : ''}`
      : null;
  return (
    <div className="export-history-row">
      <span>
        <b>{shownName}</b>
        <small>
          {sentToExceptions ?? (
            <>
              {document.documentType}
              {shownFolders ? ` · ${shownFolders}` : ''}
            </>
          )}{' '}
          · {truthfulState}
          {document.failureMessage ? ` · ${operatorMessage(document.failureMessage)}` : ''}
          {backupUntil ? ` · ${backupUntil}` : ''}
        </small>
        {document.failureMessage && (
          <TechnicalDetails detail={splitTechnicalDetails(document.failureMessage).detail} />
        )}
        {contentCentral && document.remoteDocumentId && (
          <small className="export-history-remote-detail">
            Content Central document {document.remoteDocumentId}
            {document.remoteIndexingState ? ` · indexing ${document.remoteIndexingState}` : ''}
            {document.remoteWorkflowState ? ` · workflow ${document.remoteWorkflowState}` : ''}
          </small>
        )}
        {cleanup && (
          <small className={`export-history-maintenance-detail ${cleanup.state}`}>
            {cleanup.text}
          </small>
        )}
      </span>
      <div className="export-history-actions">
        {retryAction && (
          <button
            className="quiet-button small"
            disabled={busy || locked}
            aria-busy={activeAction === 'retry' || undefined}
            aria-label={retryAccessibleLabel}
            onClick={() => onRetry(retryAction)}
          >
            {activeAction === 'retry' ? retryBusyLabel : retryLabel}
          </button>
        )}
        {canCancel && (
          <button
            className="quiet-button small"
            disabled={busy || locked}
            aria-busy={activeAction === 'cancel' || undefined}
            aria-label={`${activeAction === 'cancel' ? 'Canceling' : 'Cancel'} delivery of ${shownName}`}
            onClick={onCancel}
          >
            {activeAction === 'cancel' ? 'Canceling…' : 'Cancel delivery'}
          </button>
        )}
        {canReturn && (
          <button
            className="quiet-button small"
            disabled={busy || locked}
            aria-busy={activeAction === 'return' || undefined}
            aria-label={`${activeAction === 'return' ? 'Returning' : 'Return'} ${shownName} to work`}
            onClick={onReturn}
          >
            {activeAction === 'return' ? 'Returning…' : 'Return to work'}
          </button>
        )}
        {onDelete &&
          (deleted ? (
            <small className="export-history-deleted-mark">
              <Trash2 size={13} aria-hidden="true" />
              Deleted
            </small>
          ) : (
            <button
              className="quiet-button small"
              data-telemetry="exceptions-delete"
              disabled={busy || deleting}
              aria-busy={deleting || undefined}
              aria-label={`${deleting ? 'Deleting' : 'Delete'} ${shownName}`}
              onClick={onDelete}
            >
              {deleting ? (
                <LoaderCircle className="spin" size={13} aria-hidden="true" />
              ) : (
                <Trash2 size={13} aria-hidden="true" />
              )}
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          ))}
        <TooltipButton
          className="icon-button"
          disabled={!canOpen}
          onClick={() => onOpen('pdf')}
          tooltip={contentCentral ? 'Open backup PDF' : 'Open PDF'}
          aria-label={`Open ${contentCentral ? 'backup copy of ' : ''}${shownName}`}
        >
          <ExternalLink size={13} />
        </TooltipButton>
        <TooltipButton
          className="icon-button"
          disabled={!canOpen}
          onClick={() => onOpen('folder')}
          tooltip={contentCentral ? 'Open backup copy folder' : 'Open folder'}
          aria-label={`Open ${contentCentral ? 'backup copy ' : ''}folder for ${shownName}`}
        >
          <FolderOpen size={13} />
        </TooltipButton>
      </div>
    </div>
  );
}

/** An automatic delete in Activity (ADR-0096): laid out as an export row, with Restore as its one action. */
function ActivityDeletedRow({
  document,
  busy,
  restoring,
  onRestore,
}: {
  document: DeletedDocument;
  busy: boolean;
  restoring: boolean;
  onRestore: () => void;
}) {
  const said = (document.reasonText || AUTOMATIC_DELETE_SENTENCE).replace(/\.\s*$/u, '');
  return (
    <div className="export-history-row export-history-deleted">
      <span>
        <b>{document.sourceName}</b>
        <small>
          {said} · {new Date(document.deletedAtUtc).toLocaleString()}
        </small>
      </span>
      <div className="export-history-actions">
        <button
          className="quiet-button small"
          data-telemetry="deleted-restore"
          disabled={busy}
          aria-busy={restoring || undefined}
          aria-label={`${restoring ? 'Restoring' : 'Restore'} ${document.sourceName}`}
          onClick={onRestore}
        >
          {restoring ? 'Restoring…' : 'Restore'}
        </button>
      </div>
    </div>
  );
}

/** The common value types, shown as pictures; the rest are one choice away. */
const ROUTE_COMMON_KINDS: FieldKind[] = [
  'text',
  'identifier',
  'date',
  'currency',
  'number',
  'postal-address',
];

/**
 * One field drawn as the trip its value takes: find it on the page, check its
 * shape, decide whether a person looks, and what gets saved. Every setting the
 * field has lives at the step it acts on.
 */
/** What one field read on the setup sample: the value, how sure, and where on the page. */
type FieldSample = {
  value: string;
  confidence: number | null;
  needsReview: boolean;
  previewPath: string | null;
  box: PageBox | null;
  from: string | null;
};

/** Short, plain names for the type tiles; the full names stay in the type help and elsewhere. */
const ROUTE_KIND_NAMES: Partial<Record<FieldKind, string>> = {
  identifier: 'Code',
  currency: 'Money',
};

/** The four numbered stops a field's value passes, joined by arrows; the last leg is green. */
function RouteLine() {
  const step = (number: number, title: string, subtitle: string, result = false) => (
    <div className={`setup-route-step${result ? ' is-result' : ''}`}>
      <span className="setup-route-number" aria-hidden="true">
        {number}
      </span>
      <strong>{title}</strong>
      <small>{subtitle}</small>
    </div>
  );
  return (
    <div className="setup-route-line" aria-hidden="true">
      {step(1, 'Find it', 'on the page')}
      {step(2, 'Check it', 'is it the right shape?')}
      {step(3, 'Decide', 'does a person look?')}
      {step(4, 'Result', 'what gets saved', true)}
    </div>
  );
}

function FieldRoute({
  field: item,
  schemaLocked,
  lookupRules,
  reviewDefault = REVIEW_POLICY_DEFAULTS.fields,
  impact = { kind: 'unavailable' },
  sample,
  onChange,
  onDelete,
  onOpenJourney,
  onResetLearning,
  onOpenThresholds,
}: {
  field: Field;
  schemaLocked: boolean;
  lookupRules: LookupRuleDefinition[];
  reviewDefault?: number;
  impact?: FieldReviewImpact;
  sample?: FieldSample;
  onChange: (patch: Partial<Field>) => void;
  onDelete: () => void;
  onOpenJourney: () => void;
  onResetLearning?: () => void;
  onOpenThresholds?: () => void;
}) {
  const fieldControlId = useId();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [readingHelpOpen, setReadingHelpOpen] = useState(false);
  const [formatHelpOpen, setFormatHelpOpen] = useState(false);
  const [valueTypeHelpOpen, setValueTypeHelpOpen] = useState(false);
  const [defaultValueHelpOpen, setDefaultValueHelpOpen] = useState(false);
  const [requiredHelpOpen, setRequiredHelpOpen] = useState(false);
  const removeFieldButtonRef = useRef<HTMLButtonElement>(null);
  const keepFieldButtonRef = useRef<HTMLButtonElement>(null);
  const deletionFocusPending = useRef(false);
  const showRemovalConfirmation = (confirming: boolean) => {
    deletionFocusPending.current = true;
    setConfirmingDelete(confirming);
  };
  useLayoutEffect(() => {
    if (!deletionFocusPending.current) return;
    deletionFocusPending.current = false;
    (confirmingDelete ? keepFieldButtonRef : removeFieldButtonRef).current?.focus();
  }, [confirmingDelete]);
  const internalBar = item.reviewBelow ?? reviewDefault;
  // The true state: any bar on the field decides review for it, whoever set it.
  const customReview = item.reviewBelow != null;
  const barOff = internalBar === 0;
  const shownBar = Math.round(internalBar * 100);
  const scalar = item.kind !== 'document-type' && item.kind !== 'line-items';
  const invalidPatternExample = Boolean(
    item.formatExample?.trim() && !valuePatternFromExample(item.formatExample),
  );
  const otherKinds = (Object.keys(kindLabel) as FieldKind[]).filter(
    (kind) =>
      kind !== 'line-items' && kind !== 'document-type' && !ROUTE_COMMON_KINDS.includes(kind),
  );
  const kindName = (kind: FieldKind) => ROUTE_KIND_NAMES[kind] ?? kindLabel[kind];
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [moreKinds, setMoreKinds] = useState(() => otherKinds.includes(item.kind));
  const assignment = fieldLookup(item);
  const fromList = Boolean(assignment);
  const rule = lookupRules.find((candidate) => candidate.id === assignment?.ruleId) ?? null;
  const listSpelling = usesListSpelling(item, assignment, rule);
  const barcodeSummary = `${item.barcodeSymbology ? friendlyBarcodeSymbology(item.barcodeSymbology) : 'Any type'} · ${item.barcodeValuePrefix ? `starts with ${item.barcodeValuePrefix}` : 'any start'}`;
  const helpDialogs = (
    <>
      {readingHelpOpen && (
        <WorkspaceDialog
          telemetry="help-reading-sources"
          title="Reading sources"
          compact
          onClose={() => setReadingHelpOpen(false)}
        >
          <div className="field-reading-help">
            <section>
              <FileText size={25} />
              <div>
                <h3>Printed text</h3>
                <p>Read what you can see on the page, like a name, date, or amount.</p>
              </div>
            </section>
            <section>
              <QrCode size={25} />
              <div>
                <h3>Barcodes</h3>
                <p>
                  Read decoded values anywhere in the document. If there are several barcodes, use a
                  barcode type or value prefix to narrow the choice. Turning this on alone does not
                  teach which barcode belongs to this field.
                </p>
              </div>
            </section>
            <section>
              <CheckCircle2 size={25} />
              <div>
                <h3>Both on</h3>
                <p>
                  Allow either source, for example an invoice number printed as text or encoded in a
                  barcode. Use barcode alone when a decoded value is required. If the barcode choice
                  is ambiguous, it may remain unresolved; visible text can still be used when
                  allowed.
                </p>
              </div>
            </section>
          </div>
        </WorkspaceDialog>
      )}
      {valueTypeHelpOpen && (
        <WorkspaceDialog
          telemetry="help-value-types"
          title="Types"
          compact
          onClose={() => setValueTypeHelpOpen(false)}
        >
          <div className="setup-value-type-help">
            <p>Choose what the value represents.</p>
            <dl>
              {Object.entries(valueTypeDescriptions).map(([kind, description]) => (
                <div key={kind}>
                  <dt>{kindLabel[kind as FieldKind]}</dt>
                  <dd>{description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </WorkspaceDialog>
      )}
      {defaultValueHelpOpen && (
        <WorkspaceDialog
          telemetry="help-default-value"
          title="If empty, use"
          compact
          onClose={() => setDefaultValueHelpOpen(false)}
        >
          <p>Used only when the field has no value.</p>
        </WorkspaceDialog>
      )}
      {requiredHelpOpen && (
        <WorkspaceDialog
          telemetry="help-required"
          title="Required field"
          compact
          onClose={() => setRequiredHelpOpen(false)}
        >
          <p>Ask for review when empty.</p>
          {schemaLocked && <p>This setting is controlled by Content Central.</p>}
        </WorkspaceDialog>
      )}
      {formatHelpOpen && (
        <WorkspaceDialog
          telemetry="help-looks-like"
          title="Looks like"
          compact
          onClose={() => setFormatHelpOpen(false)}
        >
          <div className="setup-explainer">
            <section>
              <h3>An expected shape is an extra check</h3>
              <p>
                Enter an example such as ABC-12345 when an identifier must have three letters, a
                dash, and five digits. Other letters and digits in that same shape are allowed. A
                different shape asks for review; the original value is kept.
              </p>
            </section>
            <section>
              <h3>Leave it blank when values vary</h3>
              <p>
                You usually do not need a shape for names, dates, or amounts. It does not rewrite
                dates, add currency symbols, or change Text into another type.
              </p>
            </section>
          </div>
        </WorkspaceDialog>
      )}
    </>
  );
  if (item.kind === 'document-type')
    return (
      <section
        className="setup-route is-fixed"
        data-setup-field={item.id}
        aria-label={`${item.name} settings`}
      >
        <header className="setup-route-head">
          <h3>{item.name}</h3>
        </header>
        <p className="setup-route-fixed">
          Always captured: it names what each document is, and it cannot be removed.
        </p>
      </section>
    );
  return (
    <section
      className="setup-route"
      data-setup-field={item.id}
      aria-label={`${item.name} settings`}
    >
      {helpDialogs}
      {/* Only the name, edited in place: its type and Required are chosen in Check it, and the sample shows in Find it. */}
      <header className="setup-route-head">
        <label className="setup-route-name">
          <input
            id={`${fieldControlId}-name`}
            aria-label="Field name"
            value={item.name}
            disabled={schemaLocked}
            size={Math.max(4, item.name.length + 1)}
            onChange={(event) => onChange({ name: event.target.value })}
          />
          {!schemaLocked && <Pencil size={15} aria-hidden="true" />}
        </label>
      </header>
      <RouteLine />
      <div className="setup-route-stops">
        <section className="setup-stop" aria-labelledby={`${fieldControlId}-find`}>
          <h4 id={`${fieldControlId}-find`}>
            <span className="setup-stop-number">1</span>Find it
          </h4>
          <div className="setup-stop-block">
            <div className="setup-stop-label">
              Read from
              <TooltipButton
                type="button"
                className="icon-button"
                aria-label={`About reading sources for ${item.name}`}
                tooltip="About reading sources"
                onClick={() => setReadingHelpOpen(true)}
              >
                <Info size={15} />
              </TooltipButton>
            </div>
            <div className="setup-sources">
              <label className={`setup-source-row${item.readText ? ' is-on' : ''}`}>
                <FileText size={16} aria-hidden="true" />
                <span>Printed text</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={item.readText}
                  onChange={(event) => onChange({ readText: event.target.checked })}
                />
              </label>
              <div className="setup-source-group">
                <label className={`setup-source-row${item.readBarcode ? ' is-on' : ''}`}>
                  <Barcode size={16} aria-hidden="true" />
                  <span>Barcode</span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={item.readBarcode}
                    onChange={(event) => {
                      if (!event.target.checked) setBarcodeOpen(false);
                      onChange({ readBarcode: event.target.checked });
                    }}
                  />
                </label>
                {scalar &&
                  item.readBarcode &&
                  (barcodeOpen ? (
                    <div className="setup-barcode-choice">
                      <label htmlFor={`${fieldControlId}-barcode-type`}>
                        Barcode type
                        <select
                          id={`${fieldControlId}-barcode-type`}
                          value={item.barcodeSymbology ?? ''}
                          onChange={(event) =>
                            onChange({ barcodeSymbology: event.target.value || null })
                          }
                        >
                          <option value="">Any type</option>
                          {Array.from(
                            new Set([
                              'Code39',
                              'Code93',
                              'Code128',
                              'QrCode',
                              'DataMatrix',
                              'Pdf417',
                              'Ean13',
                              'Ean8',
                              'UpcA',
                              'UpcE',
                              ...(item.barcodeSymbology ? [item.barcodeSymbology] : []),
                            ]),
                          ).map((type) => (
                            <option key={type} value={type}>
                              {friendlyBarcodeSymbology(type)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label htmlFor={`${fieldControlId}-barcode-prefix`}>
                        Starts with
                        <input
                          id={`${fieldControlId}-barcode-prefix`}
                          value={item.barcodeValuePrefix ?? ''}
                          onChange={(event) =>
                            onChange({ barcodeValuePrefix: event.target.value || null })
                          }
                          placeholder="e.g. INV-"
                        />
                      </label>
                      <button
                        type="button"
                        className="setup-link"
                        onClick={() => setBarcodeOpen(false)}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="setup-source-ghost"
                      aria-label={`Barcode type and start: ${barcodeSummary}`}
                      onClick={() => setBarcodeOpen(true)}
                    >
                      {barcodeSummary}
                    </button>
                  ))}
              </div>
              {scalar && (
                <div className="setup-source-group">
                  {/* On: open the lookup editor to choose the list. Off: read from the page again; the saved lookup stays. */}
                  <label className={`setup-source-row${fromList ? ' is-on' : ''}`}>
                    <Database size={16} aria-hidden="true" />
                    <span>From a list</span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={fromList}
                      onChange={(event) =>
                        event.target.checked ? onOpenJourney() : onChange({ lookups: null })
                      }
                    />
                  </label>
                  {fromList ? (
                    <p className="setup-source-sub">
                      {rule ? (
                        listSpelling ? (
                          <>
                            Uses the spelling in <strong>{rule.name}</strong>.
                          </>
                        ) : (
                          <>
                            Filled by <strong>{rule.name}</strong>, matched on {rule.findColumn}.
                          </>
                        )
                      ) : (
                        <>Filled from a list.</>
                      )}{' '}
                      <button type="button" className="setup-link" onClick={onOpenJourney}>
                        Edit lookup
                      </button>
                    </p>
                  ) : (
                    <p className="setup-source-sub">Fill or check from a list</p>
                  )}
                </div>
              )}
            </div>
          </div>
          {scalar && (
            <div className="setup-stop-block">
              <label className="setup-stop-label" htmlFor={`${fieldControlId}-hint`}>
                Reading hint
              </label>
              <textarea
                id={`${fieldControlId}-hint`}
                className="setup-hint"
                rows={2}
                value={item.description ?? ''}
                placeholder="What to look for, in your words"
                onChange={(event) => onChange({ description: event.target.value })}
              />
            </div>
          )}
          {scalar && sample?.value && (
            <div className="setup-stop-block setup-stop-end">
              {sample.previewPath && sample.box && (
                <>
                  <div className="setup-stop-labelrow">
                    <span className="setup-stop-label">On the page</span>
                    {sample.from && <span className="setup-stop-note">{sample.from}</span>}
                  </div>
                  <FieldOnPage previewPath={sample.previewPath} box={sample.box} />
                </>
              )}
              <p className="setup-stop-sample">
                <span className="setup-stop-label">Sample</span>
                <span className="setup-stop-sample-value" title={sample.value}>
                  {sample.value}
                </span>
              </p>
            </div>
          )}
        </section>
        <section className="setup-stop" aria-labelledby={`${fieldControlId}-check`}>
          <h4 id={`${fieldControlId}-check`}>
            <span className="setup-stop-number">2</span>Check it
          </h4>
          {scalar && (
            <fieldset className="setup-kind-choice" disabled={schemaLocked}>
              <legend className="setup-stop-label">
                Type
                <TooltipButton
                  type="button"
                  className="icon-button"
                  aria-label="About types"
                  tooltip="About types"
                  onClick={() => setValueTypeHelpOpen(true)}
                >
                  <Info size={15} />
                </TooltipButton>
              </legend>
              <div className="setup-kind-tiles">
                {ROUTE_COMMON_KINDS.map((kind) => (
                  <label
                    key={kind}
                    className={`setup-kind-tile${item.kind === kind ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`${fieldControlId}-kind`}
                      value={kind}
                      checked={item.kind === kind}
                      onChange={() => onChange({ kind })}
                    />
                    {item.kind === kind && (
                      <span className="setup-kind-check" aria-hidden="true">
                        <Check size={10} />
                      </span>
                    )}
                    {kind === 'number' ? (
                      <span className="setup-kind-glyph" aria-hidden="true">
                        123
                      </span>
                    ) : (
                      kindIconFor(kind, 17)
                    )}
                    <span>{kindName(kind)}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="setup-link"
                aria-expanded={moreKinds}
                onClick={() => setMoreKinds((open) => !open)}
              >
                More types
                <span className="setup-link-count">
                  {otherKinds.includes(item.kind) ? kindLabel[item.kind] : otherKinds.length}
                </span>
                <ChevronDown size={13} aria-hidden="true" />
              </button>
              {moreKinds && (
                <div className="setup-kind-more">
                  {otherKinds.map((kind) => (
                    <label
                      key={kind}
                      className={`setup-kind-chip${item.kind === kind ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`${fieldControlId}-kind`}
                        value={kind}
                        checked={item.kind === kind}
                        onChange={() => onChange({ kind })}
                      />
                      {kindLabel[kind]}
                    </label>
                  ))}
                </div>
              )}
              {schemaLocked && <small>Controlled by the connected app.</small>}
            </fieldset>
          )}
          {scalar && (
            <div className="setup-stop-block">
              <div className="setup-stop-labelrow">
                <label className="setup-stop-label" htmlFor={`${fieldControlId}-format`}>
                  Looks like
                </label>
                <span className="setup-stop-note">
                  Optional
                  <TooltipButton
                    type="button"
                    className="icon-button"
                    aria-label="About looks like"
                    tooltip="About looks like"
                    onClick={() => setFormatHelpOpen(true)}
                  >
                    <Info size={14} />
                  </TooltipButton>
                </span>
              </div>
              <input
                id={`${fieldControlId}-format`}
                value={item.formatExample ?? ''}
                aria-describedby={
                  invalidPatternExample ? `${fieldControlId}-format-help` : undefined
                }
                onChange={(event) => {
                  const formatExample = event.target.value;
                  onChange({
                    formatExample: formatExample || null,
                    valuePattern: valuePatternFromExample(formatExample),
                  });
                }}
                placeholder="Type an example"
              />
              {invalidPatternExample ? (
                <small id={`${fieldControlId}-format-help`} className="setup-stop-error">
                  Use letters and digits, such as ABC-12345. This example does not create a check.
                </small>
              ) : (
                <span className="setup-stop-note">Other shape → review</span>
              )}
            </div>
          )}
          <div className="setup-stop-foot">
            <div className="setup-toggle">
              <label htmlFor={`${fieldControlId}-required`}>Required</label>
              <TooltipButton
                type="button"
                className="icon-button"
                aria-label="About required fields"
                tooltip="About required"
                onClick={() => setRequiredHelpOpen(true)}
              >
                <Info size={14} />
              </TooltipButton>
              <input
                id={`${fieldControlId}-required`}
                type="checkbox"
                role="switch"
                checked={item.required}
                disabled={schemaLocked}
                onChange={(event) => onChange({ required: event.target.checked })}
              />
            </div>
            <span className="setup-stop-note">missing → review</span>
          </div>
        </section>
        <section className="setup-stop" aria-labelledby={`${fieldControlId}-decide`}>
          <h4 id={`${fieldControlId}-decide`}>
            <span className="setup-stop-number">3</span>Decide
          </h4>
          {scalar ? (
            <div className="field-review-bar setup-decide">
              <div className="setup-stop-block">
                <div className="setup-stop-label">Review threshold</div>
                <div
                  className="setup-segmented"
                  role="radiogroup"
                  aria-label={`${item.name} threshold source`}
                >
                  <label className={!customReview ? 'is-selected' : ''}>
                    <input
                      type="radio"
                      name={`${fieldControlId}-review-mode`}
                      checked={!customReview}
                      onChange={() => onChange({ reviewBelow: null, reviewBelowSource: null })}
                    />
                    Job · {Math.round(reviewDefault * 100)}
                  </label>
                  <label className={customReview ? 'is-selected' : ''}>
                    <input
                      type="radio"
                      name={`${fieldControlId}-review-mode`}
                      checked={customReview}
                      onChange={() =>
                        onChange({ reviewBelow: internalBar, reviewBelowSource: 'operator' })
                      }
                    />
                    Own
                  </label>
                </div>
              </div>
              <div className="setup-decide-value">
                <span>Review below</span>
                <strong>{barOff ? 'Off' : shownBar}</strong>
              </div>
              <ReviewThresholdChart
                value={internalBar}
                histogram={impact.kind === 'measured' ? impact.histogram : undefined}
                disabled={!customReview}
                label={`${item.name} review threshold`}
                onChange={(reviewBelow) => onChange({ reviewBelow, reviewBelowSource: 'operator' })}
              />
              <p
                className={`review-impact ${impact.kind === 'measured' ? '' : 'unknown'}`}
                aria-description={FIELD_REVIEW_IMPACT_SCOPE}
              >
                {barOff
                  ? 'Never asks on confidence alone. Hard problems (required and empty, nothing on the page behind it) still ask.'
                  : fieldReviewImpactText(impact, internalBar)}
              </p>
              {customReview && item.reviewBelowSource === 'auto' && (
                <small className="setup-stop-note">
                  Set automatically by an older version. Choose Job to use the job threshold.
                </small>
              )}
              {onOpenThresholds && (
                <div className="setup-stop-foot">
                  <button type="button" className="setup-link" onClick={onOpenThresholds}>
                    Job review thresholds
                    <ArrowRight size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="setup-stop-note">
              Tables are checked row by row when a document is reviewed.
            </p>
          )}
        </section>
        <section className="setup-stop is-result" aria-labelledby={`${fieldControlId}-result`}>
          <h4 id={`${fieldControlId}-result`}>
            <span className="setup-stop-number">4</span>Result
          </h4>
          {scalar && sample?.value && (
            <div className={`setup-sample${sample.needsReview ? ' is-review' : ''}`}>
              <span className="setup-sample-head">
                {sample.needsReview ? (
                  <TriangleAlert size={13} aria-hidden="true" />
                ) : (
                  <Check size={13} aria-hidden="true" />
                )}
                On this sample
              </span>
              <FitText className="setup-sample-value" text={sample.value} />
              <span className="setup-sample-note">
                {sample.confidence != null
                  ? `Confidence ${Math.round(sample.confidence * 100)} · `
                  : ''}
                {sample.needsReview ? 'asks for review' : 'no review'}
              </span>
            </div>
          )}
          {scalar && (
            <div className="setup-stop-block">
              <div className="setup-stop-labelrow">
                <label className="setup-stop-label" htmlFor={`${fieldControlId}-default`}>
                  If empty, use
                </label>
                <span className="setup-stop-note">
                  Optional
                  <TooltipButton
                    type="button"
                    className="icon-button"
                    aria-label="About if empty, use"
                    tooltip="About if empty, use"
                    onClick={() => setDefaultValueHelpOpen(true)}
                  >
                    <Info size={14} />
                  </TooltipButton>
                </span>
              </div>
              <input
                id={`${fieldControlId}-default`}
                value={item.defaultValue ?? ''}
                maxLength={4096}
                onChange={(event) => onChange({ defaultValue: event.target.value || null })}
                placeholder="Leave empty"
              />
            </div>
          )}
          <div className="setup-stop-block setup-stop-end">
            {(item.learnedFromCount ?? 0) > 0 && (
              <p className="setup-stop-learned">
                <GraduationCap size={15} aria-hidden="true" />
                <span>
                  Learned from <strong>{item.learnedFromCount?.toLocaleString()}</strong>{' '}
                  {item.learnedFromCount === 1 ? 'confirmation' : 'confirmations'}
                </span>
              </p>
            )}
            {onResetLearning && (
              <button type="button" className="setup-quiet" onClick={onResetLearning}>
                <RotateCw size={15} /> Reset learning
              </button>
            )}
          </div>
          {!schemaLocked && (
            <div className="setup-stop-foot setup-field-actions">
              {confirmingDelete ? (
                // One click must not delete configuration. The confirm names the
                // field and says what removal means, in place, instead of a modal.
                <div
                  className="delete-confirm-row"
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      event.stopPropagation();
                      showRemovalConfirmation(false);
                    }
                  }}
                >
                  <span>
                    Remove <strong>{item.name}</strong>? Future documents stop reading it. Values
                    already captured stay on their documents.
                  </span>
                  <button className="delete-button" onClick={onDelete}>
                    <Trash2 size={14} /> Remove
                  </button>
                  <button
                    ref={keepFieldButtonRef}
                    className="keep-button"
                    onClick={() => showRemovalConfirmation(false)}
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <button
                  ref={removeFieldButtonRef}
                  className="delete-button setup-quiet is-danger"
                  onClick={() => showRemovalConfirmation(true)}
                >
                  <Trash2 size={15} /> Remove field
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

/** How Automatic treats each source (ADR-0091), in one line under the Document starts choices. */
const AUTOMATIC_SOURCE_NOTE =
  'Automatic keeps imported files whole and finds documents inside scans.';

function SeparationCard({
  job,
  onChange,
  onOpenThresholds,
}: {
  job: Job;
  onChange: (job: Job) => void;
  onOpenThresholds?: () => void;
}) {
  const choiceId = useId();
  const choices = [
    {
      mode: SEPARATION.automatic,
      title: 'Automatically',
      description: 'Paige decides which pages belong together.',
    },
    {
      mode: SEPARATION.existingBarcode,
      title: 'At matching barcodes',
      description: 'Start a document at every barcode that matches your value or prefix.',
    },
    {
      mode: SEPARATION.automaticBarcode,
      title: 'Automatic + matching barcodes',
      description: 'Start a document at every matching barcode. Paige also finds other starts.',
    },
    {
      mode: SEPARATION.capturePointSeparator,
      title: 'Only at break sheets',
      description: 'Keep pages together until a Paige break sheet appears.',
    },
    {
      mode: SEPARATION.filePerDocument,
      title: 'Each file is one document',
      description: 'Every PDF, TIFF or image becomes one document. Nothing splits it.',
    },
  ];
  const wholeFiles = job.separationMode === SEPARATION.filePerDocument;
  const barcodeHelp = job.separationBarcodeText?.trim()
    ? 'For example, DOC- matches DOC-001 and DOC-002.'
    : 'Enter the barcode value or prefix before saving.';
  // Each choice is a card: its name, one line, and a strip of pages cut where it starts documents.
  // The picture is a second label for the same radio, so the whole card chooses it.
  return (
    <section className="setup-document-starts" aria-labelledby="setup-document-starts-title">
      <h2 id="setup-document-starts-title" tabIndex={-1}>
        <Scissors size={22} />
        Document starts
      </h2>
      <fieldset className="boundary-choices" aria-describedby={`${choiceId}-sheets`}>
        <legend>How should documents start?</legend>
        {choices.map(({ mode, title, description }) => {
          const selected = job.separationMode === mode;
          const barcode =
            mode === SEPARATION.existingBarcode || mode === SEPARATION.automaticBarcode;
          const inputId = `${choiceId}-${mode}-input`;
          return (
            <div
              key={mode}
              className={`boundary-choice${selected ? ' selected' : ''}${selected && barcode ? ' has-barcode' : ''}`}
              data-mode={mode}
            >
              <label className="boundary-choice-label">
                <input
                  id={inputId}
                  type="radio"
                  name={choiceId}
                  value={mode}
                  checked={selected}
                  aria-labelledby={`${choiceId}-${mode}-title`}
                  aria-describedby={`${choiceId}-${mode}-description`}
                  onChange={() => onChange({ ...job, separationMode: mode })}
                />
                <span>
                  <strong id={`${choiceId}-${mode}-title`}>{title}</strong>
                  <small id={`${choiceId}-${mode}-description`}>{description}</small>
                </span>
              </label>
              {/* The barcode box sits on the chosen card's title line, so choosing a barcode option never resizes the pictures. */}
              {selected && barcode && (
                <label className="separator-barcode-rule">
                  <Barcode size={16} aria-hidden="true" />
                  <span className="visually-hidden">Barcode value or prefix</span>
                  <input
                    value={job.separationBarcodeText ?? ''}
                    onChange={(event) =>
                      onChange({ ...job, separationBarcodeText: event.target.value })
                    }
                    placeholder="Value or prefix, e.g. DOC-"
                    aria-invalid={!job.separationBarcodeText?.trim()}
                    aria-describedby={`${choiceId}-barcode-help`}
                  />
                </label>
              )}
              {selected && barcode && (
                <small className="visually-hidden" id={`${choiceId}-barcode-help`}>
                  {barcodeHelp}
                </small>
              )}
              <label className="boundary-choice-art" htmlFor={inputId}>
                <DocumentStartsArt mode={mode} />
              </label>
            </div>
          );
        })}
      </fieldset>
      <p className="setup-starts-note" id={`${choiceId}-sheets`}>
        {wholeFiles
          ? 'Break sheets inside a file are taken out but do not split it.'
          : 'Paige break sheets work with every option.'}
      </p>
      {/* ADR-0091: Automatic is source-aware. One line; the job learns which source does what. */}
      {(job.separationMode === SEPARATION.automatic ||
        job.separationMode === SEPARATION.automaticBarcode) && (
        <p className="setup-starts-note setup-starts-source">{AUTOMATIC_SOURCE_NOTE}</p>
      )}
      {onOpenThresholds && (
        <section
          className="setup-starts-confidence"
          aria-labelledby={`${choiceId}-confidence-title`}
        >
          <div>
            <h3 id={`${choiceId}-confidence-title`}>Want fewer requests to review?</h3>
            <p>Adjust how certain Cortexa must be about where each document starts.</p>
          </div>
          <button type="button" className="quiet-button small" onClick={onOpenThresholds}>
            Adjust review thresholds
            <ArrowRight size={14} />
          </button>
        </section>
      )}
    </section>
  );
}

function SetupHelp({ kind }: { kind: 'review' | 'learning' }) {
  const [open, setOpen] = useState(false);
  const review = kind === 'review';
  const title = review ? 'About review thresholds' : 'When to pause suggestions';
  return (
    <>
      <TooltipButton
        type="button"
        className="setup-help-trigger"
        aria-label={title}
        tooltip={title}
        onClick={() => setOpen(true)}
      >
        <Info size={18} />
      </TooltipButton>
      {open && (
        <WorkspaceDialog
          telemetry={kind === 'review' ? 'help-review' : 'help-learning'}
          title={title}
          onClose={() => setOpen(false)}
          compact
        >
          <div className="setup-explainer">
            {review ? (
              <>
                <p>
                  These bars decide when Paige asks for your attention. They do not change the value
                  it reads or confirm anything for you.
                </p>
                <section>
                  <h3>Pilot mode decides what happens next</h3>
                  <p>
                    With Manual, a document below a bar asks for review. With Autopilot, Cortexa
                    checks it against the page, then it goes out, flagged with the reason when
                    Cortexa can’t settle it.
                  </p>
                </section>
                <section>
                  <h3>Higher means more checking</h3>
                  <p>
                    Raise a bar to require more confidence before an answer passes without review.
                    This gives you more opportunities to catch mistakes and teach the job.
                  </p>
                </section>
                <section>
                  <h3>Lower means fewer interruptions</h3>
                  <p>
                    Lower a bar when you are comfortable with the results. More answers can pass
                    without a check, so an incorrect answer may be easier to miss. Missing required
                    values still ask for review.
                  </p>
                </section>
                <section>
                  <h3>You can change your mind</h3>
                  <p>
                    Start with the current settings. Check a few documents, then adjust gradually.
                    Each dot is a document read so far; the amber ones would be affected. Individual
                    fields can have their own bar.
                  </p>
                </section>
              </>
            ) : (
              <>
                <p>
                  Once your job reads the documents you need reliably and quickly, you may want to
                  keep its setup settled.
                </p>
                <section>
                  <h3>Keep the setup you like</h3>
                  <p>
                    Pause suggestions when you no longer want recommendations for new document types
                    or fields. Pause either area, or both. Your existing types, fields, and learned
                    patterns stay in place.
                  </p>
                </section>
                <section>
                  <h3>Your teaching still matters</h3>
                  <p>
                    This pauses additions to the setup, not all learning. Reading, review, and other
                    improvements continue. Your confirmations and corrections still help the job
                    improve.
                  </p>
                </section>
                <section>
                  <h3>Resume when your work changes</h3>
                  <p>
                    If new types of documents arrive or you need more information captured, turn
                    suggestions back on. You can also make changes yourself in Job setup.
                  </p>
                </section>
              </>
            )}
          </div>
        </WorkspaceDialog>
      )}
    </>
  );
}

// The engine's measured defaults, mirrored so the sliders show real positions
// before a job has ever saved a policy. ReviewPolicy in Contracts.cs owns the
// figures; these restate them for display only.

/** Names the reader uses when it has not settled on a kind. Never offered as a choice. */
const DocumentStoreIsNonAnswer = (name: string) =>
  /^(unclassified|unknown|document|other|n\/a|none)\b/i.test(name.trim());

/** Room below the last thumbnail, and scroll padding for a selected one, so the
 * floating Add pages pill never covers a page the list brings into view. */
const SPINE_FLOATING_ADD_CLEARANCE = 60;

/** PAIGE: the review bar for the setup overview, from the bars in force (paige/review-policy-summary.ts). */
const reviewBarLabel = (target: Job) =>
  reviewPolicySummary(target.documentTypes, target.reviewPolicy, REVIEW_POLICY_DEFAULTS.fields);

/** The review settings a release preview depends on: the policy and each field's own bar. */
const reviewSettingsKey = (job: Job) =>
  JSON.stringify([
    job.id,
    job.reviewPolicy ?? null,
    job.documentTypes.map((type) => [
      type.id,
      type.fields.map((field) => [field.id, field.reviewBelow ?? null]),
    ]),
  ]);

/**
 * What a review setting would do to the latest batch, asked of the engine
 * (DocumentStore.PreviewReviewRelease, which runs ReviewState itself), so the screen and
 * the queue cannot disagree. Debounced while a slider moves; a reply for an older draft or
 * another job is dropped.
 */
function useReviewReleasePreview(job: Job): {
  preview: ReviewReleasePreview | null;
  settled: boolean;
} {
  const key = reviewSettingsKey(job);
  const [read, setRead] = useState<{
    key: string;
    jobId: string;
    preview: ReviewReleasePreview | null;
  } | null>(null);
  const draft = useRef(job);
  draft.current = job;
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.previewReviewRelease) {
      setRead({ key, jobId: job.id, preview: null });
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      void api
        .previewReviewRelease(job.id, draft.current as StoredJobDefinition)
        .then((preview) => {
          if (live) setRead({ key, jobId: job.id, preview });
        })
        .catch(() => {
          if (live) setRead({ key, jobId: job.id, preview: null });
        });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [key]);
  if (read?.jobId !== job.id) return { preview: null, settled: false };
  return { preview: read.preview, settled: read.key === key };
}

/**
 * What happens to a document the checks catch, in the words of its pilot mode (owner,
 * 2026-10-03: the field Decide card's "asks for review", never "wait" or "flagged"). Keyed by the
 * mode in force (effectiveProcessingMode): "straight-through" is Autopilot on a computer that
 * cannot run the AI check, not a mode anyone chooses.
 */
const CAUGHT_WORDS: Record<string, { one: string; many: string }> = {
  'human-reviewed': { one: 'asks for review', many: 'ask for review' },
  'ai-reviewed': { one: 'gets a closer look', many: 'get a closer look' },
  'straight-through': { one: 'leaves with a note', many: 'leave with a note' },
};
/** The same, short enough for the label inside a chart's caught zone. */
const ZONE_WORDS: Record<string, string> = {
  'human-reviewed': 'asks for review',
  'ai-reviewed': 'closer look',
  'straight-through': 'leaves with a note',
};
/** What happens to a document the checks let through, in the same words. */
const STRAIGHT_WORDS: Record<string, { one: string; many: string }> = {
  'human-reviewed': { one: 'goes without review', many: 'go without review' },
  'ai-reviewed': { one: 'goes straight out', many: 'go straight out' },
  'straight-through': { one: 'leaves clean', many: 'leave clean' },
};
const agree = (words: { one: string; many: string }, count: number) =>
  count === 1 ? words.one : words.many;
const caughtWords = (mode: string, count: number) =>
  agree(CAUGHT_WORDS[mode] ?? CAUGHT_WORDS['human-reviewed'], count);
const straightWords = (mode: string, count: number) =>
  agree(STRAIGHT_WORDS[mode] ?? STRAIGHT_WORDS['human-reviewed'], count);

/** The note Autopilot adds to a caught document's data file (a PDF-only folder export: the PDF's properties) where it goes out unchecked, word for word (ReviewState.ExportedWithoutReview). */
const EXPORT_EXCEPTION_NOTE =
  'Exported without review: Paige would have held this document for a check.';

/** "What the note says": the real note, so "leave with a note" is never vague (owner, 2026-10-03). */
function ExportNoteExample() {
  return (
    <details className="thresholds-note-example">
      <summary>
        <FileText size={14} aria-hidden="true" />
        What the note says
        <ChevronDown size={14} aria-hidden="true" />
      </summary>
      <div className="thresholds-note-pop" role="note">
        <p>
          Each document that leaves with a note carries this in its data file (or the PDF’s
          properties, with PDF only):
        </p>
        <pre>{`"exportedWithExceptions": true,\n"exceptions": [\n  "${EXPORT_EXCEPTION_NOTE}"\n]`}</pre>
        <p>
          An empty required value or a line that doesn’t add up is noted the same way, in its own
          words. Folder exports only.
        </p>
      </div>
    </details>
  );
}

/** Where this job exports, from its saved export settings: a folder unless it is Content Central. */
function useExportDestination(jobId: string): 'filesystem' | 'content-central' | null {
  const [read, setRead] = useState<{
    jobId: string;
    kind: 'filesystem' | 'content-central';
  } | null>(null);
  useEffect(() => {
    let live = true;
    const api = window.capturePointDesktop;
    if (!api?.getExportSettings) {
      setRead({ jobId, kind: 'filesystem' });
      return;
    }
    void api
      .getExportSettings(jobId)
      .then((settings) => {
        if (live) setRead({ jobId, kind: settings?.destinationKind ?? 'filesystem' });
      })
      .catch(() => {
        if (live) setRead({ jobId, kind: 'filesystem' });
      });
    return () => {
      live = false;
    };
  }, [jobId]);
  return read?.jobId === jobId ? read.kind : null;
}

/**
 * The latest batch under this setting, in documents: the one thing this screen is for. It says
 * what the draft would do, never how that differs from what is saved (owner, 2026-10-03). On
 * Autopilot what a bar catches leaves without a person too, so the caught ones are counted apart
 * from the ones that go straight out, and the two add up; what can't be exported yet (not read,
 * no document type) is counted on its own.
 */
function ReviewReleaseSummary({
  preview,
  settled,
}: {
  preview: ReviewReleasePreview | null;
  settled: boolean;
}) {
  if (!preview || preview.documents === 0)
    return (
      <section className="thresholds-release is-empty" aria-label="Latest batch">
        <p>
          {settled
            ? 'What these settings would do appears after documents are read.'
            : 'Checking the latest batch…'}
        </p>
      </section>
    );
  const automatic = preview.mode !== 'human-reviewed',
    notes = preview.mode === 'straight-through';
  const caught = automatic ? preview.flagged : preview.waitForPerson;
  const straight = automatic
    ? Math.max(0, preview.goWithoutReview - preview.flagged)
    : preview.goWithoutReview;
  const notRead = preview.cannotExportNotRead ?? 0,
    noType = preview.cannotExportNoType ?? 0;
  // Autopilot's exceptions folder (ADR-0096): the documents that will go there, counted apart from the rest, and
  // those of the batch that already went, said once in the basis line.
  const toExceptions = automatic ? (preview.toExceptions ?? 0) : 0,
    sentToExceptions = automatic ? (preview.sentToExceptions ?? 0) : 0;
  // A learned junk shape deletes on both modes; its deletes are counted beside them (Deleted documents lists them).
  const deletedAutomatically = preview.deletedAutomatically ?? 0;
  return (
    <section
      className={`thresholds-release${settled ? '' : ' is-updating'}`}
      aria-label="Latest batch"
      aria-live="polite"
      aria-busy={!settled}
    >
      <div className="thresholds-release-row">
        <span className="is-go">
          <strong>{straight.toLocaleString()}</strong> {straightWords(preview.mode, straight)}
        </span>
        <span className={notes ? 'is-note' : 'is-wait'}>
          <strong>{caught.toLocaleString()}</strong> {caughtWords(preview.mode, caught)}
          {preview.mode === 'ai-reviewed' ? ' first' : ''}
        </span>
        {toExceptions > 0 && (
          <span className="is-exceptions">
            <strong>{toExceptions.toLocaleString()}</strong> {toExceptions === 1 ? 'goes' : 'go'} to
            exceptions
          </span>
        )}
        {notRead + noType > 0 && (
          <span
            className="is-blocked"
            title={
              notRead > 0
                ? `Paige reads ${notRead === 1 ? 'it' : 'them'} again on its own.`
                : undefined
            }
          >
            <strong>{(notRead + noType).toLocaleString()}</strong> can’t be exported yet{' '}
            <small>
              {[
                notRead > 0 && `${notRead.toLocaleString()} not read`,
                noType > 0 && `${noType.toLocaleString()} no document type`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </small>
          </span>
        )}
        <small className="thresholds-release-basis">
          Latest batch of {preview.documents.toLocaleString()}
          {preview.alreadyConfirmed > 0 &&
            ` · ${preview.alreadyConfirmed.toLocaleString()} already checked aren’t counted`}
          {sentToExceptions > 0 && ` · ${sentToExceptions.toLocaleString()} went to exceptions`}
          {deletedAutomatically > 0 &&
            ` · ${deletedAutomatically.toLocaleString()} deleted automatically`}
        </small>
      </div>
      <div className="thresholds-release-bar" aria-hidden="true">
        <span className="is-go" style={{ flexGrow: straight }} />
        <span className={notes ? 'is-note' : 'is-wait'} style={{ flexGrow: caught }} />
        <span className="is-exceptions" style={{ flexGrow: toExceptions }} />
        <span className="is-blocked" style={{ flexGrow: notRead + noType }} />
      </div>
    </section>
  );
}

/**
 * A review check's On/Off switch. Declared here, not inside ReviewPolicyCard: a component
 * declared in a render is a new type each time, so the switch remounted and lost keyboard
 * focus on every toggle and every preview reply.
 */
function ReviewRuleSwitch({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="quiet-button review-rule-toggle"
      role="switch"
      aria-label={label}
      aria-checked={on}
      onClick={onToggle}
    >
      <span className="review-mode-switch" aria-hidden="true">
        <span />
      </span>
      {on ? 'On' : 'Off'}
    </button>
  );
}

/**
 * Review thresholds asks two questions (owner decisions, 2026-10-03). First Pilot mode: when
 * Paige is unsure. Then what counts as unsure: the latest batch, and each check as a 0–100
 * dot chart of the batch with its bar (the field Decide card's picture), beside the two checks
 * that have no bar. Where Autopilot sends documents out unchecked (a computer that cannot run the AI
 * check) nothing below a bar is held, so the second question becomes which documents leave with a note, drawn in
 * the note colour; a job exporting to Content Central gets no note, so there it is one line instead.
 * Only these checks decide (owner decision, 2026-09-25).
 */
function ReviewPolicyCard({
  job,
  onChange,
  onOpenField,
  onOpenStarts,
}: {
  job: Job;
  onChange: (job: Job) => void;
  onOpenField?: (typeId: string, fieldId: string) => void;
  onOpenStarts?: () => void;
}) {
  const release = useReviewReleasePreview(job);
  // Undefined until the check answers: the card follows the chosen mode meanwhile.
  const aiReviewAvailable = useAiReviewAnswer();
  const destination = useExportDestination(job.id);
  // PAIGE: Paige's cloud always runs the AI reviewer (the cell's reasoner), so the card follows the chosen mode
  // and never draws the desktop's unchecked Autopilot ("leaves with a note", "On this computer…"), even when
  // the answer is no (Dustin, 2026-10-20; AutopilotChoices' MODE_SAYS likewise). Before it answers, CapturePoint's
  // own undefined (73e1bdac) already follows the mode.
  const autopilot = hostHas('local-performance')
    ? effectiveProcessingMode(job.reviewPolicy?.processingMode, aiReviewAvailable)
    : pilotMode(job.reviewPolicy?.processingMode);
  const notes = autopilot === 'straight-through';
  const policy = job.reviewPolicy ?? REVIEW_POLICY_DEFAULTS;
  const fieldScope = fieldReviewPolicyScope(job.documentTypes);
  const fieldTargets = new Map<string, { typeId: string; fieldId: string }>(
    job.documentTypes.flatMap((type) =>
      type.fields.map(
        (field) => [`${type.id}:${field.id}`, { typeId: type.id, fieldId: field.id }] as const,
      ),
    ),
  );
  type ConfidenceSwitch = 'documentTypeEnabled' | 'fieldsEnabled' | 'boundaryEnabled';
  const rows: Array<{
    key: 'documentType' | 'fields' | 'boundary';
    enabledKey: ConfidenceSwitch;
    label: string;
    icon: ReactNode;
    note: string;
  }> = [
    {
      key: 'documentType',
      enabledKey: 'documentTypeEnabled',
      label: 'Document type',
      icon: <FileType size={18} />,
      note: 'Confirmed and learned types aren’t drawn',
    },
    {
      key: 'fields',
      enabledKey: 'fieldsEnabled',
      label: 'Field values',
      icon: <TextCursorInput size={18} />,
      note: '0 = read with a problem',
    },
    {
      key: 'boundary',
      enabledKey: 'boundaryEnabled',
      label: 'Document starts',
      icon: <Scissors size={18} />,
      note: 'Confirmed starts aren’t drawn',
    },
  ];
  const preview = release.preview;
  const checks = preview?.checks ?? null;
  const documents = preview?.documents ?? 0;
  const caught = (count: number) => caughtWords(autopilot, count);
  const never = notes
    ? 'Never adds a note'
    : autopilot === 'ai-reviewed'
      ? 'Never sends for a closer look'
      : 'Never asks for review';
  const firstRequired =
    job.documentTypes.flatMap((type) =>
      type.fields
        .filter((field) => field.required && field.kind !== 'line-items')
        .map((field) => ({ typeId: type.id, fieldId: field.id })),
    )[0] ??
    job.documentTypes.flatMap((type) =>
      type.fields.map((field) => ({ typeId: type.id, fieldId: field.id })),
    )[0];
  const arithmetic = job.reviewPolicy?.lineItemArithmeticEnabled !== false;
  // Fields with their own bar, marked on the Field values chart; one mark per bar value.
  const ownBars = new Map<number, typeof fieldScope.overrides>();
  for (const field of fieldScope.overrides)
    ownBars.set(field.sensitivity, [...(ownBars.get(field.sensitivity) ?? []), field]);
  const marks = [...ownBars].map(([value, fields]) => ({
    value: value / 100,
    label: `${fields[0].field}${fields.length > 1 ? ` +${fields.length - 1}` : ''} ${value}`,
    title: fields.map((field) => field.field).join(', '),
  }));
  const ofDocuments = `of ${documents.toLocaleString()} ${documents === 1 ? 'document' : 'documents'}`;
  return (
    <section className={`panel review-policy-card thresholds-card${notes ? ' is-notes' : ''}`}>
      <header className="thresholds-head">
        <span className="section-icon">
          <SlidersHorizontal size={17} />
        </span>
        <div>
          <h3 className="setup-heading-with-help">
            Review thresholds
            <SetupHelp kind="review" />
          </h3>
        </div>
      </header>
      <AutopilotChoices
        mode={job.reviewPolicy?.processingMode ?? 'human-reviewed'}
        aiReviewAvailable={aiReviewAvailable}
        calibration={{
          jobId: job.id,
          calibrating: isCalibrating(job.learningStance),
          sample: Boolean(sampleSetOfJob(job.id)) || isSampleJob(job),
        }}
        onMode={(mode) => onChange({ ...job, reviewPolicy: { ...policy, processingMode: mode } })}
      />
      {notes && destination === 'content-central' ? (
        <section className="thresholds-part" aria-label="No notes in Content Central">
          <div className="thresholds-cc">
            <span className="thresholds-cc-icon">
              <Database size={18} aria-hidden="true" />
            </span>
            <p>
              <b>
                On this computer Autopilot sends every document to Content Central as soon as it’s
                read, or to the exceptions folder if it can’t be filed.
              </b>{' '}
              Nothing asks for review and no notes are added, so there’s nothing to set here. Choose
              Manual to use these bars.
            </p>
          </div>
        </section>
      ) : (
        <section className="thresholds-part" aria-labelledby="thresholds-part-title">
          <div className="setup-part-head">
            <b id="thresholds-part-title">
              {notes ? 'Which documents leave with a note' : 'What counts as unsure'}
            </b>
            {notes && (
              <span>
                The note goes in the document’s data file (or the PDF’s properties, with PDF only),
                for whoever checks your exports later.
              </span>
            )}
            {notes && <ExportNoteExample />}
          </div>
          <ReviewReleaseSummary preview={release.preview} settled={release.settled} />
          <div className="thresholds-checks">
            {rows.map(({ key, enabledKey, label, icon, note }) => {
              const enabled = job.reviewPolicy?.[enabledKey] !== false;
              // The same documents as the headline: the engine's preview, never a separate profile.
              const histogram = checks
                ? ((key === 'documentType'
                    ? checks.typeHistogram
                    : key === 'boundary'
                      ? checks.boundaryHistogram
                      : checks.fieldsHistogram) ?? null)
                : null;
              const count = checks
                ? checks[
                    key === 'documentType'
                      ? 'documentType'
                      : key === 'boundary'
                        ? 'boundary'
                        : 'fields'
                  ]
                : null;
              const threshold = Math.round(policy[key] * 100);
              // Held only by a field's own bar: drawn at or above the job's line, and not a problem (0).
              const held =
                key === 'fields' && release.settled ? (checks?.fieldsHeldHistogram ?? null) : null;
              const ownHeld = held
                ? held.reduce(
                    (sum, documents, score) =>
                      sum + (score >= threshold && score > 0 ? documents : 0),
                    0,
                  )
                : 0;
              return (
                <section
                  key={key}
                  className={`thresholds-check${enabled ? '' : ' is-off'}`}
                  aria-label={label}
                >
                  <div className="review-rule-heading">
                    <strong>
                      {icon}
                      {label}
                    </strong>
                    <ReviewRuleSwitch
                      label={`${label} review check`}
                      on={enabled}
                      onToggle={() =>
                        onChange({ ...job, reviewPolicy: { ...policy, [enabledKey]: !enabled } })
                      }
                    />
                  </div>
                  <JobThresholdChart
                    value={policy[key]}
                    histogram={histogram}
                    held={held}
                    marks={key === 'fields' ? marks : []}
                    disabled={!enabled}
                    tone={notes ? 'note' : 'review'}
                    label={`${label} review threshold`}
                    zoneLabel={ZONE_WORDS[autopilot] ?? ZONE_WORDS['human-reviewed']}
                    onChange={(value) =>
                      onChange({ ...job, reviewPolicy: { ...policy, [key]: value } })
                    }
                  />
                  <div className="thresholds-check-foot">
                    {count === null ? (
                      <>
                        <b className="is-none">–</b>
                        <span>No documents yet</span>
                      </>
                    ) : enabled ? (
                      <>
                        <b>{count.toLocaleString()}</b>
                        <span>
                          {caught(count)}
                          <small>
                            {ownHeld > 0
                              ? `${ownHeld.toLocaleString()} because of a field’s own bar`
                              : ofDocuments}
                          </small>
                        </span>
                      </>
                    ) : (
                      <>
                        <b className="is-none">Off</b>
                        <span>
                          {never}
                          <small>
                            On at {threshold}: {count.toLocaleString()} {caught(count)}
                          </small>
                        </span>
                      </>
                    )}
                  </div>
                  <div className="thresholds-check-extra">
                    <span>{note}</span>
                    {key === 'fields' && fieldScope.overrides.length > 0 && (
                      <details className="review-policy-overrides">
                        <summary>
                          {fieldScope.overrides.length} own{' '}
                          {fieldScope.overrides.length === 1 ? 'bar' : 'bars'}
                          <ChevronDown size={14} aria-hidden="true" />
                        </summary>
                        <ul>
                          {fieldScope.overrides.map((field) => {
                            const target = fieldTargets.get(field.key);
                            return (
                              <li key={field.key}>
                                <span>
                                  <strong>{field.field}</strong>
                                  <small>
                                    {field.documentType} ·{' '}
                                    {field.automatic
                                      ? 'Automatically adjusted'
                                      : 'Set for this field'}
                                  </small>
                                </span>
                                <span className="setup-threshold-value">{field.sensitivity}</span>
                                {onOpenField && target && (
                                  <button
                                    type="button"
                                    className="quiet-button small"
                                    aria-label={`Open ${field.field} in ${field.documentType}`}
                                    onClick={() => onOpenField(target.typeId, target.fieldId)}
                                  >
                                    Open
                                    <ArrowRight size={14} />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    )}
                    {key === 'boundary' && onOpenStarts && (
                      <button
                        type="button"
                        className="quiet-button small setup-context-link"
                        onClick={onOpenStarts}
                      >
                        See starts
                        <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
            <div className="thresholds-also">
              <span className="thresholds-also-label">Also</span>
              <section className="thresholds-check is-plain" aria-label="Required, empty">
                <div className="review-rule-heading">
                  <strong>
                    <Asterisk size={18} />
                    Required, empty
                  </strong>
                  <span className="thresholds-always">Always</span>
                </div>
                <div className="thresholds-required-picture" aria-hidden="true">
                  <small>
                    Invoice date <i>*</i>
                  </small>
                  <span>empty</span>
                </div>
                <div className="thresholds-check-foot">
                  {checks ? (
                    <>
                      <b>{checks.required.toLocaleString()}</b>
                      <span>
                        {caught(checks.required)}
                        <small>of {documents.toLocaleString()}</small>
                      </span>
                    </>
                  ) : (
                    <>
                      <b className="is-none">–</b>
                      <span>No documents yet</span>
                    </>
                  )}
                  {onOpenField && firstRequired && (
                    <button
                      type="button"
                      className="quiet-button small setup-context-link"
                      title="Required is set per field"
                      onClick={() => onOpenField(firstRequired.typeId, firstRequired.fieldId)}
                    >
                      Fields
                      <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </section>
              <section
                className={`thresholds-check is-plain${arithmetic ? '' : ' is-off'}`}
                aria-label="Line-item arithmetic"
              >
                <div className="review-rule-heading">
                  <strong>
                    <Calculator size={18} />
                    Line-item arithmetic
                  </strong>
                  <ReviewRuleSwitch
                    label="Line-item arithmetic review check"
                    on={arithmetic}
                    onToggle={() =>
                      onChange({
                        ...job,
                        reviewPolicy: { ...policy, lineItemArithmeticEnabled: !arithmetic },
                      })
                    }
                  />
                </div>
                <div className="thresholds-sum" aria-hidden="true">
                  <span>3</span>
                  <i>×</i>
                  <span>2.00</span>
                  <i>=</i>
                  <s>9.00</s>
                  <X size={14} />
                </div>
                <div className="thresholds-check-foot">
                  {!checks ? (
                    <>
                      <b className="is-none">–</b>
                      <span>No documents yet</span>
                    </>
                  ) : arithmetic ? (
                    <>
                      <b>{checks.arithmetic.toLocaleString()}</b>
                      <span>
                        {caught(checks.arithmetic)}
                        <small>of {documents.toLocaleString()}</small>
                      </span>
                    </>
                  ) : (
                    <>
                      <b className="is-none">Off</b>
                      <span>
                        {never}
                        <small>
                          On: {checks.arithmetic.toLocaleString()} {caught(checks.arithmetic)}
                        </small>
                      </span>
                    </>
                  )}
                </div>
              </section>
            </div>
          </div>
        </section>
      )}
    </section>
  );
}

function ProposalEvidence({
  batch,
  selectedType,
  selectedFieldId,
  documentTypes,
  schemaLocked,
  handoffLocked,
  canLeaveOut = false,
  onMoveDocument,
}: {
  batch: ProcessResult;
  selectedType?: DocumentTypeDefinition;
  selectedFieldId: string | null;
  documentTypes: DocumentTypeDefinition[];
  schemaLocked: boolean;
  handoffLocked: boolean;
  /** A representative setup's samples can be left out of the job (setup-sample-leave-out.ts). */ canLeaveOut?: boolean;
  onMoveDocument: (documentId: string, typeId: string) => void;
}) {
  // This type's samples, then the ones left out of it, which stay here with the choice to put them back, and those
  // left out of a type no longer in the setup, which every type shows so they can still be put back.
  const typeIds = new Set(documentTypes.map((type) => type.id));
  const documents = selectedType
    ? [
        ...batch.documents.filter(
          (document) =>
            !document.leftOutOfJob &&
            (document.documentTypeId === selectedType.id ||
              document.documentType.localeCompare(selectedType.name, undefined, {
                sensitivity: 'accent',
              }) === 0),
        ),
        ...batch.documents.filter((document) =>
          leftOutSampleShownUnder(document, selectedType.id, typeIds),
        ),
      ]
    : [];
  const selectedField = selectedType?.fields.find((field) => field.id === selectedFieldId);
  const recognizedField = (document: ProcessResult['documents'][number]) =>
    document.fields.find(
      (field) =>
        field.fieldId === selectedField?.id ||
        field.name.localeCompare(selectedField?.name ?? '', undefined, {
          sensitivity: 'accent',
        }) === 0,
    );
  const evidenceDocument = selectedField
    ? documents.find(
        (document) => !document.leftOutOfJob && Boolean(recognizedField(document)?.value),
      )
    : undefined;
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const activeDocument =
    documents.find((document) => document.id === selectedDocumentId) ??
    evidenceDocument ??
    documents[0];
  const activeField = activeDocument ? recognizedField(activeDocument) : undefined;
  // The stored index can belong to a longer sample that has since moved to another type
  // ("3/1"): every reader uses it clamped to the sample on show.
  const pageIndex = Math.max(
    0,
    Math.min(selectedPageIndex, (activeDocument?.pages.length ?? 1) - 1),
  );
  const activePage = activeDocument?.pages[pageIndex];
  // The field's evidence is drawn on its own page only: its line indexes and boxes mean nothing on another.
  const onEvidencePage = Boolean(
    activeField &&
    activePage &&
    (activeField.evidencePageNumber ?? activeDocument?.pages[0]?.number) === activePage.number,
  );
  const activeEvidenceLines = onEvidencePage ? (activeField?.evidenceLineIndexes ?? []) : [];
  const activeBarcode = onEvidencePage ? (activeField?.barcodeEvidence ?? null) : null;
  const activeMarkEvidence = onEvidencePage && activeField ? markEvidenceOf(activeField) : null;
  const leftOutFileStaysIn = activeDocument?.leftOutOfJob
    ? leftOutSampleFileStaysIn(activeDocument, batch.documents)
    : null;

  useEffect(() => {
    const next = evidenceDocument ?? documents[0];
    if (!next) {
      setSelectedDocumentId('');
      setSelectedPageIndex(0);
      return;
    }
    // A sample just left out stays on show, with its choice to put it back.
    setSelectedDocumentId((current) =>
      documents.some(
        (document) => document.id === current && (!selectedField || Boolean(document.leftOutOfJob)),
      )
        ? current
        : next.id,
    );
    const evidence = recognizedField(next);
    const evidencePage = evidence?.evidencePageNumber;
    setSelectedPageIndex(
      Math.max(0, evidencePage ? next.pages.findIndex((page) => page.number === evidencePage) : 0),
    );
  }, [selectedType?.id, selectedField?.id, evidenceDocument?.id]);
  useEffect(() => {
    setEvidenceExpanded(false);
  }, [selectedType?.id]);

  const chooseDocument = (documentId: string) => {
    const next = documents.find((document) => document.id === documentId);
    if (!next) return;
    setSelectedDocumentId(documentId);
    const evidencePage = recognizedField(next)?.evidencePageNumber;
    setSelectedPageIndex(
      Math.max(0, evidencePage ? next.pages.findIndex((page) => page.number === evidencePage) : 0),
    );
  };
  return (
    <>
      <section className="proposal-evidence proposal-viewer">
        {activeDocument && activePage ? (
          <>
            <div className="proposal-viewer-toolbar">
              <span>
                <strong>{activeDocument.sourceName}</strong>
                <small>
                  {selectedField && activeField?.value
                    ? `${selectedField.name}: ${activeField.value}`
                    : `${activeDocument.pages.length} page${activeDocument.pages.length === 1 ? '' : 's'}`}
                </small>
              </span>
              <div>
                <button
                  onClick={() => setSelectedPageIndex(Math.max(0, pageIndex - 1))}
                  disabled={pageIndex === 0}
                  aria-label="Previous sample page"
                >
                  <ChevronLeft size={13} />
                </button>
                <strong>
                  {pageIndex + 1}/{activeDocument.pages.length}
                </strong>
                <button
                  onClick={() =>
                    setSelectedPageIndex(Math.min(activeDocument.pages.length - 1, pageIndex + 1))
                  }
                  disabled={pageIndex === activeDocument.pages.length - 1}
                  aria-label="Next sample page"
                >
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
            <ProposalPageImage
              page={activePage}
              evidenceLines={activeEvidenceLines}
              barcodeEvidence={activeBarcode}
              markEvidence={activeMarkEvidence}
              label={activeDocument.sourceName}
              onExpand={() => setEvidenceExpanded(true)}
            />
            <div className="proposal-sample-strip" aria-label="Supporting documents">
              {documents.map((document) => (
                <button
                  key={document.id}
                  className={`${document.id === activeDocument.id ? 'active' : ''}${document.leftOutOfJob ? ' is-left-out' : ''}`}
                  onClick={() => chooseDocument(document.id)}
                  aria-label={`Show ${document.sourceName}${document.leftOutOfJob ? ' (left out)' : ''}`}
                  aria-pressed={document.id === activeDocument.id}
                >
                  <LazyPageThumbnail
                    previewPath={document.pages[0].previewPath}
                    label={document.sourceName}
                  />
                </button>
              ))}
            </div>
            <label className="proposal-sample-assignment">
              <span>Document type</span>
              <select
                value={activeDocument.leftOutOfJob ? LEAVE_SAMPLE_OUT : (selectedType?.id ?? '')}
                disabled={handoffLocked}
                onChange={(event) => onMoveDocument(activeDocument.id, event.target.value)}
              >
                {documentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
                {!schemaLocked && <option value="__new__">New type from this sample…</option>}
                {(canLeaveOut || activeDocument.leftOutOfJob) && (
                  <option value={LEAVE_SAMPLE_OUT}>Leave out of this job</option>
                )}
              </select>
            </label>
            {activeDocument.leftOutOfJob && (
              <p className="proposal-sample-left-out" role="status">
                {typeIds.has(activeDocument.leftOutOfJob.typeId)
                  ? 'Left out'
                  : `Left out of ${activeDocument.leftOutOfJob.typeName}, a type no longer in this setup`}
                {leftOutFileStaysIn === 'not-read'
                  ? ': its file is still brought into the job because the sample wasn’t read. Choose a type to put it back.'
                  : leftOutFileStaysIn === 'other-documents'
                    ? ': its file is still brought into the job because it holds other documents. Choose a type to put it back.'
                    : ': it won’t be brought into the job. Choose a type to put it back.'}
              </p>
            )}
          </>
        ) : (
          <div className="proposal-no-examples">
            No representative page is linked to this type yet.
          </div>
        )}
      </section>
      {evidenceExpanded && activeDocument && activePage && (
        <ProposalEvidenceModal
          page={activePage}
          evidenceLines={activeEvidenceLines}
          barcodeEvidence={activeBarcode}
          markEvidence={activeMarkEvidence}
          label={activeDocument.sourceName}
          fieldLabel={
            selectedField && activeField?.value
              ? `${selectedField.name}: ${activeField.value}`
              : undefined
          }
          pageIndex={pageIndex}
          pageCount={activeDocument.pages.length}
          onPage={(index) =>
            setSelectedPageIndex(Math.min(activeDocument.pages.length - 1, Math.max(0, index)))
          }
          onClose={() => setEvidenceExpanded(false)}
        />
      )}
    </>
  );
}

/**
 * Field hover lives outside React state on purpose. Profiled live: holding it
 * in workspace state re-rendered the entire review tree — about 175ms of pure
 * element construction — on every row the pointer crossed, which is why
 * scrolling the fields panel felt broken on a fast machine. The store notifies
 * two feather-weight subscribers instead: the on-page highlight overlay and
 * the row echo. Nothing else renders.
 */
const fieldHoverStore = {
  id: null as string | null,
  listeners: new Set<() => void>(),
  set(next: string | null) {
    if (fieldHoverStore.id === next) return;
    fieldHoverStore.id = next;
    for (const listener of fieldHoverStore.listeners) listener();
  },
  subscribe(listener: () => void) {
    fieldHoverStore.listeners.add(listener);
    return () => {
      fieldHoverStore.listeners.delete(listener);
    };
  },
  snapshot: () => fieldHoverStore.id,
};

const noMarks: OcrBox[] = [];

type HoverEvidenceEntry = {
  fieldId: string;
  pageNumber: number | undefined;
  lineIndexes: number[];
  barcode: BarcodeResult | null;
  mark?: OcrBox | null;
  /** The value and its stored word citation: only the words that spell it are outlined (field-evidence-words.ts). */
  value?: string;
  words?: number[] | null;
};
const hoverCitation = (entry: HoverEvidenceEntry): EvidenceCitation => ({
  lineIndexes: entry.lineIndexes,
  value: entry.value,
  stored: entry.words,
});

/** Mirrors the hovered field onto its panel row — both hover directions land here. */
function FieldHoverRowEcho() {
  const hovered = useSyncExternalStore(fieldHoverStore.subscribe, fieldHoverStore.snapshot);
  useEffect(() => {
    if (!hovered) return;
    const row = window.document.querySelector(`label[data-field-id="${CSS.escape(hovered)}"]`);
    if (!row) return;
    row.classList.add('evidence-hover');
    return () => row.classList.remove('evidence-hover');
  }, [hovered]);
  return null;
}

/** The hovered field's evidence, painted over one page. Renders alone on hover. */
function HoverEvidenceLayer({
  page,
  size,
  defaultPageNumber,
  evidence,
}: {
  page: ProcessResult['documents'][number]['pages'][number];
  size: { width: number; height: number };
  defaultPageNumber: number | undefined;
  evidence: HoverEvidenceEntry[];
}) {
  const hovered = useSyncExternalStore(fieldHoverStore.subscribe, fieldHoverStore.snapshot);
  const entry = hovered
    ? evidence.find(
        (candidate) =>
          candidate.fieldId === hovered &&
          (candidate.pageNumber ?? defaultPageNumber) === page.number,
      )
    : null;
  if (!entry) return null;
  const words = citedWordIndexes(page.words, [hoverCitation(entry)]).map(
    (index) => page.words[index],
  );
  if (words.length === 0 && !entry.barcode && !entry.mark) return null;
  return (
    <svg
      className="ocr-text-layer hover-evidence"
      viewBox={`0 0 ${size.width} ${size.height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {entry.barcode && (
        <rect
          className="evidence-highlight barcode-highlight"
          x={entry.barcode.x}
          y={entry.barcode.y}
          width={entry.barcode.width}
          height={entry.barcode.height}
          rx={Math.max(2, entry.barcode.height * 0.08)}
        />
      )}
      {entry.mark && (
        <rect
          className="evidence-highlight mark-highlight"
          x={entry.mark.x}
          y={entry.mark.y}
          width={entry.mark.width}
          height={entry.mark.height}
          rx={2}
        />
      )}
      {words.map((word, index) => (
        <rect
          className="evidence-highlight"
          key={`${word.lineIndex}-${index}`}
          x={inkBox(word).x}
          y={inkBox(word).y}
          width={inkBox(word).width}
          height={inkBox(word).height}
          rx={Math.max(2, inkBox(word).height * 0.08)}
        />
      ))}
    </svg>
  );
}

/**
 * "Find the table", second step: the thorough reader found nothing, so the operator drags a box around the
 * table on the page and only the words inside it are read. Coordinates are the page image's own pixels, the
 * same space the OCR words use. Escape or a click without a drag leaves the page as it was.
 */
function TableMarkLayer({
  pageNumber,
  size,
  onMarked,
  onCancel,
}: {
  pageNumber: number;
  size: { width: number; height: number };
  onMarked: (region: TableRegion) => void;
  onCancel: () => void;
}) {
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const point = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(
        size.width,
        Math.max(0, ((event.clientX - rect.left) / Math.max(1, rect.width)) * size.width),
      ),
      y: Math.min(
        size.height,
        Math.max(0, ((event.clientY - rect.top) / Math.max(1, rect.height)) * size.height),
      ),
    };
  };
  useEffect(() => {
    const cancel = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', cancel);
    return () => window.removeEventListener('keydown', cancel);
  }, [onCancel]);
  const box = drag && {
    x: Math.min(drag.x0, drag.x1),
    y: Math.min(drag.y0, drag.y1),
    width: Math.abs(drag.x1 - drag.x0),
    height: Math.abs(drag.y1 - drag.y0),
  };
  return (
    <svg
      className="table-mark-layer"
      viewBox={`0 0 ${size.width} ${size.height}`}
      preserveAspectRatio="none"
      role="application"
      aria-label={`Drag a box around the table on page ${pageNumber}`}
      onPointerDown={(event) => {
        event.stopPropagation();
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const at = point(event);
        setDrag({ x0: at.x, y0: at.y, x1: at.x, y1: at.y });
      }}
      onPointerMove={(event) => {
        if (!drag) return;
        event.stopPropagation();
        const at = point(event);
        setDrag({ ...drag, x1: at.x, y1: at.y });
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        if (!box) return;
        setDrag(null);
        // A click is not a box: the smallest table worth reading is a few words across and a line tall.
        if (box.width < size.width * 0.03 || box.height < size.height * 0.01) return;
        onMarked({
          pageNumber,
          x: Math.round(box.x),
          y: Math.round(box.y),
          width: Math.round(box.width),
          height: Math.round(box.height),
        });
      }}
      onPointerCancel={() => setDrag(null)}
    >
      {box && <rect x={box.x} y={box.y} width={box.width} height={box.height} />}
    </svg>
  );
}

/**
 * The page's OCR layer: an outlined box and a selectable glyph run for every word.
 *
 * Its own component, and memoised, because it is the most expensive thing on the
 * screen and almost nothing changes it. Typing one character into a line item
 * rebuilds the batch, the documents array and the document — so the parent
 * re-rendered, and with it seven hundred-odd SVG nodes, on every key press. The
 * page object itself is untouched by that edit, which is what makes this sound.
 *
 * Compared by hand rather than shallowly: the evidence and barcode props are
 * fresh arrays on every parent render and would defeat the memo on identity
 * alone, so they are compared by content.
 */
const OcrTextLayer = memo(
  function OcrTextLayer({
    documentId,
    documentMutationToken,
    page,
    size,
    evidenceLines,
    evidenceWords,
    barcodeEvidence,
    markEvidence,
    fieldLinkSignature,
    fieldIdForLine,
    fieldIdForBarcode,
    onHoverField,
    onBarcodeMenu,
  }: {
    documentId: string;
    documentMutationToken: string;
    page: ProcessResult['documents'][number]['pages'][number];
    size: { width: number; height: number };
    evidenceLines: number[];
    /** When given, the words to outline (indexes into page.words) in place of every word of evidenceLines. */
    evidenceWords?: number[];
    barcodeEvidence: BarcodeResult[];
    /** The marked boxes of checkbox readings whose caption lines are on show (mark-evidence.ts). */
    markEvidence: OcrBox[];
    fieldLinkSignature: string;
    fieldIdForLine: (pageNumber: number, lineIndex: number) => string | null;
    fieldIdForBarcode: (pageNumber: number, barcode: BarcodeResult) => string | null;
    onHoverField: (fieldId: string | null) => void;
    onBarcodeMenu: (menu: ViewerSelectionMenu) => void;
  }) {
    return (
      <svg
        className="ocr-text-layer"
        viewBox={`0 0 ${size.width} ${size.height}`}
        preserveAspectRatio="none"
        aria-label="Selectable OCR text layer"
      >
        {barcodeEvidence.map((barcode, index) => (
          <rect
            className="evidence-highlight barcode-highlight"
            key={`barcode-evidence-${barcode.value}-${index}`}
            x={barcode.x}
            y={barcode.y}
            width={barcode.width}
            height={barcode.height}
            rx={Math.max(2, barcode.height * 0.08)}
          />
        ))}
        {markEvidence.map((mark, index) => (
          <rect
            className="evidence-highlight mark-highlight"
            key={`mark-evidence-${index}`}
            x={mark.x}
            y={mark.y}
            width={mark.width}
            height={mark.height}
            rx={2}
          />
        ))}
        {(evidenceWords
          ? evidenceWords.map((index) => page.words[index]).filter(Boolean)
          : page.words.filter((word) => evidenceLines.includes(word.lineIndex))
        ).map((word, index) => (
          <rect
            className="evidence-highlight"
            key={`evidence-${word.lineIndex}-${index}`}
            x={inkBox(word).x}
            y={inkBox(word).y}
            width={inkBox(word).width}
            height={inkBox(word).height}
            rx={Math.max(2, inkBox(word).height * 0.08)}
          />
        ))}
        {page.barcodes.map((barcode, index) => (
          <g
            className="barcode-zone"
            key={`${barcode.value}-${index}`}
            onMouseEnter={() => onHoverField(fieldIdForBarcode(page.number, barcode))}
            onMouseLeave={() => onHoverField(null)}
            onContextMenu={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onBarcodeMenu({
                documentId,
                documentMutationToken,
                x: event.clientX,
                y: event.clientY,
                text: barcode.value,
                lineIndexes: [],
                pageNumber: page.number,
                barcode,
              });
            }}
          >
            <rect x={barcode.x} y={barcode.y} width={barcode.width} height={barcode.height} />
            <text x={barcode.x + barcode.width / 2} y={Math.max(12, barcode.y - 5)}>
              {barcode.value}
            </text>
            <TooltipTitle>
              {barcode.symbology}: {barcode.value}
            </TooltipTitle>
          </g>
        ))}
        {page.words.map((word, index) => {
          const linkedFieldId = fieldIdForLine(page.number, word.lineIndex);
          return (
            <g
              className={linkedFieldId ? 'ocr-word-zone linked' : 'ocr-word-zone'}
              key={`${word.lineIndex}-${index}`}
              onMouseEnter={() => onHoverField(linkedFieldId)}
              onMouseLeave={() => onHoverField(null)}
            >
              <rect x={word.box.x} y={word.box.y} width={word.box.width} height={word.box.height} />
              <text
                data-line-index={word.lineIndex}
                x={word.box.x}
                y={word.box.y + word.box.height * 0.82}
                fontSize={word.box.height * 0.82}
                textLength={Math.max(word.box.width, 1)}
                lengthAdjust="spacingAndGlyphs"
              >
                {word.text}
              </text>
            </g>
          );
        })}
      </svg>
    );
  },
  (before, after) =>
    before.documentId === after.documentId &&
    before.documentMutationToken === after.documentMutationToken &&
    before.page === after.page &&
    before.size.width === after.size.width &&
    before.size.height === after.size.height &&
    before.evidenceLines.join(',') === after.evidenceLines.join(',') &&
    String(before.evidenceWords) === String(after.evidenceWords) &&
    before.barcodeEvidence.length === after.barcodeEvidence.length &&
    before.barcodeEvidence.every(
      (barcode, index) =>
        barcode.value === after.barcodeEvidence[index]?.value &&
        barcode.x === after.barcodeEvidence[index]?.x &&
        barcode.y === after.barcodeEvidence[index]?.y,
    ) &&
    JSON.stringify(before.markEvidence) === JSON.stringify(after.markEvidence) &&
    before.fieldLinkSignature === after.fieldLinkSignature,
);

function ProposalPageImage({
  page,
  evidenceLines,
  barcodeEvidence,
  markEvidence,
  label,
  onExpand,
}: {
  page: ProcessResult['documents'][number]['pages'][number];
  evidenceLines: number[];
  barcodeEvidence: BarcodeResult | null;
  markEvidence?: OcrBox | null;
  label: string;
  onExpand?: () => void;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  useEffect(() => {
    let canceled = false;
    setSource(null);
    void window.capturePointDesktop
      ?.readPagePreview(page.previewPath)
      .then((value) => {
        if (!canceled) setSource(value);
      })
      .catch(() => {
        if (!canceled) setSource('');
      });
    return () => {
      canceled = true;
    };
  }, [page.previewPath]);
  const ratio = size.width / Math.max(1, size.height);
  return (
    <div className="proposal-page-stage">
      {source ? (
        <>
          <button
            className="proposal-page-expand"
            onClick={onExpand}
            aria-label="Open sample evidence viewer"
          >
            <Maximize2 size={13} />
            <span>Inspect</span>
          </button>
          <div
            className="proposal-page-stack"
            role={onExpand ? 'button' : undefined}
            tabIndex={onExpand ? 0 : undefined}
            title={onExpand ? 'Click to enlarge' : undefined}
            onClick={onExpand}
            onKeyDown={(event) => {
              if (onExpand && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onExpand();
              }
            }}
            style={
              {
                '--sample-aspect': ratio,
                aspectRatio: `${size.width} / ${size.height}`,
              } as CSSProperties
            }
          >
            <img
              src={source}
              alt={label}
              draggable={false}
              onLoad={(event) =>
                setSize({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
            />
            {size.width > 1 && (
              <svg
                viewBox={`0 0 ${size.width} ${size.height}`}
                preserveAspectRatio="none"
                aria-label="Sample field evidence"
              >
                {barcodeEvidence && (
                  <rect
                    className="proposal-evidence-box barcode"
                    x={barcodeEvidence.x}
                    y={barcodeEvidence.y}
                    width={barcodeEvidence.width}
                    height={barcodeEvidence.height}
                  />
                )}
                {markEvidence && (
                  <rect
                    className="proposal-evidence-box mark"
                    x={markEvidence.x}
                    y={markEvidence.y}
                    width={markEvidence.width}
                    height={markEvidence.height}
                  />
                )}
                {page.words
                  .filter((word) => evidenceLines.includes(word.lineIndex))
                  .map((word, index) => (
                    <rect
                      className="proposal-evidence-box"
                      key={`${word.lineIndex}-${index}`}
                      x={word.box.x}
                      y={word.box.y}
                      width={word.box.width}
                      height={word.box.height}
                    />
                  ))}
              </svg>
            )}
          </div>
        </>
      ) : source === '' ? (
        <span>
          <TriangleAlert size={17} /> Preview unavailable
        </span>
      ) : (
        <LoaderCircle className="spin" size={18} />
      )}
    </div>
  );
}

function ProposalEvidenceModal({
  page,
  evidenceLines,
  barcodeEvidence,
  markEvidence,
  label,
  fieldLabel,
  pageIndex,
  pageCount,
  onPage,
  onClose,
}: {
  page: ProcessResult['documents'][number]['pages'][number];
  evidenceLines: number[];
  barcodeEvidence: BarcodeResult | null;
  markEvidence?: OcrBox | null;
  label: string;
  fieldLabel?: string;
  pageIndex: number;
  pageCount: number;
  onPage: (index: number) => void;
  onClose: () => void;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(
    null,
  );
  useEffect(() => {
    let canceled = false;
    setSource(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    void window.capturePointDesktop
      ?.readPagePreview(page.previewPath)
      .then((value) => {
        if (!canceled) setSource(value);
      })
      .catch(() => {
        if (!canceled) setSource('');
      });
    return () => {
      canceled = true;
    };
  }, [page.previewPath]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && pageIndex > 0) onPage(pageIndex - 1);
      if (event.key === 'ArrowRight' && pageIndex < pageCount - 1) onPage(pageIndex + 1);
      if (event.key === '0' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        setZoom(1);
        setPan({ x: 0, y: 0 });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, onPage, pageIndex, pageCount]);
  // React attaches wheel handlers passively, so preventDefault inside an onWheel
  // prop does nothing but warn — the page scrolls behind the evidence viewer
  // while it zooms. The listener has to be attached natively to opt out, which
  // is how the review canvas and the detached panel already do it.
  const zoomAtPointer = (event: WheelEvent) => {
    event.preventDefault();
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const pointerX = event.clientX - bounds.left - bounds.width / 2;
    const pointerY = event.clientY - bounds.top - bounds.height / 2;
    const next = Math.min(5, Math.max(0.25, zoom * Math.exp(-event.deltaY * 0.0015)));
    const ratio = next / zoom;
    setPan((current) => ({
      x: pointerX - (pointerX - current.x) * ratio,
      y: pointerY - (pointerY - current.y) * ratio,
    }));
    setZoom(next);
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', zoomAtPointer, { passive: false });
    return () => canvas.removeEventListener('wheel', zoomAtPointer);
  }, [zoom]);
  const beginPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button === 0 && event.defaultPrevented) return;
    if (event.button !== 0) return;
    gesture.current = { startX: event.clientX, startY: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture(event.pointerId);
    setPanning(true);
  };
  const movePan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!gesture.current) return;
    setPan({
      x: gesture.current.panX + event.clientX - gesture.current.startX,
      y: gesture.current.panY + event.clientY - gesture.current.startY,
    });
  };
  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    gesture.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return (
    <div
      className="proposal-inspector-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="proposal-inspector"
        role="dialog"
        aria-modal="true"
        aria-label={`Inspect evidence from ${label}`}
      >
        <header>
          <span>
            <small>Sample document</small>
            <strong>{label}</strong>
            {fieldLabel && <em>{fieldLabel}</em>}
          </span>
          <nav>
            <button
              onClick={() => onPage(pageIndex - 1)}
              disabled={pageIndex === 0}
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>
            <b>
              {pageIndex + 1} / {pageCount}
            </b>
            <button
              onClick={() => onPage(pageIndex + 1)}
              disabled={pageIndex === pageCount - 1}
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </nav>
          <div>
            <TooltipButton
              onClick={() => {
                setZoom(1);
                setPan({ x: 0, y: 0 });
              }}
              tooltip="Fit page · Ctrl+0"
              aria-label="Fit page"
            >
              <Minimize2 size={15} />
            </TooltipButton>
            <strong>{Math.round(zoom * 100)}%</strong>
            <button autoFocus onClick={onClose} aria-label="Close evidence viewer">
              <X size={16} />
            </button>
          </div>
        </header>
        <div
          ref={canvasRef}
          className={`proposal-inspector-canvas ${panning ? 'panning' : ''}`}
          onPointerDown={beginPan}
          onPointerMove={movePan}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        >
          {source ? (
            <div
              className="proposal-inspector-page"
              style={{
                aspectRatio: `${size.width} / ${size.height}`,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              }}
            >
              <img
                src={source}
                alt={label}
                draggable={false}
                onLoad={(event) =>
                  setSize({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
              />
              {size.width > 1 && (
                <svg
                  viewBox={`0 0 ${size.width} ${size.height}`}
                  preserveAspectRatio="none"
                  aria-label="Selected field evidence"
                >
                  {barcodeEvidence && (
                    <rect
                      className="proposal-evidence-box barcode"
                      x={barcodeEvidence.x}
                      y={barcodeEvidence.y}
                      width={barcodeEvidence.width}
                      height={barcodeEvidence.height}
                    />
                  )}
                  {markEvidence && (
                    <rect
                      className="proposal-evidence-box mark"
                      x={markEvidence.x}
                      y={markEvidence.y}
                      width={markEvidence.width}
                      height={markEvidence.height}
                    />
                  )}
                  {page.words
                    .filter((word) => evidenceLines.includes(word.lineIndex))
                    .map((word, index) => (
                      <rect
                        className="proposal-evidence-box"
                        key={`${word.lineIndex}-${index}`}
                        x={word.box.x}
                        y={word.box.y}
                        width={word.box.width}
                        height={word.box.height}
                      />
                    ))}
                </svg>
              )}
            </div>
          ) : source === '' ? (
            <span>
              <TriangleAlert size={20} /> Preview unavailable
            </span>
          ) : (
            <LoaderCircle className="spin" size={25} />
          )}
        </div>
        <footer>
          <Hand size={12} />
          <span>Drag to pan</span>
          <i />
          <span>Mouse wheel to zoom</span>
          <i />
          <span>Arrow keys change pages</span>
        </footer>
      </section>
    </div>
  );
}

/**
 * One page of the continuous reading flow — the mode where the wheel scrolls
 * like a document reader instead of zooming. Each page loads its image only
 * when the operator scrolls near it, and carries the same selectable OCR
 * layer as the pan viewer, so hover, text selection, and right-click work on
 * every page in the stack, not just the current one.
 */
export function ReaderPage({
  documentId,
  documentMutationToken,
  page,
  index,
  width,
  onSize,
  height,
  evidenceLines,
  evidenceWords,
  barcodeEvidence,
  markEvidence = noMarks,
  fieldLinkSignature,
  defaultPageNumber,
  hoverEvidence,
  fieldIdForLine,
  fieldIdForBarcode,
  onHoverField,
  onBarcodeMenu,
  onMarkTable,
  onCancelMarkTable,
  quarterTurns = 0,
}: {
  documentId: string;
  documentMutationToken: string;
  page: ProcessResult['documents'][number]['pages'][number];
  index: number;
  width: string;
  onSize?: (width: number, height: number) => void;
  height?: number;
  evidenceLines: number[];
  /** The words to outline, when narrower than evidenceLines (OcrTextLayer). */
  evidenceWords?: number[];
  barcodeEvidence: BarcodeResult[];
  /** Checkbox readings' marked boxes on show with their caption lines. */
  markEvidence?: OcrBox[];
  fieldLinkSignature: string;
  defaultPageNumber: number | undefined;
  hoverEvidence: HoverEvidenceEntry[];
  fieldIdForLine: (pageNumber: number, lineIndex: number) => string | null;
  fieldIdForBarcode: (pageNumber: number, barcode: BarcodeResult) => string | null;
  onHoverField: (fieldId: string | null) => void;
  onBarcodeMenu: (menu: ViewerSelectionMenu) => void;
  /** Set while the operator is marking this document's table ("Find the table"). */
  onMarkTable?: ((region: TableRegion) => void) | null;
  onCancelMarkTable?: () => void;
  /** Turn page: quarter turns to draw this page turned while the engine saves the turn (page-turn-queue.ts). */
  quarterTurns?: number;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !window.capturePointDesktop) return;
    let disposed = false;
    let near = false;
    let loaded = false;
    let pending = false;
    let refused = false;
    setSource(null);
    setFailed(false);
    setSize({ width: 0, height: 0 });
    const load = () => {
      if (disposed || !near || loaded || pending || refused) return;
      pending = true;
      void window
        .capturePointDesktop!.readPagePreview(page.previewPath)
        .then((value) => {
          if (disposed || !near) return;
          loaded = true;
          setSource(value);
        })
        .catch(() => {
          if (disposed || !near) return;
          refused = true;
          setFailed(true);
        })
        .finally(() => {
          pending = false;
        });
    };
    const stopObserving = observeReaderVisibility(host, (visible) => {
      near = visible;
      setNearViewport(near);
      if (near) load();
      else {
        loaded = false;
        setSource(null);
        // Retain measured aspect ratio so releasing pixels does not move pages.
      }
    });
    return () => {
      disposed = true;
      stopObserving();
    };
  }, [page.previewPath]);
  return (
    <div
      ref={hostRef}
      className="reader-page"
      data-reader-index={index}
      data-reader-page-number={page.number}
      style={{
        width,
        height,
        aspectRatio: size.width > 1 ? `${size.width} / ${size.height}` : '8.5 / 11',
        transform: pageTurnTransform(quarterTurns, size.width, size.height),
      }}
    >
      {source ? (
        <>
          <img
            src={source}
            draggable={false}
            alt={`Page ${page.number}`}
            onLoad={(event) => {
              const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
              setSize({ width, height });
              onSize?.(width, height);
            }}
          />
          {size.width > 1 && (
            <>
              <OcrTextLayer
                documentId={documentId}
                documentMutationToken={documentMutationToken}
                page={page}
                size={size}
                evidenceLines={evidenceLines}
                evidenceWords={evidenceWords}
                barcodeEvidence={barcodeEvidence}
                markEvidence={markEvidence}
                fieldLinkSignature={fieldLinkSignature}
                fieldIdForLine={fieldIdForLine}
                fieldIdForBarcode={fieldIdForBarcode}
                onHoverField={onHoverField}
                onBarcodeMenu={onBarcodeMenu}
              />
              <HoverEvidenceLayer
                page={page}
                size={size}
                defaultPageNumber={defaultPageNumber}
                evidence={hoverEvidence}
              />
              {onMarkTable && (
                <TableMarkLayer
                  pageNumber={page.number}
                  size={size}
                  onMarked={onMarkTable}
                  onCancel={onCancelMarkTable ?? (() => undefined)}
                />
              )}
            </>
          )}
        </>
      ) : nearViewport ? (
        failed ? (
          <span className="reader-preview-unavailable" role="status">
            <FileSearch size={20} />
            Preview unavailable
          </span>
        ) : (
          <LoaderCircle className="preview-loader spin" size={24} />
        )
      ) : null}
      <span className="reader-page-number">{page.number}</span>
    </div>
  );
}

/**
 * The height of a document's header row in the spine, in pixels.
 *
 * It carries the type, the position and the state, and it is read at a glance
 * beside a page image rather than instead of one — so it is sized to be read,
 * not to disappear. Must match `.document-marker` in styles.css: the spine's
 * layout is measured in code and drawn in CSS, and the two have to agree or a
 * row lands off the mark. Module scope because the spine's layout table is
 * memoised on the batch and this must not be a reason to rebuild it.
 */
const SPINE_HEADER_ROW = 48;
// One narrow horizontal frame, owned by page sizing rather than scroll padding.
const READER_PAGE_SIDE_SPACE = 16;
/**
 * What a document group costs beyond its header row and its pages: 5px of
 * `.document-group` bottom padding, plus the 8px gap the next group starts
 * after — the box is drawn at `height - 8` a few hundred lines below, so the
 * spacing between two documents is the difference between the slot and the box.
 *
 * Unchanged in value. This is the 13 that was inside `41 + pages * 110`, whose
 * other 28 was the header. Splitting the 41 is the change; what a group spends
 * below its last page is deliberately what it was.
 */
const SPINE_GROUP_CHROME = 13;
/**
 * How tall a document sits in the spine, and where one of its pages starts.
 *
 * Every measurement of the spine goes through these two — the memoised layout
 * table, the per-render review table, the two scroll-into-view sites and the
 * mounted rows themselves. The page row is an argument rather than a constant
 * because the operator sets it by dragging the spine wider; the point of these
 * functions is that there is exactly one place that knows how it composes with
 * the header. It was five places, each with the row height and the header
 * written into it by hand — consistent while both were constants, and five
 * chances to disagree the moment one of them is a value the operator sets.
 * A jump to page seven of document nine hundred landing on page six is the
 * shape of that disagreement.
 */
const spineGroupHeight = (pageCount: number, pageRow: number) =>
  SPINE_HEADER_ROW + SPINE_GROUP_CHROME + pageCount * pageRow;
const spinePageTop = (groupTop: number, pageIndex: number, pageRow: number) =>
  groupTop + SPINE_HEADER_ROW + pageIndex * pageRow;

/**
 * A document the engine has not reached yet is not one it failed to classify.
 * Shared by the workspace's per-render closure and the memoised batch census,
 * so the two can never drift apart on what "still being read" means.
 */
const isAwaitingUnderstanding = (document: DocumentResult, stillReading: boolean) =>
  stillReading &&
  /^unclassified/i.test(document.documentType ?? '') &&
  !document.documentTypeVerified;

const captureProgressLabel = (
  progress?: Pick<CaptureProcessingProgress, 'stage' | 'completed' | 'total'> | null,
) =>
  progress?.stage === 'preparing'
    ? `Preparing file ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
    : progress?.stage === 'recognizing'
      ? `Reading page ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
      : progress?.stage === 'organizing'
        ? 'Grouping pages into documents'
        : progress?.stage === 'understanding'
          ? `Understanding document ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
          : progress?.stage === 'finalizing'
            ? 'Finishing the batch'
            : null;

/**
 * The five stages every page goes through, in order, named for what they do.
 *
 * The status said one sentence — "Reading page 40 of 126" — and nothing about
 * what came before it or after. So an operator watching a batch had no idea
 * whether reading pages was most of the work or the first tenth of it, and the
 * wait felt like staring at a wall. Naming the stages costs a strip of pixels
 * and turns an unexplained delay into visible progress through known work.
 *
 * This list is the ONLY home the stage vocabulary has. The queue chips and the
 * workbench strip each carried their own words for the same machinery, so one
 * read "Split" where the other read "Organize" and an operator watching both
 * had six stages, not five. The headline still differs by journey — importing
 * a chosen folder, an ordinary acquisition, and the once-per-job pass that
 * ends in a proposed job are different sentences — but the differences live
 * here, one column per journey, where they cannot drift apart.
 */
type CaptureStageContext = {
  /** A chosen folder entering the batch as waves. */
  folder: boolean;
  /** An acquisition being read on this screen — scan, files, or folder wave. */
  capture: boolean;
  /** The pass that ends in a proposed job rather than a saved batch. */
  proposing: boolean;
};
const captureStages: ReadonlyArray<{
  id: 'preparing' | 'recognizing' | 'organizing' | 'understanding' | 'finalizing';
  short: string;
  label: string;
  title: (context: CaptureStageContext) => string;
}> = [
  {
    id: 'preparing',
    short: 'Prepare',
    label: 'Turning the scan into pages',
    title: ({ folder, capture }) =>
      folder
        ? 'Preparing source files'
        : capture
          ? 'Preparing scanned pages'
          : 'Preparing representative documents',
  },
  {
    id: 'recognizing',
    short: 'Read',
    label: 'Reading the text and barcodes on every page',
    title: () => 'Reading pages and barcodes',
  },
  {
    id: 'organizing',
    short: 'Split',
    label: 'Working out where each document starts',
    title: () => 'Grouping pages into documents',
  },
  {
    id: 'understanding',
    short: 'Understand',
    label: 'Identifying each document and pulling its values',
    title: ({ folder, capture, proposing }) =>
      folder
        ? 'Indexing completed documents'
        : capture && !proposing
          ? 'Reading fields from each document'
          : 'Discovering types and fields',
  },
  {
    id: 'finalizing',
    short: 'Finish',
    label: 'Saving the batch',
    title: ({ folder, proposing }) =>
      folder ? 'Committing the batch' : proposing ? 'Finishing job setup' : 'Saving the batch',
  },
];

/** Where a stage sits in the pipeline; "complete" ranks past the last stage. */
const captureStageRank = (stage: CaptureProcessingProgress['stage']) => {
  const index = captureStages.findIndex((item) => item.id === stage);
  return index === -1 ? captureStages.length : index;
};

/**
 * The in-flight waves, furthest-along first.
 *
 * Up to three waves overlap — one reading ahead while an earlier one extracts —
 * and whichever is furthest speaks for the import wherever only one may speak.
 * That is what keeps a hand-off from walking the narration backwards: when a
 * fresh wave starts preparing behind a wave deep in understanding, the chip
 * stays on the understanding rather than jumping to the newcomer's "Prepare".
 */
const wavesByProgress = (waves: ReadonlyMap<string, CaptureProcessingProgress>) =>
  [...waves.values()].sort(
    (left, right) =>
      captureStageRank(right.stage) - captureStageRank(left.stage) ||
      right.completed - left.completed,
  );

const furthestAlongWave = (waves: ReadonlyMap<string, CaptureProcessingProgress>) =>
  wavesByProgress(waves)[0] ?? null;

/**
 * How long is left, once there is enough to say.
 *
 * THREE SAMPLES, NOT ONE. This extrapolates the mean, and the first item of a
 * stage is the worst estimator it will ever have: the reader is warming up, the
 * model's first call pays for its own start, and one slow sample becomes the
 * whole forecast. Measured on an eight-document import: at one document done it
 * announced "about 2 minutes left" for a stage that finished ten seconds later,
 * then walked the number down through 52 and 29 seconds as the mean recovered.
 *
 * A wrong estimate is worse than none — it is the number an operator plans their
 * next ten minutes around — so below the floor this returns null and the screen
 * says what it is doing instead. That is already the shape of every other
 * waiting state here: never a bare spinner, and never a confident wrong number.
 */
const estimateNeedsSamples = 3;

/**
 * How often a workspace waiting for a job's first batch asks whether it exists.
 *
 * Two seconds because the wait is minutes long and one of the two questions is
 * an engine call — asked of the same engine that is reading the pages being
 * waited for. The screen's numbers come from the other question, which is
 * answered out of main-process memory and costs nothing.
 */
const firstBatchPollMilliseconds = 2_000;
/** Reads of the latest batch tried, one poll apart (about 30 seconds), before opening says it failed. */
const OPENING_OVERVIEW_ATTEMPTS = 15;

/**
 * The one place the arithmetic lives. Every surface that says how long is left
 * — the queue line, the workbench header — divides through this, so no surface
 * can print a number the three-sample floor above has not yet earned.
 */
const remainingMilliseconds = (
  progress?: Pick<
    CaptureProcessingProgress,
    'completed' | 'total' | 'stageElapsedMilliseconds'
  > | null,
) => {
  if (
    !progress ||
    progress.completed < estimateNeedsSamples ||
    progress.completed >= progress.total
  )
    return null;
  const perItem = progress.stageElapsedMilliseconds / progress.completed;
  const milliseconds = perItem * (progress.total - progress.completed);
  return Number.isFinite(milliseconds) && milliseconds > 0 ? milliseconds : null;
};

const remainingLabel = (progress?: CaptureProcessingProgress | null) => {
  const milliseconds = remainingMilliseconds(progress);
  if (milliseconds === null) return null;
  const minutes = Math.round(milliseconds / 60_000);
  if (minutes >= 2) return `about ${minutes} minutes left`;
  const seconds = Math.max(5, Math.round(milliseconds / 1_000));
  return seconds >= 60 ? 'about a minute left' : `about ${seconds} seconds left`;
};

/**
 * The long representative pass belongs in Setup, but Setup still has to show
 * the work. This component is deliberately passive: its only native read asks
 * preload for an already-authorised page preview. It never discovers captures,
 * starts a queue, resumes an operation, or hydrates an operational batch.
 *
 * CapturePreview owns all of those effects and therefore must not be mounted,
 * even hidden, for a provisional representative job. Keeping this surface
 * separate preserves that boundary while sharing the existing stage language
 * and visual vocabulary.
 */
export function AutomaticSetupTransition({
  jobId,
  checkingDestination,
}: {
  jobId: string;
  checkingDestination: boolean;
}) {
  const processingState = useJobProcessingState(jobId);
  if (processingState && (processingState.pauseRequested || processingState.status === 'queued'))
    return <HeldSetupProgress state={processingState} />;
  return (
    <section className="setup-analysis-progress panel" role="status" aria-live="polite">
      <header className="setup-analysis-heading">
        <span className="section-icon">
          <LoaderCircle className="spin" size={20} />
        </span>
        <div>
          <h2>{checkingDestination ? 'Checking setup' : 'Opening your documents'}</h2>
          <p>
            {checkingDestination
              ? 'Verifying the destination before saving.'
              : 'Saving the setup. Your documents will open automatically.'}
          </p>
        </div>
      </header>
    </section>
  );
}

const FOLDER_STATUS_EVENT = 'capturepoint-folder-analysis-status';
/** Two source problems the page-source notice says, by their words here and their code for telemetry (ui.error). */
const FOLDER_NO_READABLE_PAGES =
  'That folder has no readable pages - PDFs and images are supported';
const SCANNER_NO_PAGES = 'The scanner returned no pages - check paper is loaded';

/** Telemetry: the stalled-queue panel is in front of the person (queue.stalled), each time it appears. */
function QueueStalledSignal({ jobId, files }: { jobId: string; files: number }) {
  useEffect(() => {
    reportEvent('queue.stalled', { jobId, attrs: { files: telemetryCountBucket(files) } });
  }, [jobId]);
  return null;
}

/** Jobs and causes whose field reader problem was shown this window's life, so each is said once. */
const readerProblemsShown = new Set<string>();
/**
 * Telemetry: a document says the field reader is missing or broken (reader.problem): a broken install, not the
 * document. The tour's job is left to the tour's own report (tour-telemetry.ts), so it is not counted twice.
 */
function ReaderProblemSignal({
  jobId,
  cause,
}: {
  jobId: string;
  cause: 'field-reader-missing' | 'field-reader-broken';
}) {
  useEffect(() => {
    if (readerProblemsShown.has(`${jobId}|${cause}`)) return;
    readerProblemsShown.add(`${jobId}|${cause}`);
    reportEvent(
      cause === 'field-reader-missing'
        ? 'reader.problem:field-reader-missing'
        : 'reader.problem:field-reader-broken',
      { jobId },
    );
  }, [jobId, cause]);
  return null;
}

function StopSetupButton({
  jobId,
  status,
  elapsedMilliseconds = 0,
}: {
  jobId: string;
  status?: FolderAnalysisStatus;
  /** How long setup has run, as its clock shows (telemetry setup.stopped). */
  elapsedMilliseconds?: number;
}) {
  const [confirming, setConfirming] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState('');
  const stop = async () => {
    if (!status?.operationId || stopping) return;
    setStopping(true);
    setError('');
    try {
      const stopped = await window.capturePointDesktop!.stopFolderAnalysisOperation({
        jobId,
        operationId: status.operationId,
        evidenceRevision: status.evidenceRevision,
      });
      window.dispatchEvent(
        new CustomEvent(FOLDER_STATUS_EVENT, { detail: { jobId, status: stopped } }),
      );
      // The person's choice, not a failure: the stage the screen showed and how long setup had run.
      reportSetupStopped(jobId, elapsedMilliseconds);
      setConfirming(false);
    } catch (reason) {
      setError(messageFor(reason, 'Setup could not be stopped.'));
    } finally {
      setStopping(false);
    }
  };
  if (!status?.operationId || !window.capturePointDesktop?.stopFolderAnalysisOperation) return null;
  return (
    <>
      <button
        className="quiet-button small"
        data-telemetry="stop-setup"
        onClick={() => setConfirming(true)}
      >
        Stop setup
      </button>
      {confirming && (
        <WorkspaceDialog
          telemetry="stop-setup"
          title="Stop this setup?"
          compact
          onClose={() => {
            if (!stopping) setConfirming(false);
          }}
        >
          <div className="setup-explainer">
            <p>
              {hostHas('job-library')
                ? `Your completed work will be saved. You can continue later or delete this job from ${hostWords().jobsCapital}.`
                : 'Cortexa stops reading this setup. Choose the documents again when you are ready.'}
            </p>
            {error && (
              <p role="alert" data-telemetry="error:setup-stop-failed">
                {error}
              </p>
            )}
            <div className="export-actions">
              <button
                className="primary-button"
                data-telemetry="stop-setup-confirm"
                disabled={stopping}
                onClick={() => void stop()}
              >
                {stopping ? 'Stopping…' : 'Stop setup'}
              </button>
              <button
                className="secondary-button"
                data-telemetry="stop-setup-keep-going"
                disabled={stopping}
                onClick={() => setConfirming(false)}
              >
                Keep going
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
    </>
  );
}

/** The least time the setup screen shows one page before the next replaces it. */
const SETUP_PAGE_DWELL_MILLISECONDS = 2_400;

function RepresentativeSetupProgress({
  jobId,
  status,
  progress: liveProgress,
  activity,
  work: liveWork,
  externalActivity,
  processorOnlyExtraction,
  finishing = false,
  checkingDestination = false,
  stagedDocuments = null,
  importing = null,
  learnedTypes = [],
  handoff = null,
  onStartChecking,
  headerActions,
}: {
  jobId: string;
  /**
   * Setup is done and its documents are being processed behind the wizard
   * (setup-handoff.ts): the whole-folder count, never lower than shown before,
   * and whether the first batch is in the job yet.
   */
  importing?: {
    documents: number;
    read: number;
    live: string | null;
    previewPath: string | null;
    remainingMilliseconds: number | null;
    jobDocuments?: number;
    firstBatchFiles?: number | null;
    firstBatchReady?: boolean;
  } | null;
  /** The saved document types and their fields, once setup has saved them. */
  learnedTypes?: readonly { name: string; fields: string[] }[];
  /** The hand-off this wizard waits on: what learning found. */
  handoff?: SetupHandoff | null;
  /** Leaves the wizard for the job while the rest keeps processing. */
  onStartChecking?: () => void;
  headerActions?: ReactNode;
  status?: FolderAnalysisStatus;
  progress?: FolderAnalysisProgress;
  activity: FolderActivityEntry[];
  work: FolderWorkTask[];
  externalActivity?: string;
  processorOnlyExtraction: boolean;
  finishing?: boolean;
  checkingDestination?: boolean;
  /** Files staged for processing by the accepted setup; null until they are. */
  stagedDocuments?: number | null;
}) {
  // Accepting the proposal is still this journey. Keep its paper and findings
  // mounted while the saved proposal replaces the live analysis snapshot.
  const retained = useRef({ progress: liveProgress, work: liveWork });
  if (!finishing) retained.current = { progress: liveProgress, work: liveWork };
  const progress = finishing ? (retained.current.progress ?? liveProgress) : liveProgress;
  const work = finishing && retained.current.work.length ? retained.current.work : liveWork;
  const processingState = useJobProcessingState(jobId);
  const mountedAt = useRef(Date.now());
  const [clock, setClock] = useState(Date.now());

  useEffect(() => {
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  // ONE progress state for everything on this screen (working-progress.ts):
  // step list, count, live line, facts and the page caption all read it.
  // Keyed by the job, never the engine operation: setup runs more than one operation, and a new
  // operation id reset the state, so the steps went 1 2 3 4 then back to 1 (owner, 2026-09-29:
  // "progress bars go in one direction and that's forward"). One lane, so its stage only rises.
  // A remount (failure screen, Resume) or an app restart starts this state over, and a resumed
  // setup re-reports from Prepare: the furthest point shown is folded in first (same lane).
  const setupFloor = useSetupProgressFloor(jobId, status, progress, finishing);
  const { state: workingState, now: workingNow } = useWorkingProgress(
    setupFloor.runKey,
    [
      ...setupFloor.events.map((event) => ({ lane: 'setup', event })),
      { lane: 'setup', event: progress },
    ],
    !finishing,
  );
  // PAIGE: the field design, question by question (setup-design-progress.ts).
  const designView = useSetupDesign(finishing ? null : progress, clock);
  const working = withSetupDesign(
    workingReadout(workingState, workingNow, { unit: { preparing: 'samples prepared' } }),
    designView,
  );
  const latestPreviewTask = useMemo(
    () =>
      [...work]
        .filter((task) => Boolean(task.previewPath))
        .sort((left, right) => right.updatedAt - left.updatedAt)[0],
    [work],
  );
  // The newest page the engine named is the one current document; the work
  // list only stands in for a screen restored before any event arrived, and the
  // last page this screen showed for a remount after every page was read
  // (setup-handoff.ts: never "Opening your files" while values are pulled).
  const rememberedPage = useMemo(() => setupPage(jobId), [jobId]);
  const latestPreviewPath =
    importing?.previewPath ??
    workingState.current?.previewPath ??
    latestPreviewTask?.previewPath ??
    rememberedPage ??
    null;
  // Each page stays up long enough to be seen and for its reading picture to play (about
  // 2.4 s): pages named several times a second left the page area empty more often than
  // not (learning-evidence.tsx).
  const loadedPreview = useLearningPreview(latestPreviewPath, SETUP_PAGE_DWELL_MILLISECONDS);
  useEffect(() => {
    if (loadedPreview?.path) noteSetupPage(jobId, loadedPreview.path);
  }, [jobId, loadedPreview?.path]);
  const preview = loadedPreview?.source;
  const previewIdentity =
    workingPreviewIdentity(workingState, loadedPreview?.path) ??
    learningPreviewIdentity(loadedPreview?.path, work);
  const progressStage = finishing ? 'finalizing' : working.stageId;
  const clockAnchor =
    learningClockAnchor({
      now: mountedAt.current,
      externalActivity: Boolean(externalActivity),
      representativeAnalysisRunning: status?.running === true,
      operationalCaptureActive: false,
      nativeStartedAt: status?.startedAt,
      activityStartedAt: activity[0]?.at,
      captureElapsedMilliseconds: progress?.elapsedMilliseconds,
    }) ?? mountedAt.current;
  const elapsedMilliseconds = Math.max(progress?.elapsedMilliseconds ?? 0, clock - clockAnchor);
  const shownRemaining = working.remainingMilliseconds ?? remainingMilliseconds(progress);
  const cpuTableLearning = processorOnlyExtraction && (progress?.lineItemCount ?? 0) > 0;

  const discoveredDocuments = work
    .filter((task): task is FolderWorkTask & { document: DocumentResult } => Boolean(task.document))
    .sort((left, right) => left.updatedAt - right.updatedAt)
    .map((task) => task.document);
  const reportedTypeNames = [
    ...new Set(
      (progress?.documentTypes ?? [])
        .map((name) => name.trim())
        .filter((name) => name.length > 0 && !DocumentStoreIsNonAnswer(name)),
    ),
  ];
  const discoveredTypeCards = [
    ...new Set(discoveredDocuments.map((document) => document.documentType)),
  ].map((name) => {
    const documents = discoveredDocuments.filter((document) => document.documentType === name);
    return {
      name,
      fields: [
        ...new Set(
          documents.flatMap((document) =>
            document.fields
              .filter((field) => field.name !== 'Document type' && field.name !== 'Line items')
              .map((field) => field.name),
          ),
        ),
      ],
      samples: documents.length,
      rows: documents.reduce((sum, document) => sum + document.lineItems.length, 0),
    };
  });
  const discoveredCardNames = new Set(discoveredTypeCards.map((type) => type.name));
  const typeNamesAwaitingFields = reportedTypeNames.filter(
    (name) => !discoveredCardNames.has(name),
  );
  rememberReadyType(jobId, progress?.activity);
  for (const type of progress?.activity?.design?.types ?? [])
    if (type.state === 'ready')
      rememberReadyType(jobId, {
        id: 'bootstrap:fields',
        discoveredDocumentTypes: [type.name],
        tags: readyTags(type),
      });
  const filledTypeCards = fillTypeCards(jobId, discoveredTypeCards, typeNamesAwaitingFields);
  const previewEvidence = useMemo(
    () => learningPageEvidence(loadedPreview?.path, work),
    [loadedPreview?.path, work],
  );
  // SETUP ENDS COMPLETE, AND STAYS UP WHILE ITS DOCUMENTS ARE PROCESSED (setup-handoff.ts).
  // Once its documents are staged the setup is decided: the learning part of the bar is
  // full and stays full, and the screen reads every file, "0 of 240" rising to "240 of 240",
  // offering "Start checking" once the first batch is in the job. Nothing on it starts again
  // at Prepare. (A staging failure still lands on the setup's own failure screen.)
  const setupDone = finishing && !checkingDestination && stagedDocuments !== null;
  const reading =
    importing ??
    (setupDone
      ? {
          documents: stagedDocuments || status?.totalFileCount || 0,
          read: 0,
          live: null,
          previewPath: null,
          remainingMilliseconds: null,
          jobDocuments: 0,
          firstBatchFiles: null,
          firstBatchReady: false,
        }
      : null);
  const view = describeWorking(workingState);
  // Saved progress was folded in ahead of the live engine (Try again, or a restart): while the
  // engine is still behind it, the screen says it is catching up instead of looking frozen.
  const liveRank = progress && !progress.heartbeat ? workingStageRank(progress.stage) : -1;
  const shownRank = workingStageRank(view.stageId);
  const catchUp =
    setupFloor.events.length > 0 &&
    !finishing &&
    liveRank >= 0 &&
    view.counted &&
    (liveRank < shownRank ||
      (liveRank === shownRank && shownRank <= 2 && (progress?.completed ?? 0) < view.completed))
      ? {
          done: liveRank === shownRank ? Math.max(0, progress?.completed ?? 0) : null,
          to: view.completed,
        }
      : null;
  // One color per type, the same on every chip, dot and card of this screen.
  const typeNames = useMemo(() => {
    const names =
      learnedTypes.length && (finishing || reading)
        ? learnedTypes.map((type) => type.name)
        : [...filledTypeCards.cards.map((card) => card.name), ...filledTypeCards.pending];
    return [...new Set(names)];
  }, [learnedTypes, finishing, reading, filledTypeCards]);
  const shades = useMemo(() => typeShades(typeNames.map((name) => ({ name }))), [typeNames]);
  // While learning, the chips keep one order, A to Z: a type that finishes changes its look,
  // never its place (they jumped to the front as each finished, seen October 3, 2026).
  const journeyTypes: JourneyType[] =
    learnedTypes.length && (finishing || reading)
      ? learnedTypes.map((type) => ({
          name: type.name,
          shade: shades.get(type.name) ?? 0,
          fields: type.fields,
          state: 'ready' as const,
        }))
      : [
          ...filledTypeCards.cards.map((card) => ({
            name: card.name,
            shade: shades.get(card.name) ?? 0,
            fields: card.fields,
            state:
              card.fields.length > 0
                ? ('ready' as const)
                : card.name === working.activeType
                  ? ('working' as const)
                  : ('waiting' as const),
          })),
          ...filledTypeCards.pending.map((name) => ({
            name,
            shade: shades.get(name) ?? 0,
            fields: [],
            state: name === working.activeType ? ('working' as const) : ('waiting' as const),
          })),
        ].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  // The learning bar and the reading stage never draw shorter, across a remount or an app restart too (setup-progress-floor.ts).
  const learnFloor = useRef<{ runKey: string; learn: number; read: number } | null>(null);
  if (learnFloor.current?.runKey !== setupFloor.runKey)
    learnFloor.current = { runKey: setupFloor.runKey, ...readLearnFloor(jobId, setupFloor.runKey) };
  // The reading stage is one stage across setup's own read and every file's read
  // (setup-journey-model.ts): how much of it setup's read is, fixed once counted, so the
  // files' read carries on from there instead of starting again (owner, October 10, 2026).
  // PAIGE: the count the person chose, before the folder's own (pages, for a multi-page TIFF, in Paige).
  const selectionFiles = setupFiles(jobId) || status?.totalFileCount || null;
  const setupRead = useRef<{ weight: number; perItem: number | null }>({
    weight: 0,
    perItem: null,
  });
  if (!reading && view.stepId === 'values' && view.counted)
    setupRead.current = {
      ...setupRead.current,
      weight: Math.max(setupRead.current.weight, setupReadWeight(view.total, selectionFiles)),
    };
  // Time left that recalibrates: never an old estimate standing still (timeLeft).
  const leftMemory = useRef<LeftMemory | null>(null);
  let journeyLeft: number | null;
  if (reading) {
    // Setup's measured pace prices the files before their own pace is known. It goes through
    // timeLeft like any estimate, so it counts down and is dropped once overrun: added after it,
    // it stood still ("About 1 min" while the last document took twelve).
    const perItem = handoff?.perItemMilliseconds ?? setupRead.current.perItem;
    const priced =
      perItem && reading.documents > reading.read
        ? perItem * (reading.documents - reading.read)
        : null;
    const result = timeLeft(leftMemory.current, {
      key: 'reading:files',
      now: clock,
      done: reading.read,
      total: reading.documents,
      measured: reading.remainingMilliseconds ?? priced,
    });
    leftMemory.current = result.memory;
    journeyLeft = result.milliseconds;
  } else if (learningPart(view).part >= 4) {
    // Setup's own read, then every file at the pace setup measured, until the files' own pace is known.
    const result = timeLeft(leftMemory.current, {
      key: 'reading:setup',
      now: clock,
      done: view.counted ? view.completed : null,
      total: view.counted ? view.total : null,
    });
    leftMemory.current = result.memory;
    if (result.perItem) setupRead.current = { ...setupRead.current, perItem: result.perItem };
    const perItem = setupRead.current.perItem;
    journeyLeft =
      result.milliseconds !== null && perItem && selectionFiles
        ? result.milliseconds + perItem * selectionFiles
        : null;
  } else {
    const result = timeLeft(leftMemory.current, {
      key: `${view.stageId}:${view.stepId}`,
      now: clock,
      done: view.counted ? view.completed : null,
      total: view.counted ? view.total : null,
      measured: shownRemaining,
    });
    leftMemory.current = result.memory;
    journeyLeft = result.milliseconds;
  }
  const reasonerLine = (now: number) => {
    if (!view.question) return null;
    const line = questionLine(view.question, now)
      .replace(/^Question /, 'step ')
      .replace(/^Still working on question /, 'still on step ');
    return view.activeType
      ? `${view.activeType} · ${line}`
      : line.charAt(0).toUpperCase() + line.slice(1);
  };
  const journeyModel = reading
    ? readingModel({
        total: reading.documents,
        read: reading.read,
        live: reading.live,
        remainingMilliseconds: journeyLeft,
        documents: reading.jobDocuments ?? 0,
        firstBatchFiles: reading.firstBatchFiles ?? null,
        firstBatchReady: Boolean(reading.firstBatchReady),
        setupWeight: handoff?.setupWeight ?? setupRead.current.weight,
        readFloor: learnFloor.current.read,
      })
    : learningModel({
        view,
        now: workingNow,
        remainingMilliseconds: journeyLeft,
        questionLine: reasonerLine,
        types: journeyTypes,
        catchUp,
        learnFloor: learnFloor.current.learn,
        saving: finishing,
        checkingDestination,
        files: selectionFiles,
        readFloor: learnFloor.current.read,
        setupWeight: setupRead.current.weight,
      });
  // PAIGE: Paige's engine reports the whole field design, question by question
  // (setup-design-progress.ts); while fields are chosen the live line says how far it has got.
  const model =
    designView && journeyModel.phase === 'learning' && journeyModel.part === 3
      ? {
          ...journeyModel,
          hero: {
            ...journeyModel.hero,
            live: designView.stalled ? designView.live : designView.detail,
          },
        }
      : journeyModel;
  if (
    model.phase !== 'reading' &&
    model.phase !== 'ready' &&
    (model.bar.learn > learnFloor.current.learn || model.bar.read > learnFloor.current.read)
  )
    learnFloor.current = {
      ...learnFloor.current,
      learn: Math.max(learnFloor.current.learn, model.bar.learn),
      read: Math.max(learnFloor.current.read, model.bar.read),
    };
  const learnShown = learnFloor.current.learn;
  const readShown = learnFloor.current.read;
  useEffect(() => {
    if (learnShown > 0)
      writeLearnFloor(jobId, setupFloor.runKey, { learn: learnShown, read: readShown });
  }, [jobId, setupFloor.runKey, learnShown, readShown]);
  // What learning found, kept with the hand-off so the wizard over the job can fold it into
  // "Learned enough to start" (setup-handoff.ts); and how much of the reading stage setup's
  // own read was, so the files' read carries the stage on from there.
  const learnedResults = learningTiles(view, 5, journeyTypes, true);
  useEffect(() => {
    if (!reading && (finishing || model.part >= 4))
      noteSetupResults(jobId, {
        results: learnedResults,
        setupWeight: setupRead.current.weight || undefined,
        perItemMilliseconds: setupRead.current.perItem ?? undefined,
      });
  });
  // Telemetry (setup-signals.ts): the stage this screen shows, timed (each stage it moves on from is sent with its
  // time and pages), and its counts standing still for three visible minutes while it works (not while paused, or
  // waiting its turn behind another job).
  const telemetryStage = setupStageOf(model.part, model.phase);
  useSetupStageTelemetry(jobId, telemetryStage, reading ? reading.read : view.pageCount);
  useNoProgressTelemetry(
    jobId,
    !processingState?.pauseRequested && processingState?.status !== 'queued',
    `${telemetryStage}|${view.stageId}|${view.stepId}|${view.completed}|${view.pageCount}|${view.documentCount}|${view.question?.answered ?? ''}|${reading?.read ?? ''}`,
    clock,
    telemetryStage,
  );
  // The page's own finds, on the step that reads it: what a person looks for first.
  const finds = useMemo<JourneyFind[]>(() => {
    const page = spotPage(previewEvidence.lines);
    return [
      ...page.spots.map((spot) => ({
        kind: spot.kind,
        label: spot.kind === 'title' ? 'Looks like' : spot.label,
        value: spot.value,
      })),
      ...page.tables.map((table) => ({
        kind: 'table' as const,
        label: 'Table',
        value: `${table.rows.length.toLocaleString()} rows`,
      })),
      ...previewEvidence.barcodes
        .filter((code) => code.value?.trim())
        .slice(0, 1)
        .map((code) => ({ kind: 'barcode' as const, label: 'Barcode', value: code.value.trim() })),
    ].slice(0, 4);
  }, [previewEvidence]);
  const fold =
    reading && handoff?.results && clock - handoff.at < 20_000
      ? (handoff.results as JourneyTile[])
      : null;

  if (processingState && (processingState.pauseRequested || processingState.status === 'queued'))
    return (
      <>
        <div className="export-actions">
          {!importing && (
            <StopSetupButton
              jobId={jobId}
              status={status}
              elapsedMilliseconds={elapsedMilliseconds}
            />
          )}
          {processingState.pauseRequested && <JobProcessingButton jobId={jobId} compact />}
        </div>
        <HeldSetupProgress
          state={processingState}
          preview={preview}
          pageCount={importing ? undefined : progress?.pageCount}
          pageScope="sample"
        />
      </>
    );

  return (
    <SetupJourney
      model={model}
      // PAIGE: the files the person chose first. Paige hands the engine a multi-page TIFF as its pages, so the
      // folder's own count is pages there, and "Read all 27 files" stood over 20 chosen (first-run walk, 2026-10-11).
      total={reading ? reading.documents : setupFiles(jobId) || status?.totalFileCount || null}
      types={journeyTypes}
      activeType={finishing ? null : working.activeType}
      finds={model.phase === 'learning' && model.part === 0 ? finds : []}
      learned={reading ? journeyTypes : null}
      fold={fold}
      actions={
        finishing ? (
          headerActions
        ) : (
          <StopSetupButton
            jobId={jobId}
            status={status}
            elapsedMilliseconds={elapsedMilliseconds}
          />
        )
      }
      onStartChecking={onStartChecking}
      note={cpuTableLearning && !reading ? 'Line items take longer without a speed boost.' : null}
      previewLabel={preview && !importing ? previewIdentity.label : ''}
      pageNumber={preview && !importing ? previewIdentity.pageNumber : undefined}
      preview={
        preview ? (
          <LearningEvidencePreview
            source={preview}
            label={previewIdentity.label}
            evidence={previewEvidence}
            stage={progressStage}
          />
        ) : (
          <div className="processing-workspace-awaiting">
            <Files size={38} />
            <p>Opening your files</p>
          </div>
        )
      }
      tips={<SetupTips jobId={jobId} learningDone={model.phase !== 'learning'} />}
    />
  );
}

/** The capture queue's progress: what is being read, what was found, and the files that failed. */
function CaptureQueueStatus({
  queue,
  processingId,
  progress,
  waves,
  folder = null,
  learningPhaseLabel = 'Reading new pages',
  condensed = false,
  step = null,
  onOpenCluster,
  onRetry,
  onDiscard,
}: {
  queue: StagedCapture[];
  processingId: string;
  progress?: CaptureProcessingProgress | null;
  /** The import's step (importStepShown), so a later wave never puts the strip back at Prepare. */
  step?: string | null;
  waves?: ReadonlyMap<string, CaptureProcessingProgress>;
  folder?: FolderImportProgress | null;
  learningPhaseLabel?: string;
  condensed?: boolean;
  onOpenCluster: () => void;
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  // The cluster, watched from the batch panel while this panel exists at all —
  // which is exactly the stretch of time a peer can be helping. The main
  // process broadcasts every change; this only listens, and mounts nothing
  // network-side that was not already running.
  const [clusterView, setClusterView] = useState<ClusterView | null>(null);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !SHARED_PROCESSING_ENABLED) return;
    let cancelled = false;
    desktop
      .getCluster()
      .then((next) => {
        if (!cancelled) setClusterView(next);
      })
      .catch(() => undefined);
    const stop = desktop.onClusterChanged(setClusterView);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);
  // Accumulate findings per wave without double-counting repeated renders.
  // Counts arrive per wave, so reading them off a single wave meant every
  // hand-off dropped the found line from the whole import to the newest
  // arrival's opening numbers.
  const foundByWave = useRef<
    Map<
      string,
      { pages: number; barcodes: number; documents: number; values: number; rows: number }
    >
  >(new Map());
  const lanes = waves ? wavesByProgress(waves) : progress ? [progress] : [];
  for (const lane of lanes)
    foundByWave.current.set(lane.captureId, {
      pages: lane.pageCount,
      barcodes: lane.barcodeCount,
      documents: lane.documentCount,
      values: lane.fieldCount,
      rows: lane.lineItemCount,
    });
  // A failed capture's findings never landed in the batch — a retry starts it
  // over and re-reports from zero. Leaving its counts standing made the found
  // line claim pages and values the import does not hold (adversarial review).
  for (const item of queue) if (item.status === 'failed') foundByWave.current.delete(item.id);
  // A WAVE STILL BEING READ IS STILL WORTH REPORTING.
  //
  // The queue empties when the last sheet has been staged, not when the pages
  // have been read — so this returned null for the whole read phase. Measured on
  // a 120-sheet wave: a live counter during the feed, then FOUR MINUTES of a
  // screen saying nothing at all while the engine read every page. An operator
  // who has just fed paper and sees no movement has no way to tell working from
  // hung, and the honest answer was already in hand.
  const working = lanes.filter((lane) => lane.stage !== 'complete');
  if (queue.length === 0 && working.length === 0) return null;
  const active = queue.find((item) => item.id === processingId);
  const waiting = queue.filter(
    (item) => item.status === 'pending' && item.id !== processingId,
  ).length;
  const waitingFiles = queue
    .filter((item) => item.status === 'pending' && item.id !== processingId)
    .reduce((sum, item) => sum + item.fileCount, 0);
  // A host may hold files for pages (credit) rather than lose them: those wait, they were not interrupted.
  const waitingForPages = queue.filter((item) => item.status === 'failed' && item.waitingForPages);
  const failed = queue.filter((item) => item.status === 'failed' && !item.waitingForPages);
  const shownStep = step ?? progress?.stage ?? null;
  const stageIndex =
    shownStep === 'complete'
      ? captureStages.length
      : shownStep
        ? captureStages.findIndex((stage) => stage.id === shownStep)
        : -1;
  // Only what has actually been found — summed across every wave the import
  // has run, not read off whichever wave is speaking. A zero here is a stage
  // that has not run, and printing "0 barcodes" beside a page still being read
  // is a lie about the document rather than a fact about the batch.
  const foundTotals = [...foundByWave.current.values()].reduce(
    (sum, item) => ({
      pages: sum.pages + item.pages,
      barcodes: sum.barcodes + item.barcodes,
      documents: sum.documents + item.documents,
      values: sum.values + item.values,
      rows: sum.rows + item.rows,
    }),
    { pages: 0, barcodes: 0, documents: 0, values: 0, rows: 0 },
  );
  const found = [
    foundTotals.pages ? `${foundTotals.pages.toLocaleString()} pages` : null,
    foundTotals.barcodes ? `${foundTotals.barcodes.toLocaleString()} barcodes` : null,
    foundTotals.documents ? `${foundTotals.documents.toLocaleString()} documents` : null,
    foundTotals.values ? `${foundTotals.values.toLocaleString()} values` : null,
    foundTotals.rows ? `${foundTotals.rows.toLocaleString()} line items` : null,
  ].filter(Boolean) as string[];
  // Peers with units in flight right now. Until this line, a helped batch and
  // an unhelped one looked identical from the batch panel — the other desks'
  // work was visible only to somebody who happened to have the cluster screen
  // open on the right fifteen seconds.
  const helping = (clusterView?.cluster?.members ?? [])
    .filter((member) => !member.isThisComputer)
    .map((member) => ({
      name: member.name,
      units: member.activity.reduce((sum, entry) => sum + entry.inProgress, 0),
      pages: member.activity.find((entry) => entry.kind === 'ocr')?.inProgress ?? 0,
    }))
    .filter((member) => member.units > 0);
  const helpingLine = (() => {
    if (helping.length === 0) return null;
    const names = helping.map((member) => member.name);
    const nameList =
      names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    const units = helping.reduce((sum, member) => sum + member.units, 0);
    const reading = helping.filter((member) => member.pages > 0).map((member) => member.name);
    return (
      `${nameList} ${names.length === 1 ? 'is' : 'are'} helping - ${counted(units, 'unit')} in flight` +
      (reading.length > 0 ? ` · pages being read on ${reading.join(', ')}` : '')
    );
  })();
  return (
    <section
      className="capture-queue-status"
      aria-live="polite"
      data-queue-count={queue.length}
      data-waiting-count={waiting}
    >
      {/* Under the live workbench the header already says this. */}
      {/* A folder is one import: its whole count, never a wave's lanes or the files "queued behind" one. */}
      {!condensed && folder && (
        <div className="capture-queue-total" aria-label="Import activity">
          <span>
            <strong>{folderImportLines(folder).title}</strong>
            {folderImportLines(folder).count && <small>{folderImportLines(folder).count}</small>}
          </span>
        </div>
      )}
      {!condensed && !folder && (active || waitingFiles > 0) && (
        <div className="capture-queue-total" aria-label="Import activity">
          <span>
            <strong>Adding pages…</strong>
          </span>
        </div>
      )}
      {!condensed && !folder && active && lanes.length === 0 && (
        <div className="capture-queue-active">
          <LoaderCircle className="spin" size={13} />
          <span>
            <strong>{`Recognizing ${counted(active.fileCount, 'file')}`}</strong>
            <small>{captureDisplayName(active)}</small>
          </span>
        </div>
      )}
      {/* One lane per wave in flight, the furthest first. Up to three waves
          are real work at once — one extracting while the next reads — and
          narrating only the baton-holder made every overlap look like the
          import starting over. */}
      {!condensed &&
        !folder &&
        active &&
        lanes.slice(0, 3).map((lane, index) => (
          <div
            className={`capture-queue-active${index > 0 ? ' parallel' : ''}`}
            key={lane.captureId}
          >
            <LoaderCircle className="spin" size={13} />
            <span>
              <strong>{captureProgressLabel(lane) ?? 'Finishing the batch'}</strong>
              <small>
                {index === 0
                  ? (remainingLabel(lane) ?? lane.sourceName ?? captureDisplayName(active))
                  : 'running alongside'}
              </small>
            </span>
          </div>
        ))}

      {/* Where this batch is in work that has a known shape, rather than one
          sentence with nothing either side of it. */}
      {!condensed && active && stageIndex >= 0 && (
        <ol className="capture-stages" aria-label="Stages of processing">
          {/* The phase, named in the strip's own dead space: while the job has
              few confirmed answers this passage IS the getting-acquainted
              phase, and saying so reframes early questions as early. */}
          <li className="capture-stages-phase" aria-hidden="true">
            <Brain size={12} />
            <span>{learningPhaseLabel}</span>
          </li>
          {captureStages.map((stage, index) => (
            <li
              key={stage.id}
              className={index < stageIndex ? 'done' : index === stageIndex ? 'now' : 'next'}
            >
              <i />
              <span>{stage.short}</span>
            </li>
          ))}
        </ol>
      )}

      {/* The other desks, while they carry part of this batch. One line, and a
          way to the screen that shows every computer — not a second rendering
          of it here. */}
      {helpingLine && (
        <button
          type="button"
          className="capture-cluster-help"
          onClick={onOpenCluster}
          title="See what every computer in this group is doing"
        >
          <Handshake size={12} />
          <span>{helpingLine}</span>
        </button>
      )}

      {!condensed && active && found.length > 0 && (
        <button
          className="capture-found"
          onClick={() => setShowDetail((open) => !open)}
          aria-expanded={showDetail}
        >
          <span>{found.join(' · ')}</span>
          <ChevronDown size={12} className={showDetail ? 'flip' : ''} />
        </button>
      )}
      {!condensed && active && showDetail && (
        <dl className="capture-found-detail">
          {captureStages.map((stage, index) => (
            <div
              key={stage.id}
              className={index === stageIndex ? 'now' : index < stageIndex ? 'done' : 'next'}
            >
              <dt>{stage.short}</dt>
              <dd>
                {index === stageIndex && progress && progress.stage === shownStep && !folder
                  ? `${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()} · ${stage.label}`
                  : index < stageIndex
                    ? `done · ${stage.label}`
                    : stage.label}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {!condensed && !folder && waiting > 0 && (
        <div className="capture-queue-waiting">
          <Files size={12} />
          <span>{waitingFiles.toLocaleString()} files queued behind this one</span>
        </div>
      )}
      {/* Many failures are ONE event, not many cards. An engine outage during a
          waved import failed 189 queued waves in minutes, and rendering a card
          per wave buried the entire workspace under identical warnings. Three
          or fewer render individually; more collapse to one card whose Retry
          all re-queues everything in order. */}
      {waitingForPages.length > 0 && (
        <div className="capture-queue-waiting">
          <Files size={12} />
          <span>
            Waiting for pages ·{' '}
            {waitingForPages.reduce((count, item) => count + item.fileCount, 0).toLocaleString()}{' '}
            files
          </span>
        </div>
      )}
      {failed.length > 0 && (
        <details className="capture-import-attention">
          <summary>
            <Files size={13} />
            <span>
              Import unfinished
              <small>
                {failed.reduce((count, item) => count + item.fileCount, 0)} files in {failed.length}{' '}
                interrupted {failed.length === 1 ? 'part' : 'parts'}
              </small>
            </span>
            <ChevronDown size={12} />
          </summary>
          <p>
            The documents already added are available to review. Retry the unfinished import to add
            the remaining work.
          </p>
          <button
            type="button"
            className="import-retry"
            onClick={() => failed.forEach((item) => onRetry(item.id))}
          >
            <RotateCw size={13} />
            Retry unfinished import
          </button>
          {failed.map((item) => (
            <div className="import-interruption" key={item.id}>
              <strong>{captureDisplayName(item)}</strong>
              <small>
                {item.error?.includes('usable OCR lanes idle')
                  ? 'Some documents could not finish processing. Other files may still be processing.'
                  : /shutting down|engine.*(exited|stopped)|process.*terminated/i.test(
                        item.error ?? '',
                      )
                    ? 'Processing stopped before these files finished. Retry to continue.'
                    : 'These files could not finish processing. Retry, or see Technical details.'}
              </small>
              <button type="button" onClick={() => onDiscard(item.id)}>
                Remove from queue
              </button>
              {item.error && (
                <details>
                  <summary>Technical details</summary>
                  <small>{item.error}</small>
                </details>
              )}
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

/** One outstanding thing on a document, and where to go to deal with it. */
type ReviewReason = {
  readonly label: string;
  readonly fieldId?: string;
  readonly fieldName?: string;
  readonly documentType?: true;
  /**
   * Says nothing the type control does not already say.
   *
   * A type reason when the engine did not hold the document for its type
   * (always "Document type not confirmed yet"; any type reason once the engine
   * named other reasons): every unconfirmed document is that, and the one
   * confirm confirms the type with it. A bare reason stays in the document's
   * reasons but puts no cue on the type control (typeReviewReasons) and is not
   * a thing to check (reasonsToCheck). A type the engine is not sure of is
   * never bare: the control is marked.
   */
  readonly bare?: true;
  /**
   * A table the engine holds that no finding names (its rows miss the page's
   * total, or a line differs from the page): answered on the line items.
   */
  readonly table?: true;
  /** A whole-document finding rather than one field. */
  readonly finding?: true;
  /** Presentation routing only: never changes whether the finding still holds. */
  readonly findingCode?: string;
  /**
   * The line the finding is about, when it is about one.
   *
   * Worked out from the rows rather than parsed out of the message, so a batch
   * processed before the message named its rows still travels to the right one.
   */
  readonly lineRow?: number;
  /** Every line the finding is about, so tapping again goes to the next one. */
  readonly lineRows?: number[];
  /**
   * A question about where this document begins, rather than about a value.
   *
   * Nothing on the fields panel answers it. The operator has to look at the seam
   * — the last page of the document before and the first page of this one — and
   * decide whether they are one document or two. So this is the one item that
   * sends them to the spine.
   */
  readonly boundary?: true;
  /**
   * Where on the page the thing lives, so the viewer can go there.
   *
   * Naming a field and leaving the operator to find it on the page is half an
   * instruction. The evidence is already recorded against every value the reader
   * took off the document; this carries it as far as the control that acts on it.
   */
  readonly pageNumber?: number;
  readonly evidenceLineIndexes?: number[];
};

/** Where a reason is answered, to place it in the engine's order (review-wait-reasons.ts). */
const reviewReasonCue = (reason: ReviewReason): WaitCue =>
  reason.boundary
    ? 'start'
    : reason.documentType
      ? 'type'
      : reason.lineRow || reason.table
        ? 'table'
        : reason.fieldName
          ? 'field'
          : 'other';

/**
 * The reader's "may be incomplete" finding in the operator's words. The engine
 * writes "The pages show about 40 amount-shaped lines but only 3 table rows were
 * read - the table may be incomplete."; anything else is shown as written.
 */
function possiblyIncompleteTableNote(message: string) {
  const counts = /about (\d+) amount-shaped lines but only (\d+) table rows/.exec(message);
  if (!counts) return message;
  const rows = Number(counts[2]);
  return `The table may be missing rows: the pages show about ${counts[1]} amounts, and ${rows} ${rows === 1 ? 'row was' : 'rows were'} read.`;
}

/** What Undo needs to put a saved boundary change back: the documents it replaced, and which queued change made it. */
type BoundaryUndoEntry = {
  mutation: BoundaryDocumentMutation;
  pageNumber: number | null;
  changeId: number;
};

/**
 * A split, join or undo that could not be saved, in plain words first and the
 * exact error behind Technical details. Only that boundary was put back.
 */
function boundaryFailureMessage(kind: 'split' | 'join' | 'undo', reason: unknown) {
  const plain =
    kind === 'split'
      ? "The new document start couldn't be saved, so it was taken back. Everything else is kept."
      : kind === 'join'
        ? "Joining those documents couldn't be saved, so they were put back. Everything else is kept."
        : "Undo couldn't be saved, so the documents stay as they were. Everything else is kept.";
  // PAIGE: a fault of the program itself is no detail a person can use (shared/customer-message.ts).
  const detail = typeof reason === 'string' ? reason : messageFor(reason, '');
  return detail ? `${plain}${TECHNICAL_DETAILS_MARKER}${detail}` : plain;
}

/** The documents screen's message above the panes: cleared with null, or shown with the code telemetry sends for it. */
type WorkspaceErrorSetter = {
  (message: null): void;
  (message: string | null, code: TelemetryUiErrorCode): void;
};
function CapturePreview({
  workspaceNavigation,
  onDocumentCount,
  onSetupImport,
  workspaceJobSelector,
  suspended = false,
  job,
  batch: savedBatch,
  onJobs,
  folderAnalysisStatus,
  externalActivity,
  externalFailure,
  folderAnalysisProgress,
  folderActivity,
  folderWork,
  processorOnlyExtraction,
  onDismissExternalFailure,
  onSetupStopped = () => {},
  onChooseRepresentativeSource,
  onRestored,
  onJobChange,
  onCommitJob,
  onBackToSetup,
  onProcessed,
  onBootstrapAnalyzed,
  onNotice,
  onOpenCluster,
  learningOpen = false,
  teachingDocumentId,
  onTeachingDocumentOpened,
  suggestionApplyRequest,
  onSuggestionApplyConsumed,
  teachRequested = false,
  onTeachRequestConsumed,
  canReturnToNewJob = false,
}: {
  workspaceNavigation?: ReactNode;
  onDocumentCount?: (count: number) => void;
  onSetupImport?: (report: SetupImportReport) => void;
  workspaceJobSelector?: ReactNode;
  suspended?: boolean;
  job: Job;
  batch: ProcessResult | null;
  onJobs: () => void;
  folderAnalysisStatus?: FolderAnalysisStatus;
  externalActivity?: string;
  externalFailure?: string;
  folderAnalysisProgress?: FolderAnalysisProgress;
  folderActivity: FolderActivityEntry[];
  folderWork: FolderWorkTask[];
  processorOnlyExtraction: boolean;
  onDismissExternalFailure: () => void;
  onSetupStopped?: (jobId: string) => void;
  onChooseRepresentativeSource: (job: Job, source: 'files' | 'folder') => Promise<void>;
  onRestored: (result: ProcessResult) => void;
  onJobChange: (job: Job) => void;
  onCommitJob: (job: Job) => Promise<Job>;
  onBackToSetup: () => void;
  onProcessed: (result: ProcessResult) => void;
  onBootstrapAnalyzed: (jobId: string, result: ProcessResult) => void;
  onNotice: PublishNotice;
  onOpenCluster: () => void;
  learningOpen?: boolean;
  teachingDocumentId?: string;
  onTeachingDocumentOpened?: () => void;
  suggestionApplyRequest?: SuggestionApplyRequest | null;
  onSuggestionApplyConsumed?: () => void;
  teachRequested?: boolean;
  onTeachRequestConsumed?: () => void;
  canReturnToNewJob?: boolean;
}) {
  const [health, setHealth] = useState<EngineHealth | null>(null);
  const [scanners, setScanners] = useState<ScannerDiscovery | null>(null);
  const [processing, setProcessing] = useState(false);
  const [acquiring, setAcquiring] = useState(false);
  // The physical scan on the wire, by the identity main echoes on its progress
  // and accepts on Stop. Cleared when the operator stops it, so a late result
  // or progress event from that scan can never touch the next one.
  const [scanOperationId, setScanOperationId] = useState<string | null>(null);
  const scanOperationRef = useRef<string | null>(null);
  // Stopped, and the scanner program is still finishing: the screen is usable,
  // but another physical scan is not admitted until this one has settled.
  const [scanSettling, setScanSettling] = useState(false);
  const acquisitionGeneration = useRef(0);
  const [keptScanDismissed, setKeptScanDismissed] = useState<ReadonlySet<string>>(() => new Set());
  // A fruitless acquisition answers HERE, under the control that asked, and
  // stays until dismissed. It went through setError before, which wears the
  // engine-status row — so "the scanner returned no pages" displaced "Local
  // recognition ready" and dressed a paper problem as an engine failure.
  const [acquisitionNotice, setAcquisitionNotice] = useState<string | null>(null);
  const workspaceNotice = useContext(WorkspaceNoticeContext);
  // The notice as it is now, for an answer that arrives after the render that asked (a delete's Undo).
  const workspaceNoticeRef = useRef(workspaceNotice);
  workspaceNoticeRef.current = workspaceNotice;
  const processingState = useJobProcessingState(job.id);
  const [captureQueue, setCaptureQueue] = useState<StagedCapture[]>([]);
  // The batch claims below are also called from async admission loops (a page turn waits up to
  // two minutes) that keep the render they started in; reading this ref lets a wave that finished
  // meanwhile stop counting as pending there.
  const captureQueueRef = useRef(captureQueue);
  captureQueueRef.current = captureQueue;
  // An empty initial array means "not loaded" rather than "there is no queued
  // work". A schema refresh must not claim the batch in that startup window.
  const [stagedCapturesLoaded, setStagedCapturesLoaded] = useState(false);
  const captureAdmissionBlocked = useSyncExternalStore(
    useCallback((listener: () => void) => subscribeCaptureDrainGate(job.id, listener), [job.id]),
    useCallback(() => captureDrainBlocked(job.id), [job.id]),
    () => false,
  );
  const [captureProcessingId, setCaptureProcessingId] = useState('');
  // One lane per wave in flight, keyed by capture. Up to three waves overlap —
  // reading ahead while an earlier wave extracts — and holding a single
  // progress object meant only the baton-holder was ever narrated: every
  // hand-off read as the import starting over at "Preparing".
  const [captureWaves, setCaptureWaves] = useState<ReadonlyMap<string, CaptureProcessingProgress>>(
    new Map(),
  );
  /** Every capture progress event of the last flush, in order, for the working screen's one state. */
  const [workingEvents, setWorkingEvents] = useState<readonly CaptureProcessingProgress[]>([]);
  const [incomingPages, setIncomingPages] = useState<
    ReadonlyMap<
      string,
      { captureId: string; pageNumber: number; previewPath: string; label: string }
    >
  >(new Map());
  // Where only one wave may speak — the workbench header, the spine chip —
  // the furthest-along lane speaks for the import.
  const captureProgress = furthestAlongWave(captureWaves);
  // NAMES BELONG TO THE JOB, NOT TO A WAVE.
  //
  // This was derived from wave state, which is the wrong place for it in three
  // separate ways, each measured on a 120-page scanner bootstrap:
  //   - pendingCaptureWaves is emptied on EVERY flush tick, so the guard against
  //     a typeless event erasing the list only ever spanned one interval;
  //   - the committed wave map is overwritten wholesale by the next event, so
  //     the erasure it was meant to prevent happened anyway, one tick later;
  //   - the wave RETIRES when its promise resolves, and that happens before the
  //     understanding stage names a single type. Measured: zero events during
  //     the 170 seconds the panel was on screen, then 44 type-carrying events
  //     after it had been torn down.
  //
  // A kind the engine has named in this job is a fact about the job. It is
  // recorded from the raw event before any wave or in-flight filter, and
  // forgotten only when the job changes.
  const namedTypes = useRef<Set<string>>(new Set());
  const [seenTypeNames, setSeenTypeNames] = useState<string[]>([]);
  // DOCUMENTS BEING READ RIGHT NOW.
  //
  // The engine understands about eight documents at once and reports only
  // COMPLETIONS, so the counter cannot move until a whole wave lands together.
  // Measured on a 120-page first run: "0 of 61" for four minutes at 100% GPU,
  // then 0 -> 15 in a single step. Nothing was wrong and the operator was
  // watching a number that could not move; reported as the app being stuck and
  // "users are going to kill it for sure".
  //
  /**
   * Whether the engine is still understanding this job: some wave has reached that stage and
   * not moved past it. Each wave's stage only rises (foldWaveStage), so a late "recognizing"
   * report, or the next wave reading pages, no longer flips the first pass to review and back.
   */
  const waveStages = useRef<ReadonlyMap<string, number>>(new Map());
  const [engineUnderstanding, setEngineUnderstanding] = useState(false);
  useEffect(() => {
    namedTypes.current = new Set();
    setSeenTypeNames([]);
    waveStages.current = new Map();
    setEngineUnderstanding(false);
  }, [job.id]);
  const [captureWork, setCaptureWork] = useState<FolderWorkTask[]>([]);
  const [captureActivity, setCaptureActivity] = useState<FolderActivityEntry[]>([]);
  const [batch, setBatch] = useState<ProcessResult | null>(savedBatch);
  // App decides whether this is the document workbench (one status bar, the
  // workbench layout); tell it what is actually on screen, live documents included.
  const shownDocuments = batch?.documents.length ?? 0;
  useEffect(() => {
    onDocumentCount?.(shownDocuments);
  }, [onDocumentCount, shownDocuments]);
  const [loadedDocumentIds, setLoadedDocumentIds] = useState<Set<string>>(
    () =>
      new Set(
        savedBatch?.workspaceProjection
          ? (savedBatch.loadedDocumentIds ?? [])
          : (savedBatch?.documents.map((item) => item.id) ?? []),
      ),
  );
  const [loadingDocumentId, setLoadingDocumentId] = useState('');
  const loadedDocumentIdsRef = useRef(loadedDocumentIds);
  // Folder (including subfolders) is the default. Device discovery adds choices
  // but never changes this default or an operator's explicit selection.
  // PAIGE: in a browser a job set up from chosen files adds files: Add pages opened the browser's
  // folder picker and its "upload every file?" prompt for them (first-run walk, 2026-10-11).
  const [sourceKey, setSourceKey] = useState(() =>
    hostHas('local-storage') || job.bootstrapMode === 'folder' ? 'folder' : 'files',
  );
  const chooseSource = (key: string) => {
    setSourceKey(key);
  };
  // With several scanners, Scan paper uses the one scanned with last (owner, October 4, 2026).
  const [lastScanner, setLastScanner] = useState(readLastScanner);
  // A demonstration workstation is exactly the machine with no scanner
  // attached, so simulator availability is discovered separately from device
  // discovery. Files and a whole folder are always real choices; scanner and
  // simulator discovery only add to that baseline list.
  const [simulatorTrayFound, setSimulatorTray] = useState<SimulatedScannerTray | null>(null);
  const [simulatorStacks, setSimulatorStacks] = useState<SampleStack[]>([]);
  // The stack the simulator opens on: a sample job's own set, else the set that
  // fits this job's kind (it used to open on Freight whatever the job was).
  const [simulatorCorpus, setSimulatorCorpus] = useState<string | null>(null);
  // The tour feeds only its chosen documents of that stack.
  const [simulatorDocuments, setSimulatorDocuments] = useState<string[] | undefined>(undefined);
  // The tour's own job shows the tour card where the first-export card would be.
  const tour = useTour();
  const touring = tour?.jobId === job.id;
  // PAIGE: Simulate a scanner only on the sample tour's own job. CapturePoint offers it on every job
  // (a demonstration tool on the operator's computer); in Paige it put sample invoices into a
  // customer's own job, at their credits (first-run walk, 2026-10-06; owner, the same day).
  const simulatorTray = touring && !tour?.own ? simulatorTrayFound : null;
  useEffect(() => {
    // "Try with sample documents" hands its new job here with the set chosen.
    const openRequested = () => {
      // PAIGE: the new-job route opens the tour's job on the job's own route, whose channel reads
      // its pages; a workspace that comes up here before the route changes leaves the scan to it.
      if (window.paigeWorkbench?.createOnOpen) return;
      const requested = takeSampleScan(job.id);
      if (!requested) return;
      // The tour's five go straight in, with no scanner window (owner, 2026-10-04).
      if (requested.documents) {
        void startTourScan(job.id, requested.corpus, requested.documents)
          .then((capture) => {
            if (capture)
              setCaptureQueue((current) =>
                current.some((item) => item.id === capture.id) ? current : [...current, capture],
              );
          })
          .catch((reason) =>
            onNotice(
              reason instanceof Error
                ? reason.message
                : "The tour's sample documents could not be fed.",
              'tour-samples-not-fed',
            ),
          );
        return;
      }
      setSimulatorCorpus(requested.corpus);
      setSimulatorDocuments(requested.documents);
      setSimulatorOpen(true);
    };
    openRequested();
    window.addEventListener(SAMPLE_SCAN_EVENT, openRequested);
    return () => window.removeEventListener(SAMPLE_SCAN_EVENT, openRequested);
  }, [job.id]);
  // True while the job's last batch is still being read from disk. Rendering
  // the empty "Feed it pages" screen during that beat made every open of a
  // working job flash the new-job state before the real workspace appeared.
  const [openingSavedBatch, setOpeningSavedBatch] = useState(!savedBatch);
  // The documents are in hand (or the job really is empty): Paige drops its held skeleton.
  useEffect(() => {
    if (!openingSavedBatch) window.paigeWorkbench?.opened?.();
  }, [openingSavedBatch]);
  // What the job is doing while that wait runs long, retained by the main
  // process rather than by this component — which is the point, because this
  // component is the thing that went away. Null on the ordinary open, where the
  // wait is a beat rather than a stage of work worth narrating.
  const [openingProgress, setOpeningProgress] = useState<LiveCaptureProgress | null>(null);
  // The capture that was already running when this view mounted (a reload, a
  // restart, coming back to the job): its live events are taken again from here
  // so the working screen is rebuilt, never the bare "still reading" card. The
  // ref is what the progress listener reads; the state drives the render.
  const rejoinedCapture = useRef('');
  const [rejoinedCaptureId, setRejoinedCaptureId] = useState('');
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [breakSheetsOpen, setBreakSheetsOpen] = useState(false);
  const [spineMenuOpen, setSpineMenuOpen] = useState(false);
  const [scanProblem, setScanProblem] = useState<{
    source: string;
    sourceKey: string;
    problem: ScanProblem;
    keptPagesNotQueued?: boolean;
  } | null>(null);
  // Like any menu, the page-source menu closes on a click or focus anywhere else.
  useEffect(() => {
    if (!spineMenuOpen) return;
    const outside = (event: Event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.spine-actions'))
        setSpineMenuOpen(false);
    };
    window.document.addEventListener('pointerdown', outside, true);
    window.document.addEventListener('focusin', outside, true);
    return () => {
      window.document.removeEventListener('pointerdown', outside, true);
      window.document.removeEventListener('focusin', outside, true);
    };
  }, [spineMenuOpen]);
  /** What "Add pages" will actually use, named in the control and its tooltip. */
  const sourceLabel =
    sourceKey === 'files'
      ? 'files on this computer'
      : sourceKey === 'folder'
        ? 'a folder and all its subfolders'
        : sourceKey === 'simulator'
          ? 'the simulated scanner'
          : (scanners?.sources[Number(sourceKey.split(':')[1])]?.name ?? 'the selected scanner');
  const sourceIcon = (size: number) =>
    sourceKey === 'files' ? (
      <FileUp size={size} />
    ) : sourceKey === 'folder' ? (
      <FolderOpen size={size} />
    ) : (
      <ScanLine size={size} />
    );
  const acquisitionWorkingVerb =
    sourceKey === 'files' ? 'Selecting' : sourceKey === 'folder' ? 'Adding' : 'Scanning';
  const [selectedDocument, setSelectedDocument] = useState(() =>
    openingDocumentIndex(
      savedBatch?.documents ?? [],
      calibrationAsks(job.learningStance, job.reviewPolicy?.processingMode),
    ),
  );
  /**
   * The document already on screen when a paged batch opens has to be loaded too.
   *
   * A large batch travels as skeletons, hydrated one at a time, and the only
   * thing that hydrates is `selectDocument` — which nothing calls for the
   * document that is ALREADY selected when the batch opens. So opening a batch
   * big enough to be paged showed its first document with every field empty and
   * no line items while the values sat intact in the store: confirmed by calling
   * getBatchDocument for that exact document and getting "Total=-61337.42",
   * "Credit Memo Number=CYBCM-63759" and five rows straight back. This is the
   * reported "it knows the type on the left and the right is blank" — a
   * projection never filled in, not lost data.
   *
   * It lives HERE, above every early return, and reaches the loader through a
   * ref. Placed next to `selectDocument` where it reads more naturally, it sat
   * below `if (!document) return …`, so on any render without a selected document
   * the hook did not run and React tore the whole workspace down with "rendered
   * more hooks than during the previous render". A blank screen is a worse bug
   * than the one being fixed.
   */
  const selectTeachingDocumentRef = useRef<((id: string) => boolean) | null>(null);
  selectTeachingDocumentRef.current = null;
  useEffect(() => {
    if (!teachingDocumentId || !batch || batch !== savedBatch) return;
    if (selectTeachingDocumentRef.current?.(teachingDocumentId)) onTeachingDocumentOpened?.();
  }, [teachingDocumentId, batch, savedBatch]);
  // Support access (ADR-0088). Pages a support request queued for this job join this
  // view's queue and are read and shown here, as pages queued here are (queueKeptScanPages).
  useEffect(
    () =>
      window.capturePointDesktop?.onJobsChanged?.((event) => {
        if (event.jobId !== job.id || event.change !== 'documents') return;
        void window.capturePointDesktop
          ?.listStagedCaptures(job.id)
          .then((staged) => {
            if (!mounted.current) return;
            setCaptureQueue((current) => {
              const known = new Set(current.map((item) => item.id));
              const added = staged.filter((item) => !known.has(item.id));
              return added.length > 0 ? [...current, ...added] : current;
            });
          })
          .catch(() => undefined);
      }),
    [job.id],
  );
  // Confirm on one document, asked of this workspace by the shell (support-window.ts): the
  // document is selected as a click selects it, then this workspace's own confirmation runs.
  // Both are assigned below the early return, where they live.
  const supportSelectDocumentRef = useRef<((id: string) => boolean) | null>(null);
  const supportConfirmRef = useRef<
    ((onFailed?: (documentId: string) => void) => Promise<boolean>) | null
  >(null);
  supportSelectDocumentRef.current = null;
  supportConfirmRef.current = null;
  // One request at a time, ended by a different selection, a time limit or this view closing
  // (support-confirmation.ts), so a missed selection can't confirm the document later.
  const supportConfirmation = useRef<PendingSupportConfirmation | null>(null);
  supportConfirmation.current ??= new PendingSupportConfirmation({
    setTimeout: (callback, milliseconds) => window.setTimeout(callback, milliseconds),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
  });
  const [supportConfirmRequest, setSupportConfirmRequest] = useState(0);
  useEffect(() => {
    const withdraw = registerWorkspaceSupportHandler(
      'confirm-document',
      job.id,
      (args) =>
        new Promise((resolve, reject) => {
          const documentId = supportArgument(args, 'documentId');
          if (supportConfirmation.current!.busy) {
            reject(new Error('Another confirmation asked by support is still running.'));
            return;
          }
          if (!supportSelectDocumentRef.current) {
            reject(new Error("This job's documents are still opening. Try again in a moment."));
            return;
          }
          if (!supportSelectDocumentRef.current(documentId)) {
            reject(new Error("That document is not in this job's current documents."));
            return;
          }
          supportConfirmation.current!.begin({ documentId, settle: resolve, fail: reject });
          setSupportConfirmRequest((value) => value + 1);
        }),
    );
    return () => {
      withdraw();
      supportConfirmation.current!.cancel();
    };
  }, [job.id]);
  useEffect(() => {
    const confirmSelected = supportConfirmRef.current;
    if (!supportConfirmation.current!.busy || !confirmSelected) return;
    const pending = supportConfirmation.current!.take(
      batchRef.current?.documents[selectedDocument]?.id,
    );
    if (!pending) return;
    let failed = false;
    void confirmSelected(() => {
      failed = true;
    }).then(
      async (started) => {
        if (!started) {
          pending.fail(new Error('Paige did not confirm this document. The window shows why.'));
          return;
        }
        while (confirmingDocumentsRef.current.has(pending.documentId))
          await new Promise((resolve) => window.setTimeout(resolve, 200));
        if (failed)
          pending.fail(new Error('The confirmation was not saved. The window shows why.'));
        else pending.settle({ confirmed: pending.documentId });
      },
      (reason: unknown) =>
        pending.fail(
          reason instanceof Error ? reason : new Error('The document could not be confirmed.'),
        ),
    );
  }, [supportConfirmRequest, selectedDocument, batch]);
  const selectTeachingExampleRef = useRef<(() => boolean) | null>(null);
  selectTeachingExampleRef.current = null;
  useEffect(() => {
    if (!teachRequested || !batch || batch !== savedBatch) return;
    if (selectTeachingExampleRef.current?.()) onTeachRequestConsumed?.();
  }, [teachRequested, batch, savedBatch]);
  const hydrateDocumentRef = useRef<(index: number) => void>(() => {});
  const hydrationAttemptedDocuments = useRef<WeakSet<DocumentResult>>(new WeakSet());
  const selectedHydrationCandidate = batch?.documents[selectedDocument];
  useEffect(() => {
    if (
      batch?.workspaceProjection !== 'paged' ||
      !batch.batchId ||
      !selectedHydrationCandidate ||
      (loadedDocumentIds.has(selectedHydrationCandidate.id) &&
        selectedHydrationCandidate.sourcePath) ||
      loadingDocumentId === selectedHydrationCandidate.id ||
      hydrationAttemptedDocuments.current.has(selectedHydrationCandidate)
    )
      return;
    // One attempt per actual skeleton object, not one attempt per document key.
    // Each append can replace the selected skeleton while an earlier request is
    // in flight; when that request fails closed, this effect sees the newer
    // object and retries after a short yield. A transport failure on an unchanged
    // object does not spin forever, and a deliberate navigation still calls the
    // loader directly.
    const timer = window.setTimeout(() => hydrateDocumentRef.current(selectedDocument), 75);
    return () => window.clearTimeout(timer);
  }, [
    batch?.batchId,
    batch?.workspaceProjection,
    selectedDocument,
    selectedHydrationCandidate,
    loadedDocumentIds,
    loadingDocumentId,
  ]);
  const [selectedPage, commitSelectedPage] = useState(0);
  const [pageNavigationRevision, commitPageNavigationRevision] = useState(0);
  const selectedPageIntent = useRef(0);
  const readerSelectionSync = useRef({
    frame: 0,
    revision: 0,
    navigationPending: false,
    scrollSelection: null as { documentId: string | undefined; pageIndex: number } | null,
    programmedPosition: null as { viewer: HTMLDivElement; top: number; left: number } | null,
  });
  // Every explicit page command retires queued reader-scroll observations.
  // Keep requested pages synchronous so a rapid run of commands cannot lose
  // steps while React is committing the preceding selection.
  const setSelectedPage = useCallback((update: number | ((current: number) => number)) => {
    const sync = readerSelectionSync.current;
    sync.revision += 1;
    if (sync.frame) window.cancelAnimationFrame(sync.frame);
    sync.frame = 0;
    sync.scrollSelection = null;
    const next = typeof update === 'function' ? update(selectedPageIntent.current) : update;
    if (next !== selectedPageIntent.current) {
      sync.navigationPending = true;
      // A batched forward/back command can end on the original page. It still
      // needs a commit to release scroll ownership, even if the index is equal.
      commitPageNavigationRevision(sync.revision);
    }
    selectedPageIntent.current = next;
    commitSelectedPage(next);
  }, []);
  // A click, right-click or drag-select on a page selects that page where it already is. In
  // reading mode a plain page selection scrolls the page to the top, so on a partly visible page
  // the menu opened over other text and a double-click's second click picked another line.
  const selectGesturePage = useCallback(
    (index: number, documentId: string | undefined) => {
      setSelectedPage(index);
      readerSelectionSync.current.scrollSelection = { documentId, pageIndex: index };
    },
    [setSelectedPage],
  );
  /* One list of every document (owner and Dustin, 2026-10-30): what it asks about follows the job's
     Calibration (document-work-filter.ts, shared/calibration.ts). On Autopilot Calibration is paused (owner,
     2026-10-31): no teaching examples asked, no Calibration tag; it resumes on Manual. */
  const calibrating = calibrationAsks(job.learningStance, job.reviewPolicy?.processingMode);
  const calibrationOn = isCalibrating(job.learningStance);
  // The paperclip's words, said before the click (owner, 2026-10-30): a join may ask about a few like it while the
  // job checks document starts (DocumentStore.JoinLookalikes), on Manual only: Autopilot asks no one (owner,
  // 2026-10-31). The list and the reader say the same.
  const joinTooltip =
    job.reviewPolicy?.boundaryEnabled === false ||
    pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed'
      ? 'Join with previous document'
      : 'Join with previous document · may mark a few like it to check';
  /* A document on its way to the exceptions folder (ADR-0096; owner, 2026-10-31): nothing is left for a person, but it
     is not done either, so it wears a neutral "Goes to exceptions" note, never the calm green check. The engine
     honours the route only on Autopilot. */
  // PAIGE: no exceptions folder (NoExceptionsFolder): a route stored before it was turned off waits for a person in
  // Needs review (SpineHold.WaitsOnRoute), so it is shown as any document waiting for one, never "Goes to exceptions".
  const exceptionsRouteOf = (item: { exceptionRoute?: DocumentResult['exceptionRoute'] }) =>
    hostHas('desktop-export') && pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed'
      ? (item.exceptionRoute ?? null)
      : null;
  // The document open here, kept for a tour started from this job: Leave tour comes back to it
  // (Brandy, 2026-10-05; tourOriginOf in tour.ts).
  const shownDocumentId = batch?.documents[selectedDocument]?.id;
  useEffect(() => {
    if (shownDocumentId) noteOpenDocument(job.id, shownDocumentId);
  }, [job.id, shownDocumentId]);
  // On the way back from a tour that document opens again: in its filter while it still belongs
  // there, in All otherwise. Gone meanwhile (exported, removed), the job opens as it always does.
  // The selection is assigned below the early return, where selectDocument lives, so this looks
  // after every render until one has reached it (a few cheap checks until then).
  const selectReturnDocumentRef = useRef<((back: { documentId: string }) => void) | null>(null);
  selectReturnDocumentRef.current = null;
  useEffect(() => {
    if (!batch || batch !== savedBatch || !selectReturnDocumentRef.current) return;
    // PAIGE: the job opens on its own route (openJob), where its list has just arrived. The request
    // waits for the batch that holds its document, and is answered after this commit's effects: the
    // arriving list re-selects the document open before it, read from a ref one render behind.
    const wanted = tourReturnDocument(job.id);
    if (wanted && !batch.documents.some((item) => item.id === wanted)) return;
    const back = takeTourReturn(job.id);
    if (back) queueMicrotask(() => selectReturnDocumentRef.current?.(back));
  });
  // Delete's Undo (undoDelete, below the early return): the restored document opens in the one list of every
  // document, once the batch it is back in is on screen.
  const openAfterRestore = useRef<string | null>(null);
  const restoringDeletedRef = useRef(false);
  useEffect(() => {
    const documentId = openAfterRestore.current;
    if (!documentId || !batch || batch !== savedBatch || !selectReturnDocumentRef.current) return;
    if (!batch.documents.some((item) => item.id === documentId)) return;
    openAfterRestore.current = null;
    selectReturnDocumentRef.current({ documentId });
  });
  const [teachingPlan, setTeachingPlan] = useState<{
    jobId: string;
    batchId: string;
    ids: string[];
  } | null>(null);
  const teachingOrder =
    teachingPlan?.jobId === job.id && teachingPlan.batchId === batch?.batchId
      ? teachingPlan.ids
      : [];
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!batch || !api || typeof api.teachingRecommendations !== 'function') return;
    const batchId = batch.batchId;
    let current = true;
    // Coalesce streaming/confirmation updates. This is a read-only inventory,
    // never an inference call and never on the optimistic confirmation path.
    const timer = window.setTimeout(() => {
      void api
        .teachingRecommendations(job.id, batchId)
        .then((result) => {
          if (current)
            setTeachingPlan({
              jobId: job.id,
              batchId,
              ids: result?.map((item) => item.documentId) ?? [],
            });
        })
        .catch(() => {
          if (current) setTeachingPlan(null); // Ordinary navigation remains available.
        });
    }, 300);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [job.id, batch?.batchId, batch?.documents]);
  const [lineItemsMode, setLineItemsMode] = useState<LineItemsMode>(
    () => readReviewLayout(job.id).lineItemsMode,
  );
  /**
   * Whether the outstanding list is open.
   *
   * Shut by default: on a document with one thing left the bar already says what
   * it is, and an operator working a clean batch should not have to close a
   * panel on every document to see the page.
   */
  const [detachedPanels, setDetachedPanels] = useState<Record<ReviewPanelKind, boolean>>(() => {
    const preferred = new Set(readReviewLayout(job.id).detachedPanels);
    return {
      viewer: preferred.has('viewer'),
      fields: preferred.has('fields'),
      'line-items': preferred.has('line-items'),
    };
  });
  const [activeEvidenceLines, setActiveEvidenceLines] = useState<number[]>([]);
  // The field value behind the shown lines, kept with the very array it was shown with: any
  // other highlight (find, a selection) sets a new array and so shows its whole lines.
  const [activeEvidenceCitation, setActiveEvidenceCitation] = useState<{
    lines: number[];
    value: string;
    words: number[] | null;
  } | null>(null);
  const citeActiveEvidence = (lines: number[] | undefined, field: FieldResult | undefined) =>
    setActiveEvidenceCitation(
      lines && field?.value && !markEvidenceOf(field)
        ? { lines, value: field.value, words: field.evidenceWordIndexes ?? null }
        : null,
    );
  const [activeBarcodeEvidence, setActiveBarcodeEvidence] = useState<BarcodeResult | null>(null);
  const [activeLineItemRow, setActiveLineItemRow] = useState<number | null>(null);
  const [activeEvidenceTarget, setActiveEvidenceTarget] = useState<{
    documentId: string;
    pageNumber: number;
  } | null>(null);
  /**
   * What the operator was last sent to, and it stays marked until they deal with it.
   *
   * This was a flash: two pulses of a pale blue ring, then gone. Watching real
   * operators use it, they missed it — "it blinks twice and goes away and they
   * barely see it". Of course they did. The eye is still on the list at the
   * moment of the click, the movement is over before it arrives, and what it
   * leaves behind is nothing.
   *
   * A marker that persists is not clutter here: there is exactly one at a time,
   * it names the single thing the operator chose to work on, and it goes when
   * they have worked on it. That is a different object from the review dots,
   * which describe the document; this describes the task in hand.
   */
  const [arrivedFieldId, setArrivedFieldId] = useState<string | null>(null);
  const [imageSelectionSerial, setImageSelectionSerial] = useState(0);
  const [imageSelectedCell, setImageSelectedCell] = useState<LineItemCell | null>(null);
  const [arrivedLineRow, setArrivedLineRow] = useState<number | null>(null);
  /** The document whose opening page is in question, marked in the spine. */
  const [arrivedBoundary, setArrivedBoundary] = useState<number | null>(null);
  // Hover is deliberately NOT state here; see fieldHoverStore.
  const setHoveredFieldId = fieldHoverStore.set;
  const [pinnedFieldIds, setPinnedFieldIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewImagePath, setPreviewImagePath] = useState<string | null>(null);
  const [previewSize, setPreviewSize] = useState({ width: 1, height: 1 });
  // The message above the panes, and the code telemetry sends for it instead of its words: every message names it.
  const [errorShown, setErrorShown] = useState<{
    message: string;
    code: TelemetryUiErrorCode;
  } | null>(null);
  const error = errorShown?.message ?? null;
  const setError = useCallback(
    ((message: string | null, code?: TelemetryUiErrorCode) =>
      setErrorShown(
        message === null ? null : { message, code: code ?? 'other' },
      )) as WorkspaceErrorSetter,
    [],
  );
  // Background work (a wave starting, the automatic schema refresh) clears only the message it
  // raised itself: clearing every message wiped what the operator was in the middle of reading.
  const clearErrorOf = useCallback(
    (...codes: TelemetryUiErrorCode[]) =>
      setErrorShown((current) => (current && codes.includes(current.code) ? null : current)),
    [],
  );
  // Both messages sit above the panes; one that arrives mid-click waits for the release.
  const shownError = useAfterPointerRelease(error);
  // A refusal main gave because the trial or license ended is that, whichever action met it.
  const errorCode: TelemetryUiErrorCode = !errorShown
    ? 'other'
    : isTrialWall(errorShown.message)
      ? 'work-refused'
      : errorShown.code;
  const shownErrorCode: TelemetryUiErrorCode =
    shownError && shownError === error ? errorCode : 'other';
  const shownAcquisitionNotice = useAfterPointerRelease(acquisitionNotice);
  const [folderRecoveryActive, setFolderRecoveryActive] = useState(false);
  const [folderRecoveryError, setFolderRecoveryError] = useState('');
  const [learningClock, setLearningClock] = useState(Date.now());
  const learningStartedAt = useRef(Date.now());
  const activityFeedRef = useRef<HTMLDivElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [findIndex, setFindIndex] = useState(0);
  // Splits, joins and their undo, in the order the operator asked for them
  // (boundary-queue.ts). The count is what admission elsewhere reads: an
  // operator's boundary answer that is waiting for its turn is still an answer
  // on its way to durable storage, so whole-batch passes and CPU waves wait
  // for it the way they wait for a field save.
  const boundaryQueueDepth = useRef(0);
  const boundaryWaitsForCapture = useRef(false);
  const boundaryTurnRef = useRef<(item: BoundaryQueueItem) => BoundaryTurn>(() => 'wait');
  const boundaryRunRef = useRef<(item: BoundaryQueueItem) => Promise<'done' | 'retry'>>(
    async () => 'done',
  );
  const saveLaneWaiters = useRef(new Set<() => void>());
  const signalSaveLaneChangeRef = useRef<() => void>(() => undefined);
  const boundaryQueue = useMemo(
    () =>
      new BoundaryQueue({
        turn: (item) => boundaryTurnRef.current(item),
        run: (item) => boundaryRunRef.current(item),
        nextChange: () => nextSaveLaneChange(),
        // Admission elsewhere and the capture drain are told when either fact
        // changes: how many operator changes wait, and whether the first one
        // waits only for the pages being read.
        changed: (items, waitingForCapture) => {
          const depth = items.filter((item) => item.kind !== 'recheck').length;
          if (
            depth === boundaryQueueDepth.current &&
            waitingForCapture === boundaryWaitsForCapture.current
          )
            return;
          boundaryQueueDepth.current = depth;
          boundaryWaitsForCapture.current = waitingForCapture;
          signalSaveLaneChangeRef.current();
        },
      }),
    [],
  );
  const boundaryQueueItems = useSyncExternalStore(
    boundaryQueue.subscribe,
    boundaryQueue.getSnapshot,
  );
  const schemaRefreshBusyRef = useRef(false);
  // Save lanes are ref-owned because admission has to close in the same event
  // turn as the gesture. This revision is only the scheduler wake-up: a CPU
  // wave that found one of those refs occupied must be reconsidered when the
  // owner enters or leaves, including a line edit that exits before it ever
  // reaches the visible `lineSaving` state.
  const [saveLaneRevision, setSaveLaneRevision] = useState(0);
  const saveLaneGeneration = useRef(0);
  const captureDrainGateOwner = useRef<CaptureDrainGateOwner | null>(null);
  if (!captureDrainGateOwner.current)
    captureDrainGateOwner.current = new CaptureDrainGateOwner(job.id, 'saving document work');
  const signalSaveLaneChange = () => {
    saveLaneGeneration.current += 1;
    captureDrainGateOwner.current!.setBlocked(processorWaveLocalMutationBlocked());
    if (mounted.current) setSaveLaneRevision((current) => current + 1);
    for (const wake of [...saveLaneWaiters.current]) wake();
  };
  signalSaveLaneChangeRef.current = signalSaveLaneChange;
  /**
   * Resolves when a save lane opens or closes. A capture wave finishing does not
   * signal, so anything waiting also looks again a few times a second.
   */
  function nextSaveLaneChange() {
    return new Promise<void>((resolve) => {
      const done = () => {
        window.clearTimeout(timer);
        saveLaneWaiters.current.delete(done);
        resolve();
      };
      const timer = window.setTimeout(done, 150);
      saveLaneWaiters.current.add(done);
    });
  }
  useEffect(() => {
    const unsubscribe = subscribeCaptureDrainGate(job.id, () =>
      setSaveLaneRevision((current) => current + 1),
    );
    return () => {
      unsubscribe();
      captureDrainGateOwner.current?.retireAfterSettled();
    };
  }, [job.id]);
  const wholeBatchMutationRef = useRef<{ token: symbol; label: string } | null>(null);
  const wholeBatchCompletionRef = useRef<Promise<void>>(Promise.resolve());
  const wholeBatchReleaseRef = useRef<(() => void) | null>(null);
  const claimWholeBatchMutation = (
    label: string,
    ownedTypeDocumentId?: string,
    allowCaptureWork = false,
  ) => {
    // Whole-batch readers eventually write a complete document projection. They
    // must not take a snapshot while any narrower writer is admitted, and the
    // narrower lanes make the reciprocal check below. A UI busy state alone is
    // one render too late for two gestures in the same event turn.
    const ownedConfirmationCount =
      ownedTypeDocumentId && confirmingDocumentsRef.current.has(ownedTypeDocumentId) ? 1 : 0;
    if (
      wholeBatchMutationRef.current ||
      structuralMutationRef.current ||
      boundaryRefreshesInFlight.current > 0 ||
      boundaryQueueDepth.current > 0 ||
      pageEditBusyRef.current ||
      fieldSavingRef.current ||
      (typeSavingDocumentIdRef.current &&
        typeSavingDocumentIdRef.current !== ownedTypeDocumentId) ||
      lineMutationAdmissions.current > 0 ||
      pendingLineSaves.current > 0 ||
      confirmingDocumentsRef.current.size > ownedConfirmationCount ||
      (!allowCaptureWork &&
        (captureInFlight.current.size > 0 ||
          captureQueueRef.current.some((item) => item.status === 'pending')))
    )
      return null;
    const token = Symbol(label);
    wholeBatchMutationRef.current = { token, label };
    wholeBatchCompletionRef.current = new Promise<void>((resolve) => {
      wholeBatchReleaseRef.current = resolve;
    });
    signalSaveLaneChange();
    return token;
  };
  /**
   * What a whole-batch gesture that has to wait is waiting for: the change that owns the batch, else
   * the pages still being read (the claim waits for those too), else the general batch update.
   */
  const wholeBatchWaitMessage = (action: string, captureWorkAllowed = false) =>
    `${action[0].toUpperCase()}${action.slice(1)} after ${
      wholeBatchMutationRef.current
        ? wholeBatchMutationRef.current.label
        : !captureWorkAllowed &&
            (captureInFlight.current.size > 0 ||
              captureQueueRef.current.some((item) => item.status === 'pending'))
          ? 'the pages already arriving are read'
          : 'the current batch update'
    }`;
  const releaseWholeBatchMutation = (token: symbol) => {
    if (wholeBatchMutationRef.current?.token === token) {
      wholeBatchMutationRef.current = null;
      wholeBatchReleaseRef.current?.();
      wholeBatchReleaseRef.current = null;
      signalSaveLaneChange();
    }
  };
  // Re-reads and job improvements wait for the batch instead of being refused (whole-batch-queue.ts).
  const wholeBatchQueue = useRef(new WholeBatchQueue());
  // Single-document gestures waiting for their document's lane (openFieldSaveLaneWhenFree).
  const documentLaneWaits = useRef(new Set<string>());
  const wholeBatchTurn = (
    label: string,
    stillWanted: () => boolean = () => true,
    allowCaptureWork = false,
  ): WholeBatchTurn => ({
    claim: () => claimWholeBatchMutation(label, undefined, allowCaptureWork),
    release: releaseWholeBatchMutation,
    nextChange: nextSaveLaneChange,
    stillWanted: () => mounted.current && stillWanted(),
  });
  const boundaryRefreshesInFlight = useRef(0);
  /** The documents the re-read after a split or join is reading. They are its own until it saves; structural work on
      every other document goes on beside it, because the engine merges the re-read into the batch as it then stands
      (PreserveBoundaryReclassificationState). Before 2026-11-01 the re-read held the whole batch, and every later
      split or join waited for the model. */
  const boundaryRereadingIds = useRef(new Set<string>());
  /** Whether these documents meet the re-read's (any, for work that names none). */
  const rereadOverlaps = (documentIds: Iterable<string>) => {
    const reading = boundaryRereadingIds.current;
    if (reading.size === 0) return false;
    const named = [...documentIds];
    return named.length === 0 || named.some((documentId) => reading.has(documentId));
  };
  const lineMutationAdmissions = useRef(0);
  const lineMutationAdmissionsByDocument = useRef<
    Map<
      string,
      {
        count: number;
        /** Of them, those that are not typing in a cell (a row added or removed, Looks right, a fixed row). */
        untyped: number;
        failed: boolean;
        completion: Promise<boolean>;
        resolve: (succeeded: boolean) => void;
      }
    >
  >(new Map());
  const lineMutationAdmitted = (documentId: string) =>
    (lineMutationAdmissionsByDocument.current.get(documentId)?.count ?? 0) > 0;
  /** A line item save that is not typing is admitted for this document: it moves the document on by more than its own
      typing (ownTypingRevisions), so a moved fields panel's Delete is off meanwhile (snapshot.review.deleteBusy). */
  const untypedLineMutationAdmitted = (documentId: string) =>
    (lineMutationAdmissionsByDocument.current.get(documentId)?.untyped ?? 0) > 0;
  const admitLineMutation = (documentId: string, typed = false) => {
    if (wholeBatchMutationRef.current) return false;
    const existing = lineMutationAdmissionsByDocument.current.get(documentId);
    if (existing) {
      existing.count += 1;
      if (!typed) existing.untyped += 1;
    } else {
      let resolve!: (succeeded: boolean) => void;
      const completion = new Promise<boolean>((done) => {
        resolve = done;
      });
      lineMutationAdmissionsByDocument.current.set(documentId, {
        count: 1,
        untyped: typed ? 0 : 1,
        failed: false,
        completion,
        resolve,
      });
    }
    lineMutationAdmissions.current += 1;
    signalSaveLaneChange();
    return true;
  };
  const releaseLineMutation = (documentId: string, succeeded: boolean, typed = false) => {
    lineMutationAdmissions.current = Math.max(0, lineMutationAdmissions.current - 1);
    signalSaveLaneChange();
    const existing = lineMutationAdmissionsByDocument.current.get(documentId);
    if (!existing) return;
    if (!succeeded) existing.failed = true;
    existing.count -= 1;
    if (!typed) existing.untyped -= 1;
    if (existing.count <= 0) {
      lineMutationAdmissionsByDocument.current.delete(documentId);
      existing.resolve(!existing.failed);
    }
  };
  const waitForLineMutations = async (documentId: string) => {
    let succeeded = true;
    while (lineMutationAdmitted(documentId))
      succeeded =
        (await lineMutationAdmissionsByDocument.current.get(documentId)!.completion) && succeeded;
    return succeeded;
  };
  // One ownership token spans hydration, durable page/boundary persistence and
  // any affected-document refresh. Pairwise booleans left gaps between those
  // phases where an inverse structural command could enter against the same
  // pages. Tokens also keep a stale finally from releasing a newer owner.
  const structuralMutationRef = useRef<{
    token: symbol;
    documentIds: ReadonlySet<string>;
    label: string;
  } | null>(null);
  // `betweenWaves`: the boundary queue may take its turn while captures are
  // still waiting to be read, because the capture drain does not start another
  // wave while this owner holds the batch (and yields to a queued change); only
  // a wave already being read rules it out.
  /** Whether claimStructuralMutation would admit these documents now, claiming nothing (Delete tests it before asking). */
  const structuralMutationFree = (documentIds: Iterable<string>, betweenWaves = false) =>
    !(
      wholeBatchMutationRef.current ||
      structuralMutationRef.current ||
      schemaRefreshBusyRef.current ||
      rereadOverlaps(documentIds) ||
      captureInFlight.current.size > 0 ||
      (!betweenWaves && captureQueueRef.current.some((item) => item.status === 'pending')) ||
      [...documentIds].some(lineMutationAdmitted)
    );
  const claimStructuralMutation = (
    documentIds: Iterable<string>,
    label: string,
    betweenWaves = false,
  ) => {
    const requestedDocumentIds = new Set(documentIds);
    if (!structuralMutationFree(requestedDocumentIds, betweenWaves)) return null;
    const token = Symbol(label);
    structuralMutationRef.current = { token, documentIds: requestedDocumentIds, label };
    signalSaveLaneChange();
    return token;
  };
  const releaseStructuralMutation = (token: symbol) => {
    if (structuralMutationRef.current?.token === token) {
      structuralMutationRef.current = null;
      signalSaveLaneChange();
    }
  };
  const extendStructuralMutation = (token: symbol, documentIds: Iterable<string>) => {
    const owner = structuralMutationRef.current;
    if (owner?.token !== token) return;
    structuralMutationRef.current = {
      ...owner,
      documentIds: new Set([...owner.documentIds, ...documentIds]),
    };
  };
  const structuralMutationBlocks = (documentId?: string | null) => {
    const owner = structuralMutationRef.current;
    return (
      Boolean(owner && (documentId == null || owner.documentIds.has(documentId))) ||
      rereadOverlaps(documentId == null ? [] : [documentId])
    );
  };
  /** A page turn owns the structure (and the field lane with it; sendPageTurn's label), so a
      notice about waiting names the turn rather than a boundary change or a field save. */
  const pageTurnOwnsStructure = () => structuralMutationRef.current?.label === 'turning a page';
  // Rechecking the documents either side of a boundary still writes the durable
  // batch. The structural owner stays with it until that write has settled; the
  // separate state lets the UI say it is a recheck rather than a first save.
  const [boundaryRefreshing, setBoundaryRefreshing] = useState(false);
  // Bumped by every boundary edit. A recheck that finishes after a newer edit is
  // answering a question about a batch that no longer exists, so it is dropped —
  // which is what lets the operator undo a mistake instead of queueing behind it.
  const boundaryGeneration = useRef(0);
  const [typePromptDocumentId, setTypePromptDocumentId] = useState<string | null>(null);
  // The field a review marker sent the operator to, cleared once they are there.
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const [correctionArrival, setCorrectionArrival] = useState<OperatorCorrection | null>(null);
  const [focusTypeControl, setFocusTypeControl] = useState(false);
  // Corrections a merge could not keep. One value has to win when two documents
  // that were both corrected are joined, and the earlier one does — but the
  // operator who made the other correction has to be told it is gone.
  const [supersededByMerge, setSupersededByMerge] = useState<
    { documentId: string; fieldId?: string; fieldName: string; kept: string; discarded: string }[]
  >([]);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const documentTypeSelectRef = useRef<HTMLSelectElement>(null);
  const [schemaRefreshBusy, setSchemaRefreshBusy] = useState(false);
  // The schema pass owns a whole-batch snapshot. Keep its exact completion so
  // a type confirmation can be accepted now and saved after that owner leaves.
  const schemaRefreshCompletionRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const lastSchemaRefreshAt = useRef(0);
  /**
   * Whether a speed check is measuring this computer right now.
   *
   * Bringing a batch up to the current field contract is work the product starts
   * by itself when a job is opened. It no longer cancels a running check, but a
   * measurement it ran alongside would be a measurement of a machine doing two
   * things — so it waits, and goes when the check is done.
   */
  const [speedCheckRunning, setSpeedCheckRunning] = useState(false);
  /* WHICH document's type is being saved, not merely whether one is. The
     operator can keep reviewing other documents; only another type mutation is
     serialized behind this rare re-extraction because the surrounding learning
     pass still carries one global projection. */
  const [typeSavingDocumentId, setTypeSavingDocumentId] = useState<string | null>(null);
  const typeSavingDocumentIdRef = useRef<string | null>(null);
  const typeSaveCompletionRef = useRef<Promise<void>>(Promise.resolve());
  const typeSaveReleaseRef = useRef<(() => void) | null>(null);
  const queuedTypeConfirmationIdsRef = useRef<Set<string>>(new Set());
  const [queuedTypeConfirmationIds, setQueuedTypeConfirmationIds] = useState<Set<string>>(
    () => new Set(),
  );
  const typeConfirmationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const openTypeSaveLane = (documentId: string) => {
    if (wholeBatchMutationRef.current || typeSavingDocumentIdRef.current) return false;
    typeSavingDocumentIdRef.current = documentId;
    typeSaveCompletionRef.current = new Promise<void>((resolve) => {
      typeSaveReleaseRef.current = resolve;
    });
    setTypeSavingDocumentId(documentId);
    signalSaveLaneChange();
    return true;
  };
  const closeTypeSaveLane = (documentId: string) => {
    if (typeSavingDocumentIdRef.current !== documentId) return;
    typeSavingDocumentIdRef.current = null;
    typeSaveReleaseRef.current?.();
    typeSaveReleaseRef.current = null;
    setTypeSavingDocumentId(null);
    signalSaveLaneChange();
  };
  const [addingTypeName, setAddingTypeName] = useState<string | null>(null);
  /* Which document is being re-read after a type correction. Named rather than a
     bare boolean because the operator is free to walk away from it. */
  const [retypingDocumentId, setRetypingDocumentId] = useState<string | null>(null);
  const typeRefreshOwners = useRef(new Map<string, symbol>());
  /* The type being applied, so the note names where the document is going rather
     than reading whatever it currently says. */
  const [retypingAs, setRetypingAs] = useState<string | null>(null);
  const [typeSuggestion, setTypeSuggestion] = useState<DocumentTypeSuggestion | null>(null);
  const [jobSuggestions, setJobSuggestions] = useState<JobSuggestion[]>([]);
  /* Which self-applied rules this session has already told the operator about.
     A ref, not state: announcing one must not re-run the effect that finds it.
     Primed by the job's first reply: rules applied before this view opened are not
     news, and announcing them made every old rule a new "Job improved" receipt, one
     per batch change, each with an Undo. */
  const announcedSelfApplied = useRef<{ jobId: string; primed: boolean; ids: Set<string> }>({
    jobId: job.id,
    primed: false,
    ids: new Set(),
  });
  if (announcedSelfApplied.current.jobId !== job.id)
    announcedSelfApplied.current = { jobId: job.id, primed: false, ids: new Set() };
  /* Applying what the job has learned to the work still waiting, and how much of
     it stopped needing review. Named so the operator is told what is happening
     rather than shown a spinner: work vanishing out of their queue with no
     sentence attached is worse than the work still being there. */
  const [applyingLearning, setApplyingLearning] = useState(false);
  /* The receipt for the learning pass, as a sentence with the numbers in it. This
     is the moment the product proves its promise — YOUR confirmations just took
     work off YOUR list — and it ran as a bare spinner followed by silence. If the
     pass settles nothing, the receipt says that too, with what would unlock more,
     because a spinner that vanishes wordlessly reads as nothing having happened. */
  const [learningReceipt, setLearningReceipt] = useState<string | null>(null);
  /* Learning that took OTHER documents out of Needs review: "Your confirmation also
     cleared 1 more Invoice", said once under the documents list's counts
     (cleared-news.ts). The lesson simply applies; a quiet Undo puts back what the
     engine recorded as released (DocumentStore.RejudgeHeldWork). The documents it
     released take on their green check in place, never vanishing mid-click. */
  const [clearedNews, setClearedNews] = useState<ClearedNews | null>(null);
  const [clearedNewsUndoing, setClearedNewsUndoing] = useState(false);
  const noteCleared = (documents: ClearedDocument[]) => {
    if (documents.length > 0)
      setClearedNews((previous) => addClearedDocuments(previous, documents));
  };
  /** When the last "sent back for another look" news arrived: it is said only if the count settled higher around then. */
  const backNewsArrivedAt = useRef(Number.NEGATIVE_INFINITY);
  /* Released documents pulled back into review by what the operator confirmed since: the
     same kind of line, under the counts ("Your confirmation sent 2 Invoices back for another
     look"), with the reason on each document. The engine keeps them there until someone confirms
     them, so it has no action. */
  const [backNews, setBackNews] = useState<ClearedNews | null>(null);
  const componentDownload = useRuntimeComponentStatus();
  /* What this job is closest to learning. Kept beside the suggestions rather
     than inside them because it is the opposite surface: suggestions are what
     Paige has already worked out, this is what it cannot work out yet
     and what would get it there. */
  const [learningProgress, setLearningProgress] = useState<LearningProgress[]>([]);
  const [learningAcceleration, setLearningAcceleration] =
    useState<LearningAccelerationReport | null>(null);
  const [learningAccelerationUnavailable, setLearningAccelerationUnavailable] = useState(false);
  const [jobSuggestionBusy, setJobSuggestionBusy] = useState('');
  const [lastJobImprovement, setLastJobImprovement] = useState<JobSuggestion | null>(null);
  const [jobImprovementUndone, setJobImprovementUndone] = useState(false);
  const [lastJobImprovementScope, setLastJobImprovementScope] =
    useState<SuggestionApplicationScope>('refresh-safe');
  // What the person chose when the last improvement was applied: the type's name, the documents left out and the
  // proposed fields kept (D10), so Redo after Undo applies the same choice, not the card's original one.
  // Keyed by the suggestion it was made for: a self-applied rule's receipt can replace the last improvement,
  // and its Redo must not take another suggestion's choice.
  const lastApplyChoice = useRef<{
    suggestionId?: string;
    proposedName?: string;
    excludedDocumentIds?: string[];
    keptFieldNames?: string[];
    addedTypeId?: string;
  }>({});
  const applyChoiceFor = (suggestionId: string) =>
    lastApplyChoice.current.suggestionId === suggestionId ? lastApplyChoice.current : undefined;
  const [lastJobImprovementOutcome, setLastJobImprovementOutcome] =
    useState<SuggestionApplicationOutcome>('refreshed');
  /* The stance's own receipt, and the state Undo puts back. One receipt is on
     screen at a time: throwing a switch from the apply receipt's offer replaces
     that receipt rather than stacking a second one under it. */
  const [stanceReceipt, setStanceReceipt] = useState<{
    area: StanceArea;
    action: 'stopped' | 'resumed';
  } | null>(null);
  /* ONE receipt stands in the column at a time — the newest. Receipts used to
     stack: a job improvement, a stance change, and a learning receipt could all
     hold standing space at once, and the fields — the actual work — paid for it.
     Every place that raises a receipt promotes its kind to the front of this
     order; the slot renders the first kind whose receipt is still live, and a
     dismissed or expired one lets the next-newest surface again so its Undo is
     not lost under a receipt that outlived it. */
  const [receiptOrder, setReceiptOrder] = useState<Array<'improvement' | 'stance'>>([]);
  const promoteReceipt = (kind: 'improvement' | 'stance') =>
    setReceiptOrder((current) => [kind, ...current.filter((item) => item !== kind)]);
  /* The nudge's receipt: what the Ask-less/Ask-more click changed, with the same
     Undo affordance every other job change carries. The previous job rides in it
     so Undo restores each field's bar exactly, through the same onCommitJob flow
     that recorded the nudge. */
  const [stanceBefore, setStanceBefore] = useState<LearningStance | null>(null);
  const [stanceBusy, setStanceBusy] = useState(false);
  /* The offer riding an apply receipt, under the maturity rule — never on the
     job's first acceptance, at most once while this workspace is open, and held off for
     thirty days by a dismissal. */
  const [receiptOffer, setReceiptOffer] = useState<StanceArea | null>(null);
  const offerRaisedThisSession = useRef(false);
  const improvementBell = useImprovementBell(job.id, jobSuggestions);
  const [scorecard, setScorecard] = useState<LearningScorecard | null>(null);
  const [dismissedSuggestionKey, setDismissedSuggestionKey] = useState('');
  // Set while "Start scanning instead" saves the job; neither recovery button starts meanwhile.
  const switchingToScan = useRef(false);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const retryFolderLearning = async () => {
    if (!window.capturePointDesktop || folderRecoveryActive || switchingToScan.current) return;
    setFolderRecoveryError('');
    if (folderAnalysisStatus?.state === 'recoverable' && folderAnalysisStatus.operationId) {
      setFolderRecoveryActive(true);
      try {
        const resumed = await window.capturePointDesktop.resumeFolderAnalysis(job.id);
        requireRepresentativeProposal(resumed.result);
        onBootstrapAnalyzed(job.id, resumed.result);
        onBackToSetup();
        onNotice('Resumed the saved document setup analysis');
      } catch (reason) {
        // Held first: the shell never continues a setup that just failed here by itself.
        onSetupStopped(job.id);
        reportSetupFailure(job.id, reason instanceof Error ? reason.message : '');
        setFolderRecoveryError(
          messageFor(reason, 'The saved folder analysis could not be resumed.'),
        );
      } finally {
        setFolderRecoveryActive(false);
      }
      return;
    }
    try {
      // The picker inside the try: a picker that fails says so here instead of nowhere.
      const selection =
        job.bootstrapMode === 'sample-scan'
          ? await window.capturePointDesktop.chooseRepresentativeFiles()
          : await window.capturePointDesktop.chooseDocumentFolder();
      if (selection.canceled || !selection.token) return;
      // A new run, as runRepresentativeSourceBootstrap starts one: its own file count, and not
      // the failed run's step, page, reading share or time (chunk 28, R5-C1).
      noteSetupFiles(job.id, selection.supportedFileCount ?? 0);
      forgetSetupRun(job.id);
      setFolderRecoveryActive(true);
      const result = await window.capturePointDesktop.importFolderDocuments({
        jobId: job.id,
        token: selection.token,
        documentTypes: [],
        separationMode: job.separationMode,
        separationBarcodeText: job.separationBarcodeText,
        boundaryOpeningProfiles: job.boundaryOpeningProfiles,
        boundaryContinuationProfiles: job.boundaryContinuationProfiles,
      });
      requireRepresentativeProposal(result);
      const proposed = applyBootstrapProposal(job, result);
      const saved = migrateJob(await onCommitJob(proposed));
      onJobChange(saved);
      onBootstrapAnalyzed(job.id, result);
      onBackToSetup();
      onNotice(
        `Found ${saved.documentTypes.length} provisional document type${saved.documentTypes.length === 1 ? '' : 's'}`,
      );
    } catch (reason) {
      onSetupStopped(job.id);
      reportSetupFailure(job.id, reason instanceof Error ? reason.message : '');
      setFolderRecoveryError(messageFor(reason, 'Folder analysis stopped before it began.'));
    } finally {
      setFolderRecoveryActive(false);
    }
  };
  const continueWithoutFolderLearning = async () => {
    // Never while a retry runs, and once at a time (a latch, not folderRecoveryActive, which
    // would swap this failure screen for the learning screen during a plain job save).
    if (folderRecoveryActive || switchingToScan.current) return;
    switchingToScan.current = true;
    setFolderRecoveryError('');
    try {
      const saved = migrateJob(
        await onCommitJob({
          ...job,
          bootstrapMode: 'scan',
          description: 'Built from documents as they are scanned or imported',
          updatedAt: new Date().toISOString(),
        }),
      );
      onJobChange(saved);
    } catch (reason) {
      // "Start scanning instead" clears the failure it was offered on, so a save that fails
      // must put a reason back on that screen rather than leave it saying nothing.
      if (mounted.current)
        setFolderRecoveryError(
          reason instanceof Error ? reason.message : 'The job could not switch to scanning.',
        );
    } finally {
      switchingToScan.current = false;
    }
  };
  // Any capture being read, however it was acquired. The live workbench used to be
  // gated on folder imports alone, so an operator who SCANNED got a one-line queue
  // status and nothing else while their pages were being read — blind, on what
  // looks like a progress bar, which is not how scanning works anywhere else.
  const operationalCaptureActive = Boolean(captureProcessingId);
  // A capture already running when this view mounted, until its batch is saved:
  // shown exactly like one this view started (display only; the queue drain is
  // untouched and still waits for the saved batch).
  const rejoinedShown =
    openingSavedBatch && openingProgress && rejoinedCaptureId === openingProgress.captureId
      ? rejoinedCaptureId
      : '';
  const displayCaptureActive = operationalCaptureActive || Boolean(rejoinedShown);
  const operationalRepresentativeActive =
    operationalCaptureActive && usesRepresentativeBootstrap(job);
  useEffect(() => {
    // Direct Files/scanner discovery used to have no ticking clock at all; it
    // changed only when the engine emitted another progress event. Anchor the
    // one-second display clock to the latest durable engine elapsed value, so a
    // long CPU model call still shows time passing and a retry never resets it.
    const now = Date.now();
    const anchor = learningClockAnchor({
      now,
      externalActivity: Boolean(externalActivity),
      representativeAnalysisRunning: folderAnalysisStatus?.running === true,
      operationalCaptureActive,
      nativeStartedAt: folderAnalysisStatus?.startedAt,
      activityStartedAt: folderActivity[0]?.at,
      captureElapsedMilliseconds: captureProgress?.elapsedMilliseconds,
    });
    if (anchor === null) return;
    learningStartedAt.current = anchor;
    setLearningClock(now);
    const timer = window.setInterval(() => setLearningClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [
    Boolean(externalActivity),
    operationalCaptureActive,
    captureProgress?.elapsedMilliseconds,
    folderAnalysisStatus?.running,
    folderAnalysisStatus?.startedAt,
    folderActivity[0]?.at,
    job.id,
  ]);
  useEffect(() => {
    const feed = activityFeedRef.current;
    if (feed) feed.scrollTop = 0;
  }, [folderActivity.length]);
  // BOTH CHANNELS, BECAUSE A DOCUMENT IS A DOCUMENT.
  //
  // This chose between them on operationalCaptureActive while the progress and
  // activity beside it chose on activeCapture, and those differ by exactly one
  // term — activeCapture is false while learningActivity is set, which is the
  // bootstrap screen titled "Discovering types and fields". So that screen drew
  // its progress from the folder channel and hunted its completed documents in
  // the capture channel. The panel showed "Reusable patterns will appear here"
  // for every second of every run since the screen was written, and the card
  // list it exists to show was structurally unreachable. Measured on a whole
  // 120-page run: the capture handler received ZERO events while the progress
  // bar advanced the entire way.
  //
  // Choosing the OTHER flag would only move which run is starved. Neither
  // channel is authoritative: a scanner bootstrap fills the folder one, an added
  // wave fills the capture one, and the panel is the same panel. So take both and
  // key by id, which is unique per activity in either.
  const displayedWork = useMemo(() => {
    if (captureWork.length === 0) return folderWork;
    if (folderWork.length === 0) return captureWork;
    const byId = new Map<string, FolderWorkTask>();
    for (const task of [...folderWork, ...captureWork]) byId.set(task.id, task);
    return [...byId.values()];
  }, [captureWork, folderWork]);
  const learningPreviewTasks = useMemo(() => {
    const seen = new Set<string>();
    return [...displayedWork]
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .filter((task) => {
        if (!task.previewPath || seen.has(task.previewPath)) return false;
        seen.add(task.previewPath);
        return true;
      })
      .slice(0, 8);
  }, [displayedWork]);
  // ONE PROGRESS STATE FOR THE WORKING SCREEN (working-progress.ts).
  //
  // The page image used to rotate through the last eight previews every 3.5s
  // while the header named the newest event and the counter read whichever wave
  // sorted first: three files on screen at once. Everything below is drawn from
  // this state, including which page is shown and what its caption says.
  const learningActivityShown = Boolean(
    externalActivity ||
    (folderAnalysisStatus === undefined && usesRepresentativeBootstrap(job)) ||
    folderAnalysisStatus?.running ||
    folderRecoveryActive,
  );
  const workingFromCapture = !learningActivityShown && displayCaptureActive;
  // Before the engine's first event the one fact already known is how many files
  // this capture carries: "0 of 23" and a bar from the first frame, never a blank.
  const workingCaptureFiles =
    captureQueue.find((item) => item.id === captureProcessingId)?.fileCount ?? 0;
  // A folder is ONE thing to the operator however many waves it is read in:
  // every count below is of the whole folder (folder-import-progress.ts).
  const folderImportRegistry = useRef<FolderImportRegistry>(appFolderImports);
  registerFolderImports(folderImportRegistry.current, captureQueue);
  const folderImport = folderImportProgress(
    folderImportRegistry.current,
    captureQueue,
    captureWaves,
  );
  // The import a job's setup started is its own phase, naming the finished setup
  // (setup-handoff.ts): the same five steps starting again read as setup going back.
  // PAIGE: setup reads the whole selection, so no import follows it to be labelled as its phase.
  const setupHandoff = useSetupHandoff(
    job.id,
    hostHas('setup-import-phase') && workingFromCapture && job.status === 'active'
      ? (folderImport?.id ?? (captureProcessingId || rejoinedShown || null))
      : null,
  );
  const workingSeed = useMemo(
    () =>
      captureProcessingId && workingCaptureFiles > 0
        ? { stage: 'preparing' as const, completed: 0, total: workingCaptureFiles }
        : null,
    [captureProcessingId, workingCaptureFiles],
  );
  // One import is one run, however many waves it is read in: the key names the
  // import (a folder's waves share its id), so later waves continue its steps and
  // a new import starts its own. Remembered per capture, so a remount knows it too.
  const workingImport = rememberCaptureImport(captureQueue, captureProcessingId || rejoinedShown);
  const { state: workingState, now: workingNow } = useWorkingProgress(
    `${job.id}:${workingFromCapture ? `capture:${workingImport}` : `folder:${folderAnalysisStatus?.operationId ?? ''}`}`,
    workingFromCapture
      ? [
          ...(workingSeed && !captureWaves.has(captureProcessingId)
            ? [{ lane: captureProcessingId, event: workingSeed }]
            : []),
          // What main kept for a capture this view came back to: the stage, the counts
          // and the clock, from the first frame after the remount, until its live
          // events arrive (main's copy is as old as its last report, so re-reading it
          // later would set the clock back).
          ...(rejoinedShown && openingProgress && !captureWaves.has(rejoinedShown)
            ? [{ lane: rejoinedShown, event: openingProgress }]
            : []),
          ...workingEvents.map((event) => ({ lane: event.captureId, event })),
          ...[...captureWaves].map(([lane, event]) => ({ lane, event })),
        ]
      : [{ lane: 'folder', event: folderAnalysisProgress }],
    workingFromCapture || learningActivityShown,
  );
  // The step every list of the five steps shows for this import: the capture strip too (working-progress.ts).
  const importStep = importStepShown(workingState, Boolean(setupHandoff));
  // Telemetry (setup-signals.ts). A job's first scanned pages, read with no document types yet, are its setup: the
  // stage the screen shows is timed like the setup screen's. And reading here whose counts stop moving for three
  // visible minutes is a stuck wait (not while paused or queued behind another job, nor under the setup wizard,
  // which watches its own counts).
  const workingView = describeWorking(workingState);
  const scanSetupStage =
    Boolean(captureProcessingId || rejoinedShown) &&
    job.status === 'provisional' &&
    job.documentTypes.length === 0 &&
    !usesRepresentativeBootstrap(job) &&
    workingState.rank >= 0
      ? setupStageOf(learningPart(workingView).part, 'learning')
      : null;
  useSetupStageTelemetry(job.id, scanSetupStage, workingView.pageCount);
  useNoProgressTelemetry(
    job.id,
    !suspended &&
      !onSetupImport &&
      (displayCaptureActive || learningActivityShown) &&
      !processingState?.pauseRequested &&
      processingState?.status !== 'queued',
    `${workingView.stageId}|${workingView.stepId}|${workingView.completed}|${workingView.pageCount}|${workingView.documentCount}|${workingView.question?.answered ?? ''}`,
    workingNow,
    scanSetupStage ??
      (workingFromCapture ? 'importing' : setupStageOf(learningPart(workingView).part, 'learning')),
  );
  // For the setup wizard waiting over this screen (setup-handoff.ts): the import, counted
  // whole, and whether any of it is still waiting, being read, or stopped on a failure.
  // The first batch is the import's first two waves (a tiny first wave opens review fast,
  // the next makes it worth starting): once both have left the queue read, their documents
  // are in the job and the wizard offers "Start checking".
  const trackedImport = folderImport
    ? folderImportRegistry.current.get(folderImport.id)
    : undefined;
  const firstWaves = trackedImport
    ? trackedImport.waves.filter((wave) => !trackedImport.discarded.has(wave.id)).slice(0, 2)
    : [];
  const firstBatchFiles = firstWaves.length
    ? firstWaves.reduce((sum, wave) => sum + wave.fileCount, 0)
    : null;
  const firstBatchReady =
    firstWaves.length > 0 &&
    firstWaves.every((wave) => !captureQueue.some((item) => item.id === wave.id));
  const setupImportReport = onSetupImport
    ? JSON.stringify({
        loaded: stagedCapturesLoaded,
        busy:
          captureQueue.some((item) => item.status === 'pending') ||
          Boolean(captureProcessingId) ||
          Boolean(rejoinedShown),
        failed: captureQueue.some((item) => item.status === 'failed'),
        total: folderImport?.total ?? null,
        read: folderImport?.read ?? 0,
        queuedFiles: captureQueue
          .filter((item) => item.status === 'pending')
          .reduce((sum, item) => sum + item.fileCount, 0),
        documents: batch?.documents.length ?? 0,
        firstBatchFiles,
        firstBatchReady,
        live:
          workingFromCapture && workingState.rank >= 0
            ? workingReadout(workingState, workingNow).live.text || null
            : null,
        // The page being read; once none is (values being pulled, a remount), the newest page this
        // screen saw, then the job's first document: the wizard's page area is never empty.
        previewPath:
          (workingFromCapture
            ? (workingState.current?.previewPath ?? workingState.previews[0]?.previewPath)
            : null) ??
          batch?.documents[0]?.pages[0]?.previewPath ??
          null,
        // Whole seconds: the estimate is recomputed from the clock on every render.
        remainingMilliseconds: folderImport?.remainingMilliseconds
          ? Math.round(folderImport.remainingMilliseconds / 1000) * 1000
          : null,
      } satisfies SetupImportReport)
    : '';
  useEffect(() => {
    if (onSetupImport && setupImportReport)
      onSetupImport(JSON.parse(setupImportReport) as SetupImportReport);
  }, [onSetupImport, setupImportReport]);
  const loadedLearningPreview = useLearningPreview(
    workingState.current?.previewPath ?? learningPreviewTasks[0]?.previewPath,
  );
  const loadedLearningIdentity =
    workingPreviewIdentity(workingState, loadedLearningPreview?.path) ??
    learningPreviewIdentity(loadedLearningPreview?.path, displayedWork);
  const operationalPreviewEvidence = useMemo(
    () => learningPageEvidence(loadedLearningPreview?.path, displayedWork),
    [loadedLearningPreview?.path, displayedWork],
  );
  const learningPreview = loadedLearningPreview?.source;
  const [suggestionMenuOpen, setSuggestionMenuOpen] = useState(false);
  const [fieldSaving, setFieldSaving] = useState(false);
  // React's disabled state paints on the next render. Keep the same fact in a
  // synchronous latch so a second gesture in that narrow window cannot admit a
  // competing whole-document save from the docked or detached workspace.
  const fieldSavingRef = useRef(false);
  const fieldSavingDocumentIdRef = useRef<string | null>(null);
  const fieldSaveCompletionRef = useRef<Promise<void>>(Promise.resolve());
  const fieldSaveReleaseRef = useRef<(() => void) | null>(null);
  /** Whether openFieldSaveLane would open for this document now, opening nothing: its one admission rule. */
  const fieldSaveLaneFree = (documentId: string) =>
    !(
      wholeBatchMutationRef.current ||
      fieldSavingRef.current ||
      typeSavingDocumentIdRef.current === documentId
    );
  /* The lane is held for typing: a value typed in a field (its draft binding). Its revisions are the document's own
     typing (ownTypingRevisions), which a moved fields panel's Delete bound to the revision before them may cross. Any
     other holder (a picked value, value Undo, Find the table, Recheck, a page turn, Delete) moves the document on
     without typing, so that Delete would be refused as an older document: it is off meanwhile (snapshot.review.deleteBusy). */
  const fieldSaveTypedRef = useRef(false);
  // `quiet`: the lane is held for a write the person does not wait on (a page turn running in the
  // background). Admission is exactly the same; only the editors stay open, and what is typed in
  // them is saved after it, the way any save waits its turn. `typed`: see fieldSaveTypedRef.
  const openFieldSaveLane = (documentId: string, quiet = false, typed = false) => {
    if (!fieldSaveLaneFree(documentId)) return false;
    fieldSavingRef.current = true;
    fieldSavingDocumentIdRef.current = documentId;
    fieldSaveTypedRef.current = typed;
    fieldSaveCompletionRef.current = new Promise<void>((resolve) => {
      fieldSaveReleaseRef.current = resolve;
    });
    if (!quiet) setFieldSaving(true);
    signalSaveLaneChange();
    return true;
  };
  const closeFieldSaveLane = () => {
    fieldSavingRef.current = false;
    fieldSavingDocumentIdRef.current = null;
    fieldSaveTypedRef.current = false;
    fieldSaveReleaseRef.current?.();
    fieldSaveReleaseRef.current = null;
    setFieldSaving(false);
    signalSaveLaneChange();
  };
  /* Documents whose confirm chain is still writing. Confirming used to hold the
     whole panel behind one boolean for as many round-trips as the document had
     unverified values — ten values, ten waits, a frozen column. The lock is per
     document now: the confirmed document's controls close until its chain lands,
     and every other document stays workable. The ref is the guard async code
     reads; the state is the same set for render. */
  const confirmingDocumentsRef = useRef<Set<string>>(new Set());
  // Learned projections reconcile against native operator truth. They do not own
  // the interactive save lane; coalesce rapid confirmations into one follow-up.
  const typeProjectionRunning = useRef(false);
  const pendingTypeProjection = useRef<(() => Promise<void>) | null>(null);
  const queueTypeProjection = (work: () => Promise<void>) => {
    pendingTypeProjection.current = work;
    if (typeProjectionRunning.current) return;
    typeProjectionRunning.current = true;
    void (async () => {
      try {
        while (mounted.current && pendingTypeProjection.current) {
          const next = pendingTypeProjection.current;
          pendingTypeProjection.current = null;
          // Finish an admitted operator write before taking the recheck snapshot.
          // Review stays interactive; a later write is handled by the native CAS
          // and the coalesced retry below, never by weakening correction protection.
          while (mounted.current && activeNarrowMutationDocumentIds().size > 0)
            await new Promise((resolve) => setTimeout(resolve, 50));
          if (!mounted.current) break;
          await next();
        }
      } finally {
        typeProjectionRunning.current = false;
      }
    })();
  };

  const [confirmingDocumentIds, setConfirmingDocumentIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  /** Grows with each confirmation the person starts: the list's tab numbers hold steady around it (steady-count.ts). */
  const [confirmationSerial, setConfirmationSerial] = useState(0);
  // While calibrating, a confirmation may be the one that ends Calibration (the engine decides as it saves
  // it): once confirmations settle, the job's stance is read again, so the tag gives way to the count. A paused
  // Calibration (Autopilot) can end that way too, so it is read whenever Calibration is on.
  const confirmationsInFlight = confirmingDocumentIds.size;
  useEffect(() => {
    if (!calibrationOn || confirmationsInFlight > 0 || confirmationSerial === 0) return;
    window.dispatchEvent(new CustomEvent('capturepoint-calibration-check', { detail: job.id }));
  }, [calibrationOn, confirmationsInFlight, confirmationSerial, job.id]);
  /* The one main button (single confirm, 2026-10-05). The document showing "Done ✓" for its
     half second; the one just confirmed, offered back for six seconds after a move ("✓ Confirmed ·
     Back", only while the document it moved to is open); the documents confirmed in this visit
     (All caught up says "You confirmed N"); the last document of All confirmed in place, which
     shows All caught up once nothing waits in Needs review; and a line the button refused over. */
  const [confirmBeat, setConfirmBeat] = useState<string | null>(null);
  /** The confirmation the main button is running (its Done beat included); a new one waits. */
  const mainActionBusy = useRef<symbol | null>(null);
  /** The bar is saying why the button refused; the next confirmation clears it. */
  const refusalShown = useRef(false);
  const [backOffer, setBackOffer] = useState<{ documentId: string; shownOn: string } | null>(null);
  const [confirmedInVisit, setConfirmedInVisit] = useState<ReadonlySet<string>>(new Set());
  const [allDoneArmed, setAllDoneArmed] = useState<string | null>(null);
  const [refusedLine, setRefusedLine] = useState<{ rowNumber: number; serial: number } | null>(
    null,
  );
  /** When the review last moved the operator on, or changed what the open document shows, by
   *  itself: Next, the move after Done, Skip, Back, Ctrl+PgDn/PgUp, a tab switched (the tour does
   *  it by itself), or a re-read of the open document. A press or click sooner than
   *  ARRIVAL_GUARD_MS lands on a document nobody has seen (the second click of a double-click, a
   *  double press). Opening a document by hand from the list is a look the operator chose, and
   *  the first document the screen opens on has nothing before it to be mistaken for. On
   *  performance.now()'s clock, the one an event's timeStamp is on: the guard measures from when
   *  the press was made, not from when a busy screen got round to handling it. */
  const arrivedAt = useRef(Number.NEGATIVE_INFINITY);
  /** The document such a move opened, until the screen draws it with its values loaded: the
   *  guard runs from then, not from when the move began. On a busy machine the move's render
   *  took a second, and a press made after the move but before the new document was drawn, or
   *  just after, passed a guard that had started at the move (chunk 37a review). */
  const arrivalPending = useRef<string | null>(null);
  /** A document that opened without the person choosing it (theirs left the list): not confirmed until they move or are told (B-09). */
  const movedUnder = useRef<string | null>(null);
  useEffect(() => {
    if (!backOffer) return;
    const timer = window.setTimeout(
      () => setBackOffer((current) => (current === backOffer ? null : current)),
      BACK_OFFER_MS,
    );
    return () => window.clearTimeout(timer);
  }, [backOffer]);
  useEffect(() => {
    setConfirmedInVisit(new Set());
    setBackOffer(null);
    setAllDoneArmed(null);
  }, [job.id]);
  const activeNarrowMutationDocumentIds = () =>
    new Set<string>([
      ...(fieldSavingDocumentIdRef.current ? [fieldSavingDocumentIdRef.current] : []),
      ...(typeSavingDocumentIdRef.current ? [typeSavingDocumentIdRef.current] : []),
      ...lineMutationAdmissionsByDocument.current.keys(),
      ...confirmingDocumentsRef.current,
    ]);
  const [lineSaving, setLineSaving] = useState(false);
  /** "Find the table": the document whose table is being read, and the one waiting for the operator's box. */
  const [findingTableFor, setFindingTableFor] = useState<string | null>(null);
  const [markingTableFor, setMarkingTableFor] = useState<string | null>(null);
  const selectedDocumentIdForMarking = batch?.documents[selectedDocument]?.id ?? null;
  useEffect(() => {
    setMarkingTableFor((current) => (current === selectedDocumentIdForMarking ? current : null));
  }, [selectedDocumentIdForMarking]);
  // Whatever opened the document (a click, Next, the next item after a confirm),
  // show its current stored reading. See refreshSelectedDocument.
  //
  // Through a ref: refreshSelectedDocument is declared after this screen's early returns
  // (the reading/tour screens under `if (!document || stillDiscovering)`). A render that
  // returned early never initialised it, and calling it from this effect threw
  // "Cannot access before initialization" in the commit, which unmounted the whole window
  // (blank white on the tour's first launch, October 4). The ref holds the last one reached.
  const refreshSelectedDocumentRef = useRef<((documentId: string) => Promise<void>) | null>(null);
  useEffect(() => {
    if (selectedDocumentIdForMarking)
      void refreshSelectedDocumentRef.current?.(selectedDocumentIdForMarking);
  }, [selectedDocumentIdForMarking]);
  const pendingRowConfirmations = useRef(new Set<string>());
  const [rowConfirmationKeys, setRowConfirmationKeys] = useState<ReadonlySet<string>>(new Set());
  // What the operator is currently typing in a numeric cell, held as text. The
  // cells are bound to numbers, so a keystroke used to round-trip through
  // Number() on every change — "12." became 12 and the decimal point was deleted
  // as soon as it was typed, which made a decimal impossible to enter at all.
  // Line item saves run one after another rather than the second being dropped.
  // The old guard returned early while a save was in flight, so a correction made
  // during that window was lost without saying so.
  const lineSaveChain = useRef<Promise<unknown>>(Promise.resolve());
  const pendingLineSaves = useRef(0);
  const processorWaveLocalMutationBlocked = () =>
    Boolean(
      wholeBatchMutationRef.current ||
      schemaRefreshBusyRef.current ||
      structuralMutationRef.current ||
      boundaryRefreshesInFlight.current > 0 ||
      boundaryQueueDepth.current > 0 ||
      pageEditBusyRef.current ||
      typeSavingDocumentIdRef.current ||
      queuedTypeConfirmationIdsRef.current.size > 0 ||
      fieldSavingRef.current ||
      lineMutationAdmissions.current > 0 ||
      pendingLineSaves.current > 0 ||
      confirmingDocumentsRef.current.size > 0,
    );
  const processorWaveSaveLaneBlocked = () =>
    Boolean(
      processorWaveLocalMutationBlocked() || captureDrainGateOwner.current!.blockedByAnotherOwner(),
    );
  // Lane settlement is not success: each save catches its own error so the next
  // correction can still run. Keep the failed outcome beside the lane so a fast
  // whole-document confirmation cannot seal a row the engine refused.
  const lineSaveFailed = useRef(false);
  // Per-document engine-accepted watermarks for the current serialized lane.
  // Optimistic rows never become the input to a native save merely because a
  // later edit happened to be admitted before the earlier call returned.
  const lineSaveWatermarks = useRef<Map<string, DocumentResult>>(new Map());
  // Per document, the revisions this lane's saves showed (before each admission and its paint).
  // Typing begun on one of them moves onto the saved copy when the lane lands (lineDraftRebaseBinding).
  const lineSaveProducedTokens = useRef<Map<string, Set<string>>>(new Map());
  // Documents with a line item save in this lane that is not typing in a cell: their landing is not their own typing.
  // Cleared when the lane lands or fails; until then a moved fields panel's Delete is off (snapshot.review.deleteBusy),
  // even once that save's own admission is released while typing queued behind it still saves.
  const lineSaveUntyped = useRef<Set<string>>(new Set());
  // Adding/deleting renumbers rows. Keep that structural gesture mutually
  // exclusive with every already-admitted row edit until durable row IDs become
  // part of the interchange contract; rejecting with a reason is safer than
  // applying an old row number to a different invoice line.
  const lineStructureMutationDocumentId = useRef<string | null>(null);
  const [selectionMenu, setSelectionMenu] = useState<ViewerSelectionMenu | null>(null);
  const [boundaryUndoCount, setBoundaryUndoCount] = useState(0);
  /* Removing a page keeps it: the whole page rides in the correction event and in
     this stack, so putting it back is exact rather than approximate. The engine
     refuses to remove a document's last page — that is deleting the document, a
     different decision — and the button is simply absent there. */
  const pageUndoStack = useRef<
    { documentId: string; page: ProcessResult['documents'][number]['pages'][number] }[]
  >([]);
  const [pageUndoCount, setPageUndoCount] = useState(0);
  /* The last value a person changed and saved, while it can still be taken back exactly: the More
     menu offers "Undo change to <field>" on its document, and Ctrl+Z presses it (last-change.ts). */
  const [valueUndo, setValueUndo] = useState<ValueUndo | null>(null);
  // Removing or restoring a page is in flight. It refuses line, type and page work on every
  // document; a page turn never takes it (sendPageTurn), though a turn waits for it to clear.
  const pageEditBusyRef = useRef(false);
  // Delete (ADR-0096): one document at a time; the state marks its button busy.
  const deletingDocumentRef = useRef(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  // The job's Deleted view, opened from the documents list's More menu, and its count there. With no document left to
  // list (the last one deleted, or the batch cleared), the empty workspace offers it while the job has deleted
  // documents: counted when it shows, and again when the view closes.
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const noDocumentToList = !batch?.documents.length;
  useEffect(() => {
    const list = window.capturePointDesktop?.listDeletedDocuments;
    if (!(spineMenuOpen || (noDocumentToList && !deletedOpen)) || !list) return;
    let live = true;
    // Read on every empty workspace now, so an answer without a page (a stand-in shell) is no count, never a crash.
    void list({ jobId: job.id, offset: 0, limit: 1 }).then(
      (page) => {
        if (live) setDeletedCount(page?.total ?? null);
      },
      () => {
        if (live) setDeletedCount(null);
      },
    );
    return () => {
      live = false;
    };
  }, [spineMenuOpen, noDocumentToList, deletedOpen, job.id]);
  /* Turn page answers at once (owner, 2026-10-10): each page with a turn the engine has not saved
     yet, drawn turned on screen meanwhile (page-turn-queue.ts). The ref is what async code reads;
     the state is the same map for render. */
  const pageTurnsRef = useRef<Map<string, PageTurnEntry>>(new Map());
  const [pageTurns, setPageTurns] = useState<ReadonlyMap<string, PageTurnEntry>>(() => new Map());
  const pageTurnsSaved = useRef(0);
  /** Turns the engine has saved, per page (pageTurnKey): four quarter turns leave the image and
      rotation as they were, but the page was still read again (persistFieldValue's pick check). */
  const pageTurnSavesRef = useRef<Map<string, number>>(new Map());
  const turnPageShortcutRef = useRef<() => void>(() => undefined);
  /** The page (or its toolbar) was the last thing clicked or tabbed to: R is the page's then. */
  const pageHasKeyboardRef = useRef(false);
  /** The page whose removal has been asked for and not yet answered, `documentId:pageNumber`. */
  const [confirmingPageRemoval, setConfirmingPageRemoval] = useState<string | null>(null);
  /* Just joined (owner, 2026-10-30): one line where it happened and in the status bar for six seconds, with Undo,
     saying how many like it the engine marked to check (DocumentStore.JoinLookalikes); then the page
     break's usual scissors. */
  const [joinNews, setJoinNews] = useState<{ batchId: string; page: number; added: number } | null>(
    null,
  );
  useEffect(() => {
    if (!joinNews) return;
    const timer = window.setTimeout(
      () => setJoinNews((current) => (current === joinNews ? null : current)),
      BACK_OFFER_MS,
    );
    return () => window.clearTimeout(timer);
  }, [joinNews]);
  const [batchSearchOpen, setBatchSearchOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // The magnification the operator asked for, held as a multiple of a fitted
  // page rather than as a literal factor. Every page image fires onLoad and
  // refits; without this, stepping to the next document threw away a 250%
  // reading zoom and made them set it again on every single document. Holding
  // it relative to fit also means 2.5x reads the same on a portrait invoice as
  // on a landscape placard, which a literal factor does not.
  const viewScale = useRef(1);
  const zoomRef = useRef(1);
  const panRef = useRef(pan);
  panRef.current = pan;
  const previewSizeRef = useRef({ width: 1, height: 1 });
  // Evidence the operator asked to see, waiting for its page image to arrive.
  const pendingEvidenceFocus = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const pendingReaderEvidence = useRef<{
    documentId: string;
    pageIndex: number;
    revision: number;
    apply: (height: number) => void;
  } | null>(null);
  // The framing the operator was last taken to, kept so a refit re-derives it
  // rather than putting the page back to the top under an unchanged zoom.
  const appliedEvidenceFocus = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [easingView, setEasingView] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [spineWidth, setSpineWidth] = useState(() => readReviewLayout(job.id).spineWidth);
  const [resultsWidth, setResultsWidth] = useState(() => readReviewLayout(job.id).resultsWidth);
  // A presentation experiment, not a second form or a different save owner.
  // The field editors stay mounted as the surrounding desk changes shape.
  const [compareSources, setCompareSources] = useState(false);
  const basePaneLayout = useReviewPaneLayout(
    spineWidth,
    resultsWidth,
    detachedPanels.viewer,
    detachedPanels.fields,
  );
  const revealReviewPane = useCallback(
    (pane: ReviewPane) => {
      if (pane === 'page' || pane === 'lines') setCompareSources(false);
      basePaneLayout.revealPane(pane);
    },
    [basePaneLayout.revealPane],
  );
  const paneLayout = { ...basePaneLayout, revealPane: revealReviewPane };
  // PAIGE: in a narrow window the panes are tabs and the tour's card lives in Documents. Each step
  // opens on it; without this the tour began on the page with no card at all (first-run walk, 2026-10-06).
  useEffect(() => {
    if (touring && tour?.ready && basePaneLayout.narrow) basePaneLayout.revealPane('documents');
  }, [touring, tour?.ready, tour?.step, basePaneLayout.narrow, basePaneLayout.revealPane]);
  const fieldsAtTop =
    !compareSources && !paneLayout.narrow && !detachedPanels.viewer && !detachedPanels.fields;
  const [resizingSpine, setResizingSpine] = useState(false);
  /* The spine's row geometry, derived from the width the operator chose.
     The page image was a fixed 69x88 centred in whatever column it was given,
     so dragging the spine wider bought whitespace instead of a bigger page and
     the thing an operator actually recognises a document by — how it looks —
     was the same size at every width. The image fills the chosen width now.

     `spineWidth` retains the operator's 132–360px preference. paneLayout
     fits it alongside fields and paper when the window is constrained; use
     that rendered width here too, so virtual rows match the visible images.
     Detaching the viewer retains the preferred width for its navigation data.

     56 is the horizontal chrome between the strip's width and the image, and
     it is not tight. Measured at spineWidth 360, where `.document-spine`
     reports clientWidth 342: 1px of .thumbnail-strip border, 7px of
     .document-spine resizer margin, 10px of thin scrollbar, 19px of spine
     padding, .document-group's 2px rule and 7px indent, .thumbnail's 4px of
     padding — 50 in all, leaving 6px of slack beside the image. The scrollbar
     is the only part of that the platform decides, which is what the slack is
     for. Quantised to 8px so a resize drag steps the layout table a couple of
     dozen times across its whole range rather than once per pointer move: the
     table is one entry per document and this panel has to hold ten thousand.
     1.3 is a letter page (11/8.5) and the +10 is what the row spends around
     the image — .thumbnail's 6px of margin and 4px of padding — so the image
     box comes out page-shaped and needs no letterboxing of its own.

     This is the only row height in the file. Everything that measures the
     spine takes it through spineGroupHeight/spinePageTop. */
  const spineThumbnailWidth =
    Math.round(
      ((paneLayout.narrow ? Math.min(spineWidth, 300) : paneLayout.spine || spineWidth) - 56) / 8,
    ) * 8;
  const spinePageRow = Math.round(spineThumbnailWidth * 1.3) + 10;
  const [resizingResults, setResizingResults] = useState(false);
  const [lineItemsHeight, setLineItemsHeight] = useState(
    () => readReviewLayout(job.id).lineItemsHeight,
  );
  const [resizingLines, setResizingLines] = useState(false);
  const linesResizeGesture = useRef<{ startY: number; startHeight: number } | null>(null);

  const [correctionsOpen, setCorrectionsOpen] = useState(false);
  const [correctionsFailed, setCorrectionsFailed] = useState(false);
  const [corrections, setCorrections] = useState<OperatorCorrection[]>([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [correctionsRequest, setCorrectionsRequest] = useState(0);
  useEffect(() => {
    if (!correctionsOpen || !window.capturePointDesktop) return;
    let current = true;
    void window.capturePointDesktop
      .getOperatorCorrections(job.id)
      .then((entries) => {
        if (current) {
          setCorrections(entries);
          setCorrectionsFailed(false);
        }
      })
      .catch(() => {
        if (current) setCorrectionsFailed(true);
      })
      .finally(() => {
        if (current) setCorrectionsLoading(false);
      });
    return () => {
      current = false;
    };
    // A saved document revision arriving while History is open must refresh
    // the list too. A late pre-save read may never replace that newer reply.
  }, [correctionsOpen, correctionsRequest, job.id, batch]);
  // The search, field and date filters live in CorrectionsPanel, not here:
  // held in workspace state, every keystroke re-rendered the whole workspace.
  // Spine scrolling coalesces to one frame and only commits when the window
  // has moved enough to change what renders. Setting viewport state per raw
  // scroll event re-rendered the whole workspace at scroll rate, which on a
  // 1,700-page spine froze the panel outright.
  const spineScrollFrame = useRef(0);
  const inlineFieldSavingRef = useRef(false);
  const [inlineFieldSaving, setInlineFieldSaving] = useState(false);
  const [inlineFieldName, setInlineFieldName] = useState('');
  // Fields are added here only while the operator sets up a type with none yet — a type
  // just made with "+ Add a document type…" — on the document it was made for. Done, or
  // leaving that document or type, ends it; ordinary review shows no add row.
  const [inlineFieldSetup, setInlineFieldSetup] = useState<{
    documentId: string;
    typeId: string;
  } | null>(null);
  const inlineFieldInputRef = useRef<HTMLInputElement>(null);
  // Set when Enter or the row's Add pressed: the row stays open with focus in it for
  // the next name. A name added because focus moved elsewhere leaves focus there.
  const inlineFieldKeepFocus = useRef(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  // Reading mode: the wheel scrolls a continuous stack of pages the way a
  // document reader does, and Ctrl+wheel zooms. Pan-and-zoom stays the other
  // mode. The choice is an operator habit, so it persists across jobs.
  // One viewer behavior: the wheel scrolls paper; right-drag or Ctrl+wheel zooms.
  // Ignore the retired mode preference so older workspaces get the same behavior.
  const readerMode = true;
  const [readerScale, setReaderScale] = useState(1);
  const readerScaleRef = useRef(1);
  useEffect(() => {
    readerScaleRef.current = readerScale;
  }, [readerScale]);
  // Right-drag zoom in reading mode: up grows the page, down shrinks it.
  const readerZoomGesture = useRef<{
    startY: number;
    anchorTop: number;
    anchorLeft: number;
    startScale: number;
    latestScale: number;
    moved: boolean;
  } | null>(null);
  // The hand tool in reading mode drags the scroll, the way Acrobat's hand
  // works in continuous view.
  const readerDragGesture = useRef<{
    startX: number;
    startY: number;
    originTop: number;
    originLeft: number;
  } | null>(null);
  // Ctrl+wheel zoom accumulates in a transform and commits once the wheel
  // rests — the same trick as the drag zoom, for the same smoothness.
  const readerWheelZoom = useRef<{
    startScale: number;
    latestScale: number;
    anchorTop: number;
    anchorLeft: number;
    timer: number;
  } | null>(null);
  const spineRef = useRef<HTMLDivElement>(null);
  // The spine element itself, as state: it first appears after a live batch has been on screen
  // (the documents list replaces the working view), and the viewport measurement below must
  // attach when it does, not only when the batch first arrives.
  const [spineElement, setSpineElement] = useState<HTMLDivElement | null>(null);
  const attachSpine = useCallback((element: HTMLDivElement | null) => {
    spineRef.current = element;
    setSpineElement(element);
  }, []);
  const spineStartRequest = useRef<number | null>(null);
  const spineMetricsRef = useRef<Array<{ documentIndex: number; top: number; height: number }>>([]);
  const [spineViewport, setSpineViewport] = useState({ top: 0, height: 700 });
  const schemaRefreshAttempted = useRef('');
  /* The page image's own node, so a drag can move it without going through React. */
  /* The document the operator has just finished with their own hands.
     
     "Was in the review list when you arrived" was the wrong signal: it cannot
     tell finishing a document from opening one that was never really review
     work, and the second is far more common — a stored flag that disagrees with
     the document's real state resolves the moment it loads. So a document nobody
     had touched sat pinned at the top of the review list, green, above documents
     that genuinely needed the operator. Only an actual correction earns the
     place now. */
  const settledByHand = useRef<string | null>(null);
  const pageStackRef = useRef<HTMLDivElement | null>(null);
  const panGesture = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
    button: number;
    latest?: { x: number; y: number };
  } | null>(null);
  const suppressContextMenu = useRef(false);
  const spineResizeGesture = useRef<{ startX: number; startWidth: number } | null>(null);
  const resultsResizeGesture = useRef<{ startX: number; startWidth: number } | null>(null);
  const boundaryUndoStack = useRef<BoundaryUndoEntry[]>([]);
  const invalidateBoundaryUndoFor = (documentId: string) => {
    const retained = boundaryUndoStack.current.filter(
      (entry) => !entry.mutation.removedDocumentIds.includes(documentId),
    );
    if (retained.length === boundaryUndoStack.current.length) return;
    boundaryUndoStack.current = retained;
    setBoundaryUndoCount(retained.length);
  };
  const clearBoundaryUndoHistory = () => {
    if (boundaryUndoStack.current.length === 0) return;
    boundaryUndoStack.current = [];
    setBoundaryUndoCount(0);
  };
  const boundaryAttempts = useRef(new Map<number, number>());
  /** Changes that reached the engine, so an undo that finds nothing can say why. */
  const committedBoundaryChanges = useRef(new Set<number>());
  /** Documents to read again once the cuts in front of the recheck are saved, with the types to read them against (null: the whole job). */
  const pendingBoundaryRecheck = useRef(new Map<string, ReturnType<typeof schemaRequest> | null>());
  const lastBoundaryCommitAt = useRef(0);
  /** The documents a queued item rewrites, as the batch stands now. */
  const boundaryItemDocumentIds = (item: BoundaryQueueItem, live: ProcessResult): string[] => {
    if (item.kind === 'split' || item.kind === 'join') {
      const resolved = resolveBoundaryRequest(live.documents, item);
      return resolved.kind === 'none' ? [] : [...resolved.documentIds];
    }
    if (item.kind === 'undo')
      return [
        ...(boundaryUndoStack.current.find((entry) => entry.changeId === item.changeId)?.mutation
          .removedDocumentIds ?? []),
      ];
    const present = new Set(live.documents.map((document) => document.id));
    return [...pendingBoundaryRecheck.current.keys()].filter((documentId) =>
      present.has(documentId),
    );
  };
  /** Whether a split, join or undo still waiting or saving will rewrite this document. */
  const boundaryChangeQueuedFor = (documentId: string) => {
    const live = batchRef.current;
    return (
      Boolean(live) &&
      boundaryQueue
        .getSnapshot()
        .some(
          (item) =>
            item.kind !== 'recheck' && boundaryItemDocumentIds(item, live!).includes(documentId),
        )
    );
  };
  /** Anything still on its way to the engine for this document. */
  const documentWriteInFlight = (documentId: string) =>
    fieldSavingDocumentIdRef.current === documentId ||
    typeSavingDocumentIdRef.current === documentId ||
    lineMutationAdmitted(documentId) ||
    confirmingDocumentsRef.current.has(documentId) ||
    queuedTypeConfirmationIdsRef.current.has(documentId) ||
    detachedMutationDocuments.current.has(documentId) ||
    typeRefreshOwners.current.has(documentId) ||
    boundaryRereadingIds.current.has(documentId) ||
    [...fieldDraftStore.getSnapshot(), ...lineDraftStore.getSnapshot()].some(
      (draft) => draft.documentId === documentId && draft.state === 'saving',
    );
  /**
   * Whether it is a queued item's turn. A boundary change waits for every
   * writer of its own documents and for anything rewriting the whole batch or
   * its structure; work on other documents carries on beside it, the re-read of
   * an earlier change's documents included (only its own documents wait for it).
   */
  boundaryTurnRef.current = (item) => {
    const live = batchRef.current;
    if (!live) return 'wait';
    // The workspace has closed (another job was opened). Its screen state no
    // longer changes, so only the save lanes still settling count: the change
    // runs as soon as they have, from the stored documents, and nothing this
    // closed view holds is put back on anybody's screen.
    if (!mounted.current)
      return wholeBatchMutationRef.current ||
        structuralMutationRef.current ||
        (item.kind === 'recheck' && boundaryRefreshesInFlight.current > 0) ||
        boundaryItemDocumentIds(item, live).some(documentWriteInFlight)
        ? 'wait'
        : 'ready';
    if (openingSavedBatch) return 'wait';
    if (
      wholeBatchMutationRef.current ||
      schemaRefreshBusyRef.current ||
      structuralMutationRef.current ||
      (item.kind === 'recheck' && boundaryRefreshesInFlight.current > 0)
    )
      return 'wait';
    // Pages being read land in the batch when their wave finishes. An operator's
    // change takes the gap after the waves already started (the drain yields to
    // it); the recheck does not hold up reading for its own sake.
    if (captureInFlight.current.size > 0) return item.kind === 'recheck' ? 'wait' : 'capture';
    // The batch is not stored until its first wave finishes, and with no wave
    // being read it cannot be (a paused CPU wave waits for this queue): the
    // change is tried now and says plainly that it could not be saved.
    if (live.batchId.startsWith('live:')) return 'ready';
    if (
      item.kind === 'recheck' &&
      Date.now() - lastBoundaryCommitAt.current < BOUNDARY_RECHECK_SETTLE_MS
    )
      return 'wait';
    return boundaryItemDocumentIds(item, live).some(documentWriteInFlight) ? 'wait' : 'ready';
  };
  /** The split, join or undo waiting or saving at each batch page, for its "Saving…" mark. */
  const boundaryPendingByPage = useMemo(
    () =>
      new Map(
        boundaryQueueItems.flatMap((item) =>
          item.kind === 'recheck' || item.pageNumber == null
            ? []
            : [[item.pageNumber, item.kind] as const],
        ),
      ),
    [boundaryQueueItems],
  );
  /** Keep the paged workspace's three copies of structural identity together. */
  const reconcileBoundaryProjection = (
    result: ProcessResult,
    removedDocumentIds: readonly string[],
    replacements: readonly DocumentResult[],
    replacementsAreAuthoritative = true,
  ): ProcessResult => {
    if (result.workspaceProjection !== 'paged') return result;
    const existingIds = new Set(result.documents.map((item) => item.id));
    // Boundary edits paint before their native commit. Keep that draft out of
    // the eviction cache: structural ownership guarantees its documents cannot
    // be evicted until the engine returns (or the inverse rollback restores the
    // prior authority). The accepted pass below replaces the cache atomically.
    if (replacementsAreAuthoritative) {
      for (const id of removedDocumentIds) projectionSkeletons.current.delete(id);
      for (const replacement of replacements)
        projectionSkeletons.current.set(replacement.id, toProjectionSkeleton(replacement));
    }
    const replacementIds = new Set(replacements.map((item) => item.id));
    const nextLoaded = new Set(
      [...loadedDocumentIdsRef.current].filter(
        (id) => existingIds.has(id) && (!removedDocumentIds.includes(id) || replacementIds.has(id)),
      ),
    );
    for (const id of replacementIds) if (existingIds.has(id)) nextLoaded.add(id);
    const order = [
      ...loadedDocumentOrder.current.filter((id) => nextLoaded.has(id) && !replacementIds.has(id)),
      ...replacements.map((item) => item.id).filter((id) => nextLoaded.has(id)),
    ];
    const protectedId = result.documents[selectedDocumentRef.current]?.id;
    const narrowMutationProtectedIds = activeNarrowMutationDocumentIds();
    const evicted = new Set<string>();
    while (order.length > 12) {
      let evictAt = order.findIndex(
        (id) =>
          id !== protectedId && !replacementIds.has(id) && !narrowMutationProtectedIds.has(id),
      );
      if (evictAt < 0 && replacementsAreAuthoritative)
        evictAt = order.findIndex(
          (id) => id !== protectedId && !narrowMutationProtectedIds.has(id),
        );
      if (evictAt < 0) break;
      const [id] = order.splice(evictAt, 1);
      if (id) {
        nextLoaded.delete(id);
        evicted.add(id);
      }
    }
    loadedDocumentIdsRef.current = nextLoaded;
    loadedDocumentOrder.current = order;
    setLoadedDocumentIds(nextLoaded);
    return {
      ...result,
      workspaceProjection: 'paged',
      loadedDocumentIds: [...nextLoaded],
      documents: result.documents.map((item) =>
        evicted.has(item.id)
          ? (projectionSkeletons.current.get(item.id) ?? toProjectionSkeleton(item))
          : item,
      ),
    };
  };
  const spliceLiveBoundaryDocuments = (
    result: ProcessResult,
    mutation: BoundaryDocumentMutation,
    replacementsAreAuthoritative = true,
  ) =>
    reconcileBoundaryProjection(
      spliceBoundaryDocuments(result, mutation),
      mutation.removedDocumentIds,
      mutation.documents,
      replacementsAreAuthoritative,
    );
  const lineEditBefore = useRef(new Map<string, LineItemRow>());
  // A review reason is an explicit request to see the whole row's evidence.
  // Its ensuing programmatic focus must not immediately replace that context
  // with the narrower cell-focus behavior below.
  const preserveRowEvidenceFocus = useRef<number | null>(null);
  const reviewChannelRef = useRef<Pick<BroadcastChannel, 'postMessage'> | null>(null);
  const reviewActionHandler = useRef<(message: ReviewWorkspaceMessage) => void>(() => undefined);
  const reviewSnapshotRef = useRef<ReviewWorkspaceSnapshot | null>(null);
  const detachedMutationDocuments = useRef(new Set<string>());
  const fieldDraftCommandRef = useRef<(command: FieldDraftCommand) => void>(() => {});
  const fieldDraftSubmitRef = useRef<(draft: FieldDraft) => Promise<FieldDraftOutcome>>(
    async () => ({ status: 'refused', reason: 'The document is not ready. Your typing is kept.' }),
  );
  const fieldDraftStore = useMemo(
    () =>
      new FieldDraftStore(
        (command) => fieldDraftCommandRef.current(command),
        () => !closePermissionFrozen(),
      ),
    [],
  );
  const fieldDraftCoordinator = useMemo(
    () =>
      createFieldDraftCoordinator(
        fieldDraftStore,
        (draft) => fieldDraftSubmitRef.current(draft),
        (message) => {
          if (reviewSnapshotRef.current)
            reviewSnapshotRef.current.fieldDrafts = fieldDraftStore.getSnapshot();
          reviewChannelRef.current?.postMessage(message);
        },
      ),
    [fieldDraftStore],
  );
  const previousDraftDocumentId = useRef<string | null>(null);
  const retainedSearchDraftNavigation = useRef<{
    batchId: string;
    from: string;
    to: string;
  } | null>(null);
  const lineDraftCommandRef = useRef<(command: FieldDraftCommand) => void>(() => {});
  const lineDraftSubmitRef = useRef<(draft: FieldDraft) => Promise<FieldDraftOutcome>>(
    async () => ({ status: 'refused', reason: 'The document is not ready. Your typing is kept.' }),
  );
  const lineDraftStore = useMemo(
    () =>
      new FieldDraftStore(
        (command) => lineDraftCommandRef.current(command),
        () => !closePermissionFrozen(),
      ),
    [],
  );
  const lineDraftCoordinator = useMemo(
    () =>
      createFieldDraftCoordinator(
        lineDraftStore,
        (draft) => lineDraftSubmitRef.current(draft),
        (event) => {
          if (reviewSnapshotRef.current)
            reviewSnapshotRef.current.lineDrafts = lineDraftStore.getSnapshot();
          reviewChannelRef.current?.postMessage(lineDraftEvent(event));
        },
      ),
    [lineDraftStore],
  );
  const boundaryShortcutRef = useRef<() => void>(() => undefined);
  const confirmShortcutRef = useRef<(pressedAt?: number) => void>(() => undefined);
  const confirmAndAdvanceRef = useRef<() => void>(() => undefined);
  const runMainActionRef = useRef<() => void>(() => undefined);
  /** Skip, when the tab has one (Needs review and Teach); false leaves Ctrl+PgDn its plain move. */
  const skipShortcutRef = useRef<() => boolean>(() => false);
  const goBackToConfirmedRef = useRef<() => void>(() => undefined);
  /** Ctrl+Shift+F: the Search documents button, while the review screen shows it. */
  const searchShortcutRef = useRef<() => void>(() => undefined);
  /** F6 / Shift+F6: the next or previous thing to check on this document. */
  const reviewItemShortcutRef = useRef<(direction: 1 | -1) => void>(() => undefined);
  const reviewItemCursor = useRef<{ documentId: string; index: number } | null>(null);
  /** Ctrl+Z: the undo of the person's most recent change, while it shows; true when it pressed one. */
  const undoShortcutRef = useRef<(fromTextBox?: boolean) => boolean>(() => false);
  const lastChange = useMemo(() => new LastChange(), []);
  useEffect(() => {
    lastChange.clear();
    setValueUndo(null);
  }, [job.id, lastChange]);
  /** A confirmation asked for while this document's typing was still saving; it runs once saved. */
  const confirmAfterDraftsRef = useRef<string | null>(null);
  /**
   * A detached panel's press waiting with confirmAfterDraftsRef: the revision it was bound to. Once
   * the typing has saved it confirms only if the document moved on from that revision by its own
   * typing alone (ownTypingRevisions), and is refused as a stale press otherwise.
   */
  const detachedPressRef = useRef<{ documentId: string; documentMutationToken: string } | null>(
    null,
  );
  const ownTypingRevisions = useMemo(() => new OwnTypingRevisions(), []);
  const documentNavigationRef = useRef<(direction: number, arrival?: boolean) => void>(
    () => undefined,
  );
  const batchRef = useRef<ProcessResult | null>(savedBatch);
  const liveJobRef = useRef(job);
  liveJobRef.current = job;
  const projectionSkeletons = useRef(
    new Map<string, ProcessResult['documents'][number]>(
      savedBatch?.workspaceProjection
        ? savedBatch.documents.map((item) => [item.id, toProjectionSkeleton(item)])
        : [],
    ),
  );
  const loadedDocumentOrder = useRef<string[]>(
    savedBatch?.workspaceProjection
      ? (savedBatch.loadedDocumentIds ?? [])
      : (savedBatch?.documents.map((item) => item.id) ?? []),
  );
  const hydrationRequestGenerations = useRef<Map<string, number>>(new Map());
  const hydrationRequestsInFlight = useRef<Map<string, Promise<boolean>>>(new Map());
  /** Keep the eviction copy aligned only with engine-accepted document state. */
  const cacheAuthoritativeProjectionSkeleton = (document: DocumentResult) => {
    if (batchRef.current?.workspaceProjection === 'paged')
      projectionSkeletons.current.set(document.id, toProjectionSkeleton(document));
  };
  /**
   * Rebuild a paged eviction cache without promoting an optimistic renderer
   * draft to authority. Protected ids keep their last accepted skeleton; an id
   * created only by an in-flight structural edit is omitted until commit.
   */
  const rebuildProjectionSkeletonCache = (
    documents: readonly DocumentResult[],
    protectedIds: ReadonlySet<string> = new Set(),
    authoritativeFallbacks: ReadonlyMap<string, DocumentResult> = new Map(),
  ) => {
    const accepted = projectionSkeletons.current;
    const next = new Map<string, DocumentResult>();
    for (const document of documents) {
      if (protectedIds.has(document.id)) {
        const safe = accepted.get(document.id) ?? authoritativeFallbacks.get(document.id);
        if (safe) next.set(document.id, toProjectionSkeleton(safe));
      } else {
        next.set(document.id, toProjectionSkeleton(document));
      }
    }
    projectionSkeletons.current = next;
  };
  const invalidateHydrationRequests = () => {
    // Increment rather than clear. Clearing lets a replacement request reuse
    // generation 1 while the old generation-1 response is still in flight; its
    // stale finally can then clear the newer request's loading state.
    for (const [documentId, generation] of hydrationRequestGenerations.current)
      hydrationRequestGenerations.current.set(documentId, generation + 1);
    // A parent echo can preserve the exact same skeleton object. The generation
    // bump correctly rejects its in-flight response, but the automatic loader's
    // WeakSet would otherwise remember that object as already attempted and
    // leave the selected document blank forever. Re-arm the replacement
    // authority at the same moment we invalidate its requests.
    hydrationAttemptedDocuments.current = new WeakSet();
    hydrationRequestsInFlight.current.clear();
    setLoadingDocumentId('');
  };
  // Waves overlap by one stage: while a wave is in extraction (GPU decode),
  // the next wave may start reading (rasterize + OCR + boundaries). The
  // stages use different resources, and running them back to back left the
  // model idle a quarter of every wave. Two rules keep it safe: a wave that
  // CREATES the batch runs alone until it commits, and at most two are in
  // flight - one extracting, one reading.
  const captureInFlight = useRef<Set<string>>(new Set());
  /** A capture this view is showing: one it started, or one it came back to mid-run. */
  const watchedCapture = (captureId: string) =>
    captureInFlight.current.has(captureId) ||
    (rejoinedCapture.current !== '' && rejoinedCapture.current === captureId);
  const forgetRejoinedCapture = () => {
    const id = rejoinedCapture.current;
    if (!id) return;
    rejoinedCapture.current = '';
    setRejoinedCaptureId('');
    setCaptureWaves((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  };
  const captureReachedExtraction = useRef<Set<string>>(new Set());
  // When the engine is down, the queue WAITS. Without this, one engine outage
  // failed 189 queued waves in minutes — each wave drained into the same dead
  // window and was marked as its own permanent failure. ...but it does not wait
  // for ever, and the outage rather than the wave is what is being timed.
  //
  // Both the outage and the hold live in `engine-outage.ts` at module scope
  // rather than in refs here. They are facts about the engine, which outlives
  // this component: held here, navigating to the jobs list and back dropped the
  // outage and the five-minute hold, and the drain marched the queue straight
  // into the same dead engine — while the screen was still promising it would
  // not.
  const mounted = useRef(true);
  const selectedDocumentRef = useRef(selectedDocument);
  const lastSavedBatch = useRef(savedBatch);
  useEffect(() => {
    if (savedBatch === lastSavedBatch.current) return;
    lastSavedBatch.current = savedBatch;
    const selectedId = batchRef.current?.documents[selectedDocumentRef.current]?.id;
    batchRef.current = savedBatch;
    setBatch(savedBatch);
    if (savedBatch?.workspaceProjection) {
      rebuildProjectionSkeletonCache(
        savedBatch.documents,
        new Set([
          ...activeNarrowMutationDocumentIds(),
          ...(structuralMutationRef.current?.documentIds ?? []),
        ]),
      );
    } else {
      projectionSkeletons.current = new Map();
    }
    const loaded = savedBatch?.workspaceProjection
      ? (savedBatch.loadedDocumentIds ?? [])
      : (savedBatch?.documents.map((item) => item.id) ?? []);
    loadedDocumentOrder.current = loaded;
    setLoadedDocumentIds(new Set(loaded));
    // The open document stays open. When it left the list (exported, say), what opens instead was not
    // chosen, so the main button will not confirm it unseen (B-09).
    const next = selectionAfterRefresh(selectedId, savedBatch?.documents ?? [], () =>
      openingDocumentIndex(savedBatch?.documents ?? [], calibrating),
    );
    if (next.movedUnder) movedUnder.current = next.movedUnder;
    setSelectedDocument(next.index);
    // This view's own saves echo back here: the document that stays open keeps its page. An unchanged
    // page is not a page command: setting it would retire the reader's pending scroll and centering.
    // Another document always is one, even on page 1, so a pending scroll cannot move it.
    const opened = savedBatch?.documents[next.index];
    const page = pageAfterRefresh(selectedId, opened, selectedPageIntent.current);
    if (page !== selectedPageIntent.current || opened?.id !== selectedId) setSelectedPage(page);
  }, [savedBatch]);
  const document = batch?.documents[selectedDocument];
  // Paige: the lease on the open document (ADR-0009). A refused save reloads it from the engine.
  const reviewLease = useReviewLease(document?.id ?? null, () =>
    hydrateDocumentRef.current(selectedDocumentRef.current),
  );
  // A colleague's document, or a read-only view (Ademero support): read here, never changed.
  const reviewHeld = reviewLease.status === 'held' || reviewLease.status === 'viewing';
  // PAIGE: exported documents a later check flagged (paige/flagged-after-export.ts).
  const flaggedAfterExport = useFlaggedAfterExport(job.id, batch, loadedDocumentIds, document);
  useEffect(() => {
    const host = window.document.querySelector('.paige-capturepoint');
    if (host instanceof HTMLElement) host.dataset['reviewHeld'] = reviewHeld ? 'true' : 'false';
  }, [reviewHeld]);
  const currentDocumentMutationToken = useMemo(
    () => (document ? reviewDocumentMutationToken(document) : null),
    [document],
  );
  loadedDocumentIdsRef.current = loadedDocumentIds;
  // The open document as drawn with its values loaded. When it is the one a move or a re-read
  // opened (arrivalPending), the arrival guard starts now, and again on the frame that paints it.
  const shownLoadedDocument =
    document && !(batch?.workspaceProjection === 'paged' && !loadedDocumentIds.has(document.id))
      ? document
      : null;
  useLayoutEffect(() => {
    if (!shownLoadedDocument || arrivalPending.current !== shownLoadedDocument.id) return;
    arrivalPending.current = null;
    arrivedAt.current = performance.now();
    window.requestAnimationFrame(() => {
      arrivedAt.current = Math.max(arrivedAt.current, performance.now());
    });
  }, [shownLoadedDocument]);
  // Every name each of the job's types has carried, read again when its types change. A rename
  // relabels only the open batch, so an older batch still says the old name: by these, such a
  // document is its type under the current name (document-type-identity.ts). Null until read,
  // and on an older shell or a failed read, when only a document's current name counts.
  const typeNamesKey = `${job.id}:${job.schemaVersion}:${job.documentTypes.map((type) => `${type.id}=${type.name}`).join('|')}`;
  const [typeNames, setTypeNames] = useState<{ key: string; names: DocumentTypeName[] } | null>(
    null,
  );
  useEffect(() => {
    const read = window.capturePointDesktop?.getDocumentTypeNames;
    if (!read) return;
    let live = true;
    read(job.id).then(
      (names) => {
        if (live) setTypeNames({ key: typeNamesKey, names });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [typeNamesKey]);
  const typeNameHistory = typeNames?.key === typeNamesKey ? typeNames.names : null;
  const typeNameHistoryRef = useRef(typeNameHistory);
  typeNameHistoryRef.current = typeNameHistory;
  /** The job's type a document counts as, by its type id and the names that type has had. */
  const filedType = document
    ? filedDocumentType(document, job.documentTypes, typeNameHistory)
    : undefined;
  const documentSchema = document
    ? (job.documentTypes.find((type) => type.id === document.documentTypeId) ??
      job.documentTypes.find(
        (type) =>
          type.name.localeCompare(document.documentType, undefined, { sensitivity: 'accent' }) ===
          0,
      ) ??
      filedType)
    : undefined;
  // What the type list shows. Unconfirmed, the type the document counts as; a name no type of
  // the job has had under its id (a lesson's, a deleted type's) shows itself, so choosing the
  // right type in the list answers it. Confirmed, or before the names are read, as before.
  const shownType =
    document?.documentTypeVerified || typeNameHistory === null ? documentSchema : filedType;
  const fieldOrder = useFieldDisplayOrder(
    job.id,
    documentSchema?.id ?? '',
    documentSchema?.fields.filter((field) => field.kind !== 'line-items') ?? [],
  );
  const configuredFields = [
    ...fieldOrder.fields,
    ...(documentSchema?.fields.filter((field) => field.kind === 'line-items') ?? []),
  ];
  const evidenceFieldDefinitionKey = JSON.stringify(
    configuredFields.map((field) => [field.id, field.name, field.kind]),
  );
  // Scrolling the spine changes viewport state, not the document. Reuse the
  // expensive all-page text matching until a new document snapshot or field
  // definition arrives. This cache is local to this viewer, never an authority
  // for accepting edits; mutation admission still computes its own live token.
  const { values, evidenceFields, hoverEvidence } = useMemo(() => {
    const values = new Map(
      (document?.fields ?? []).map((item) => [
        item.name,
        numericFieldDisplayEvidence(item, document!.pages),
      ]),
    );
    const evidenceFields = configuredFields
      .filter((item) => item.kind !== 'line-items')
      .map((item) => ({ configured: item, recognized: values.get(item.name) }))
      .filter((item): item is { configured: Field; recognized: FieldResult } =>
        Boolean(item.recognized?.value),
      );
    const hoverEvidence: HoverEvidenceEntry[] = document
      ? evidenceFields.flatMap(({ configured, recognized }) =>
          document.pages.map((page) => ({
            fieldId: configured.id,
            pageNumber: page.number,
            lineIndexes: [
              ...new Set([
                ...fieldDisplayMatches(page.lines, recognized.value),
                ...((recognized.evidencePageNumber ?? document.pages[0]?.number) === page.number
                  ? (recognized.evidenceLineIndexes ?? [])
                  : []),
              ]),
            ],
            barcode:
              (recognized.evidencePageNumber ?? document.pages[0]?.number) === page.number
                ? (recognized.barcodeEvidence ?? null)
                : null,
            mark:
              (recognized.evidencePageNumber ?? document.pages[0]?.number) === page.number
                ? markEvidenceOf(recognized)
                : null,
            // A checkbox reading shows its caption with the marked box; every other value only its own words.
            value: markEvidenceOf(recognized) ? undefined : recognized.value,
            words:
              (recognized.evidencePageNumber ?? document.pages[0]?.number) === page.number
                ? (recognized.evidenceWordIndexes ?? null)
                : null,
          })),
        )
      : [];
    return { values, evidenceFields, hoverEvidence };
  }, [document, evidenceFieldDefinitionKey]);
  /**
   * The kinds the reader has already named, offered while the job has none.
   *
   * A batch opens for review before its setup proposal is approved, so on every
   * new job the type control is a list containing nothing while the spine beside
   * it reads "Credit Memo", "Form W-9", "Invoice". The operator is asked to
   * choose the closest match from an empty list, about a document whose kind is
   * printed six inches to the left. These are those kinds, taken from the batch
   * itself; picking one creates the type and assigns it, which is the same thing
   * "+ Add a document type…" does with more typing.
   */
  const discoveredTypeNames =
    job.documentTypes.length > 0
      ? []
      : [
          ...new Set(
            (batch?.documents ?? [])
              .map((item) => (item.documentType ?? '').trim())
              .filter((name) => name.length > 0 && !DocumentStoreIsNonAnswer(name)),
          ),
        ].sort((left, right) => left.localeCompare(right));
  // While no type is chosen, the closed list shows the name the reader gave this document.
  // When that name can also be picked below (a found name on a new job), it leads the found
  // names and its greyed entry stays out of the open list, so no name is listed twice (owner,
  // 2026-10-19). The greyed entry stays in the list when its name is nowhere else in it.
  const readerTypeName = document?.documentType?.trim() ?? '';
  const foundTypeNames = discoveredTypeNames.includes(readerTypeName)
    ? [readerTypeName, ...discoveredTypeNames.filter((name) => name !== readerTypeName)]
    : discoveredTypeNames;
  const readerTypeNameListed =
    readerTypeName.length > 0 &&
    (foundTypeNames.includes(readerTypeName) ||
      job.documentTypes.some((type) => type.name.trim() === readerTypeName));
  const batchPageCount = batch?.documents.reduce((sum, item) => sum + item.pages.length, 0) ?? 0;
  const detachedEvidenceIsCurrent = Boolean(
    document &&
    activeEvidenceTarget?.documentId === document.id &&
    activeEvidenceTarget.pageNumber === document.pages[selectedPage]?.number,
  );
  reviewSnapshotRef.current = document
    ? {
        fieldDrafts: fieldDraftStore.getSnapshot(),
        lineDrafts: lineDraftStore.getSnapshot(),
        jobName: job.name,
        jobId: job.id,
        joinTooltip,
        documentIndex: selectedDocument,
        documentCount: batch?.documents.length ?? 0,
        pageIndex: selectedPage,
        pageCount: document.pages.length,
        document,
        documentMutationToken: reviewDocumentMutationToken(document),
        documents:
          batch?.documents.map((item) => ({
            id: item.id,
            mutationToken: reviewDocumentMutationToken(item),
            sourceName: item.sourceName,
            documentType:
              filedDocumentType(item, job.documentTypes, typeNameHistory)?.name ??
              item.documentType,
            needsReview: item.needsReview,
            needsTeaching: item.needsTeaching,
            pages: item.pages.map(
              ({
                number,
                previewPath,
                startsNewDocument,
                boundaryConfidence,
                boundaryReasons,
              }) => ({
                number,
                previewPath,
                startsNewDocument,
                boundaryConfidence,
                boundaryReasons,
              }),
            ),
          })) ?? [],
        fields: configuredFields.map(({ id, name, kind, required }) => ({
          id,
          name,
          kind,
          required,
        })),
        documentTypes: job.documentTypes.map(({ id, name }) => ({ id, name })),
        activeEvidenceLines: detachedEvidenceIsCurrent ? activeEvidenceLines : [],
        activeEvidenceCitation:
          detachedEvidenceIsCurrent && activeEvidenceCitation?.lines === activeEvidenceLines
            ? { value: activeEvidenceCitation.value, words: activeEvidenceCitation.words }
            : null,
        activeBarcodeEvidence: detachedEvidenceIsCurrent ? activeBarcodeEvidence : null,
        activeLineItemRow: detachedEvidenceIsCurrent ? activeLineItemRow : null,
        selectedFieldId: arrivedFieldId,
        pinnedFieldIds,
        hoveredFieldId: fieldHoverStore.id,
        imageSelectionSerial,
        imageSelectedCell,
        // The same states the main window shows, in the same words. These had
        // drifted apart: the main window was reworded and this was not, so a detached
        // panel described the same operation differently — and said nothing at all
        // during a recheck, which is exactly when an operator wonders what is going on.
        // A split or join saving is not one of them: it is shown on its own boundary
        // (pendingBoundaries), and the panel's other controls stay usable.
        busyMessage: schemaRefreshBusy
          ? 'Applying the current job fields…'
          : boundaryRefreshing
            ? 'Rechecking the documents either side · you can keep working'
            : null,
        pendingBoundaries: boundaryQueueItems.flatMap((item) =>
          item.kind === 'recheck' || item.pageNumber == null
            ? []
            : [{ kind: item.kind, pageNumber: item.pageNumber, phase: item.phase }],
        ),
        // Sent so the detached panel can disable a row's controls while one saves,
        // the way the docked ledger does. The handler for its edits drops anything
        // that arrives mid-save, so without this a click landed in a window where
        // nothing happened and nothing said why.
        lineSaving: lineSaving || fieldSaving || typeSavingDocumentId === document.id,
        fieldSaving: fieldSaving || lineSaving || typeSavingDocumentId === document.id,
      }
    : null;
  batchRef.current = batch;
  // Read by work that finishes long after the render it started in — a schema
  // refresh has to know which document the operator is on WHEN IT RETURNS, not
  // which one they were on when it was sent.
  selectedDocumentRef.current = selectedDocument;
  const hasFieldDrafts = useSyncExternalStore(fieldDraftStore.subscribe, () =>
    fieldDraftStore.getSnapshot().some((draft) => draft.documentId === document?.id),
  );
  const hasLineDrafts = useSyncExternalStore(lineDraftStore.subscribe, () =>
    lineDraftStore.getSnapshot().some((draft) => draft.documentId === document?.id),
  );
  useEffect(() => {
    setBackOffer((current) => (current && current.shownOn !== document?.id ? null : current));
    setRefusedLine(null);
  }, [document?.id]);

  useEffect(() => {
    const previous = previousDraftDocumentId.current;
    previousDraftDocumentId.current = document?.id ?? null;
    const searchNavigation = retainedSearchDraftNavigation.current;
    retainedSearchDraftNavigation.current = null;
    // A read-only search jump keeps the original editor's raw text. The intent
    // belongs to one exact transition, never a later ordinary navigation.
    if (
      searchNavigation &&
      searchNavigation.batchId === batchRef.current?.batchId &&
      searchNavigation.from === previous &&
      searchNavigation.to === document?.id
    )
      return;
    // Navigation is an explicit finish gesture; component cleanup is not.
    // Retained drafts outlive dock/undock, and deletion never starts a write.
    submitLeavingFieldDrafts(
      fieldDraftStore,
      previous,
      document?.id,
      Boolean(batchRef.current?.documents.some((item) => item.id === previous)),
    );
    submitLeavingFieldDrafts(
      lineDraftStore,
      previous,
      document?.id,
      Boolean(batchRef.current?.documents.some((item) => item.id === previous)),
    );
  }, [document?.id, fieldDraftStore, lineDraftStore]);

  fieldDraftCommandRef.current = fieldDraftCoordinator;
  lineDraftCommandRef.current = lineDraftCoordinator;

  useEffect(() => {
    mounted.current = true;
    setStagedCapturesLoaded(false);
    void window.capturePointDesktop
      ?.getEngineHealth()
      .then(setHealth)
      .catch((reason: unknown) =>
        setError(
          messageFor(reason, 'Paige could not prepare document processing.'),
          'engine-not-ready',
        ),
      );
    // Discovery must not silently replace Folder or the user's selected source.
    void window.capturePointDesktop
      ?.getScannerSources()
      .then((result) => {
        setScanners(result);
      })
      .catch(() => setScanners({ sources: [], errors: ['Scanner discovery unavailable'] }));
    void window.capturePointDesktop
      ?.getSimulatedScanners()
      .then((catalog) => {
        setSimulatorTray(catalog.tray);
        setSimulatorStacks(catalog.stacks ?? []);
      })
      .catch(() => setSimulatorTray(null));
    const loadStagedCaptures = () =>
      window.capturePointDesktop
        ?.listStagedCaptures(job.id)
        .then(async (staged) => {
          // A wave that failed because the machine went away - the engine died,
          // the app was closed mid-recognition - is not a verdict on its pages,
          // and asking a person to click Retry on weather is how a "Needs retry"
          // chip ends up living in the corner forever. Outage-shaped failures
          // re-queue themselves on arrival; only a wave the pipeline itself
          // refused stays failed for a person to look at.
          // An engine exception (a code fault, since fixed) is machine weather too:
          // the pages were never the problem. If a retry genuinely re-fails, the
          // wave lands back here with the new error.
          //
          // BUT ONLY SO MANY TIMES. That last sentence was true about the error and
          // false about the loop: an outage-shaped failure that re-fails the same way
          // matched the pattern again on the next mount, and re-queued again, on every
          // start of the application, for ever — because nothing counted. Weather
          // clears in a restart or two; a third identical attempt is not weather, it
          // is a machine that cannot do this work, and the operator is owed the news
          // rather than a queue that quietly cycles. The count comes from the main
          // process, where recognition actually starts, so a hand-clicked retry and
          // this automatic one are the same event.
          const outage =
            /engine|did not start|restarted safely|stopped unexpectedly|interrupted|closed before|object reference|nullreference/i;
          const healed = await Promise.all(
            staged.map(async (item) => {
              if (
                item.status !== 'failed' ||
                !outage.test(item.error ?? 'interrupted') ||
                (item.attempts ?? 0) >= AUTOMATIC_RETRY_LIMIT
              )
                return item;
              try {
                const attempts = await window.capturePointDesktop!.retryStagedCapture(item.id);
                return { ...item, status: 'pending' as const, error: null, attempts };
              } catch {
                // Main refused the durable transition, so the renderer keeps showing
                // the failed manifest rather than dispatching an imaginary retry.
                return item;
              }
            }),
          );
          if (mounted.current) {
            setCaptureQueue(healed);
            setStagedCapturesLoaded(true);
          }
        })
        .catch((reason: unknown) =>
          setError(messageFor(reason, 'Could not recover pending pages'), 'pages-recover-failed'),
        );
    if (!savedBatch && window.capturePointDesktop)
      void (async () => {
        // The flag drops when the truth is known: the job really is empty, or its
        // documents are in hand. Dropping it between those points re-flashes the
        // wrong screen for the gap the skeleton load takes.
        //
        // "No batch" was taken for the first of those and is not. A job whose
        // FIRST acquisition is still reading has no batch to load and is not
        // empty, and answering "empty" put an operator on the intake screen for
        // five minutes with no way to reach the pages his workstation was already
        // reading. So the third answer is asked for here, and while it holds this
        // waits — on the numbers the main process kept while this view was gone,
        // never on numbers rebuilt from what happens to be committed.
        let overview: BatchWorkspaceOverview | null = null;
        let waitedForFirstBatch = false;
        // A failed read is not "no batch": an engine that is down or updating made the job look
        // empty and put the operator on the intake screen. It is asked again while the opening
        // screen stays up, and only a read that keeps failing ends in the load error.
        let overviewFailures = 0;
        while (!overview) {
          const read = await window.capturePointDesktop!.getLatestBatchOverview(job.id).then(
            (value) => ({ ok: true as const, value }),
            (reason: unknown) => ({ ok: false as const, reason }),
          );
          if (!mounted.current) return;
          if (!read.ok) {
            if (++overviewFailures >= OPENING_OVERVIEW_ATTEMPTS) throw read.reason;
            await new Promise((resolve) => window.setTimeout(resolve, firstBatchPollMilliseconds));
            if (!mounted.current) return;
            continue;
          }
          overviewFailures = 0;
          const latest = read.value;
          const live = latest
            ? null
            : await window.capturePointDesktop!.getLiveCaptureProgress(job.id).catch(() => null);
          if (!mounted.current) return;
          const opening = decideOpeningScreen(latest, live);
          if (opening.screen === 'intake') {
            forgetRejoinedCapture();
            setOpeningSavedBatch(false);
            return;
          }
          if (opening.screen === 'workspace') {
            overview = opening.overview;
            break;
          }
          if (rejoinedCapture.current !== opening.progress.captureId) {
            rejoinedCapture.current = opening.progress.captureId;
            setRejoinedCaptureId(opening.progress.captureId);
          }
          setOpeningProgress((shown) => advanceOpeningProgress(shown, opening.progress));
          waitedForFirstBatch = true;
          await new Promise((resolve) => window.setTimeout(resolve, firstBatchPollMilliseconds));
          if (!mounted.current) return;
        }
        forgetRejoinedCapture();
        setOpeningProgress(null);
        // The queue was listed before that wait and the wave we waited out has
        // since committed and deleted its own manifest, so the drain below would
        // otherwise open a job by asking the engine to process pages that are
        // already in the batch it is showing.
        if (waitedForFirstBatch) await loadStagedCaptures();
        // Overview, index and windows read as one answer (readWorkspaceDocumentList): an export
        // removing a listed document between the index and its windows read again, not
        // "The document index is missing position N." at the person opening the job.
        const listed = await readWorkspaceDocumentList(window.capturePointDesktop!, job.id);
        if (!mounted.current) return;
        if (!listed) {
          setOpeningSavedBatch(false);
          return;
        }
        overview = listed.overview;
        const { index, summaryById } = listed;
        const skeletons: ProcessResult['documents'][number][] = index.map((item) => {
          const summary = summaryById.get(item.id)!;
          return {
            id: summary.id,
            sourceName: summary.sourceName,
            sourcePath: '',
            sourcePaths: [],
            documentType: summary.documentType,
            documentTypeId: summary.documentTypeId,
            documentTypeSchemaVersion: summary.documentTypeSchemaVersion,
            fieldContractSignature: summary.fieldContractSignature,
            documentTypeConfidence: summary.documentTypeConfidence,
            documentTypeVerified: summary.documentTypeVerified,
            pages: summary.pages.map((page) => ({
              number: page.number,
              previewPath: page.previewPath,
              text: '',
              ocrConfidence: 0,
              ocrElapsedMilliseconds: 0,
              barcodeElapsedMilliseconds: 0,
              lines: [],
              words: [],
              barcodes: [],
              startsNewDocument: page.startsNewDocument,
              boundaryConfidence: page.boundaryConfidence,
              boundaryReasons: page.boundaryReasons ?? [],
            })),
            fields: [],
            lineItems: [],
            validationFindings: [],
            needsReview: summary.needsReview,
            needsTeaching: summary.needsTeaching,
          } satisfies ProcessResult['documents'][number];
        });
        projectionSkeletons.current = new Map(skeletons.map((item) => [item.id, item]));
        let documents = skeletons;
        // The default review spine and the viewer must open on the same work.
        // Hydrate that document directly rather than first showing a hidden,
        // already-confirmed document and loading the useful one a second time.
        const openingIndex = openingDocumentIndex(skeletons, calibrating);
        const firstId = skeletons[openingIndex]?.id;
        let hydratedFirstId: string | undefined;
        if (firstId) {
          const first = await window.capturePointDesktop!.getBatchDocument({
            jobId: job.id,
            batchId: overview.batchId,
            documentId: firstId,
          });
          if (first) {
            documents = skeletons.map((item) => (item.id === first.id ? first : item));
            hydratedFirstId = first.id;
          }
        }
        const result: ProcessResult = {
          batchId: overview.batchId,
          startedAt: overview.startedAt,
          completedAt: overview.completedAt,
          documents,
          warnings: overview.warnings,
          timings: overview.timings ?? undefined,
          workspaceProjection: 'paged',
          loadedDocumentIds: hydratedFirstId ? [hydratedFirstId] : [],
        };
        if (!mounted.current) return;
        const openedLoaded = new Set(hydratedFirstId ? [hydratedFirstId] : []);
        loadedDocumentIdsRef.current = openedLoaded;
        loadedDocumentOrder.current = [...openedLoaded];
        setLoadedDocumentIds(openedLoaded);
        batchRef.current = result;
        // PAIGE (docs/engineering/workbench-port.md, "Review n"): the ref with the batch, as the other
        // writers of both do. A saved-batch effect still waiting from an earlier arrival runs before
        // this is drawn, and read the new batch at the old place: Document 1 under Needs review.
        selectedDocumentRef.current = openingIndex;
        setSelectedDocument(openingIndex);
        setBatch(result);
        onRestored(result);
        setOpeningSavedBatch(false);
        // A discovered proposal can outlive the renderer that heard it: the
        // engine persists it with the batch, but the auto-apply lived only in
        // the process CALLBACK — reload between completion and apply and the
        // job stayed typeless for ever, its own documents reading "Invoice"
        // while the job had no Invoice to file them under. Found the day the
        // boundary speedup let a batch complete BEFORE the smoke suite's
        // deliberate mid-flow reload. Reconcile on open: a typeless job adopts
        // the proposal its own stored batch carries. The full-batch fetch is
        // acceptable here precisely because the job is typeless — it is at the
        // very start of its life, not ten thousand pages in.
        // The job as it is NOW, not as this view mounted with it: a rename or setting saved
        // while the documents loaded must survive, a job that gained types meanwhile adopts
        // nothing, and adopting keeps the job's own status rather than forcing "provisional".
        if (liveJobRef.current.id === job.id && liveJobRef.current.documentTypes.length === 0) {
          const full = await window.capturePointDesktop!.getLatestBatch(job.id).catch(() => null);
          const current = liveJobRef.current;
          if (
            full?.bootstrapProposal?.documentTypes.length &&
            mounted.current &&
            current.id === job.id &&
            current.documentTypes.length === 0
          ) {
            const adopted = applyBootstrapProposal(current, full);
            if (adopted !== current) await onCommitJob({ ...adopted, status: current.status });
          }
        }
      })().catch((reason: unknown) => {
        forgetRejoinedCapture();
        setOpeningProgress(null);
        setOpeningSavedBatch(false);
        setError(messageFor(reason, 'Could not load the latest batch'), 'documents-load-failed');
      });
    void loadStagedCaptures();
    return () => {
      mounted.current = false;
    };
  }, [job.id]);
  useEffect(() => {
    const host = primaryReviewChannel(noteCloseBarrier);
    const channel = host.channel;
    reviewChannelRef.current = channel;
    return host.attach((event: MessageEvent<ReviewWorkspaceMessage>) => {
      if (event.data.type === 'request-state')
        channel.postMessage({
          type: 'state',
          snapshot: reviewSnapshotRef.current,
        } satisfies ReviewWorkspaceMessage);
      else reviewActionHandler.current(event.data);
    });
    // The channel lives as long as the primary renderer, not this view. Keep
    // its reference for exact outcomes from saves admitted before navigation.
  }, []);
  useEffect(
    () =>
      registerCloseBlocker(() => {
        // Only operator-owned save lanes veto closing. Background capture, learning
        // and the automatic schema refresh do not force an hours-long exit wait.
        if (
          (wholeBatchMutationRef.current && !schemaRefreshBusyRef.current) ||
          structuralMutationRef.current ||
          boundaryRefreshesInFlight.current > 0 ||
          boundaryQueueDepth.current > 0 ||
          pageEditBusyRef.current ||
          fieldSavingRef.current ||
          typeSavingDocumentIdRef.current ||
          queuedTypeConfirmationIdsRef.current.size > 0 ||
          lineMutationAdmissions.current > 0 ||
          pendingLineSaves.current > 0 ||
          confirmingDocumentsRef.current.size > 0 ||
          detachedMutationDocuments.current.size > 0 ||
          [...fieldDraftStore.getSnapshot(), ...lineDraftStore.getSnapshot()].some(
            (draft) => draft.state === 'saving',
          )
        )
          return 'saving';
        return fieldDraftStore.getSnapshot().length || lineDraftStore.getSnapshot().length
          ? 'drafts'
          : 'idle';
      }),
    [fieldDraftStore, lineDraftStore],
  );
  useEffect(() => {
    if (!window.capturePointDesktop) return;
    const preferred = new Set(readReviewLayout(job.id).detachedPanels);
    const unsubscribe = window.capturePointDesktop.onReviewPanelStateChanged(
      ({ panel, detached }) => setDetachedPanels((current) => ({ ...current, [panel]: detached })),
    );
    void window.capturePointDesktop
      .getDetachedReviewPanels()
      .then(async (actual) => {
        for (const panel of actual)
          if (!preferred.has(panel)) await window.capturePointDesktop?.closeReviewPanel(panel);
        for (const panel of preferred)
          if (!actual.includes(panel))
            await window.capturePointDesktop?.openReviewPanel(panel, job.id);
        setDetachedPanels({
          viewer: preferred.has('viewer'),
          fields: preferred.has('fields'),
          'line-items': preferred.has('line-items'),
        });
      })
      .catch((reason: unknown) =>
        setError(
          messageFor(reason, 'The saved window layout could not be restored'),
          'panel-layout-failed',
        ),
      );
    return unsubscribe;
  }, [job.id]);
  useEffect(() => {
    reviewChannelRef.current?.postMessage({
      type: 'state',
      snapshot: reviewSnapshotRef.current,
    } satisfies ReviewWorkspaceMessage);
  }, [
    batch,
    selectedDocument,
    selectedPage,
    job,
    activeEvidenceLines,
    activeEvidenceCitation,
    activeBarcodeEvidence,
    activeLineItemRow,
    arrivedFieldId,
    imageSelectionSerial,
    imageSelectedCell,
    boundaryQueueItems,
    boundaryRefreshing,
    schemaRefreshBusy,
    fieldSaving,
    lineSaving,
    typeSavingDocumentId,
    confirmingDocumentIds,
    saveLaneRevision,
    learningReceipt,
    learningAcceleration,
    learningProgress,
    scorecard,
    pinnedFieldIds,
    // What the panels' main button, Skip and Back are worked out from (snapshot.review).
    backOffer,
    confirmBeat,
    calibrating,
    hasFieldDrafts,
    hasLineDrafts,
    allDoneArmed,
    refusedLine,
    loadedDocumentIds,
    detachedPanels,
    // Pages being read, or waiting to be, turn the panel's Delete off and on (snapshot.review.deleteBusy).
    Boolean(captureProcessingId) || captureQueue.some((item) => item.status === 'pending'),
  ]);
  // Hover changes nothing else, and a pointer crossing a page changes it many times a second: only the
  // field goes, and only to a panel that is open to show it (a panel opening asks for the full state).
  const anyPanelDetached = Object.values(detachedPanels).some(Boolean);
  useEffect(
    () =>
      fieldHoverStore.subscribe(() => {
        if (!reviewSnapshotRef.current) return;
        reviewSnapshotRef.current.hoveredFieldId = fieldHoverStore.id;
        if (anyPanelDetached)
          reviewChannelRef.current?.postMessage({
            type: 'hover',
            fieldId: fieldHoverStore.id,
          } satisfies ReviewWorkspaceMessage);
      }),
    [anyPanelDetached],
  );
  useEffect(() => {
    // A queued mouse-leave from the previous revision must not control this
    // document. Clear its transient highlight at the owner instead.
    fieldHoverStore.set(null);
    return () => fieldHoverStore.set(null);
  }, [document?.id, currentDocumentMutationToken]);
  // Progress events arrive per page and per activity - many a second during a
  // wave - and every one used to re-render the whole workspace, which at four
  // thousand pages is a hundred-millisecond tree; back to back, that reads as
  // a locked interface. The screen only needs a few frames a second: events
  // land in refs, and one flush every 300ms moves them into state.
  const pendingCaptureWaves = useRef<Map<string, CaptureProcessingProgress>>(new Map());
  const pendingCaptureEvents = useRef<CaptureProcessingProgress[]>([]);
  // Every event of every wave between flushes, for the working screen's one
  // progress state: the latest-per-wave map alone skipped finished pages.
  const pendingWorkingEvents = useRef<CaptureProcessingProgress[]>([]);
  useEffect(() => {
    const unsubscribe = window.capturePointDesktop?.onCaptureProcessingProgress((progress) => {
      // NAMES ARE RECORDED BEFORE ANY FILTER.
      //
      // The kinds this job has found are the whole subject of the screen titled
      // "Discovering types and fields", and every path that was supposed to carry
      // them there ran downstream of the guard below. That guard drops any event
      // whose wave has retired, and on a bootstrap the wave retires BEFORE the
      // understanding stage names anything: measured on a 120-page run, zero
      // type-bearing events while the panel was mounted and 44 in the 70 seconds
      // after it was torn down.
      //
      // (An earlier note here blamed the guard for rejecting events. It does not —
      // measured 263 of 263 accepted, zero rejections. The events were not being
      // refused; they had not been sent yet.)
      //
      // A name, once the engine has said it, is a fact about this job: recorded
      // here from the raw event, and forgotten only when the job changes.
      if (progress.jobId === job.id) {
        if (progress.stage) {
          waveStages.current = foldWaveStage(
            waveStages.current,
            progress.captureId,
            progress.stage,
          );
          setEngineUnderstanding(wavesUnderstanding(waveStages.current));
        }
        // Before the 300ms flush can drop it behind a newer event of the same wave.
        if (!progress.heartbeat) rememberReadyType(job.id, progress.activity);
        let grew = false;
        for (const raw of [
          ...(progress.documentTypes ?? []),
          progress.completedDocument?.documentType,
        ]) {
          const name = raw?.trim();
          if (!name || DocumentStoreIsNonAnswer(name)) continue;
          if (!namedTypes.current.has(name)) {
            namedTypes.current.add(name);
            grew = true;
          }
        }
        if (grew)
          setSeenTypeNames(
            [...namedTypes.current].sort((left, right) => left.localeCompare(right)),
          );
      }
      if (progress.jobId !== job.id || !watchedCapture(progress.captureId)) return;
      // The moment a wave enters extraction, the reading stages go quiet and
      // the next wave may claim them.
      if (
        progress.stage === 'understanding' &&
        !captureReachedExtraction.current.has(progress.captureId)
      ) {
        captureReachedExtraction.current.add(progress.captureId);
        setCaptureQueue((current) => [...current]);
      }
      // Every in-flight wave keeps its own lane. Filtering to the wave that
      // held `captureProcessingId` was what made overlapped waves invisible —
      // and dropped their activity and completed documents on the floor.
      pendingCaptureWaves.current.set(progress.captureId, progress);
      pendingWorkingEvents.current.push(progress);
      // A heartbeat repeats the last state so the screen can show the engine is
      // alive; it is not new work and must not re-enter the work lists.
      if ((progress.activity || progress.completedDocument) && !progress.heartbeat)
        pendingCaptureEvents.current.push(progress);
    });
    const flush = window.setInterval(() => {
      const latest = pendingCaptureWaves.current;
      const events = pendingCaptureEvents.current;
      if (latest.size === 0 && events.length === 0) return;
      pendingCaptureWaves.current = new Map();
      pendingCaptureEvents.current = [];
      const workingBatch = pendingWorkingEvents.current;
      pendingWorkingEvents.current = [];
      if (workingBatch.length > 0) setWorkingEvents(workingBatch);
      if (latest.size > 0)
        setCaptureWaves((current) => {
          const next = new Map(current);
          // A wave that settled between event and flush stays retired.
          for (const [captureId, wave] of latest)
            if (watchedCapture(captureId)) next.set(captureId, wave);
          return next;
        });
      const withActivity = events.filter((event) => event.activity);
      const pageEvents = events.filter(
        (event) =>
          watchedCapture(event.captureId) &&
          event.activity?.kind === 'page' &&
          event.activity.previewPath &&
          event.activity.pageNumber > 0,
      );
      if (pageEvents.length > 0)
        setIncomingPages((current) => {
          const next = new Map(current);
          for (const event of pageEvents) {
            const activity = event.activity!;
            next.set(`${event.captureId}:${activity.pageNumber}`, {
              captureId: event.captureId,
              pageNumber: activity.pageNumber,
              previewPath: activity.previewPath!,
              label: activity.label,
            });
          }
          return next;
        });
      if (withActivity.length > 0) {
        const updatedAt = Date.now();
        setCaptureWork((current) => {
          let next = current;
          for (const event of withActivity) {
            const activity = event.activity!;
            const task: FolderWorkTask = {
              id: activity.id,
              kind: activity.kind,
              status: activity.status,
              label: activity.label,
              detail: activity.detail ?? '',
              pageNumber: activity.pageNumber,
              updatedAt,
              previewPath: activity.previewPath,
              tags: activity.tags ?? [],
              document: event.completedDocument,
              evidence: activity.evidence,
            };
            const existing = next.findIndex((item) => item.id === activity.id);
            next =
              existing >= 0
                ? next.map((item, index) => (index === existing ? { ...item, ...task } : item))
                : [...next, task];
          }
          return next.slice(-120);
        });
        const noteworthy = withActivity.filter((event) => {
          const activity = event.activity!;
          return (
            activity.status === 'failed' ||
            activity.kind === 'document' ||
            activity.kind === 'boundary' ||
            activity.kind === 'proposal' ||
            (activity.kind === 'barcode' && (activity.detail ?? '').includes('found'))
          );
        });
        if (noteworthy.length > 0) {
          setCaptureActivity((current) => {
            let next = current;
            for (const event of noteworthy) {
              const activity = event.activity!;
              next = [
                {
                  id: activity.id,
                  stage: event.stage,
                  kind: activity.kind,
                  status: activity.status,
                  title: activity.label,
                  detail: activity.detail ?? '',
                  at: Date.now(),
                },
                ...next.filter((item) => item.id !== activity.id),
              ];
            }
            return next.slice(0, 12);
          });
        }
      }
      // A rejoined capture's documents arrive with its saved batch; the working
      // screen stays up until then rather than opening a preview batch of its own.
      const completions = events.filter(
        (event) => event.completedDocument && event.captureId !== rejoinedCapture.current,
      );
      if (
        completions.length > 0 &&
        !(batchRef.current?.batchId && !batchRef.current.batchId.startsWith('live:'))
      ) {
        setBatch((current) => {
          let live =
            current?.batchId === `live:${captureProcessingId}`
              ? current
              : {
                  batchId: `live:${captureProcessingId}`,
                  startedAt: new Date().toISOString(),
                  completedAt: new Date().toISOString(),
                  documents: [],
                  warnings: [],
                };
          for (const event of completions) {
            if (live.documents.some((document) => document.id === event.completedDocument!.id))
              continue;
            live = {
              ...live,
              completedAt: new Date().toISOString(),
              documents: [...live.documents, event.completedDocument!],
            };
          }
          batchRef.current = live;
          return live;
        });
      }
    }, 300);
    return () => {
      unsubscribe?.();
      window.clearInterval(flush);
    };
  }, [job.id, captureProcessingId]);

  // Keyed on the page's file, not on the document object.
  //
  // It depended on `document`, and every keystroke in a line item builds a new
  // document object — so typing a single character blanked the preview, read the
  // image off disk again, and let onLoad refit the view. The operator saw the
  // page flash and their zoom and pan thrown away, once per key press, while
  // editing the very cell they had zoomed in to read.
  //
  // The path is what decides which image belongs on screen. Nothing else in that
  // object can change which file this is.
  //
  // One exception: a page turn's answer names the turned image for the same page. The old image
  // stays on screen, still drawn turned (page-turn-queue.ts), until the new one has loaded, so the
  // page does not blank or flick back. `previewImagePath` is the file the image on screen is.
  const previewPath = document?.pages[selectedPage]?.previewPath;
  const previewSlotRef = useRef<string | null>(null);
  const previewRequestRef = useRef<string | null>(null);
  useEffect(() => {
    const slot = document
      ? pageTurnKey(document.id, document.pages[selectedPage]?.number ?? -1)
      : null;
    const turnLanding =
      slot !== null && previewSlotRef.current === slot && pageTurnsRef.current.has(slot);
    // More turns of this page are still on their way (clicks coalesced behind the request just answered):
    // this answer's image is an in-between orientation. Keep the image on screen, drawn at the person's
    // turn, and load only the last answer's image, once. Loading each one re-fitted the page and showed
    // the in-between turn: four quick clicks kept the page zooming in and out for 10-15 s (Dustin, 2026-10-10).
    if (turnLanding && pageTurnBusy(pageTurnsRef.current.get(slot))) return;
    previewSlotRef.current = slot;
    previewRequestRef.current = previewPath ?? null;
    if (!turnLanding) {
      setPreview(null);
      setPreviewImagePath(null);
      setPreviewSize({ width: 1, height: 1 });
    }
    if (!previewPath) return;
    void window.capturePointDesktop
      ?.readPagePreview(previewPath)
      .then((source) => {
        if (previewSlotRef.current !== slot || previewRequestRef.current !== previewPath) return;
        setPreview(source);
        setPreviewImagePath(previewPath);
      })
      .catch((reason: unknown) =>
        setError(messageFor(reason, 'Could not load page preview'), 'page-preview-failed'),
      );
  }, [previewPath]);
  useEffect(() => {
    const spine = spineElement;
    if (!spine) return;
    const update = () =>
      setSpineViewport((current) => ({
        top: spine.scrollTop,
        height: spine.clientHeight || current.height,
      }));
    const observer = new ResizeObserver(update);
    observer.observe(spine);
    update();
    return () => observer.disconnect();
  }, [spineElement]);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const metric = spineMetricsRef.current.find(
        (item) => item.documentIndex === selectedDocument,
      );
      const spine = spineRef.current;
      if (!metric || !spine) return;
      // Measured the same way the rows are drawn. `metric.top` is already in
      // the current row height, so the offset within the document has to be
      // too — the 110 and 138 this replaced were the old fixed row and row plus
      // header, and a page deep in a long document would land further off with
      // every page once the row is the operator's to set. One header of lead-in
      // above the page keeps the document's name on screen when the spine
      // scrolls up to reach it, which is what the old 138 was buying.
      const pageTop = spinePageTop(metric.top, Math.max(0, selectedPage), spinePageRow);
      const targetTop = pageTop - SPINE_HEADER_ROW;
      const targetBottom = Math.min(metric.top + metric.height, pageTop + spinePageRow);
      if (spineStartRequest.current === selectedDocument) {
        spine.scrollTop = targetTop;
        spineStartRequest.current = null;
      } else if (targetTop < spine.scrollTop) spine.scrollTop = targetTop;
      else if (targetBottom > spine.scrollTop + spine.clientHeight)
        spine.scrollTop = Math.max(0, targetBottom - spine.clientHeight);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedDocument, selectedPage, batch?.documents.length]);

  const applyProcessedResult = async (
    result: ProcessResult,
    appendOpeningBatch: ProcessResult | null = null,
  ) => {
    // A wave may finish after setup activation or a newer operator edit.
    // Discoveries extend the live schema; they do not reopen accepted setup.
    const currentJob = liveJobRef.current;
    if (currentJob.id !== job.id) return;
    const bootstrappedJob = applyBootstrapProposal(currentJob, result);
    // The wave is already committed in the store: a job save that fails must not keep its
    // documents off screen. They land below, and the failure is thrown after (the caller
    // says so); the next wave proposes the same types again.
    // (Untyped on purpose: tests run this part of the owner as plain JavaScript.)
    let jobSaveFailure = null;
    if (bootstrappedJob !== currentJob) {
      const updated = { ...bootstrappedJob, status: currentJob.status };
      liveJobRef.current = updated;
      try {
        await onCommitJob(updated);
      } catch (reason) {
        if (liveJobRef.current === updated) liveJobRef.current = currentJob;
        jobSaveFailure = reason ?? new Error('The job could not be saved');
      }
    }
    // Saving a newly discovered type is an async boundary. Field and line-item
    // work on existing documents is intentionally still allowed while capture is
    // running, so the batch captured before that await is no longer an authority.
    // Reacquire it here before calculating append offsets or merging documents.
    const currentBatch = batchRef.current;
    const isAppend = currentBatch?.batchId === result.batchId;
    const firstAddedDocument = isAppend ? currentBatch.documents.length : 0;
    const reviewingDocumentId = isAppend
      ? currentBatch.documents[selectedDocumentRef.current]?.id
      : undefined;
    let adopted = result;
    if (isAppend && currentBatch) {
      // An append never rewrites an existing document in the store. Keep the
      // renderer's live copy of every pre-existing identity so a field save or
      // hydration that landed after the append committed cannot be replaced by
      // the append response arriving later over IPC.
      const liveById = new Map(currentBatch.documents.map((item) => [item.id, item]));
      // The response owns what this append added; the renderer owns changes made
      // after the append opened. Warnings need a three-way merge because an
      // operator may dismiss one while the native response is crossing IPC. A
      // warning that existed at open but is absent now was deliberately removed;
      // a warning absent at open was produced by this (or a sibling) append.
      const openingWarnings = new Set(
        appendOpeningBatch?.batchId === result.batchId ? appendOpeningBatch.warnings : [],
      );
      const liveWarnings = new Set(currentBatch.warnings);
      const warnings = [
        ...new Set([
          ...currentBatch.warnings,
          ...result.warnings.filter(
            (warning) => !openingWarnings.has(warning) || liveWarnings.has(warning),
          ),
        ]),
      ];
      const paged =
        currentBatch.workspaceProjection === 'paged' || result.workspaceProjection === 'paged';
      if (paged) {
        const incomingIds = new Set(result.documents.map((item) => item.id));
        const selectedId = currentBatch.documents[selectedDocumentRef.current]?.id;
        const order = [
          ...new Set(
            loadedDocumentOrder.current.filter(
              (id) => incomingIds.has(id) && loadedDocumentIdsRef.current.has(id),
            ),
          ),
        ];
        // Selection protects a genuinely hydrated document from eviction; it
        // does not turn a skeleton into a hydrated document. Marking an
        // in-flight selected skeleton loaded made the append's parent echo reject
        // its request and then suppress every retry while the pane stayed blank.
        if (
          selectedId &&
          incomingIds.has(selectedId) &&
          loadedDocumentIdsRef.current.has(selectedId) &&
          !order.includes(selectedId)
        )
          order.push(selectedId);
        const narrowMutationProtectedIds = activeNarrowMutationDocumentIds();
        while (order.length > 12) {
          const evictAt = order.findIndex(
            (id) => id !== selectedId && !narrowMutationProtectedIds.has(id),
          );
          if (evictAt < 0) break;
          order.splice(evictAt, 1);
        }
        const kept = new Set(order);
        adopted = {
          ...result,
          warnings,
          workspaceProjection: 'paged',
          loadedDocumentIds: [...kept],
          documents: result.documents.map((item) =>
            kept.has(item.id) ? (liveById.get(item.id) ?? item) : toProjectionSkeleton(item),
          ),
        };
        loadedDocumentOrder.current = order;
        loadedDocumentIdsRef.current = kept;
        setLoadedDocumentIds(kept);
        rebuildProjectionSkeletonCache(
          adopted.documents,
          activeNarrowMutationDocumentIds(),
          new Map(result.documents.map((item) => [item.id, item])),
        );
      } else {
        adopted = {
          ...result,
          warnings,
          documents: result.documents.map((item) => liveById.get(item.id) ?? item),
        };
      }
    } else {
      const loaded = new Set(
        result.workspaceProjection === 'paged'
          ? (result.loadedDocumentIds ?? [])
          : result.documents.map((item) => item.id),
      );
      loadedDocumentIdsRef.current = loaded;
      loadedDocumentOrder.current = [...loaded];
      setLoadedDocumentIds(loaded);
      projectionSkeletons.current = new Map(
        result.workspaceProjection === 'paged'
          ? result.documents.map((item) => [item.id, toProjectionSkeleton(item)])
          : [],
      );
    }
    batchRef.current = adopted;
    setBatch(adopted);
    onProcessed(adopted);
    // Background intake must not move the operator to a different document
    // between reading a value and confirming it. Keep the current identity and
    // page when appending to its batch; only a genuinely new batch opens at start.
    const appended = selectionAfterRefresh(reviewingDocumentId, adopted.documents, () =>
      Math.min(firstAddedDocument, Math.max(0, adopted.documents.length - 1)),
    );
    const reviewingIndex = reviewingDocumentId
      ? adopted.documents.findIndex((item) => item.id === reviewingDocumentId)
      : -1;
    // The document being reviewed left the list: what opens instead was not chosen (B-09).
    if (appended.movedUnder) movedUnder.current = appended.movedUnder;
    setSelectedDocument(appended.index);
    if (reviewingIndex < 0) setSelectedPage(0);
    if (jobSaveFailure) throw jobSaveFailure;
  };

  // Both operator panels, so they are reachable from the empty capture screen
  // and from the review workspace without being declared twice.
  // Kept pages of an interrupted scan, waiting for the operator's decision.
  const heldScan = captureQueue.find((item) => item.status === 'held');
  const capturePanels = (
    <>
      {simulatorOpen && (
        <SimulatedScannerModal
          jobId={job.id}
          initialStack={simulatorCorpus ?? sampleSetForJob(job, simulatorStacks)}
          stackLocked={
            isSampleJob(job) && Boolean(simulatorCorpus ?? sampleSetForJob(job, simulatorStacks))
          }
          autoStart={isSampleJob(job) && Boolean(simulatorCorpus)}
          documents={simulatorDocuments}
          onClose={() => {
            setSimulatorOpen(false);
            setSimulatorCorpus(null);
            setSimulatorDocuments(undefined);
          }}
          onToast={onNotice}
          onCaptured={(capture) => {
            // The simulated scan brought pages in (telemetry catalog source.outcome).
            reportEvent('source.outcome:picked', {
              jobId: job.id,
              attrs: { files: telemetryCountBucket(capture.fileCount) },
            });
            setCaptureQueue((current) =>
              current.some((item) => item.id === capture.id) ? current : [...current, capture],
            );
          }}
        />
      )}
      {/* The same condition as both Stop buttons: only while a scanner is
          actually on the wire. Files are the operating system's dialog and a
          folder stages instantly, so neither has a feed to watch. */}
      {/* PAIGE (docs/engineering/workbench-port.md): a scanner source with its own scanning window
          (Paige Scan's sheet) is not fed through this one; its problems still show here. */}
      {((acquiring &&
        sourceKey !== 'files' &&
        sourceKey !== 'folder' &&
        sourceKey !== 'simulator' &&
        hostHas('scanner-feed')) ||
        scanProblem) && (
        <LiveScanFeed
          jobId={job.id}
          operationId={scanOperationId}
          sourceLabel={scanProblem?.source ?? sourceLabel}
          onStop={() => void stopAcquiring()}
          problem={scanProblem?.problem}
          onClose={() => setScanProblem(null)}
          onRetry={() => {
            const problem = scanProblem;
            setScanProblem(null);
            if (problem?.keptPagesNotQueued) void queueKeptScanPages(problem.source);
            else if (problem?.sourceKey) void acquireDocuments(problem.sourceKey);
          }}
        />
      )}
      {heldScan &&
        !acquiring &&
        !scanSettling &&
        !scanProblem &&
        (keptScanDismissed.has(heldScan.id) ? (
          <button
            type="button"
            className="secondary-button kept-scan-reminder"
            onClick={() =>
              setKeptScanDismissed((current) => {
                const next = new Set(current);
                next.delete(heldScan.id);
                return next;
              })
            }
          >
            <TriangleAlert size={14} /> {heldScan.fileCount.toLocaleString()} kept{' '}
            {heldScan.fileCount === 1 ? 'page is' : 'pages are'} waiting for you
          </button>
        ) : (
          <KeptScanDialog
            key={heldScan.id}
            capture={heldScan}
            waitingBehind={captureQueue
              .slice(captureQueue.indexOf(heldScan) + 1)
              .reduce((sum, item) => sum + item.fileCount, 0)}
            onRead={() => void releaseHeldScan(heldScan.id)}
            onScanMore={
              sourceKey.startsWith('scanner:') ? () => void acquireDocuments(sourceKey) : undefined
            }
            onDiscard={() => void discardStagedCapture(heldScan.id)}
            onClose={() => setKeptScanDismissed((current) => new Set(current).add(heldScan.id))}
          />
        ))}
      {breakSheetsOpen && (
        <BreakSheetsModal job={job} onClose={() => setBreakSheetsOpen(false)} onToast={onNotice} />
      )}
    </>
  );

  /** Let an interrupted scan's kept pages (and anything queued behind them) be read. */
  const releaseHeldScan = async (captureId: string) => {
    try {
      await window.capturePointDesktop?.releaseHeldCapture(captureId);
      if (mounted.current)
        setCaptureQueue((current) =>
          current.map((item) => (item.id === captureId ? { ...item, status: 'pending' } : item)),
        );
    } catch (reason) {
      if (mounted.current)
        setError(
          reason instanceof Error ? reason.message : 'The kept pages could not be released',
          'queue-change-failed',
        );
    }
  };

  /** Retry queueing kept scanner pages whose first hand-off failed. */
  const queueKeptScanPages = async (source: string) => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    try {
      const recovered = await desktop.recoverRetainedScans(job.id);
      const staged = await desktop.listStagedCaptures(job.id);
      if (!mounted.current) return;
      setCaptureQueue((current) => {
        const known = new Set(current.map((item) => item.id));
        return [...current, ...staged.filter((item) => !known.has(item.id))];
      });
      if (recovered.failed)
        setScanProblem({
          source,
          sourceKey: '',
          keptPagesNotQueued: true,
          problem: {
            title: 'The kept pages still could not be added to the queue.',
            steps: ['They are safe on this computer and will be added the next time Paige starts.'],
            detail: recovered.failed,
          },
        });
    } catch (reason) {
      if (mounted.current)
        setError(
          reason instanceof Error
            ? reason.message
            : 'The kept pages could not be added to the queue',
          'queue-change-failed',
        );
    }
  };

  /**
   * Stop a scan that is on the wire.
   *
   * There was no way to do this. A scan started against a TWAIN source ran until
   * the scanner answered or eleven minutes elapsed, with the button disabled and
   * nothing on screen offering a way out — so an empty feeder meant killing the
   * application. The main process kills the scanner host, which is what actually
   * releases the device, and answers the call in flight as a cancellation; that
   * releases `acquiring` through the ordinary path rather than by this function
   * reaching in and clearing it, so the two can never disagree about whether a
   * scan is still running.
   */
  const stopAcquiring = async () => {
    // Scoped to the scan this screen started: a Stop that arrives after that
    // scan has ended must not stop the next one.
    const operationId = scanOperationRef.current;
    const stopped = await window.capturePointDesktop
      ?.cancelScan(operationId ?? undefined)
      .catch(() => false);
    // The stopped scan still settles in the background: its kept pages are
    // counted after the scanner program has closed, and its result is still
    // applied. Only its hold on `acquiring` is retired here, so its late
    // `finally` cannot clear a different acquisition started meanwhile.
    acquisitionGeneration.current += 1;
    // Only while that scan is still the current one: a killed host can settle the scan (its
    // `finally` clears settling) while Stop's call is still in flight, and settling set after
    // that was never cleared, refusing every later scan until the job was reopened.
    if (stopped && operationId && scanOperationRef.current === operationId) setScanSettling(true);
    // THE SCREEN COMES BACK EITHER WAY, AND THAT IS THE WHOLE POINT.
    //
    // The first version of this only asked the main process to kill the scanner
    // host and then waited for the call in flight to answer. Observed on a real
    // R40: the host had already exited while the renderer still believed a scan
    // was running, so there was nothing to kill, the call never answered, and
    // Stop did nothing at all — the operator was as stuck as before, now with a
    // button that looked like it should help.
    //
    // A scan the operator has asked to stop is over as far as the screen is
    // concerned. If the host does answer later its capture still lands on the
    // queue, which is the same place it would have gone anyway.
    setAcquiring(false);
    setAcquisitionNotice(
      stopped
        ? 'Finishing the stopped scan…'
        : 'Scanning stopped - the scanner had already finished or was not responding',
    );
  };

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const acquireDocuments = async (requestedSource = sourceKey) => {
    const desktop = window.capturePointDesktop;
    if (!desktop || acquiring) return;
    // Which source the person used (telemetry catalog source.chosen; in their own job, the first is a funnel step).
    // A scanner is sent as "scanner", never by its name.
    reportEvent(
      requestedSource === 'files'
        ? 'source.chosen:files'
        : requestedSource === 'folder'
          ? 'source.chosen:folder'
          : requestedSource === 'simulator'
            ? 'source.chosen:simulator'
            : 'source.chosen:scanner',
      { jobId: job.id },
    );
    const scannerArch = (): 'x64' | 'x86' | 'unknown' => {
      const architecture =
        scanners?.sources[Number(requestedSource.split(':')[1] ?? 0)]?.hostArchitecture;
      return architecture === 'x64' || architecture === 'x86' ? architecture : 'unknown';
    };
    // The simulated scanner needs a model chosen and shows its own feed, so the
    // button opens its panel rather than acquiring; the panel hands back a
    // staged capture through onCaptured exactly as the other sources do.
    if (requestedSource === 'simulator') {
      setSimulatorOpen(true);
      return;
    }
    const physicalScan = requestedSource.startsWith('scanner:');
    // The main process refuses too; this only says why before trying.
    if (physicalScan && scanSettling) {
      setAcquisitionNotice('Finishing the stopped scan - try again in a moment');
      return;
    }
    const scannerUsed = physicalScan
      ? scanners?.sources[Number(requestedSource.split(':')[1])]?.name
      : undefined;
    if (scannerUsed) {
      rememberLastScanner(scannerUsed);
      setLastScanner(scannerUsed);
    }
    const generation = ++acquisitionGeneration.current;
    const operationId = physicalScan ? crypto.randomUUID() : null;
    if (operationId) {
      scanOperationRef.current = operationId;
      setScanOperationId(operationId);
    }
    setAcquiring(true);
    setError(null);
    setAcquisitionNotice(null);
    try {
      if (
        (requestedSource === 'files' || requestedSource === 'folder') &&
        job.status === 'provisional' &&
        job.documentTypes.length === 0 &&
        !openingSavedBatch &&
        !batchRef.current &&
        captureQueue.length === 0 &&
        captureInFlight.current.size === 0
      ) {
        // The shell owns this long call because committing representative mode
        // unmounts this capture surface before native analysis begins. Parent
        // ownership keeps both progress and an immediate rejection visible.
        await onChooseRepresentativeSource(job, requestedSource);
        return;
      }
      if (requestedSource === 'folder') {
        // A whole folder, staged as waves onto the same queue a scanner feeds.
        // The first wave is small so documents are on screen in about a minute;
        // the rest arrive behind it while the operator reviews.
        const selection = await desktop.chooseDocumentFolder(job.id);
        if (!selection.canceled && selection.token) {
          const staged = await desktop.stageFolderWaves({ jobId: job.id, token: selection.token });
          // Every wave carries its whole folder, so every count can be of the folder.
          const waves = stampFolderImport(staged.captures);
          setCaptureQueue((current) => {
            const known = new Set(current.map((item) => item.id));
            return [...current, ...waves.filter((item) => !known.has(item.id))];
          });
          // A folder the operator DID choose, that yields nothing, is a fact worth
          // stating. Cancelling is not — that is their own decision and needs no
          // reply — which is why this sits inside the chosen branch.
          if (staged.captures.length === 0) {
            reportEvent('source.outcome:empty-folder', { jobId: job.id, attrs: { files: '0' } });
            setAcquisitionNotice(FOLDER_NO_READABLE_PAGES);
          }
        }
        return;
      }
      const scannerName =
        scanners?.sources[Number(requestedSource.split(':')[1] ?? 0)]?.name ?? 'The scanner';
      const staged =
        requestedSource === 'files'
          ? await desktop.stageDocuments({ jobId: job.id })
          : await (async () => {
              const sourceIndex = Number(requestedSource.split(':')[1] ?? 0);
              const source = scanners?.sources[sourceIndex];
              if (!source) throw new Error('The selected scanner is no longer available.');
              return desktop.stageScan({
                jobId: job.id,
                source,
                operationId: operationId ?? undefined,
              });
            })();
      if (staged.capture)
        setCaptureQueue((current) =>
          current.some((item) => item.id === staged.capture!.id)
            ? current
            : [...current, staged.capture!],
        );
      const scan = staged.scan;
      // A scan's pages in (main reports the file pickers itself), and a scan that ended on a fault with pages kept.
      if (physicalScan && staged.capture)
        reportEvent('source.outcome:picked', {
          jobId: job.id,
          attrs: { files: telemetryCountBucket(staged.capture.fileCount) },
        });
      if (physicalScan && scan?.kind === 'failed')
        reportEvent(`scanner.fault:${scanFaultFor(scan.message)}`, {
          jobId: job.id,
          attrs: { arch: scannerArch() },
        });
      // How a physical scan ended, from the count main made on disk after the
      // scanner program stopped - never from the preview events.
      if (scan) {
        const kept = `${scan.keptPages.toLocaleString()} ${scan.keptPages === 1 ? 'page' : 'pages'}`;
        if (scan.kind === 'not-queued')
          // Kept is not the same as queued; say which one happened.
          setScanProblem({
            source: scannerName,
            sourceKey: requestedSource,
            keptPagesNotQueued: true,
            problem: {
              title: `${kept} kept, but they could not be added to the queue.`,
              steps: [
                'The pages are safe on this computer.',
                'Try again, or they will be added the next time Paige starts.',
              ],
              detail: scan.message ?? undefined,
            },
          });
        else if (scan.keptPages === 0 && scan.kind === 'stopped')
          setAcquisitionNotice('Scanning stopped. No pages were scanned.');
        else if (!scan.scannerReleased)
          setAcquisitionNotice(
            'The scanner program is still closing. New scans can start once it lets go.',
          );
        else
          setAcquisitionNotice((current) =>
            current === 'Finishing the stopped scan…' ? null : current,
          );
        // Stopped or failed with pages: the capture arrives held, and the
        // kept-pages dialog opens from the queue.
      }
      // NOTHING CAME BACK, AND SILENCE IS THE WRONG ANSWER FOR A SCANNER.
      // Measured 2026-08-08: with the source sitting on a TWAIN scanner that had
      // no paper, Add pages was clicked twice, six seconds apart, and produced no
      // spinner, no message, no line in the engine log, and no change to the
      // batch. That is indistinguishable from a product ignoring the click, and
      // the operator's next move is to click it again.
      //
      // Files are deliberately excluded: an empty result there means the operator
      // closed the picker, and telling someone their own cancellation failed is
      // worse than saying nothing.
      else if (staged.capture) {
        // Queued.
      } else if (staged.canceled) {
        // The operator's own decision. Saying anything here would be telling
        // somebody that the thing they just asked for did not happen.
      } else if (requestedSource !== 'files') {
        reportEvent('source.outcome:no-pages', { jobId: job.id, attrs: { files: '0' } });
        setAcquisitionNotice(SCANNER_NO_PAGES);
      }
    } catch (reason) {
      const written = messageFor(reason, '');
      const message = written.length > 0 ? written : null;
      // The scanner host reports an empty feeder as a FAILURE ("The scanner
      // returned no pages."), so that case arrives here rather than through
      // the empty-result branch above. It is paper, not the engine: it joins
      // the other fruitless acquisitions under the control that asked, and the
      // engine-status row keeps telling the truth about the engine.
      if (message && /returned no pages/i.test(message)) {
        reportEvent('source.outcome:no-pages', { jobId: job.id, attrs: { files: '0' } });
        setAcquisitionNotice(SCANNER_NO_PAGES);
      } else if (message && /still finishing/i.test(message))
        setAcquisitionNotice('Finishing the stopped scan - try again in a moment');
      else if (requestedSource.startsWith('scanner:')) {
        // Keep the scanning window up and say what went wrong there, instead of
        // flashing it closed and leaving a raw host message across the top.
        const name =
          scanners?.sources[Number(requestedSource.split(':')[1] ?? 0)]?.name ?? 'The scanner';
        // Which fault, as its code: the scanner's name and the host's words stay on this computer.
        reportEvent(`scanner.fault:${scanFaultFor(message)}`, {
          jobId: job.id,
          attrs: { arch: scannerArch() },
        });
        reportEvent('ui.error:scanner-fault', { jobId: job.id, attrs: { kind: 'alert' } });
        setScanProblem({
          source: name,
          sourceKey: requestedSource,
          problem: scanProblemFor(name, message),
        });
      } else
        setError(
          message ??
            (requestedSource === 'files' || requestedSource === 'folder'
              ? 'The pages could not be added'
              : 'Scanner acquisition failed'),
          'pages-add-failed',
        );
    } finally {
      if (operationId) {
        // This scan has settled: its scanner program has closed (or was given
        // up on and reported so), and Stop no longer has anything to reach.
        setScanSettling(false);
        if (scanOperationRef.current === operationId) {
          scanOperationRef.current = null;
          setScanOperationId(null);
        }
      }
      // A stopped scan's late finish must not end whatever started after it.
      if (generation === acquisitionGeneration.current) {
        setFolderRecoveryActive(false);
        setAcquiring(false);
      }
    }
  };

  const retryStagedCapture = async (captureId: string) => {
    // A person asking again is a fresh start. Whatever the engine was doing
    // before, they have looked at the screen and decided to try, and holding
    // their click behind the remains of an earlier outage would answer them
    // with nothing happening. This is also the only way out of a SPENT outage
    // that no wave has since disproved — which is the point: after the queue
    // has stopped, starting it again is a person's decision.
    clearEngineOutage();
    const api = window.capturePointDesktop;
    if (!api) return;
    try {
      // Persist first, or a refused retry looks active until restart resurrects
      // the failed manifest. The queue changes only after main accepted it.
      const attempts = await api.retryStagedCapture(captureId, true);
      if (mounted.current)
        setCaptureQueue((current) =>
          current.map((item) =>
            item.id === captureId ? { ...item, status: 'pending', error: null, attempts } : item,
          ),
        );
    } catch (reason) {
      if (mounted.current)
        setError(messageFor(reason, 'These pages could not be retried'), 'queue-change-failed');
    }
  };

  const discardStagedCapture = async (captureId: string) => {
    if (!window.capturePointDesktop || captureId === captureProcessingId) return;
    try {
      await window.capturePointDesktop.discardStagedCapture(captureId);
      discardFolderImportWave(folderImportRegistry.current, captureId);
      setCaptureQueue((current) => current.filter((item) => item.id !== captureId));
    } catch (reason) {
      setError(messageFor(reason, 'The queued pages could not be removed'), 'queue-change-failed');
    }
  };

  // A job with no type left reads nothing new (ADR-0096). Main may refuse before this view's job
  // shows it; that refusal holds the drain until the job's types change, rather than asking again.
  const documentTypesKey = job.documentTypes
    .map((type) => `${type.id}:${type.status ?? ''}`)
    .join('|');
  const noTypeLeftRefusedFor = useRef<string | null>(null);
  const readsNoNewPages = hasNoTypeLeft(job) || noTypeLeftRefusedFor.current === documentTypesKey;
  useEffect(() => {
    if (!window.capturePointDesktop) return;
    // Paused behind Job setup: staged pages wait safely on disk and drain the
    // moment setup closes, read with the setup that was saved.
    if (suspended) return;
    if (readsNoNewPages) return;
    // A schema refresh rewrites the whole batch from the snapshot it opened.
    // Starting an append beside it lets the refresh commit that older snapshot
    // after the append and erase pages that were already durably acquired.
    // Keep the two batch writers mutually exclusive; a queued capture is still
    // safely staged on disk and drains as soon as the refresh releases.
    if (
      wholeBatchMutationRef.current ||
      schemaRefreshBusyRef.current ||
      structuralMutationRef.current ||
      boundaryRefreshesInFlight.current > 0
    )
      return;
    // A split or join waiting only for the pages being read gets the gap
    // between waves: the waves already started finish, it is saved, and the
    // next wave starts. Without this, overlapping waves could keep it waiting
    // for the whole folder.
    if (boundaryQueue.headWaitsForCapture) return;
    const inFlight = captureInFlight.current;
    // Decide which durable scheduling regime owns the next item before deciding
    // whether it may overlap. Ordinary file/scanner captures and legacy
    // manifests stay conservative; only an explicitly accelerated folder wave
    // participates in the measured three-wave overlap.
    // A held capture (kept pages of an interrupted scan) is a barrier: nothing
    // behind it is read until the operator has checked it.
    const next = nextDrainableCapture(captureQueue, (item) => !inFlight.has(item.id));
    if (!next) return;
    // A processor-paced wave must not open its learning-generation snapshot
    // while ANY admitted operator answer is still on the way to durable
    // storage. Type confirmations can be queued behind schema ownership, line
    // edits can be admitted before their serialized native save begins, and a
    // whole-document confirmation can be draining either of those lanes. The
    // shared ref predicate sees every such pre-render window; saveLaneRevision
    // wakes this effect when the last owner releases. Accelerated GPU waves keep
    // their measured overlap because they do not use this teaching cadence.
    if (next.waveMode === 'processor-paced' && processorWaveSaveLaneBlocked()) return;
    // One wave runs alone until the batch exists; after that a new wave may
    // start READING whenever every wave already in flight has moved on to
    // extraction (at most three; the engine admits three heavy commands).
    // Waves of one folder import never overlap: its preparation already read
    // their pages, so overlap only split the reader and held back the earlier
    // wave's documents (mayStartCaptureWave says why, with the measurement).
    const durableBatchReady = Boolean(
      batchRef.current?.batchId && !batchRef.current.batchId.startsWith('live:'),
    );
    const canStart = mayStartCaptureWave(
      next,
      [...inFlight].map((id) => captureQueue.find((item) => item.id === id) ?? { id }),
      { durableBatchReady, reachedExtraction: (id) => captureReachedExtraction.current.has(id) },
    );
    if (!canStart) return;
    // NEVER process before the job's saved batch is in hand. The drain once
    // raced the batch load after a restart, saw no current batch, and told the
    // engine to start a fresh one — forking a 584-document batch into an
    // invisible sibling. Waiting here is what keeps a job ONE batch.
    if (openingSavedBatch) return;
    // The main process projects a recovered "processing" manifest as pending,
    // allowing this renderer to rejoin its promise or resume by acquisition ID.
    if (Date.now() < engineQueueHeldUntil()) {
      const timer = window.setTimeout(
        () => setCaptureQueue((current) => [...current]),
        engineQueueHeldUntil() - Date.now() + 500,
      );
      return () => window.clearTimeout(timer);
    }
    const admittedSaveLaneGeneration = saveLaneGeneration.current;
    const admittedCaptureDrainGeneration = captureDrainGeneration(job.id);
    inFlight.add(next.id);
    setCaptureProcessingId(next.id);
    // A new attempt of this wave (Retry, a resume after an engine restart) reports from Prepare again.
    waveStages.current = forgetWave(waveStages.current, next.id);
    setEngineUnderstanding(wavesUnderstanding(waveStages.current));
    // captureWork and the wave lanes deliberately survive this start: waves
    // overlap, so a fresh wave beginning must not blank what an earlier wave
    // is still doing. Work is bounded where it is written (slice(-120)), and
    // each lane retires with its own wave.
    setCaptureActivity([]);
    setProcessing(true);
    clearErrorOf('pages-not-read', 'job-save-failed');
    // A `live:` id is this screen's own placeholder for a batch that has not
    // been stored yet, and the engine has never heard of it. On a job whose
    // first wave is still running it is what `batchRef` holds, so the wave
    // released by the overlap rule above was asking to append to a batch that
    // does not exist. Sending nothing instead asks the question the engine can
    // actually answer, at the only moment the answer is true: it resolves the
    // job's batch under its own guard when the wave commits, and creates one
    // only if no other wave got there first.
    const openBatchId = batchRef.current?.batchId;
    const currentBatchId = openBatchId?.startsWith('live:') ? undefined : openBatchId;
    const appendOpeningBatch =
      currentBatchId && batchRef.current?.batchId === currentBatchId ? batchRef.current : null;
    void (async () => {
      if (next.waveMode === 'processor-paced') {
        try {
          // The answer and its learning generation commit together. Await only
          // that exact generation before this CPU wave snapshots its maps; a
          // timeout/refusal deliberately falls through to the careful reader.
          await window.capturePointDesktop!.waitForLayoutCatchUp(job.id);
        } catch {
          // Learning is an accelerator, never an admission requirement.
        }
        // A gesture can enter while layout catch-up is awaiting the generation
        // requested above. Do not snapshot schema routes beside that newer
        // writer. Retire this not-yet-dispatched admission; the lane's revision
        // signal immediately retries the still-pending durable wave after the
        // operator's answer settles, including a refused/failed save.
        if (
          processorWaveSaveLaneBlocked() ||
          saveLaneGeneration.current !== admittedSaveLaneGeneration ||
          captureDrainGeneration(job.id) !== admittedCaptureDrainGeneration
        )
          return null;
      }
      return window.capturePointDesktop!.processStagedCapture({
        captureId: next.id,
        jobId: job.id,
        documentTypes: schemaRequest(job),
        separationMode: job.separationMode,
        separationBarcodeText: job.separationBarcodeText,
        boundaryOpeningProfiles: job.boundaryOpeningProfiles,
        boundaryContinuationProfiles: job.boundaryContinuationProfiles,
        currentBatchId,
      });
    })()
      .then(async (result) => {
        if (!result) return;
        // An engine that finished a wave is an engine that is running, whatever it
        // was doing five minutes ago — so the outage goes, and the hold with it.
        // Cleared before the mounted check because the fact is about the machine,
        // not about this view.
        clearEngineOutage();
        if (!mounted.current) return;
        // Overlapped waves commit in engine order, but their promises resolve
        // in any order. A result carrying fewer documents than the workspace
        // already shows is the earlier sibling arriving late - its documents
        // are already inside the later result. Never let it shrink the batch.
        // The wave is committed whatever happens next: a follow-up job save that fails is said
        // as that, never as a failed wave offering Retry on a staged file already removed.
        try {
          if (result.documents.length >= (batchRef.current?.documents.length ?? 0))
            await applyProcessedResult(result, appendOpeningBatch);
        } catch (reason) {
          if (mounted.current)
            setError(
              reason instanceof Error
                ? `The pages were read, but the job could not be saved: ${reason.message}`
                : 'The pages were read, but the job could not be saved',
              'job-save-failed',
            );
        }
        setCaptureQueue((current) => current.filter((item) => item.id !== next.id));
        // Bars keep tracking the job as it reads - not only when someone
        // happens to open setup. The engine ignores bars an operator set and
        // saves nothing when nothing would change.
        void window.capturePointDesktop?.autoTuneReviewBars(job.id).catch(() => undefined);
      })
      .catch(async (reason: unknown) => {
        const message = messageFor(reason, 'Recognition failed');
        if (isNoTypeLeftRefusal(message)) noTypeLeftRefusedFor.current = documentTypesKey;
        if (isFolderPreparationPaused(message) || isNoTypeLeftRefusal(message)) {
          if (mounted.current)
            setCaptureQueue((current) =>
              current.map((item) =>
                item.id === next.id ? { ...item, status: 'pending', error: null } : item,
              ),
            );
          return;
        }
        // An engine outage is weather, not a verdict on this wave. Put the wave
        // back as pending, wait out the restart, resume automatically — for as
        // long as one restart can plausibly take, and no longer. Uncounted and
        // unbounded, this branch resumed the same wave five times in one process
        // lifetime on the reporting machine and was still going when the operator
        // quit the application; `engine-outage.ts` carries the timestamps.
        //
        // Judged and recorded BEFORE the mounted check, like the success path
        // above: what the engine just did is true whether or not this view is
        // still on screen, and an outage a remount can forget is an outage that
        // starts its five minutes over every time somebody looks at the job list.
        const verdict = judgeCaptureFailure(message, engineOutageNow(), Date.now());
        recordEngineOutage(verdict.outageNow);
        // The outage outlasted a restart, so the whole queue holds rather than
        // marching the waves behind this one into the same dead engine one at a
        // time — the wall of identical retry cards b844df7 exists to prevent. The
        // hold is re-armed for every wave the spent outage condemns, so the queue
        // paces itself; what does NOT restart is the patience, which is spent
        // until the engine answers or a person presses Retry.
        if (verdict.outage)
          holdEngineQueueUntil(
            Date.now() + (verdict.resume ? ENGINE_RESTART_COOLDOWN_MS : ENGINE_OUTAGE_PATIENCE_MS),
          );
        if (!mounted.current) return;
        if (verdict.resume) {
          // On disk too, or the manifest still says failed and the next launch
          // resurrects a "Needs retry" chip for weather that already passed. The
          // count it answers with is the main process's own — recognition starts
          // there, so a hand-clicked retry and this automatic one are one event,
          // and this view never invents a number of its own.
          try {
            const attempts = await window.capturePointDesktop!.retryStagedCapture(next.id);
            if (mounted.current)
              setCaptureQueue((current) =>
                current.map((item) =>
                  item.id === next.id
                    ? { ...item, status: 'pending', error: null, attempts }
                    : item,
                ),
              );
            if (verdict.opensOutage) {
              reportEvent('engine.outage-shown', { jobId: job.id });
              onNotice(
                'Recognition paused while the engine restarts. The queue resumes by itself.',
                'engine-restarting',
              );
            }
          } catch (retryReason) {
            const retryMessage = messageFor(retryReason, 'These pages could not be retried');
            if (mounted.current) {
              setCaptureQueue((current) =>
                current.map((item) =>
                  item.id === next.id ? { ...item, status: 'failed', error: retryMessage } : item,
                ),
              );
              setError(retryMessage, 'pages-not-read');
            }
          }
          return;
        }
        // This wave rests at failed so the screen can finally say so. Said out
        // loud once, at the moment patience runs out, because a queue that has
        // quietly stopped looks exactly like a queue that is working — and said
        // only once, because every wave condemned after it is the same news. The
        // pages are still pending and still on disk; Retry is what starts them
        // again.
        // "the pages behind this one" is only true when there ARE pages behind it;
        // on a queue of one it named something that does not exist.
        if (verdict.spendsPatience) reportEvent('engine.patience-spent', { jobId: job.id });
        if (verdict.spendsPatience)
          onNotice(
            captureQueue.some((item) => item.id !== next.id && item.status === 'pending')
              ? 'The engine has not come back - the queue is holding rather than failing the pages behind this one'
              : 'The engine has not come back - these pages are still here, and Retry reads them again',
            'engine-not-responding',
          );
        setCaptureQueue((current) =>
          current.map((item) =>
            item.id === next.id ? { ...item, status: 'failed', error: message } : item,
          ),
        );
        setError(message, 'pages-not-read');
      })
      .finally(() => {
        captureInFlight.current.delete(next.id);
        captureReachedExtraction.current.delete(next.id);
        pendingCaptureWaves.current.delete(next.id);
        if (!mounted.current) return;
        // This wave's lane retires alone; the others keep narrating themselves.
        setIncomingPages(
          (current) => new Map([...current].filter(([, page]) => page.captureId !== next.id)),
        );
        setCaptureWaves((current) => {
          if (!current.has(next.id)) return current;
          const nextWaves = new Map(current);
          nextWaves.delete(next.id);
          return nextWaves;
        });
        const remaining = [...captureInFlight.current];
        setCaptureProcessingId((current) => (current === next.id ? (remaining[0] ?? '') : current));
        if (remaining.length === 0) setProcessing(false);
        // Poke the drain: a slot just opened.
        setCaptureQueue((current) => [...current]);
      });
  }, [
    captureQueue,
    processing,
    openingSavedBatch,
    schemaRefreshBusy,
    confirmingDocumentIds,
    saveLaneRevision,
    job.id,
    job.schemaVersion,
    job.separationMode,
    job.separationBarcodeText,
    suspended,
    readsNoNewPages,
  ]);

  useEffect(() => {
    if (!window.capturePointDesktop) return;
    return window.capturePointDesktop.onSpeedCheckStateChanged((next) => {
      const running = next.kind === 'running';
      setSpeedCheckRunning(running);
      // A refresh held back during the check was recorded as attempted, which
      // would otherwise keep it held back for the life of the window.
      if (!running) schemaRefreshAttempted.current = '';
    });
  }, []);

  const reprocessCurrentBatch = async (updatedJob: Job, wholeBatchToken: symbol) => {
    if (!window.capturePointDesktop || !batch) return null;
    if (wholeBatchMutationRef.current?.token !== wholeBatchToken)
      throw new Error(
        'Another whole-batch update owns this job. Wait for it to finish, then try again.',
      );
    const request = {
      jobId: updatedJob.id,
      batchId: batch.batchId,
      documentTypes: schemaRequest(updatedJob),
      separationMode: updatedJob.separationMode,
      separationBarcodeText: updatedJob.separationBarcodeText,
      boundaryOpeningProfiles: updatedJob.boundaryOpeningProfiles,
      boundaryContinuationProfiles: updatedJob.boundaryContinuationProfiles,
    };
    const impact = await window.capturePointDesktop.getBatchReprocessImpact({
      jobId: updatedJob.id,
      batchId: batch.batchId,
    });
    return impact.hasProtectedWork
      ? window.capturePointDesktop.reprocessReviewed(request)
      : window.capturePointDesktop.retryDocuments(request);
  };
  useEffect(() => {
    // Not while paused behind Job setup: its edits are an unsaved draft.
    if (suspended) return;
    if (
      !batch ||
      !window.capturePointDesktop ||
      !stagedCapturesLoaded ||
      captureDrainBlocked(job.id) ||
      processing ||
      wholeBatchMutationRef.current ||
      schemaRefreshBusyRef.current ||
      structuralMutationRef.current ||
      boundaryRefreshesInFlight.current > 0 ||
      fieldSavingRef.current ||
      typeSavingDocumentIdRef.current ||
      typeRefreshOwners.current.size > 0 ||
      lineMutationAdmissions.current > 0 ||
      pendingLineSaves.current > 0 ||
      confirmingDocumentsRef.current.size > 0 ||
      pageEditBusyRef.current ||
      batchUsesCurrentSchema(job, batch)
    )
      return;
    // A whole-batch schema refresh ships every document over IPC and back into
    // state - at three and a half thousand documents that is seconds of frozen
    // interface and an engine store lock the import waves starve behind. The
    // learners can bump the schema version several times a minute while
    // confirmations pour in, and each bump was buying another full pass. One
    // refresh a minute is plenty: later bumps coalesce into the next pass, and
    // nothing runs while a wave is being recognized.
    // Pending is as important as in-flight. Between two waves `processing`
    // briefly becomes false; starting a refresh in that gap used a two-document
    // snapshot, then overwrote the third wave after its acquisition committed.
    if (captureProcessingId || captureQueue.some((item) => item.status === 'pending')) return;
    if (Date.now() - lastSchemaRefreshAt.current < 60_000) return;
    // Not while a document is being retyped.
    //
    // Changing a type paints the new one immediately and re-reads underneath.
    // The saved pending state has no fields until its owner finishes reading.
    // Without the owner-map guard above, that intentional gap looks like stale
    // schema and starts a competing whole-batch refresh. The operator save lane
    // is already free; only this competing background operation must wait.
    // The operator watched their document be re-read twice, the first time
    // against the type they had just changed away from.
    // Deferred, not skipped: clearing the attempt key below lets it run the
    // moment the check ends, so a job opened mid-check still catches up.
    if (speedCheckRunning) return;
    const refreshKey = `${batch.batchId}:${job.schemaVersion}:${job.documentTypes.map((type) => `${type.id}:${type.schemaVersion}`).join('|')}`;
    if (schemaRefreshAttempted.current === refreshKey) return;
    const wholeBatchToken = claimWholeBatchMutation('applying the current job fields');
    if (!wholeBatchToken) return;
    // A deferral is not a refresh. Start the cooldown only after every reason to
    // wait has cleared and this pass actually owns the batch; otherwise ending a
    // speed check can leave an outdated schema idle for another full minute.
    lastSchemaRefreshAt.current = Date.now();
    schemaRefreshAttempted.current = refreshKey;
    let settleSchemaRefresh!: (succeeded: boolean) => void;
    schemaRefreshCompletionRef.current = new Promise<boolean>((resolve) => {
      settleSchemaRefresh = resolve;
    });
    let schemaRefreshSucceeded = false;
    schemaRefreshBusyRef.current = true;
    setSchemaRefreshBusy(true);
    setProcessing(true);
    clearErrorOf('job-fields-apply-failed');
    void window.capturePointDesktop
      .refreshBatchSchema({
        jobId: job.id,
        batchId: batch.batchId,
        documentTypes: schemaRequest(job),
      })
      .then((result) => {
        if (!mounted.current) return;
        // A schema refresh rewrites the documents, so any hydrated copy is
        // stale by definition: everything drops to skeletons and the open
        // document re-hydrates on selection. The engine already sent skeletons
        // past the projection threshold; this makes the local bookkeeping agree.
        const refreshedPaged =
          batch.workspaceProjection === 'paged' || result.workspaceProjection === 'paged';
        // Everything but the open document drops to a skeleton — see
        // schema-refresh-projection.ts for why that one has to stay, and what
        // the operator saw for as long as it did not.
        const projected = projectRefreshedBatch(
          result.documents,
          selectedDocumentRef.current,
          toProjectionSkeleton,
          // An empty source path is the skeleton's own signature: the engine sends
          // them past the projection threshold, and one arriving for the OPEN
          // document is what left a Credit Memo showing its type and not one value.
          (document) => Boolean(document.sourcePath),
        );
        const restored = refreshedPaged
          ? {
              ...result,
              documents: [...projected.documents],
              workspaceProjection: 'paged' as const,
              loadedDocumentIds: [...projected.loadedDocumentIds],
            }
          : result;
        if (refreshedPaged) {
          const kept = new Set(projected.loadedDocumentIds);
          loadedDocumentOrder.current = [...kept];
          loadedDocumentIdsRef.current = kept;
          setLoadedDocumentIds(kept);
        }
        if (restored.workspaceProjection === 'paged') {
          projectionSkeletons.current = new Map(
            restored.documents.map((document) => [document.id, toProjectionSkeleton(document)]),
          );
        }
        clearBoundaryUndoHistory();
        batchRef.current = restored;
        setBatch(restored);
        onRestored(restored);
        schemaRefreshSucceeded = true;
        const openIndex = Math.min(
          selectedDocumentRef.current,
          Math.max(0, result.documents.length - 1),
        );
        setSelectedDocument(openIndex);
        // The rule refuses to call the open document loaded when what arrived for
        // it was a skeleton. That refusal is only worth anything if somebody acts
        // on it: nothing else will, because no further selection fires for a
        // document that is already selected.
        if (
          refreshedPaged &&
          !projected.loadedDocumentIds.includes(restored.documents[openIndex]?.id ?? '')
        )
          setTimeout(() => hydrateDocumentRef.current(openIndex), 0);
        onNotice('Job fields updated · corrections kept');
      })
      .catch((reason: unknown) => {
        if (!mounted.current) return;
        setError(
          messageAfter('The current job fields could not be applied', reason),
          'job-fields-apply-failed',
        );
      })
      .finally(() => {
        schemaRefreshBusyRef.current = false;
        releaseWholeBatchMutation(wholeBatchToken);
        settleSchemaRefresh(schemaRefreshSucceeded);
        if (!mounted.current) return;
        setSchemaRefreshBusy(false);
        setProcessing(false);
      });
  }, [
    batch,
    job,
    processing,
    schemaRefreshBusy,
    stagedCapturesLoaded,
    captureProcessingId,
    captureQueue,
    retypingDocumentId,
    speedCheckRunning,
    captureAdmissionBlocked,
    onNotice,
    onRestored,
    suspended,
  ]);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const retryExtraction = async (targetDocumentId?: string, held?: symbol) => {
    if (!window.capturePointDesktop || !batch) return;
    const wholeBatchToken = held ?? claimWholeBatchMutation('re-reading the current documents');
    if (!wholeBatchToken) {
      // Waits for the batch, then reads with the newest window state; a batch closed meanwhile drops it.
      const asked = batch.batchId;
      await wholeBatchQueue.current.run(
        `reread:${targetDocumentId ?? ''}`,
        wholeBatchTurn(
          're-reading the current documents',
          () => batchRef.current?.batchId === asked,
        ),
        () => onNotice(wholeBatchWaitMessage('re-reading these documents'), 'reread-busy'),
        (token) => wholeBatchGestures.current.retryExtraction(targetDocumentId, token),
      );
      return;
    }
    setProcessing(true);
    setError(null);
    try {
      const target = targetDocumentId
        ? batchRef.current?.documents.find((item) => item.id === targetDocumentId)
        : undefined;
      // A document-level Retry must not rescan and regroup the entire job.
      // Reuse native targeted reclassification and its reviewed-state CAS: it
      // reads the document's unread pages again from their own images first
      // (UnreadPageReread), so a page nothing read no longer sends the whole job
      // down the source-reading path (207 pages drawn and recognised again, then
      // the ten-minute limit, for one unread page). Only a job with no document
      // types yet still has to read the batch.
      const targeted = target && job.documentTypes.length > 0;
      const result = targeted
        ? await window.capturePointDesktop.reclassifyBoundaryDocuments({
            jobId: job.id,
            batchId: batch.batchId,
            documentIds: [target.id],
            documentTypes: schemaRequest(job),
          })
        : await reprocessCurrentBatch(job, wholeBatchToken);
      // A re-read that answers nothing is not a re-read. This returned in silence, so when the
      // browser adapter had no method behind `reclassifyBoundaryDocuments` and stood in for it,
      // `Read it now` was a button attached to nothing: no spinner, no error, no console line, and
      // the document's fields stayed empty. Say so rather than leave the person clicking.
      if (!result) {
        setError(
          'Paige could not read this document again just now. Try again in a moment.',
          'reread-failed',
        );
        return;
      }

      adoptReprocessedBatch(result);
      const reread = targetDocumentId
        ? result.documents.find((item) => item.id === targetDocumentId)
        : undefined;
      if (targetDocumentId) {
        const index = result.documents.findIndex((item) => item.id === targetDocumentId);
        if (index >= 0) setSelectedDocument(index);
      }
      // A page the engine still could not read stays a hole (or waits for pages)
      // and the document is read from the pages it has: said, not "read again".
      const stillUnread = targeted
        ? (reread?.pages.filter((page) => page.notReadReason) ?? [])
        : [];
      if (reread && stillUnread.length > 0) {
        const which =
          stillUnread.length === 1
            ? `Page ${stillUnread[0].number} still could not be read`
            : `${stillUnread.length} pages still could not be read`;
        setError(
          stillUnread.length < reread.pages.length
            ? `${which}. The rest of the document was read again.`
            : `${which}.`,
          'reread-failed',
        );
        return;
      }
      onNotice(
        targeted
          ? 'Document read again · your confirmed answers were kept'
          : 'Reading finished · your confirmed answers were kept',
      );
    } catch (reason) {
      setError(messageFor(reason, 'Field extraction retry failed'), 'reread-failed');
    } finally {
      releaseWholeBatchMutation(wholeBatchToken);
      setProcessing(false);
    }
  };
  /**
   * Throwing one of the two switches, and the receipt that says so.
   *
   * It rides the ordinary job save — the stance is job state, last write wins,
   * two windows re-read, no new concurrency surface. Nothing about review moves:
   * a document the reader is unsure of is in the queue before this and after it.
   */
  const changeStance = async (area: StanceArea, stoppedAt: string | null) => {
    if (stanceBusy) return;
    setStanceBusy(true);
    setError(null);
    const before = job.learningStance ?? null;
    try {
      await onCommitJob({ ...job, learningStance: withStanceChange(before, area, stoppedAt) });
      setStanceBefore(before);
      setReceiptOffer(null);
      setLastJobImprovement(null);
      setStanceReceipt({ area, action: stoppedAt ? 'stopped' : 'resumed' });
      promoteReceipt('stance');
    } catch (reason) {
      setError(messageFor(reason, 'That change could not be saved'), 'suggestion-settings-failed');
    } finally {
      setStanceBusy(false);
    }
  };
  const undoStance = async () => {
    if (!stanceReceipt || stanceBusy) return;
    setStanceBusy(true);
    try {
      await onCommitJob({ ...job, learningStance: stanceBefore });
      setStanceReceipt(null);
    } catch (reason) {
      setError(messageFor(reason, 'That change could not be undone'), 'suggestion-settings-failed');
    } finally {
      setStanceBusy(false);
    }
  };
  /** Dismissing the offer IS the decline, and a decline holds it off for thirty days. */
  const declineStanceOffer = () => {
    const declining = receiptOffer;
    setReceiptOffer(null);
    if (!declining) return;
    void onCommitJob({
      ...job,
      learningStance: { ...(job.learningStance ?? {}), offerDeclinedAt: new Date().toISOString() },
    }).catch(() => undefined);
  };
  /**
   * Whether this apply's receipt carries the offer.
   *
   * The honest generalisation of "yes, add that type" is not "and stop asking
   * me" — so the offer waits for a job that has accepted before. The count comes
   * from the job's own decision history rather than this session, because the
   * question is about the job, not the visit.
   */
  const raiseStanceOffer = async (suggestion: JobSuggestion) => {
    if (offerRaisedThisSession.current || !window.capturePointDesktop) return;
    if (suggestion.kind !== SUGGESTION.documentType) return;
    const area = STANCE_AREA.newTypes;
    try {
      const history = await window.capturePointDesktop.listJobSuggestionHistory(job.id);
      const acceptedCount = history.filter((entry) => entry.decision === 'apply').length;
      if (!stanceOfferAllowed({ stance: job.learningStance, area, acceptedCount, now: new Date() }))
        return;
      offerRaisedThisSession.current = true;
      setReceiptOffer(area);
    } catch {
      // An offer is a nicety. It never becomes an error an operator has to read.
    }
  };
  /** True once the engine has applied the suggestion (a failed re-read after that still counts); false when it didn't run or failed. */
  const applyJobSuggestion = async (
    suggestion: JobSuggestion,
    scope: SuggestionApplicationScope = 'refresh-safe',
    proposedName?: string,
    excludedDocumentIds?: string[],
    keptFieldNames?: string[],
    held?: symbol,
  ): Promise<boolean> => {
    if (!window.capturePointDesktop) return false;
    // Adding a field changes the job definition only. It is safe to accept while
    // an import wave is reading from its already-frozen schema snapshot. The
    // active wave finishes with that snapshot; following waves see the new field,
    // and the ordinary schema convergence pass catches older documents up after
    // the durable queue drains. Blocking the click until all 100 files finished
    // made Apply look dead during the exact early-teaching flow that creates this
    // suggestion.
    const captureWorkPending =
      captureInFlight.current.size > 0 || captureQueue.some((item) => item.status === 'pending');
    const applyFieldWhileCapturing = suggestion.kind === SUGGESTION.newField && captureWorkPending;
    const wholeBatchToken =
      held ??
      claimWholeBatchMutation('applying the job improvement', undefined, applyFieldWhileCapturing);
    if (!wholeBatchToken) {
      // Waits for the batch, then applies with the newest window state.
      return (
        (await wholeBatchQueue.current.run(
          `apply:${suggestion.id}`,
          wholeBatchTurn('applying the job improvement', undefined, applyFieldWhileCapturing),
          () =>
            onNotice(
              wholeBatchWaitMessage('applying this improvement', applyFieldWhileCapturing),
              'improvement-busy',
            ),
          (token) =>
            wholeBatchGestures.current.applyJobSuggestion(
              suggestion,
              scope,
              proposedName,
              excludedDocumentIds,
              keptFieldNames,
              token,
            ),
        )) ?? false
      );
    }
    setJobSuggestionBusy(suggestion.id);
    setError(null);
    let engineApplied = false;
    try {
      const impact =
        !applyFieldWhileCapturing && batch
          ? await window.capturePointDesktop.getBatchReprocessImpact({
              jobId: job.id,
              batchId: batch.batchId,
            })
          : null;
      const protectsReviewedWork = impact?.hasProtectedWork ?? false;
      const refreshSafe =
        !applyFieldWhileCapturing &&
        scope === 'refresh-safe' &&
        Boolean(batch) &&
        (!protectsReviewedWork ||
          suggestion.kind === SUGGESTION.barcodeField ||
          suggestion.kind === SUGGESTION.fieldFormat ||
          suggestion.kind === SUGGESTION.newField);
      const applied = await window.capturePointDesktop.applyJobSuggestion({
        jobId: job.id,
        suggestionId: suggestion.id,
        proposedName: proposedName?.trim() || undefined,
        excludedDocumentIds: excludedDocumentIds?.length ? excludedDocumentIds : undefined,
        keptFieldNames: keptFieldNames?.length ? keptFieldNames : undefined,
      });
      engineApplied = true;
      const updatedJob = migrateJob(applied.job as Job);
      // Redo applies the same choice again, with the type this apply added, if any:
      // new by id and carrying the applied name.
      const addedTypeId = addedSuggestedTypeId(
        job.documentTypes,
        updatedJob.documentTypes,
        applied.suggestion.proposedLabel,
      );
      lastApplyChoice.current = {
        suggestionId: applied.suggestion.id,
        proposedName,
        excludedDocumentIds,
        keptFieldNames,
        addedTypeId,
      };
      // A new type added with fields: the engine filed its documents, held
      // them for those fields and woke the automatic re-read (D10). Re-reading the
      // batch here would pick their types again among all types and race that
      // re-read, so the open batch is reloaded from the store instead. The engine holds
      // only when it filed some (its filed count) and the added type has fields; kept
      // names can come back empty (a renamed card's stored design), and then nothing
      // is queued.
      const newTypeHeld =
        suggestion.kind === SUGGESTION.documentType &&
        (updatedJob.documentTypes.find((type) => type.id === addedTypeId)?.fields.length ?? 0) >
          0 &&
        Boolean(addedTypeId) &&
        applied.clearedFromReview > 0;
      const refreshCurrentBatch = refreshSafe && !newTypeHeld;
      onJobChange(updatedJob);
      setJobSuggestions((current) => current.filter((item) => item.id !== suggestion.id));
      setLastJobImprovement(applied.suggestion);
      setStanceReceipt(null);
      promoteReceipt('improvement');
      setJobImprovementUndone(false);
      setLastJobImprovementScope(scope);
      setLastJobImprovementOutcome(
        applyFieldWhileCapturing || newTypeHeld
          ? 'queued'
          : scope === 'future-only' || !batch
            ? 'future-only'
            : refreshCurrentBatch
              ? 'refreshed'
              : 'kept',
      );
      void raiseStanceOffer(suggestion);
      if (newTypeHeld) {
        // As the Learning apply does: the open batch only, once the work is durable. The
        // whole batch is replaced, not merged, because the engine moved the held
        // documents' answers onto the new type's fields and marked them Not read, and
        // this lane is held, so no edit of the person's is in flight to keep.
        const opening = batchRef.current;
        if (opening) {
          // The latest batch is the open one: a job appends every capture to its one
          // batch (DocumentStore.SaveOrAppendWave) and the workspace follows the latest
          // batch (the overview poll above), so they differ only for a job's `live:`
          // placeholder, which has nothing filed to reload.
          const refreshed = await window.capturePointDesktop
            .getLatestBatch(job.id)
            .catch(() => null);
          if (
            refreshed &&
            batchRef.current?.batchId === opening.batchId &&
            refreshed.batchId === opening.batchId
          )
            adoptReprocessedBatch(refreshed);
        }
      } else if (batch && refreshCurrentBatch) {
        setProcessing(true);
        try {
          const request = {
            jobId: updatedJob.id,
            batchId: batch.batchId,
            documentTypes: schemaRequest(updatedJob),
            separationMode: updatedJob.separationMode,
            separationBarcodeText: updatedJob.separationBarcodeText,
            boundaryOpeningProfiles: updatedJob.boundaryOpeningProfiles,
            boundaryContinuationProfiles: updatedJob.boundaryContinuationProfiles,
          };
          const result =
            suggestion.kind === SUGGESTION.fieldFormat
              ? await window.capturePointDesktop.refreshBatchSchema({
                  ...request,
                  formatChecksOnly: true,
                })
              : protectsReviewedWork
                ? await window.capturePointDesktop.reprocessReviewed(request)
                : await window.capturePointDesktop.retryDocuments(request);
          adoptReprocessedBatch(result);
          setSelectedDocument(0);
          setSelectedPage(0);
          onNotice(
            protectsReviewedWork
              ? 'Job improved · untouched values refreshed; corrections kept'
              : 'Job improved · current documents re-evaluated',
          );
        } catch (reason) {
          setLastJobImprovementOutcome('kept');
          setError(
            messageAfter('Job improved, but this batch could not be re-evaluated', reason),
            'improvement-failed',
          );
        } finally {
          setProcessing(false);
        }
      } else {
        onNotice(
          applyFieldWhileCapturing
            ? 'Field added · incoming files use it now; earlier documents update after the import'
            : scope === 'future-only'
              ? 'Job improved · new pages only; current batch kept'
              : batch
                ? 'Job improved · reviewed work kept; new pages will use it'
                : 'Job improved',
        );
      }
    } catch (reason) {
      setError(
        messageFor(reason, 'The job improvement could not be applied'),
        'improvement-failed',
      );
    } finally {
      releaseWholeBatchMutation(wholeBatchToken);
      setJobSuggestionBusy('');
    }
    return engineApplied;
  };
  const undoJobSuggestion = async (
    suggestion: Pick<JobSuggestion, 'id' | 'kind'>,
    scope: SuggestionApplicationScope = 'refresh-safe',
    held?: symbol,
  ) => {
    if (!window.capturePointDesktop) return;
    const wholeBatchToken = held ?? claimWholeBatchMutation('undoing the job improvement');
    if (!wholeBatchToken) {
      await wholeBatchQueue.current.run(
        `undo:${suggestion.id}`,
        wholeBatchTurn('undoing the job improvement'),
        () => onNotice(wholeBatchWaitMessage('undoing this improvement'), 'improvement-busy'),
        (token) => wholeBatchGestures.current.undoJobSuggestion(suggestion, scope, token),
      );
      return;
    }
    setJobSuggestionBusy(suggestion.id);
    setError(null);
    try {
      const impact = batch
        ? await window.capturePointDesktop.getBatchReprocessImpact({
            jobId: job.id,
            batchId: batch.batchId,
          })
        : null;
      const protectsReviewedWork = impact?.hasProtectedWork ?? false;
      const refreshCurrentBatch =
        scope === 'refresh-safe' &&
        Boolean(batch) &&
        (!protectsReviewedWork ||
          suggestion.kind === SUGGESTION.barcodeField ||
          suggestion.kind === SUGGESTION.fieldFormat ||
          suggestion.kind === SUGGESTION.newField);
      const undone = await window.capturePointDesktop.undoJobSuggestion({
        jobId: job.id,
        suggestionId: suggestion.id,
      });
      const restoredJob = migrateJob(undone.job as Job);
      onJobChange(restoredJob);
      setLastJobImprovement(undone.suggestion);
      setJobImprovementUndone(true);
      promoteReceipt('improvement');
      setLastJobImprovementScope(scope);
      setLastJobImprovementOutcome(
        scope === 'future-only' || !batch
          ? 'future-only'
          : refreshCurrentBatch
            ? 'refreshed'
            : 'kept',
      );
      if (batch && refreshCurrentBatch) {
        setProcessing(true);
        try {
          const request = {
            jobId: restoredJob.id,
            batchId: batch.batchId,
            documentTypes: schemaRequest(restoredJob),
            separationMode: restoredJob.separationMode,
            separationBarcodeText: restoredJob.separationBarcodeText,
            boundaryOpeningProfiles: restoredJob.boundaryOpeningProfiles,
            boundaryContinuationProfiles: restoredJob.boundaryContinuationProfiles,
          };
          const result =
            suggestion.kind === SUGGESTION.fieldFormat
              ? await window.capturePointDesktop.refreshBatchSchema({
                  ...request,
                  formatChecksOnly: true,
                })
              : protectsReviewedWork
                ? await window.capturePointDesktop.reprocessReviewed(request)
                : await window.capturePointDesktop.retryDocuments(request);
          adoptReprocessedBatch(result);
          setSelectedDocument(0);
          setSelectedPage(0);
          onNotice('Job change undone · current documents restored');
        } catch (reason) {
          setLastJobImprovementOutcome('kept');
          setError(
            messageAfter('Job change undone, but this batch could not be refreshed', reason),
            'improvement-undo-failed',
          );
        } finally {
          setProcessing(false);
        }
      } else {
        onNotice(
          scope === 'future-only' ? 'Job change undone · current batch kept' : 'Job change undone',
        );
      }
    } catch (reason) {
      const message = messageFor(reason, 'The job change could not be undone');
      setError(message, 'improvement-undo-failed');
    } finally {
      releaseWholeBatchMutation(wholeBatchToken);
      setJobSuggestionBusy('');
    }
  };
  // A queued gesture runs with the render current when its turn comes, not the one that was clicked.
  const wholeBatchGestures = useRef({ retryExtraction, applyJobSuggestion, undoJobSuggestion });
  wholeBatchGestures.current = { retryExtraction, applyJobSuggestion, undoJobSuggestion };
  const appliedLearningRequest = useRef<SuggestionApplyRequest | null>(null);
  useEffect(() => {
    // Reviewed in Learning: run the same admission, batch lock, re-evaluation
    // and receipt as every other apply, once, after the saved batch is adopted.
    if (!suggestionApplyRequest || appliedLearningRequest.current === suggestionApplyRequest)
      return;
    if (suspended || (savedBatch && batch !== savedBatch)) return;
    appliedLearningRequest.current = suggestionApplyRequest;
    onSuggestionApplyConsumed?.();
    void applyJobSuggestion(
      suggestionApplyRequest.suggestion,
      suggestionApplyRequest.scope,
      suggestionApplyRequest.proposedName,
      suggestionApplyRequest.excludedDocumentIds,
      suggestionApplyRequest.keptFieldNames,
    );
  }, [suggestionApplyRequest, batch, savedBatch, suspended]);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const addInlineField = async () => {
    const name = inlineFieldName.trim();
    if (
      inlineFieldSavingRef.current ||
      !name ||
      !documentSchema ||
      fieldNamed(documentSchema.fields, name)
    )
      return;
    const updatedJob = {
      ...job,
      documentTypes: job.documentTypes.map((type) =>
        type.id === documentSchema.id
          ? {
              ...type,
              fields: [
                ...type.fields,
                field(name, 'text', 'Recognized automatically from the document'),
              ],
            }
          : type,
      ),
    };
    if (!window.capturePointDesktop || !batch) return;
    // Adding schema is not permission to replace the batch. The normal job-save
    // coordinator persists it and blocks new capture admission while saving;
    // existing documents/corrections remain untouched. Automatic recognition
    // catches up through the guarded schema refresh once capture work drains.
    inlineFieldSavingRef.current = true;
    setInlineFieldSaving(true);
    setError(null);
    try {
      await onCommitJob(updatedJob);
      if (!mounted.current) return;
      lastSchemaRefreshAt.current = Date.now();
      // A new type needs several fields: the row stays ready for the next name
      // (owner, 2026-10-05: it closed after one, and nothing offered another).
      setInlineFieldName('');

      setArrivedFieldId(
        updatedJob.documentTypes.find((type) => type.id === documentSchema.id)!.fields.at(-1)!.id,
      );
      onNotice('Field added · enter a value or select its text on the page');
    } catch (reason) {
      if (mounted.current)
        setError(messageFor(reason, 'Could not add the new field'), 'field-add-failed');
    } finally {
      inlineFieldSavingRef.current = false;
      if (mounted.current) setInlineFieldSaving(false);
    }
  };
  // The input is disabled while its field saves, which drops focus; give it back.
  useEffect(() => {
    if (inlineFieldSaving || !inlineFieldKeepFocus.current) return;
    inlineFieldKeepFocus.current = false;
    inlineFieldInputRef.current?.focus();
  }, [inlineFieldSaving]);
  // As in Job setup (43a17e9d): a name typed here is not a field yet, so moving on to
  // anything else — a field's value, the type, confirm — adds it first, and the
  // setting or value chosen next can only land on a field that exists.
  const pendingInlineFieldAdd = useRef<() => void>(() => {});
  pendingInlineFieldAdd.current = () => {
    if (inlineFieldName.trim()) void addInlineField();
  };
  const inlineFieldPending = inlineFieldName.trim().length > 0;
  useEffect(() => {
    if (!inlineFieldPending) return;
    const commitOnLeave = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('.inline-add-field')) return;
      inlineFieldKeepFocus.current = false;
      pendingInlineFieldAdd.current();
    };
    window.document.addEventListener('pointerdown', commitOnLeave, true);
    window.document.addEventListener('focusin', commitOnLeave, true);
    return () => {
      window.document.removeEventListener('pointerdown', commitOnLeave, true);
      window.document.removeEventListener('focusin', commitOnLeave, true);
    };
  }, [inlineFieldPending]);
  const addInlineFieldHere = () => {
    inlineFieldKeepFocus.current = true;
    void addInlineField();
  };
  const finishInlineFieldSetup = () => {
    if (inlineFieldName.trim()) void addInlineField();
    setInlineFieldSetup(null);
  };
  const inlineFieldSetupHere = Boolean(
    inlineFieldSetup &&
    documentSchema &&
    inlineFieldSetup.typeId === documentSchema.id &&
    inlineFieldSetup.documentId === document?.id,
  );
  useEffect(() => {
    if (inlineFieldSetup && !inlineFieldSetupHere) {
      setInlineFieldSetup(null);
      setInlineFieldName('');
    }
  }, [inlineFieldSetup, inlineFieldSetupHere]);

  // Retained reading failures stay available for explicit retry. Opening a
  // workspace must not restart the batch and lock out the operator's review.

  const findMatches = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query || !document) return [];
    return (
      document.pages[selectedPage]?.lines
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => line.text.toLocaleLowerCase().includes(query))
        .map(({ index }) => index) ?? []
    );
  }, [document, selectedPage, searchQuery]);
  // The highlight Find itself put on the page (the exact lines array), so clearing the search
  // takes back only that one, never a field's that replaced it.
  const findEvidenceLines = useRef<number[] | null>(null);
  // The matches themselves, by value: a stand-in document's lines can arrive after the search was
  // typed, and Find must then highlight its first match ("1/3" with nothing marked before).
  const findMatchKey = findMatches.join(',');
  useEffect(() => {
    setFindIndex(0);
    // With no search, the highlight on show is a field's or a review marker's (a field clicked
    // on another page, a marker landing): changing page must not wipe it, as it used to.
    if (!searchQuery.trim()) {
      const own = findEvidenceLines.current;
      findEvidenceLines.current = null;
      if (own && activeEvidenceLines === own) {
        setActiveEvidenceLines([]);
        setActiveEvidenceTarget(null);
      }
      return;
    }
    const lines = findMatches.length ? [findMatches[0]] : [];
    findEvidenceLines.current = lines;
    setActiveEvidenceLines(lines);
    setActiveEvidenceTarget(
      findMatches.length && document?.pages[selectedPage]
        ? { documentId: document.id, pageNumber: document.pages[selectedPage].number }
        : null,
    );
  }, [searchQuery, selectedDocument, selectedPage, findMatchKey]);
  useEffect(() => {
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const onKeyDown = (event: KeyboardEvent) => {
      // Behind Job setup this screen is frozen (inert): its shortcuts answer
      // boundaries and change pages, so they must not act on keys typed there.
      if (viewerRef.current?.closest('[inert]')) return;
      // Nor behind an open window, question or menu: under the F1 sheet, PgDn changed the page,
      // Ctrl+B split the document and Ctrl+F opened Find (shortcuts audit, 2026-10-07).
      // The browser's own undo and redo under a window are blockUndoBehindWindow's (below).
      if (anyWindowOpen()) return;
      const key = event.key.toLocaleLowerCase();
      if ((event.ctrlKey || event.metaKey) && !event.altKey && key === 'f') {
        event.preventDefault();
        // Ctrl+Shift+F is the Search documents button, which had no key.
        if (event.shiftKey) searchShortcutRef.current();
        else setSearchOpen(true);
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === 'c') {
        const selection = window.getSelection()?.toString() ?? '';
        if (!selection.trim()) return;
        event.preventDefault();
        void window.capturePointDesktop?.copyText(selection);
        return;
      }
      // ABOVE the editable-target return on purpose: the operator's hands are
      // in a field when they finish a document. It does what the main button
      // says from ANYWHERE on the review screen (single confirm, 2026-10-05):
      // it used to work only inside the fields panel, so after clicking a tab,
      // a document or a button, or right after it moved on, it did nothing.
      // Still never from an open window, Job setup, the page's Find box or a
      // name being typed (reviewShortcutTarget): unscoped, it once fired from
      // the search box and wrote false ground truth (adversarial review). A
      // held key repeats; only the first press counts.
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'Enter' &&
        !event.altKey &&
        !event.shiftKey
      ) {
        if (event.repeat || !reviewShortcutTarget(event.target)) return;
        event.preventDefault();
        confirmShortcutRef.current(event.timeStamp);
        return;
      }
      // F6 / Shift+F6: the next or previous thing this document asks to check, as clicking it
      // does. From a field too (it moves on like Tab), never from the Find box or a name being typed.
      if (event.key === 'F6' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (!event.repeat && reviewShortcutTarget(event.target))
          reviewItemShortcutRef.current(event.shiftKey ? -1 : 1);
        return;
      }
      // Ctrl+Z in a field's box that holds no typing of its own (the person tabbed or clicked into it
      // and typed nothing): the box has nothing to undo, so it undoes the value just saved, and
      // only that (text-undo.ts). A box with typing in it keeps the text's own undo.
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        !event.altKey &&
        key === 'z' &&
        isEditableTarget(event.target) &&
        reviewShortcutTarget(event.target) &&
        textBoxUntouched(event.target)
      ) {
        if (!event.repeat && undoShortcutRef.current(true)) event.preventDefault();
        return;
      }
      // Ctrl+Page Up / Ctrl+Page Down move between documents from a field too: they mean nothing in a
      // text box, and a document arriving can put the keyboard in one, which swallowed a quick step back
      // (Paige's review tests). Never from the Find box, a name being typed or an open window.
      // PAIGE (workbench-port.md): Alt as well as Ctrl, as below (shared/document-step-keys.ts); one of the
      // two, never both (AltGr).
      if (
        stepsDocument(event) &&
        !(event.ctrlKey && event.altKey) &&
        (event.key === 'PageUp' || event.key === 'PageDown') &&
        isEditableTarget(event.target)
      ) {
        if (!reviewShortcutTarget(event.target)) return;
        event.preventDefault();
        arrivedAt.current = performance.now();
        if (event.key === 'PageUp') documentNavigationRef.current(-1, true);
        else if (!skipShortcutRef.current()) documentNavigationRef.current(1, true);
        return;
      }
      if (isEditableTarget(event.target)) return;
      // Ctrl+Z outside a text box (in one it stays the text's own undo): the undo of the person's
      // most recent change, while it still shows (last-change.ts). Otherwise it does nothing.
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && key === 'z') {
        if (!event.repeat && undoShortcutRef.current()) event.preventDefault();
        return;
      }
      // PAIGE (docs/engineering/workbench-port.md): Alt steps documents too, since a browser keeps
      // Ctrl+Page Up / Ctrl+Page Down for its own tabs (shared/document-step-keys.ts).
      if (event.key === 'PageUp') {
        event.preventDefault();
        if (stepsDocument(event)) {
          arrivedAt.current = performance.now();
          documentNavigationRef.current(-1, true);
        } else setSelectedPage((current) => Math.max(0, current - 1));
      }
      if (event.key === 'PageDown') {
        event.preventDefault();
        if (stepsDocument(event)) {
          arrivedAt.current = performance.now();
          if (!skipShortcutRef.current()) documentNavigationRef.current(1, true);
        } else
          setSelectedPage((current) => Math.min((document?.pages.length ?? 1) - 1, current + 1));
      }
      if (event.ctrlKey && (key === 'b' || event.code === 'KeyB')) {
        event.preventDefault();
        // A held key is one press: repeats would open the seam and join through it unseen.
        if (!event.repeat) boundaryShortcutRef.current();
      }
      // R turns the page on screen, only while the page or its toolbar has the keyboard:
      // a stray R anywhere else on the screen turns nothing, and in a text box it is a letter
      // (the editable-target return above).
      if (
        key === 'r' &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        !event.repeat &&
        pageHasKeyboardRef.current
      ) {
        event.preventDefault();
        turnPageShortcutRef.current();
      }
      if (event.ctrlKey && (key === '=' || key === '+')) {
        event.preventDefault();
        applyZoom((readerMode ? readerScaleRef.current : zoomRef.current) * 1.15);
      }
      if (event.ctrlKey && key === '-') {
        event.preventDefault();
        applyZoom(Math.max(0.25, (readerMode ? readerScaleRef.current : zoomRef.current) / 1.15));
      }
      if (event.ctrlKey && key === '0') {
        event.preventDefault();
        resetView();
      }
    };
    // Dragging the page does not move focus (the drag owns the pointer), so "the page has the
    // keyboard" is the last click or focus: on the page or its toolbar, or anywhere else.
    const onPageKeyboard = (event: Event) => {
      const target = event.target;
      pageHasKeyboardRef.current =
        target instanceof Element &&
        Boolean(viewerRef.current?.contains(target) || target.closest('.viewer-toolbar'));
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPageKeyboard, true);
    window.addEventListener('focusin', onPageKeyboard, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPageKeyboard, true);
      window.removeEventListener('focusin', onPageKeyboard, true);
    };
  }, [batch?.documents.length, document?.pages.length]);
  // Under the F1 sheet or another window, the browser's own undo and redo never reach a box behind it (text-undo.ts).
  useEffect(() => {
    window.addEventListener('keydown', blockUndoBehindWindow, true);
    return () => window.removeEventListener('keydown', blockUndoBehindWindow, true);
  }, []);
  useEffect(() => {
    const viewer = viewerRef.current;
    const activeDocument = document;
    if (!viewer || !activeDocument) return;
    const activeDocumentMutationToken = reviewDocumentMutationToken(activeDocument);
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const onContextMenu = (event: MouseEvent, automatic = false) => {
      if (suppressContextMenu.current) {
        event.preventDefault();
        suppressContextMenu.current = false;
        return;
      }
      // Reading mode renders every page at once while selectedPage follows the
      // viewer midpoint. Bind the gesture to the OCR word's own page instead.
      // Pan mode has one rendered page, so its selected page is authoritative.
      const renderedPageNumber = activeDocument.pages[selectedPage]?.number;
      const pageNumberForText = (element: SVGTextElement) => {
        const readerPage = element.closest<HTMLElement>('[data-reader-page-number]');
        const parsed = Number(readerPage?.dataset.readerPageNumber);
        return Number.isFinite(parsed) ? parsed : (renderedPageNumber ?? Number.NaN);
      };
      const selectionState = window.getSelection();
      const selection = selectionState?.toString() ?? '';
      if (automatic && !selection.trim()) return;
      // No highlight needed: a bare right-click on printed text takes the whole
      // line under the cursor. Dragging the invisible overlay is a skill;
      // pointing at the thing you can see is not.
      if (!selection.trim()) {
        // By position, never by event target: the right-button pan gesture
        // captures the pointer, which retargets this event to the pan surface —
        // the text under the cursor is found from the cursor itself.
        const under =
          [...viewer.querySelectorAll<SVGTextElement>('text[data-line-index]')].find((element) => {
            const box = element.getBoundingClientRect();
            return (
              event.clientX >= box.left - 2 &&
              event.clientX <= box.right + 2 &&
              event.clientY >= box.top - 2 &&
              event.clientY <= box.bottom + 2
            );
          }) ?? null;
        const underLine = under ? Number(under.dataset.lineIndex) : Number.NaN;
        if (!under || !Number.isFinite(underLine)) return;
        event.preventDefault();
        const sourcePageNumber = pageNumberForText(under);
        if (!Number.isFinite(sourcePageNumber)) return;
        const sourcePageIndex = activeDocument.pages.findIndex(
          (page) => page.number === sourcePageNumber,
        );
        if (sourcePageIndex < 0) return;
        const underText = [...viewer.querySelectorAll<SVGTextElement>('text[data-line-index]')]
          .filter(
            (element) =>
              Number(element.dataset.lineIndex) === underLine &&
              pageNumberForText(element) === sourcePageNumber,
          )
          .sort((left, right) => {
            const leftBox = left.getBoundingClientRect();
            const rightBox = right.getBoundingClientRect();
            return Math.abs(leftBox.top - rightBox.top) >
              Math.max(2, Math.min(leftBox.height, rightBox.height) / 2)
              ? leftBox.top - rightBox.top
              : leftBox.left - rightBox.left;
          })
          .map((element) => element.textContent?.replace(/\s+/g, ' ').trim() ?? '')
          .filter(Boolean)
          .join(' ');
        if (!underText) return;
        selectGesturePage(sourcePageIndex, activeDocument.id);
        setActiveEvidenceLines([underLine]);
        setActiveBarcodeEvidence(null);
        setActiveLineItemRow(null);
        setActiveEvidenceTarget({ documentId: activeDocument.id, pageNumber: sourcePageNumber });
        setSelectionMenu({
          documentId: activeDocument.id,
          documentMutationToken: activeDocumentMutationToken,
          x: Math.max(8, Math.min(event.clientX, window.innerWidth - 206)),
          y: Math.max(8, Math.min(event.clientY, window.innerHeight - 290)),
          text: underText,
          lineIndexes: [underLine],
          pageNumber: sourcePageNumber,
        });
        return;
      }
      event.preventDefault();
      const textElementAtEndpoint = (node: Node | null) =>
        node instanceof Element
          ? node.closest<SVGTextElement>('text[data-line-index]')
          : (node?.parentElement?.closest<SVGTextElement>('text[data-line-index]') ?? null);
      const anchorText = textElementAtEndpoint(selectionState?.anchorNode ?? null);
      const focusText = textElementAtEndpoint(selectionState?.focusNode ?? null);
      if (
        !anchorText ||
        !focusText ||
        !viewer.contains(anchorText) ||
        !viewer.contains(focusText)
      ) {
        if (!automatic) {
          selectionState?.removeAllRanges();
          onContextMenu(event);
          return;
        }
        setSelectionMenu(null);
        setActiveEvidenceLines([]);
        setActiveBarcodeEvidence(null);
        setActiveLineItemRow(null);
        setActiveEvidenceTarget(null);
        if (!automatic) onNotice('Select only printed text on the page', 'text-select-refused');
        return;
      }
      const range = selectionState?.rangeCount ? selectionState.getRangeAt(0) : null;
      // Geometry, not DOM-range semantics. `intersectsNode` over SVG text is
      // unreliable in Chromium, and when it came back empty the value was saved
      // with no idea where on the page it came from — the one thing a
      // click-the-page flow exists to know. The selection's own rectangles
      // intersected with each element's box cannot be wrong about overlap.
      const selectionRects = range
        ? [...range.getClientRects()].filter((rect) => rect.width > 0 && rect.height > 0)
        : [];
      const overlaps = (a: DOMRect, b: DOMRect) =>
        a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
      const hasBoxSelection = Boolean(viewer.querySelector('.ocr-box-selected'));
      const hit = [...viewer.querySelectorAll<SVGTextElement>('text[data-line-index]')]
        .map((element) => ({
          element,
          box: element.getBoundingClientRect(),
          lineIndex: Number(element.dataset.lineIndex),
          pageNumber: pageNumberForText(element),
        }))
        .filter(
          (entry) =>
            Number.isFinite(entry.lineIndex) &&
            Number.isFinite(entry.pageNumber) &&
            (hasBoxSelection
              ? entry.element.parentElement?.classList.contains('ocr-box-selected')
              : selectionRects.some((rect) => overlaps(rect, entry.box))),
        );
      const hitPages = [...new Set(hit.map((entry) => entry.pageNumber))];
      if (hitPages.length !== 1) {
        setSelectionMenu(null);
        setActiveEvidenceLines([]);
        setActiveBarcodeEvidence(null);
        setActiveLineItemRow(null);
        if (!automatic)
          onNotice(
            hitPages.length > 1
              ? 'Select text from one page at a time'
              : 'Select printed text on the page',
            'text-select-refused',
          );
        return;
      }
      const text = ocrBoxText(hit.map((entry) => entry.element));
      const sourcePageNumber = hitPages[0]!;
      const sourcePageIndex = activeDocument.pages.findIndex(
        (page) => page.number === sourcePageNumber,
      );
      if (sourcePageIndex < 0) return;
      const lineIndexes = [...new Set(hit.map((entry) => entry.lineIndex))];
      selectGesturePage(sourcePageIndex, activeDocument.id);
      setActiveEvidenceLines(lineIndexes);
      setActiveBarcodeEvidence(null);
      setActiveLineItemRow(null);
      setActiveEvidenceTarget({ documentId: activeDocument.id, pageNumber: sourcePageNumber });
      setSelectionMenu({
        documentId: activeDocument.id,
        documentMutationToken: activeDocumentMutationToken,
        x: event.clientX,
        y: event.clientY,
        text,
        copyText: text,
        automatic,
        lineIndexes,
        pageNumber: sourcePageNumber,
      });
    };
    let selectionFrame = 0;
    const showAfterSelection = (event: MouseEvent) => {
      if (event.button !== 0) return;
      cancelAnimationFrame(selectionFrame);
      selectionFrame = requestAnimationFrame(() => onContextMenu(event, true));
    };
    const showAfterKeyboardSelection = (event: KeyboardEvent) => {
      if (
        !(event.shiftKey || event.key === 'Shift') ||
        isEditableTarget(event.target) ||
        (event.target instanceof Element && event.target.closest('.selection-actions'))
      )
        return;
      const selection = window.getSelection();
      const rect = selection?.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : null;
      if (rect)
        showAfterSelection(
          new MouseEvent('mouseup', { button: 0, clientX: rect.right, clientY: rect.bottom }),
        );
    };
    let textPointerStart: { x: number; y: number } | null = null;
    const unbindBoxes = bindOcrBoxSelection(viewer);
    const rememberTextPointer = (event: PointerEvent) => {
      textPointerStart = event.button === 0 ? { x: event.clientX, y: event.clientY } : null;
    };
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const selectClickedText = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        !textPointerStart ||
        Math.hypot(event.clientX - textPointerStart.x, event.clientY - textPointerStart.y) > 4
      )
        return;
      const target =
        event.target instanceof Element
          ? (event.target.closest<SVGTextElement>('text[data-line-index]') ??
            event.target
              .closest('.ocr-word-zone')
              ?.querySelector<SVGTextElement>('text[data-line-index]'))
          : null;
      if (!target || !viewer.contains(target)) return;
      cancelAnimationFrame(selectionFrame);
      const page = target.closest('.ocr-text-layer');
      const words =
        event.detail >= 2 && page
          ? [...page.querySelectorAll<SVGTextElement>('text[data-line-index]')].filter(
              (word) => word.dataset.lineIndex === target.dataset.lineIndex,
            )
          : [target];
      if (!words.length) return;
      selectOcrBoxes(viewer, words[0], words[words.length - 1]);
      const range = window.document.createRange();
      const firstText = words[0].firstChild;
      const lastText = words[words.length - 1].firstChild;
      if (!firstText || !lastText) return;
      // Keep endpoints inside SVG text so the source-selection owner can
      // identify the page and draw its evidence boxes, just like native selection.
      range.setStart(firstText, 0);
      range.setEnd(lastText, lastText.textContent?.length ?? 0);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      const readerPage = target.closest<HTMLElement>('[data-reader-page-number]');
      const pageNumber = readerPage
        ? Number(readerPage.dataset.readerPageNumber)
        : activeDocument.pages[selectedPage]?.number;
      const pageIndex = activeDocument.pages.findIndex((page) => page.number === pageNumber);
      if (pageIndex < 0 || pageNumber === undefined) return;
      const lineIndexes = [...new Set(words.map((word) => Number(word.dataset.lineIndex)))];
      selectGesturePage(pageIndex, activeDocument.id);
      setActiveEvidenceLines(lineIndexes);
      setActiveBarcodeEvidence(null);
      setActiveLineItemRow(null);
      setActiveEvidenceTarget({ documentId: activeDocument.id, pageNumber });
      setSelectionMenu({
        documentId: activeDocument.id,
        documentMutationToken: activeDocumentMutationToken,
        automatic: true,
        x: event.clientX,
        y: event.clientY,
        text: words
          .map((word) => word.textContent?.trim() ?? '')
          .filter(Boolean)
          .join(' '),
        lineIndexes,
        pageNumber,
      });
    };
    viewer.addEventListener('pointerdown', rememberTextPointer);
    viewer.addEventListener('click', selectClickedText);
    viewer.addEventListener('contextmenu', onContextMenu);
    viewer.addEventListener('pointerup', showAfterSelection);
    window.addEventListener('keyup', showAfterKeyboardSelection);
    return () => {
      cancelAnimationFrame(selectionFrame);
      viewer.removeEventListener('pointerdown', rememberTextPointer);
      unbindBoxes();
      viewer.removeEventListener('click', selectClickedText);
      viewer.removeEventListener('contextmenu', onContextMenu);
      viewer.removeEventListener('pointerup', showAfterSelection);
      window.removeEventListener('keyup', showAfterKeyboardSelection);
    };
  }, [currentDocumentMutationToken, detachedPanels.viewer, document?.id, selectedPage]);
  const currentSelectionPageNumber = document?.pages[selectedPage]?.number;
  useEffect(() => {
    if (!activeEvidenceTarget) return;
    if (
      activeEvidenceTarget.documentId === document?.id &&
      activeEvidenceTarget.pageNumber === currentSelectionPageNumber
    )
      return;
    setActiveEvidenceLines([]);
    setActiveBarcodeEvidence(null);
    setActiveLineItemRow(null);
    setActiveEvidenceTarget(null);
  }, [activeEvidenceTarget, currentSelectionPageNumber, document?.id]);
  useEffect(() => {
    if (!selectionMenu) return;
    if (!document) {
      setSelectionMenu(null);
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
      return;
    }
    if (
      selectionMenu.documentId === document.id &&
      selectionMenu.documentMutationToken === currentDocumentMutationToken &&
      selectionMenu.pageNumber === currentSelectionPageNumber
    )
      return;
    // A context menu is an edit intent, not a clipboard. Once navigation moves
    // away from the exact document page that supplied it, the captured text is
    // no longer allowed to write into whatever happens to be current.
    setSelectionMenu(null);
    setActiveEvidenceLines([]);
    setActiveBarcodeEvidence(null);
    setActiveEvidenceTarget(null);
  }, [currentDocumentMutationToken, currentSelectionPageNumber, document?.id, selectionMenu]);
  useEffect(() => {
    if (!selectionMenu) {
      if (viewerRef.current?.dataset.ocrSelecting !== 'true')
        viewerRef.current
          ?.querySelectorAll('.ocr-box-selected')
          .forEach((word) => word.classList.remove('ocr-box-selected'));
      return;
    }
    const close = (event: PointerEvent) => {
      if ((event.target as Element | null)?.closest('.viewer-selection-menu')) return;
      setSelectionMenu(null);
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSelectionMenu(null);
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
    };
    const closeCollapsedSelection = () => {
      if (
        !selectionMenu.automatic ||
        window.getSelection()?.toString().trim() ||
        window.document.activeElement?.closest('.selection-actions')
      )
        return;
      setSelectionMenu(null);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', closeOnEscape);
    window.document.addEventListener('selectionchange', closeCollapsedSelection);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', closeOnEscape);
      window.document.removeEventListener('selectionchange', closeCollapsedSelection);
    };
  }, [selectionMenu]);
  useEffect(() => {
    setTypeSuggestion(null);
    if (
      !batch ||
      !document ||
      !loadedDocumentIds.has(document.id) ||
      document.documentTypeVerified ||
      document.documentTypeSource === TYPE_FROM_CONFIRMED_EXAMPLES ||
      !window.capturePointDesktop
    )
      return;
    let canceled = false;
    void window.capturePointDesktop
      .suggestDocumentType({ jobId: job.id, batchId: batch.batchId, documentId: document.id })
      .then((suggestion) => {
        const configured =
          suggestion &&
          job.documentTypes.some(
            (type) =>
              type.name.localeCompare(suggestion.suggestedType, undefined, {
                sensitivity: 'accent',
              }) === 0,
          );
        const key = suggestion
          ? `document-type:${suggestion.suggestedType.toLocaleLowerCase()}`
          : '';
        const paused = job.suggestionsPausedUntil
          ? new Date(job.suggestionsPausedUntil).getTime() > Date.now()
          : false;
        const suppressed = key && job.suppressedSuggestions?.includes(key);
        if (!canceled)
          setTypeSuggestion(
            configured && !paused && !suppressed && dismissedSuggestionKey !== key
              ? suggestion
              : null,
          );
      })
      .catch(() => {
        if (!canceled) setTypeSuggestion(null);
      });
    return () => {
      canceled = true;
    };
  }, [
    batch?.batchId,
    document?.id,
    document?.documentTypeVerified,
    document?.documentTypeSource,
    loadedDocumentIds,
    dismissedSuggestionKey,
    job.suggestionsPausedUntil,
    job.suppressedSuggestions,
  ]);
  const suggestionRefreshOwner = useRef({ jobId: job.id, running: false, dirty: false });
  if (suggestionRefreshOwner.current.jobId !== job.id)
    suggestionRefreshOwner.current = { jobId: job.id, running: false, dirty: false };
  const refreshJobSuggestions = useCallback(() => {
    const api = window.capturePointDesktop;
    if (!api) return;
    const owner = suggestionRefreshOwner.current;
    if (owner.jobId !== job.id) return;
    owner.dirty = true;
    if (owner.running) return;
    owner.running = true;
    const current = () => mounted.current && suggestionRefreshOwner.current === owner;
    // Every batch replacement may carry new truth, but queuing a native mining
    // request for each replacement only repeats the same expensive population.
    // Keep one request and one trailing fresh read. Superseded replies remain
    // hidden, as they were under the former effect's canceled-response guard.
    void (async () => {
      try {
        while (current() && owner.dirty) {
          owner.dirty = false;
          try {
            const suggestions = await api.listJobSuggestions(owner.jobId);
            if (current() && !owner.dirty) setJobSuggestions(suggestions.filter(isJobAdjustment));
          } catch {
            if (current() && !owner.dirty) setJobSuggestions([]);
          }
        }
      } finally {
        owner.running = false;
      }
    })();
  }, [job.id]);
  useEffect(() => {
    if (!window.capturePointDesktop || suspended) return;
    let canceled = false;
    refreshJobSuggestions();
    /* A rule the operator applies announces itself, because they clicked the
       thing that applied it. A rule that meets the bar and applies itself has no
       click to hang a notice on, so without this the only evidence would be a
       review queue that is shorter than it was — and a queue that shrinks with
       nothing saying why is the fastest way to stop trusting the number. It
       lands on the same banner, with the same Undo. */
    /* Everything the job has learned, applied to the work still waiting.
       This is the half of learning that was missing. The product would recognise
       a document from twenty-seven confirmed examples, attach the right schema,
       record how many examples backed it — and then ask the operator anyway,
       because nothing was allowed to settle a type but a person. Forty-seven
       confirmations on the real job, forty-seven agreements, and not one
       document that ever came in green.
       Runs on the same poll as the suggestions, so answering a question shortens
       the queue behind you. */
    /* The spine's colours reconciled against the engine, cheaply, every poll.

       Refreshing only when a call reported clearing something was not enough.
       Work cleared on an earlier poll, by the settling pass, or from a detached
       panel never reached this copy of the batch, so the spine went on showing a
       count from whenever it last happened to reload — "17 to review" over a job
       where sixteen did. The count was not miscomputed; it was correctly counting
       a stale list.
       This asks for ids and flags only, not documents, so it is affordable to ask
       every time and there is nowhere for staleness to hide. It settles in one
       extra pass: patching the flags re-fires this, the second answer matches,
       and nothing is written. */
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const reconcileLearnedReviewIndex = () => {
      if (!batch || !mounted.current || !window.capturePointDesktop) return;
      const indexOpeningGeneration = boundaryGeneration.current;
      const indexOpeningTokens = new Map(
        (batchRef.current?.documents ?? batch.documents).map((item) => [
          item.id,
          reviewDocumentMutationToken(item),
        ]),
      );
      void window.capturePointDesktop
        .getBatchDocumentIndex({ jobId: job.id, batchId: batch.batchId })
        .then((index) => {
          if (
            canceled ||
            structuralMutationRef.current ||
            wholeBatchMutationRef.current ||
            boundaryQueueDepth.current > 0 ||
            boundaryUndoStack.current.length > 0 ||
            boundaryGeneration.current !== indexOpeningGeneration
          )
            return;
          const settled = new Map(index.map((item) => [item.id, item]));
          const current = batchRef.current;
          if (!current || current.batchId !== batch.batchId) return;
          let changed = false;
          const documents = current.documents.map((item) => {
            if (indexOpeningTokens.get(item.id) !== reviewDocumentMutationToken(item)) return item;
            const truth = settled.get(item.id);
            if (
              !truth ||
              (truth.needsReview === item.needsReview &&
                Boolean(truth.needsTeaching) === Boolean(item.needsTeaching))
            )
              return item;
            changed = true;
            return { ...item, needsReview: truth.needsReview, needsTeaching: truth.needsTeaching };
          });
          if (!changed) return;
          const reconciled = { ...current, documents };
          batchRef.current = reconciled;
          setBatch(reconciled);
          onRestored(reconciled);
        })
        .catch(() => {});
    };
    // Coalesce a burst of operator edits before claiming the whole-batch lane.
    // Starting on every optimistic/saved field render let automatic catch-up
    // jump between consecutive confirmation clicks and block the next save.
    // Cleanup cancels only admission, never an already-running durable write.
    const learningTimer = setTimeout(() => {
      if (canceled || !mounted.current || !window.capturePointDesktop) return;
      const learningToken = claimWholeBatchMutation('applying what this job learned');
      if (learningToken) {
        const learningOpening = batchRef.current;
        setApplyingLearning(true);
        void window.capturePointDesktop
          .applyLearningToWaitingWork(job.id)
          .then((cleared) => {
            if (
              !mounted.current ||
              cleared <= 0 ||
              !window.capturePointDesktop ||
              batchRef.current?.batchId !== learningOpening?.batchId
            )
              return;
            /* Reloaded once, after the work is done rather than during it. The engine
             refuses to re-derive until the operator has confirmed something new, so
             the poll this reload re-fires answers in a single counting query and
             stops. Before that guard existed, clearing work re-triggered the whole
             apparatus and an operator watched a spinner grind through a chain it
             had started itself. */
            return window.capturePointDesktop.getLatestBatch(job.id).then((refreshed) => {
              if (
                !mounted.current ||
                !refreshed ||
                batchRef.current?.batchId !== learningOpening?.batchId
              )
                return;
              /* What it took out of Needs review is the cleared news under the list's counts,
               said once: the engine records these same documents as held-work releases, and
               the ledger poll joins their Undo to the same line rather than saying it twice. */
              const live = new Map(
                (batchRef.current?.documents ?? []).map((item) => [item.id, item]),
              );
              const openId = batchRef.current?.documents[selectedDocumentRef.current]?.id;
              noteCleared(
                refreshed.documents
                  .filter(
                    (item) =>
                      !item.needsReview && item.id !== openId && live.get(item.id)?.needsReview,
                  )
                  .map((item) => clearedDocument(item.id, item)),
              );
              adoptBatch(refreshed, learningOpening);
            });
          })
          .catch(() => {})
          .finally(() => {
            releaseWholeBatchMutation(learningToken);
            if (mounted.current) setApplyingLearning(false);
            // Reading before claiming this lane made the index reply refuse
            // itself as a concurrent writer. Reconcile after durable learning.
            reconcileLearnedReviewIndex();
          });
      } else reconcileLearnedReviewIndex();
    }, 1500);
    void window.capturePointDesktop
      .listSelfAppliedSuggestions(job.id)
      .then((applied) => {
        const seen = announcedSelfApplied.current;
        if (seen.jobId !== job.id) return;
        // Primed by the first reply even when its effect has since re-run: it still says
        // what was applied before this view opened.
        if (!seen.primed) {
          seen.primed = true;
          for (const item of applied) seen.ids.add(item.id);
          return;
        }
        if (canceled) return;
        const fresh = applied.find((item) => !seen.ids.has(item.id));
        if (!fresh) return;
        seen.ids.add(fresh.id);
        setLastJobImprovement(fresh);
        promoteReceipt('improvement');
        setJobImprovementUndone(false);
        setLastJobImprovementScope('refresh-safe');
        setLastJobImprovementOutcome('refreshed');
      })
      .catch(() => {});
    return () => {
      canceled = true;
      clearTimeout(learningTimer);
    };
  }, [batch, job.id, job.schemaVersion, refreshJobSuggestions, suspended]);
  /* Held work re-judged as truth arrives. The engine re-weighs the documents still
     waiting in the background after confirmations settle (and when the job adopts
     something), records every one it releases, and this asks for those records: a
     cheap ledger read. The flags are patched in place, never reordered; the document
     open stays exactly as it is (its flag waits until the operator moves on), and a
     released document fades out of Needs review rather than vanishing under the cursor.
     The Review thresholds decided each release; nothing here decides anything. */
  const heldWorkSeen = useRef<{ jobId: string; since: string | null; primed: boolean }>({
    jobId: job.id,
    since: null,
    primed: false,
  });
  if (heldWorkSeen.current.jobId !== job.id)
    heldWorkSeen.current = { jobId: job.id, since: null, primed: false };
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.listHeldWorkReleases || suspended) return;
    let canceled = false;
    let running = false;
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const poll = async () => {
      if (running || canceled) return;
      running = true;
      try {
        const seen = heldWorkSeen.current;
        const releases = await api.listHeldWorkReleases(job.id, seen.since ?? undefined);
        if (canceled || heldWorkSeen.current !== seen) return;
        const newest = releases.reduce<string | null>(
          (latest, release) =>
            !latest || release.releasedAt > latest ? release.releasedAt : latest,
          seen.since,
        );
        seen.since = newest;
        // Releases from before this workspace opened are history, not news.
        if (!seen.primed) {
          seen.primed = true;
          return;
        }
        const pulledBack = releases.filter((release) => release.cause === 'pulled-back');
        if (pulledBack.length > 0) {
          const named = new Map((batchRef.current?.documents ?? []).map((item) => [item.id, item]));
          const backDocuments = pulledBack.flatMap((release) =>
            release.documents.map((item) =>
              clearedDocument(
                item.documentId,
                item.batchId === batchRef.current?.batchId ? named.get(item.documentId) : undefined,
              ),
            ),
          );
          backNewsArrivedAt.current = Date.now();
          setBackNews((previous) => addClearedDocuments(previous, backDocuments, 'back'));
          const live = batchRef.current;
          if (live && !structuralMutationRef.current && !wholeBatchMutationRef.current) {
            const openId = live.documents[selectedDocumentRef.current]?.id;
            const back = new Set(
              pulledBack.flatMap((release) => release.documents.map((item) => item.documentId)),
            );
            let changed = false;
            const documents = live.documents.map((item) => {
              if (!back.has(item.id) || item.id === openId || item.needsReview) return item;
              changed = true;
              return { ...item, needsReview: true, needsTeaching: false };
            });
            if (changed) {
              const patched = { ...live, documents };
              batchRef.current = patched;
              setBatch(patched);
              onRestored(patched);
            }
          }
        }
        // Saved Review thresholds released these: the thresholds screen already said so, and
        // nothing was "cleared from what you confirmed".
        const fresh = releases.filter(
          (release) =>
            release.cause !== 'pulled-back' &&
            release.cause !== 'review-settings' &&
            release.documents.some((item) => !item.reopened),
        );
        if (fresh.length === 0) return;
        const current = batchRef.current;
        if (!current) return;
        const released = new Set(
          fresh.flatMap((release) =>
            release.documents
              .filter((item) => !item.reopened && item.batchId === current.batchId)
              .map((item) => item.documentId),
          ),
        );
        const byId = new Map(current.documents.map((item) => [item.id, item]));
        // The AI reviewer's confirmations (ai-review, CapturePoint aa8e8696) leave Needs review like any release,
        // but they are its work, not "cleared from what you confirmed".
        noteCleared(
          fresh
            .filter((release) => release.cause !== 'ai-review')
            .flatMap((release) =>
              release.documents
                .filter((item) => !item.reopened)
                .map((item) =>
                  clearedDocument(
                    item.documentId,
                    item.batchId === current.batchId ? byId.get(item.documentId) : undefined,
                    release.releaseId,
                  ),
                ),
            ),
        );
        if (released.size === 0 || structuralMutationRef.current || wholeBatchMutationRef.current)
          return;
        const index = await api.getBatchDocumentIndex({ jobId: job.id, batchId: current.batchId });
        if (canceled || !mounted.current) return;
        const live = batchRef.current;
        if (
          !live ||
          live.batchId !== current.batchId ||
          structuralMutationRef.current ||
          wholeBatchMutationRef.current
        )
          return;
        const flags = new Map(index.map((item) => [item.id, item]));
        const openId = live.documents[selectedDocumentRef.current]?.id;
        let changed = false;
        const documents = live.documents.map((item) => {
          const truth = flags.get(item.id);
          // The open document is the operator's: its flag catches up when they move on.
          if (
            !released.has(item.id) ||
            item.id === openId ||
            !truth ||
            truth.needsReview ||
            !item.needsReview
          )
            return item;
          changed = true;
          return { ...item, needsReview: false, needsTeaching: truth.needsTeaching };
        });
        if (!changed) return;
        const patched = { ...live, documents };
        batchRef.current = patched;
        setBatch(patched);
        onRestored(patched);
      } catch {
        // A missed poll is caught by the next one.
      } finally {
        running = false;
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 4000);
    return () => {
      canceled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, suspended]);
  /* The engine never changes the verdict of the document the operator has open (held-work
     re-judge, pull-backs): it is told which one that is whenever it changes. */
  const openDocumentId = batch?.documents[selectedDocument]?.id ?? null;
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.noteOpenDocument || suspended) return;
    void api.noteOpenDocument(job.id, openDocumentId).catch(() => {});
    const renew = window.setInterval(
      () => void api.noteOpenDocument(job.id, openDocumentId).catch(() => {}),
      5 * 60_000,
    );
    return () => window.clearInterval(renew);
  }, [job.id, openDocumentId, suspended]);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  /** The cleared news's quiet Undo: puts the engine's recorded releases back in Needs review. */
  const undoClearedNews = async () => {
    const api = window.capturePointDesktop;
    const news = clearedNews;
    if (!api?.reopenHeldWorkRelease || !news || clearedNewsUndoing) return;
    setClearedNewsUndoing(true);
    try {
      let reopened = 0;
      for (const releaseId of clearedReleaseIds(news))
        reopened += await api.reopenHeldWorkRelease(job.id, releaseId);
      if (!mounted.current) return;
      setClearedNews(clearedNewsReopened(news, reopened));
      const current = batchRef.current;
      if (!current) return;
      const index = await api.getBatchDocumentIndex({ jobId: job.id, batchId: current.batchId });
      const live = batchRef.current;
      if (!mounted.current || !live || live.batchId !== current.batchId) return;
      const flags = new Map(index.map((item) => [item.id, item]));
      const patched = {
        ...live,
        documents: live.documents.map((item) => {
          const truth = flags.get(item.id);
          return truth && truth.needsReview !== item.needsReview
            ? { ...item, needsReview: truth.needsReview, needsTeaching: truth.needsTeaching }
            : item;
        }),
      };
      batchRef.current = patched;
      setBatch(patched);
      onRestored(patched);
    } catch (reason) {
      if (mounted.current)
        setError(
          reason instanceof Error
            ? reason.message
            : 'Those documents could not be put back in review',
          'documents-return-failed',
        );
    } finally {
      if (mounted.current) setClearedNewsUndoing(false);
    }
  };
  /* Shared workspace teaching status and age refresh after confirmations.
     These reports still belong to the document workflow even though the
     separate learning-progress dialog has been retired. */
  const learningRefreshOwner = useRef({ jobId: job.id, running: false, dirty: false });
  if (learningRefreshOwner.current.jobId !== job.id)
    learningRefreshOwner.current = { jobId: job.id, running: false, dirty: false };
  const refreshLearningProgress = useCallback(() => {
    const api = window.capturePointDesktop;
    if (!api) return;
    const owner = learningRefreshOwner.current;
    if (owner.jobId !== job.id) return;
    owner.dirty = true;
    if (owner.running) return;
    owner.running = true;
    const current = () => mounted.current && learningRefreshOwner.current === owner;
    // These historical reports are substantial at scale. Coalesce confirmation
    // bursts, but always read again if truth changed during an in-flight read.
    // Never allow an old job's late response to replace the current job's data.
    void (async () => {
      try {
        while (current() && owner.dirty) {
          owner.dirty = false;
          await Promise.all([
            Promise.resolve()
              .then(() => api.getLearningProgress(owner.jobId))
              .then((value) => {
                if (current()) setLearningProgress(value);
              })
              .catch(() => {
                if (current()) setLearningProgress([]);
              }),
            Promise.resolve()
              .then(() => api.getLearningScorecard(owner.jobId))
              .then((value) => {
                if (current()) setScorecard(value);
              })
              .catch(() => {
                if (current()) setScorecard(null);
              }),
            Promise.resolve()
              .then(() => api.getLearningAcceleration(owner.jobId))
              .then((value) => {
                if (current()) {
                  setLearningAcceleration(value);
                  setLearningAccelerationUnavailable(false);
                }
              })
              .catch(() => {
                if (current()) setLearningAccelerationUnavailable(true);
              }),
          ]);
        }
      } finally {
        owner.running = false;
      }
    })();
  }, [job.id]);
  useEffect(() => {
    setLearningAcceleration(null);
    setLearningAccelerationUnavailable(false);
  }, [job.id]);
  useEffect(refreshLearningProgress, [
    refreshLearningProgress,
    batch?.batchId,
    batch?.documents.length,
  ]);
  // Deliberately event-driven. This report includes historical provenance and
  // must never become a two-second full-document status scan while the CPU is
  // already reading pages. Batch arrivals and confirmations refresh it above;
  // the wave catch-up receipt refreshes naturally with the next committed wave.
  // Measured learning outcomes remain in the job's status bar until replaced
  // or dismissed. A timer must not make the result vanish before it is read.
  useEffect(() => {
    const saved = readReviewLayout(job.id);
    setSpineWidth(saved.spineWidth);
    setResultsWidth(saved.resultsWidth);
    setLineItemsMode(saved.lineItemsMode);
    const preferred = new Set(saved.detachedPanels);
    setDetachedPanels({
      viewer: preferred.has('viewer'),
      fields: preferred.has('fields'),
      'line-items': preferred.has('line-items'),
    });
  }, [job.id]);
  useEffect(() => {
    const detached = (Object.entries(detachedPanels) as Array<[ReviewPanelKind, boolean]>)
      .filter(([, value]) => value)
      .map(([panel]) => panel);
    localStorage.setItem(
      reviewLayoutKey(job.id),
      JSON.stringify({
        resultsWidth: Math.round(resultsWidth),
        spineWidth: Math.round(spineWidth),
        lineItemsMode,
        lineItemsHeight: Math.round(lineItemsHeight),
        detachedPanels: detached,
      } satisfies JobReviewLayout),
    );
  }, [job.id, resultsWidth, spineWidth, lineItemsMode, lineItemsHeight, detachedPanels]);
  useEffect(() => {
    window.document
      .querySelector<HTMLElement>(
        `[data-document-index="${selectedDocument}"][data-page-index="${selectedPage}"]`,
      )
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedDocument, selectedPage]);
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const smoothZoom = pointerZoomGesture(
      viewer,
      () => ({ zoom: zoomRef.current, ...panRef.current }),
      (view) => {
        const stack = pageStackRef.current;
        if (!stack) return;
        stack.style.transition = 'none';
        stack.style.transform = `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${view.zoom})${viewerTurnRef.current}`;
      },
      (view) => {
        zoomRef.current = view.zoom;
        panRef.current = { x: view.x, y: view.y };
        const fit = fitZoomFor(previewSizeRef.current);
        if (fit > 0) viewScale.current = view.zoom / fit;
        setPan(panRef.current);
        setZoom(view.zoom);
        if (pageStackRef.current) pageStackRef.current.style.transition = '';
      },
    );
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const zoomAtPointer = (event: WheelEvent) => {
      // Reading mode: the wheel is for scrolling, exactly like a document
      // reader. Zoom stays on Ctrl+wheel, and the scroll position is kept so
      // the paper under the pointer stays put while it grows.
      if (readerMode) {
        if (!event.ctrlKey) return;
        event.preventDefault();
        const flow = viewer.querySelector<HTMLElement>('.reader-flow');
        if (!flow) return;
        const pending = readerWheelZoom.current ?? {
          startScale: readerScaleRef.current,
          latestScale: readerScaleRef.current,
          anchorTop: event.clientY,
          anchorLeft: event.clientX,
          timer: 0,
        };
        pending.latestScale = Math.max(
          0.35,
          Math.min(3, pending.latestScale * Math.exp(-event.deltaY * 0.0015)),
        );
        const box = viewer.getBoundingClientRect();
        flow.style.transformOrigin = `${viewer.scrollLeft + (pending.anchorLeft - box.left)}px ${viewer.scrollTop + (pending.anchorTop - box.top)}px`;
        flow.style.transform = `scale(${pending.latestScale / pending.startScale})`;
        window.clearTimeout(pending.timer);
        pending.timer = window.setTimeout(() => {
          const gesture = readerWheelZoom.current;
          readerWheelZoom.current = null;
          if (!gesture) return;
          flow.style.transform = '';
          flow.style.transformOrigin = '';
          const ratio = gesture.latestScale / gesture.startScale;
          const rect = viewer.getBoundingClientRect();
          const anchorTop = gesture.anchorTop - rect.top;
          const anchorLeft = gesture.anchorLeft - rect.left;
          const scrollTop = viewer.scrollTop;
          const scrollLeft = viewer.scrollLeft;
          setReaderScale(gesture.latestScale);
          window.requestAnimationFrame(() => {
            viewer.scrollTop = (scrollTop + anchorTop) * ratio - anchorTop;
            viewer.scrollLeft = (scrollLeft + anchorLeft) * ratio - anchorLeft;
          });
        }, 140);
        readerWheelZoom.current = pending;
        return;
      }
      // The operator has taken the view over; a refit must keep what they chose,
      // not travel back to the last piece of evidence.
      appliedEvidenceFocus.current = null;
      // Record it against fit like every other zoom gesture. Without this the
      // wheel — the way an operator actually zooms — was the one gesture whose
      // magnification did not survive stepping to the next document.
      pendingEvidenceFocus.current = null;
      setEasingView(false);
      smoothZoom.wheel(event);
    };
    viewer.addEventListener('wheel', zoomAtPointer, { passive: false });
    return () => {
      viewer.removeEventListener('wheel', zoomAtPointer);
      smoothZoom.dispose();
    };
  }, [document?.id, selectedPage, zoom, detachedPanels.viewer, readerMode]);
  // Reading mode keeps "current page" meaning the page under the middle of
  // the viewer, so the spine, the fields panel, and the page counter follow
  // the operator's scrolling.
  useEffect(() => {
    if (!readerMode) return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    const state = readerSelectionSync.current;
    const sync = (revision: number) => {
      state.frame = 0;
      if (state.navigationPending || revision !== state.revision) return;
      const middle = viewer.getBoundingClientRect().top + viewer.clientHeight / 2;
      const nearest = nearestReaderPage(
        viewer.querySelectorAll<HTMLElement>('[data-reader-index]'),
        middle,
        (element) => element.getBoundingClientRect(),
      );
      const best =
        virtualReaderPageAt(viewer, viewer.scrollTop + viewer.clientHeight / 2) ??
        (nearest ? Number(nearest.dataset.readerIndex) : -1);
      if (best >= 0 && best !== selectedPageIntent.current) {
        state.scrollSelection = { documentId: document?.id, pageIndex: best };
        selectedPageIntent.current = best;
        commitSelectedPage(best);
      }
    };
    const onScroll = () => {
      if (state.navigationPending) return;
      const programmed = state.programmedPosition;
      if (
        programmed?.viewer === viewer &&
        programmed.top === viewer.scrollTop &&
        programmed.left === viewer.scrollLeft
      )
        return;
      state.programmedPosition = null;
      if (!state.frame) {
        const revision = state.revision;
        state.frame = window.requestAnimationFrame(() => sync(revision));
      }
    };
    viewer.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      viewer.removeEventListener('scroll', onScroll);
      if (state.frame) window.cancelAnimationFrame(state.frame);
      state.frame = 0;
    };
  }, [readerMode, document?.id, detachedPanels.viewer]);
  // The other direction: an arrow key, a spine click, or evidence on another
  // page owns its destination. Only the exact selection produced by scrolling
  // skips this movement; elapsed time cannot identify the operator's intent.
  // The page shown above page 1 changing (a Restore or a join putting another document before this one) moves
  // this document down in the reader, so it is placed again.
  const documentAbove = selectedDocument > 0 ? batch?.documents[selectedDocument - 1] : undefined;
  const readerPageAbove = documentAbove
    ? `${documentAbove.id}:${documentAbove.pages[documentAbove.pages.length - 1]?.number ?? ''}`
    : '';
  useLayoutEffect(() => {
    const state = readerSelectionSync.current;
    state.navigationPending = false;
    const scrollSelection = state.scrollSelection;
    state.scrollSelection = null;
    if (
      !readerMode ||
      (scrollSelection?.documentId === document?.id && scrollSelection?.pageIndex === selectedPage)
    )
      return;
    const viewer = viewerRef.current;
    const target = viewer?.querySelector<HTMLElement>(`[data-reader-index="${selectedPage}"]`);
    if (!viewer || !target) return;
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.frame = 0;
    // Page 1 opens with the last inch of the page before peeking in above it (owner, 2026-10-30).
    if (!(selectedPage === 0 && scrollToNeighbourSeam(viewer)))
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
    state.programmedPosition = { viewer, top: viewer.scrollTop, left: viewer.scrollLeft };
  }, [
    readerMode,
    selectedPage,
    pageNavigationRevision,
    document?.id,
    detachedPanels.viewer,
    readerPageAbove,
  ]);

  useEffect(() => {
    if (!zoomMenuOpen) return;
    const close = (event: PointerEvent) => {
      if ((event.target as Element | null)?.closest('.zoom-control')) return;
      setZoomMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setZoomMenuOpen(false);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [zoomMenuOpen]);
  // Clicking a review marker names a field; land the operator on it. The row has
  // to exist first, which it will not until the document it belongs to is open.
  useEffect(() => {
    if (!focusFieldId) return;
    if (
      !document ||
      (batch?.workspaceProjection === 'paged' && !loadedDocumentIds.has(document.id))
    )
      return;
    paneLayout.revealPane('fields');
    const frame = window.requestAnimationFrame(() => {
      const row = window.document.querySelector<HTMLElement>(
        `label[data-field-id="${CSS.escape(focusFieldId)}"]`,
      );
      if (!row && !detachedPanels.fields) return;
      const configured = configuredFields.find((field) => field.id === focusFieldId);
      const source = document.fields.find((field) => field.name === configured?.name);
      const recognized = source ? numericFieldDisplayEvidence(source, document.pages) : undefined;
      setArrivedFieldId(focusFieldId);
      setImageSelectedCell(null);
      setActiveLineItemRow(null);
      setImageSelectionSerial((serial) => serial + 1);
      const pageNumber = recognized?.evidencePageNumber ?? document.pages[0]?.number;
      if (pageNumber != null) {
        setActiveEvidenceTarget({ documentId: document.id, pageNumber });
        const lines = recognized?.evidenceLineIndexes ?? [];
        setActiveEvidenceLines(lines);
        citeActiveEvidence(lines, recognized);
        setActiveBarcodeEvidence(recognized?.barcodeEvidence ?? null);
        const pageIndex = document.pages.findIndex((page) => page.number === pageNumber);
        if (pageIndex >= 0) setSelectedPage(pageIndex);
      }
      row?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      const editor = row?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
      editor?.focus({ preventScroll: true });
      editor?.select();
      setFocusFieldId(null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    focusFieldId,
    selectedDocument,
    loadedDocumentIds,
    detachedPanels.fields,
    document?.id,
    paneLayout.revealPane,
  ]);
  // Put the caret in the type control the moment a split lands on its document.
  useEffect(() => {
    if (!typePromptDocumentId || typePromptDocumentId !== document?.id) return;
    paneLayout.revealPane('fields');
    const frame = window.requestAnimationFrame(() => documentTypeSelectRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [typePromptDocumentId, document?.id, paneLayout.revealPane]);
  // The same landing, for the reasons that are about the type rather than a
  // field. "No document type chosen" was the commonest reason of all and the
  // review marker did nothing at all when it was showing — the reason named the
  // control to fix and then would not take the operator to it.
  useEffect(() => {
    if (!focusTypeControl) return;
    paneLayout.revealPane('fields');
    const frame = window.requestAnimationFrame(() => {
      const control = documentTypeSelectRef.current;
      if (!control) return;
      control.scrollIntoView({ block: 'center', behavior: 'smooth' });
      control.focus();
      setFocusTypeControl(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusTypeControl, selectedDocument, loadedDocumentIds, paneLayout.revealPane]);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    previewSizeRef.current = previewSize;
  }, [previewSize]);
  /* The whole-batch tallies, recounted only when the batch itself changes.
     Three separate full passes over the documents ran on every render — and at
     ten-thousand-document scale "every render" includes every zoom tick, menu
     open and panel click, none of which moves a document between ready and
     review. The batch is state: its identity changes exactly when a document
     changes, so that identity is the honest cache key. Above the early return
     on purpose — a hook below it runs on some renders and not others, which is
     React error #310 and a blank window. */
  // Autopilot with the AI check: a document the AI reviewer has not finished with is its work, not a person's (Dustin, 2026-10-10).
  const assistedReview =
    effectiveProcessingMode(job.reviewPolicy?.processingMode, useAiReviewAvailable()) ===
    'ai-reviewed';
  const documentCensus = useMemo(() => {
    const stillReading = Boolean(captureProcessingId);
    let aiReviewing = 0;
    let pending = 0;
    let ready = 0;
    let verified = 0;
    let pages = 0;
    const reviewIndexes: number[] = [];
    const teachIndexes: number[] = [];
    const sourcePaths = new Set<string>();
    (batch?.documents ?? []).forEach((item, index) => {
      if (isAwaitingUnderstanding(item, stillReading)) pending += 1;
      // A waiting document is review work even when it is also a teaching example (documentWorkKind).
      else if (item.needsReview && assistedReview && awaitsAiReviewer(item)) aiReviewing += 1;
      else if (item.needsReview) reviewIndexes.push(index);
      else {
        ready += 1;
        // Teach offers released examples only, as optional extras (owner decision, 2026-10-03).
        if (item.needsTeaching) teachIndexes.push(index);
      }
      if (item.documentTypeVerified) verified += 1;
      pages += item.pages.length;
      for (const path of item.sourcePaths?.length ? item.sourcePaths : [item.sourcePath])
        if (path) sourcePaths.add(path);
    });
    return {
      pending,
      ready,
      verified,
      pages,
      reviewIndexes,
      teachIndexes,
      aiReviewing,
      sourceFiles: sourcePaths.size,
    };
  }, [batch, captureProcessingId, assistedReview]);
  // This is a job-learning age, not a count of documents still on screen.
  // Exported documents leave the active batch but remain ground truth, so the
  // durable type-answer ledger must keep the first-document language from
  // returning every time an operator clears the workspace.
  const confirmedLearningDocuments = Math.max(
    documentCensus.verified,
    scorecard?.verifiedTypeCount ?? 0,
  );
  const learningLanguage = captureLearningLanguage(
    job.bootstrapMode === 'content-central',
    confirmedLearningDocuments,
  );
  /* The spine's layout table for the every-document view, built when the batch
     changes rather than per render. Row heights are a fixed function of each
     document's page count, so nothing but the batch can move a row — yet the
     table was rebuilt (two allocations per document) on every render of the
     workspace. The review-filtered view stays derived inline below: it is as
     long as the review queue, not the batch. */
  const allSpineMetrics = useMemo(() => {
    let offset = 0;
    return (batch?.documents ?? []).map((item, index) => {
      const height = spineGroupHeight(item.pages.length, spinePageRow);
      const metric = { item, documentIndex: index, top: offset, height };
      offset += height;
      return metric;
    });
  }, [batch, spinePageRow]);
  /* Above the early return, and it must stay there.
     This lives in a component that returns before a batch has loaded, so a hook
     placed after that return runs on some renders and not others. React counts
     them: the first render of a job has no document and stops early, the render
     after the batch arrives reaches this — "Rendered more hooks than during the
     previous render", and the job opens to a blank screen. It sat below the
     return for a long time and only fired once something else changed when the
     renders happened. */
  useEffect(() => {
    // Another document is another job of work; carrying the mark across would
    // put it on a field the operator never asked to be taken to.
    setArrivedFieldId(null);
    setArrivedLineRow(null);
    setArrivedBoundary(null);
    // An unanswered "Remove page 3?" belongs to the document it was asked on.
    setConfirmingPageRemoval(null);
  }, [document?.id]);
  // Resolve history only after the destination is hydrated. A user who leaves
  // before it arrives must never be focused into a different document's field.
  useEffect(() => {
    if (!correctionArrival) return;
    if (document?.id !== correctionArrival.documentId) {
      setCorrectionArrival(null);
      return;
    }
    if (batch?.workspaceProjection === 'paged' && !loadedDocumentIds.has(document.id)) return;
    const field = configuredFields.find(
      (field) =>
        field.name.localeCompare(correctionArrival.fieldName, undefined, {
          sensitivity: 'accent',
        }) === 0,
    );
    if (field) setFocusFieldId(field.id);
    else if (
      correctionArrival.kind === 'document-type-correction' ||
      correctionArrival.kind === 'document-type-verified'
    )
      setFocusTypeControl(true);
    else if (
      correctionArrival.kind === 'document-confirmed' ||
      correctionArrival.kind === 'line-items-verified'
    ) {
      /* The document itself is the history destination. */
    } else
      setError(
        `The field “${correctionArrival.fieldName}” is no longer in this document's setup.`,
        'field-gone',
      );
    setCorrectionArrival(null);
  }, [correctionArrival, document?.id, loadedDocumentIds, configuredFields]);
  // Product telemetry (ADR-0081): the work screen's part of the area in front. Each view below says what it shows as
  // it renders; this passes it on after each render (an unchanged answer costs a comparison).
  const telemetryCapture = useRef<{
    capture: TelemetryAreaParts['capture'] | null;
    documents?: TelemetryAreaParts['documents'];
  }>({ capture: null });
  useEffect(() => {
    reportAreaPart('capture', telemetryCapture.current.capture);
    if (telemetryCapture.current.documents)
      reportAreaPart('documents', telemetryCapture.current.documents);
  });
  useEffect(
    () => () => {
      reportAreaPart('capture', null);
      reportAreaPart('documents', null);
    },
    [],
  );
  useTelemetryArea('tour', touring);
  useTelemetryArea('breakSheets', breakSheetsOpen);
  // Turn page: a page whose turn the engine has saved and whose new image is the one on screen
  // (or that is not on screen) is no longer drawn turned by the renderer. Every hook of this
  // component runs before the no-document return below (React error #310 otherwise).
  const turnSlotOnScreen = document
    ? pageTurnKey(document.id, document.pages[selectedPage]?.number ?? -1)
    : null;
  useEffect(() => {
    let changed = false;
    for (const [key, entry] of pageTurnsRef.current) {
      const onScreen = key === turnSlotOnScreen;
      if (
        !pageTurnSettled(entry, onScreen ? previewImagePath : null, onScreen ? previewPath : null)
      )
        continue;
      pageTurnsRef.current.delete(key);
      changed = true;
    }
    if (changed) setPageTurns(new Map(pageTurnsRef.current));
  }, [pageTurns, turnSlotOnScreen, previewImagePath, previewPath]);
  // THE FIRST-RUN SCREEN OUTLIVES THE FIRST DOCUMENT.
  //
  // The discovery workbench was gated on `!document` alone, so the moment the
  // first document landed in the batch and got selected, the whole branch was
  // skipped and review took over — while understanding kept running for minutes
  // and the types kept arriving. Measured live at the understanding stage:
  // engineStage "understanding", the panel gate true, a real type name already
  // recorded ("Bill of Lading"), and .learning-intelligence not in the DOM at
  // all. The gate inside the branch could never fire because the branch above it
  // never ran.
  //
  // What the operator got instead was a half-read document, a green "Everything
  // here is right" over an orange "still flagged", and no sight of the types
  // being found — as the FIRST thing a new job shows them.
  //
  // So the workbench holds while the engine is still understanding this job. It
  // ends when the stage does: "finalizing" and "complete" both follow it.
  // Scoped to the job's FIRST pass. On an established job the operator wants
  // review the moment a document is ready, and nothing here should delay it.
  // This is about status, not about what has been learned — gating on the type
  // count is the trap described above.

  // Operational scans (including the simulator) stay in document review once
  // their durable wave is available. Schema discovery must not navigate away
  // from the operator's workspace. Representative setup keeps its separate
  // proof-gated completion path; manual setup remains an explicit action.
  // The first operational wave is intentionally small on a processor-only
  // workstation. Once that wave and its proposed schema are durable, Review is
  // the useful screen: the operator can start supplying ground truth while the
  // remaining processor-paced waves continue one at a time. A transient live
  // preview is not editable authority, so it keeps the discovery workbench.
  const durableReviewReady = Boolean(
    batch?.batchId && !batch.batchId.startsWith('live:') && job.documentTypes.length > 0,
  );
  const stillDiscovering =
    engineUnderstanding && job.status === 'provisional' && !durableReviewReady;
  // BEFORE THE SETUP IS SAVED, THIS JOB HAS NO SCHEMA TO JUDGE AGAINST.
  //
  // Distinct from the mount gate above, which must NOT look at the type count.
  // Here the count is the actual subject: a provisional job with no saved types
  // is one whose setup has not been saved yet, and until it is, every reviewer
  // question ("required field empty?", "type chosen?") is asked against a schema
  // that does not exist. The reader has proposed a type and read its fields, and
  // the header beside the document already says both.
  const beforeSetupSaved = job.status === 'provisional' && job.documentTypes.length === 0;
  // FEEDING PAPER NEEDS NO PROCESSING SLOT (UI bug fix, for Brandy's review).
  //
  // A job with no documents yet whose reading was queued behind another job, or
  // paused, or still reading its first pages, showed no way to add pages at all:
  // an operator who scanned into job B while job A was reading could not scan
  // more into B until A finished, hours with a large backlog (TWAIN soak,
  // 2026-10-04). The job's ordinary page source and Add pages stay, under the
  // status; the new pages queue behind the ones already waiting.
  const keepFeedingPaper = job.documentTypes.length > 0 && !stillDiscovering;
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const feedMorePaper = (withStop: boolean) => (
    <div className="acquisition-control">
      <label>
        <span>{sourceIcon(18)}</span>
        <select
          value={sourceKey}
          onChange={(event) => chooseSource(event.target.value)}
          aria-label="Page source"
        >
          <option value="folder">Folder (including subfolders)</option>
          <option value="files">Files</option>
          {scanners?.sources.map((source, index) => (
            <option value={`scanner:${index}`} key={`${source.hostArchitecture}-${source.name}`}>
              {source.name}
            </option>
          ))}
          {simulatorTray && <option value="simulator">Simulated scanner</option>}
        </select>
      </label>
      <div className="capture-source-actions">
        <button
          className="secondary-button"
          onClick={() => void acquireDocuments()}
          disabled={acquiring}
        >
          {acquiring ? <LoaderCircle className="spin" size={18} /> : sourceIcon(18)}
          {acquiring ? `${acquisitionWorkingVerb}…` : 'Add more pages'}
        </button>
        {withStop &&
          acquiring &&
          sourceKey !== 'files' &&
          sourceKey !== 'folder' &&
          sourceKey !== 'simulator' && (
            <button
              className="secondary-button acquisition-stop"
              onClick={() => void stopAcquiring()}
            >
              <Square size={14} /> Stop
            </button>
          )}
      </div>
    </div>
  );
  // Trial telemetry: the document open as work (each new one counts as opened) and what waits in Needs review.
  // Counters in memory only; nothing is sent from here (review-telemetry.ts).
  reviewTally.shown(job.id, document && !stillDiscovering ? document.id : null);
  reviewTally.waiting(job.id, documentCensus.reviewIndexes.length);
  /* The Needs review number, held steady around the person's confirmations (owner,
     2026-10-10: 50 waiting, confirm one, and the tab said 83, then 65, then 49). A lower count ticks
     down to at once; a higher one after a confirmation shows only once it has settled, so a pass
     that briefly raises flags is never drawn (steady-count.ts). Above the early return: hooks. */
  const countScope = `${job.id}:${batch?.batchId ?? ''}`;
  const countBusy = confirmingDocumentIds.size > 0 || applyingLearning;
  const reviewSteady = useSteadyCount(
    documentCensus.reviewIndexes.length,
    countScope,
    confirmationSerial,
    countBusy,
    backNews !== null && Date.now() - backNewsArrivedAt.current < 10_000,
  );
  // The page's on-screen turn, for the drag and wheel writers that set the page's transform
  // directly between renders (set below with viewerTurn); without it a turned page snaps upright.
  const viewerTurnRef = useRef('');
  // The review screen's own keys act only while it shows (set again below once it does).
  searchShortcutRef.current = () => undefined;
  reviewItemShortcutRef.current = () => undefined;
  // With no document left on screen (the last one deleted), Ctrl+Z still takes that delete back while its Undo shows.
  undoShortcutRef.current = (fromTextBox = false) => {
    const undo = fromTextBox
      ? null
      : lastChange.undoable(
          (change) => change.kind === 'delete' && workspaceNotice.action === change.entry,
        );
    if (undo?.kind !== 'delete') return false;
    undo.entry.run();
    return true;
  };
  /** Restore in the Deleted view: a document of the batch on screen comes back into it, read again from the store
      the way the workspace opens (only the open document read whole), never the whole batch. */
  const documentRestored = async (restored: DeletedDocument) => {
    onNotice(`Restored ${restored.sourceName}`, 'notice');
    const showing = batchRef.current ?? batch;
    if (!showing || showing.batchId !== restored.batchId) return;
    const refreshed = await loadLatestWorkspaceBatch(
      job.id,
      showing.documents[selectedDocumentRef.current]?.id,
    ).catch(() => null);
    if (refreshed && mounted.current && refreshed.batchId === (batchRef.current ?? batch)?.batchId)
      onRestored(refreshed);
  };
  // The job's Deleted view, from the documents list's More menu or, with no document left to list, the empty
  // workspace. Above the early return, and the first child of both screens, so a Restore that brings the first
  // document back keeps the same window open.
  const deletedDialog = deletedOpen && (
    <DeletedDocumentsDialog
      jobId={job.id}
      onClose={() => setDeletedOpen(false)}
      onRestored={(restored) => void documentRestored(restored)}
      onAlreadyRestored={() => onNotice('That document was already restored.', 'notice')}
    />
  );
  if (!document || stillDiscovering) {
    if (processingState && (processingState.pauseRequested || processingState.status === 'queued'))
      telemetryCapture.current = {
        capture: job.status === 'provisional' ? 'setup-wait' : 'reading',
      };
    if (processingState && (processingState.pauseRequested || processingState.status === 'queued'))
      return (
        <div className="capture-view capture-empty">
          <JobProcessingButton jobId={job.id} />
          <HeldSetupProgress
            state={processingState}
            preview={learningPreview}
            pageCount={
              operationalCaptureActive
                ? (folderImport?.found.pages ?? captureProgress?.pageCount)
                : folderAnalysisProgress?.pageCount
            }
            pageScope={operationalCaptureActive ? 'wave' : 'sample'}
          />
          {keepFeedingPaper && feedMorePaper(true)}
          {keepFeedingPaper && acquisitionNotice && (
            <div
              key={acquisitionNotice}
              className="acquisition-notice"
              role="status"
              data-telemetry={
                acquisitionNotice === SCANNER_NO_PAGES
                  ? 'error:source-no-pages'
                  : acquisitionNotice === FOLDER_NO_READABLE_PAGES
                    ? 'error:source-empty-folder'
                    : undefined
              }
            >
              <Info size={13} />
              <span>{acquisitionNotice}</span>
              <button onClick={() => setAcquisitionNotice(null)} aria-label="Dismiss this notice">
                <X size={12} />
              </button>
            </div>
          )}
          {keepFeedingPaper && capturePanels}
        </div>
      );
    const representativeStatusFailure =
      folderAnalysisStatus?.state === 'failed'
        ? (folderAnalysisStatus.failure ??
          'Document setup analysis stopped before it could publish a proposal.')
        : folderAnalysisStatus?.state === 'no-proposal'
          ? 'Paige finished reading these documents but could not identify a reusable document-type and field setup. Choose a more varied set, or start scanning and teach this job as you work.'
          : null;
    const interruptedRepresentativeLearning =
      usesRepresentativeBootstrap(job) &&
      job.status === 'provisional' &&
      job.documentTypes.length === 0 &&
      !savedBatch &&
      !externalActivity &&
      folderAnalysisStatus?.state === 'idle' &&
      !folderRecoveryActive;
    // A general error stops the screen only while the setup it would retry is still pending.
    // On any other job (an unsupported file picked, a queue change refused) it is shown in the
    // normal screen's status row below: this screen's buttons would reset the job's setup.
    const setupError =
      usesRepresentativeBootstrap(job) &&
      job.status === 'provisional' &&
      job.documentTypes.length === 0
        ? error
        : null;
    if (
      (externalFailure ||
        representativeStatusFailure ||
        folderRecoveryError ||
        setupError ||
        interruptedRepresentativeLearning) &&
      !externalActivity &&
      !folderRecoveryActive
    ) {
      telemetryCapture.current = { capture: 'setup-wait' };
      const canResumeRepresentativeAnalysis =
        folderAnalysisStatus?.state === 'recoverable' && Boolean(folderAnalysisStatus.operationId);
      // What the job learned from, never the Add pages source (which defaults to folder): the
      // label and both retry paths open the same picker retryFolderLearning would.
      const failedSource: 'files' | 'folder' =
        job.bootstrapMode === 'sample-scan' ? 'files' : 'folder';
      // PAIGE: the tour's start window says its reading failed (paige/TourStartFailed.tsx), and is
      // the one message: the job's own failure screen behind it said the same thing twice.
      if (touring && !tour?.own && !tour?.ready)
        return (
          <div className="capture-view capture-empty">
            <TourReadingFailed jobId={job.id} />
          </div>
        );
      const shownSetupFailure =
        externalFailure || representativeStatusFailure || folderRecoveryError || setupError;
      return (
        <div className="capture-view capture-empty bootstrap-failed">
          <JobProcessingButton jobId={job.id} />
          <section
            role="alert"
            data-telemetry={
              !shownSetupFailure
                ? 'error:setup-interrupted'
                : stoppedByPerson(shownSetupFailure)
                  ? 'notice'
                  : setupFailureCategory(shownSetupFailure) === 'unsupported'
                    ? 'error:source-unsupported-files'
                    : folderRecoveryError
                      ? 'error:setup-resume-failed'
                      : 'error:setup-failed'
            }
          >
            <span>
              <TriangleAlert size={28} />
            </span>
            <div className="eyebrow">Setup analysis stopped</div>
            <h2>No work is running.</h2>
            <p>
              {externalFailure ||
                representativeStatusFailure ||
                folderRecoveryError ||
                setupError ||
                'Paige was closed or interrupted before this job finished learning from its documents.'}
            </p>
            <small>Your source documents were not changed. This job is still available.</small>
            <div>
              <button
                className="primary-button"
                onClick={() => {
                  onDismissExternalFailure();
                  setError(null);
                  void (canResumeRepresentativeAnalysis
                    ? retryFolderLearning()
                    : externalFailure
                      ? onChooseRepresentativeSource(job, failedSource)
                      : retryFolderLearning());
                }}
              >
                <RotateCw size={15} />{' '}
                {canResumeRepresentativeAnalysis
                  ? 'Resume saved analysis'
                  : failedSource === 'files'
                    ? 'Choose files and retry'
                    : 'Choose folder and retry'}
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  onDismissExternalFailure();
                  setError(null);
                  void continueWithoutFolderLearning();
                }}
              >
                <ScanLine size={15} /> Start scanning instead
              </button>
            </div>
          </section>
        </div>
      );
    }
    const learningActivity =
      externalActivity ??
      (folderAnalysisStatus === undefined && usesRepresentativeBootstrap(job)
        ? 'Checking setup analysis'
        : folderAnalysisStatus?.running
          ? 'Learning from representative documents'
          : folderRecoveryActive
            ? 'Learning from representative documents'
            : undefined);
    // The first pass on a job that has learned nothing yet IS the setup, and it
    // looks exactly like ordinary scanning from the outside. An expert operator
    // could not tell what he was waiting on here, so the copy has to say it: this
    // happens once, and it is building the job rather than just reading pages.
    const buildingTheJob =
      Boolean(captureProcessingId || rejoinedShown) &&
      job.status === 'provisional' &&
      job.documentTypes.length === 0 &&
      !usesRepresentativeBootstrap(job);
    const activeRepresentativeImport = !learningActivity && operationalRepresentativeActive;
    // Drives the workbench itself. Representative acceptance stays distinct
    // from an ordinary scan so neither a folder nor selected files are described
    // as freshly scanned pages.
    const activeCapture = !learningActivity && displayCaptureActive;
    // THE SCREEN CALLED "DISCOVERING TYPES AND FIELDS" MUST OUTLIVE THE WAVE.
    //
    // The workbench was mounted on the capture wave alone, and the wave resolves
    // when the engine hands back the batch — which is BEFORE the understanding
    // stage has named anything. Measured on a 120-page bootstrap: the panel was
    // torn down at t=330s and the first type-bearing event arrived at t=330s.
    // Its entire visible life was the provisioning wait, so the screen promising
    // discovery was guaranteed to show none of it.
    //
    // Held open only while the engine says it is still understanding, on a job
    // that has learned nothing yet. "finalizing" and "complete" both follow that
    // stage, so this cannot hold the screen open on a batch that has finished.
    // NOT gated on the job having learned nothing. That is the trap the old
    // buildingTheJob flag fell into and I repeated once here: a screen whose
    // subject is the types it is discovering cannot be conditioned on there
    // being none, or it closes at the exact instant it has something to say.
    // Measured: it unmounted at t=370s, which is when the first name arrived.
    const displayedProgress = activeCapture
      ? (captureProgress ?? (rejoinedShown ? (openingProgress ?? undefined) : undefined))
      : folderAnalysisProgress;
    const displayedActivity = activeCapture ? captureActivity : folderActivity;
    // The step list, count, live line and facts all come from the one working
    // state; the stage there never moves backwards within a run.
    const stepReadout = workingReadout(workingState, workingNow, {
      // PAIGE: scanned or uploaded pages are pages, not setup samples (journey 1, 2026-10-04).
      unit: {
        preparing: activeCapture
          ? 'pages prepared'
          : activeRepresentativeImport
            ? 'files prepared'
            : 'samples prepared',
      },
    });
    // A folder's waves are counted as the folder; a setup step keeps its own count.
    const folderShown =
      activeCapture && folderImport && !describeWorking(workingState).stepId ? folderImport : null;
    const working = folderShown ? folderImportReadout(stepReadout, folderShown) : stepReadout;
    const progressStage = displayedProgress || workingState.rank >= 0 ? working.stageId : undefined;
    // Which journey the one stage list is narrating. The words themselves live
    // in captureStages — one column per journey — so this strip's chips, the
    // queue's chips and the headline can no longer disagree about what the
    // same machinery is called.
    const stageContext: CaptureStageContext = {
      folder: activeRepresentativeImport,
      capture: activeCapture,
      // The representative-documents pass (learningActivity) also ends in a
      // proposed job, so it keeps the proposing words.
      proposing: buildingTheJob || Boolean(learningActivity),
    };
    const activeTitle = progressStage
      ? captureStages.find((stage) => stage.id === progressStage)?.title(stageContext)
      : learningActivity;
    // The documents a job's setup just staged: the "Processing N documents" phase.
    const afterSetup = activeCapture && !stageContext.proposing ? setupHandoff : null;
    // ONE JOURNEY: setup ended with every step done, so the import it starts keeps them done.
    // Its own steps began again at Prepare, and the operator watched the wizard go back after
    // ten minutes of setup (owner, 2026-10-03: "the user should never see the wizard go
    // backwards"). The whole-folder count and bar carry the import's progress; the live line
    // still says what is being done.
    const shownStage = afterSetup ? 'complete' : (progressStage ?? 'preparing');
    /**
     * A failed capture is not an empty job.
     *
     * Opening a job whose only acquisition failed is correct in its ROUTE — there
     * is no committed batch, so `decideOpeningScreen` sends the operator to
     * intake, and there is nowhere better to send them. It was wrong in its
     * HIERARCHY: the headline said "Feed it pages." over 100 pages that had
     * already been fed and had failed, and the only sign of that was a small grey
     * "Needs retry" line BELOW the Add pages button. The biggest words on the
     * screen described the least true thing on it.
     *
     * So when nothing is running and pages are waiting on a retry, the headline
     * says what happened and how many, and Retry becomes the primary action.
     * Nothing is rerouted: the operator is still on intake, and Add pages is
     * still there, one step down.
     */
    // Files a host holds for pages (credit) are waiting, not stalled.
    const stalledCaptures = captureQueue.filter(
      (item) => item.status === 'failed' && !item.waitingForPages,
    );
    const stalledFiles = stalledCaptures.reduce((sum, item) => sum + item.fileCount, 0);
    const stalled =
      !learningActivity &&
      !activeCapture &&
      !activeRepresentativeImport &&
      stalledCaptures.length > 0
        ? {
            captures: stalledCaptures,
            files: stalledFiles,
            reason: stalledCaptures[0].error ?? 'Processing was interrupted',
            // The most-tried capture speaks for the group. A wave that has been
            // through this several times is a different situation from one that
            // has just arrived here, and until attempts were counted the screen
            // showed the same words for both.
            attempts: Math.max(...stalledCaptures.map((item) => item.attempts ?? 0), 0),
            // Whether the engine was given everything a restart can take and did
            // not come back. It is the only thing on this screen that can honestly
            // carry "retrying is unlikely to help": a count cannot, because the
            // same count means half a minute in one build and nine minutes in
            // another.
            patienceSpent: engineOutageNow()?.spent === true,
          }
        : null;
    // The same three-sample floor the queue's estimate obeys. This header
    // extrapolated from ONE completed item, and the first item of a stage pays
    // for warm-up — so the number it planned an operator's next ten minutes
    // around could be five times the truth. Below the floor the label already
    // says "Calculating", which is the honest sentence.
    const elapsedMilliseconds = Math.max(
      displayedProgress?.elapsedMilliseconds ?? 0,
      learningClock - learningStartedAt.current,
    );
    const shownRemaining =
      working.remainingMilliseconds ?? remainingMilliseconds(displayedProgress);
    const discoveredDocuments = displayedWork
      .filter((item): item is FolderWorkTask & { document: DocumentResult } =>
        Boolean(item.document),
      )
      .map((item) => item.document);
    /** Kinds the engine has named in progress, whether or not a document has completed. */
    const reportedTypeNames =
      seenTypeNames.length > 0
        ? seenTypeNames
        : (displayedProgress?.documentTypes ?? [])
            .map((name) => name.trim())
            .filter((name) => name.length > 0 && !DocumentStoreIsNonAnswer(name));
    const discoveredTypeCards = [
      ...new Set(discoveredDocuments.map((item) => item.documentType)),
    ].map((name) => {
      const documents = discoveredDocuments.filter((item) => item.documentType === name);
      const fields = [
        ...new Set(
          documents.flatMap((item) =>
            item.fields
              .filter((field) => field.name !== 'Document type' && field.name !== 'Line items')
              .map((field) => field.name),
          ),
        ),
      ];
      return {
        name,
        fields,
        samples: documents.length,
        rows: documents.reduce((sum, item) => sum + item.lineItems.length, 0),
      };
    });
    const discoveredCardNames = new Set(discoveredTypeCards.map((type) => type.name));
    const typeNamesAwaitingFields = [...new Set(reportedTypeNames)].filter(
      (name) => !discoveredCardNames.has(name),
    );
    rememberReadyType(job.id, displayedProgress?.activity);
    const filledTypeCards = fillTypeCards(job.id, discoveredTypeCards, typeNamesAwaitingFields);
    const displayedTypeCount = new Set([...reportedTypeNames, ...discoveredCardNames]).size;
    // WHAT THE ANIMATION IS ALLOWED TO SIT NEXT TO.
    //
    // A loop on its own over a counter that is not moving is what made this
    // screen read as hung. Every stage that shows an animation also states a
    // measured fact from the engine here, and where the engine has sent nothing
    // yet this is null and the stage falls back to describing the work.
    // A beat of quiet, never a flash of the wrong screen. While the job's last
    // batch reads from disk this component cannot yet know whether the job is
    // empty, and guessing "empty" flashed the new-job screen on every open of
    // a working job.
    if (openingSavedBatch && !rejoinedShown) {
      telemetryCapture.current = { capture: null };
      // A beat while the job's saved batch is read from disk: this view cannot yet
      // know whether the job is empty, and guessing "empty" flashed the new-job
      // screen on every open of a working job. It never stands in for a run: a
      // job whose first capture is still being read (main keeps its progress)
      // gets the full working screen below, rebuilt from that progress, however
      // the view was remounted (owner, 2026-09-27: the bare "Still reading the
      // first pages" card read as being thrown out of the wizard).
      return (
        <div className="capture-view capture-empty">
          <JobProcessingButton jobId={job.id} />
          <section className="capture-source-panel opening-job" role="status">
            <LoaderCircle className="spin" size={17} />
            <span>
              <strong>Opening {job.name}</strong>
            </span>
          </section>
        </div>
      );
    }
    // A job whose batch emptied (its documents exported, or removed) keeps its workspace:
    // the documents list says it is clear and the main area says All caught up, with adding
    // pages underneath (owner, 2026-10-03). A job that never had a batch still starts here.
    const caughtUpWorkspace =
      !stalled &&
      !learningActivity &&
      !activeCapture &&
      !stillDiscovering &&
      !activeRepresentativeImport &&
      job.documentTypes.length > 0 &&
      Boolean(batch);
    // Cleared by what they confirmed here (not a job that was already clear): once per job (review-telemetry.ts).
    if (caughtUpWorkspace && confirmedInVisit.size > 0) reviewTally.caughtUp(job.id);
    // Until Start the tour, the tour's start window (TourStart) sits over this screen. Behind it
    // the real learning screen shows while the engine reads (owner, 2026-10-04: "a great way to show
    // off our wizard"); in the beats with nothing running, a quiet page instead of the start screen.
    const tourWaiting = touring && !tour?.own && !tour?.ready;
    // The documents whose reading finished count here too, one at a time, as the engine reports each.
    const tourReport = tourWaiting && (
      <TourReadingReport
        jobId={job.id}
        stage={captureProgress?.stage ?? null}
        types={(batch?.documents ?? []).map((document) => tourDocumentType(document, true))}
        again={tourReadAgain(batch?.documents ?? [])}
        problem={tourReaderProblem(batch?.documents ?? [])}
        reading
      />
    );
    if (tourWaiting && !stalled && !(learningActivity || activeCapture || stillDiscovering)) {
      telemetryCapture.current = { capture: 'reading' };
      return (
        <div className="capture-view capture-empty caught-up-workspace tour-job tour-reading">
          {tourReport}
          <aside className="caught-up-rail" aria-label="Documents" />
          <section className="capture-source-panel tour-reading-main">
            <div className="processing-workspace-awaiting">
              <Files size={38} aria-hidden="true" />
              <p>The first page shows here as it is read</p>
            </div>
          </section>
        </div>
      );
    }
    // Reading: a first setup's reading is the setup wait; otherwise pages on their way, all caught up, or nothing yet.
    telemetryCapture.current = {
      capture:
        learningActivity || activeCapture || stillDiscovering || activeRepresentativeImport
          ? buildingTheJob || learningActivity || stillDiscovering
            ? 'setup-wait'
            : 'reading'
          : caughtUpWorkspace
            ? 'caught-up'
            : 'start',
    };
    return (
      <div
        className={`capture-view capture-empty ${learningActivity || activeCapture || stillDiscovering ? 'processing-intake' : ''} ${caughtUpWorkspace ? 'caught-up-workspace' : ''}${touring && !tour?.own ? ' tour-job' : ''}`}
      >
        {deletedDialog}
        {tourReport}
        {!(learningActivity || activeCapture || stillDiscovering) && !caughtUpWorkspace && (
          <JobProcessingButton jobId={job.id} />
        )}
        {caughtUpWorkspace && (
          <CaughtUpRail>
            {touring ? (
              <TourCard jobId={job.id} />
            ) : (
              <DocumentHint
                jobId={job.id}
                documents={batch?.documents ?? []}
                pilot={job.reviewPolicy?.processingMode}
                sample={Boolean(sampleSetOfJob(job.id))}
                calibrating={isCalibrating(job.learningStance)}
              />
            )}
          </CaughtUpRail>
        )}
        <section
          className={`capture-source-panel ${learningActivity || activeCapture || stillDiscovering ? 'processing-source-panel' : !stalled && !activeRepresentativeImport ? 'capture-start-screen' : ''}`}
        >
          {caughtUpWorkspace && (
            <>
              <JobProcessingButton jobId={job.id} />
              <CaughtUpHero key={job.id} jobId={job.id} onNotice={onNotice} />
            </>
          )}
          {/* PAIGE (docs/engineering/workbench-port.md): files and folders dragged onto the start
            screen go through the same Add pages as the cards, without the browser's upload prompt. */}
          {!learningActivity &&
            !activeCapture &&
            !stillDiscovering &&
            !activeRepresentativeImport && (
              <PageDropZone
                heading={
                  job.documentTypes.length === 0
                    ? 'Drop a folder or files to start'
                    : 'Drop a folder or files to add pages'
                }
                disabled={acquiring}
                onPages={(source) => void acquireDocuments(source)}
              />
            )}
          {!(learningActivity || activeCapture || stillDiscovering) && (
            <div
              className={`capture-source-copy ${stalled ? 'capture-stalled' : ''} ${!stalled && !learningActivity && !activeCapture ? 'one-path-idle' : ''}`}
            >
              {/* Idle wears the same paper-feed the new-job card opened with — the
                operator just named the slot; here it sits waiting to be fed.
                Active states keep their working illustration. */}
              {stalled ? (
                <span className="source-illustration stalled" aria-hidden="true">
                  <TriangleAlert size={31} />
                </span>
              ) : learningActivity || activeCapture ? (
                <span className="source-illustration thinking">
                  <BrainCircuit size={31} />
                  <i />
                  <b />
                </span>
              ) : null}
              {(stalled || activeRepresentativeImport) && (
                <button
                  type="button"
                  className="active-job-identity"
                  onClick={onJobs}
                  title="Change job"
                  aria-label={`Working in ${job.name} · change job`}
                >
                  <JobIdentity key={job.id} jobId={job.id} />
                  <span className="active-job-label">Job</span>
                  <strong>{job.name}</strong>
                </button>
              )}
              {(stalled || learningActivity || activeCapture || activeRepresentativeImport) && (
                <div className="eyebrow">
                  {stalled
                    ? 'Nothing was lost · the pages are still here'
                    : buildingTheJob
                      ? 'Setting up this job · happens once'
                      : learningActivity
                        ? 'Building this job'
                        : activeRepresentativeImport
                          ? job.bootstrapMode === 'folder'
                            ? 'Importing selected folder'
                            : 'Processing selected files'
                          : 'Reading scanned pages'}
                </div>
              )}
              <>
                <h2>
                  {stalled
                    ? `${stalled.files.toLocaleString()} ${stalled.files === 1 ? 'file' : 'files'} did not finish being read.`
                    : (activeTitle ??
                      (activeRepresentativeImport
                        ? `Reading ${folderAnalysisStatus?.totalFileCount.toLocaleString() ?? ''} source files`
                        : job.documentTypes.length === 0
                          ? 'How would you like to start?'
                          : job.bootstrapMode === 'import' && !batch
                            ? 'Your job is imported. Add your first pages.'
                            : 'Add your next pages'))}
                </h2>
                <p>
                  {stalled
                    ? 'They were brought in, but recognition stopped before any of them reached the batch. Nothing has been thrown away - a retry reads the same pages again from where they are staged.'
                    : buildingTheJob
                      ? 'Paige is reading your first pages to work out the document types and fields for this job. This only happens once - later scans go straight to review.'
                      : learningActivity
                        ? 'Paige is grouping documents, proposing reusable types, and finding the fields that belong to each type.'
                        : activeRepresentativeImport
                          ? job.bootstrapMode === 'folder'
                            ? 'The full folder is entering this batch now. The first completed document will open for review while the remaining pages continue in the background.'
                            : 'Every selected file is entering this batch under the saved setup. The first completed document will open for review while the rest continue in the background.'
                          : job.documentTypes.length === 0
                            ? 'Choose where your documents will come from.'
                            : learningLanguage.activeDescription}
                </p>
              </>
              {/* The engine's own words, quoted rather than paraphrased: it is what
                a support representative asks for first. */}
              {stalled && <p className="capture-stalled-reason">{stalled.reason}</p>}
              {/* Said only once it means something, and only as much as is known.
                The count is a fact the main process keeps, so it is stated as a
                fact. The old claim that every attempt had failed alike was NOT
                known — nothing keeps a history of the errors, and two outages
                followed by an unrelated failure would have asserted it anyway —
                so it is gone.
                And the advice to stop clicking is gated on the engine having
                been given a full restart's worth of time and not come back,
                rather than on a count: the same count is thirty seconds on one
                build and nine minutes on another, and telling an operator his
                computer cannot do the work half a minute in is the lie this fix
                exists to stop telling. */}
              {stalled && (stalled.attempts >= ATTEMPTS_WORTH_SAYING || stalled.patienceSpent) && (
                <p className="capture-stalled-attempts">
                  {stalled.attempts >= ATTEMPTS_WORTH_SAYING &&
                    `Tried ${stalled.attempts.toLocaleString()} times. `}
                  {stalled.patienceSpent &&
                    'The engine was given the full five minutes a restart is allowed and did not come back, so the queue stopped rather than feeding it more pages. Retrying is unlikely to help until something changes.'}
                </p>
              )}
              {/* The retry, at the top, as the primary action — above the source
                picker rather than under it. The one below stays and steps down
                to secondary: an operator who wants to scan something else
                instead is not blocked by a stalled wave. */}
              {stalled && (
                <div className="capture-stalled-actions">
                  <QueueStalledSignal jobId={job.id} files={stalled.files} />
                  <button
                    className="primary-button"
                    data-telemetry="retry-stalled-files"
                    onClick={() => stalled.captures.forEach((item) => retryStagedCapture(item.id))}
                  >
                    <RotateCw size={17} /> Retry {stalled.files.toLocaleString()}{' '}
                    {stalled.files === 1 ? 'file' : 'files'}
                  </button>
                  <button
                    className="secondary-button"
                    data-telemetry="discard-stalled-files"
                    onClick={() =>
                      stalled.captures.forEach((item) => void discardStagedCapture(item.id))
                    }
                  >
                    <X size={16} /> Discard {stalled.captures.length === 1 ? 'them' : 'all'}
                  </button>
                </div>
              )}
            </div>
          )}
          {(learningActivity || activeCapture || stillDiscovering) && (
            <ProcessingWorkspace
              title={
                afterSetup
                  ? processingTitle(folderShown?.total ?? afterSetup.documents)
                  : activeCapture && !stageContext.proposing
                    ? folderShown
                      ? folderImportLines(folderShown).title
                      : 'Adding your documents'
                    : (activeTitle ?? 'Reading your pages')
              }
              phase={
                afterSetup
                  ? { id: 'processing', finished: setupTypesLine(afterSetup.types) }
                  : undefined
              }
              stageId={shownStage}
              processingAction={<JobProcessingButton jobId={job.id} compact />}
              findingsTitle={
                job.bootstrapMode === 'content-central'
                  ? learningLanguage.discoveryLabel
                  : undefined
              }
              stages={captureStages}
              live={working.live}
              activeType={working.activeType}
              progress={working}
              elapsed={shortDuration(elapsedMilliseconds)}
              remaining={shownRemaining ? `~${shortDuration(shownRemaining)}` : null}
              metrics={working.metrics}
              previewLabel={learningPreview ? loadedLearningIdentity.label : ''}
              pageNumber={learningPreview ? loadedLearningIdentity.pageNumber : undefined}
              preview={
                learningPreview ? (
                  <LearningEvidencePreview
                    source={learningPreview}
                    label={loadedLearningIdentity.label}
                    evidence={operationalPreviewEvidence}
                    stage={progressStage ?? 'preparing'}
                  />
                ) : (
                  <div className="processing-workspace-awaiting">
                    <Files size={38} />
                    <p>
                      {workingState.rank >= 1
                        ? 'The next page shows here as it is read'
                        : 'Opening the first page'}
                    </p>
                  </div>
                )
              }
              types={filledTypeCards.cards}
              pendingTypes={filledTypeCards.pending}
              reportedTypeCount={displayedTypeCount}
              nextCard={
                <SetupNextCardView
                  stage={
                    activeCapture && !stageContext.proposing
                      ? undefined
                      : (progressStage ?? 'preparing')
                  }
                />
              }
              emptyInsight={
                <>
                  <StageAnimation
                    stage={progressStage}
                    signals={{ barcodeCount: displayedProgress?.barcodeCount ?? 0 }}
                  />
                  {job.bootstrapMode === 'content-central' && (
                    <p>{learningLanguage.pendingTypeLabel}</p>
                  )}
                </>
              }
              // What reading found stays up until the types it leads to arrive.
              finds={
                workingState.reading.pages > 0 &&
                (progressStage === 'recognizing' ||
                  (progressStage !== 'preparing' &&
                    !filledTypeCards.cards.length &&
                    !filledTypeCards.pending.length)) ? (
                  <ReadingFinds
                    reading={workingState.reading}
                    barcodeCount={describeWorking(workingState).barcodeCount}
                    pageLines={operationalPreviewEvidence.lines}
                    pageBarcodes={operationalPreviewEvidence.barcodes}
                  />
                ) : undefined
              }
              details={working.details}
            />
          )}
          {activeCapture &&
            !stageContext.proposing &&
            keepFeedingPaper &&
            !acquiring &&
            feedMorePaper(false)}
          {!learningActivity &&
            !activeCapture &&
            !stillDiscovering &&
            !activeRepresentativeImport && (
              <div className="acquisition-control">
                {!stalled ? (
                  <PageSourceCards
                    scannerNames={scanners?.sources.map((source) => source.name) ?? []}
                    // PAIGE: Paige's own sources (email, a watched folder) are cards of their own.
                    scannerSources={scanners?.sources ?? []}
                    scannersListed={scanners !== null}
                    onNoScanner={() => reportNoScanner(job.id)}
                    lastScanner={lastScanner}
                    working={acquiring ? sourceKey : null}
                    workingVerb={acquisitionWorkingVerb}
                    disabled={acquiring || readOnlyView()}
                    onPick={(key) => {
                      chooseSource(key);
                      void acquireDocuments(key);
                    }}
                    onPrintSeparatorSheets={
                      hostHas('break-sheets') ? () => setBreakSheetsOpen(true) : undefined
                    }
                  />
                ) : (
                  <label>
                    <span>{sourceIcon(18)}</span>
                    <select
                      value={sourceKey}
                      onChange={(event) => chooseSource(event.target.value)}
                      aria-label="Page source"
                    >
                      <option value="folder">Folder (including subfolders)</option>
                      <option value="files">Files</option>
                      {scanners?.sources.map((source, index) => (
                        <option
                          value={`scanner:${index}`}
                          key={`${source.hostArchitecture}-${source.name}`}
                        >
                          {source.name}
                        </option>
                      ))}
                      {simulatorTray && <option value="simulator">Simulated scanner</option>}
                    </select>
                  </label>
                )}
                <div className="capture-source-actions">
                  {canReturnToNewJob && !stalled && (
                    <button
                      type="button"
                      className="secondary-button source-back-button"
                      disabled={acquiring}
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent('capturepoint-back-to-new-job', { detail: job }),
                        )
                      }
                      aria-label="Back to new job"
                    >
                      <ChevronLeft size={18} /> Back
                    </button>
                  )}
                  {!stalled && simulatorTray && (
                    <SimulateScannerButton
                      jobName={job.name}
                      disabled={acquiring}
                      onConfirm={() => {
                        chooseSource('simulator');
                        void acquireDocuments('simulator');
                      }}
                    />
                  )}
                  {/* PAIGE: the drop above, said once (PageDropZone). */}
                  {!stalled && <span className="page-drop-hint">or drag a folder here</span>}
                  {/* No documents list to hold its More menu: the job's Deleted view opens here, while it has any. */}
                  {!stalled && deletedCount && window.capturePointDesktop?.listDeletedDocuments ? (
                    <button
                      type="button"
                      className="break-sheets-link"
                      data-telemetry="documents-deleted"
                      onClick={() => setDeletedOpen(true)}
                    >
                      <Trash2 size={15} aria-hidden="true" /> Deleted documents ·{' '}
                      {deletedCount.toLocaleString()}
                    </button>
                  ) : null}
                  {stalled && (
                    <button
                      className="secondary-button"
                      onClick={() => void acquireDocuments()}
                      disabled={acquiring}
                    >
                      {acquiring ? <LoaderCircle className="spin" size={18} /> : sourceIcon(18)}
                      {acquiring
                        ? `${acquisitionWorkingVerb}…`
                        : captureProcessingId
                          ? 'Add more pages'
                          : 'Add pages'}
                    </button>
                  )}
                  {/* Only while a scanner is actually on the wire. A file picker is
                the operating system's to cancel, and a folder stages instantly. */}
                  {acquiring &&
                    sourceKey !== 'files' &&
                    sourceKey !== 'folder' &&
                    sourceKey !== 'simulator' && (
                      <button
                        className="secondary-button acquisition-stop"
                        onClick={() => void stopAcquiring()}
                      >
                        <Square size={14} /> Stop
                      </button>
                    )}
                </div>
              </div>
            )}
          {(learningActivity || activeCapture || stillDiscovering || activeRepresentativeImport) &&
            acquiring &&
            sourceKey !== 'files' &&
            sourceKey !== 'folder' &&
            sourceKey !== 'simulator' && (
              <button
                className="secondary-button acquisition-stop"
                onClick={() => void stopAcquiring()}
              >
                <Square size={14} /> Stop scanner
              </button>
            )}
          {!learningActivity && acquisitionNotice && (
            <div
              key={acquisitionNotice}
              className="acquisition-notice"
              role="status"
              data-telemetry={
                acquisitionNotice === SCANNER_NO_PAGES
                  ? 'error:source-no-pages'
                  : acquisitionNotice === FOLDER_NO_READABLE_PAGES
                    ? 'error:source-empty-folder'
                    : undefined
              }
            >
              <Info size={13} />
              <span>{acquisitionNotice}</span>
              <button onClick={() => setAcquisitionNotice(null)} aria-label="Dismiss this notice">
                <X size={12} />
              </button>
            </div>
          )}
          {/* Condensed under the live workbench: the workbench already narrates
              the active wave and its stages, so the queue keeps only what the
              workbench does not say — the whole-import bar and anything needing
              a retry. Two stage strips with different words was how
              Split/Organize happened. */}
          {!activeRepresentativeImport && (
            <CaptureQueueStatus
              queue={captureQueue}
              processingId={captureProcessingId}
              progress={captureProgress}
              step={importStep}
              waves={captureWaves}
              folder={folderImport}
              condensed={Boolean(learningActivity) || activeCapture}
              learningPhaseLabel={learningLanguage.phaseLabel}
              onOpenCluster={onOpenCluster}
              onRetry={retryStagedCapture}
              onDiscard={(id) => void discardStagedCapture(id)}
            />
          )}
          {(error || (!learningActivity && !health)) && (
            <div
              role={error ? 'alert' : 'status'}
              className={`engine-status ${error ? 'error' : ''}`}
              data-telemetry={
                error && setupFailureCategory(error) === 'unsupported'
                  ? 'error:source-unsupported-files'
                  : error
                    ? `error:${errorCode}`
                    : undefined
              }
            >
              {error ? <TriangleAlert size={16} /> : <span className="status-dot" />}
              <span>
                <strong>{error ?? 'Preparing Paige…'}</strong>
              </span>
            </div>
          )}
          {hostHas('break-sheets') &&
            !learningActivity &&
            !activeCapture &&
            (stalled || stillDiscovering || activeRepresentativeImport) && (
              <button className="break-sheets-link" onClick={() => setBreakSheetsOpen(true)}>
                <QrCode size={17} /> Print break sheets
              </button>
            )}
          {capturePanels}
        </section>
      </div>
    );
  }

  const currentBatchPageNumber = document.pages[selectedPage]?.number;
  const fieldLinkSignature = JSON.stringify(
    evidenceFields.map(({ configured, recognized }) => [
      configured.id,
      recognized.evidencePageNumber ?? document.pages[0]?.number ?? 1,
      recognized.evidenceLineIndexes ?? [],
      recognized.barcodeEvidence
        ? [
            recognized.barcodeEvidence.symbology,
            recognized.barcodeEvidence.value,
            recognized.barcodeEvidence.x,
            recognized.barcodeEvidence.y,
            recognized.barcodeEvidence.width,
            recognized.barcodeEvidence.height,
          ]
        : null,
    ]),
  );
  const highlightedFieldIds = new Set(pinnedFieldIds);
  const supplementaryEvidence = hoverEvidence.filter(
    (entry) =>
      highlightedFieldIds.has(entry.fieldId) && entry.pageNumber === currentBatchPageNumber,
  );
  // Index once, not once per rendered page. A long document otherwise scans
  // every page's field matches again for every page (quadratic work).
  const hoverEvidenceByPage = new Map<number, HoverEvidenceEntry[]>();
  const pinnedEvidenceByPage = new Map<number, number[]>();
  const pinnedCitationsByPage = new Map<number, EvidenceCitation[]>();
  for (const entry of hoverEvidence) {
    const pageNumber = entry.pageNumber ?? document.pages[0]?.number;
    if (pageNumber == null) continue;
    const entries = hoverEvidenceByPage.get(pageNumber) ?? [];
    entries.push(entry);
    hoverEvidenceByPage.set(pageNumber, entries);
    if (highlightedFieldIds.has(entry.fieldId)) {
      const lines = pinnedEvidenceByPage.get(pageNumber) ?? [];
      lines.push(...entry.lineIndexes);
      pinnedEvidenceByPage.set(pageNumber, lines);
      pinnedCitationsByPage.set(pageNumber, [
        ...(pinnedCitationsByPage.get(pageNumber) ?? []),
        hoverCitation(entry),
      ]);
    }
  }
  const activeEvidenceIsCurrent =
    activeEvidenceTarget?.documentId === document.id &&
    activeEvidenceTarget.pageNumber === currentBatchPageNumber;
  const displayedEvidenceLines = [
    ...new Set([
      ...(activeEvidenceIsCurrent ? activeEvidenceLines : []),
      ...supplementaryEvidence.flatMap((entry) => entry.lineIndexes),
    ]),
  ];
  // Outline the words that spell each shown value, not every word of its cited lines.
  const displayedEvidenceWords = citedWordIndexes(document.pages[selectedPage]?.words ?? [], [
    ...(activeEvidenceIsCurrent
      ? [
          {
            lineIndexes: activeEvidenceLines,
            ...(activeEvidenceCitation?.lines === activeEvidenceLines
              ? { value: activeEvidenceCitation.value, stored: activeEvidenceCitation.words }
              : {}),
          },
        ]
      : []),
    ...supplementaryEvidence.map(hoverCitation),
  ]);
  // A checkbox field's marked box goes with its caption lines: clicked, pinned or hovered (mark-evidence.ts).
  const displayedMarkEvidence = [
    ...markEvidenceForLines(
      document.fields,
      currentBatchPageNumber,
      document.pages[0]?.number,
      activeEvidenceIsCurrent ? activeEvidenceLines : [],
    ),
    ...supplementaryEvidence.flatMap((entry) => (entry.mark ? [entry.mark] : [])),
  ].filter(
    (mark, index, marks) =>
      marks.findIndex((other) => other.x === mark.x && other.y === mark.y) === index,
  );
  const displayedBarcodeEvidence = [
    ...(activeEvidenceIsCurrent && activeBarcodeEvidence ? [activeBarcodeEvidence] : []),
    ...supplementaryEvidence.flatMap((entry) => (entry.barcode ? [entry.barcode] : [])),
  ].filter(
    (value, index, values) =>
      values.findIndex(
        (candidate) =>
          candidate.value === value.value && candidate.x === value.x && candidate.y === value.y,
      ) === index,
  );
  const openViewerSelectionMenu = (menu: ViewerSelectionMenu) => {
    if (
      menu.documentId !== document.id ||
      menu.documentMutationToken !== currentDocumentMutationToken
    )
      return;
    const pageIndex = document.pages.findIndex((page) => page.number === menu.pageNumber);
    if (pageIndex < 0) return;
    setSelectedPage(pageIndex);
    setActiveEvidenceLines(menu.lineIndexes);
    setActiveBarcodeEvidence(menu.barcode ?? null);
    setActiveLineItemRow(null);
    setActiveEvidenceTarget({ documentId: menu.documentId, pageNumber: menu.pageNumber });
    setSelectionMenu(menu);
  };
  const fieldIdForLine = (pageNumber: number, lineIndex: number) =>
    evidenceFields.find(
      ({ recognized }) =>
        (recognized.evidencePageNumber ?? document.pages[0]?.number) === pageNumber &&
        recognized.evidenceLineIndexes?.includes(lineIndex),
    )?.configured.id ?? null;
  const fieldIdForBarcode = (pageNumber: number, barcode: BarcodeResult) =>
    evidenceFields.find(
      ({ recognized }) =>
        (recognized.evidencePageNumber ?? document.pages[0]?.number) === pageNumber &&
        recognized.barcodeEvidence?.value === barcode.value &&
        recognized.barcodeEvidence.x === barcode.x &&
        recognized.barcodeEvidence.y === barcode.y,
    )?.configured.id ?? null;
  const togglePinnedField = (fieldId: string) =>
    setPinnedFieldIds((current) =>
      current.includes(fieldId)
        ? current.filter((candidate) => candidate !== fieldId)
        : [...current, fieldId],
    );
  const extractionFailure = document.validationFindings.find(
    (item) => item.code === 'semantic_processing_failed',
  );
  const extractionNeedsType = extractionNeedsDocumentType(extractionFailure?.message);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  /**
   * This document was never put to the model.
   *
   * Kept separate from `extractionFailure` on purpose, and said in different
   * words. "Fields were not extracted" is what an operator reads when the reader
   * tried and failed; it invites them to look at the document. This one has to
   * stop them looking at the document at all — there is nothing wrong with it,
   * nobody read it, and every empty box below is an unasked question rather than
   * a miss.
   */
  const notReadReason = document.notReadReason ?? null;
  /**
   * Which of the two ways this document went unread, because the sentence and the
   * repair differ.
   *
   * A reading model that could not be reached leaves every page intact and no
   * fields; a page the recogniser never read leaves a hole in the document itself
   * and everything downstream of it is reasoning about text that does not exist.
   * The banner said the first thing in both cases, which would have told an
   * operator to go and fix a runtime that was working.
   *
   * Both are answered by the same button. "Read it again" reads the pages nothing
   * read again from their own images, the way the batch reads a page (the page
   * budget, the upright check, the job's turns), then the document's values, so
   * it reads the page again rather than only re-asking the model about stored
   * text, and it reads nothing else in the job.
   */
  const pagesNeverRead = document.pages.filter((page) => Boolean(page.notReadReason));
  /**
   * Confirm the whole document in one action, so the product has something to learn from.
   *
   * Measured on the real workstation on 2026-07-28: 1,416 field observations, 284
   * boundary, 266 type — and four verified labels between them. Every miner needs
   * three or four verified examples, so `suggestion_candidates` was empty and the
   * learning loop had never run at all.
   *
   * The ledgers were not broken. The write path fires correctly whenever a
   * correction happens. The problem was the price of a label: confirming a value
   * meant clicking a twelve-pixel check inside each field, one field at a time, so
   * a document with eight fields cost eight clicks and nobody ever paid it. The
   * product asked for the one thing it needed in the most expensive way it could.
   *
   * This is still an explicit assertion, never inferred. `adaptive-learning.md` is
   * emphatic that the absence of a correction is weak evidence and must not be
   * treated as truth, so moving on from a document records nothing, exactly as
   * before. What changes is that saying "this one is right" costs one click
   * instead of eight.
   */
  /**
   * The way out of "still flagged, and this panel cannot say what for".
   *
   * The dead end: a stored review flag that disagrees with everything on
   * screen, and an operator whose only advice was to reopen the document and
   * wonder. The honest move is to re-derive the flag from fresh detail — not
   * the cache, which is exactly the thing the flag disagrees with — and then
   * either the fresh findings NAME the fault in the checklist, or the settle
   * clears the flag durably and says so in the correction ledger.
   */
  /**
   * Enter's whole contract: save what was typed, then put the caret in the next field. It never
   * vouches for a value it didn't change (single confirm, 2026-10-05): the document's one main
   * button confirms every value at once.
   */
  const advanceAfterFieldSave = (configured: Field, documentId: string) => {
    const stillSelected = () =>
      mounted.current &&
      liveJobRef.current.id === job.id &&
      batchRef.current?.batchId === batch?.batchId &&
      batchRef.current?.documents[selectedDocumentRef.current]?.id === documentId;
    if (!stillSelected()) return;
    // The next field down, in the order the fields panel shows them (configuredFields is that order).
    const scalarFields = configuredFields.filter((field) => field.kind !== 'line-items');
    const at = scalarFields.findIndex((field) => field.id === configured.id);
    const next = at >= 0 ? scalarFields[at + 1] : undefined;
    if (next) {
      // Two frames, not now: the inputs are disabled until the save's state
      // flip renders, and focus() on a disabled input silently goes nowhere
      // (adversarial review of the smoke pin that drives this).
      const selector = `label[data-field-id="${next.id}"] input, label[data-field-id="${next.id}"] textarea`;
      // Only while the caret is still in this field's box (or was dropped by its save): moving on
      // never pulls focus out of somewhere the operator has gone since, its own pin button included.
      const stillHere = () => {
        const active = window.document.activeElement;
        return (
          !active ||
          active === window.document.body ||
          (active.matches('input, textarea') &&
            Boolean(active.closest(`label[data-field-id="${configured.id}"]`)))
        );
      };
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (stillSelected() && stillHere())
            window.document.querySelector<HTMLElement>(selector)?.focus();
        }),
      );
    }
  };
  /**
   * Opens the field save lane for one document once the other work on it (a value or line-item save, a
   * page turn or boundary change, a type change) has ended, instead of refusing the gesture: the
   * engine-structure goal's refusals-to-queues. Says once what it waits for. False when the wait was
   * dropped (the window or batch closed, the document is being confirmed, or the same gesture already
   * waits).
   */
  const openFieldSaveLaneWhenFree = async (
    key: string,
    documentId: string,
    waiting: string,
    code: TelemetryUiErrorCode,
    alsoWaitFor: () => boolean = () => false,
  ) => {
    if (documentLaneWaits.current.has(key)) return false;
    const batchId = batchRef.current?.batchId;
    const otherWork = () =>
      fieldSavingRef.current ||
      lineMutationAdmitted(documentId) ||
      structuralMutationBlocks(documentId) ||
      pageEditBusyRef.current ||
      typeSavingDocumentIdRef.current === documentId ||
      alsoWaitFor();
    if (!otherWork() && openFieldSaveLane(documentId)) return true;
    onNotice(waiting, code);
    documentLaneWaits.current.add(key);
    try {
      for (;;) {
        await nextSaveLaneChange();
        if (
          !mounted.current ||
          batchRef.current?.batchId !== batchId ||
          confirmingDocumentsRef.current.has(documentId)
        )
          return false;
        if (!otherWork() && openFieldSaveLane(documentId)) return true;
      }
    } finally {
      documentLaneWaits.current.delete(key);
    }
  };
  /**
   * "Find the table" on a document whose reading has no rows at all. The engine re-reads its pages with the
   * thorough table reader, then the page's printed columns; with a region, only the operator's box. Rows come
   * back unconfirmed, for the operator to check and confirm like any reading. The engine refuses a document
   * that has any row, and the same save lanes as a re-check keep this from racing an edit.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const findTable = async (region?: TableRegion) => {
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop || findingTableFor) return;
    lastChange.note(); // a change with no undo for Ctrl+Z (last-change.ts)
    const requestedTargetId = batch.documents[selectedDocument]?.id;
    if (!requestedTargetId || confirmingDocumentsRef.current.has(requestedTargetId)) return;
    if (batch.batchId.startsWith('live:')) {
      onNotice(
        'This document is still being saved. Find its table once saving finishes',
        'table-find-busy',
      );
      return;
    }
    setError(null);
    setFindingTableFor(requestedTargetId);
    if (
      !(await openFieldSaveLaneWhenFree(
        `table:${requestedTargetId}`,
        requestedTargetId,
        "Finding this document's table after the current change",
        'table-find-busy',
        () => pendingLineSaves.current > 0,
      ))
    ) {
      if (mounted.current) setFindingTableFor(null);
      return;
    }
    try {
      // Read after the wait: the document may have changed while this waited.
      const target = batchRef.current?.documents.find((item) => item.id === requestedTargetId);
      const schemas = schemaRequest(liveJobRef.current);
      const schema =
        target &&
        (schemas.find((type) => type.id === target.documentTypeId) ??
          schemas.find(
            (type) =>
              type.name.localeCompare(target.documentType, undefined, { sensitivity: 'accent' }) ===
              0,
          ));
      if (!target || !schema || target.lineItems.length > 0) return;
      const result = await desktop.findDocumentTable({
        jobId: job.id,
        batchId: batch.batchId,
        documentId: requestedTargetId,
        documentType: schema,
        ...(region ? { region } : {}),
      });
      if (!mounted.current || batchRef.current?.batchId !== batch.batchId) return;
      if (!result.found) {
        setMarkingTableFor(requestedTargetId);
        onNotice(
          region
            ? "Nothing readable in that box. Drag a box around the table's rows, or add them yourself"
            : 'No table found. Drag a box around the table on the page',
          'table-not-found',
        );
        return;
      }
      setMarkingTableFor(null);
      cacheAuthoritativeProjectionSkeleton(result.document);
      const current = batchRef.current;
      if (!current) return;
      const saved = {
        ...current,
        documents: current.documents.map((item) =>
          item.id === result.document.id ? result.document : item,
        ),
      };
      batchRef.current = saved;
      setBatch(saved);
      onRestored(saved);
      invalidateBoundaryUndoFor(result.document.id);
      onNotice(
        `Found ${result.rows} ${result.rows === 1 ? 'row' : 'rows'}. Check them against the page, then confirm`,
      );
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'The table could not be read',
        'table-find-failed',
      );
    } finally {
      setFindingTableFor(null);
      closeFieldSaveLane();
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const recheckReviewFlag = async () => {
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop) return;
    lastChange.note(); // a change with no undo for Ctrl+Z (last-change.ts)
    const requestedTargetId = batch.documents[selectedDocument]?.id;
    if (!requestedTargetId || confirmingDocumentsRef.current.has(requestedTargetId)) return;
    if (
      !(await openFieldSaveLaneWhenFree(
        `recheck:${requestedTargetId}`,
        requestedTargetId,
        'Re-checking this document after the current change',
        'recheck-busy',
        () => schemaRefreshBusyRef.current,
      ))
    )
      return;
    setError(null);
    try {
      if (pendingLineSaves.current > 0)
        onNotice('Saving the line-item correction, then re-checking this document');
      const lineSavesSucceeded = await waitForPendingSaveLane(
        () => pendingLineSaves.current,
        () => lineSaveChain.current,
        () => lineSaveFailed.current,
      );
      if (!lineSavesSucceeded) {
        onNotice(
          'The line-item correction was not saved, so this document was not re-checked',
          'recheck-failed',
        );
        return;
      }
      const target = (batchRef.current ?? batch).documents.find(
        (item) => item.id === requestedTargetId,
      );
      if (!target) return;
      const detail = await desktop.getBatchDocument({
        jobId: job.id,
        batchId: batch.batchId,
        documentId: target.id,
      });
      if (!detail) return;
      const settled = settledDocument(detail);
      const persisted = settled.needsReview
        ? settled
        : await desktop.saveDocumentCorrection({
            jobId: job.id,
            batchId: batch.batchId,
            document: settled,
            correction: {
              kind: 'review-flag-reconciled',
              targetId: detail.id,
              beforeJson: JSON.stringify({ documentId: detail.id, needsReview: true }),
              afterJson: JSON.stringify({ documentId: detail.id, needsReview: false }),
            },
          });
      cacheAuthoritativeProjectionSkeleton(persisted);
      const current = batchRef.current;
      if (!current) return;
      loadedDocumentIdsRef.current = new Set([...loadedDocumentIdsRef.current, persisted.id]);
      const saved = {
        ...current,
        loadedDocumentIds: [...loadedDocumentIdsRef.current],
        documents: current.documents.map((item) => (item.id === persisted.id ? persisted : item)),
      };
      batchRef.current = saved;
      setBatch(saved);
      onRestored(saved);
      invalidateBoundaryUndoFor(persisted.id);
    } catch (reason) {
      setError(messageFor(reason, 'The document could not be re-checked'), 'recheck-failed');
    } finally {
      closeFieldSaveLane();
    }
  };
  /** Confirms the whole document: the only confirm there is (single confirm, 2026-10-05). */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const confirmDocument = async (onFailed?: (documentId: string) => void) => {
    // Captured as a const so its presence survives into the background chain's
    // closure — property narrowing does not.
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop) return false;
    const requestedTarget = batch.documents[selectedDocument];
    if (!requestedTarget) return false;
    const requestedBatchId = batch.batchId;
    const requestedContract = documentFieldContractToken(requestedTarget);
    // What the later field and row checks compare against: the document as seen, or as the page
    // turn below landed it once its values proved the same (the turn moves every row's evidence,
    // so the pre-turn copy would read as "changed while saving"). The contract token and the page
    // numbers stay as the operator saw them: a turn never renumbers pages, so a removal, split or
    // restore that landed before it must still refuse.
    let baseline: DocumentResult = requestedTarget;
    // A page of this document is being turned and read again. Confirming now would seal the
    // reading from before the turn, so the confirmation waits for the turn to land, and goes ahead
    // only if the values read again are the ones the person saw.
    if (pageTurnPendingFor(pageTurnsRef.current, requestedTarget.id)) {
      onNotice('Confirmation queued · finishing the page turn');
      while (mounted.current && pageTurnPendingFor(pageTurnsRef.current, requestedTarget.id))
        await nextSaveLaneChange();
      const current = batchRef.current?.documents.find((item) => item.id === requestedTarget.id);
      if (!mounted.current || batchRef.current?.batchId !== requestedBatchId || !current)
        return false;
      if (
        documentFieldContractToken(current) !== requestedContract ||
        requestedTarget.fields.some(
          (field) => current.fields.find((item) => item.name === field.name)?.value !== field.value,
        ) ||
        // Rows are compared by what they say: a turn moves every row's evidence onto the turned page.
        JSON.stringify(
          current.lineItems.map((row) => [
            row.description,
            row.quantity ?? null,
            row.unitPrice ?? null,
            row.amount ?? null,
          ]),
        ) !==
          JSON.stringify(
            requestedTarget.lineItems.map((row) => [
              row.description,
              row.quantity ?? null,
              row.unitPrice ?? null,
              row.amount ?? null,
            ]),
          )
      ) {
        onNotice(
          'The turned page was read again and its values changed. Check them, then confirm.',
          'confirm-changed',
        );
        return false;
      }
      baseline = current;
    }
    // Keep the click bound to the document and values the operator saw. A save
    // on another field/document need not make them wait to click Confirm all.
    while (fieldSavingRef.current || wholeBatchMutationRef.current) {
      onNotice('Confirmation queued · finishing the current save');
      await (wholeBatchMutationRef.current
        ? wholeBatchCompletionRef.current
        : fieldSaveCompletionRef.current);
      const current = batchRef.current?.documents.find((item) => item.id === requestedTarget.id);
      if (!mounted.current || batchRef.current?.batchId !== requestedBatchId || !current)
        return false;
      // A split or join saved while this waited regroups pages without
      // changing the first document's id; that is not the document the
      // operator confirmed.
      if (
        documentFieldContractToken(current) !== requestedContract ||
        current.pages.map((page) => page.number).join(',') !==
          requestedTarget.pages.map((page) => page.number).join(',') ||
        baseline.fields.some(
          (field) => current.fields.find((item) => item.name === field.name)?.value !== field.value,
        ) ||
        JSON.stringify(current.lineItems.map((row) => ({ ...row, verified: false }))) !==
          JSON.stringify(baseline.lineItems.map((row) => ({ ...row, verified: false })))
      ) {
        onNotice(
          'The displayed values changed while saving. Review them before confirming.',
          'confirm-changed',
        );
        return false;
      }
    }
    if (schemaRefreshBusyRef.current) {
      onNotice(
        'Finish applying the current job fields before confirming this document',
        'confirm-busy',
      );
      return false;
    }
    if (fieldDraftStore.getSnapshot().some((draft) => draft.documentId === requestedTarget.id)) {
      onNotice(
        'Save or discard the unsaved field values before confirming this document',
        'confirm-unsaved-values',
      );
      return false;
    }
    if (lineDraftStore.getSnapshot().some((draft) => draft.documentId === requestedTarget.id)) {
      onNotice(
        'Save or discard the unsaved line-item values before confirming this document',
        'confirm-unsaved-values',
      );
      return false;
    }
    // Confirming is an assertion that these answers are right. A document with no answers at all
    // asserts that nothing is right, and is then learned from and exported: on dev a Credit Memo
    // with two empty fields took the click without a word and the customer's ledger received it
    // blank, off a page carrying its number, its date and its amount (shared/empty-confirmation.ts).
    if (
      confirmingNothing(
        requestedTarget,
        liveJobRef.current.documentTypes.find(
          (type) => type.id === requestedTarget.documentTypeId,
        ) ??
          liveJobRef.current.documentTypes.find(
            (type) => type.name === requestedTarget.documentType,
          ),
      )
    ) {
      onNotice(NOTHING_TO_CONFIRM);
      return false;
    }
    if (lineMutationAdmitted(requestedTarget.id)) {
      onNotice('Saving the line-item correction, then confirming this document');
      if (!(await waitForLineMutations(requestedTarget.id))) {
        onNotice(
          'The line-item correction was not saved, so this document was not confirmed',
          'confirm-failed',
        );
        return false;
      }
      // The line lane releases before this continuation resumes. A whole-batch
      // projection can claim that gap, so repeat the reciprocal admission check
      // before publishing this document's confirmation token.
      if (wholeBatchMutationRef.current) {
        onNotice('Finish the current batch update before confirming this document', 'confirm-busy');
        return false;
      }
      if (
        schemaRefreshBusyRef.current ||
        structuralMutationBlocks(requestedTarget.id) ||
        typeSavingDocumentIdRef.current === requestedTarget.id ||
        fieldSavingRef.current ||
        confirmingDocumentsRef.current.has(requestedTarget.id)
      ) {
        // Something else took the lane while this waited for the line items (a page turn, a type change).
        onNotice(
          pageTurnOwnsStructure()
            ? 'Finishing the page turn · confirm this document in a moment'
            : 'Finish the current change before confirming this document',
          'confirm-busy',
        );
        return false;
      }
    }
    // A split or join asked for on this document, still waiting its turn,
    // changes which pages it holds. Confirming now would seal the old grouping
    // the operator has already corrected.
    if (boundaryChangeQueuedFor(requestedTarget.id)) {
      onNotice(
        'A boundary change on this document is still saving. Confirm it once that is done.',
        'confirm-busy',
      );
      return false;
    }
    if (structuralMutationBlocks(requestedTarget.id) || pageEditBusyRef.current) {
      onNotice(
        'Finish the current page or boundary change before confirming this document',
        'confirm-busy',
      );
      return false;
    }
    if (typeSavingDocumentIdRef.current === requestedTarget.id) {
      onNotice('Finish changing the document type before confirming this document', 'confirm-busy');
      return false;
    }
    // Claim the document before the first await. React cannot paint the disabled
    // controls until the next render, and without this synchronous admission
    // lock a blur or detached-panel action could start another whole-document
    // write while confirmation was draining the line-item lane.
    if (confirmingDocumentsRef.current.has(requestedTarget.id)) return false;
    // A turn asked for while this confirmation waited above: never seal the reading it replaces.
    if (pageTurnPendingFor(pageTurnsRef.current, requestedTarget.id)) {
      onNotice(
        'A page of this document is still turning. Confirm it once it has been read again.',
        'confirm-busy',
      );
      return false;
    }
    confirmingDocumentsRef.current.add(requestedTarget.id);
    signalSaveLaneChange();
    setConfirmingDocumentIds(new Set(confirmingDocumentsRef.current));
    setConfirmationSerial((serial) => serial + 1);
    const releaseConfirmationAdmission = () => {
      confirmingDocumentsRef.current.delete(requestedTarget.id);
      signalSaveLaneChange();
      setConfirmingDocumentIds(new Set(confirmingDocumentsRef.current));
    };
    // A line correction is already painted optimistically but its durable save
    // may still be in flight. Preserve this confirmation gesture and drain that
    // exact serialized lane before reading the document back; otherwise a fast
    // Ctrl+Enter can seal the pre-correction revision.
    if (pendingLineSaves.current > 0) {
      onNotice('Saving the line-item correction, then confirming this document');
    }
    let lineSavesSucceeded = false;
    try {
      lineSavesSucceeded = await waitForPendingSaveLane(
        () => pendingLineSaves.current,
        () => lineSaveChain.current,
        () => lineSaveFailed.current,
      );
    } catch (reason) {
      releaseConfirmationAdmission();
      setError(messageFor(reason, 'The line-item save could not be completed.'), 'confirm-failed');
      return false;
    }
    if (!lineSavesSucceeded) {
      releaseConfirmationAdmission();
      onNotice(
        'The line-item correction was not saved, so this document was not confirmed',
        'confirm-failed',
      );
      return false;
    }
    const hydratedTarget = await hydrateDocumentForWholeMutation(
      requestedTarget.id,
      'The complete document could not be loaded, so it was not confirmed',
      'confirm-failed',
    );
    if (!hydratedTarget) {
      releaseConfirmationAdmission();
      return false;
    }
    const confirmationBatch = batchRef.current ?? batch;
    const target = confirmationBatch.documents.find(
      (document) => document.id === requestedTarget.id,
    );
    if (!target) {
      releaseConfirmationAdmission();
      return false;
    }
    if (
      target.pages.map((page) => page.number).join(',') !==
      requestedTarget.pages.map((page) => page.number).join(',')
    ) {
      releaseConfirmationAdmission();
      onNotice("This document's pages changed. Check it, then confirm it.", 'confirm-changed');
      return false;
    }
    // A confirmation seals ground truth, so it must be for the values stored now,
    // not a cached copy: learning can rewrite a waiting document's reading (a new
    // correction rule after another document was confirmed). If the store moved,
    // show its values in place and let the operator confirm what they now see.
    if (!confirmationBatch.batchId.startsWith('live:')) {
      let stored: DocumentResult | null = null;
      try {
        stored = await desktop.getBatchDocument({
          jobId: job.id,
          batchId: confirmationBatch.batchId,
          documentId: target.id,
        });
      } catch {
        stored = null;
      }
      if (!stored) {
        releaseConfirmationAdmission();
        onNotice('The document could not be re-read, so it was not confirmed', 'confirm-failed');
        return false;
      }
      if (reviewedValuesToken(stored) !== reviewedValuesToken(target)) {
        replaceDocumentWithStored(stored);
        releaseConfirmationAdmission();
        onNotice(
          'Updated from what Cortexa just learned. Check and confirm again.',
          'confirm-changed',
        );
        return false;
      }
    }
    const unverified = unconfirmedFields(target, configuredFields);
    if (confirmationChangesNothing(target, unverified)) {
      releaseConfirmationAdmission();
      return false;
    }
    /* The click answers at once; the one bulk write follows. Confirming used to
       hold the whole panel behind one engine round-trip PER VALUE, so a ten-value
       document froze the column for ten waits. The ticks flip optimistically
       here, only this document's controls lock, and the engine saves all of the
       progressively proven values plus the document confirmation together. */
    setError(null);
    const unverifiedNames = new Set(unverified.map((configured) => configured.name));
    // Read now: whether this confirmation is the one that confirms the type (then other waiting
    // documents learn from it at once, below).
    const typeWasVerified = Boolean(target.documentTypeVerified);
    const optimistic = settledDocument({
      ...target,
      fields: target.fields.map((field) =>
        unverifiedNames.has(field.name) ? { ...field, verified: true, needsReview: false } : field,
      ),
      lineItems: target.lineItems.map((row) => ({ ...row, verified: true })),
    });
    const painted = {
      ...confirmationBatch,
      documents: confirmationBatch.documents.map((item) =>
        item.id === target.id ? optimistic : item,
      ),
    };
    batchRef.current = painted;
    setBatch(painted);
    onRestored(painted);
    /* Reconciled against the LIVE batch, not the click-render's: the operator
       keeps working other documents while this chain runs, and a snapshot from
       the click would silently revert that work on screen. Only the confirmed
       document is replaced — by identity — and batchRef is set eagerly so work
       that starts before the next render still sees this landing. */
    const reconcile = (settled: DocumentResult) => {
      const live = batchRef.current;
      if (!live) return;
      cacheAuthoritativeProjectionSkeleton(settled);
      const saved = {
        ...live,
        documents: live.documents.map((item) => (item.id === settled.id ? settled : item)),
      };
      batchRef.current = saved;
      setBatch(saved);
      onRestored(saved);
      invalidateBoundaryUndoFor(settled.id);
    };
    void (async () => {
      // Threaded through one document rather than read from state between awaits:
      // each save returns the stored document, and the next correction is recorded
      // against that, so a stale closure cannot drop an earlier confirmation.
      let current = target;
      try {
        if (!confirmationBatch.batchId.startsWith('live:')) {
          // Every value, agreeing row, start and type this confirmation vouches for, then the
          // whole-document receipt (review-edits.ts: support confirms through the same list).
          const built = confirmationEntries({
            target: current,
            unconfirmed: unverified,
            configuredFields,
            documentSchema,
            filedTypeId: (document) =>
              filedDocumentType(document, job.documentTypes, typeNameHistoryRef.current)?.id,
            settle: settledDocument,
          });
          const correctionEntries = built.entries;
          current = built.document;
          const saved = await desktop.saveDocumentCorrections({
            jobId: job.id,
            batchId: confirmationBatch.batchId,
            correctionEntries,
          });
          if (saved.applied !== correctionEntries.length || saved.missingDocumentIds.length > 0)
            throw new Error('The document changed before all of its confirmations could be saved.');
          const settled = await desktop.getBatchDocument({
            jobId: job.id,
            batchId: confirmationBatch.batchId,
            documentId: target.id,
          });
          if (!settled) throw new Error('The confirmed document could not be read back.');
          current = settled;
          reconcile(current);
          reviewTally.confirmed(job.id, target.id);
          window.dispatchEvent(new CustomEvent('capturepoint-review-saved', { detail: job.id }));
          refreshLearningProgress();
          if (!typeWasVerified && current.documentTypeVerified)
            queueTypeProjection(() =>
              recheckConfirmedTypes(confirmationBatch.batchId, job, current.id),
            );
          return;
        }

        for (const configured of unverified) {
          const existing = current.fields.find((field) => field.name === configured.name);
          if (!existing) continue;
          const verifiedField: FieldResult = { ...existing, verified: true, needsReview: false };
          const withField = {
            ...current,
            fields: current.fields.map((field) =>
              field.name === configured.name ? verifiedField : field,
            ),
          };
          const nextDocument = settledDocument(withField);
          current = await desktop.saveDocumentCorrection({
            jobId: job.id,
            batchId: confirmationBatch.batchId,
            document: nextDocument,
            correction: {
              // Asked, not assumed.
              //
              // This said "verified, never corrected: the value is unchanged and
              // the operator is vouching for it" — and never checked. Confirming a
              // document confirms every field on it, including ones the operator
              // had just typed, so a value the reader never produced was filed as
              // evidence that the reader got it right. On the real job that cost
              // twenty-one of twenty-two corrections.
              //
              // A value carrying an operator source is the operator's, whatever
              // button confirmed it. That rule now lives in
              // shared/field-correction-kind.ts, because it was stated here and
              // applied only here while two other sites decided the same question
              // by comparing strings — see that module for what each got wrong.
              //
              // Confirming does not change the value, so there is nothing to
              // compare: authorship is the entire question at this site.
              kind: valueIsHumanAuthored(existing)
                ? CORRECTION.fieldValueCorrected
                : CORRECTION.fieldValueVerified,
              targetId: `${current.id}:${configured.name}`,
              beforeJson: JSON.stringify({
                documentId: current.id,
                fieldName: configured.name,
                field: existing,
              }),
              afterJson: JSON.stringify({
                documentId: current.id,
                fieldName: configured.name,
                field: verifiedField,
              }),
              metadataJson: JSON.stringify({
                fieldKind: configured.kind,
                evidencePageNumber: verifiedField.evidencePageNumber,
                evidenceLineIndexes: verifiedField.evidenceLineIndexes ?? [],
              }),
            },
          });
        }
        for (const before of agreeingUncheckedRows(current.lineItems)) {
          const after = { ...before, verified: true, arithmeticValidated: true };
          current = await desktop.saveDocumentCorrection({
            jobId: job.id,
            batchId: confirmationBatch.batchId,
            document: settledDocument({
              ...current,
              lineItems: current.lineItems.map((row) =>
                row.rowNumber === before.rowNumber ? after : row,
              ),
            }),
            correction: {
              kind: CORRECTION.lineItemCorrected,
              targetId: `${current.id}:${before.rowNumber}`,
              beforeJson: JSON.stringify({ documentId: current.id, row: before }),
              afterJson: JSON.stringify({ documentId: current.id, row: after }),
            },
          });
        }
        reconcile(current);
        // Where this document begins is part of what was just vouched for: a
        // document holding pages that did not belong would not be right. Without
        // this the boundary ledger only ever hears about boundaries somebody
        // CHANGED, and most of them are correct — 284 observations, no labels, and
        // no boundary miner able to reach its three verified starts.
        //
        // A page-one opening itself teaches nothing, but every later page inside
        // that document teaches a continuation. Do NOT use selectedDocument here.
        // Export retires documents from the active projection, so the
        // document that originally opened at page three eventually becomes index
        // zero. Treating that projected position as batch position stranded the
        // document behind an unproved boundary route even after the operator had
        // confirmed the whole thing.
        const opensAt = current.pages[0]?.number;
        if (opensAt !== undefined && (opensAt > 1 || current.pages.length > 1)) {
          const opening = current.pages[0];
          const boundaryConfirmed =
            opensAt > 1
              ? {
                  ...current,
                  pages: current.pages.map((page, index) =>
                    index !== 0
                      ? page
                      : {
                          ...page,
                          boundaryConfidence: 1,
                          boundaryReasons: ['You confirmed this document starts here'],
                        },
                  ),
                }
              : current;
          const verifiedDocumentBatchPages = current.pages.map((page) => page.number);
          current = await desktop.saveDocumentCorrection({
            jobId: job.id,
            batchId: confirmationBatch.batchId,
            document: boundaryConfirmed,
            correction: {
              kind: CORRECTION.boundaryVerified,
              targetId: `${current.id}:boundaries`,
              beforeJson: JSON.stringify({
                documentId: current.id,
                batchPages: verifiedDocumentBatchPages,
                boundaryConfidence: opening?.boundaryConfidence,
                boundaryReasons: opening?.boundaryReasons ?? [],
              }),
              afterJson: JSON.stringify({
                documentId: current.id,
                batchPages: verifiedDocumentBatchPages,
              }),
              metadataJson: JSON.stringify({
                verifiedStartBatchPage: opensAt,
                verifiedDocumentBatchPages,
              }),
            },
          });
          reconcile(current);
        }
        // The type is the highest-value label of the lot, and it has its own path.
        // Named by id: the operator may be documents away by the time the chain
        // gets here, and "whatever is selected" is no longer this document.
        if (!current.documentTypeVerified && documentSchema) {
          const typed = await setDocumentType(documentSchema.id, true, job, target.id);
          if (!typed) throw new Error('The document type could not be confirmed.');
          current = typed;
        }
        // Only this final receipt means "the whole document is confirmed." The
        // field/type/boundary writes above are intentionally not release
        // authorization: if any one of them fails, there is no partial
        // confirmation for continuous export to mistake for a completed answer.
        const missingRequired = configuredFields.some(
          (configured) =>
            configured.required &&
            (configured.kind === 'line-items'
              ? current.lineItems.length === 0
              : !current.fields.some(
                  (field) => field.name === configured.name && Boolean(field.value.trim()),
                )),
        );
        if (missingRequired)
          throw new Error('Fill every required value before confirming the document.');
        if (
          current.lineItems.some(
            (row) =>
              row.quantity != null &&
              row.unitPrice != null &&
              row.amount != null &&
              !row.arithmeticValidated,
          )
        )
          throw new Error('Resolve the line-item arithmetic before confirming the document.');
        if (
          current.fields.some(
            (field) =>
              field.name !== 'Document type' &&
              Boolean((field.canonicalValue ?? field.value).trim()) &&
              !field.verified,
          )
        )
          throw new Error('Confirm every captured value before confirming the document.');
        if (current.notReadReason || current.pages.some((page) => page.notReadReason))
          throw new Error('A page that was not read cannot be confirmed for export.');
        // Live scan results are not in SQLite yet; their ordered correction
        // ledger is replayed after recognition commits. Use the exact document
        // returned by the last staged save instead of asking the engine for a
        // batch that intentionally does not exist yet.
        const confirmationInput = confirmationBatch.batchId.startsWith('live:')
          ? current
          : await desktop.getBatchDocument({
              jobId: job.id,
              batchId: confirmationBatch.batchId,
              documentId: target.id,
            });
        if (!confirmationInput)
          throw new Error('The saved document could not be read back for confirmation.');
        // Submit the exact reviewed pre-confirmation revision. The engine first
        // compares this document with durable state, then atomically marks every
        // visible line item verified and seals the whole-document receipt. Doing
        // either transition here makes a legitimate confirmation look stale.
        const savedConfirmation = await desktop.saveDocumentCorrection({
          jobId: job.id,
          batchId: confirmationBatch.batchId,
          document: confirmationInput,
          correction: {
            kind: CORRECTION.documentConfirmed,
            targetId: confirmationInput.id,
            beforeJson: JSON.stringify({ documentId: confirmationInput.id }),
            afterJson: JSON.stringify({ documentId: confirmationInput.id }),
            metadataJson: JSON.stringify({ confirmedWithDocument: true }),
          },
        });
        // A live capture has no durable batch yet: main stages the exact command
        // above and returns its unchanged input. Paint only the successful staged
        // answer here; replay still lets the engine own the durable transition.
        current = confirmationBatch.batchId.startsWith('live:')
          ? {
              ...savedConfirmation,
              lineItems: savedConfirmation.lineItems.map((row) => ({ ...row, verified: true })),
              confirmedAsWhole: true,
            }
          : savedConfirmation;
        reconcile(current);
        reviewTally.confirmed(job.id, target.id);
        window.dispatchEvent(new CustomEvent('capturepoint-review-saved', { detail: job.id }));
        refreshLearningProgress();
      } catch (reason) {
        // A persisted whole-document save stages all corrections in `current`
        // before one atomic call. It is not an accepted watermark on failure.
        // Read back the native truth (including an uncertain successful response)
        // so a retry cannot omit confirmations that were never saved.
        if (!confirmationBatch.batchId.startsWith('live:')) {
          current =
            (await desktop
              .getBatchDocument({
                jobId: job.id,
                batchId: confirmationBatch.batchId,
                documentId: target.id,
              })
              .catch(() => null)) ?? target;
        }
        reconcile(current);
        setError(messageFor(reason, 'The document could not be confirmed.'), 'confirm-failed');
        onFailed?.(target.id);
      } finally {
        confirmingDocumentsRef.current.delete(target.id);
        signalSaveLaneChange();
        setConfirmingDocumentIds(new Set(confirmingDocumentsRef.current));
      }
    })();
    return true;
  };
  const persistFieldValue = async (
    configured: Field,
    value: string,
    evidenceLineIndexes: number[],
    before?: FieldResult,
    targetDocumentId?: string,
    targetPageNumber?: number,
    expectedFieldContract?: string,
    targetBarcodeEvidence?: BarcodeResult | null,
    draftBinding?: Pick<FieldDraftBinding, 'documentMutationToken' | 'fieldId'> &
      Partial<Pick<FieldDraftBinding, 'name' | 'baseValue'>>,
  ) => {
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop) return;
    // Ctrl+Z takes a changed value back once it has saved, unless the person has changed something since (last-change.ts).
    const change = lastChange.note();
    if (schemaRefreshBusyRef.current) {
      onNotice(
        'Finish applying the current job fields before saving this field',
        'field-save-busy',
      );
      return;
    }
    const requestedTargetId = targetDocumentId ?? batch.documents[selectedDocument]?.id;
    if (!requestedTargetId) return;
    // Bind the same rendered revision that supplied `configured`, not a newer
    // batchRef revision which may already have completed a retype before React
    // has replaced the old control that emitted this blur.
    const requestedTarget = batch.documents.find((item) => item.id === requestedTargetId);
    if (!requestedTarget) return;
    const requestedFieldContract =
      expectedFieldContract ?? documentFieldContractToken(requestedTarget);
    const requestedBatchId = batch.batchId;
    const requestedJobId = job.id;
    const originalWorkspaceStillCurrent = () =>
      mounted.current &&
      liveJobRef.current.id === requestedJobId &&
      batchRef.current?.batchId === requestedBatchId &&
      batchRef.current.documents.some((item) => item.id === requestedTargetId);
    const draftWorkspaceStillCurrent = () => !draftBinding || originalWorkspaceStillCurrent();
    // An OCR or barcode pick names lines of the page as it was read when the person picked. A page
    // turn reads that page again (a new image, new line numbers), so a pick made while the page was
    // turning, or before a turn that landed since, must not be saved against the new reading:
    // learning would take those line numbers as where this value sits. Typed values carry no lines.
    const pickedPageNumber =
      evidenceLineIndexes.length > 0 || targetBarcodeEvidence
        ? (targetPageNumber ?? requestedTarget.pages[selectedPage]?.number)
        : undefined;
    const pickedPageReading = (target: DocumentResult | undefined) => {
      const page = target?.pages.find((item) => item.number === pickedPageNumber);
      return page ? JSON.stringify([page.previewPath, page.rotationDegrees ?? 0]) : null;
    };
    const pickedReading = pickedPageReading(requestedTarget);
    // A full circle of turns leaves the image and rotation as they were; the count of saved turns still moves.
    const pickedTurnSaves =
      pickedPageNumber == null
        ? 0
        : (pageTurnSavesRef.current.get(pageTurnKey(requestedTargetId, pickedPageNumber)) ?? 0);
    const pickReadAgain = () =>
      pickedPageNumber != null &&
      (pageTurnBusy(pageTurnsRef.current.get(pageTurnKey(requestedTargetId, pickedPageNumber))) ||
        (pageTurnSavesRef.current.get(pageTurnKey(requestedTargetId, pickedPageNumber)) ?? 0) !==
          pickedTurnSaves ||
        pickedPageReading(
          batchRef.current?.documents.find((item) => item.id === requestedTargetId),
        ) !== pickedReading);
    const refusePickReadAgain = () =>
      onNotice(
        "This page was read again after you picked that text, so it wasn't saved. Pick it again.",
        'field-save-failed',
      );
    if (pickReadAgain()) {
      refusePickReadAgain();
      return;
    }
    // A detached field can blur in the render before a previous save's disabled
    // state arrives. Preserve that captured value by waiting instead of silently
    // discarding the action.
    for (;;) {
      while (fieldSavingRef.current || wholeBatchMutationRef.current) {
        onNotice('Your field change is queued · finishing the current save');
        await (wholeBatchMutationRef.current
          ? wholeBatchCompletionRef.current
          : fieldSaveCompletionRef.current);
        const currentTarget = batchRef.current?.documents.find(
          (item) => item.id === requestedTargetId,
        );
        if (!mounted.current || batchRef.current?.batchId !== requestedBatchId) return;
        if (
          !currentTarget ||
          documentFieldContractToken(currentTarget) !== requestedFieldContract
        ) {
          onNotice(
            "This document's fields changed while the edit was waiting, so the old field was not saved",
            'field-save-failed',
          );
          return;
        }
      }
      if (lineMutationAdmitted(requestedTargetId)) {
        onNotice('Saving the line-item correction, then saving this field');
        if (!(await waitForLineMutations(requestedTargetId))) {
          onNotice(
            'The line-item correction was not saved, so this field was not changed',
            'field-save-failed',
          );
          return;
        }
      }
      if (schemaRefreshBusyRef.current) {
        onNotice(
          'Finish applying the current job fields before saving this field',
          'field-save-busy',
        );
        return;
      }
      if (confirmingDocumentsRef.current.has(requestedTargetId)) {
        onNotice(
          "This document is being confirmed, so that value wasn't saved. Pick it again once it is confirmed.",
          'field-save-busy',
        );
        return;
      }
      if (structuralMutationBlocks(requestedTargetId) || pageEditBusyRef.current) {
        onNotice(
          'Finish the current page or boundary change before saving this field',
          'field-save-busy',
        );
        return;
      }
      if (typeSavingDocumentIdRef.current === requestedTargetId) {
        onNotice(
          "Finish changing this document's type, then enter the value in its current fields",
          'field-save-busy',
        );
        return;
      }
      // A line-item await above may have let another writer enter. Rejoin its
      // completion, then claim synchronously; never discard the captured text.
      if (fieldSavingRef.current || wholeBatchMutationRef.current) continue;
      if (!draftWorkspaceStillCurrent()) return;
      // The turn holds the field lane while it runs, so a pick queued behind it gets here after it.
      // Once this save holds the lane no turn can start until it is saved.
      if (pickReadAgain()) {
        refusePickReadAgain();
        return;
      }
      // Claim the full-document mutation lane synchronously. A line edit can be
      // admitted from a detached window before React has painted `fieldSaving`.
      if (!openFieldSaveLane(requestedTargetId, false, Boolean(draftBinding))) {
        onNotice(
          'The document changed before this field could be saved. Please try again.',
          'field-save-failed',
        );
        return;
      }
      break;
    }
    setError(null);
    let ledgerBefore = before;
    let rollbackBefore: FieldResult | undefined;
    let optimisticPainted = false;
    try {
      // Preserve a field gesture that lands in the same render tick as an
      // already-admitted line edit. Freeze admission, drain that lane, and then
      // build from the engine-accepted document rather than dropping either edit.
      if (pendingLineSaves.current > 0) {
        onNotice('Saving the line-item correction, then saving this field');
        const lineSavesSucceeded = await waitForPendingSaveLane(
          () => pendingLineSaves.current,
          () => lineSaveChain.current,
          () => lineSaveFailed.current,
        );
        if (!lineSavesSucceeded) {
          onNotice(
            'The line-item correction was not saved, so this field was not changed',
            'field-save-failed',
          );
          return;
        }
      }
      if (
        !(await hydrateDocumentForWholeMutation(
          requestedTargetId,
          'The complete document could not be loaded, so this field was not saved',
          'field-save-failed',
        ))
      )
        return;
      const targetDocument = (batchRef.current ?? batch).documents.find(
        (item) => item.id === requestedTargetId,
      );
      if (!targetDocument) return;
      if (documentFieldContractToken(targetDocument) !== requestedFieldContract) {
        onNotice(
          "This document's fields changed before the edit could be saved. Please select its current field.",
          'field-save-failed',
        );
        return;
      }
      const draftStillCurrent = () => {
        if (!draftBinding) return true;
        if (!draftWorkspaceStillCurrent()) return false;
        const current = batchRef.current?.documents.find((item) => item.id === requestedTargetId);
        const schema =
          liveJobRef.current.documentTypes.find((type) => type.id === current?.documentTypeId) ??
          liveJobRef.current.documentTypes.find((type) => type.name === current?.documentType);
        return Boolean(
          current &&
          fieldDraftStillApplies(current, draftBinding) &&
          schema?.fields.some(
            (field) => field.id === draftBinding.fieldId && field.name === configured.name,
          ),
        );
      };
      // Not silently: a draft refused here ends "Not saved" with no reason otherwise.
      const draftChanged = () =>
        onNotice(
          'This document changed while your value was being saved. Your typing is kept; check the current value.',
          'field-save-failed',
        );
      if (!draftStillCurrent()) {
        draftChanged();
        return;
      }
      const existing = targetDocument.fields.find((item) => item.name === configured.name);
      ledgerBefore = before ?? existing;
      // The gesture's opening value belongs in the correction ledger, but a
      // failed optimistic save must restore the authoritative value found after
      // hydration. Those can differ when a background refresh landed meanwhile.
      rollbackBefore = existing;
      settledByHand.current = targetDocument.id;
      const interpretation = await desktop.interpretField({ kind: configured.kind, value });
      if (!draftStillCurrent()) {
        draftChanged();
        return;
      }
      // The field and its correction (review-edits.ts: support corrects through the same rule).
      const edit = fieldValueEdit({
        document: targetDocument,
        configured,
        value,
        interpretation,
        before: ledgerBefore,
        existing,
        evidenceLineIndexes,
        barcodeEvidence: targetBarcodeEvidence,
        viewedPage: targetPageNumber ?? targetDocument.pages[selectedPage]?.number,
      });
      const nextField = edit.field;
      // The third copy of this rule, and the one that disagreed most: it ignored
      // whether the type had been confirmed and counted only errors, so saving a
      // field could quietly mark an untyped document as needing nothing.
      const nextDocument = settledDocument(edit.document);
      // From the LIVE batch by identity, not the click-render's by position:
      // a confirm chain reconciling another document while this save is in
      // flight must not be silently reverted by this write landing on top of
      // it (adversarial review — the same rule reconcile() states above).
      const live = batchRef.current ?? batch;
      const next = {
        ...live,
        documents: live.documents.map((item) =>
          item.id === targetDocument.id ? nextDocument : item,
        ),
      };
      // Typing's own revisions: a detached press sent right after this typing may be bound to the one before.
      const paintedOver = live.documents.find((item) => item.id === targetDocument.id);
      if (draftBinding && paintedOver)
        ownTypingRevisions.record(
          targetDocument.id,
          reviewDocumentMutationToken(paintedOver),
          reviewDocumentMutationToken(nextDocument),
        );
      batchRef.current = next;
      setBatch(next);
      onRestored(next);
      optimisticPainted = true;
      let savedDocument = await desktop.saveDocumentCorrection({
        jobId: job.id,
        batchId: next.batchId,
        document: nextDocument,
        correction: edit.correction,
      });
      // A value the person changed (not one re-saved as it was) is review work: counted, never sent itself.
      if (fieldCorrectionIsCorrection(ledgerBefore, value))
        reviewTally.fieldChanged(requestedJobId, targetDocument.id);
      // Native ownership was established before awaiting this save. It may
      // finish for the original document, but never restore a workspace left
      // behind (or replace another job that happens to reuse a document ID).
      if (!originalWorkspaceStillCurrent()) return savedDocument;
      cacheAuthoritativeProjectionSkeleton(savedDocument);
      const settledLive = batchRef.current ?? next;
      const savedOver = settledLive.documents.find((item) => item.id === savedDocument.id);
      if (draftBinding && savedOver)
        ownTypingRevisions.record(
          savedDocument.id,
          reviewDocumentMutationToken(savedOver),
          reviewDocumentMutationToken(savedDocument),
        );
      const saved = {
        ...settledLive,
        documents: settledLive.documents.map((item) =>
          item.id === savedDocument.id ? savedDocument : item,
        ),
      };
      batchRef.current = saved;
      setBatch(saved);
      onRestored(saved);
      invalidateBoundaryUndoFor(savedDocument.id);
      // A changed value on a stored batch can be taken back: the engine puts the field back as it was
      // and removes the correction. Only when the ledger's "before" is the field this save replaced.
      const savedField = savedDocument.fields.find((item) => item.name === configured.name);
      if (
        savedField &&
        !next.batchId.startsWith('live:') &&
        fieldCorrectionIsCorrection(ledgerBefore, value) &&
        JSON.stringify(ledgerBefore ?? null) === JSON.stringify(existing ?? null)
      ) {
        const entry: ValueUndo = {
          jobId: requestedJobId,
          batchId: next.batchId,
          documentId: savedDocument.id,
          fieldName: configured.name,
          field: savedField,
        };
        setValueUndo(entry);
        lastChange.settle(change, { kind: 'value', entry });
      } else setValueUndo(null);
      return savedDocument;
    } catch {
      if (!originalWorkspaceStillCurrent()) return;
      // Roll the failed optimistic value out of both React state and the async
      // authority ref. Leaving it in batchRef let a fast whole-document confirm
      // seal a value the engine had just refused. Preserve unrelated work and
      // restore only this field (or remove it when this edit had created it).
      if (optimisticPainted) {
        const live = batchRef.current ?? batch;
        const rolledBack = {
          ...live,
          documents: live.documents.map((item) => {
            if (item.id !== requestedTargetId) return item;
            const fields = rollbackBefore
              ? item.fields.some((fieldValue) => fieldValue.name === configured.name)
                ? item.fields.map((fieldValue) =>
                    fieldValue.name === configured.name ? rollbackBefore! : fieldValue,
                  )
                : [...item.fields, rollbackBefore]
              : item.fields.filter((fieldValue) => fieldValue.name !== configured.name);
            return settledDocument({ ...item, fields });
          }),
        };
        batchRef.current = rolledBack;
        setBatch(rolledBack);
        onRestored(rolledBack);
      }
      setError('This value could not be saved. The saved value is unchanged.', 'field-save-failed');
    } finally {
      closeFieldSaveLane();
    }
  };
  fieldDraftSubmitRef.current = async (draft) => {
    const refused = (reason: string): FieldDraftOutcome => ({ status: 'refused', reason });
    const current = batchRef.current?.documents.find((item) => item.id === draft.documentId);
    if (!current || !mounted.current)
      return refused('That document is no longer open. Your typing is kept.');
    const schema =
      liveJobRef.current.documentTypes.find((type) => type.id === current.documentTypeId) ??
      liveJobRef.current.documentTypes.find((type) => type.name === current.documentType);
    const configured = schema?.fields.find(
      (field) => field.id === draft.fieldId && field.name === draft.name,
    );
    if (!configured || documentFieldContractToken(current) !== draft.fieldContractToken)
      return refused(
        'This field or document type changed. Your typing is kept; check the current field before saving.',
      );
    if (!fieldDraftStillApplies(current, draft))
      return refused(
        'This document changed since you began typing. Check the current value before saving yours.',
      );
    const sameField = (field: FieldResult) =>
      field.fieldId
        ? field.fieldId === draft.fieldId && field.name === draft.name
        : field.name === draft.name;
    const original = current.fields.find(sameField);
    const saved = await persistFieldValue(
      configured,
      draft.value,
      [],
      original,
      draft.documentId,
      draft.pageNumber,
      draft.fieldContractToken,
      null,
      draft,
    );
    const accepted = saved?.id === draft.documentId ? saved.fields.find(sameField) : undefined;
    // The engine normalises what it stores (typed "1234" is kept as "1,234.00"), so an exact
    // string match refused saves that had worked: the draft stayed "not saved" over the saved
    // value and the document flickered as it refreshed (owner, 2026-09-27). The same value
    // written differently counts as saved.
    const sameValue = (left: string, right: string) =>
      left === right ||
      left.replace(/[\s,$€£]/g, '').toLowerCase() ===
        right.replace(/[\s,$€£]/g, '').toLowerCase() ||
      (Number.isFinite(Number(left.replace(/[\s,$€£]/g, ''))) &&
        left.replace(/[\s,$€£]/g, '') !== '' &&
        Number(left.replace(/[\s,$€£]/g, '')) === Number(right.replace(/[\s,$€£]/g, '')));
    // Acknowledge the typing itself: the store clears a draft only when the outcome carries the
    // draft's own value, so answering with the engine's spelling left "1234" stuck as "not saved".
    return accepted?.verified && sameValue(accepted.value ?? '', draft.value)
      ? { status: 'saved', value: draft.value }
      : refused('Not saved. Your typing is kept; check the current value before trying again.');
  };
  const fieldDraftBinding = (configured: Field, recognized?: FieldResult): FieldDraftBinding => ({
    documentId: document.id,
    fieldId: configured.id,
    name: configured.name,
    documentMutationToken: reviewDocumentMutationToken(document),
    fieldContractToken: documentFieldContractToken(document),
    pageNumber: document.pages[selectedPage]?.number ?? document.pages[0]?.number ?? 1,
    baseValue: recognized?.value ?? '',
  });
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const persistViewerSelection = (configured: Field) => {
    const selected = selectionMenu;
    if (!selected) return;
    const live = batchRef.current ?? batch;
    const currentTarget = live.documents[selectedDocument];
    const sourcePageIndex =
      currentTarget?.pages.findIndex((page) => page.number === selected.pageNumber) ?? -1;
    const currentPageNumber = currentTarget?.pages[selectedPage]?.number;
    if (
      document.id !== selected.documentId ||
      currentTarget?.id !== selected.documentId ||
      (currentTarget &&
        reviewDocumentMutationToken(currentTarget) !== selected.documentMutationToken) ||
      currentPageNumber !== selected.pageNumber ||
      sourcePageIndex < 0
    ) {
      setSelectionMenu(null);
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
      onNotice(
        'That selection belonged to the page you left, so nothing was changed',
        'text-select-refused',
      );
      return;
    }
    const before = currentTarget.fields.find((fieldValue) => fieldValue.name === configured.name);
    setSelectedPage(sourcePageIndex);
    setActiveEvidenceLines(selected.lineIndexes);
    setActiveBarcodeEvidence(selected.barcode ?? null);
    setActiveEvidenceTarget({ documentId: selected.documentId, pageNumber: selected.pageNumber });
    setSelectionMenu(null);
    void persistFieldValue(
      configured,
      selected.barcode ? selected.text : selectedTextForField(selected.text, configured),
      selected.lineIndexes,
      before,
      selected.documentId,
      selected.pageNumber,
      undefined,
      selected.barcode ?? null,
    );
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const beginFieldEdit = (configured: Field, recognized?: FieldResult) => {
    // The document being edited stays in the Teach / Needs review list until the
    // operator moves off it: a saved field could otherwise take it out of the filter,
    // and the screen jumped to another document mid-edit, so the typing looked lost
    // (owner, 2026-09-27).
    settledByHand.current = document.id;
    if (!compareSources || detachedPanels.fields) {
      const page = document.pages[selectedPage];
      const matches =
        recognized?.value && page ? fieldDisplayMatches(page.lines, recognized.value) : [];
      const viewer = viewerRef.current;
      const viewport = viewer?.getBoundingClientRect();
      const visible =
        viewport &&
        matches.find((index) => {
          const element = viewer?.querySelector(
            `[data-reader-index="${selectedPage}"] text[data-line-index="${index}"]`,
          );
          const rect = element?.getBoundingClientRect();
          return (
            rect &&
            rect.bottom > viewport.top &&
            rect.top < viewport.bottom &&
            rect.right > viewport.left &&
            rect.left < viewport.right
          );
        });
      if (page && matches.length) {
        showEvidence(page.number, [typeof visible === 'number' ? visible : matches[0]]);
        setActiveEvidenceLines(matches);
        citeActiveEvidence(matches, recognized);
      } else {
        showEvidence(
          recognized?.evidencePageNumber,
          recognized?.evidenceLineIndexes,
          recognized?.barcodeEvidence,
        );
        citeActiveEvidence(recognized?.evidenceLineIndexes, recognized);
      }
    }
  };
  /**
   * Settles a lookup tie with one of the rows the list offered. Every unconfirmed field
   * that lookup fills on this document takes that row's value, through the same save as
   * typing it, so the pick is recorded as the operator's own answer.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const pickLookupChoice = async (documentId: string, fieldName: string, rowNumber: number) => {
    const live = (batchRef.current ?? batch)?.documents.find((item) => item.id === documentId);
    const choice = live
      ? tieChoices(live, fieldName).find((candidate) => candidate.rowNumber === rowNumber)
      : undefined;
    if (!live || !choice) {
      onNotice(
        'That field changed. Check its current value before choosing a row.',
        'field-save-failed',
      );
      return;
    }
    const schema =
      job.documentTypes.find((type) => type.id === live.documentTypeId) ??
      job.documentTypes.find(
        (type) =>
          type.name.localeCompare(live.documentType, undefined, { sensitivity: 'accent' }) === 0,
      );
    const targets = choice.fills.map((fill) => ({
      fill,
      configured: schema?.fields.find((field) => field.name === fill.fieldName),
    }));
    if (
      targets.some(
        (target) =>
          !target.configured || fieldDraftStore.get({ documentId, fieldId: target.configured.id }),
      )
    ) {
      onNotice('Finish or discard the value you are typing, then choose a row.', 'field-save-busy');
      return;
    }
    for (const { fill, configured } of targets) {
      const current = (batchRef.current ?? batch)?.documents.find((item) => item.id === documentId);
      if (!current) return;
      const saved = await persistFieldValue(
        configured!,
        fill.value,
        [],
        current.fields.find((field) => field.name === fill.fieldName),
        documentId,
        undefined,
        documentFieldContractToken(current),
      );
      if (!saved) return;
    }
  };
  // Leaving a field's box clears what it showed. Its typing saves through the field drafts (FieldDraftStore).
  const finishFieldEdit = (configured: Field) => {
    if (arrivedFieldId === configured.id) setArrivedFieldId(null);
    setActiveEvidenceLines([]);
    setActiveBarcodeEvidence(null);
    setActiveLineItemRow(null);
  };
  const persistLineItemChange = async (
    kind:
      | typeof CORRECTION.lineItemCorrected
      | typeof CORRECTION.lineItemAdded
      | typeof CORRECTION.lineItemDeleted,
    next: ProcessResult,
    targetId: string,
    before: LineItemRow | null,
    after: LineItemRow | null,
    rollback?: ProcessResult,
    requestedDocumentId?: string,
    draft?: FieldDraft,
    receipt?: { document?: DocumentResult },
  ) => {
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop) return false;
    lastChange.note(); // a row saved has no undo for Ctrl+Z (last-change.ts)
    const targetDocumentId = requestedDocumentId ?? document.id;
    const requestedJobId = job.id;
    const requestedBatchId = batch.batchId;
    const originalWorkspaceStillCurrent = () =>
      mounted.current &&
      liveJobRef.current.id === requestedJobId &&
      batchRef.current?.batchId === requestedBatchId &&
      batchRef.current.documents.some((item) => item.id === targetDocumentId);
    const draftStillCurrent = () => {
      if (!originalWorkspaceStillCurrent()) return false;
      if (!draft) return true;
      const current = batchRef.current?.documents.find((item) => item.id === targetDocumentId);
      return Boolean(
        current &&
        reviewDocumentMutationToken(current) === draft.documentMutationToken &&
        lineDraftMatchesRow(draft, current),
      );
    };
    if (!draftStillCurrent()) return false;
    // Editors remain available while automatic catch-up owns the batch, and while a page turn or
    // a boundary change owns this document (turn-page.md: a value typed meanwhile is saved after
    // it). Keep the submitted intent pending until that owner releases it, then enforce the
    // original revision fence; waiting never authorizes a stale overwrite.
    const rowsAtCall = batchRef.current?.documents.find(
      (item) => item.id === targetDocumentId,
    )?.lineItems;
    let waitedForStructure = false;
    while (
      wholeBatchMutationRef.current ||
      (structuralMutationBlocks(targetDocumentId) &&
        !pageEditBusyRef.current &&
        !schemaRefreshBusyRef.current)
    ) {
      if (wholeBatchMutationRef.current) await wholeBatchCompletionRef.current;
      else {
        waitedForStructure = true;
        await nextSaveLaneChange();
      }
      if (!draftStillCurrent()) {
        // A join or an undone split took the document away meanwhile. A draft is kept and shown
        // refused; a row added or removed with a button had nothing else to say it was not saved.
        if (
          waitedForStructure &&
          !draft &&
          mounted.current &&
          liveJobRef.current.id === requestedJobId &&
          batchRef.current?.batchId === requestedBatchId
        )
          onNotice(
            'The document changed while this line item was waiting, so it was not saved',
            'line-item-save-failed',
          );
        return false;
      }
    }
    // Rows are named by number. A row edit or removal made before the document was read again
    // could land on a different row afterwards, so it is not saved; an added row is.
    if (
      waitedForStructure &&
      kind !== CORRECTION.lineItemAdded &&
      batchRef.current?.documents.find((item) => item.id === targetDocumentId)?.lineItems !==
        rowsAtCall
    ) {
      onNotice(
        'The document changed while this line item was waiting, so it was not saved',
        'line-item-save-failed',
      );
      return false;
    }
    if (schemaRefreshBusyRef.current) {
      onNotice(
        'Finish applying the current job fields before saving this line item',
        'line-item-busy',
      );
      return false;
    }
    if (structuralMutationBlocks(targetDocumentId) || pageEditBusyRef.current) {
      onNotice(
        'Finish the current page or boundary change before saving this line item',
        'line-item-busy',
      );
      return false;
    }
    const changesRowStructure =
      kind === CORRECTION.lineItemAdded || kind === CORRECTION.lineItemDeleted;
    if (changesRowStructure && lineMutationAdmissions.current > 0) {
      onNotice(
        'Finish saving the current line item before adding or removing a row',
        'line-item-busy',
      );
      return false;
    }
    if (!changesRowStructure && lineStructureMutationDocumentId.current) {
      onNotice(
        'Finish adding or removing the row before editing another line item',
        'line-item-busy',
      );
      return false;
    }
    let savedThisMutation = false;
    // Typing in a cell (its draft): its revisions are the document's own typing (ownTypingRevisions).
    const typed = Boolean(draft);
    if (!admitLineMutation(targetDocumentId, typed)) {
      onNotice('Finish the current batch update before saving this line item', 'line-item-busy');
      return false;
    }
    if (changesRowStructure) lineStructureMutationDocumentId.current = targetDocumentId;
    try {
      if (typeSavingDocumentIdRef.current === targetDocumentId) {
        onNotice(
          "Finish changing this document's type, then edit its current line items",
          'line-item-busy',
        );
        return false;
      }
      if (
        schemaRefreshBusyRef.current ||
        structuralMutationBlocks(targetDocumentId) ||
        pageEditBusyRef.current
      ) {
        onNotice(
          'The document changed while this line item was waiting, so it was not saved',
          'line-item-save-failed',
        );
        return false;
      }
      if (confirmingDocumentsRef.current.has(targetDocumentId)) {
        onNotice(
          'This document is being confirmed; its line items are temporarily locked',
          'line-item-busy',
        );
        return false;
      }
      // A field save owns the same whole-document native command. Preserve a line
      // gesture from the pre-render window by waiting for that exact save instead
      // of allowing two snapshots to race or silently dropping the row edit.
      if (fieldSavingRef.current) {
        // A page turn on another document holds the lane too (sendPageTurn).
        onNotice(
          pageTurnOwnsStructure()
            ? 'Finishing the page turn, then saving this line item'
            : 'Saving the field, then saving this line item',
        );
        await fieldSaveCompletionRef.current;
      }
      if (!draftStillCurrent()) return false;
      if (
        schemaRefreshBusyRef.current ||
        structuralMutationBlocks(targetDocumentId) ||
        pageEditBusyRef.current ||
        typeSavingDocumentIdRef.current === targetDocumentId
      ) {
        onNotice(
          'The document changed while this line item was waiting, so it was not saved',
          'line-item-save-failed',
        );
        return false;
      }
      if (confirmingDocumentsRef.current.has(targetDocumentId)) {
        onNotice(
          'This document is being confirmed; its line items are temporarily locked',
          'line-item-busy',
        );
        return false;
      }
      if (
        !(await hydrateDocumentForWholeMutation(
          targetDocumentId,
          'The complete document could not be loaded, so this line item was not saved',
          'line-item-save-failed',
        ))
      )
        return false;
      if (!draftStillCurrent()) return false;
      settledByHand.current = targetDocumentId;
      // The complaint has to go with the fault, or the document keeps saying it
      // needs review after the operator has fixed it.
      //
      // The arithmetic finding is recorded when a document is read and only ever
      // recomputed by re-extraction. The screen re-checks it against the rows, so a
      // corrected table read as settled there and as outstanding in the stored
      // flag — the thumbnail sat red until it was clicked and then turned green
      // with the operator having done nothing. Measured on the real job: six
      // documents in exactly that state, all of them tables the operator had
      // already put right.
      const previous = rollback ?? batch;
      const liveAtAdmission = batchRef.current ?? previous;
      const beforeAdmission =
        liveAtAdmission.documents.find((item) => item.id === targetDocumentId) ??
        previous.documents.find((item) => item.id === targetDocumentId);
      if (!beforeAdmission) return false;
      const requestedTableIdentity = lineDraftTableIdentity(beforeAdmission);
      let effectiveBefore = before;
      let effectiveAfter = after;
      let effectiveTargetId = targetId;
      // Add is an intent, not a row identity. A paged skeleton has no rows, so a
      // caller can only supply a provisional number. Allocate the real number from
      // the hydrated document; otherwise every unopened invoice tries to add row 1.
      if (kind === CORRECTION.lineItemAdded) {
        if (!after) return false;
        const rowNumber = Math.max(0, ...beforeAdmission.lineItems.map((row) => row.rowNumber)) + 1;
        effectiveAfter = { ...after, rowNumber, source: OPERATOR_ENTERED_ROW, verified: false };
        effectiveTargetId = `${targetDocumentId}:${rowNumber}`;
      }
      // Rebuild this one gesture against the hydrated/live rows. `next` was formed
      // by the control before hydration and can therefore be a skeleton-shaped
      // whole-document snapshot; it is never row authority.
      const optimisticRows = applyLineItemSave(
        beforeAdmission.lineItems,
        kind,
        effectiveBefore,
        effectiveAfter,
      );
      const optimisticDocument = { ...beforeAdmission, lineItems: optimisticRows };
      const optimistic = withSettledArithmetic(
        {
          ...liveAtAdmission,
          documents: liveAtAdmission.documents.map((item) =>
            item.id === targetDocumentId ? optimisticDocument : item,
          ),
        },
        targetDocumentId,
      );
      if (pendingLineSaves.current === 0) {
        lineSaveFailed.current = false;
        lineSaveWatermarks.current.clear();
        lineSaveUntyped.current.clear();
      }
      if (!lineSaveWatermarks.current.has(targetDocumentId))
        lineSaveWatermarks.current.set(targetDocumentId, beforeAdmission);
      const produced = lineSaveProducedTokens.current.get(targetDocumentId) ?? new Set<string>();
      produced.add(reviewDocumentMutationToken(beforeAdmission));
      const painted = optimistic.documents.find((item) => item.id === targetDocumentId);
      if (painted) produced.add(reviewDocumentMutationToken(painted));
      lineSaveProducedTokens.current.set(targetDocumentId, produced);
      // Typing's own revisions, as a typed value's: a detached press sent right after this typing may be bound to the one
      // before. Any other line save makes this document's landing more than typing.
      if (!typed) lineSaveUntyped.current.add(targetDocumentId);
      else if (painted)
        ownTypingRevisions.record(
          targetDocumentId,
          reviewDocumentMutationToken(beforeAdmission),
          reviewDocumentMutationToken(painted),
        );
      batchRef.current = optimistic;
      setBatch(optimistic);
      onRestored(optimistic);
      pendingLineSaves.current += 1;
      signalSaveLaneChange();
      setLineSaving(true);
      setError(null);
      const reconcileWatermarks = () => {
        if (!originalWorkspaceStillCurrent()) return;
        const live = batchRef.current ?? previous;
        const restored = {
          ...live,
          documents: live.documents.map((item) => lineSaveWatermarks.current.get(item.id) ?? item),
        };
        batchRef.current = restored;
        setBatch(restored);
        onRestored(restored);
      };
      // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
      const run = async () => {
        try {
          // One failure invalidates every later optimistic snapshot already in
          // this lane. Do not let a later correction smuggle the rejected value
          // back in as part of its full-document payload.
          if (lineSaveFailed.current) return;
          if (!originalWorkspaceStillCurrent()) return;
          const current = batchRef.current?.documents.find((item) => item.id === targetDocumentId);
          if (draft && (!current || lineDraftTableIdentity(current) !== requestedTableIdentity))
            throw new Error(
              'This table changed before the edit could be saved. Your typing is kept.',
            );
          const authoritative = lineSaveWatermarks.current.get(targetDocumentId);
          if (!authoritative) throw new Error('The edited document has no durable save watermark.');
          let nextRows = applyLineItemSave(
            authoritative.lineItems,
            kind,
            effectiveBefore,
            effectiveAfter,
          );
          if (kind === CORRECTION.lineItemCorrected && effectiveBefore && effectiveAfter) {
            const durableBefore = authoritative.lineItems.find(
              (row) => row.rowNumber === effectiveBefore.rowNumber,
            );
            const projected = nextRows.find((row) => row.rowNumber === effectiveBefore.rowNumber);
            if (!durableBefore || !projected)
              throw new Error('The corrected line item no longer has one durable row.');
            assertLineItemConfirmationCurrent(durableBefore, effectiveBefore, effectiveAfter);
            const numericGesture = ['quantity', 'unitPrice', 'amount'].some(
              (key) =>
                !Object.is(
                  effectiveBefore[key as keyof LineItemRow],
                  effectiveAfter[key as keyof LineItemRow],
                ),
            );
            const requestedAcceptance =
              !numericGesture &&
              !effectiveBefore.arithmeticValidated &&
              effectiveAfter.arithmeticValidated &&
              Boolean(effectiveAfter.verified);
            const acceptanceMatchesDurableNumbers = ['quantity', 'unitPrice', 'amount'].every(
              (key) =>
                Object.is(
                  durableBefore[key as keyof LineItemRow],
                  effectiveBefore![key as keyof LineItemRow],
                ),
            );
            if (requestedAcceptance && !acceptanceMatchesDurableNumbers)
              throw new Error(
                "This line's numbers changed before ‘Looks right’ was saved. Review the current line and accept it again.",
              );
            const explicitAcceptance = requestedAcceptance && acceptanceMatchesDurableNumbers;
            // Recompute against the accumulated durable row. Two queued numeric
            // edits can make a row balance together even though each stale draft
            // did not; carrying the second draft's old verdict would leave a
            // correct row red. “Looks right” remains an explicit exception.
            const reconciled = {
              ...projected,
              arithmeticValidated:
                explicitAcceptance || arithmeticValidatedAfterEdit(durableBefore, projected),
            };
            const typedCellsChanged = ['description', 'quantity', 'unitPrice', 'amount'].some(
              (key) =>
                !Object.is(
                  effectiveBefore[key as keyof LineItemRow],
                  effectiveAfter[key as keyof LineItemRow],
                ),
            );
            // What the operator typed is their answer: the engine's standing repairs leave the row
            // as typed (LineItemRow.OperatorTyped). Without it, an amount typed beside a quantity
            // and price that multiply to something close was "repaired" back on every save.
            if (typedCellsChanged) reconciled.operatorTyped = true;
            if (typedCellsChanged && durableBefore.source === OPERATOR_ENTERED_ROW) {
              reconciled.verified = operatorEnteredRowIsComplete(reconciled);
              assertLineItemConfirmationCurrent(
                durableBefore,
                { ...effectiveBefore, verified: false },
                reconciled,
              );
            }
            nextRows = nextRows.map((row) =>
              row.rowNumber === reconciled.rowNumber ? reconciled : row,
            );
          }
          const recordedBefore = effectiveBefore
            ? (authoritative.lineItems.find((row) => row.rowNumber === effectiveBefore.rowNumber) ??
              effectiveBefore)
            : null;
          const recordedAfter = effectiveAfter
            ? (nextRows.find((row) => row.rowNumber === effectiveAfter.rowNumber) ?? effectiveAfter)
            : null;
          const nextDocument = settledDocument({
            ...authoritative,
            lineItems: nextRows,
          });
          let savedDocument = await desktop.saveDocumentCorrection({
            jobId: job.id,
            batchId: optimistic.batchId,
            document: nextDocument,
            correction: {
              kind,
              targetId: effectiveTargetId,
              beforeJson: JSON.stringify({ documentId: targetDocumentId, row: recordedBefore }),
              afterJson: JSON.stringify({ documentId: targetDocumentId, row: recordedAfter }),
            },
          });
          // The native save is already durable even if this workspace closed while
          // waiting. Its exact response acknowledges only the original draft; it
          // cannot repaint or restore a different job/batch.
          savedThisMutation = true;
          reviewTally.lineSaved(requestedJobId, targetDocumentId, before, after);
          if (receipt) receipt.document = savedDocument;
          if (!originalWorkspaceStillCurrent()) return;
          cacheAuthoritativeProjectionSkeleton(savedDocument);
          lineSaveWatermarks.current.set(targetDocumentId, savedDocument);
          invalidateBoundaryUndoFor(savedDocument.id);
          // Silent on success. An operator correcting a table tabs across four
          // cells in a row and down thirty rows; announcing each save put a toast
          // between them and the next cell, for news they could already see in the
          // row they had just fixed. Failure still speaks — that they cannot see.
          if (kind !== CORRECTION.lineItemCorrected) onNotice('Line item saved');
        } catch (reason) {
          lineSaveFailed.current = true;
          reconcileWatermarks();
          if (originalWorkspaceStillCurrent())
            setError(messageFor(reason, 'Could not save line item'), 'line-item-save-failed');
        } finally {
          pendingLineSaves.current = Math.max(0, pendingLineSaves.current - 1);
          signalSaveLaneChange();
          if (pendingLineSaves.current === 0) {
            // A landing of typing alone, every save of it saved, is the document's own typing too (ownTypingRevisions).
            const typedLandings = lineSaveFailed.current
              ? []
              : [...lineSaveWatermarks.current.keys()]
                  .filter((documentId) => !lineSaveUntyped.current.has(documentId))
                  .flatMap((documentId) => {
                    const shown = batchRef.current?.documents.find(
                      (item) => item.id === documentId,
                    );
                    return shown ? [[documentId, reviewDocumentMutationToken(shown)] as const] : [];
                  });
            // Land every engine-returned document at once. Until this point the
            // UI may retain later optimistic rows, but authority never does.
            reconcileWatermarks();
            for (const [documentId, shownToken] of typedLandings) {
              const landed = batchRef.current?.documents.find((item) => item.id === documentId);
              if (landed)
                ownTypingRevisions.record(
                  documentId,
                  shownToken,
                  reviewDocumentMutationToken(landed),
                );
            }
            lineSaveWatermarks.current.clear();
            lineSaveUntyped.current.clear();
            // The next cell typed while this lane saved began on its paint (or before it); the saved
            // copy differs from both, so that typing moves onto it rather than being refused mid-row.
            const produced = lineSaveProducedTokens.current;
            lineSaveProducedTokens.current = new Map();
            if (originalWorkspaceStillCurrent())
              for (const [documentId, tokens] of produced) {
                const landed = batchRef.current?.documents.find((item) => item.id === documentId);
                if (landed)
                  for (const typing of lineDraftStore.getSnapshot()) {
                    const binding =
                      typing.documentId === documentId
                        ? lineDraftRebaseBinding(typing, landed, tokens)
                        : null;
                    if (binding) lineDraftStore.rebase(binding);
                  }
              }
            if (originalWorkspaceStillCurrent()) setLineSaving(false);
          }
        }
      };
      lineSaveChain.current = lineSaveChain.current.then(run, run);
      await lineSaveChain.current;
      return savedThisMutation;
    } finally {
      releaseLineMutation(targetDocumentId, savedThisMutation, typed);
      if (changesRowStructure && lineStructureMutationDocumentId.current === targetDocumentId)
        lineStructureMutationDocumentId.current = null;
    }
  };
  lineDraftSubmitRef.current = async (draft) => {
    const refused = (reason: string): FieldDraftOutcome => ({ status: 'refused', reason });
    const target = lineDraftTarget(draft);
    const live = batchRef.current;
    const current = live?.documents.find((item) => item.id === draft.documentId);
    if (!mounted.current || !live || !target || !current)
      return refused('That document or row is no longer open. Your typing is kept.');
    if (!lineDraftMatchesRow(draft, current))
      return refused(
        'This table changed. The original row cannot be identified safely; copy or discard your typing.',
      );
    if (reviewDocumentMutationToken(current) !== draft.documentMutationToken)
      return refused(
        'This document changed since you began typing. Check the current row before saving yours.',
      );
    if (detachedMutationDocuments.current.has(current.id))
      return refused('This document is already saving another change. Your typing is kept.');
    const value = readLineDraftValue(target.cell, draft.value);
    if (value === undefined) return refused('Enter a valid number. Your typing is kept.');
    const before = current.lineItems.find((row) => row.rowNumber === target.rowNumber)!;
    // Typed back to the value it holds ("1500.00" over 1500): nothing changed, so nothing is recorded.
    // Saving it would put a before == after correction in the learning ledger and history.
    if (Object.is(before[target.cell] ?? null, value))
      return { status: 'saved', value: draft.value };
    const patched = { ...before, [target.cell]: value };
    const after = {
      ...patched,
      arithmeticValidated: arithmeticValidatedAfterEdit(before, patched),
    };
    const receipt: { document?: DocumentResult } = {};
    await persistLineItemChange(
      CORRECTION.lineItemCorrected,
      live,
      `${current.id}:${target.rowNumber}`,
      before,
      after,
      live,
      current.id,
      draft,
      receipt,
    );
    return lineDraftSavedOutcome(draft, receipt.document);
  };
  const addLineItem = () => {
    if (!batch || lineSaving) return;
    // Row zero is deliberately provisional. The persistence lane hydrates the
    // document and allocates the first unused row number from durable rows.
    const row: LineItemRow = {
      rowNumber: 0,
      description: '',
      quantity: undefined,
      unitPrice: undefined,
      amount: undefined,
      confidence: 1,
      arithmeticValidated: false,
      pageNumber: document.pages[selectedPage]?.number ?? document.pages[0]?.number ?? 1,
      evidenceLineIndexes: [],
    };
    void persistLineItemChange(
      CORRECTION.lineItemAdded,
      batchRef.current ?? batch,
      `${document.id}:new`,
      null,
      row,
    );
  };
  const deleteLineItem = (rowNumber: number) => {
    if (!batch || lineSaving) return;
    const before = document.lineItems.find((row) => row.rowNumber === rowNumber);
    if (!before) return;
    if (activeLineItemRow === rowNumber) setActiveLineItemRow(null);
    const next = {
      ...batch,
      documents: batch.documents.map((item, index) =>
        index !== selectedDocument
          ? item
          : {
              ...item,
              lineItems: item.lineItems
                .filter((row) => row.rowNumber !== rowNumber)
                .map((row, rowIndex) => ({ ...row, rowNumber: rowIndex + 1 })),
            },
      ),
    };
    void persistLineItemChange(
      CORRECTION.lineItemDeleted,
      next,
      `${document.id}:${rowNumber}`,
      before,
      null,
    );
  };
  const beginLineItemEdit = (row: LineItemRow, cell: LineItemCell) => {
    lineEditBefore.current.set(`${document.id}:${row.rowNumber}`, row);
    if (compareSources) {
      setActiveLineItemRow(row.rowNumber);
      return;
    }
    if (preserveRowEvidenceFocus.current === row.rowNumber) {
      preserveRowEvidenceFocus.current = null;
      return;
    }
    preserveRowEvidenceFocus.current = null;
    const evidence = lineItemEvidenceForCell(row, cell);
    // Only proved cell evidence may paint a cell. showEvidence also keeps the
    // current view when a table cell has no retained position.
    showEvidence(evidence.pageNumber, evidence.lineIndexes, null, row.rowNumber);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const removePageAt = async (documentIndex: number, pageIndex: number) => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !batch) return;
    if (pageEditBusyRef.current) {
      onNotice('Finish the current page change before removing another page', 'page-remove-busy');
      return;
    }
    const requestedTarget = batch.documents[documentIndex];
    const requestedPageNumber = requestedTarget?.pages[pageIndex]?.number;
    if (!requestedTarget || requestedPageNumber == null || requestedTarget.pages.length <= 1)
      return;
    if (confirmingDocumentsRef.current.has(requestedTarget.id)) {
      onNotice(
        'This document is being confirmed; its pages are temporarily locked',
        'page-remove-busy',
      );
      return;
    }
    if (typeSavingDocumentIdRef.current === requestedTarget.id) {
      onNotice('Finish changing the document type before removing a page', 'page-remove-busy');
      return;
    }
    const structuralToken = claimStructuralMutation([requestedTarget.id], 'removing a page');
    if (!structuralToken) {
      onNotice(
        'Finish the current page or boundary change before removing another page',
        'page-remove-busy',
      );
      return;
    }
    if (!openFieldSaveLane(requestedTarget.id)) {
      releaseStructuralMutation(structuralToken);
      onNotice('Finish saving the current value before removing a page', 'page-remove-busy');
      return;
    }
    // Ctrl+Z puts it back once it is removed, unless the person has changed something since.
    const change = lastChange.note();
    pageEditBusyRef.current = true;
    try {
      if (pendingLineSaves.current > 0) {
        // Only waits for the lane, as a page turn does: the failure receipt is batch-wide, so a
        // line save that failed on another document (it told the person itself) must not refuse
        // this removal; this document is read again from the store below either way.
        await waitForPendingSaveLane(
          () => pendingLineSaves.current,
          () => lineSaveChain.current,
        );
      }
      if (
        !(await hydrateDocumentForWholeMutation(
          requestedTarget.id,
          'The complete document could not be loaded, so its page was not removed',
          'page-remove-failed',
        ))
      )
        return;
      const working = batchRef.current ?? batch;
      const target = working.documents.find((item) => item.id === requestedTarget.id);
      const page = target?.pages.find((item) => item.number === requestedPageNumber);
      if (!target || !page || target.pages.length <= 1) return;
      const result = await desktop.removePage({
        jobId: job.id,
        batchId: working.batchId,
        documentId: target.id,
        pageNumber: page.number,
        correction: {
          kind: CORRECTION.pageRemoved,
          targetId: `${target.id}:${page.number}`,
          beforeJson: JSON.stringify({ documentId: target.id, page }),
          afterJson: JSON.stringify({
            documentId: target.id,
            pages: target.pages
              .filter((item) => item.number !== page.number)
              .map((item) => item.number),
          }),
          metadataJson: JSON.stringify({ reason: CORRECTION.pageRemoved }),
        },
      });
      reviewTally.pageRemoved(job.id, target.id);
      const replacement = result.documents[0];
      cacheAuthoritativeProjectionSkeleton(replacement);
      invalidateBoundaryUndoFor(replacement.id);
      const live = batchRef.current ?? batch;
      const next = {
        ...live,
        documents: live.documents.map((item) => (item.id === replacement.id ? replacement : item)),
      };
      batchRef.current = next;
      setBatch(next);
      onRestored(next);
      const entry = { documentId: target.id, page };
      pageUndoStack.current.push(entry);
      setPageUndoCount(pageUndoStack.current.length);
      lastChange.settle(change, { kind: 'page', entry });
      // Where the person is now, not where they were at the click: they may have moved on.
      const viewedIndex = selectedDocumentRef.current;
      if (
        next.documents[viewedIndex]?.id === replacement.id &&
        selectedPageIntent.current >= replacement.pages.length
      )
        selectDocument(viewedIndex, replacement.pages.length - 1);
    } catch (reason) {
      setError(messageFor(reason, 'The page could not be removed.'), 'page-remove-failed');
    } finally {
      pageEditBusyRef.current = false;
      closeFieldSaveLane();
      releaseStructuralMutation(structuralToken);
    }
  };
  /* DELETE (ADR-0096). On Manual, every document not yet exported can be deleted, from the document's own heading
     (here and in a moved fields panel), at once: no question. The engine moves it to the job's Deleted view and keeps
     its record and pages; Restore there puts it back, and so does the Undo of the status bar's "Deleted an Invoice ·
     Undo" (or Ctrl+Z while it shows). It leaves this list at once, and the next document of the list being worked
     opens, as after Confirm & next. Autopilot has no Delete (the engine refuses it), and an exported document is
     refused, never deleted. */
  // Pages being read (a wave running or waiting) have their own sentence: no page or boundary change is the person's to
  // finish then.
  const structureBusyForDelete = () =>
    captureInFlight.current.size > 0 ||
    captureQueueRef.current.some((item) => item.status === 'pending')
      ? 'Delete once the pages being read are in'
      : 'Finish the current page or boundary change before deleting a document';
  /**
   * Why Delete would be refused for this document now: a page or boundary change, pages being read, or a page edit,
   * confirmation or type change being saved to it. Null when it can go ahead. A value or line item being saved to it
   * is not a reason: Delete waits for it (typing then clicking Delete starts that save as the click lands), and its
   * claim tests this document's structure again then. The moved fields panel's Delete is off whenever this says no
   * (snapshot.review.deleteBusy), so a click there is never refused in this window, and while anything but typing (a
   * value or line typed) is being saved to it: only typing may move it on from the revision it was clicked on.
   */
  const deleteRefusal = (documentId: string): string | null =>
    pageEditBusyRef.current ||
    confirmingDocumentsRef.current.has(documentId) ||
    typeSavingDocumentIdRef.current === documentId
      ? 'Finish the change being saved before deleting this document'
      : !structuralMutationFree([])
        ? structureBusyForDelete()
        : null;
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const deleteDocument = async (documentId: string) => {
    const desktop = window.capturePointDesktop;
    const opening = batchRef.current ?? batch;
    if (!desktop?.deleteDocuments || !opening || deletingDocumentRef.current) return;
    if (opening.batchId.startsWith('live:')) {
      onNotice(
        'This document is still being saved. Delete it once saving finishes',
        'document-delete-busy',
      );
      return;
    }
    const refusal = deleteRefusal(documentId);
    if (refusal) {
      onNotice(refusal, 'document-delete-busy');
      return;
    }
    // A value still being saved (typed, then Delete clicked) or another change on this document finishes first; the
    // delete then goes ahead, never refused for it, so the save is never lost. False when the wait was dropped
    // (another batch, or confirming).
    if (
      !(await openFieldSaveLaneWhenFree(
        `delete:${documentId}`,
        documentId,
        'Deleting this document after the current change',
        'document-delete-busy',
      ))
    )
      return;
    const structuralToken = claimStructuralMutation([documentId], 'deleting a document');
    if (!structuralToken) {
      closeFieldSaveLane();
      onNotice(structureBusyForDelete(), 'document-delete-busy');
      return;
    }
    // Noted for Ctrl+Z only once it is deleted, with the Undo on its notice: a Delete refused or failed is no change,
    // so the earlier change keeps its undo.
    deletingDocumentRef.current = true;
    setDeletingDocumentId(documentId);
    try {
      if (pendingLineSaves.current > 0)
        await waitForPendingSaveLane(
          () => pendingLineSaves.current,
          () => lineSaveChain.current,
        );
      const working = batchRef.current ?? batch;
      if (!working) return;
      const at = working.documents.findIndex((item) => item.id === documentId);
      const target = working.documents[at];
      if (!target) return;
      const result = await desktop.deleteDocuments({
        jobId: job.id,
        batchId: working.batchId,
        documentIds: [documentId],
      });
      if (!result.done.includes(documentId)) {
        const refusal = result.refused.find((item) => item.documentId === documentId)?.reason;
        setError(
          refusal === 'exported'
            ? 'This document was already exported, so it can’t be deleted.'
            : refusal === 'deleted'
              ? 'This document was already deleted.'
              : 'This document could not be deleted.',
          'document-delete-failed',
        );
        return;
      }
      const live = batchRef.current ?? working;
      // Where it is now: the list may have changed during the call.
      const liveAt = live.documents.findIndex((item) => item.id === documentId);
      // What opens next, chosen before the list changes: the next document asked about, as Confirm & next and Skip
      // move in the one list (#103). Only when the deleted document is the one open; with none asked about, its
      // neighbour in the list (below).
      const viewed = live.documents[selectedDocumentRef.current]?.id;
      let opensNext: string | undefined;
      if (viewed === documentId) {
        const next = nextAsked(
          live.documents,
          liveAt,
          calibrating,
          (item) =>
            item.id !== documentId &&
            !isAwaitingUnderstanding(item, Boolean(captureProcessingId)) &&
            !(assistedReview && item.needsReview && awaitsAiReviewer(item)),
        );
        opensNext = next.index != null ? live.documents[next.index]?.id : undefined;
      }
      const next = reconcileBoundaryProjection(
        {
          ...live,
          documents: live.documents.filter((item) => item.id !== documentId),
          ...(live.loadedDocumentIds
            ? { loadedDocumentIds: live.loadedDocumentIds.filter((id) => id !== documentId) }
            : {}),
        },
        [documentId],
        [],
      );
      batchRef.current = next;
      setBatch(next);
      // Its split or join and its removed pages can't be put back now: their undos go with it.
      invalidateBoundaryUndoFor(documentId);
      const retainedPageUndos = pageUndoStack.current.filter(
        (entry) => entry.documentId !== documentId,
      );
      if (retainedPageUndos.length !== pageUndoStack.current.length) {
        pageUndoStack.current = retainedPageUndos;
        setPageUndoCount(retainedPageUndos.length);
      }
      if (viewed === documentId) {
        // Nothing else is asked about: its neighbour in the list opens, never an empty screen.
        const opensAt = opensNext ? next.documents.findIndex((item) => item.id === opensNext) : -1;
        const openAt = opensAt >= 0 ? opensAt : Math.min(liveAt, next.documents.length - 1);
        if (openAt >= 0) {
          selectDocument(openAt, 0, false, true);
          // It often opens at the deleted one's place, which selectDocument doesn't count as a move: its arrival guard
          // still starts again when it is drawn.
          arrivalPending.current = next.documents[openAt]?.id ?? null;
        }
      } else {
        // The person moved on during the call: the document they are on stays open, found by id in the shorter list
        // (its old position now names the one after it), on the page they are on.
        const stays = viewed ? next.documents.findIndex((item) => item.id === viewed) : -1;
        if (stays >= 0 && stays !== selectedDocumentRef.current) {
          selectedDocumentRef.current = stays;
          setSelectedDocument(stays);
        }
      }
      onRestored(next);
      const name = filedDocumentType(target, job.documentTypes, typeNameHistoryRef.current)?.name;
      // Its way back, in the status bar while the notice shows (and Ctrl+Z then): a Restore of this document.
      const deleted = { jobId: job.id, batchId: working.batchId, documentId };
      const undo: NoticeAction = {
        label: 'Undo',
        telemetry: DELETE_UNDO_TELEMETRY,
        run: () => void undoDelete(deleted),
      };
      onNotice(deletedNotice(name ? withArticle(name) : null), 'notice', undo);
      lastChange.note({ kind: 'delete', entry: undo });
      // The newest change's notice is the one in the status bar, as Ctrl+Z undoes only it: a join's line before it goes
      // (its Undo stays in the list's menu, Undo boundary change).
      setJoinNews(null);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'The document could not be deleted.',
        'document-delete-failed',
      );
    } finally {
      deletingDocumentRef.current = false;
      if (mounted.current) setDeletingDocumentId(null);
      closeFieldSaveLane();
      releaseStructuralMutation(structuralToken);
    }
  };
  /**
   * The Undo of "Deleted an Invoice · Undo" (and Ctrl+Z while it shows): a Restore of that document, as Deleted
   * documents' Restore does, and the document opens again. It takes the batch's structure as a split does, since
   * Restore saves the batch whole; while another change holds it, it says so and the notice stays. One already
   * restored (from Deleted documents or another window) is said plainly.
   */
  const undoDelete = async (deleted: { jobId: string; batchId: string; documentId: string }) => {
    const desktop = window.capturePointDesktop;
    if (
      !desktop?.restoreDocuments ||
      restoringDeletedRef.current ||
      liveJobRef.current.id !== deleted.jobId
    )
      return;
    const structuralToken = claimStructuralMutation([deleted.documentId], 'restoring a document');
    if (!structuralToken) {
      onNotice('Finish the current change, then undo the delete', 'document-delete-busy');
      return;
    }
    restoringDeletedRef.current = true;
    try {
      const result = await desktop.restoreDocuments({
        jobId: deleted.jobId,
        batchId: deleted.batchId,
        documentIds: [deleted.documentId],
      });
      // The undo is spent only once the engine answered: a Restore that fails keeps "Deleted … · Undo" and Ctrl+Z.
      // No other delete can start meanwhile (this holds the batch's structure), so a delete's notice and Ctrl+Z
      // entry are still this one's; a change made meanwhile replaced the entry and keeps its own.
      if (lastChange.undoable((change) => change.kind === 'delete')) lastChange.clear(); // nothing is left for Ctrl+Z
      if (workspaceNoticeRef.current.action?.telemetry === DELETE_UNDO_TELEMETRY)
        workspaceNoticeRef.current.dismiss();
      if (!mounted.current || liveJobRef.current.id !== deleted.jobId) return;
      if (!result.done.includes(deleted.documentId)) {
        onNotice('That document was already restored.', 'notice');
        return;
      }
      const showing = batchRef.current ?? batch;
      if (showing && showing.batchId !== deleted.batchId) return;
      const refreshed = await loadLatestWorkspaceBatch(deleted.jobId, deleted.documentId);
      const now = batchRef.current ?? batch;
      if (!refreshed || !mounted.current || (now && refreshed.batchId !== now.batchId)) return;
      // It opens once the batch it is back in is the one on screen (openAfterRestore, above the early return).
      openAfterRestore.current = deleted.documentId;
      onRestored(refreshed);
    } catch (reason) {
      if (mounted.current)
        setError(
          reason instanceof Error ? reason.message : 'The document could not be restored.',
          'document-restore-failed',
        );
    } finally {
      restoringDeletedRef.current = false;
      releaseStructuralMutation(structuralToken);
    }
  };
  /* TURN PAGE (owner-requested). One quarter turn clockwise of one page. The engine turns a copy
     of the page image (the scan itself is never written to), reads the page again, reads the
     document's values again from it, and keeps what was already confirmed. Four turns bring the
     page back to how it was scanned, which is its undo.

     IT ANSWERS AT ONCE (owner, 2026-10-10). The click turns the page on screen straight away and
     the person keeps working: other values stay editable and they can move on. The write takes
     the structural token and the field lane, as removing a page does (both admit one owner across
     the batch, so no other structural change or whole-document save runs while the page is read
     again), but not the page-removal lock; only the write waits for them, never the person.
     More clicks on the same page before the engine answers add up, and one request for the net
     turn follows the answer (page-turn-queue.ts). The answer's values are applied quietly; typing
     on the document is kept (fieldDraftConflicted). A turn that fails turns the page back and
     says so. */
  const publishPageTurns = () => {
    if (mounted.current) setPageTurns(new Map(pageTurnsRef.current));
    signalSaveLaneChange();
  };
  const settleTurnOf = (
    key: string,
    outcome: { saved: true; imagePath: string } | { saved: false },
  ) => {
    const settled = settlePageTurn(pageTurnsRef.current.get(key), outcome);
    if (settled.entry) pageTurnsRef.current.set(key, settled.entry);
    else pageTurnsRef.current.delete(key);
    publishPageTurns();
    return settled.send;
  };
  const turnPage = (documentId: string, pageNumber: number) => {
    const desktop = window.capturePointDesktop;
    const working = batchRef.current ?? batch;
    if (!desktop || !working) return;
    const target = working.documents.find((item) => item.id === documentId);
    const page = target?.pages.find((item) => item.number === pageNumber);
    if (!target || !page) return;
    if (working.batchId.startsWith('live:')) {
      onNotice(
        'This document is still being saved. Turn its page once saving finishes',
        'page-turn-busy',
      );
      return;
    }
    if (confirmingDocumentsRef.current.has(documentId)) {
      onNotice(
        'This document is being confirmed; its pages are temporarily locked',
        'page-turn-busy',
      );
      return;
    }
    if (typeSavingDocumentIdRef.current === documentId) {
      onNotice('Finish changing the document type before turning a page', 'page-turn-busy');
      return;
    }
    lastChange.note(); // a turn has no undo for Ctrl+Z to press
    const key = pageTurnKey(documentId, pageNumber);
    const asked = askPageTurn(pageTurnsRef.current.get(key), page.previewPath);
    pageTurnsRef.current.set(key, asked.entry);
    publishPageTurns();
    if (asked.send) void sendPageTurn(documentId, pageNumber, asked.send);
  };
  /** How long a turn waits for another write on its document before it gives up and turns back. */
  const PAGE_TURN_ADMISSION_MS = 120_000;
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const sendPageTurn = async (documentId: string, pageNumber: number, firstTurns: number) => {
    const desktop = window.capturePointDesktop;
    const key = pageTurnKey(documentId, pageNumber);
    const failed = () => {
      settleTurnOf(key, { saved: false });
      if (mounted.current) onNotice("Couldn't turn the page. Try again.", 'page-turn-failed');
    };
    if (!desktop) {
      failed();
      return;
    }
    // The write's locks, as before; waiting for them is the write's business, not the person's.
    let structuralToken: symbol | null = null;
    const admitBy = Date.now() + PAGE_TURN_ADMISSION_MS;
    for (;;) {
      if (!mounted.current) return;
      if (
        !confirmingDocumentsRef.current.has(documentId) &&
        typeSavingDocumentIdRef.current !== documentId &&
        !fieldSavingRef.current &&
        !wholeBatchMutationRef.current &&
        !pageEditBusyRef.current
      ) {
        structuralToken = claimStructuralMutation([documentId], 'turning a page');
        if (structuralToken && openFieldSaveLane(documentId, true)) break;
        if (structuralToken) releaseStructuralMutation(structuralToken);
        structuralToken = null;
      }
      if (Date.now() > admitBy) {
        failed();
        return;
      }
      await nextSaveLaneChange();
    }
    // The structural token and the field lane are the turn's locks. Each admits one owner across
    // the batch: no field, boundary or turn write on any document starts while this one runs.
    // Only this document's line and type edits count as blocked by structure
    // (structuralMutationBlocks); a line edit elsewhere waits for the lane instead.
    // It never takes pageEditBusyRef: that refuses work on every document outright, and the
    // person may move on to another one while the engine reads this page again.
    let quarterTurns: number | null = firstTurns;
    try {
      while (quarterTurns) {
        if (pendingLineSaves.current > 0) {
          // Only waits for the lane: the failure receipt is batch-wide, so a line save that failed
          // on another document (it told the person itself) must not cancel this turn, and the
          // turn reads this document again from the store below either way.
          await waitForPendingSaveLane(
            () => pendingLineSaves.current,
            () => lineSaveChain.current,
          );
        }
        if (
          !(await hydrateDocumentForWholeMutation(
            documentId,
            "Couldn't turn the page. Try again.",
            'page-turn-failed',
          ))
        ) {
          settleTurnOf(key, { saved: false });
          return;
        }
        const working = batchRef.current ?? batch;
        const target = working?.documents.find((item) => item.id === documentId);
        const page = target?.pages.find((item) => item.number === pageNumber);
        if (!working || !target || !page) {
          failed();
          return;
        }
        const schemas = schemaRequest(liveJobRef.current);
        const schema =
          schemas.find((type) => type.id === target.documentTypeId) ??
          schemas.find(
            (type) =>
              type.name.localeCompare(target.documentType, undefined, { sensitivity: 'accent' }) ===
              0,
          );
        const guessed = target.validationFindings.some(
          (finding) =>
            finding.code === FINDING.pageRotationGuessed &&
            findingStillHolds(finding, target) &&
            (finding.pageNumbers?.includes(page.number) ?? true),
        );
        const result = await desktop.turnPage({
          jobId: job.id,
          batchId: working.batchId,
          documentId: target.id,
          pageNumber: page.number,
          quarterTurns: quarterTurns as 1 | 2 | 3,
          ...(schema ? { documentType: schema } : {}),
        });
        pageTurnsSaved.current += 1;
        pageTurnSavesRef.current.set(key, (pageTurnSavesRef.current.get(key) ?? 0) + 1);
        const replacement = result.documents[0];
        const turned = replacement.pages.find((item) => item.number === page.number);
        reportEvent('review.page-turned', {
          jobId: job.id,
          attrs: {
            count: pageTurnsSaved.current,
            quarter_turns: ((((turned?.rotationDegrees ?? 0) / 90) % 4) + 4) % 4,
            guessed,
          },
        });
        if (!mounted.current) return;
        cacheAuthoritativeProjectionSkeleton(replacement);
        invalidateBoundaryUndoFor(replacement.id);
        const live = batchRef.current ?? working;
        const next = {
          ...live,
          documents: live.documents.map((item) =>
            item.id === replacement.id ? replacement : item,
          ),
        };
        batchRef.current = next;
        setBatch(next);
        onRestored(next);
        quarterTurns = settleTurnOf(key, {
          saved: true,
          imagePath: turned?.previewPath ?? page.previewPath,
        });
      }
    } catch (reason) {
      console.warn('Turn page failed', reason);
      failed();
    } finally {
      closeFieldSaveLane();
      if (structuralToken) releaseStructuralMutation(structuralToken);
    }
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const undoPageRemoval = async () => {
    const last = pageUndoStack.current[pageUndoStack.current.length - 1];
    const desktop = window.capturePointDesktop;
    if (!desktop || !batch || !last) return;
    if (pageEditBusyRef.current) {
      onNotice('Finish the current page change before restoring a page', 'page-remove-busy');
      return;
    }
    if (!(batchRef.current ?? batch).documents.some((item) => item.id === last.documentId)) {
      pageUndoStack.current.pop();
      setPageUndoCount(pageUndoStack.current.length);
      onNotice(
        'That document changed after the page was removed, so this Undo is no longer available',
        'page-restore-failed',
      );
      return;
    }
    if (confirmingDocumentsRef.current.has(last.documentId)) {
      onNotice(
        'This document is being confirmed; its pages are temporarily locked',
        'page-remove-busy',
      );
      return;
    }
    if (typeSavingDocumentIdRef.current === last.documentId) {
      onNotice('Finish changing the document type before restoring a page', 'page-remove-busy');
      return;
    }
    const structuralToken = claimStructuralMutation([last.documentId], 'restoring a page');
    if (!structuralToken) {
      onNotice(
        'Finish the current page or boundary change before restoring a page',
        'page-remove-busy',
      );
      return;
    }
    if (!openFieldSaveLane(last.documentId)) {
      releaseStructuralMutation(structuralToken);
      onNotice('Finish saving the current value before restoring a page', 'page-remove-busy');
      return;
    }
    lastChange.clear(); // an undo: nothing is left for Ctrl+Z
    pageEditBusyRef.current = true;
    try {
      if (pendingLineSaves.current > 0) {
        // Only waits for the lane (as removing the page does): another document's failed line
        // save must not refuse this restore; this document is read again from the store below.
        await waitForPendingSaveLane(
          () => pendingLineSaves.current,
          () => lineSaveChain.current,
        );
      }
      if (
        !(await hydrateDocumentForWholeMutation(
          last.documentId,
          'The complete document could not be loaded, so its page was not restored',
          'page-restore-failed',
        ))
      )
        return;
      const working = batchRef.current ?? batch;
      if (!working.documents.some((item) => item.id === last.documentId)) return;
      const result = await desktop.restorePage({
        jobId: job.id,
        batchId: working.batchId,
        documentId: last.documentId,
        page: last.page,
        correction: {
          kind: CORRECTION.pageRestored,
          targetId: `${last.documentId}:${last.page.number}`,
          beforeJson: JSON.stringify({ documentId: last.documentId, removed: last.page.number }),
          afterJson: JSON.stringify({ documentId: last.documentId, restored: last.page.number }),
          metadataJson: JSON.stringify({ reason: CORRECTION.pageRestored }),
        },
      });
      pageUndoStack.current.pop();
      setPageUndoCount(pageUndoStack.current.length);
      const replacement = result.documents[0];
      cacheAuthoritativeProjectionSkeleton(replacement);
      invalidateBoundaryUndoFor(replacement.id);
      const live = batchRef.current ?? batch;
      const next = {
        ...live,
        documents: live.documents.map((item) => (item.id === replacement.id ? replacement : item)),
      };
      batchRef.current = next;
      setBatch(next);
      onRestored(next);
    } catch (reason) {
      setError(messageFor(reason, 'The page could not be restored.'), 'page-restore-failed');
    } finally {
      pageEditBusyRef.current = false;
      closeFieldSaveLane();
      releaseStructuralMutation(structuralToken);
    }
  };
  /* SPLITS AND JOINS WAIT THEIR TURN; A CLICK IS NEVER REFUSED FOR BEING EARLY.

     A split or a join rewrites whole documents, so it used to be refused while
     anything else was saving on them ("Saving a value on this document - a
     moment") and the operator had to wait and click again. Now the click goes
     into boundaryQueue (boundary-queue.ts) at once, the boundary shows "Saving…"
     where the control was, and the change waits its turn: whatever is saving on
     those documents finishes first, then this change loads them whole, builds
     the new documents from what is stored NOW (never from what was on screen at
     the click), paints them and saves. Several in a row are saved in the order
     they were made.

     The engine is still the judge. A change carries the stored documents it was
     built from, and the engine refuses it if they changed after they were loaded
     (BOUNDARY_REVISION_CHANGED); the change is then rebuilt from the newer
     documents and tried again, so it can never write over newer work. A change
     that cannot be saved puts back only its own boundary and says so. */
  const MAX_BOUNDARY_ATTEMPTS = 3;
  /** Keep the operator on the page they are looking at while documents around it regroup. */
  const followSelectedPage = (before: ProcessResult, after: ProcessResult) => {
    const viewed = before.documents[selectedDocumentRef.current];
    const pageNumber =
      viewed?.pages[Math.min(selectedPageIntent.current, viewed.pages.length - 1)]?.number;
    const at = pageNumber == null ? null : locateBatchPage(after.documents, pageNumber);
    if (!at) {
      const clamped = Math.max(
        0,
        Math.min(selectedDocumentRef.current, after.documents.length - 1),
      );
      if (clamped !== selectedDocumentRef.current) {
        selectedDocumentRef.current = clamped;
        setSelectedDocument(clamped);
        setSelectedPage(0);
      }
      return;
    }
    if (at.documentIndex !== selectedDocumentRef.current) {
      selectedDocumentRef.current = at.documentIndex;
      setSelectedDocument(at.documentIndex);
    }
    if (at.pageIndex !== selectedPageIntent.current) setSelectedPage(at.pageIndex);
  };
  /** A split or a merge as built from the workspace's batch (review-edits.ts says what each part is). */
  type BoundaryChange = BoundaryChangeOf<ProcessResult, ReturnType<typeof schemaRequest>[number]>;
  /**
   * Every document a boundary change is about to rewrite, loaded whole and fresh.
   *
   * A document nobody has opened is held as a skeleton: real pages, but
   * `fields: []` and `lineItems: []`. A split or a merge rewrites the documents it
   * touches wholesale, so performing one against a skeleton destroys every value
   * the operator had confirmed on it — durably, and without a word. Nothing on
   * screen distinguishes a skeleton from a document with no values, which is why
   * this cannot be left to the operator to avoid. Fresh, because this is the copy
   * the new documents are built from and the copy the engine checks is current.
   */
  const loadForBoundaryChange = async (
    documentIds: readonly string[],
  ): Promise<ProcessResult | null> => {
    const desktop = window.capturePointDesktop;
    for (const documentId of documentIds) {
      const latest = batchRef.current;
      const latestIndex =
        latest?.documents.findIndex((document) => document.id === documentId) ?? -1;
      if (!desktop || !latest || latestIndex < 0) return null;
      if (mounted.current) {
        // The freshness poll can hand the workspace new objects for the same documents while this load
        // is out, and the load's answer is then refused as stale (hydration-result.ts compares identity).
        // The document is still here, so load it again, a few times at most, from where it is now.
        let loaded = await hydrateDocument(latestIndex, true);
        for (let again = 0; !loaded && again < 3; again++) {
          const index =
            batchRef.current?.documents.findIndex((document) => document.id === documentId) ?? -1;
          if (!mounted.current || index < 0) return null;
          loaded = await hydrateDocument(index, true);
        }
        if (!loaded) return null;
        continue;
      }
      // The workspace closed while this change waited its turn. The change is
      // still the operator's, so it is still saved, from the stored documents.
      const detail = await desktop.getBatchDocument({
        jobId: job.id,
        batchId: latest.batchId,
        documentId,
      });
      const current = batchRef.current;
      if (!detail || !current || current.batchId !== latest.batchId) return null;
      batchRef.current = {
        ...current,
        documents: current.documents.map((document) =>
          document.id === documentId ? detail : document,
        ),
      };
    }
    const loaded = batchRef.current;
    if (
      !loaded ||
      documentIds.some((documentId) => {
        const document = loaded.documents.find((item) => item.id === documentId);
        return (
          !document?.sourcePath ||
          (mounted.current && !loadedDocumentIdsRef.current.has(documentId))
        );
      })
    )
      return null;
    return loaded;
  };
  // The split and the merge themselves (review-edits.ts: support regroups pages through the same builders).
  const buildSplit = (
    working: ProcessResult,
    documentIndex: number,
    pageIndex: number,
  ): BoundaryChange | string => buildBoundarySplit(working, documentIndex, pageIndex);
  const buildMerge = (working: ProcessResult, documentIndex: number): BoundaryChange | string =>
    buildBoundaryMerge(working, documentIndex, schemaRequest(job));
  /**
   * Typing on the documents a boundary change rewrites. A value being saved is
   * waited for, so the change is built from the saved document. Typing is never
   * submitted for the operator (they may be in the middle of a word): on a
   * document the change keeps, it stays where it is and is saved, or offered
   * again, when they finish; on a document the change would remove it could
   * only be thrown away, so that change does not happen and says why.
   */
  const boundaryTypingSettled = (
    documentIds: readonly string[],
    removedIds: ReadonlySet<string>,
  ): 'ready' | 'wait' | 'kept' => {
    let state: 'ready' | 'wait' | 'kept' = 'ready';
    for (const store of [fieldDraftStore, lineDraftStore])
      for (const draft of store.getSnapshot()) {
        if (!documentIds.includes(draft.documentId)) continue;
        if (draft.state === 'saving') {
          if (state === 'ready') state = 'wait';
          continue;
        }
        if (removedIds.has(draft.documentId)) state = 'kept';
      }
    return state;
  };
  /**
   * Save one boundary change built from `working`.
   *
   * The change is painted first, then written. Everything up to the engine's
   * answer can be taken back exactly (the inverse splice of only these
   * documents); nothing after it may be, because the engine and the screen would
   * then disagree about where every later document sits.
   */
  const saveBoundaryChange = async (
    item: BoundaryQueueItem & { kind: 'split' | 'join' },
    working: ProcessResult,
    change: BoundaryChange,
    structuralToken: symbol,
  ): Promise<'done' | 'retry'> => {
    const desktop = window.capturePointDesktop;
    if (!desktop || structuralMutationRef.current?.token !== structuralToken) return 'retry';
    const { mutation, superseded } = change;
    const structurallyChangedIds = new Set([
      ...change.affectedDocumentIds,
      ...mutation.removedDocumentIds,
      ...mutation.documents.map((document) => document.id),
    ]);
    extendStructuralMutation(structuralToken, structurallyChangedIds);
    const pageUndosBeforeBoundary = pageUndoStack.current;
    const retainedPageUndos = pageUndosBeforeBoundary.filter(
      (entry) => !structurallyChangedIds.has(entry.documentId),
    );
    if (retainedPageUndos.length !== pageUndosBeforeBoundary.length) {
      pageUndoStack.current = retainedPageUndos;
      setPageUndoCount(retainedPageUndos.length);
    }
    const undoEntry: BoundaryUndoEntry = {
      mutation: {
        position: mutation.position,
        removedDocumentIds: mutation.documents.map((document) => document.id),
        documents: working.documents.slice(
          mutation.position,
          mutation.position + mutation.removedDocumentIds.length,
        ),
      },
      pageNumber: item.pageNumber,
      changeId: item.id,
    };
    boundaryUndoStack.current.push(undoEntry);
    setBoundaryUndoCount(boundaryUndoStack.current.length);
    const liveBeforePaint = batchRef.current ?? working;
    const optimistic = spliceLiveBoundaryDocuments(liveBeforePaint, mutation, false);
    batchRef.current = optimistic;
    setBatch(optimistic);
    if (mounted.current) onRestored(optimistic);
    ++boundaryGeneration.current;
    // Follow the change only when the operator is still on the document they
    // changed and it is saved straight away; anyone who has moved on, or who
    // has been working while it waited its turn, stays exactly where they are.
    const viewed = liveBeforePaint.documents[selectedDocumentRef.current];
    const landed =
      viewed &&
      item.viewedDocumentId === viewed.id &&
      mutation.removedDocumentIds.includes(viewed.id) &&
      Date.now() - item.requestedAt < BOUNDARY_FOLLOW_MS
        ? locateBatchPage(optimistic.documents, change.landOn)
        : null;
    if (landed) {
      selectedDocumentRef.current = landed.documentIndex;
      setSelectedDocument(landed.documentIndex);
      setSelectedPage(landed.pageIndex);
      if (change.newDocumentId) {
        setTypePromptDocumentId(change.newDocumentId);
        onNotice(`New document started at page ${change.landOn} · choose its type`);
      }
    } else followSelectedPage(liveBeforePaint, optimistic);
    // Not a toast and not a validation finding. A toast expires in under three
    // seconds and the recheck that follows a merge recomputes findings, so either
    // one would take the news away again on its own. This stays on the merged
    // document until the operator dismisses it.
    const supersededShown: {
      documentId: string;
      fieldId?: string;
      fieldName: string;
      kept: string;
      discarded: string;
    }[] = superseded.map((entry) => ({
      documentId: entry.documentId,
      fieldId: entry.kept.fieldId ?? entry.discarded.fieldId ?? undefined,
      fieldName: entry.fieldName,
      kept: entry.kept.value,
      discarded: entry.discarded.value,
    }));
    if (supersededShown.length > 0) {
      setSupersededByMerge(supersededShown);
      onNotice(
        supersededShown.length === 1
          ? `${supersededShown[0].fieldName} kept “${supersededShown[0].kept}” · “${supersededShown[0].discarded}” was replaced`
          : `${counted(supersededShown.length, 'corrected value')} from the pages merged in were replaced`,
      );
    }
    let mutationCommitted = false;
    try {
      const mutationResult = await desktop.saveBoundaryMutation({
        jobId: job.id,
        batchId: optimistic.batchId,
        position: mutation.position,
        removedDocumentIds: [...mutation.removedDocumentIds],
        documents: [...mutation.documents],
        expectedDocuments: change.expected,
        correction: boundaryCorrection(working, change),
      });
      // This is the durable commit watermark. No failure after this point may
      // paint the pre-mutation batch or remove its undo receipt: the engine and
      // renderer would otherwise disagree about where every later position is.
      mutationCommitted = true;
      committedBoundaryChanges.current.add(item.id);
      reviewTally.boundary(job.id, item.kind, structurallyChangedIds);
      // A join's line, unless a Delete made since owns the status bar (and Ctrl+Z): the newest change's notice shows.
      // Shown, it ends a Delete's notice made before it (that delete's Restore stays in Deleted documents).
      if (item.kind === 'join' && !lastChange.undoable((change) => change.kind === 'delete')) {
        setJoinNews({
          batchId: optimistic.batchId,
          page: item.pageNumber,
          added: mutationResult.lookalikesAdded ?? 0,
        });
        if (workspaceNoticeRef.current.action?.telemetry === DELETE_UNDO_TELEMETRY)
          workspaceNoticeRef.current.dismiss();
      }
      void readQuestionsAgain(optimistic.batchId, mutationResult.questionsChanged);
      boundaryAttempts.current.delete(item.id);
      const liveAfterCommit = batchRef.current ?? optimistic;
      const durableMutation: BoundaryDocumentMutation = {
        position: mutation.position,
        removedDocumentIds: mutation.documents.map((document) => document.id),
        documents: mutationResult.documents,
      };
      const saved = spliceLiveBoundaryDocuments(liveAfterCommit, durableMutation);
      batchRef.current = saved;
      setBatch(saved);
      if (mounted.current) onRestored(saved);
      // A correction the merge could not keep is still a change to a value an
      // operator made, so it goes into the same ledger every other value change
      // goes into. Its own kind, though: the engine projects
      // field-value-correction and field-value-verified into teaching examples,
      // and the value that lost a merge is the last thing that should be taught.
      if (superseded.length > 0) {
        const survivor = saved.documents.find(
          (document) => document.id === superseded[0].documentId,
        );
        if (survivor) {
          try {
            for (const entry of superseded)
              await desktop.saveDocumentCorrection({
                jobId: job.id,
                batchId: saved.batchId,
                document: survivor,
                correction: supersededCorrection(survivor.id, entry, mutation.removedDocumentIds),
              });
          } catch (reason) {
            setError(
              messageAfter(
                'The merge replaced a value you had corrected, and that document correction could not be saved',
                reason,
              ),
              'boundary-save-failed',
            );
          }
        }
      }
      // The documents either side are read again once the cuts in front of the
      // recheck are saved (boundaryQueue keeps it last).
      for (const documentId of change.affectedDocumentIds)
        pendingBoundaryRecheck.current.set(documentId, change.recheckDocumentTypes ?? null);
      lastBoundaryCommitAt.current = Date.now();
      boundaryQueue.requestRecheck();
      return 'done';
    } catch (reason) {
      if (mutationCommitted) {
        setError(
          messageAfter('The boundary was saved, but its follow-up could not finish', reason),
          'boundary-save-failed',
        );
        onNotice('Boundary saved · follow-up needs review');
        return 'done';
      }
      boundaryUndoStack.current = boundaryUndoStack.current.filter((entry) => entry !== undoEntry);
      setBoundaryUndoCount(boundaryUndoStack.current.length);
      pageUndoStack.current = pageUndosBeforeBoundary;
      setPageUndoCount(pageUndosBeforeBoundary.length);
      const live = batchRef.current ?? optimistic;
      let rolledBack = live;
      try {
        rolledBack = spliceLiveBoundaryDocuments(live, invertBoundaryMutation(working, mutation));
      } catch {
        // If optimistic painting itself never completed, the live batch still
        // has the original identities. Keep it rather than replacing unrelated
        // work with the click-time snapshot.
        rolledBack = live;
      }
      batchRef.current = rolledBack;
      setBatch(rolledBack);
      if (mounted.current) onRestored(rolledBack);
      followSelectedPage(live, rolledBack);
      if (change.newDocumentId)
        setTypePromptDocumentId((current) => (current === change.newDocumentId ? null : current));
      if (supersededShown.length > 0)
        setSupersededByMerge((current) =>
          current.filter((entry) => !supersededShown.includes(entry)),
        );
      const attempts = (boundaryAttempts.current.get(item.id) ?? 0) + 1;
      boundaryAttempts.current.set(item.id, attempts);
      // Built from documents that changed after they were loaded: nothing was
      // written. Build it again from the newer documents.
      if (
        reason instanceof Error &&
        reason.message.includes(BOUNDARY_REVISION_CHANGED) &&
        attempts < MAX_BOUNDARY_ATTEMPTS
      )
        return 'retry';
      boundaryAttempts.current.delete(item.id);
      setError(boundaryFailureMessage(item.kind, reason), 'boundary-save-failed');
      return 'done';
    }
  };
  const runBoundaryChange = async (
    item: BoundaryQueueItem & { kind: 'split' | 'join' },
  ): Promise<'done' | 'retry'> => {
    const live = batchRef.current;
    if (!window.capturePointDesktop || !live) return 'done';
    if (live.batchId.startsWith('live:')) {
      setError(
        boundaryFailureMessage(
          item.kind,
          new Error('The pages being added have not been saved as a batch yet.'),
        ),
        'boundary-save-failed',
      );
      return 'done';
    }
    const resolved = resolveBoundaryRequest(live.documents, item);
    // Already true (the page starts a document, or no longer does): nothing to do.
    if (resolved.kind === 'none') {
      if (resolved.reason === 'missing')
        onNotice(
          'That page is no longer in this batch, so its boundary was left as it is',
          'boundary-save-failed',
        );
      return 'done';
    }
    const typing = boundaryTypingSettled(
      resolved.documentIds,
      new Set(resolved.kind === 'join' ? [resolved.documentIds[1]] : []),
    );
    if (typing === 'wait') return 'retry';
    if (typing === 'kept') {
      setError(
        "That document has typing that isn't saved yet, so the documents weren't joined. Your typing is kept: save or discard it, then join them again.",
        'boundary-refused',
      );
      return 'done';
    }
    const structuralToken = claimStructuralMutation(
      resolved.documentIds,
      item.kind === 'split' ? 'splitting a document' : 'joining documents',
      true,
    );
    if (!structuralToken) return 'retry';
    try {
      const working = await loadForBoundaryChange(resolved.documentIds);
      const again = working ? resolveBoundaryRequest(working.documents, item) : null;
      if (again?.kind === 'none') return 'done';
      if (!working || !again || again.documentIds.join('|') !== resolved.documentIds.join('|')) {
        const attempts = (boundaryAttempts.current.get(item.id) ?? 0) + 1;
        boundaryAttempts.current.set(item.id, attempts);
        if (attempts < MAX_BOUNDARY_ATTEMPTS) return 'retry';
        boundaryAttempts.current.delete(item.id);
        setError(
          boundaryFailureMessage(
            item.kind,
            new Error('The complete documents could not be loaded.'),
          ),
          'boundary-save-failed',
        );
        return 'done';
      }
      const change =
        again.kind === 'split'
          ? buildSplit(working, again.documentIndex, again.pageIndex)
          : buildMerge(working, again.documentIndex);
      if (typeof change === 'string') {
        setError(change, 'boundary-save-failed');
        return 'done';
      }
      return await saveBoundaryChange(item, working, change, structuralToken);
    } finally {
      releaseStructuralMutation(structuralToken);
    }
  };
  /** Put back the boundary change queued as `item.changeId`, once it has been saved. */
  const runBoundaryUndo = async (
    item: BoundaryQueueItem & { kind: 'undo' },
  ): Promise<'done' | 'retry'> => {
    const desktop = window.capturePointDesktop;
    const entry = boundaryUndoStack.current.find(
      (candidate) => candidate.changeId === item.changeId,
    );
    // The change never landed (its failure was already said), or a later edit to
    // its documents made its old documents untrue (said now).
    const cannotUndo = () => {
      if (committedBoundaryChanges.current.has(item.changeId))
        onNotice(
          'Those documents changed after the boundary change, so it can no longer be undone',
          'boundary-undo-failed',
        );
      return 'done' as const;
    };
    if (!entry) return cannotUndo();
    const live = batchRef.current;
    if (!desktop || !live) return 'done';
    const replacedIds = entry.mutation.removedDocumentIds;
    const position = live.documents.findIndex((document) => document.id === replacedIds[0]);
    if (
      position < 0 ||
      replacedIds.some((id, offset) => live.documents[position + offset]?.id !== id)
    ) {
      boundaryUndoStack.current = boundaryUndoStack.current.filter(
        (candidate) => candidate !== entry,
      );
      setBoundaryUndoCount(boundaryUndoStack.current.length);
      return cannotUndo();
    }
    const restoredIds = new Set(entry.mutation.documents.map((document) => document.id));
    const typing = boundaryTypingSettled(
      replacedIds,
      new Set(replacedIds.filter((id) => !restoredIds.has(id))),
    );
    if (typing === 'wait') return 'retry';
    if (typing === 'kept') {
      setError(
        "That document has typing that isn't saved yet, so the boundary change wasn't undone. Your typing is kept: save or discard it, then undo again.",
        'boundary-refused',
      );
      return 'done';
    }
    const structuralToken = claimStructuralMutation(
      [...replacedIds, ...restoredIds],
      'undoing a boundary change',
      true,
    );
    if (!structuralToken) return 'retry';
    try {
      // Fresh copies of the documents being replaced: the engine checks they are
      // still the stored ones, so an undo can never write over newer work.
      const working = await loadForBoundaryChange(replacedIds);
      const current = batchRef.current;
      const at = current?.documents.findIndex((document) => document.id === replacedIds[0]) ?? -1;
      if (
        !working ||
        !current ||
        at < 0 ||
        replacedIds.some((id, offset) => current.documents[at + offset]?.id !== id)
      ) {
        setError(
          boundaryFailureMessage('undo', new Error('The complete documents could not be loaded.')),
          'boundary-undo-failed',
        );
        return 'done';
      }
      const expected = replacedIds.map((id) =>
        working.documents.find((document) => document.id === id)!,
      );
      const restore: BoundaryDocumentMutation = {
        position: at,
        removedDocumentIds: [...replacedIds],
        documents: [...entry.mutation.documents],
      };
      const restored = spliceLiveBoundaryDocuments(current, restore, false);
      const redo = invertBoundaryMutation(current, restore);
      boundaryUndoStack.current = boundaryUndoStack.current.filter(
        (candidate) => candidate !== entry,
      );
      setBoundaryUndoCount(boundaryUndoStack.current.length);
      setTypePromptDocumentId(null);
      // Undoing the merge puts the replaced correction back, so the notice about it
      // has to go with it; a notice about another merge stays.
      setSupersededByMerge((shown) =>
        shown.filter((notice) => !replacedIds.includes(notice.documentId)),
      );
      batchRef.current = restored;
      setBatch(restored);
      if (mounted.current) onRestored(restored);
      followSelectedPage(current, restored);
      ++boundaryGeneration.current;
      let committed = false;
      try {
        const mutationResult = await desktop.saveBoundaryMutation({
          jobId: job.id,
          batchId: current.batchId,
          position: at,
          removedDocumentIds: [...replacedIds],
          documents: [...entry.mutation.documents],
          expectedDocuments: expected,
          correction: {
            kind: CORRECTION.boundaryUndo,
            targetId: current.batchId,
            beforeJson: JSON.stringify({
              documents: current.documents.map((document) => ({
                id: document.id,
                pages: document.pages.map((page) => page.number),
              })),
            }),
            afterJson: JSON.stringify({
              documents: restored.documents.map((document) => ({
                id: document.id,
                pages: document.pages.map((page) => page.number),
              })),
            }),
          },
        });
        committed = true;
        boundaryAttempts.current.delete(item.id);
        setJoinNews(null);
        void readQuestionsAgain(current.batchId, mutationResult.questionsChanged);
        // Undo puts the documents back as they were; a re-read queued for the
        // change being undone must not read them again.
        for (const documentId of [...replacedIds, ...restoredIds])
          pendingBoundaryRecheck.current.delete(documentId);
        const liveAfterCommit = batchRef.current ?? restored;
        const accepted = spliceLiveBoundaryDocuments(liveAfterCommit, {
          position: at,
          removedDocumentIds: entry.mutation.documents.map((document) => document.id),
          documents: mutationResult.documents,
        });
        batchRef.current = accepted;
        setBatch(accepted);
        if (mounted.current) onRestored(accepted);
        return 'done';
      } catch (reason) {
        if (committed) {
          setError(
            messageAfter(
              'The boundary was undone, but the screen could not finish refreshing',
              reason,
            ),
            'boundary-undo-failed',
          );
          return 'done';
        }
        boundaryUndoStack.current.push(entry);
        setBoundaryUndoCount(boundaryUndoStack.current.length);
        const liveAfterFailure = batchRef.current ?? restored;
        let rolledForward = liveAfterFailure;
        try {
          rolledForward = spliceLiveBoundaryDocuments(liveAfterFailure, redo);
        } catch {
          rolledForward = liveAfterFailure;
        }
        batchRef.current = rolledForward;
        setBatch(rolledForward);
        if (mounted.current) onRestored(rolledForward);
        followSelectedPage(liveAfterFailure, rolledForward);
        const attempts = (boundaryAttempts.current.get(item.id) ?? 0) + 1;
        boundaryAttempts.current.set(item.id, attempts);
        if (
          reason instanceof Error &&
          reason.message.includes(BOUNDARY_REVISION_CHANGED) &&
          attempts < MAX_BOUNDARY_ATTEMPTS
        )
          return 'retry';
        boundaryAttempts.current.delete(item.id);
        setError(boundaryFailureMessage('undo', reason), 'boundary-undo-failed');
        return 'done';
      }
    } finally {
      releaseStructuralMutation(structuralToken);
    }
  };
  /**
   * Read again the documents either side of the boundaries just saved.
   *
   * The reclassifier writes the durable batch as well as returning a new
   * projection, so it owns the documents it reads until it settles: releasing
   * them early let a later undo or field save commit, after which the old
   * reclassifier could write its pre-edit grouping back into the store.
   * Unaffected documents remain editable because the owner is identity scoped.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const runBoundaryRecheck = async (): Promise<'done' | 'retry'> => {
    const desktop = window.capturePointDesktop;
    const live = batchRef.current;
    // A closed workspace leaves the documents in review rather than re-reading
    // them into a picture of the batch nobody is looking at.
    if (!desktop || !live || live.batchId.startsWith('live:') || !mounted.current) {
      pendingBoundaryRecheck.current.clear();
      return 'done';
    }
    const present = new Set(live.documents.map((document) => document.id));
    const groups = new Map<
      string,
      { documentIds: string[]; documentTypes: ReturnType<typeof schemaRequest> }
    >();
    for (const [documentId, documentTypes] of pendingBoundaryRecheck.current) {
      const types = documentTypes ?? schemaRequest(job);
      if (!present.has(documentId) || types.length === 0) continue;
      const key = documentTypes ? JSON.stringify(types.map((type) => type.id ?? type.name)) : 'job';
      const group = groups.get(key) ?? { documentIds: [], documentTypes: types };
      group.documentIds.push(documentId);
      groups.set(key, group);
    }
    const affectedDocumentIds = [...groups.values()].flatMap((group) => group.documentIds);
    if (affectedDocumentIds.length === 0) {
      pendingBoundaryRecheck.current.clear();
      return 'done';
    }
    // Its own documents only: while the model reads them, splits, joins and saves on the rest of the batch go on
    // (boundaryRereadingIds). It still starts only when no structural change is being saved.
    if (
      !structuralMutationFree(affectedDocumentIds, true) ||
      affectedDocumentIds.some(documentWriteInFlight)
    )
      return 'retry';
    for (const documentId of affectedDocumentIds) boundaryRereadingIds.current.add(documentId);
    signalSaveLaneChange();
    pendingBoundaryRecheck.current.clear();
    const generation = boundaryGeneration.current;
    boundaryRefreshesInFlight.current += 1;
    setBoundaryRefreshing(true);
    try {
      for (const group of groups.values()) {
        const saved = batchRef.current ?? live;
        const refreshed = await desktop.reclassifyBoundaryDocuments({
          jobId: job.id,
          batchId: saved.batchId,
          documentIds: group.documentIds,
          documentTypes: group.documentTypes,
        });
        // Another batch opened meanwhile: nothing here is on screen any more.
        if (batchRef.current && batchRef.current.batchId !== saved.batchId) return 'done';
        // Only documents untouched since this refresh was sent may take its
        // projection. Work on another document is allowed while indexing runs;
        // replacing the whole returned batch used to erase that newer work. A
        // split or join elsewhere in the batch meanwhile is such work, so it does
        // not discard this result: the engine has saved it, and dropping it here
        // left the screen showing these documents as they were before the read.
        const affected = new Set(group.documentIds);
        const sentById = new Map(saved.documents.map((document) => [document.id, document]));
        const refreshedById = new Map(
          refreshed.documents.map((document) => [document.id, document]),
        );
        const current = batchRef.current ?? saved;
        const replacementDocuments: DocumentResult[] = [];
        const mergedDocuments = current.documents.map((document) => {
          const replacement =
            affected.has(document.id) && document === sentById.get(document.id)
              ? refreshedById.get(document.id)
              : undefined;
          if (replacement) replacementDocuments.push(replacement);
          return replacement ?? document;
        });
        // Reclassification changes only the named documents. Preserve the
        // live batch envelope (especially paged projection bookkeeping and a
        // warning dismissed while inference ran) rather than spreading the
        // engine's full, unprojected response over it.
        const merged = reconcileBoundaryProjection(
          { ...current, documents: mergedDocuments },
          [],
          replacementDocuments,
        );
        batchRef.current = merged;
        setBatch(merged);
        if (mounted.current) onRestored(merged);
      }
    } catch (reason) {
      if (boundaryGeneration.current === generation) {
        const detail = messageFor(reason, '');
        setError(
          `The new boundary is saved, but the documents beside it couldn't be read again.${detail ? `${TECHNICAL_DETAILS_MARKER}${detail}` : ''}`,
          'boundary-save-failed',
        );
      }
    } finally {
      boundaryRefreshesInFlight.current = Math.max(0, boundaryRefreshesInFlight.current - 1);
      setBoundaryRefreshing(false);
      for (const documentId of affectedDocumentIds) boundaryRereadingIds.current.delete(documentId);
      signalSaveLaneChange();
    }
    return 'done';
  };
  boundaryRunRef.current = (item) =>
    item.kind === 'split' || item.kind === 'join'
      ? runBoundaryChange(item)
      : item.kind === 'undo'
        ? runBoundaryUndo(item)
        : runBoundaryRecheck();
  /** A split or join the person just asked for is their most recent change (a repeated request is not new). */
  const noteBoundaryChange = (item: BoundaryQueueItem | null) => {
    if (item) lastChange.note({ kind: 'boundary', changeId: item.id });
  };
  /** A new document starts at this page. Shown and saved in turn; never refused for being early. */
  const splitDocumentAt = (documentIndex: number, pageIndex: number) => {
    const page = batch?.documents[documentIndex]?.pages[pageIndex];
    if (!page || pageIndex <= 0) return;
    noteBoundaryChange(
      boundaryQueue.enqueue(
        { kind: 'split', pageNumber: page.number },
        (batchRef.current ?? batch)?.documents[selectedDocumentRef.current]?.id ?? null,
      ),
    );
  };
  /**
   * A file kept whole that looks like several documents (ADR-0091). Split: the existing split at each
   * likely start, queued in turn, so each is a confirmed start. Keep as one: the existing boundary
   * confirmation of its pages, so each page inside is a confirmed continuation. Both are the truth the
   * engine's separation learner reads.
   */
  const splitAtSuggestedStarts = (documentIndex: number) => {
    const target = batch?.documents[documentIndex];
    const suggestion = target ? sourceSplitSuggestion(target) : null;
    if (!target || !suggestion) return;
    for (const pageNumber of suggestion.startPages) {
      const pageIndex = target.pages.findIndex((page) => page.number === pageNumber);
      if (pageIndex > 0) splitDocumentAt(documentIndex, pageIndex);
    }
  };
  const keepSuggestedFileWhole = async (documentIndex: number) => {
    const current = batchRef.current ?? batch;
    const target = current?.documents[documentIndex];
    if (!current || !target) return;
    try {
      const saved = await window.capturePointDesktop!.saveDocumentCorrection({
        jobId: job.id,
        batchId: current.batchId,
        ...keepFileWhole(target),
      });
      if (!mounted.current || batchRef.current?.batchId !== current.batchId) return;
      const live = batchRef.current ?? current;
      const next = {
        ...live,
        documents: live.documents.map((item) => (item.id === saved.id ? saved : item)),
      };
      batchRef.current = next;
      setBatch(next);
      onRestored(next);
    } catch (reason) {
      onNotice(
        reason instanceof Error
          ? reason.message
          : 'The document could not be kept as one. Try again.',
      );
    }
  };
  /** This document joins the one before it. Shown and saved in turn; never refused for being early. */
  const mergeWithPrevious = (documentIndex: number) => {
    const page = batch?.documents[documentIndex]?.pages[0];
    if (!page || documentIndex <= 0) return null;
    const queued = boundaryQueue.enqueue(
      { kind: 'join', pageNumber: page.number },
      (batchRef.current ?? batch)?.documents[selectedDocumentRef.current]?.id ?? null,
    );
    noteBoundaryChange(queued);
    return queued;
  };
  boundaryShortcutRef.current = () => {
    if (selectedPage > 0) splitDocumentAt(selectedDocument, selectedPage);
    // The page before is always above page 1, so the pair is in view.
    else if (selectedDocument > 0) mergeWithPrevious(selectedDocument);
  };
  /** A join's questions asked or released on documents outside the change: their review fields read again. */
  const readQuestionsAgain = async (batchId: string, ids: readonly string[] | null | undefined) => {
    const api = window.capturePointDesktop;
    if (!api || !ids?.length) return;
    const fresh = (
      await Promise.all(
        ids.map((documentId) =>
          api.getBatchDocument({ jobId: job.id, batchId, documentId }).catch(() => null),
        ),
      )
    ).filter((item): item is DocumentResult => item !== null);
    const live = batchRef.current;
    if (!mounted.current || !live || live.batchId !== batchId || fresh.length === 0) return;
    const byId = new Map(fresh.map((item) => [item.id, item]));
    const next = {
      ...live,
      documents: live.documents.map((item) => {
        const read = byId.get(item.id);
        return read
          ? {
              ...item,
              needsReview: read.needsReview,
              needsTeaching: read.needsTeaching,
              waitReasons: read.waitReasons,
              startQuestionedPage: read.startQuestionedPage ?? null,
              startQuestionedBy: read.startQuestionedBy ?? null,
            }
          : item;
      }),
    };
    batchRef.current = next;
    setBatch(next);
    onRestored(next);
  };
  /** Undo from the join's own line: the newest boundary change, which is that join. */
  const undoJoinNews = () => {
    setJoinNews(null);
    undoBoundaryCorrection();
  };
  /**
   * Undo the newest boundary change. One not saved yet is simply taken back;
   * one being saved, or saved already, is put back in its turn.
   */
  const undoBoundaryCorrection = () => {
    if (boundaryQueue.cancelNewestWaiting()) {
      lastChange.clear();
      return;
    }
    const target = boundaryUndoTarget();
    if (!target) return;
    boundaryQueue.enqueue(
      { kind: 'undo', pageNumber: target.pageNumber, changeId: target.changeId },
      (batchRef.current ?? batch)?.documents[selectedDocumentRef.current]?.id ?? null,
    );
    lastChange.clear();
  };
  /** The split or join Undo would put back now: the newest one not already being put back. */
  const boundaryUndoTarget = () => {
    const queued = boundaryQueue.getSnapshot();
    const targeted = new Set(
      queued.flatMap((item) => (item.kind === 'undo' ? [item.changeId] : [])),
    );
    const candidates = [
      ...[...queued]
        .reverse()
        .flatMap((item) =>
          item.kind === 'split' || item.kind === 'join'
            ? [{ changeId: item.id, pageNumber: item.pageNumber }]
            : [],
        ),
      ...[...boundaryUndoStack.current]
        .reverse()
        .map((entry) => ({ changeId: entry.changeId, pageNumber: entry.pageNumber })),
    ];
    return candidates.find((candidate) => !targeted.has(candidate.changeId)) ?? null;
  };
  /** Whether an Undo has anything to put back: a saved change, or one still on its way. */
  const boundaryUndoAvailable =
    boundaryUndoCount > 0 ||
    boundaryQueueItems.some((item) => item.kind === 'split' || item.kind === 'join');
  /**
   * The operator names the kind the list is missing. "This is a purchase
   * order" is the most valuable answer a human can give a young job, and the
   * dropdown used to refuse it — every choice was wrong and the only escape
   * was Job setup. The type is created empty-handed (fields grow later) and
   * the document is confirmed as it in the same gesture, so the answer seeds
   * examples and layouts immediately.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const addTypeAndAssign = async (rawName: string) => {
    const name = rawName.trim();
    if (!name || !batch) return;
    // Job setup persistence can outlive navigation. Keep the document which
    // asked for the new type, rather than assigning it to whatever happens to
    // be selected when that await returns.
    const requestedDocumentId = batch.documents[selectedDocument]?.id;
    if (!requestedDocumentId) return;
    const existing = job.documentTypes.find(
      (item) => item.name.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0,
    );
    if (existing) return setDocumentType(existing.id, false, job, requestedDocumentId);
    const now = new Date().toISOString();
    const addition: DocumentTypeDefinition = {
      id: crypto.randomUUID(),
      name,
      semanticId: null,
      description: 'Added by an operator during review',
      status: 'active',
      schemaVersion: job.schemaVersion,
      position: job.documentTypes.length,
      fields: [],
    };
    let saved: Job;
    try {
      saved = migrateJob(
        await onCommitJob({
          ...job,
          documentTypes: [...job.documentTypes, addition],
          updatedAt: now,
        }),
      );
    } catch (reason) {
      if (!mounted.current) return;
      setError(
        reason instanceof Error
          ? `${name} was not added: ${reason.message}`
          : `${name} was not added`,
        'type-change-failed',
      );
      // The name goes back in its box while the person is still on that document, to try again.
      if (batchRef.current?.documents[selectedDocumentRef.current]?.id === requestedDocumentId)
        setAddingTypeName((current) => current ?? rawName);
      return;
    }
    onJobChange(saved);
    const created = saved.documentTypes.find(
      (item) => item.name.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0,
    );
    if (created) await setDocumentType(created.id, false, saved, requestedDocumentId);
  };
  /**
   * Other waiting documents learn from a confirmed type at once: the engine re-checks their types
   * against the confirmed examples, and the cleared news says how many no longer wait. Runs after
   * a type change and after a whole-document confirmation that confirmed the type (it ran after
   * Confirm type, which the single confirm folded into the document's).
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  async function recheckConfirmedTypes(batchId: string, withinJob: Job, targetId: string) {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    const saved = batchRef.current;
    if (!mounted.current || !saved || saved.batchId !== batchId) return;
    let learnedCount = 0;
    try {
      const projected = await desktop.applyConfirmedTypeExamples({
        jobId: job.id,
        batchId,
        documentTypes: schemaRequest(withinJob),
        ...(batchRef.current?.workspaceProjection === 'paged'
          ? { loadedDocumentIds: [...loadedDocumentIdsRef.current] }
          : {}),
      });
      if (!mounted.current || batchRef.current?.batchId !== batchId) return;
      const sourceById = new Map(saved.documents.map((item) => [item.id, item.documentTypeSource]));
      learnedCount = projected.documents.filter(
        (item) =>
          item.documentTypeSource === TYPE_FROM_CONFIRMED_EXAMPLES &&
          sourceById.get(item.id) !== TYPE_FROM_CONFIRMED_EXAMPLES,
      ).length;
      /* The number the operator actually feels: OTHER documents that no longer
         need them. Counted as the red-to-green flips this pass produced, named by
         kind, and said as the cleared news under the documents list's counts. */
      const wasRed = new Set(
        saved.documents.filter((item) => item.needsReview).map((item) => item.id),
      );
      const settledNow = projected.documents.filter(
        (item) => !item.needsReview && wasRed.has(item.id) && item.id !== targetId,
      );
      if (settledNow.length > 0) {
        noteCleared(settledNow.map((item) => clearedDocument(item.id, item)));
      } else if (learnedCount > 0) {
        setLearningReceipt(
          `Applied to ${counted(learnedCount, 'waiting document')} · they still carry other questions`,
        );
      }
      /* Land the pass as a merge, not a replacement. The operator is allowed
         to keep reviewing while the recheck runs — so any document they
         touched after the snapshot went out (its object identity differs
         from what we sent) keeps their version, and the pass's answer is
         taken only for the documents they left alone. Their newer work can
         never be clobbered by an older pass landing late. */
      const sentById = new Map(saved.documents.map((item) => [item.id, item]));
      const projectedById = new Map(projected.documents.map((item) => [item.id, item]));
      const live = batchRef.current ?? saved;
      let merged: ProcessResult = {
        ...live,
        documents: live.documents.map((item) => {
          const projectedDocument = projectedById.get(item.id);
          if (!projectedDocument) return item;
          // A pending edit or confirmation owns its current UI revision.
          // Never merge an older learning response over those values.
          return item === sentById.get(item.id) ? projectedDocument : item;
        }),
      };
      if (live.workspaceProjection === 'paged') {
        merged = {
          ...merged,
          workspaceProjection: 'paged',
          loadedDocumentIds: [...loadedDocumentIdsRef.current],
          documents: merged.documents.map((item) =>
            loadedDocumentIdsRef.current.has(item.id) ? item : toProjectionSkeleton(item),
          ),
        };
        projectionSkeletons.current = new Map(
          merged.documents.map((item) => [item.id, toProjectionSkeleton(item)]),
        );
      }
      clearBoundaryUndoHistory();
      batchRef.current = merged;
      setBatch(merged);
      onRestored(merged);
    } catch (reason) {
      const message = messageFor(reason, '');
      if (
        message.includes(
          'The job, batch, or its corrections changed while reviewed pages were being proved. Try again.',
        ) ||
        message.includes(
          'The batch changed before its reviewed state could be preserved. Try again.',
        )
      ) {
        // A saved correction superseded this snapshot. Prefer the newer queued
        // confirmation; otherwise retry this lesson against fresh native truth.
        await new Promise((resolve) => setTimeout(resolve, 250));
        if (
          mounted.current &&
          batchRef.current?.batchId === saved.batchId &&
          !pendingTypeProjection.current
        )
          queueTypeProjection(() => recheckConfirmedTypes(batchId, withinJob, targetId));
        return;
      }
      setError(
        messageAfter('Correction saved, but other documents could not be rechecked', reason),
        'type-change-failed',
      );
    }
    if (learnedCount > 0) refreshLearningProgress();
  }
  const setDocumentType = async (
    typeId: string,
    verificationOnly = false,
    withinJob: Job = job,
    targetDocumentId?: string,
  ) => {
    const desktop = window.capturePointDesktop;
    if (!batch || !desktop) return;
    // A type chosen has no undo for Ctrl+Z (last-change.ts); a confirmation's own type check is part of that Confirm.
    if (!verificationOnly) lastChange.note();
    const schema = withinJob.documentTypes.find((item) => item.id === typeId);
    if (!schema) return;
    const requestedBatch = batchRef.current ?? batch;
    const requestedDocument =
      targetDocumentId != null
        ? requestedBatch.documents.find((item) => item.id === targetDocumentId)
        : requestedBatch.documents[selectedDocument];
    if (!requestedDocument) return;
    const targetId = requestedDocument.id;
    if (schemaRefreshBusyRef.current) {
      if (!verificationOnly) {
        onNotice(
          'Finish applying the current job fields before changing the document type',
          'type-change-busy',
        );
        return;
      }
      // The refresh writes a complete batch snapshot, so accepting a type
      // confirmation beside it could erase the operator's answer. Preserve the
      // click instead: bind it to this job, batch, document, and displayed type,
      // then run the existing save lane after the snapshot owner releases.
      if (queuedTypeConfirmationIdsRef.current.has(targetId)) return;
      const expectedJobId = withinJob.id;
      const expectedJobSchemaVersion = withinJob.schemaVersion;
      const expectedBatchId = requestedBatch.batchId;
      const expectedTypeName = schema.name;
      const expectedTypeSchemaVersion = schema.schemaVersion;
      const refreshAtClick = schemaRefreshCompletionRef.current;
      queuedTypeConfirmationIdsRef.current.add(targetId);
      signalSaveLaneChange();
      setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
      onNotice(`Updating the job fields, then confirming ${schema.name}`);
      const queued = typeConfirmationQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          let refreshed = await refreshAtClick;
          while (mounted.current && schemaRefreshBusyRef.current)
            refreshed = (await schemaRefreshCompletionRef.current) && refreshed;
          if (!mounted.current) return;
          if (!refreshed) {
            onNotice(
              `${expectedTypeName} was not confirmed because the job fields could not be updated`,
              'type-change-failed',
            );
            return;
          }
          const liveBatch = batchRef.current;
          const liveJob = liveJobRef.current;
          const liveDocument = liveBatch?.documents.find((item) => item.id === targetId);
          const liveSchema = liveJob.documentTypes.find((item) => item.id === typeId);
          if (
            liveJob.id !== expectedJobId ||
            liveJob.schemaVersion !== expectedJobSchemaVersion ||
            liveBatch?.batchId !== expectedBatchId ||
            !liveDocument ||
            !liveSchema ||
            liveSchema.schemaVersion !== expectedTypeSchemaVersion ||
            liveSchema.name.localeCompare(expectedTypeName, undefined, {
              sensitivity: 'accent',
            }) !== 0 ||
            liveDocument.documentTypeVerified
          )
            return;
          const stillShowsExpectedType =
            liveDocument.documentTypeId === typeId ||
            liveDocument.documentType.localeCompare(expectedTypeName, undefined, {
              sensitivity: 'accent',
            }) === 0;
          if (!stillShowsExpectedType) {
            onNotice(
              'The document type changed while its fields were updating. Review it before confirming.',
              'type-change-failed',
            );
            return;
          }
          await setDocumentType(typeId, true, liveJob, targetId);
        })
        .catch((reason: unknown) => {
          if (mounted.current)
            setError(
              messageFor(reason, 'The document type could not be confirmed.'),
              'type-change-failed',
            );
        })
        .finally(() => {
          queuedTypeConfirmationIdsRef.current.delete(targetId);
          signalSaveLaneChange();
          if (mounted.current)
            setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
        });
      typeConfirmationQueueRef.current = queued;
      return;
    }
    // Background learning owns the same native projection lane. The button
    // must queue the confirmation instead of showing an enabled no-op control.
    const typeContractAtClick = documentFieldContractToken(requestedDocument);
    for (;;) {
      while (wholeBatchMutationRef.current || typeSavingDocumentIdRef.current) {
        queuedTypeConfirmationIdsRef.current.add(targetId);
        setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
        signalSaveLaneChange();
        onNotice('Your document type change is queued · finishing the current save');
        try {
          await (wholeBatchMutationRef.current
            ? wholeBatchCompletionRef.current
            : typeSaveCompletionRef.current);
        } finally {
          queuedTypeConfirmationIdsRef.current.delete(targetId);
          signalSaveLaneChange();
          if (mounted.current)
            setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
        }
        if (!mounted.current || batchRef.current?.batchId !== requestedBatch.batchId) return;
        const latestTarget = batchRef.current.documents.find((item) => item.id === targetId);
        if (!latestTarget || documentFieldContractToken(latestTarget) !== typeContractAtClick) {
          onNotice(
            "This document's type changed while saving. Please review its current type.",
            'type-change-failed',
          );
          return;
        }
        if (verificationOnly && latestTarget.documentTypeVerified) return;
      }
      setTypePromptDocumentId(null);
      if (lineMutationAdmitted(targetId)) {
        onNotice('Saving the line-item correction, then changing the document type');
        if (!(await waitForLineMutations(targetId))) {
          onNotice(
            'The line-item correction was not saved, so the document type was not changed',
            'type-change-failed',
          );
          return;
        }
        // A structural owner that arrived meanwhile is waited for below, and a confirmation is
        // judged there too (its own type check goes ahead).
        if (schemaRefreshBusyRef.current) {
          onNotice(
            'Finish applying the current job fields before changing the document type',
            'type-change-busy',
          );
          return;
        }
      }
      // A split or join being saved, the re-read of the documents beside it, or a
      // page turn owns this document for a moment. The type chosen in the new
      // document's prompt waits for it instead of being refused, then goes on
      // the document as it is after the change.
      if (structuralMutationBlocks(targetId) && !pageEditBusyRef.current) {
        queuedTypeConfirmationIdsRef.current.add(targetId);
        setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
        signalSaveLaneChange();
        // Read once: the owner is gone by the time the wait ends.
        const pageTurnOwned = pageTurnOwnsStructure();
        onNotice(
          pageTurnOwned
            ? 'Your document type change is queued · finishing the page turn'
            : 'Your document type change is queued · finishing the boundary change',
        );
        try {
          while (mounted.current && structuralMutationBlocks(targetId)) await nextSaveLaneChange();
        } finally {
          queuedTypeConfirmationIdsRef.current.delete(targetId);
          signalSaveLaneChange();
          if (mounted.current)
            setQueuedTypeConfirmationIds(new Set(queuedTypeConfirmationIdsRef.current));
        }
        if (!mounted.current || batchRef.current?.batchId !== requestedBatch.batchId) return;
        const latestTarget = batchRef.current.documents.find((item) => item.id === targetId);
        if (!latestTarget || documentFieldContractToken(latestTarget) !== typeContractAtClick) {
          onNotice(
            pageTurnOwned
              ? 'This document changed while its page was read again. Please choose its type again.'
              : 'This document changed while its boundary was saving. Please choose its type again.',
            'type-change-failed',
          );
          return;
        }
        if (verificationOnly && latestTarget.documentTypeVerified) return;
        continue;
      }
      if (structuralMutationBlocks(targetId) || pageEditBusyRef.current) {
        onNotice(
          'Finish the current page or boundary change before changing the document type',
          'type-change-busy',
        );
        return;
      }
      const ownedByConfirmation =
        verificationOnly &&
        targetDocumentId === targetId &&
        confirmingDocumentsRef.current.has(targetId);
      if (confirmingDocumentsRef.current.has(targetId) && !ownedByConfirmation) {
        onNotice(
          'This document is being confirmed; its type is temporarily locked',
          'type-change-busy',
        );
        return;
      }
      if (wholeBatchMutationRef.current || typeSavingDocumentIdRef.current) continue;
      if (!openTypeSaveLane(targetId)) {
        onNotice(
          'Finish the current document-type change before starting another',
          'type-change-busy',
        );
        return;
      }
      break;
    }
    // A type correction re-extracts and then submits the whole document. Let an
    // already-admitted field or line mutation finish, while the synchronous type
    // lane prevents a new one from entering behind it.
    try {
      if (fieldSavingRef.current && fieldSavingDocumentIdRef.current === targetId) {
        onNotice('Saving the field, then changing the document type');
        await fieldSaveCompletionRef.current;
      }
      if (pendingLineSaves.current > 0) {
        onNotice('Saving the line-item correction, then changing the document type');
        // Only waits for the lane: the failure receipt is batch-wide, and a line save that failed
        // on another document must not refuse this one's type change.
        await waitForPendingSaveLane(
          () => pendingLineSaves.current,
          () => lineSaveChain.current,
        );
      }
    } catch (reason) {
      closeTypeSaveLane(targetId);
      setError(
        messageFor(reason, 'The document type could not wait for the current save.'),
        'type-change-failed',
      );
      return;
    }
    if (
      !(await hydrateDocumentForWholeMutation(
        targetId,
        'The complete document could not be loaded, so its type was not changed',
        'type-change-failed',
      ))
    ) {
      closeTypeSaveLane(targetId);
      return;
    }
    // The live batch, not this closure's render of it: the background confirm
    // chain calls in here after its own saves have landed, and a snapshot from
    // the click would put pre-confirm fields back on screen. Callers that need
    // a document other than the selected one name it by id.
    const previous = batchRef.current ?? batch;
    const previousDocument = previous.documents.find((item) => item.id === targetId);
    if (!previousDocument) {
      closeTypeSaveLane(targetId);
      return;
    }
    // By identity, not by name: a document filed under its type's former name is that type, so
    // choosing it confirms it. Compared by name, that "change" re-read the document and saved a
    // false correction (document-type-identity.ts).
    const isCorrection =
      filedDocumentType(previousDocument, withinJob.documentTypes, typeNameHistoryRef.current)
        ?.id !== schema.id;
    if (verificationOnly && isCorrection) {
      closeTypeSaveLane(targetId);
      return;
    }
    // The operator acted on this document, so it keeps its place in the review
    // list until they leave it rather than vanishing under their hand.
    settledByHand.current = targetId;
    setError(null);
    // The type is what the operator just said it is, so say it back at once and
    // re-read underneath. Waiting for extraction before showing the new name
    // made a correction feel like a request the product was considering — five
    // to ten seconds of spinner on the one control they had just used, in an
    // app that otherwise never stops them.
    if (isCorrection) {
      setRetypingDocumentId(targetId);
      setRetypingAs(schema.name);
      const optimistic = {
        ...previousDocument,
        documentType: schema.name,
        documentTypeId: schema.id,
        documentTypeSchemaVersion: schema.schemaVersion,
        documentTypeConfidence: 1,
        documentTypeVerified: true,
      };
      const painted = {
        ...previous,
        documents: previous.documents.map((item) => (item.id === targetId ? optimistic : item)),
      };
      batchRef.current = painted;
      setBatch(painted);
      onRestored(painted);
    }
    let persistedDocument: DocumentResult | null = null;
    const refreshOwner = Symbol(targetId);
    typeRefreshOwners.current.set(targetId, refreshOwner);
    let pendingTypeDocument: DocumentResult | null = null;
    try {
      const configuredSchema = schemaRequest(withinJob).find((item) => item.id === schema.id);
      if (!configuredSchema) throw new Error('The selected document type schema is unavailable.');
      if (isCorrection) {
        // Persist the operator's answer before borrowing the reader. Empty fields
        // are deliberately not answers: the durable not-read state protects them
        // from confirmation/export and offers a retry after an interrupted read.
        // review-edits.ts: support changes a type through the same two saves.
        pendingTypeDocument = await desktop.saveDocumentCorrection({
          jobId: job.id,
          batchId: previous.batchId,
          ...typeCorrection(previousDocument, schema),
        });
        persistedDocument = pendingTypeDocument;
        reviewTally.typeChanged(job.id, targetId);
        closeTypeSaveLane(targetId);
        if (!mounted.current || batchRef.current?.batchId !== previous.batchId)
          return persistedDocument;
        cacheAuthoritativeProjectionSkeleton(pendingTypeDocument);
        const current = batchRef.current ?? previous;
        const accepted = {
          ...current,
          documents: current.documents.map((item) =>
            item.id === targetId ? pendingTypeDocument! : item,
          ),
        };
        batchRef.current = accepted;
        setBatch(accepted);
        onRestored(accepted);
        refreshLearningProgress();
      }
      const nextDocument = isCorrection
        ? await desktop.reextractPreparedDocument({
            jobId: job.id,
            document: previousDocument,
            documentType: configuredSchema,
          })
        : {
            ...previousDocument,
            documentType: schema.name,
            documentTypeId: schema.id,
            documentTypeSchemaVersion: schema.schemaVersion,
            documentTypeConfidence: 1,
            documentTypeVerified: true,
            needsReview: documentNeedsReview({
              documentTypeVerified: true,
              fields: previousDocument.fields,
              validationFindings: previousDocument.validationFindings,
            }),
            fields: previousDocument.fields.map((field) =>
              field.name === 'Document type'
                ? {
                    ...field,
                    value: schema.name,
                    confidence: 1,
                    source: 'operator selected type',
                    needsReview: false,
                    verified: true,
                  }
                : field,
            ),
          };
      if (typeRefreshOwners.current.get(targetId) !== refreshOwner) return persistedDocument;
      const liveBeforeSave = batchRef.current ?? previous;
      const next = {
        ...liveBeforeSave,
        // By identity, not by position. The operator can navigate while this is
        // in flight, and selectedDocument is wherever they have got to by the
        // time it lands — which is how a slow retype came to write its result
        // onto whichever document they had moved on to.
        documents: liveBeforeSave.documents.map((item) =>
          item.id === targetId ? nextDocument : item,
        ),
      };
      if (!isCorrection) {
        batchRef.current = next;
        setBatch(next);
        onRestored(next);
      }
      let savedDocument = await desktop.saveDocumentCorrection({
        jobId: job.id,
        // The reader may finish after navigation to a different batch. Native
        // ownership stays with the document whose pending type was saved.
        batchId: previous.batchId,
        document: nextDocument,
        ...(pendingTypeDocument
          ? {
              expectedDocument: pendingTypeDocument,
              expectedJobSchemaVersion: withinJob.schemaVersion,
            }
          : {}),
        correction: typeFieldsCorrection(previousDocument, nextDocument, isCorrection),
      });
      if (typeRefreshOwners.current.get(targetId) !== refreshOwner) return persistedDocument;
      if (!mounted.current || batchRef.current?.batchId !== previous.batchId) return savedDocument;
      persistedDocument = savedDocument;
      cacheAuthoritativeProjectionSkeleton(savedDocument);
      persistedDocument = savedDocument;
      invalidateBoundaryUndoFor(savedDocument.id);
      const liveAfterSave = batchRef.current ?? next;
      const saved = {
        ...liveAfterSave,
        documents: liveAfterSave.documents.map((item) =>
          item.id === savedDocument.id ? savedDocument : item,
        ),
      };
      batchRef.current = saved;
      setBatch(saved);
      onRestored(saved);
      setTypeSuggestion(null);
      if (!pendingTypeDocument) closeTypeSaveLane(targetId);
      queueTypeProjection(() => recheckConfirmedTypes(saved.batchId, withinJob, targetId));
    } catch (reason) {
      if (typeRefreshOwners.current.get(targetId) !== refreshOwner) return persistedDocument;
      const live = batchRef.current ?? previous;
      const rolledBack = persistedDocument
        ? live
        : {
            ...live,
            documents: live.documents.map((item) =>
              item.id === targetId ? previousDocument : item,
            ),
          };
      batchRef.current = rolledBack;
      setBatch(rolledBack);
      onRestored(rolledBack);
      setError(messageFor(reason, 'Could not save document type'), 'type-change-failed');
    } finally {
      if (typeRefreshOwners.current.get(targetId) === refreshOwner) {
        typeRefreshOwners.current.delete(targetId);
        if (!pendingTypeDocument) closeTypeSaveLane(targetId);
        setRetypingDocumentId((current) => (current === targetId ? null : current));
        setRetypingAs(null);
      }
      // The countdown this confirmation just moved.
      //
      // "Confirm 3 more to recognise Invoice on sight" is counted from the
      // confirmed type examples, and confirming a type is what adds one — but
      // this path never re-read it, so the line said "3 more" before the
      // confirmation and "3 more" after it. Driven as an operator: five
      // documents confirmed in a row, every one of them answered with the same
      // sentence, and only a reload revealed the real figure, which by then was
      // "1 more". A learning product that cannot show its own count moving is
      // indistinguishable from one that is not learning.
      refreshLearningProgress();
    }
    return persistedDocument;
  };
  /** Where a field's value physically sits on the page, in page pixels. */
  const evidenceBounds = (
    pageIndex: number,
    lineIndexes: number[] | undefined,
    barcode?: BarcodeResult | null,
  ) => {
    const page = document.pages[pageIndex];
    if (!page) return null;
    const boxes = page.words
      .filter((word) => (lineIndexes ?? []).includes(word.lineIndex))
      .map((word) => inkBox(word));
    if (barcode)
      boxes.push({ x: barcode.x, y: barcode.y, width: barcode.width, height: barcode.height });
    if (!boxes.length) return null;
    const left = Math.min(...boxes.map((box) => box.x));
    const top = Math.min(...boxes.map((box) => box.y));
    return {
      x: left,
      y: top,
      width: Math.max(1, Math.max(...boxes.map((box) => box.x + box.width)) - left),
      height: Math.max(1, Math.max(...boxes.map((box) => box.y + box.height)) - top),
    };
  };
  /**
   * Put a value in front of the operator. The zoom comes from how big the
   * evidence actually is — a line of body text lands closer than a barcode band
   * spanning the sheet — and the pan is clamped to the page, so a total at the
   * foot of an invoice does not centre itself against half a screen of white.
   */
  const focusEvidence = (
    bounds: { x: number; y: number; width: number; height: number },
    size = previewSizeRef.current,
  ) => {
    const viewer = viewerRef.current;
    const fit = fitZoomFor(size);
    if (!viewer || fit <= 0) return false;
    // How much of the viewer the evidence should occupy. It was padded to a
    // 2.2x width margin and an 8x height margin, which on a line-item row
    // spanning the sheet worked out to barely more than fit — 15% to 22% on a
    // measured click, a change nobody reads as "it zoomed in on the value".
    // Tighter: the evidence fills about three quarters of the width, and enough
    // of the height to be read rather than merely located.
    const wide = Math.min(
      viewer.clientWidth / (bounds.width * 1.3),
      viewer.clientHeight / (bounds.height * 3.5),
    );
    // Never less than a clearly visible step past fit. Something small in the
    // corner of a page was landing at fit itself, so the view did not move and
    // the click looked ignored — but a run of evidence already wider than most
    // of the sheet cannot be magnified without hiding the end of it, so the
    // floor only applies while there is room to zoom into.
    const room = bounds.width < size.width * 0.72;
    const nextZoom = Math.max(room ? fit * 1.6 : fit, Math.min(6, wide));
    const offsetX = bounds.x + bounds.width / 2 - size.width / 2;
    const offsetY = bounds.y + bounds.height / 2 - size.height / 2;
    const limitX = Math.max(0, (size.width * nextZoom - viewer.clientWidth) / 2);
    const limitY = Math.max(0, (size.height * nextZoom - viewer.clientHeight) / 2);
    viewScale.current = nextZoom / fit;
    // Remember the framing, not just its result. Measured in the packaged app: the
    // travel landed correctly on translate(-205px, -1364px) and one frame later the
    // view was back at translate(0px, 0px) with the zoom intact — a refit ran and
    // put the page back to the top, and because magnification is carried in
    // viewScale the zoom survived and hid it. So a value below the fold was
    // magnified and then scrolled off screen again, which is the complaint the
    // travel was written to answer. A refit re-derives this instead of discarding
    // it now; ordinary navigation clears it, so a new document still opens at the
    // top of its first page.
    appliedEvidenceFocus.current = bounds;
    setZoom(nextZoom);
    setPan({
      x: Math.max(-limitX, Math.min(limitX, -offsetX * nextZoom)),
      y: Math.max(-limitY, Math.min(limitY, -offsetY * nextZoom)),
    });
    setEasingView(true);
    window.setTimeout(() => setEasingView(false), 260);
    return true;
  };
  /**
   * A brief highlight on whatever the operator was just sent to.
   *
   * The caret moving is not visible to somebody whose eye is still on the list
   * they clicked. Removed and re-added so a second click on the same item plays
   * again rather than doing nothing.
   */
  const showEvidence = (
    pageNumber: number | undefined,
    lineIndexes: number[] | undefined,
    barcode?: BarcodeResult | null,
    lineItemRow: number | null = null,
    retainedBounds?: { x: number; y: number; width: number; height: number },
  ) => {
    pendingReaderEvidence.current = null;
    if (
      lineItemRow != null &&
      !retainedBounds &&
      !evidenceBounds(
        document.pages.findIndex((page) => page.number === pageNumber),
        lineIndexes,
        barcode,
      )
    ) {
      // No cell position is not a request to navigate. Both docked and detached
      // tables use this path; clear stale highlights without losing the view.
      pendingEvidenceFocus.current = null;
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
      setActiveLineItemRow(lineItemRow);
      setActiveEvidenceTarget({
        documentId: document.id,
        pageNumber: document.pages[selectedPage]?.number ?? 1,
      });
      return;
    }
    appliedEvidenceFocus.current = null;
    const pageIndex = document.pages.findIndex(
      (page) => page.number === (pageNumber ?? document.pages[0]?.number),
    );
    const target = Math.max(0, pageIndex);
    const alreadyShowing = target === selectedPage && previewSizeRef.current.width > 1;
    setSelectedPage(target);
    setActiveEvidenceLines(lineIndexes ?? []);
    setActiveBarcodeEvidence(barcode ?? null);
    setActiveLineItemRow(lineItemRow);
    setActiveEvidenceTarget({
      documentId: document.id,
      pageNumber: document.pages[target]?.number ?? document.pages[0]?.number ?? 1,
    });
    const bounds = retainedBounds ?? evidenceBounds(target, lineIndexes, barcode);
    // Reading mode: the page is already in the flow — travel to it and put
    // the evidence in the middle of the view. The zoom the operator chose is
    // left alone; a reader that re-zooms on every click feels possessed.
    if (readerMode) {
      const revision = readerSelectionSync.current.revision;
      const owner = document.id;
      const position = (naturalHeight: number) => {
        if (
          revision !== readerSelectionSync.current.revision ||
          batchRef.current?.documents[selectedDocumentRef.current]?.id !== owner ||
          selectedPageIntent.current !== target
        )
          return;
        const viewer = viewerRef.current;
        const element = viewer?.querySelector<HTMLElement>(`[data-reader-index="${target}"]`);
        if (!viewer || !element) return;
        const centered =
          bounds && naturalHeight > 0
            ? ((bounds.y + bounds.height / 2) / naturalHeight) * element.clientHeight -
              viewer.clientHeight / 2
            : 0;
        const pageTop =
          element.getBoundingClientRect().top -
          viewer.getBoundingClientRect().top +
          viewer.scrollTop;
        viewer.scrollTo({ top: pageTop + Math.max(0, centered) - 14, behavior: 'smooth' });
      };
      window.requestAnimationFrame(() => {
        if (revision !== readerSelectionSync.current.revision) return;
        const viewer = viewerRef.current;
        const element = viewer?.querySelector<HTMLElement>(`[data-reader-index="${target}"]`);
        if (!viewer || !element) return;
        const naturalHeight = element.querySelector('img')?.naturalHeight ?? 0;
        if (bounds && naturalHeight === 0) {
          pendingReaderEvidence.current = {
            documentId: owner,
            pageIndex: target,
            revision,
            apply: position,
          };
          return;
        }
        position(naturalHeight);
      });
      return;
    }
    if (!bounds) return;
    // On another page the image has to load first, so hand the focus to fitView
    // rather than moving a view that is about to be replaced.
    if (alreadyShowing) focusEvidence(bounds);
    else pendingEvidenceFocus.current = bounds;
  };
  /**
   * Take the operator to the thing, on the page and in the panel, and say so.
   *
   * Naming a fault and leaving them to find it is half an instruction: "I'd
   * expect it to highlight the area on the document that it's referring to and
   * put my cursor in the value and glow it, zoom in on that part of the page."
   * Three separate moves, and all three have to happen or the click reads as
   * having done nothing:
   *
   *  - the page viewer goes to the right page and zooms to the evidence,
   *  - the panel scrolls the control into view and takes the caret,
   *  - and the control flashes, because a caret alone is invisible on a screen
   *    somebody has just looked away from.
   */
  const showRowSource = (row: LineItemRow) => {
    const source = resolveRowSource(
      document,
      row,
      reviewDocumentMutationToken(document),
      lineDraftTableIdentity(document),
    );
    if (source.kind !== 'source') return;
    const x = Math.min(...source.boxes.map((box) => box.x)),
      y = Math.min(...source.boxes.map((box) => box.y));
    const bounds = {
      x,
      y,
      width: Math.max(...source.boxes.map((box) => box.x + box.width)) - x,
      height: Math.max(...source.boxes.map((box) => box.y + box.height)) - y,
    };
    paneLayout.revealPane('page');
    showEvidence(source.page.number, source.lineIndexes, null, row.rowNumber, bounds);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const goToReviewItem = (reason: ReviewReason) => {
    if (reason.boundary) {
      paneLayout.revealPane('documents');
      // The answer is not in a field, it is two pages in the spine: the last of
      // the document before and the first of this one. Put both on screen, show
      // the opening page whole rather than magnified — the question is whether
      // this LOOKS like a new document, which a zoomed corner cannot answer —
      // and surface the control that joins them, which otherwise only appears on
      // hover and so cannot be found by somebody who has just been told to
      // decide something.
      setArrivedFieldId(null);
      setArrivedLineRow(null);
      setArrivedBoundary(selectedDocument);
      setSelectedPage(0);
      setActiveEvidenceLines([]);
      setActiveBarcodeEvidence(null);
      window.requestAnimationFrame(() =>
        window.document
          .querySelector<HTMLElement>(`[data-document-index="${selectedDocument}"]`)
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      );
      return;
    }
    setArrivedBoundary(null);
    // One item can name several lines. Tapping it again walks to the next one
    // rather than re-arriving where the operator already is, which read as the
    // control having stopped working once they had seen the first.
    const rows = reason.lineRows ?? (reason.lineRow ? [reason.lineRow] : []);
    let target = reason.lineRow;
    if (rows.length > 0) {
      const at = arrivedLineRow === null ? -1 : rows.indexOf(arrivedLineRow);
      target = rows[(at + 1) % rows.length];
    }
    // Exactly one thing is being worked on, so arriving somewhere clears the last.
    setArrivedFieldId(reason.documentType ? 'document-type' : (reason.fieldId ?? null));
    setArrivedLineRow(target ?? null);
    if (target) {
      paneLayout.revealPane('lines');
      const row = document.lineItems.find((item) => item.rowNumber === target);
      setLineItemsMode('docked');
      preserveRowEvidenceFocus.current = target;
      showEvidence(
        row?.pageNumber ?? reason.pageNumber,
        row?.evidenceLineIndexes ?? reason.evidenceLineIndexes,
        null,
        target,
      );
      window.requestAnimationFrame(() => {
        const element = window.document.querySelector<HTMLElement>(`tr[data-line-row="${target}"]`);
        element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        element?.querySelector<HTMLInputElement>('input.number-cell')?.focus();
        if (preserveRowEvidenceFocus.current === target) preserveRowEvidenceFocus.current = null;
      });
      return;
    }
    // Evidence first: the viewer takes a frame to settle, and moving it after
    // the caret lands steals focus back off the field it was just given to.
    if (reason.pageNumber || reason.evidenceLineIndexes?.length)
      showEvidence(reason.pageNumber, reason.evidenceLineIndexes, null, null);
    if (reason.fieldId) {
      setFocusFieldId(reason.fieldId);
      return;
    }
    if (isTypeReviewReason(reason)) {
      setFocusTypeControl(true);
      return;
    }
    if (isTableReviewReason(reason) && expectsLineItems) {
      if (detachedPanels['line-items']) detachPanel('line-items');
      else {
        paneLayout.revealPane('lines');
        setLineItemsMode('docked');
      }
      return;
    }
    // A whole-document diagnostic is not automatically a table complaint.
    // With no known editor to visit, show the original instead of an unrelated ledger.
    paneLayout.revealPane('page');
  };
  /** The Find box's Close button, and Esc in the box (as in the moved viewer). */
  const closeFind = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setActiveEvidenceLines([]);
  };
  const moveFind = (direction: number) => {
    if (!findMatches.length) return;
    const next = (findIndex + direction + findMatches.length) % findMatches.length;
    setFindIndex(next);
    const lines = [findMatches[next]];
    findEvidenceLines.current = lines;
    showEvidence(document.pages[selectedPage]?.number, lines);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const jumpToBatchSearchResult = (result: PageSearchResult) => {
    // Search requests bind the displayed batch. Still refuse a stale result,
    // never borrowing another batch or falling back to page one after an edit.
    const current = batchRef.current;
    const documentIndex =
      current?.documents.findIndex((item) => item.id === result.documentId) ?? -1;
    const pageIndex =
      documentIndex >= 0
        ? current!.documents[documentIndex].pages.findIndex(
            (page) => page.number === result.pageNumber,
          )
        : -1;
    if (
      !current ||
      current.batchId !== batch.batchId ||
      result.batchId !== current.batchId ||
      documentIndex < 0 ||
      pageIndex < 0 ||
      batch.documents[documentIndex]?.id !== result.documentId ||
      batch.documents[documentIndex]?.pages[pageIndex]?.number !== result.pageNumber
    ) {
      onNotice(
        'That search result is no longer in these documents. Search again.',
        'document-gone',
      );
      return false;
    }
    setCompareSources(false);
    const leavingDocument = current.documents[selectedDocumentRef.current];
    if (leavingDocument && leavingDocument.id !== result.documentId)
      retainedSearchDraftNavigation.current = {
        batchId: current.batchId,
        from: leavingDocument.id,
        to: result.documentId,
      };
    selectDocument(documentIndex, pageIndex);
    setBatchSearchOpen(false);
    window.requestAnimationFrame(() => viewerRef.current?.focus({ preventScroll: true }));
    return true;
  };
  /** The zoom at which a page of this size sits wholly inside the viewer. */
  const fitZoomFor = (size: { width: number; height: number }) => {
    const viewer = viewerRef.current;
    if (!viewer || size.width <= 1 || size.height <= 1) return 0;
    return Math.max(
      0.08,
      Math.min((viewer.clientWidth - 40) / size.width, (viewer.clientHeight - 40) / size.height, 1),
    );
  };
  /** Zoom the operator asked for. Records it against fit so navigation keeps it. */
  const applyZoom = (nextZoom: number, size = previewSizeRef.current) => {
    if (readerMode) {
      const readerClamped = Math.max(0.35, Math.min(3, nextZoom));
      setReaderScale(readerClamped);
      return readerClamped;
    }
    const clamped = Math.max(0.08, Math.min(5, nextZoom));
    // Every deliberate zoom gesture — Ctrl+plus, Ctrl+minus, fit width, fit height
    // — is the operator taking the view over, so the remembered evidence framing
    // is given up here rather than in each caller.
    appliedEvidenceFocus.current = null;
    const fit = fitZoomFor(size);
    if (fit > 0) viewScale.current = clamped / fit;
    setZoom(clamped);
    return clamped;
  };
  /** Back to a whole page, and forget the magnification. Fit page and Ctrl+0. */
  const resetView = () => {
    if (readerMode) {
      setReaderScale(1);
      return;
    }
    const fit = fitZoomFor(previewSizeRef.current);
    appliedEvidenceFocus.current = null;
    viewScale.current = 1;
    setZoom(fit > 0 ? fit : 1);
    setPan({ x: 0, y: 0 });
  };
  // Fires on every page image load. It re-establishes what "fit" means for a
  // page of this shape and then re-applies the operator's chosen magnification
  // on top of it, rather than discarding it. Pan does reset — a new document
  // should start at the top of the page, which is where reading begins.
  const fitView = (size = previewSize) => {
    const fit = fitZoomFor(size);
    if (fit <= 0) return;
    const focus = pendingEvidenceFocus.current ?? appliedEvidenceFocus.current;
    pendingEvidenceFocus.current = null;
    if (focus && focusEvidence(focus, size)) return;
    setZoom(Math.max(0.08, Math.min(5, fit * viewScale.current)));
    setPan({ x: 0, y: 0 });
  };
  const fitWidth = () => {
    if (readerMode) {
      setReaderScale(1);
      return;
    }
    const viewer = viewerRef.current;
    if (!viewer || previewSize.width <= 1) return;
    appliedEvidenceFocus.current = null;
    applyZoom(Math.min(4, (viewer.clientWidth - 36) / previewSize.width), previewSize);
    setPan({ x: 0, y: 0 });
  };
  const fitHeight = () => {
    if (readerMode) {
      const viewer = viewerRef.current;
      if (viewer)
        setReaderScale(
          Math.max(
            0.35,
            Math.min(
              3,
              (viewer.clientHeight - 44) /
                ((viewer.clientWidth - READER_PAGE_SIDE_SPACE) * (11 / 8.5)),
            ),
          ),
        );
      return;
    }
    const viewer = viewerRef.current;
    if (!viewer || previewSize.height <= 1) return;
    appliedEvidenceFocus.current = null;
    applyZoom(Math.min(4, (viewer.clientHeight - 36) / previewSize.height), previewSize);
    setPan({ x: 0, y: 0 });
  };
  /* Taking a freshly read batch into the workspace, projection and all.

     Two things have to happen together and neither is optional. Documents the
     operator has not loaded are reduced to skeletons, or a hundred full readings
     sit in memory. And the skeleton map is rebuilt, because that map is what
     hydrateDocument restores an evicted document from — leave it holding the
     copies captured when the batch first opened and a document that has since
     been settled comes back wearing the review flag it had an hour ago. Which is
     exactly the fault of a green document with a red thumbnail. */
  const adoptReprocessedBatch = (result: ProcessResult) => {
    clearBoundaryUndoHistory();
    invalidateHydrationRequests();
    const loaded = new Set(
      result.workspaceProjection === 'paged'
        ? (result.loadedDocumentIds ?? [])
        : result.documents.map((item) => item.id),
    );
    loadedDocumentIdsRef.current = loaded;
    loadedDocumentOrder.current = [...loaded];
    setLoadedDocumentIds(loaded);
    projectionSkeletons.current = new Map(
      result.workspaceProjection === 'paged'
        ? result.documents.map((item) => [item.id, toProjectionSkeleton(item)])
        : [],
    );
    batchRef.current = result;
    setBatch(result);
    onRestored(result);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const adoptBatch = (result: ProcessResult, opening: ProcessResult | null) => {
    clearBoundaryUndoHistory();
    const live = batchRef.current;
    const paged = live?.workspaceProjection === 'paged' || result.workspaceProjection === 'paged';
    const liveById = new Map((live?.documents ?? []).map((item) => [item.id, item]));
    const openingById = new Map((opening?.documents ?? []).map((item) => [item.id, item]));
    const documents = result.documents.map((learned) => {
      const current = liveById.get(learned.id);
      if (!current) return learned;
      const before = openingById.get(learned.id);
      const typeChangedByOperator = Boolean(
        before &&
        (current.documentType !== before.documentType ||
          current.documentTypeId !== before.documentTypeId ||
          current.documentTypeSchemaVersion !== before.documentTypeSchemaVersion ||
          Boolean(current.documentTypeVerified) !== Boolean(before.documentTypeVerified)),
      );
      const contentChangedByOperator = Boolean(
        before && reviewDocumentMutationToken(current) !== reviewDocumentMutationToken(before),
      );
      // Learning owns its inferred type metadata; the live renderer owns fields,
      // rows, pages and explicit operator type choices made since the pass began.
      // Merge those authorities instead of choosing either a stale full document
      // or a fresh-but-empty projection skeleton wholesale.
      return {
        ...current,
        ...(!typeChangedByOperator
          ? {
              documentType: learned.documentType,
              documentTypeId: learned.documentTypeId,
              documentTypeSchemaVersion: learned.documentTypeSchemaVersion,
              documentTypeConfidence: learned.documentTypeConfidence,
              documentTypeTrust: learned.documentTypeTrust,
              documentTypeVerified: learned.documentTypeVerified,
              documentTypeSource: learned.documentTypeSource,
              documentTypeOriginalPrediction: learned.documentTypeOriginalPrediction,
              documentTypeOriginalConfidence: learned.documentTypeOriginalConfidence,
              documentTypeExampleSupport: learned.documentTypeExampleSupport,
              documentTypeMatchingCues: learned.documentTypeMatchingCues,
            }
          : {}),
        needsReview: contentChangedByOperator ? current.needsReview : learned.needsReview,
        needsTeaching: contentChangedByOperator ? current.needsTeaching : learned.needsTeaching,
      };
    });
    const resultIds = new Set(documents.map((item) => item.id));
    const loaded = new Set(
      paged
        ? [...loadedDocumentIdsRef.current].filter((id) => resultIds.has(id))
        : documents.map((item) => item.id),
    );
    const adopted: ProcessResult = {
      ...(live ?? result),
      documents: paged
        ? documents.map((item) => (loaded.has(item.id) ? item : toProjectionSkeleton(item)))
        : documents,
      workspaceProjection: paged ? 'paged' : result.workspaceProjection,
      loadedDocumentIds: paged ? [...loaded] : result.loadedDocumentIds,
    };
    loadedDocumentIdsRef.current = loaded;
    loadedDocumentOrder.current = loadedDocumentOrder.current.filter((id) => loaded.has(id));
    setLoadedDocumentIds(loaded);
    if (adopted.workspaceProjection === 'paged') {
      projectionSkeletons.current = new Map(
        adopted.documents.map((item) => [item.id, toProjectionSkeleton(item)]),
      );
    }
    batchRef.current = adopted;
    setBatch(adopted);
    onRestored(adopted);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const hydrateDocument = async (documentIndex: number, force = false): Promise<boolean> => {
    const current = batchRef.current;
    const candidate = current?.documents[documentIndex];
    const desktop = window.capturePointDesktop;
    // `force` exists because "already loaded" is a claim carried over from when
    // the batch was PROCESSED, and a batch restored as skeletons contradicts it:
    // the id sits in loadedDocumentIds while the document holding it has had its
    // values stripped. Trusting the set alone left the first document of every
    // reopened large batch permanently blank — the probe read
    // {projection:"paged", selected:0, loaded:1} with a skeleton on screen.
    if (!current || !candidate || !desktop) return false;
    if (!force && loadedDocumentIdsRef.current.has(candidate.id) && candidate.sourcePath)
      return true;
    // Every whole-document gesture needs the same authoritative document. Share
    // one physical read per document: two quick line edits used to start
    // generations N and N+1, making N reject itself before it ever entered the
    // serialized save lane and silently losing the first edit.
    const pending = hydrationRequestsInFlight.current.get(candidate.id);
    if (pending) return pending;
    // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
    const request = (async (): Promise<boolean> => {
      const requestGeneration = (hydrationRequestGenerations.current.get(candidate.id) ?? 0) + 1;
      hydrationRequestGenerations.current.set(candidate.id, requestGeneration);
      hydrationAttemptedDocuments.current.add(candidate);
      setLoadingDocumentId(candidate.id);
      try {
        const detail = await desktop.getBatchDocument({
          jobId: job.id,
          batchId: current.batchId,
          documentId: candidate.id,
        });
        if (!detail || !mounted.current) return false;
        const latest = batchRef.current;
        if (!latest || latest.batchId !== current.batchId) return false;
        const liveDocument = latest.documents.find((item) => item.id === candidate.id);
        if (
          detail.id !== candidate.id ||
          !hydrationResultIsCurrent(
            hydrationRequestGenerations.current.get(candidate.id) ?? 0,
            requestGeneration,
            liveDocument,
            candidate,
          )
        )
          return false;
        // A stale response is not allowed to mutate the LRU either. Protect the
        // live selection, not the document captured by the render that started
        // this request, because the operator may have navigated while it waited.
        const protectedId = latest.documents[selectedDocumentRef.current]?.id;
        const structurallyProtectedIds =
          structuralMutationRef.current?.documentIds ?? new Set<string>();
        const narrowMutationProtectedIds = activeNarrowMutationDocumentIds();
        const order = [...loadedDocumentOrder.current.filter((id) => id !== detail.id), detail.id];
        const evicted = new Set<string>();
        while (order.length > 12) {
          const evictAt = order.findIndex(
            (id) =>
              id !== protectedId &&
              id !== detail.id &&
              !structurallyProtectedIds.has(id) &&
              !narrowMutationProtectedIds.has(id),
          );
          if (evictAt < 0) break;
          const [id] = order.splice(evictAt, 1);
          if (id) evicted.add(id);
        }
        loadedDocumentOrder.current = order;
        const nextLoaded = new Set(
          [...loadedDocumentIdsRef.current].filter((id) => !evicted.has(id)),
        );
        nextLoaded.add(detail.id);
        loadedDocumentIdsRef.current = nextLoaded;
        const next = {
          ...latest,
          loadedDocumentIds: [...nextLoaded],
          documents: latest.documents.map((item) => {
            if (item.id === detail.id) return detail;
            if (evicted.has(item.id)) return projectionSkeletons.current.get(item.id) ?? item;
            return item;
          }),
        };
        // Eagerly, not only on the next render. A caller that awaits this needs the
        // hydrated document before React has re-rendered — a boundary edit reading
        // the pre-hydration batch is reading empty fields.
        batchRef.current = next;
        setLoadedDocumentIds(nextLoaded);
        setBatch(next);
        onRestored(next);
        return true;
      } catch (reason) {
        setError(messageFor(reason, 'Could not load that document'), 'document-open-failed');
        return false;
      } finally {
        if (hydrationRequestGenerations.current.get(candidate.id) === requestGeneration)
          setLoadingDocumentId((id) => (id === candidate.id ? '' : id));
      }
    })();
    hydrationRequestsInFlight.current.set(candidate.id, request);
    try {
      return await request;
    } finally {
      if (hydrationRequestsInFlight.current.get(candidate.id) === request)
        hydrationRequestsInFlight.current.delete(candidate.id);
    }
  };
  const hydrateDocumentForWholeMutation = async (
    documentId: string,
    failureNotice: string,
    failureCode: TelemetryUiErrorCode,
  ): Promise<DocumentResult | null> => {
    const current = batchRef.current;
    const documentIndex = current?.documents.findIndex((item) => item.id === documentId) ?? -1;
    if (!current || documentIndex < 0) return null;
    // Small/non-paged batches already contain the authoritative document. A
    // paged batch deliberately strips OCR, paths, fields and rows from unopened
    // documents; every whole-document write must replace that skeleton first.
    if (current.workspaceProjection !== 'paged') return current.documents[documentIndex];
    // `loadedDocumentIds` is bookkeeping; a non-empty durable source path is the
    // projection's own proof that the live object is not a stripped skeleton.
    // Reuse it instead of forcing another native read for every admitted edit.
    if (loadedDocumentIdsRef.current.has(documentId) && current.documents[documentIndex].sourcePath)
      return current.documents[documentIndex];
    if (current.batchId.startsWith('live:')) {
      onNotice(failureNotice, failureCode);
      return null;
    }
    // A read can lose to the selection's own load in flight, or to a batch append that replaced the
    // skeleton it started from, and come back stale. That is not a failure of the gesture: a Turn page
    // clicked the moment a document opened was dropped this way (Paige's review tests under load).
    // Read again, from the live batch, a few times before refusing.
    for (let attempt = 1; attempt <= 3; attempt++) {
      const live = batchRef.current;
      const liveIndex = live?.documents.findIndex((item) => item.id === documentId) ?? -1;
      if (!live || liveIndex < 0) break;
      if (loadedDocumentIdsRef.current.has(documentId) && live.documents[liveIndex].sourcePath)
        return live.documents[liveIndex];
      if (await hydrateDocument(liveIndex, true))
        return batchRef.current?.documents.find((item) => item.id === documentId) ?? null;
    }
    onNotice(failureNotice, failureCode);
    return null;
  };
  hydrateDocumentRef.current = (index: number) => {
    void hydrateDocument(index, true);
  };
  // Puts the engine's stored revision of one document on screen in place: no
  // navigation, no loading state. Used when learning changed a waiting document's
  // reading after the shell cached it.
  const replaceDocumentWithStored = (stored: DocumentResult) => {
    const live = batchRef.current;
    if (!live || !live.documents.some((item) => item.id === stored.id)) return;
    // A new reading of the open document (learning re-read it) is a new look: the main button
    // waits out the arrival guard again before it can confirm it, from when the new reading is drawn.
    if (live.documents[selectedDocumentRef.current]?.id === stored.id) {
      arrivedAt.current = performance.now();
      arrivalPending.current = stored.id;
    }
    cacheAuthoritativeProjectionSkeleton(stored);
    const next = {
      ...live,
      documents: live.documents.map((item) => (item.id === stored.id ? stored : item)),
    };
    batchRef.current = next;
    setBatch(next);
    onRestored(next);
  };
  // Opening a document shows its current stored reading. A cached copy can be
  // older than the store: confirming another document can teach a rule that the
  // engine applies to this waiting one. Refresh quietly, and never over the
  // operator's own unsaved or in-flight work on this document. A split or join
  // being saved is such work: a join made from the later document opens the
  // joined one under the earlier one's id, and the store still holds that id's
  // pages alone until the join is saved, so a refresh then would take the later
  // document's pages off the screen.
  const refreshSelectedDocument = async (documentId: string) => {
    const current = batchRef.current;
    const cached = current?.documents.find((item) => item.id === documentId);
    const desktop = window.capturePointDesktop;
    if (!current || !cached || !desktop || current.batchId.startsWith('live:')) return;
    if (!loadedDocumentIdsRef.current.has(cached.id) && current.workspaceProjection === 'paged')
      return;
    const busy = () =>
      fieldDraftStore.getSnapshot().some((draft) => draft.documentId === cached.id) ||
      lineDraftStore.getSnapshot().some((draft) => draft.documentId === cached.id) ||
      pendingLineSaves.current > 0 ||
      fieldSavingRef.current ||
      confirmingDocumentsRef.current.has(cached.id) ||
      lineMutationAdmitted(cached.id) ||
      structuralMutationBlocks(cached.id);
    if (busy()) return;
    let stored: DocumentResult | null = null;
    try {
      stored = await desktop.getBatchDocument({
        jobId: job.id,
        batchId: current.batchId,
        documentId: cached.id,
      });
    } catch {
      return;
    }
    if (!stored || !mounted.current || busy()) return;
    const live = batchRef.current;
    // Only replace the exact copy that was checked; anything newer on screen wins.
    if (
      !live ||
      live.batchId !== current.batchId ||
      live.documents.find((item) => item.id === cached.id) !== cached
    )
      return;
    if (reviewedValuesToken(stored) !== reviewedValuesToken(cached))
      replaceDocumentWithStored(stored);
  };
  refreshSelectedDocumentRef.current = refreshSelectedDocument;
  const selectDocument = (
    documentIndex: number,
    pageIndex = 0,
    alignSpineStart = false,
    arrival = false,
  ) => {
    // The person moved: what they open is theirs to confirm.
    movedUnder.current = null;
    // A move the review made by itself (`arrival`: Next, the move after Done, Skip, Back,
    // Ctrl+PgDn, a tab switched): the main button waits out the arrival guard from when the
    // screen draws the document it opens (arrivalPending), and from now until then. A document
    // opened by hand is a look the operator chose.
    if (arrival) {
      arrivedAt.current = performance.now();
      arrivalPending.current =
        documentIndex !== selectedDocumentRef.current
          ? ((batchRef.current ?? batch)?.documents[documentIndex]?.id ?? null)
          : null;
    } else arrivalPending.current = null;
    setAllDoneArmed(null);
    spineStartRequest.current = alignSpineStart ? documentIndex : null;
    // Document navigation preserves the operator's comparison workspace.
    // Explicit source/page actions still switch to the full-page viewer.
    paneLayout.revealPane(compareSources ? 'fields' : 'page');
    // Before the render, as followSelectedPage does: a save's echo handled first must keep this
    // document open, not the one just left (it re-opened a confirmed document after the move).
    selectedDocumentRef.current = documentIndex;
    setSelectedDocument(documentIndex);
    setSelectedPage(pageIndex);
    void hydrateDocument(documentIndex);
    // Ordinary navigation is not evidence navigation: a new document opens at the
    // top of its first page, holding only the magnification. A framing still waiting for the
    // previous document's page to load is dropped too, or this one opens zoomed to it.
    appliedEvidenceFocus.current = null;
    pendingEvidenceFocus.current = null;
    setActiveEvidenceLines([]);
    setActiveBarcodeEvidence(null);
    setActiveLineItemRow(null);
    const metric = spineMetricsRef.current.find((item) => item.documentIndex === documentIndex);
    const spine = spineRef.current;
    if (metric && spine) {
      // Same measurement as the rows themselves — see the selection effect.
      const pageTop = spinePageTop(metric.top, Math.max(0, pageIndex), spinePageRow);
      const targetTop = pageTop - SPINE_HEADER_ROW;
      const targetBottom = Math.min(metric.top + metric.height, pageTop + spinePageRow);
      if (alignSpineStart) spine.scrollTop = targetTop;
      else if (targetTop < spine.scrollTop) spine.scrollTo({ top: targetTop, behavior: 'smooth' });
      else if (targetBottom > spine.scrollTop + spine.clientHeight)
        spine.scrollTo({ top: Math.max(0, targetBottom - spine.clientHeight), behavior: 'smooth' });
    }
  };

  /**
   * Whether the pointer is close enough to a word for the operator to have meant
   * to select it.
   *
   * Sampled in a small ring rather than at the single pixel under the cursor,
   * because the gaps between words and lines are text as far as anybody dragging
   * across a sentence is concerned. Landing in one of those gaps and being told
   * "you meant to pan" would break selection in exactly the place it is used
   * most — the middle of a line.
   */
  const nearPageText = (x: number, y: number) => {
    const reach = 12;
    return [
      [0, 0],
      [-reach, 0],
      [reach, 0],
      [0, -reach],
      [0, reach],
    ].some(([dx, dy]) =>
      window.document.elementFromPoint(x + dx, y + dy)?.closest('.ocr-word-zone, .barcode-zone'),
    );
  };
  const openCorrections = () => {
    setCorrectionsOpen(true);
    setCorrectionsLoading(true);
    setCorrectionsFailed(false);
    setCorrectionsRequest((value) => value + 1);
  };

  selectTeachingDocumentRef.current = (id) => {
    const index = batch?.documents.findIndex((item) => item.id === id) ?? -1;
    if (index < 0) {
      onNotice('That document is no longer in this job’s current documents.', 'document-gone');
      return true;
    }
    selectDocument(index, 0, true);
    return true;
  };
  supportSelectDocumentRef.current = (id) => {
    const index = batch?.documents.findIndex((item) => item.id === id) ?? -1;
    if (index < 0) return false;
    selectDocument(index, 0, true);
    return true;
  };
  supportConfirmRef.current = confirmDocument;
  selectReturnDocumentRef.current = ({ documentId }) => {
    const index = batch?.documents.findIndex((item) => item.id === documentId) ?? -1;
    if (index < 0) return;
    selectDocument(index, 0, true);
  };
  const jumpToCorrection = (entry: OperatorCorrection) => {
    if (!batch) return;
    const index = batch.documents.findIndex((item) => item.id === entry.documentId);
    if (index < 0) {
      setError('That document is no longer in this batch.', 'document-gone');
      return;
    }
    setCorrectionsOpen(false);
    selectDocument(index);
    setCorrectionArrival(entry);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const beginPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button === 0 && event.defaultPrevented) return;
    // Reading mode scrolls natively, so there is no pan — but a right-drag is
    // its zoom: up grows the page, down shrinks it. A bare right-click still
    // opens the value menu; the gesture claims the pointer only once it has
    // clearly moved.
    if (readerMode) {
      const viewer = viewerRef.current;
      if (event.button === 0 && viewer && !nearPageText(event.clientX, event.clientY)) {
        // Dragging blank paper moves the scroll; text remains selectable.
        event.preventDefault();
        window.getSelection()?.removeAllRanges();
        readerDragGesture.current = {
          startX: event.clientX,
          startY: event.clientY,
          originTop: viewer.scrollTop,
          originLeft: viewer.scrollLeft,
        };
        setIsPanning(true);
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      if (event.button !== 2 || window.getSelection()?.toString().trim()) return;
      event.preventDefault();
      readerZoomGesture.current = {
        startY: event.clientY,
        anchorTop: event.clientY,
        anchorLeft: event.clientX,
        startScale: readerScaleRef.current,
        latestScale: readerScaleRef.current,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    // One interaction: select text, drag blank paper to pan. Right-drag can pan
    // from text too, without a separate tool mode.
    const blankPage = event.button === 0 && !nearPageText(event.clientX, event.clientY);
    if (event.button !== 2 && !blankPage) return;
    if (event.button === 2 && window.getSelection()?.toString().trim()) return;
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    panGesture.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: panRef.current.x,
      originY: panRef.current.y,
      moved: false,
      button: event.button,
    };
    appliedEvidenceFocus.current = null;
    setIsPanning(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  /**
   * The drag moves the page directly; React hears about it once, at the end.
   *
   * Every pointermove used to call setPan, which re-rendered the whole review
   * workspace — a hundred spine thumbnails, the fields panel, the line-item
   * table — sixty to a hundred and twenty times a second. It mostly kept up, and
   * then periodically did not: a steady drag ran smooth, stuttered, ran smooth,
   * which is the shape of allocation churn rather than of slow layout.
   *
   * Writing the transform straight onto the node keeps the gesture off the React
   * path entirely. The state is set once on release, so everything derived from
   * pan is still correct the moment the operator lets go.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const movePan = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragGesture = readerDragGesture.current;
    if (dragGesture) {
      const viewer = viewerRef.current;
      if (!viewer) return;
      viewer.scrollTop = dragGesture.originTop - (event.clientY - dragGesture.startY);
      viewer.scrollLeft = dragGesture.originLeft - (event.clientX - dragGesture.startX);
      event.preventDefault();
      return;
    }
    const readerGesture = readerZoomGesture.current;
    if (readerGesture) {
      // Live feedback is a single composited transform on the flow; the real
      // width change and re-render happen once, on release. Setting state per
      // pointermove re-rendered the whole workspace at mouse rate, which is
      // exactly the jitter the pan gesture solved the same way.
      const viewer = viewerRef.current;
      const flow = viewer?.querySelector<HTMLElement>('.reader-flow');
      if (!viewer || !flow) return;
      if (!readerGesture.moved && Math.abs(event.clientY - readerGesture.startY) < 4) return;
      readerGesture.moved = true;
      const target = Math.max(
        0.35,
        Math.min(
          3,
          readerGesture.startScale * Math.exp((readerGesture.startY - event.clientY) * 0.006),
        ),
      );
      readerGesture.latestScale = target;
      const box = viewer.getBoundingClientRect();
      flow.style.transformOrigin = `${viewer.scrollLeft + (readerGesture.anchorLeft - box.left)}px ${viewer.scrollTop + (readerGesture.anchorTop - box.top)}px`;
      flow.style.transform = `scale(${target / readerGesture.startScale})`;
      event.preventDefault();
      return;
    }
    const gesture = panGesture.current;
    if (!gesture) return;
    const x = event.clientX - gesture.startX;
    const y = event.clientY - gesture.startY;
    if (!gesture.moved && Math.hypot(x, y) < 3) return;
    if (!gesture.moved) setIsPanning(true);
    gesture.moved = true;
    const next = { x: gesture.originX + x, y: gesture.originY + y };
    gesture.latest = next;
    const stack = pageStackRef.current;
    if (stack)
      stack.style.transform = `translate(-50%, -50%) translate(${next.x}px, ${next.y}px) scale(${zoomRef.current})${viewerTurnRef.current}`;
    else setPan(next);
    event.preventDefault();
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (readerDragGesture.current) {
      readerDragGesture.current = null;
      setIsPanning(false);
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
      return;
    }
    if (readerZoomGesture.current) {
      // A drag zoomed; a stationary release is a real right-click and the
      // context menu should follow.
      const gesture = readerZoomGesture.current;
      readerZoomGesture.current = null;
      suppressContextMenu.current = gesture.moved;
      const viewer = viewerRef.current;
      const flow = viewer?.querySelector<HTMLElement>('.reader-flow');
      if (flow) {
        flow.style.transform = '';
        flow.style.transformOrigin = '';
      }
      if (gesture.moved && viewer) {
        // The one place the gesture becomes state: commit the scale, then move
        // the scroll so the paper under the anchor stays put through the
        // transform-to-layout handoff.
        const ratio = gesture.latestScale / gesture.startScale;
        const box = viewer.getBoundingClientRect();
        const anchorTop = gesture.anchorTop - box.top;
        const anchorLeft = gesture.anchorLeft - box.left;
        const scrollTop = viewer.scrollTop;
        const scrollLeft = viewer.scrollLeft;
        setReaderScale(gesture.latestScale);
        window.requestAnimationFrame(() => {
          viewer.scrollTop = (scrollTop + anchorTop) * ratio - anchorTop;
          viewer.scrollLeft = (scrollLeft + anchorLeft) * ratio - anchorLeft;
        });
      }
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
      return;
    }
    if (!panGesture.current) return;
    suppressContextMenu.current = panGesture.current.button === 2 && panGesture.current.moved;
    // The one place the gesture becomes state. Anything reading pan — the
    // evidence framing, the clamps, a detached panel — sees the finished
    // position rather than a hundred intermediate ones.
    const settled = panGesture.current.latest;
    panGesture.current = null;
    if (settled) setPan(settled);
    setIsPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const beginSpineResize = (event: React.PointerEvent<HTMLDivElement>) => {
    spineResizeGesture.current = { startX: event.clientX, startWidth: paneLayout.spine };
    setResizingSpine(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const moveSpineResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = spineResizeGesture.current;
    if (!gesture) return;
    setSpineWidth(
      Math.min(360, Math.max(132, gesture.startWidth + event.clientX - gesture.startX)),
    );
  };
  const endSpineResize = (event: React.PointerEvent<HTMLDivElement>) => {
    spineResizeGesture.current = null;
    setResizingSpine(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const beginResultsResize = (event: React.PointerEvent<HTMLDivElement>) => {
    resultsResizeGesture.current = { startX: event.clientX, startWidth: paneLayout.fields };
    setResizingResults(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const moveResultsResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = resultsResizeGesture.current;
    if (!gesture) return;
    setResultsWidth(
      Math.min(600, Math.max(270, gesture.startWidth + gesture.startX - event.clientX)),
    );
  };
  const endResultsResize = (event: React.PointerEvent<HTMLDivElement>) => {
    resultsResizeGesture.current = null;
    setResizingResults(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  // The ledger never exceeds what the window can spare, so a tall pane saved on a
  // big monitor does not swallow the page on a laptop.
  const clampLineItemsHeight = (height: number) =>
    Math.min(
      Math.min(LINE_ITEMS_MAX_HEIGHT, Math.round(window.innerHeight * 0.72)),
      Math.max(LINE_ITEMS_MIN_HEIGHT, Math.round(height)),
    );
  const beginLinesResize = (event: React.PointerEvent<HTMLDivElement>) => {
    linesResizeGesture.current = { startY: event.clientY, startHeight: lineItemsHeight };
    setResizingLines(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const moveLinesResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = linesResizeGesture.current;
    if (!gesture) return;
    setLineItemsHeight(clampLineItemsHeight(gesture.startHeight + gesture.startY - event.clientY));
  };
  const endLinesResize = (event: React.PointerEvent<HTMLDivElement>) => {
    linesResizeGesture.current = null;
    setResizingLines(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  reviewActionHandler.current = (message) => {
    if (message.type === 'field-draft-command') {
      fieldDraftCommandRef.current(message.command);
      return;
    }
    if (message.type === 'line-draft-command') {
      lineDraftCommandRef.current(message.command);
      return;
    }
    if (message.type !== 'action') return;
    const action = message.action;
    if (action.type === 'select-document') {
      const live = batchRef.current ?? batch;
      if (action.index >= 0 && action.index < live.documents.length) {
        selectDocument(
          action.index,
          action.pageIndex ?? 0,
          false,
          action.index !== selectedDocumentRef.current,
        );
      }
      return;
    }
    if (action.type === 'select-page') {
      const live = batchRef.current ?? batch;
      const liveDocument = live.documents[selectedDocumentRef.current];
      if (liveDocument && action.index >= 0 && action.index < liveDocument.pages.length)
        selectDocument(selectedDocumentRef.current, action.index);
      return;
    }

    if (action.type === 'toggle-field-evidence' || action.type === 'hover-field-evidence') {
      // Presentation gestures share the same document identity checks as edits,
      // but never enter the native mutation lane or create operator truth.
      const live = batchRef.current ?? batch;
      const target = live.documents[selectedDocumentRef.current];
      if (
        !target ||
        target.id !== action.documentId ||
        reviewDocumentMutationToken(target) !== action.documentMutationToken
      )
        return;
      const schema =
        job.documentTypes.find((type) => type.id === target.documentTypeId) ??
        job.documentTypes.find(
          (type) =>
            type.name.localeCompare(target.documentType, undefined, { sensitivity: 'accent' }) ===
            0,
        );
      if (action.type === 'toggle-field-evidence' && typeof action.fieldId !== 'string') return;
      if (
        action.fieldId !== null &&
        !schema?.fields.some((field) => field.id === action.fieldId && field.kind !== 'line-items')
      )
        return;
      if (action.type === 'toggle-field-evidence') togglePinnedField(action.fieldId);
      else setHoveredFieldId(action.fieldId);
      return;
    }

    const startDetachedMutation = (
      bindings: Array<{ documentId: string; documentMutationToken: string }>,
      work: (live: ProcessResult, documents: DocumentResult[]) => void | Promise<unknown>,
      lane: 'exclusive' | 'line-edit' = 'exclusive',
    ) => {
      const live = batchRef.current ?? batch;
      const documents = bindings.map((binding) =>
        live.documents.find((item) => item.id === binding.documentId),
      );
      if (
        documents.some((item) => !item) ||
        documents.some(
          (item, index) =>
            reviewDocumentMutationToken(item!) !== bindings[index].documentMutationToken,
        )
      ) {
        onNotice(
          'That detached panel was showing an older document. It has been refreshed; try the change again.',
          'detached-change-failed',
        );
        reviewChannelRef.current?.postMessage({
          type: 'state',
          snapshot: reviewSnapshotRef.current,
        } satisfies ReviewWorkspaceMessage);
        return;
      }
      if (bindings.some((binding) => detachedMutationDocuments.current.has(binding.documentId))) {
        onNotice(
          'That document is already saving a change from the detached panel',
          'detached-change-failed',
        );
        return;
      }
      if (lane === 'line-edit') {
        // Completed cell edits use the same serialized native-save lane as the
        // docked ledger. A second detached-only document lock used to reject
        // the next cell while its input was still explicitly editable. Enter
        // synchronously after validating the displayed revision; the save lane
        // owns admission, structural exclusion and durable delta accumulation.
        try {
          void Promise.resolve(work(live, documents as DocumentResult[])).catch((reason: unknown) =>
            setError(
              messageFor(reason, 'The detached-panel change could not be saved.'),
              'detached-change-failed',
            ),
          );
        } catch (reason) {
          setError(
            messageFor(reason, 'The detached-panel change could not be saved.'),
            'detached-change-failed',
          );
        }
        return;
      }
      for (const binding of bindings) detachedMutationDocuments.current.add(binding.documentId);
      return Promise.resolve()
        .then(() => {
          // Re-read after admission as well. A native save already resolving in
          // the microtask queue can advance the document between the Broadcast-
          // Channel callback and this work item even though no second detached
          // gesture was admitted.
          const latest = batchRef.current ?? live;
          const latestDocuments = bindings.map((binding) =>
            latest.documents.find((item) => item.id === binding.documentId),
          );
          if (
            latestDocuments.some((item) => !item) ||
            latestDocuments.some(
              (item, index) =>
                reviewDocumentMutationToken(item!) !== bindings[index].documentMutationToken,
            )
          )
            throw new Error(
              'That document changed before the detached-panel edit could begin. Try the edit again.',
            );
          return work(latest, latestDocuments as DocumentResult[]);
        })
        .catch((reason: unknown) =>
          setError(
            messageFor(reason, 'The detached-panel change could not be saved.'),
            'detached-change-failed',
          ),
        )
        .finally(() => {
          for (const binding of bindings)
            detachedMutationDocuments.current.delete(binding.documentId);
        });
    };

    // A split or join from a detached viewer joins the same queue as one made
    // here. It names a page, so it does not need the panel's picture of the
    // document to be current: the page is where the operator wants the
    // boundary, and the queue applies it to the documents as they are when its
    // turn comes. Joining still needs the two documents the panel showed to be
    // next to each other now.
    if (action.type === 'split-document') {
      const live = batchRef.current ?? batch;
      if (!locateBatchPage(live.documents, action.pageNumber)) {
        onNotice(
          'That page is no longer in this batch, so its boundary was left as it is',
          'boundary-save-failed',
        );
        return;
      }
      noteBoundaryChange(
        boundaryQueue.enqueue(
          { kind: 'split', pageNumber: action.pageNumber },
          live.documents[selectedDocumentRef.current]?.id ?? null,
        ),
      );
      return;
    }
    if (action.type === 'merge-document') {
      const live = batchRef.current ?? batch;
      const documentIndex = live.documents.findIndex((item) => item.id === action.documentId);
      const opening = live.documents[documentIndex]?.pages[0];
      if (
        documentIndex <= 0 ||
        !opening ||
        live.documents[documentIndex - 1]?.id !== action.previousDocumentId
      ) {
        onNotice(
          'Those documents are no longer next to each other, so they were not joined',
          'boundary-save-failed',
        );
        return;
      }
      noteBoundaryChange(
        boundaryQueue.enqueue(
          { kind: 'join', pageNumber: opening.number },
          live.documents[selectedDocumentRef.current]?.id ?? null,
        ),
      );
      return;
    }
    if (action.type === 'focus-field') {
      const live = batchRef.current ?? batch;
      const targetIndex = live.documents.findIndex((item) => item.id === action.documentId);
      if (targetIndex < 0) return;
      const target = live.documents[targetIndex];
      const source = target.fields.find((item) => item.name === action.name);
      const value = source ? numericFieldDisplayEvidence(source, target.pages) : undefined;
      if (targetIndex !== selectedDocumentRef.current) {
        const targetPageIndex = Math.max(
          0,
          target.pages.findIndex((page) => page.number === value?.evidencePageNumber),
        );
        selectDocument(targetIndex, targetPageIndex);
        return;
      }
      showEvidence(value?.evidencePageNumber, value?.evidenceLineIndexes, value?.barcodeEvidence);
      citeActiveEvidence(value?.evidenceLineIndexes, value);
      return;
    }
    if (action.type === 'skip-document') {
      const live = batchRef.current ?? batch;
      // Skip, as in the main window: the next document asked about.
      const next = nextAsked(
        live.documents,
        selectedDocumentRef.current,
        calibrating,
        (item) =>
          !isAwaitingUnderstanding(item, Boolean(captureProcessingId)) &&
          !(assistedReview && item.needsReview && awaitsAiReviewer(item)),
      );
      if (next.index != null) {
        reviewTally.skipped(job.id);
        selectDocument(next.index, 0, false, true);
      }
      return;
    }
    if (action.type === 'back-to-confirmed') {
      goBackToConfirmedRef.current();
      return;
    }
    if (action.type === 'undo') {
      undoShortcutRef.current(Boolean(action.fromTextBox));
      return;
    }
    if (action.type === 'undo-delete') {
      if (workspaceNotice.action?.telemetry === DELETE_UNDO_TELEMETRY) workspaceNotice.action.run();
      return;
    }
    if (
      action.type === 'set-document-type' ||
      action.type === 'confirm-document' ||
      action.type === 'focus-review' ||
      action.type === 'delete-document'
    ) {
      const live = batchRef.current ?? batch;
      const targetIndex = live.documents.findIndex((item) => item.id === action.documentId);
      const target = live.documents[targetIndex];
      // The panel sends its confirmation at once, right after the typing it submitted, bound to the
      // revision on its screen; by the time it arrives this window may already show that typing (its
      // save paints first). Its Delete is bound to the revision it was clicked on, and the typing whose
      // blur that click caused may save before it arrives. Only that difference is allowed,
      // and only for the confirmation and Delete, which both wait for the typing to save. Typing is a
      // value or a line typed; anything else saved meanwhile (a row added or accepted, a picked value,
      // value Undo, Recheck) is not, and the panel's Delete is off while it saves.
      if (
        !target ||
        targetIndex !== selectedDocumentRef.current ||
        (reviewDocumentMutationToken(target) !== action.documentMutationToken &&
          !(
            (action.type === 'confirm-document' || action.type === 'delete-document') &&
            ownTypingRevisions.reaches(
              target.id,
              action.documentMutationToken,
              reviewDocumentMutationToken(target),
            )
          ))
      ) {
        onNotice(
          'That detached panel was showing an older document. It has been refreshed; try again.',
          'detached-change-failed',
        );
        reviewChannelRef.current?.postMessage({
          type: 'state',
          snapshot: reviewSnapshotRef.current,
        } satisfies ReviewWorkspaceMessage);
        return;
      }
      if (action.type === 'set-document-type') void setDocumentType(action.documentTypeId);
      // Delete is Manual's alone, as here.
      else if (action.type === 'delete-document') {
        if (pilotMode(job.reviewPolicy?.processingMode) === 'human-reviewed')
          void deleteDocument(target.id);
      } else if (action.type === 'focus-review') {
        const reason = currentReviewWork[action.index];
        if (reason?.label === action.label) goToReviewItem(reason);
      } else {
        // Waiting for typing to save (confirmAfterDraftsRef), it compares again once saved.
        detachedPressRef.current = {
          documentId: target.id,
          documentMutationToken: action.documentMutationToken,
        };
        confirmAndAdvanceRef.current();
        if (confirmAfterDraftsRef.current !== target.id) detachedPressRef.current = null;
      }
      return;
    }
    if (action.type === 'pick-lookup-choice') {
      startDetachedMutation([action], (_live, [target]) =>
        pickLookupChoice(target.id, action.name, action.rowNumber),
      );
      return;
    }
    if (action.type === 'set-field') {
      startDetachedMutation([action], (_live, [target]) => {
        const schema =
          job.documentTypes.find((type) => type.id === target.documentTypeId) ??
          job.documentTypes.find(
            (type) =>
              type.name.localeCompare(target.documentType, undefined, { sensitivity: 'accent' }) ===
              0,
          );
        const configured = schema?.fields.find((field) => field.name === action.name);
        const value = target.fields.find((field) => field.name === action.name);
        if (!configured) throw new Error('That field is no longer part of this document type.');
        // A value picked from the page answers that field only.
        return persistFieldValue(
          configured,
          action.barcodeEvidence ? action.value : selectedTextForField(action.value, configured),
          action.lineIndexes ?? [],
          value,
          target.id,
          action.pageNumber,
          documentFieldContractToken(target),
          action.barcodeEvidence ?? null,
        );
      });
      return;
    }
    if (action.type === 'focus-line-item') {
      const live = batchRef.current ?? batch;
      const targetIndex = live.documents.findIndex((item) => item.id === action.documentId);
      if (targetIndex !== selectedDocumentRef.current) {
        if (targetIndex >= 0) selectDocument(targetIndex, 0);
        return;
      }
      const row = live.documents[targetIndex]?.lineItems.find(
        (item) => item.rowNumber === action.rowNumber,
      );
      if (row) {
        if (action.cell === 'row') {
          showRowSource(row);
          return;
        }
        const evidence = lineItemEvidenceForCell(row, action.cell);
        showEvidence(evidence.pageNumber, evidence.lineIndexes, null, row.rowNumber);
      }
      return;
    }
    const rowCompletion = startDetachedMutation(
      [action],
      (live, [target]) => {
        if (action.type === 'add-line-item') {
          const row: LineItemRow = {
            rowNumber: 0,
            description: '',
            quantity: undefined,
            unitPrice: undefined,
            amount: undefined,
            confidence: 1,
            arithmeticValidated: false,
            pageNumber: action.pageNumber,
            evidenceLineIndexes: [],
          };
          return persistLineItemChange(
            CORRECTION.lineItemAdded,
            live,
            `${target.id}:new`,
            null,
            row,
            live,
            target.id,
          );
        }
        const before = target.lineItems.find((row) => row.rowNumber === action.rowNumber);
        if (!before) throw new Error('That line item changed before the edit arrived.');
        if (action.type === 'delete-line-item') {
          const next = {
            ...live,
            documents: live.documents.map((item) =>
              item.id === target.id
                ? {
                    ...item,
                    lineItems: item.lineItems
                      .filter((row) => row.rowNumber !== action.rowNumber)
                      .map((row, index) => ({ ...row, rowNumber: index + 1 })),
                  }
                : item,
            ),
          };
          return persistLineItemChange(
            CORRECTION.lineItemDeleted,
            next,
            `${target.id}:${action.rowNumber}`,
            before,
            null,
            live,
            target.id,
          );
        }
        const patched =
          action.type === 'set-line-item'
            ? { ...before, ...action.patch }
            : { ...before, arithmeticValidated: true, verified: true };
        const after =
          action.type === 'set-line-item'
            ? { ...patched, arithmeticValidated: arithmeticValidatedAfterEdit(before, patched) }
            : patched;
        const next = {
          ...live,
          documents: live.documents.map((item) =>
            item.id === target.id
              ? {
                  ...item,
                  lineItems: item.lineItems.map((row) =>
                    row.rowNumber === action.rowNumber ? after : row,
                  ),
                }
              : item,
          ),
        };
        return persistLineItemChange(
          CORRECTION.lineItemCorrected,
          next,
          `${target.id}:${action.rowNumber}`,
          before,
          after,
          live,
          target.id,
        );
      },
      action.type === 'set-line-item' ? 'line-edit' : 'exclusive',
    );
    if (action.type === 'accept-line-item' && action.requestId) {
      void Promise.resolve(rowCompletion).finally(() => {
        const current = batchRef.current?.documents.find((item) => item.id === action.documentId);
        reviewChannelRef.current?.postMessage({
          type: 'row-confirmation-result',
          requestId: action.requestId!,
          documentMutationToken: current ? reviewDocumentMutationToken(current) : null,
        } satisfies ReviewWorkspaceMessage);
      });
    }
  };
  /**
   * Whether a row is settled: the arithmetic works, or a person has said it is
   * fine anyway.
   *
   * A document can be right in ways the rule cannot see — a rounded line, a
   * discount folded into an amount, a printed total that is simply what the
   * customer was charged. There was no way to say so, so the only route past a
   * row the product disagreed with was to change the numbers on it, which is
   * asking an operator to falsify a document to clear a warning.
   *
   * `arithmeticValidated` carries the acceptance. The engine sets it when the
   * multiplication works; an operator sets it when they have looked and it is
   * right regardless. Both mean the same thing to everything downstream — nobody
   * needs to look at this row again.
   */
  const rowIsSettled = (item: LineItemRow) => lineItemBalances(item) || item.arithmeticValidated;
  /**
   * What is wrong with these rows, in the words the rest of the screen uses.
   *
   * This printed the engine's stored sentences verbatim, so the panel under the
   * table still read "1 fields and 0 line items could not be aligned to faithful
   * OCR evidence" after the checklist above had been taught to say which field
   * and why. Grounding is a field's problem and is reported on the field, so it
   * has no business under the line items at all; what belongs here is the rows.
   */
  const visibleFindings = document.lineItems.filter((row) => !rowIsSettled(row));
  /** Write the multiplication into the amount, and record it as an operator's edit. */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const useCalculatedAmount = (row: LineItemRow, value: number) => {
    // Not refused while another row saves: the save queue takes it after that one, as it does
    // "Looks right" (acceptLineItem). The row's own pending key keeps a second click from
    // queueing the same amount twice, and holds its buttons (accepting) until it has saved.
    if (!batch) return;
    const targetDocumentId = document.id;
    const key = `${targetDocumentId}:${row.rowNumber}`;
    if (pendingRowConfirmations.current.has(key)) return;
    pendingRowConfirmations.current.add(key);
    setRowConfirmationKeys(new Set(pendingRowConfirmations.current));
    // Rounded to the currency, not left at whatever the floating-point
    // multiplication produced: 0.1 x 3 is not 0.30000000000000004 on an invoice.
    const amount = Math.round(value * 100) / 100;
    const after = { ...row, amount, arithmeticValidated: true };
    const next = {
      ...batch,
      documents: batch.documents.map((item, index) =>
        index !== selectedDocument
          ? item
          : {
              ...item,
              lineItems: item.lineItems.map((entry) =>
                entry.rowNumber === row.rowNumber ? after : entry,
              ),
            },
      ),
    };
    if (arrivedLineRow === row.rowNumber) setArrivedLineRow(null);
    void persistLineItemChange(
      CORRECTION.lineItemCorrected,
      next,
      key,
      row,
      after,
      undefined,
      targetDocumentId,
    )
      .catch((reason) => {
        if (mounted.current)
          setError(
            reason instanceof Error ? reason.message : 'Could not save line item',
            'line-item-save-failed',
          );
      })
      .finally(() => {
        pendingRowConfirmations.current.delete(key);
        if (mounted.current) setRowConfirmationKeys(new Set(pendingRowConfirmations.current));
      });
  };
  /* Rows on this document whose columns are shifted one to the left. */
  const shiftedRows = document.lineItems
    .map((row) => ({ row, fixed: shiftedLineItem(row) }))
    .filter(
      (
        entry,
      ): entry is { row: LineItemRow; fixed: NonNullable<ReturnType<typeof shiftedLineItem>> } =>
        entry.fixed !== null,
    );
  /**
   * Put every shifted row right at once.
   *
   * The operator corrected nineteen rows by hand on one batch and fifteen were
   * the same fault, three edits each. The product watched them do it forty-four
   * times and offered nothing — the correction is entirely determined by the two
   * numbers already captured, so there was never anything to guess at.
   *
   * Still their decision, and still one correction event per row: this writes
   * exactly what the operator would have written, which is what any future
   * table-learning has to read.
   */
  const fixShiftedRows = () => {
    if (!batch || lineSaving || shiftedRows.length === 0) return;
    if (fieldSavingRef.current) {
      onNotice('Finish saving the field before fixing the shifted rows', 'line-item-busy');
      return;
    }
    void (async () => {
      let saved = 0;
      for (const { row, fixed } of shiftedRows) {
        // Paint one row only after that row has been admitted to the durable
        // lane. Publishing the whole corrected table first let the first native
        // save carry later rows whose correction events had not been written;
        // a crash then left durable changes with no ground-truth ledger.
        const live = batchRef.current ?? batch;
        // Validated only when the shifted numbers add up (a shifted row was never
        // accepted, so the shared edit rule reduces to that): the engine trusts
        // the flag alone, and 5 × 100.00 = 499.95 must stay in review.
        const after = { ...row, ...fixed };
        if (
          !(await persistLineItemChange(
            CORRECTION.lineItemCorrected,
            live,
            `${document.id}:${row.rowNumber}`,
            row,
            { ...after, arithmeticValidated: arithmeticValidatedAfterEdit(row, after) },
          ))
        )
          break;
        saved += 1;
      }
      if (saved === shiftedRows.length)
        onNotice(`${counted(shiftedRows.length, 'line')} put right · columns were read one across`);
    })();
  };
  /** Mark a row as looked at and correct as printed. */
  const acceptLineItem = (row: LineItemRow) => {
    if (!batch || (row.verified && row.arithmeticValidated)) return;
    const targetDocumentId = document.id;
    const key = `${targetDocumentId}:${row.rowNumber}`;
    if (pendingRowConfirmations.current.has(key)) return;
    pendingRowConfirmations.current.add(key);
    setRowConfirmationKeys(new Set(pendingRowConfirmations.current));
    const after = { ...row, arithmeticValidated: true, verified: true };
    const next = {
      ...batch,
      documents: batch.documents.map((item, index) =>
        index !== selectedDocument
          ? item
          : {
              ...item,
              lineItems: item.lineItems.map((entry) =>
                entry.rowNumber === row.rowNumber ? after : entry,
              ),
            },
      ),
    };
    if (arrivedLineRow === row.rowNumber) setArrivedLineRow(null);
    void persistLineItemChange(
      CORRECTION.lineItemCorrected,
      next,
      key,
      row,
      after,
      undefined,
      targetDocumentId,
    )
      .catch((reason) => {
        if (mounted.current)
          setError(messageFor(reason, 'Could not confirm line item'), 'line-item-save-failed');
      })
      .finally(() => {
        pendingRowConfirmations.current.delete(key);
        if (mounted.current) setRowConfirmationKeys(new Set(pendingRowConfirmations.current));
      });
  };
  const expectsLineItems = configuredFields.some((item) => item.kind === 'line-items');
  /**
   * Why a document is not finished, in the words an operator needs, and which
   * field is to blame where there is one.
   *
   * This used to be a conjunction of five conditions collapsed into a single
   * orange dot, so a document could be flagged and there was no way to see what
   * for. It also treated an empty field as a fault regardless of whether the job
   * asks for that field, which flagged every credit memo that legitimately has
   * no purchase order number. Required-ness decides that now, not emptiness.
   */
  /**
   * Everything outstanding on a document, not the first thing found.
   *
   * This returned one reason and stopped. So a document with four problems said
   * one, the operator fixed it, and the same banner came back wearing a different
   * sentence — with no way to know whether they were near the end or had just
   * started. Dustin: "if there's multiple things wrong it seems like we should
   * indicate like four items needing reviewed, and what they are, and as you
   * confirm those individual things it counts down."
   *
   * So it is a list, in the order an operator should work it: the type first,
   * because every other check depends on which fields the document is supposed to
   * have; then required fields that are empty; then fields the reader is unsure
   * of; then whole-document findings; then flags the engine raised about itself.
   */
  const documentReviewReasons = (
    item: (typeof batch.documents)[number],
    documentIndex = -1,
  ): ReviewReason[] => {
    if (!item.needsReview && item.needsTeaching) return [];
    // A document nobody has opened cannot say what is wrong with it — naming a
    // fault needs the fields and rows in hand. It answers from the flag, which is
    // the same thing the spine and the queue read, and says plainly that the
    // detail is not here yet.
    //
    // It used to answer from a separate `ready` column with a rule of its own,
    // and emit a bare "Needs review" with no name, no cause and nowhere to go —
    // an item an operator could neither act on nor clear.
    // ... and it must not say that about the document the operator is LOOKING AT.
    //
    // This fired on any document missing from loadedDocumentIds, open or not, so
    // the document on screen — showing its type and its read fields — carried a
    // banner telling the operator to open it. Reported from a screenshot of the
    // document open, fields visible, banner showing. An instruction to reach a
    // place you are already standing in is worse than silence: it reads as the
    // screen not knowing what it is displaying.
    //
    // Silent rather than reworded, because without the content in hand there is
    // nothing true to say about what this document needs, and the panel beside
    // it already states its actual condition.
    // A document nobody has opened cannot say what is wrong with it, and before
    // this job's setup is saved NOTHING here can. Both earlier attempts at a
    // sentence for that state were measured against a real screen and both were
    // false: "Open this document to see what it needs" pointed at the document
    // already open, and "Its fields arrive when you save this setup" sat directly
    // above Order Number SO-733326 and Packing Slip Number PS-980099 — already
    // read, already on screen.
    //
    // Returning nothing is also not free: an item still carrying needsReview with
    // an empty reason list trips the "Still flagged, and nothing here can say why"
    // notice, which then renders under the green confirm bar. So the notice is
    // suppressed for the same state, where it is asked below.
    if (!loadedDocumentIds.has(item.id))
      return item.needsReview &&
        !beforeSetupSaved &&
        batch.documents[selectedDocument]?.id !== item.id
        ? [{ label: 'Open this document to see what it needs' }]
        : [];
    const schema =
      filedDocumentType(item, job.documentTypes, typeNameHistory) ??
      job.documentTypes.find(
        (type) =>
          type.name.localeCompare(item.documentType, undefined, { sensitivity: 'accent' }) === 0,
      );
    // A name the job has no type for (the AI reviewer may answer one) is shown in the type box,
    // so "No document type chosen" under it read as false. "Unclassified document" is the engine's
    // name for no type, never a type the job lacks.
    const named = item.documentType?.trim();
    if (!schema)
      return [
        {
          label:
            named &&
            named.localeCompare(UNCLASSIFIED_DOCUMENT, undefined, { sensitivity: 'accent' }) !== 0
              ? `${named} is not one of this job's types`
              : 'No document type chosen',
          documentType: true,
        },
      ];

    const reasons: ReviewReason[] = [];
    const extracted = new Map(item.fields.map((value) => [value.name.toLocaleLowerCase(), value]));
    for (const configured of schema.fields) {
      if (!configured.required) continue;
      if (configured.kind === 'line-items') {
        // No fieldId: the fields pane filters line-items out, so there is no row to
        // travel to and "Go to the field" would do nothing at all.
        continue;
      }
      const value = extracted.get(configured.name.toLocaleLowerCase());
      if (!value?.value.trim())
        reasons.push({
          label: `${configured.name} is empty`,
          fieldId: configured.id,
          fieldName: configured.name,
          // An empty field still carries where the reader looked, which is the
          // part of the page an operator has to read to fill it in.
          ...(value?.evidencePageNumber ? { pageNumber: value.evidencePageNumber } : {}),
          ...(value?.evidenceLineIndexes?.length
            ? { evidenceLineIndexes: value.evidenceLineIndexes }
            : {}),
        });
    }
    for (const flagged of item.fields.filter((value) => value.needsReview && !value.verified)) {
      // The synthetic type field is handled by the type decision below, not
      // by scalar review. Its retained flag can predate a learned authorization.
      if (flagged.name.toLocaleLowerCase() === 'document type') continue;
      // Not twice for one field: an empty required field is already named above,
      // and saying it again would inflate the count the operator is working down.
      if (
        reasons.some(
          (reason) => reason.fieldName?.toLocaleLowerCase() === flagged.name.toLocaleLowerCase(),
        )
      )
        continue;
      const configured = schema.fields.find(
        (field) =>
          field.name.localeCompare(flagged.name, undefined, { sensitivity: 'accent' }) === 0,
      );
      if (configured?.kind === 'line-items') continue;
      reasons.push({
        label: `${flagged.name} needs review`,
        fieldId: configured?.id,
        fieldName: flagged.name,
        ...(flagged.evidencePageNumber ? { pageNumber: flagged.evidencePageNumber } : {}),
        ...(flagged.evidenceLineIndexes?.length
          ? { evidenceLineIndexes: flagged.evidenceLineIndexes }
          : {}),
      });
    }
    // A value under its field's threshold holds the document without a reader flag: name it (onboarding.ts, uncertainFields).
    // PAIGE: as the check that flagged an exported document saw it (paige/flagged-after-export.ts).
    for (const uncertain of uncertainFields(asTheCheckSeesIt(item), schema, job.reviewPolicy)) {
      if (
        reasons.some(
          (reason) => reason.fieldName?.toLocaleLowerCase() === uncertain.name.toLocaleLowerCase(),
        )
      )
        continue;
      reasons.push({
        label: `${uncertain.name} needs review`,
        fieldId: schema.fields.find(
          (field) =>
            field.name.localeCompare(uncertain.name, undefined, { sensitivity: 'accent' }) === 0,
        )?.id,
        fieldName: uncertain.name,
        ...(uncertain.evidencePageNumber ? { pageNumber: uncertain.evidencePageNumber } : {}),
        ...(uncertain.evidenceLineIndexes?.length
          ? { evidenceLineIndexes: uncertain.evidenceLineIndexes }
          : {}),
      });
    }

    for (const finding of item.validationFindings.filter(
      (entry) => entry.severity === 'error' || entry.severity === 'warning',
    )) {
      if (!findingStillHolds(finding, item)) continue;
      if (
        finding.code === FINDING.lineItemReadIssue ||
        finding.code === FINDING.lineItemsPossiblyIncomplete
      )
        continue;
      if (
        finding.code === FINDING.partialSemanticProcessing &&
        finding.message ===
          'No line-item rows were mapped from explicit table headings. Review the document.'
      )
        continue;
      if (
        finding.code === FINDING.lineItemArithmetic &&
        job.reviewPolicy?.lineItemArithmeticEnabled === false
      )
        continue;
      // This is a durable account of a successful repair, not a question. Keep
      // it visible below as a neutral page note, but do not put it on the review
      // checklist: a permanent checklist item made confirmation impossible.
      if (finding.code === FINDING.pageRotationApplied) continue;
      // A finding is a claim recorded when the document was processed. This one
      // can be re-checked from the rows in hand, so it is — and it is dropped
      // when it no longer holds. Two ways it goes stale: an operator corrects
      // the row, and the rule itself changes. Both happened here. Credit memos
      // were flagged for having a negative extended amount, that was fixed, and
      // the documents already in the batch kept telling an operator to go and
      // look at rows the product now agrees with — every one of them showing a
      // green tick underneath the warning.
      if (finding.code === FINDING.lineItemArithmetic) {
        const offending = item.lineItems.filter(
          (row) => !lineItemBalances(row) && !row.arithmeticValidated,
        );
        if (offending.length === 0) continue;
        reasons.push({
          // Said from the rows rather than from the stored sentence, so it names
          // the lines even on a batch processed before the message did.
          label:
            offending.length === 1
              ? `Line ${offending[0].rowNumber}: the amount does not equal quantity × unit price`
              : `${offending.length} lines do not add up: ${offending.map((row) => row.rowNumber).join(', ')}`,
          finding: true,
          lineRow: offending[0].rowNumber,
          lineRows: offending.map((row) => row.rowNumber),
          pageNumber: offending[0].pageNumber,
          evidenceLineIndexes: offending[0].evidenceLineIndexes,
        });
        continue;
      }
      // "1 fields and 0 line items could not be aligned to faithful OCR evidence."
      // Engine vocabulary, a count instead of a name, and nowhere to go — the
      // operator's verdict was "that one just seems to do nothing and I don't
      // know what it even is". It means something worth saying: a value was
      // produced that cannot be found anywhere on the page, so it may not be
      // what the document says. Said that way, per field, and it travels.
      if (finding.code === FINDING.ungroundedExtraction) {
        const loose = item.fields.filter(valueIsUngrounded);
        if (loose.length === 0) continue;
        for (const field of loose) {
          const configured = schema.fields.find(
            (entry) =>
              entry.name.localeCompare(field.name, undefined, { sensitivity: 'accent' }) === 0,
          );
          if (
            reasons.some(
              (reason) => reason.fieldName?.toLocaleLowerCase() === field.name.toLocaleLowerCase(),
            )
          )
            continue;
          reasons.push({
            label: `${field.name} is not printed anywhere on this page · check it`,
            fieldId: configured?.id,
            fieldName: field.name,
            ...(field.evidencePageNumber ? { pageNumber: field.evidencePageNumber } : {}),
          });
        }
        continue;
      }
      // The individual fields already appear above with a direct place to edit.
      // Repeating the engine's broad alias diagnosis created a second, jargon-
      // heavy task that went nowhere, and it survived after the fields were
      // confirmed. One question per value is both clearer and finishable.
      if (finding.code === FINDING.semanticAliasConflict) continue;
      // A value set aside is said on its field (SET_ASIDE_REASON): one thing to check there,
      // not a second, placeless one beside it ("3 to check" over one marked field, owner,
      // 2026-10-14). With none of its fields in hand the finding stays whole.
      if (finding.code === FINDING.misplacedValue) {
        const setAside = item.fields.filter(isWithheldField);
        if (setAside.length > 0) {
          for (const field of setAside) {
            if (
              reasons.some(
                (reason) =>
                  reason.fieldName?.toLocaleLowerCase() === field.name.toLocaleLowerCase(),
              )
            )
              continue;
            reasons.push({
              label: `${field.name} is empty`,
              findingCode: finding.code,
              fieldName: field.name,
              fieldId: schema.fields.find(
                (entry) =>
                  entry.name.localeCompare(field.name, undefined, { sensitivity: 'accent' }) === 0,
              )?.id,
              ...(field.evidencePageNumber ? { pageNumber: field.evidencePageNumber } : {}),
            });
          }
          continue;
        }
      }
      reasons.push({
        label: findingWords(finding.message),
        finding: true,
        findingCode: finding.code,
      }); // PAIGE: Paige's words (finding-words.ts)
    }

    // The engine's own reasons, when it named them: the cues below attach to them
    // (review-wait-reasons.ts). Null for a document saved before it did.
    const waits = engineWaitReasons(item);
    // The table holds no finding names, said on the table in the waiting line's words.
    for (const label of tableWaitSentences(waits, WAIT_REASON_WORDS))
      reasons.push({ label, table: true });
    if (item.needsReview) {
      // Only ever an explanation of a flag the engine actually raised. Sitting
      // above this gate it fired on finished documents too — and since the detector
      // opens a document at 0.75 and ordinarily scores 0.82 to 0.88, that meant
      // every automatically separated document carried a permanent review dot no
      // operator action could clear.
      //
      // When the engine named its reasons, it asks exactly when it holds for the
      // start: beside an unread page it holds whatever the threshold or the
      // document's place, and the screen's own threshold test missed those.
      const opening = item.pages[0];
      const startHeld = engineHoldsAt(waits, 'start');
      // A file kept whole starts where it starts; its doubt is the split suggestion's question instead (ADR-0091).
      const askStart =
        (startHeld ??
          (job.reviewPolicy?.boundaryEnabled !== false &&
            documentIndex > 0 &&
            (opening?.boundaryConfidence ?? 1) < (job.reviewPolicy?.boundary ?? 0.9))) &&
        !sourceSplitSuggestion(item);
      // A type cue counts only when the engine holds the document for its type;
      // otherwise it is bare: no cue on the type control, not a thing to check.
      const typeHeld = engineHoldsAt(waits, 'type');
      const typeReason = (label: string): ReviewReason =>
        typeHeld === false
          ? { label, documentType: true, bare: true }
          : { label, documentType: true };
      // Not sure of the type: the engine's reason, or for a document saved before the
      // engine named reasons, the same test it applies (ReviewState.Settle): the type
      // threshold is on, the type is not from a layout, and its confidence is under it.
      const typeUnsure = waits
        ? waits.includes('type-confidence')
        : job.reviewPolicy?.documentTypeEnabled !== false &&
          !(item.documentTypeSource ?? '').startsWith('layout') &&
          (item.documentTypeConfidence ?? 1) <
            (job.reviewPolicy?.documentType ?? REVIEW_POLICY_DEFAULTS.documentType);
      if (askStart)
        reasons.push({
          label:
            item.startQuestionedPage != null && item.startQuestionedPage === opening?.number
              ? 'Not certain this document starts here · Its first page reads like a page you joined to the document before'
              : `Not certain this document starts here${opening?.boundaryReasons?.length ? ` · ${opening.boundaryReasons[0]}` : ''}`,
          boundary: true,
          pageNumber: opening?.number,
        });
      // Every branch below asks the operator to confirm or change the type, so
      // every one of them is answered by having done it. None used to check,
      // and documentTypeSource keeps saying TYPE_FROM_CONFIRMED_EXAMPLES after a
      // confirmation — so a document read "Expense Report · Confirmed" in the
      // heading while the checklist beside it still asked for the type, for
      // ever. Confirming appeared to be ignored, which is corrosive in the one
      // place the product asks an operator to keep doing something.
      // Without engine reasons a boundary question stood in for the type's, unless
      // the type is unsure too; with them, each is asked when the engine holds for it.
      if (
        (waits || !askStart || typeUnsure) &&
        !item.documentTypeVerified &&
        item.documentTypeTrust !== TYPE_TRUST_AUTHORIZED_LEARNED
      ) {
        if (item.documentTypeSource === TYPE_FROM_BOUNDARY_REEVALUATION)
          reasons.push(typeReason('Re-read after a boundary change · check the type and values'));
        // A removed type's document read again against the remaining types, which found it one; no boundary changed.
        else if (item.documentTypeSource === TYPE_FROM_REMOVED_TYPE_REREAD)
          reasons.push(
            typeReason('Read again after its type was removed · check the type and values'),
          );
        else if (item.documentTypeSource === TYPE_FROM_CONFIRMED_EXAMPLES)
          // Names the type and says where the guess came from, in that order.
          // "Type matched from 9 confirmed examples" described the machinery and
          // not the answer — it never said WHICH type, so on its own it meant
          // nothing, and "confirmed examples" is our word for it, not the
          // operator's. They confirmed documents.
          reasons.push(
            typeReason(
              `Looks like ${withArticle(item.documentType)} - matches ${counted(item.documentTypeExampleSupport ?? 0, 'document')} you confirmed · check the type`,
            ),
          );
        else if (item.documentTypeSource === TYPE_FROM_OPERATOR_SPLIT)
          reasons.push(typeReason('New document from your split · check its type'));
        else if (item.documentTypeSource === TYPE_FROM_REMOVED_TYPE)
          reasons.push(typeReason('Its type was removed from this job · choose a type'));
        else if (item.documentTypeSource === TYPE_FROM_REMOVED_TYPE_ANSWERS_KEPT)
          reasons.push(
            typeReason(
              "Its type was removed and its confirmed values don't fit the type it was read as · choose a type",
            ),
          );
        // Held because it is not sure of the type (its type threshold,
        // ReviewState.Settle): the type control is marked. Bare here left an
        // unmarked control on a document held for its type (AP Test 2, 2026-10-13:
        // 25 of 40 held documents). The waiting line above the fields names only the
        // engine's first reason, so this says it at the control, naming the type
        // rather than repeating that line's words.
        else if (typeUnsure)
          reasons.push({
            label: item.documentType
              ? `Not sure this is ${withArticle(item.documentType)}`
              : WAIT_REASON_WORDS['type-confidence'],
            documentType: true,
          });
        else
          // `bare` when the engine did not hold it for the type: "not confirmed
          // yet" is true of every unconfirmed document, and the one confirm
          // (2026-10-05) confirms the type with it, so the control stays quiet.
          reasons.push({
            label: 'Document type not confirmed yet',
            documentType: true,
            bare: true,
          });
      }
      // No bare "Flagged for review" fallback. needsReview is a claim recorded
      // when the document was processed, and the branches above cover every
      // reason the engine raises it for. Reaching here means the flag is stale —
      // the operator has already dealt with whatever caused it — and inventing an
      // item with no name, no cause and nowhere to go leaves them hunting for a
      // fault that is not there, and blocks the document from ever being
      // confirmed. Silence is the honest answer.
    }
    // In the engine's order (the waiting line says its first). Without engine
    // reasons the type leads: which fields a document is supposed to have
    // depends on it, so working any other item first risks doing it twice.
    return orderByWaitReasons(reasons, waits, reviewReasonCue);
  };
  // Both dots now answer the same question about the same document.
  const currentReviewReasons = documentReviewReasons(document, selectedDocument);
  // The things to check: "N to check", F6 and the detached panel's count. A bare
  // cue marks nothing, so it is never one (review-wait-reasons.ts).
  const currentReviewWork = reasonsToCheck(currentReviewReasons);
  // "N to check" on the Fields heading: only while the document is held.
  const summaryToCheck =
    document.needsReview && !document.confirmedAsWhole ? currentReviewWork.length : 0;
  // Place the existing questions beside their answer controls. These lists do
  // not add/remove review reasons or change any readiness/confirmation gate.
  const isTypeReviewReason = (reason: ReviewReason) =>
    Boolean(reason.documentType || reason.findingCode === FINDING.pageRotationGuessed);
  const isTableReviewReason = (reason: ReviewReason) =>
    Boolean(
      reason.lineRow ||
      reason.table ||
      (!reason.fieldId &&
        reason.fieldName &&
        configuredFields.some(
          (field) => field.kind === 'line-items' && field.name === reason.fieldName,
        )) ||
      reason.findingCode === 'line_item_processing_failed' ||
      reason.findingCode === 'line_item_read_needs_review' ||
      reason.findingCode === 'pages_not_read_for_line_items',
    );
  const misplacedFields = configuredFields.filter(
    (field) =>
      field.kind !== 'line-items' &&
      document.fields.some(
        (value) =>
          value.name.localeCompare(field.name, undefined, { sensitivity: 'accent' }) === 0 &&
          value.source === MISPLACED_VALUE_SOURCE &&
          !value.value.trim() &&
          !value.verified,
      ),
  );
  const typeReviewReasons = currentReviewReasons.filter(
    (reason) => isTypeReviewReason(reason) && !reason.bare,
  );
  // "Turn it": the page the guessed-direction finding names (the first still waiting). A finding
  // stored before findings named their pages means the page on screen.
  const guessedTurnFinding = typeReviewReasons.some(
    (reason) => reason.findingCode === FINDING.pageRotationGuessed,
  )
    ? document.validationFindings.find(
        (finding) =>
          finding.code === FINDING.pageRotationGuessed && findingStillHolds(finding, document),
      )
    : undefined;
  const guessedTurnPage = !guessedTurnFinding
    ? null
    : (guessedTurnFinding.pageNumbers?.find((number) =>
        document.pages.some((page) => page.number === number),
      ) ??
      document.pages[selectedPage]?.number ??
      null);
  // Turn page: how far the page on screen is drawn turned while the engine saves the turn, and
  // whether it is still being read again (a quiet mark on the page, not a spinner).
  const viewerTurnEntry = turnSlotOnScreen ? pageTurns.get(turnSlotOnScreen) : undefined;
  const viewerTurn = pageTurnTransform(
    pageTurnShown(viewerTurnEntry, previewImagePath),
    previewSize.width,
    previewSize.height,
  );
  viewerTurnRef.current = viewerTurn ? ` ${viewerTurn}` : '';
  const viewerRereading = pageTurnBusy(viewerTurnEntry);
  turnPageShortcutRef.current = () => {
    const page = document.pages[selectedPage];
    if (page && !detachedPanels.viewer && !sourceReview && !doneStage)
      void turnPage(document.id, page.number);
  };
  const tableReviewReasons = currentReviewReasons.filter(
    (reason) => isTableReviewReason(reason) && !reason.lineRow,
  );
  const otherReviewReasons = currentReviewReasons.filter(
    (reason) =>
      !reason.boundary &&
      !reason.fieldId &&
      !isTypeReviewReason(reason) &&
      !(expectsLineItems && isTableReviewReason(reason)) &&
      !(reason.findingCode === FINDING.misplacedValue && misplacedFields.length > 0) &&
      // These already have an explicit visible retry control with their full cause.
      !(
        notReadReason &&
        [FINDING.pageNotRead, FINDING.modelUnreachable, FINDING.modelPartiallyUnreachable].some(
          (code) => code === reason.findingCode,
        )
      ) &&
      !(extractionFailure && reason.findingCode === 'semantic_processing_failed'),
  );
  // The reader thinks the table may be missing rows. A reader finding is a note
  // on the document, not a hold (ReviewState.Settle; e556f472 stopped it asking
  // for review), so it is said on the table and is never a thing to check.
  const tableNotes = document.validationFindings.filter(
    (finding) =>
      finding.code === FINDING.lineItemsPossiblyIncomplete && findingStillHolds(finding, document),
  );
  // Why the table needs a look, always shown, as a field says it (owner, 2026-10-14). Beside
  // the rows the arithmetic chip already says a total they miss or a line that differs from
  // the page (tableHoldWords), so those are said there once; with the rows in another window,
  // here.
  const tableReviewDetail = (besideChip: boolean) => {
    const reasons =
      besideChip && document.lineItems.length > 0
        ? tableReviewReasons.filter((reason) => !reason.table)
        : tableReviewReasons;
    return (
      !awaitingUnderstanding(document) &&
      (reasons.length > 0 || tableNotes.length > 0) && (
        <div className="table-context-issues">
          {reasons.map((reason, index) => (
            <p className="review-reason-line" key={`table-issue-${index}`}>
              {reason.label}
            </p>
          ))}
          {reasons.length > 0 && detachedPanels['line-items'] && (
            <button
              type="button"
              className="review-reason-action"
              onClick={() => detachPanel('line-items')}
            >
              Show line items
            </button>
          )}
          {tableNotes.map((finding, index) => (
            <div className="document-audit-note" key={`table-note-${index}`}>
              <Info size={13} />
              <span>{possiblyIncompleteTableNote(finding.message)}</span>
            </div>
          ))}
        </div>
      )
    );
  };
  const pageAuditNotes = document.validationFindings.filter(
    (finding) =>
      finding.code === FINDING.pageRotationApplied && findingStillHolds(finding, document),
  );
  const teachingDocumentType = documentSchema?.name ?? document.documentType ?? 'this type';
  const teachingTypeProgress = learningAccelerationUnavailable
    ? undefined
    : learningAcceleration?.documentTypes.find(
        (item) =>
          item.documentType.localeCompare(teachingDocumentType, undefined, {
            sensitivity: 'accent',
          }) === 0,
      );
  const processorTeachingActive = captureQueue.some(
    (item) => item.waveMode === 'processor-paced' && item.status === 'pending',
  );
  // Show completion only after the native receipt lands. The final explicit
  // visible confirmation requests that receipt; an optimistic tick is not it.
  const documentFullyConfirmed = Boolean(document?.confirmedAsWhole);
  /**
   * A document the engine has not reached yet is not one it failed to classify.
   *
   * Documents stream into the spine as boundaries are found, and understanding
   * runs per document afterwards, so for a while a real document legitimately has
   * no type. It rendered as "Unclassified Document" beside a red review dot —
   * indistinguishable from a genuine failure, and a batch mid-run read as "0 ready,
   * 14 review", which is alarming and untrue.
   */
  const stillReading = Boolean(captureProcessingId);
  const awaitingUnderstanding = (document: DocumentResult) =>
    isAwaitingUnderstanding(document, stillReading);
  const toCheckItems =
    summaryToCheck > 0 && !awaitingUnderstanding(document)
      ? currentReviewWork.map((reason) => ({
          place: reviewItemPlace(reason),
          reason: reason.label,
        }))
      : [];
  // The same flag the dot and the queue read. Counting from the checklist
  // instead put a third answer on the screen: a tally that could disagree with
  // the dots it was tallying, and only for documents somebody had opened.
  // Counted once per batch in documentCensus, above the early return.
  const reviewDocuments = documentCensus.reviewIndexes.length;
  const teachDocuments = documentCensus.teachIndexes.length;
  // The same set the count promises. The count subtracts documents still being
  // read; this used to include them, so the filter offered "1 needing review" and
  // then showed two. A document the engine has not finished reading is not one an
  // operator can review.
  /* The queue is keyed on what is written down, not on what happens to be
     loaded.

     It filtered on the live reason list, which for a document nobody has opened
     falls back to a stored flag and for an opened one is recomputed from the
     document itself. Those two answers can differ, and loading is exactly what
     clicking a thumbnail does — so a document went red, was clicked, and left the
     queue while the operator watched, as though opening it had approved it.

     One source now. needsReview is reconciled for every document when the store
     opens and updated whenever a correction is saved, so a document leaves this
     list when its state changes and not when somebody looks at it. The checklist
     still computes its reasons live, because naming what is wrong needs the
     document in hand — but it no longer decides who is in the queue. */
  telemetryCapture.current = {
    capture: 'documents',
    documents: calibrating ? 'calibration' : 'review',
  };
  /* The next document asked about (review work, and while calibrating teaching examples too), wrapping
     to the top of the list. Wrapping matters: an operator who works from the middle down would otherwise
     be told there is nothing left while eleven documents above them still want an answer. */
  const nextRequest = nextAsked(
    batch.documents,
    selectedDocument,
    calibrating,
    (item) =>
      !awaitingUnderstanding(item) &&
      !(assistedReview && item.needsReview && awaitsAiReviewer(item)),
  );
  const nextReviewIndex = nextRequest.index;
  // PAIGE: the tour's Export step on Paige glows on its export panel, where the desktop shows its export card
  // (`desktop-export`): the sample's exported count (paige/TourExportScene.tsx), their job's destinations.
  const hostExportPanel = (
    <HostExportPanel key={job.id} jobId={job.id} documents={batch.documents} />
  );
  /** How many documents are asked about, the open one included. */
  const askedDocuments = nextRequest.count;
  /* Not gated on the spine's review filter. The doorway is the answer to "I
     finished this one, where next?", and that question exists whenever red work
     remains — tying it to how the LEFT rail happened to be filtered meant an
     operator in show-everything mode confirmed a document, went green, and got
     silence. */
  const nextReviewDocument =
    nextReviewIndex != null
      ? { index: nextReviewIndex, item: batch.documents[nextReviewIndex] }
      : null;
  /* Paige: "next" is answered by the server (ADR-0009). The candidates are the documents asked about,
     after the open one and then wrapping, as nextAsked picks; while calibrating the teaching
     recommendation reorders them (next-arrival.ts), and Paige hands back the first one no colleague
     holds. With no queue (setup, one reviewer offline) the first candidate is the answer. */
  const askedAvailable = (item: (typeof batch.documents)[number]) =>
    askedWork(item, calibrating) !== null &&
    !awaitingUnderstanding(item) &&
    !(assistedReview && item.needsReview && awaitsAiReviewer(item));
  const advanceToNext = (onArrive?: (index: number) => void, stillWanted?: () => boolean) => {
    // The local pick leads the candidates (CPv6 a0802c11).
    const localPick = nextReviewDocument?.item.id;
    // CapturePoint moves at once; Paige's answer comes back from the server, so it moves only if the
    // person is still where they asked from and the confirm behind it did not fail meanwhile.
    const askedFrom = selectedDocumentRef.current;
    void (async () => {
      const asked = batch.documents.flatMap((item, index) => (askedAvailable(item) ? [index] : []));
      const after = asked.filter((index) => index > selectedDocument);
      const before = asked.filter((index) => index < selectedDocument);
      const ordered = [...after, ...before].map((index) => batch.documents[index]?.id ?? '');
      const candidates = nextCandidates(ordered, calibrating ? teachingOrder : [], localPick);
      if (candidates.length === 0) return;
      try {
        const target = await reviewLease.next(candidates);
        if (!nextStillArrives(selectedDocumentRef.current, askedFrom, stillWanted)) return;
        const index = target
          ? (batchRef.current ?? batch).documents.findIndex((item) => item.id === target)
          : -1;
        if (index >= 0) {
          selectDocument(index, 0, false, true);
          onArrive?.(index);
          const passed = passedOverNotice(
            candidates,
            target ?? '',
            (id) => reviewLease.held.get(id)?.name,
          );
          if (passed) onNotice(passed);
        } else onNotice('Every document still to review is with a colleague.');
      } catch (reason) {
        onNotice(messageFor(reason, 'Paige could not pick the next document.'));
      }
    })();
  };
  /* ONE main button and one rule (single confirm, owner and Dustin, 2026-10-05). It confirms the
     whole document, then opens the next document asked about, wrapping (one list since 2026-10-30).
     Its words follow the document: Confirm & next, Next on one already confirmed, Confirm on the last
     one asked about (review-main-action.ts). The button, Ctrl+Enter and the detached panel all run this. */
  const documentDrafted = hasFieldDrafts || hasLineDrafts;
  const mainNextIndex = nextReviewIndex;
  const mainAction = mainReviewAction(documentFullyConfirmed && !documentDrafted, mainNextIndex);
  const beatHere = confirmBeat === document.id;
  // Its values aren't in yet: it can't be confirmed, though Next (which only moves) can be used.
  const mainWaitsForReading =
    awaitingUnderstanding(document) || !loadedDocumentIds.has(document.id);
  /** Why the button can't confirm this document yet, in one plain line, with where to look. */
  const mainRefusal = () => {
    const missing = configuredFields.find(
      (configured) =>
        configured.required &&
        (configured.kind === 'line-items'
          ? document.lineItems.length === 0
          : !document.fields.some(
              (field) => field.name === configured.name && Boolean(field.value.trim()),
            )),
    );
    return confirmRefusal({
      rows: document.lineItems,
      missingRequired: missing
        ? missing.kind === 'line-items'
          ? 'the line items'
          : missing.name
        : undefined,
      notRead: Boolean(document.notReadReason || document.pages.some((page) => page.notReadReason)),
      typeChosen: Boolean(
        document.documentTypeVerified || (documentSchema && filedType?.id === documentSchema.id),
      ),
      typeRenamed: Boolean(
        documentSchema && !document.documentTypeVerified && filedType?.id !== documentSchema.id,
      ),
    });
  };
  /** The line the button is waiting on: shown, highlighted and flashed, here or in its own window. */
  const showRefusedLine = (rowNumber: number) => {
    setArrivedFieldId(null);
    setArrivedLineRow(rowNumber);
    if (!detachedPanels['line-items']) {
      setLineItemsMode('docked');
      if (paneLayout.narrow) paneLayout.revealPane('lines');
    }
    setRefusedLine((current) => ({ rowNumber, serial: (current?.serial ?? 0) + 1 }));
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const runMainAction = (
    source: 'button' | 'keyboard' | 'panel' | 'continue' = 'button',
    pressedAt = performance.now(),
  ) => {
    if (!document || mainActionBusy.current || allDone) return;
    // A press here is this window's own: no detached press is waiting any more.
    if (source === 'button' || source === 'keyboard') detachedPressRef.current = null;
    // Pressed the moment a document opened (or its reading changed under it), or clicked the moment
    // the bar moved on: not a look at it. A held key, a double press or a double-click must never
    // confirm a document nobody has seen (a double-click on Next used to land its second click on
    // the next document's Confirm & next). Measured from the press itself (the event's timeStamp):
    // on a busy machine the move's render can hold a double-click's second click for over a
    // second, and timed when it was handled it looked like a look.
    if (source !== 'continue' && pressedAt - arrivedAt.current < ARRIVAL_GUARD_MS) return;
    const action = mainAction;
    if (action.kind === 'confirmed') return;
    if (action.kind === 'next') {
      if (action.next != null) {
        // PAIGE: Paige picks the next one asked about that no colleague holds (ADR-0009).
        advanceToNext();
      }
      return;
    }
    // PAIGE: a colleague's document is read here, not confirmed; the click (or Ctrl+Enter) says so.
    if (reviewHeld) {
      onNotice(
        reviewLease.status === 'viewing'
          ? 'Read-only view: nothing was changed'
          : `${reviewLease.holder?.name ?? 'Another reviewer'} is reviewing this document, so it is open to read only`,
      );
      return;
    }
    // It opened because the one they were on left the list, and they have not moved since: say so once
    // instead of confirming a document they may not have looked at (B-09).
    if (!mayConfirmShown(document.id, movedUnder.current)) {
      movedUnder.current = null;
      onNotice(MOVED_UNDER_NOTICE, 'notice');
      return;
    }
    if (mainWaitsForReading) {
      reviewTally.refused(job.id, 'still-reading');
      onNotice('This document is still being read', 'confirm-still-reading');
      return;
    }
    // Typing still in a field or cell is saved first, then the document is confirmed: clicking
    // Confirm (or Ctrl+Enter) straight from a field used to do nothing, because the field's save
    // had already disabled the button under the click.
    const drafts = [...fieldDraftStore.getSnapshot(), ...lineDraftStore.getSnapshot()].filter(
      (draft) => draft.documentId === document.id,
    );
    if (drafts.length) {
      if (drafts.some((draft) => draft.state === 'refused')) {
        reviewTally.refused(job.id, 'other');
        onNotice(
          'Save or discard the unsaved values before confirming this document',
          'confirm-unsaved-values',
        );
        return;
      }
      for (const draft of fieldDraftStore.getSnapshot())
        if (draft.documentId === document.id && draft.state === 'editing')
          fieldDraftStore.submit(draft);
      for (const draft of lineDraftStore.getSnapshot())
        if (draft.documentId === document.id && draft.state === 'editing')
          lineDraftStore.submit(draft);
      confirmAfterDraftsRef.current = document.id;
      return;
    }
    // A question still open on the screen is answered first, and the bar says which one: the
    // button never confirms over a line that doesn't add up ("Looks right" or "Use $X" answer it).
    const refusal = mainRefusal();
    if (refusal) {
      refusalShown.current = true;
      reviewTally.refused(job.id, refusal.code);
      onNotice(refusal.message, confirmRefusalMessageCode[refusal.code]);
      if (refusal.lineRow != null) showRefusedLine(refusal.lineRow);
      else if (refusal.field) {
        const reason = currentReviewReasons.find((item) => item.fieldName === refusal.field);
        if (reason) goToReviewItem(reason);
      }
      return;
    }
    const documentId = document.id;
    reviewTally.confirmPressed(job.id, documentId);
    // The person's most recent change; Ctrl+Z goes Back only if it moves on and shows that offer.
    const change = lastChange.note();
    const nextId = action.next != null ? (batch.documents[action.next]?.id ?? null) : null;
    const run = Symbol(documentId);
    mainActionBusy.current = run;
    // A new confirmation replaces the last one's way back, and a refusal it has now answered.
    setBackOffer(null);
    if (refusalShown.current) {
      refusalShown.current = false;
      onNotice('');
    }
    // The move is optimistic, before the save lands. A save that fails brings the operator back
    // to the document that did not save, unless he has since moved on by himself; the failure
    // otherwise showed on the next document and the next Confirm sealed that one.
    let failed = false;
    let movedTo: string | null = null;
    void (async () => {
      try {
        const started = await confirmDocument((failedId) => {
          failed = true;
          // It can be confirmed again at once: the failed attempt no longer holds the button.
          if (mainActionBusy.current === run) mainActionBusy.current = null;
          // It did not save: no Done, no way back to it, and it isn't counted as confirmed.
          setConfirmBeat((current) => (current === failedId ? null : current));
          setConfirmedInVisit((current) => {
            const next = new Set(current);
            next.delete(failedId);
            return next;
          });
          setBackOffer((current) => (current?.documentId === failedId ? null : current));
          if (!movedTo || batchRef.current?.documents[selectedDocumentRef.current]?.id !== movedTo)
            return;
          const back = (batchRef.current ?? batch).documents.findIndex(
            (item) => item.id === failedId,
          );
          if (back >= 0) selectDocument(back, 0, false, true);
        });
        if (!started || failed || !mounted.current) return;
        setConfirmedInVisit((current) => new Set(current).add(documentId));
        // "Done ✓" on the page for half a second: finishing is a change worth showing.
        setConfirmBeat(documentId);
        // Its half second starts once it is on screen: started here, a busy machine's
        // render ate most of it and Done showed for a tenth of a second. Two frames: the one that draws
        // it, then the next. A window that draws no frames (hidden) starts it after a second anyway.
        await new Promise<void>((resolve) => {
          const fallback = window.setTimeout(resolve, 1000);
          window.requestAnimationFrame(() =>
            window.requestAnimationFrame(() => {
              window.clearTimeout(fallback);
              resolve();
            }),
          );
        });
        await new Promise((resolve) => window.setTimeout(resolve, CONFIRM_BEAT_MS));
        if (!failed) setConfirmBeat((current) => (current === documentId ? null : current));
        if (failed || !mounted.current) return;
        const live = batchRef.current;
        // Moved on by themselves during the beat: stay where they went.
        if (!live || live.documents[selectedDocumentRef.current]?.id !== documentId) return;
        // Typing begun on it during the beat stays with it: moving would save the half-typed value.
        if (
          [...fieldDraftStore.getSnapshot(), ...lineDraftStore.getSnapshot()].some(
            (draft) => draft.documentId === documentId,
          )
        )
          return;
        // The next one, worked out again: learning may have cleared it during the beat.
        const at = live.documents.findIndex((item) => item.id === documentId);
        const nextIndex =
          !nextId || at < 0
            ? -1
            : (nextAsked(
                live.documents,
                at,
                calibrating,
                (item) =>
                  !awaitingUnderstanding(item) &&
                  !(assistedReview && item.needsReview && awaitsAiReviewer(item)),
              ).index ?? -1);
        const movedId =
          nextIndex >= 0 && live.documents[nextIndex].id !== documentId
            ? live.documents[nextIndex].id
            : null;
        if (movedId) {
          // PAIGE: Paige hands back the next document asked about that no colleague holds (ADR-0009).
          advanceToNext(
            (index) => {
              const arrivedId = (batchRef.current ?? batch).documents[index]?.id ?? null;
              movedTo = arrivedId;
              if (!arrivedId) return;
              const offer = { documentId, shownOn: arrivedId };
              setBackOffer(offer);
              lastChange.settle(change, { kind: 'back', offer });
            },
            () => !failed,
          );
        } else setAllDoneArmed(documentId);
      } finally {
        if (mainActionBusy.current === run) mainActionBusy.current = null;
      }
    })();
  };
  /** "✓ Confirmed · Back": the document just confirmed, opened again (it stays in the list until left). */
  goBackToConfirmedRef.current = () => goBackToConfirmed();
  const goBackToConfirmed = () => {
    const offer = backOffer;
    setBackOffer(null);
    if (!offer) return;
    lastChange.clear(); // an undo: nothing is left for Ctrl+Z
    const index = batch.documents.findIndex((item) => item.id === offer.documentId);
    if (index < 0) return;
    reviewTally.back(job.id);
    settledByHand.current = offer.documentId;
    selectDocument(index, 0, false, true);
  };
  /** Needs review and Teach: on to the next one without confirming this one. */
  const skipDocument = () => {
    if (!nextReviewDocument) return;
    reviewTally.skipped(job.id);
    // PAIGE: Paige picks the next one no colleague holds (ADR-0009), after the open document.
    advanceToNext();
  };
  confirmShortcutRef.current = (pressedAt?: number) => runMainAction('keyboard', pressedAt);
  skipShortcutRef.current = () => {
    if (!nextReviewDocument) return false;
    skipDocument();
    return true;
  };
  runMainActionRef.current = () => runMainAction('continue');
  confirmAndAdvanceRef.current = () => runMainAction('panel');
  if (confirmAfterDraftsRef.current) {
    const waiting = confirmAfterDraftsRef.current;
    const drafts = [...fieldDraftStore.getSnapshot(), ...lineDraftStore.getSnapshot()].filter(
      (draft) => draft.documentId === waiting,
    );
    // Confirm submitted every draft it waits for, so one still being typed was begun after it: the
    // operator is correcting again, and the waiting Confirm is called off rather than sealing the
    // document once that value saves.
    if (
      waiting !== document.id ||
      drafts.some((draft) => draft.state === 'refused' || draft.state === 'editing')
    ) {
      confirmAfterDraftsRef.current = null;
      detachedPressRef.current = null;
    } else if (!drafts.length && !fieldSaving && !lineSaving) {
      confirmAfterDraftsRef.current = null;
      const press =
        detachedPressRef.current?.documentId === waiting ? detachedPressRef.current : null;
      detachedPressRef.current = null;
      queueMicrotask(() => {
        // A detached press confirms only what its panel showed plus this typing: anything else that
        // landed while it saved makes it a stale press, refused as one arriving stale is.
        const live = batchRef.current?.documents.find((item) => item.id === waiting);
        if (
          press &&
          (!live ||
            !ownTypingRevisions.reaches(
              waiting,
              press.documentMutationToken,
              reviewDocumentMutationToken(live),
            ))
        ) {
          onNotice(
            'That detached panel was showing an older document. It has been refreshed; try again.',
            'detached-change-failed',
          );
          reviewChannelRef.current?.postMessage({
            type: 'state',
            snapshot: reviewSnapshotRef.current,
          } satisfies ReviewWorkspaceMessage);
          return;
        }
        runMainActionRef.current();
      });
    }
  }
  // Every document, always (owner and Dustin, 2026-10-30): one with nothing left to do wears its check.
  const visibleDocumentIndexes = batch.documents.map((_, index) => index);
  // The last document asked about, confirmed where it is: the done scene, once nothing else is asked
  // about. Choosing any document opens it again.
  const allDone =
    allDoneArmed === document.id &&
    documentFullyConfirmed &&
    askedDocuments === 0 &&
    !detachedPanels.viewer;
  const doneStage = allDone;
  searchShortcutRef.current = () => setBatchSearchOpen(true);
  reviewItemShortcutRef.current = (direction) => {
    if (doneStage || currentReviewWork.length === 0) return;
    const cursor = reviewItemCursor.current;
    const at =
      cursor?.documentId === document.id
        ? cursor.index
        : direction > 0
          ? -1
          : currentReviewWork.length;
    const index = (at + direction + currentReviewWork.length) % currentReviewWork.length;
    reviewItemCursor.current = { documentId: document.id, index };
    goToReviewItem(currentReviewWork[index]);
  };
  /** "Undo change to <field>": offered on the document whose value was changed, while that field is still as the save left it. */
  const valueUndoOffered = Boolean(
    valueUndo &&
    valueUndo.jobId === job.id &&
    valueUndo.batchId === batch.batchId &&
    valueUndo.documentId === document.id &&
    !document.confirmedAsWhole &&
    JSON.stringify(document.fields.find((field) => field.name === valueUndo.fieldName) ?? null) ===
      JSON.stringify(valueUndo.field),
  );
  /**
   * Take the last value change back: the engine puts the field back exactly as it was (who read it,
   * its confidence, confirmed or not) and removes the correction, so it is not learned from. When the
   * engine refuses (the document changed, was confirmed or exported, or learning has used the value)
   * nothing changes; a click on the menu is told why, Ctrl+Z does nothing.
   */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const undoValueEdit = async (fromKey = false) => {
    const entry = valueUndo;
    const desktop = window.capturePointDesktop;
    if (!entry || !valueUndoOffered || !desktop?.undoFieldEdit) return;
    if (
      fieldSavingRef.current ||
      wholeBatchMutationRef.current ||
      pendingLineSaves.current > 0 ||
      schemaRefreshBusyRef.current ||
      pageEditBusyRef.current ||
      confirmingDocumentsRef.current.has(entry.documentId) ||
      structuralMutationBlocks(entry.documentId) ||
      !openFieldSaveLane(entry.documentId)
    ) {
      if (!fromKey)
        onNotice(
          pageTurnOwnsStructure()
            ? 'Finishing the page turn · try Undo again in a moment'
            : 'Finish saving the current change before undoing this one',
          'field-save-busy',
        );
      return;
    }
    setValueUndo(null);
    lastChange.clear(); // an undo: nothing is left for Ctrl+Z
    const stillHere = () =>
      mounted.current &&
      liveJobRef.current.id === entry.jobId &&
      batchRef.current?.batchId === entry.batchId;
    try {
      const restored = await desktop.undoFieldEdit({
        jobId: entry.jobId,
        batchId: entry.batchId,
        documentId: entry.documentId,
        fieldName: entry.fieldName,
        expectedField: entry.field,
      });
      if (!stillHere()) return;
      cacheAuthoritativeProjectionSkeleton(restored);
      const live = batchRef.current!;
      const next = {
        ...live,
        documents: live.documents.map((item) => (item.id === restored.id ? restored : item)),
      };
      batchRef.current = next;
      setBatch(next);
      onRestored(next);
      invalidateBoundaryUndoFor(restored.id);
      refreshLearningProgress();
    } catch {
      if (!fromKey && stillHere())
        onNotice(
          'This document changed after the value was saved, so this Undo is no longer available',
          'field-save-failed',
        );
    } finally {
      closeFieldSaveLane();
    }
  };
  undoShortcutRef.current = (fromTextBox = false) => {
    // Only the most recent change, and only while its own undo shows: never an older one.
    const offered = (undo: UndoableChange) =>
      undo.kind === 'back'
        ? Boolean(backOffer === undo.offer && backOffer.shownOn === document.id && !doneStage)
        : undo.kind === 'boundary'
          ? boundaryUndoTarget()?.changeId === undo.changeId
          : undo.kind === 'value'
            ? valueUndoOffered && valueUndo === undo.entry
            : undo.kind === 'delete'
              ? workspaceNotice.action === undo.entry
              : pageUndoStack.current[pageUndoStack.current.length - 1] === undo.entry;
    const undo = lastChange.undoable(offered);
    // From a text box only a value is taken back: a split, a removed page, a delete and Back stay for Ctrl+Z outside it.
    if (fromTextBox && undo?.kind !== 'value') return false;
    if (undo?.kind === 'delete') undo.entry.run();
    else if (undo?.kind === 'value') void undoValueEdit(true);
    else if (undo?.kind === 'back') goBackToConfirmed();
    else if (undo?.kind === 'boundary') undoBoundaryCorrection();
    else if (undo?.kind === 'page') void undoPageRemoval();
    return undo !== null;
  };
  // No document is open as work on a done stage: time there is not time stuck on the last one. All caught up after
  // confirming here is the queue cleared (not a job that was already clear), once per job.
  if (doneStage) reviewTally.shown(job.id, null);
  if (doneStage && reviewDocuments === 0 && confirmedInVisit.size > 0) reviewTally.caughtUp(job.id);
  // The title row counts the review work still left once Calibration is off, held steady around
  // confirmations and ticking down a step at a time (reviewSteady, above the early return). It replays
  // its small tick once per move: down when it starts down, up only for a rise that settled after a
  // confirmation (a document pulled back).
  const reviewCount = reviewSteady.shown;
  const tabTick = (steady: typeof reviewSteady) => {
    const tick = steady.descents + steady.roseSerial;
    return {
      tick,
      className:
        tick > 0
          ? steady.lastMove === 'up'
            ? 'count-ticked count-ticked-up'
            : 'count-ticked'
          : undefined,
    };
  };
  const { tick: reviewCountTick, className: reviewCountTickClass } = tabTick(reviewSteady);
  // Sent back for another look is said only when the Needs review number really rose once things
  // settled, never for a pull-back the same pass more than made up for (owner, 2026-10-10).
  const backNewsShown =
    backNews && reviewSteady.roseAt >= backNewsArrivedAt.current - 10_000 ? backNews : null;
  // A gap represents the physical boundary only when both neighbours are in
  // the displayed sequence. Review may hide a document; virtualization merely
  // retires its DOM, so derive this before taking the viewport slice.
  const joinableSpineDocumentIndexes = new Set(
    visibleDocumentIndexes.filter(
      (index, position) => position > 0 && visibleDocumentIndexes[position - 1] === index - 1,
    ),
  );
  /* The documents around this one (owner and Dustin, 2026-10-30): the page before above page 1 and the next
     document's first page below the last, each with its join, the list's own paperclip. Not in a viewer moved to
     its own window (it has its own list), on the done scene, or while pages are still arriving. */
  const neighboursShown =
    !doneStage && !detachedPanels.viewer && !batch.batchId.startsWith('live:');
  const neighbour =
    (
      item: (typeof batch.documents)[number],
      page: (typeof item.pages)[number],
      side: 'before' | 'after',
      documentNumber: number,
      onJoin: () => void,
      pending: ReturnType<typeof boundaryPendingByPage.get>,
    ) =>
    (width: string) => (
      <ReaderNeighbour
        side={side}
        width={width}
        joinTooltip={joinTooltip}
        onJoin={onJoin}
        pending={pending}
        joinLabel={`Merge document ${side === 'before' ? documentNumber + 1 : documentNumber} with previous`}
        label={`${side === 'before' ? 'Last page' : 'First page'} of ${
          awaitingUnderstanding(item)
            ? 'a document still being read'
            : (filedDocumentType(item, job.documentTypes, typeNameHistory)?.name ??
              (item.documentType?.trim() || 'a document'))
        }, document ${documentNumber}`}
        done={
          !awaitingUnderstanding(item) &&
          askedWork(item, calibrating) === null &&
          !exceptionsRouteOf(item)
        }
        toExceptions={
          !awaitingUnderstanding(item) &&
          askedWork(item, calibrating) === null &&
          Boolean(exceptionsRouteOf(item))
        }
        previewPath={pageTurnImage(
          pageTurns.get(pageTurnKey(item.id, page.number)),
          page.previewPath,
        )}
        pixelWidth={page.pixelWidth}
        pixelHeight={page.pixelHeight}
      />
    );
  const previousDocument =
    neighboursShown && selectedDocument > 0 ? batch.documents[selectedDocument - 1] : undefined;
  const previousPage = previousDocument?.pages[previousDocument.pages.length - 1];
  const readerLeading =
    previousDocument && previousPage
      ? neighbour(
          previousDocument,
          previousPage,
          'before',
          selectedDocument,
          () => mergeWithPrevious(selectedDocument),
          document.pages[0] ? boundaryPendingByPage.get(document.pages[0].number) : undefined,
        )
      : undefined;
  const nextDocument =
    neighboursShown && selectedDocument < batch.documents.length - 1
      ? batch.documents[selectedDocument + 1]
      : undefined;
  const nextPage = nextDocument?.pages[0];
  const readerTrailing =
    nextDocument && nextPage
      ? neighbour(
          nextDocument,
          nextPage,
          'after',
          selectedDocument + 2,
          () => mergeWithPrevious(selectedDocument + 1),
          boundaryPendingByPage.get(nextPage.number),
        )
      : undefined;
  const joinNewsHere = joinNews && joinNews.batchId === batch.batchId ? joinNews : null;
  const joinNewsLine = joinNewsHere && (
    <span className="reader-join-news" role="status">
      <Paperclip size={15} aria-hidden="true" />
      <span>
        Joined
        {joinNewsHere.added > 0 ? (
          <>
            {' '}
            · <b>{joinNewsHere.added} like it</b> to check
          </>
        ) : null}
      </span>
      <button type="button" data-telemetry="join-undo" onClick={undoJoinNews}>
        <Undo2 size={13} aria-hidden="true" />
        Undo
      </button>
    </span>
  );
  // The every-document layout, memoised on the batch above the early return.
  const spineMetrics = allSpineMetrics;
  const lastSpineMetric = spineMetrics[spineMetrics.length - 1];
  const spineOffset = lastSpineMetric ? lastSpineMetric.top + lastSpineMetric.height : 0;
  spineMetricsRef.current = spineMetrics;
  const spineBuffer = 440;
  // Display-only previews: never add unclassified placeholder documents to the
  // batch, review counts, exports, or learning. Completed pages own their usual UI.
  const displayedPagePaths = new Set(
    batch.documents.flatMap((item) => item.pages.map((page) => page.previewPath)),
  );
  const incomingPageEntries = [...incomingPages.entries()]
    .filter(([, page]) => !displayedPagePaths.has(page.previewPath))
    .sort(
      ([, left], [, right]) =>
        left.captureId.localeCompare(right.captureId) || left.pageNumber - right.pageNumber,
    );
  const incomingRowHeight = spinePageRow;
  const visibleIncomingPages = incomingPageEntries
    .map(([key, page], index) => ({ key, page, index }))
    .filter(
      ({ index }) =>
        spineOffset + (index + 1) * incomingRowHeight >= spineViewport.top - spineBuffer &&
        spineOffset + index * incomingRowHeight <=
          spineViewport.top + spineViewport.height + spineBuffer,
    );
  const virtualSpineEntries = spineMetrics.filter(
    (entry) =>
      entry.top + entry.height >= spineViewport.top - spineBuffer &&
      entry.top <= spineViewport.top + spineViewport.height + spineBuffer,
  );
  // On a tour, a mark where the first scissors go: the list draws only the pages near the screen,
  // so the Document starts step scrolls to it to reach them (TOUR_STEPS.starts.spots).
  const firstSplitEntry = touring
    ? spineMetrics.find((entry) => entry.item.pages.length > 1)
    : undefined;
  /** `arrival`: Ctrl+PgDn/PgUp, a move the operator did not watch the list for (selectDocument). */
  const moveDocument = (direction: number, arrival = false) => {
    const position = visibleDocumentIndexes.indexOf(selectedDocument);
    if (position < 0) {
      // The document is no longer in the spine — which is what CONFIRMING one
      // does while the spine is filtered to documents needing review. Carrying
      // on from where it used to sit is the whole job here: falling back to the
      // first or last entry sent the operator to the top of the batch after
      // every confirmation, which is worse than being stuck.
      const onward =
        direction > 0
          ? visibleDocumentIndexes.find((index) => index > selectedDocument)
          : [...visibleDocumentIndexes].reverse().find((index) => index < selectedDocument);
      const nearest =
        onward ??
        (direction > 0
          ? visibleDocumentIndexes[visibleDocumentIndexes.length - 1]
          : visibleDocumentIndexes[0]);
      if (nearest != null) selectDocument(nearest, 0, true, arrival);
      return;
    }
    const nextPosition = Math.min(
      visibleDocumentIndexes.length - 1,
      Math.max(0, position + direction),
    );
    const nextIndex = visibleDocumentIndexes[nextPosition];
    if (nextIndex != null) selectDocument(nextIndex, 0, true, arrival);
  };
  documentNavigationRef.current = moveDocument;
  const selectedSpinePosition = visibleDocumentIndexes.indexOf(selectedDocument);
  /* Confirming a document removes it from a spine filtered to review work, and
     the arrows were disabled on `position < 0` — so the reward for confirming
     was being stranded on the document you had just finished with no way
     forward. moveDocument has always handled being off-list; only the buttons
     believed it could not. */
  const strandedInSpine = selectedSpinePosition < 0 && visibleDocumentIndexes.length > 0;
  /* Learning's Teach in Documents: the documents, at the example Paige would most like taught
     (the engine's teaching order), else the next teaching example in the list. */
  selectTeachingExampleRef.current = () => {
    const examples = batch.documents.flatMap((item, index) =>
      item.needsTeaching && !item.needsReview && !awaitingUnderstanding(item) ? [index] : [],
    );
    if (examples.length === 0) return true;
    const byId = new Map(examples.map((index) => [batch.documents[index].id, index]));
    const recommended = teachingOrder
      .map((id) => byId.get(id))
      .find((index) => index !== undefined);
    setAllDoneArmed(null);
    selectDocument(
      recommended ?? examples.find((index) => index > selectedDocument) ?? examples[0],
      0,
      true,
      true,
    );
    return true;
  };
  const activeSuggestionKey = typeSuggestion
    ? `document-type:${typeSuggestion.suggestedType.toLocaleLowerCase()}`
    : '';
  const dismissSuggestion = (scope: 'now' | 'today' | 'never') => {
    if (!activeSuggestionKey) return;
    if (scope === 'now') setDismissedSuggestionKey(activeSuggestionKey);
    if (scope === 'today') {
      const tomorrow = new Date();
      tomorrow.setHours(24, 0, 0, 0);
      onJobChange({ ...job, suggestionsPausedUntil: tomorrow.toISOString() });
      onNotice('Suggestions paused for today');
    }
    if (scope === 'never') {
      onJobChange({
        ...job,
        suppressedSuggestions: [
          ...new Set([...(job.suppressedSuggestions ?? []), activeSuggestionKey]),
        ],
      });
      onNotice('This suggestion will not be shown again');
    }
    setSuggestionMenuOpen(false);
    setTypeSuggestion(null);
  };
  const detachPanel = (panel: ReviewPanelKind) => {
    void window.capturePointDesktop
      ?.openReviewPanel(panel, job.id)
      .then(() => setDetachedPanels((current) => ({ ...current, [panel]: true })))
      .catch((reason: unknown) =>
        setError(
          messageFor(reason, `Could not move ${panel} to another window`),
          'panel-layout-failed',
        ),
      );
  };
  const redockPanel = (panel: ReviewPanelKind) => {
    void window.capturePointDesktop
      ?.closeReviewPanel(panel)
      .then(() => setDetachedPanels((current) => ({ ...current, [panel]: false })))
      .catch((reason: unknown) =>
        setError(messageFor(reason, `Could not show ${panel} here`), 'panel-layout-failed'),
      );
  };
  const detachedSurfaceLabels: Record<ReviewPanelKind, string> = {
    viewer: 'Viewer',
    fields: 'Fields',
    'line-items': 'Line items',
  };
  const movedSurfaces = (
    Object.entries(detachedPanels) as Array<[ReviewPanelKind, boolean]>
  ).filter(([, detached]) => detached);
  const sourceReview =
    compareSources &&
    (!detachedPanels.fields || (expectsLineItems && !detachedPanels['line-items']));
  const comparingLines = sourceReview && expectsLineItems && !detachedPanels['line-items'];
  const captureColumns =
    sourceReview && !paneLayout.narrow && !detachedPanels.viewer
      ? `${paneLayout.spine}px minmax(0, 1fr)`
      : paneLayout.columns;
  const activeReviewPane =
    paneLayout.activePane === 'lines' && (!expectsLineItems || detachedPanels['line-items'])
      ? 'page'
      : paneLayout.activePane;
  /* The one receipt the column shows: the newest whose news is still live.
     Promotion order names the newest; availability filters out the dismissed
     and the expired, so an older receipt's Undo resurfaces when the newer one
     goes rather than dying underneath it. */
  // What learning did to OTHER documents (cleared, or sent back) is a line under the
  // documents list's counts, never a card here.
  const liveReceipts = {
    improvement: Boolean(lastJobImprovement),
    stance: Boolean(stanceReceipt),
  } as const;
  const activeReceipt =
    receiptOrder.find((kind) => liveReceipts[kind]) ??
    (['improvement', 'stance'] as const).find((kind) => liveReceipts[kind]) ??
    null;
  /* The cleared and sent-back news belong beside the counts they change. Only while the
     documents list is not on this screen (the viewer and its list detached, or a narrow
     window on another pane) do the same lines stand in the status bar. */
  const clearedNewsInList =
    !detachedPanels.viewer && (!paneLayout.narrow || activeReviewPane === 'documents');
  const clearedNewsLine = (place: 'list' | 'status') =>
    (place === 'list') !== clearedNewsInList ? null : (
      <>
        {clearedNews && (
          <ClearedNewsLine
            news={clearedNews}
            undoing={clearedNewsUndoing}
            onUndo={() => void undoClearedNews()}
            onExpire={() => setClearedNews((current) => (current === clearedNews ? null : current))}
          />
        )}
        {backNewsShown && (
          <ClearedNewsLine
            news={backNewsShown}
            onExpire={() => setBackNews((current) => (current === backNewsShown ? null : current))}
          />
        )}
      </>
    );
  const confirmingSelected = document ? confirmingDocumentIds.has(document.id) : false;
  // Delete (ADR-0096): on Manual only, on the open document's own heading; unavailable while it is being deleted,
  // confirmed or given a type. A value being saved leaves it on: the blur of typing starts that save as the click
  // lands, and Delete waits for it after the question.
  const canDeleteDocuments =
    pilotMode(job.reviewPolicy?.processingMode) === 'human-reviewed' &&
    Boolean(window.capturePointDesktop?.deleteDocuments);
  const deleteUnavailable =
    deletingDocumentId !== null ||
    batch.batchId.startsWith('live:') ||
    confirmingSelected ||
    typeSavingDocumentId === document.id ||
    schemaRefreshBusy;
  const documentCompletion = (
    <div
      ref={trackDocumentBarHeight}
      className="field-workbench-completion document-action-bar"
      aria-label="Document actions"
      style={
        { '--status-pane-offset': `${paneLayout.spine - paneLayout.fields}px` } as CSSProperties
      }
    >
      <div className="document-bar-status">
        <div className="document-bar-indicators">
          <LearningScoreButton
            job={job}
            onClick={() => window.dispatchEvent(new Event('capturepoint-open-learning'))}
          />
          <span className="document-bar-divider" aria-hidden="true" />
          {hostHas('workbench-settings') ? <ComputerDoorButton /> : <AccountDoorButton />}
          {hostHas('licensing') && <TrialTray />}
          {hostHas('licensing') && <PagesTray />}
          {hostHas('local-performance') && <SpeedBoostButton />}
          {hostHas('saved-connections') && <ConnectionAlert />}
        </div>
        <div className="document-bar-messages">
          {/* Quiet until needed. A folder still being read says how far along it is here
                    (owner, October 3, 2026); unfinished imports and "Ready" stay in the documents list. */}
          <FolderImportStatus progress={folderImport} />
          {/* Way back (option B): six seconds after a move, in the bar's own message area. */}
          {backOffer && backOffer.shownOn === document.id && !doneStage && (
            <span className="confirm-back-offer" role="status">
              <span className="confirm-back-done">
                <CheckCircle2 size={13} aria-hidden="true" />
                Confirmed
              </span>
              <button
                type="button"
                className="confirm-back"
                data-telemetry="review-back"
                onClick={goBackToConfirmed}
              >
                Back
              </button>
            </span>
          )}
          {/* Just joined: the same six seconds, the same place, with Undo (owner, 2026-10-30). */}
          {joinNewsHere && (
            <span className="confirm-back-offer" role="status">
              <span className="confirm-back-done join-done">
                <Paperclip size={13} aria-hidden="true" />
                Joined
              </span>
              {joinNewsHere.added > 0 && (
                <span className="join-news-added">{joinNewsHere.added} like it to check</span>
              )}
              <button
                type="button"
                className="confirm-back"
                data-telemetry="join-undo"
                onClick={undoJoinNews}
              >
                Undo
              </button>
            </span>
          )}
          {clearedNewsLine('status')}
          <WorkspaceNotice
            fallbackMessage={learningReceipt ?? ''}
            onDismissFallback={() => setLearningReceipt(null)}
          />
          <RuntimeComponentDownload status={componentDownload} />
          {processorTeachingActive && (
            <button
              className="status-learning-details"
              onClick={() => window.dispatchEvent(new Event('capturepoint-open-learning'))}
            >
              Learning progress <ArrowRight size={12} />
            </button>
          )}
          {document.needsReview &&
            currentReviewReasons.length === 0 &&
            !awaitingUnderstanding(document) &&
            loadedDocumentIds.has(document.id) &&
            !fieldSaving &&
            !lineSaving &&
            !confirmingSelected &&
            !schemaRefreshBusy &&
            typeSavingDocumentId !== document.id && (
              <button className="status-learning-details" onClick={() => void recheckReviewFlag()}>
                <RotateCw size={12} />
                Recheck review status
              </button>
            )}
        </div>
      </div>
      <div className="document-bar-navigation">
        {!doneStage && (
          <WorkspaceStepper
            label="Document"
            index={selectedDocument + 1}
            total={batch.documents.length}
            previousDisabled={strandedInSpine ? false : selectedSpinePosition <= 0}
            nextDisabled={
              strandedInSpine
                ? false
                : selectedSpinePosition < 0 ||
                  selectedSpinePosition === visibleDocumentIndexes.length - 1
            }
            onPrevious={() => moveDocument(-1)}
            onNext={() => moveDocument(1)}
            previousTitle={`Previous document · ${documentStepModifier()}+Page Up`}
            nextTitle={`Next document · ${documentStepModifier()}+Page Down`}
          />
        )}
      </div>
      <div className="document-bar-actions">
        {!doneStage && (
          <>
            {beatHere ? (
              <button type="button" className="document-confirm-bar is-done" disabled>
                <Check size={14} aria-hidden="true" />
                <span className="document-confirm-label">Confirmed</span>
              </button>
            ) : mainAction.kind === 'confirmed' ? (
              <span className="document-confirmed">
                <CheckCircle2 size={14} />
                Document confirmed
              </span>
            ) : (
              <button
                type="button"
                className={`document-confirm-bar${mainAction.kind === 'next' ? ' is-next' : ''}`}
                onClick={(event) => runMainAction('button', event.timeStamp)}
                data-telemetry={mainAction.kind === 'next' ? 'review-next' : 'review-confirm'}
                onKeyDown={(event) => {
                  if (event.repeat && (event.key === 'Enter' || event.key === ' '))
                    event.preventDefault();
                }}
                // PAIGE: a read-only view cannot confirm; an exported document's Confirm says nothing goes out again.
                title={
                  mainAction.kind === 'next'
                    ? undefined
                    : readOnlyView()
                      ? READ_ONLY_HINT
                      : confirmWords(
                          flaggedAfterExport.exported(document),
                          mainAction.label,
                          flaggedAfterExport.sendsTo(document),
                        ).tooltip
                }
                disabled={
                  mainAction.kind !== 'next' &&
                  (readOnlyView() ||
                    mainWaitsForReading ||
                    typeSavingDocumentId === document.id ||
                    confirmingSelected)
                }
              >
                {mainAction.kind === 'next' ? null : mainWaitsForReading ||
                  fieldSaving ||
                  lineSaving ||
                  typeSavingDocumentId === document.id ||
                  confirmingSelected ? (
                  <LoaderCircle className="spin" size={14} />
                ) : (
                  <CheckCircle2 size={14} />
                )}
                {/* PAIGE: the Confirm's words (flagged-after-export.ts, confirmWords). */}
                <span className="document-confirm-label">
                  {mainAction.kind === 'next'
                    ? mainAction.label
                    : confirmWords(flaggedAfterExport.exported(document), mainAction.label).label}
                </span>
                {mainAction.kind === 'next' && <ArrowRight size={14} aria-hidden="true" />}
                <kbd className="cp-key" aria-hidden="true">
                  Ctrl+Enter
                </kbd>
              </button>
            )}
            {mainAction.kind === 'confirm-next' && !beatHere && nextReviewDocument && (
              <TooltipButton
                type="button"
                className="review-skip"
                tooltip={`Shortcut: ${documentStepModifier()}+PgDn`}
                data-telemetry="review-skip"
                onClick={skipDocument}
              >
                <span>Skip</span>
                <ArrowRight size={14} aria-hidden="true" />
              </TooltipButton>
            )}
          </>
        )}
      </div>
    </div>
  );
  const wholeBatchMutationBusy = Boolean(wholeBatchMutationRef.current);
  const typeConfirmationQueued = document ? queuedTypeConfirmationIds.has(document.id) : false;
  if (reviewSnapshotRef.current) {
    reviewSnapshotRef.current.review = {
      documentTypeId: shownType?.id ?? '',
      canConfirmDocument: !awaitingUnderstanding(document),
      fullyConfirmed: documentFullyConfirmed,
      // The one main button, as the main window shows it, for the detached panel to show too.
      mainAction: {
        kind: mainAction.kind,
        label: mainAction.label,
        busy: confirmingSelected || typeSavingDocumentId === document.id || mainWaitsForReading,
      },
      skip: mainAction.kind === 'confirm-next' && !beatHere && Boolean(nextReviewDocument),
      confirmedBeat: beatHere,
      backOffer: Boolean(backOffer && backOffer.shownOn === document.id && !doneStage),
      done: doneStage,
      issues: currentReviewWork.map((reason) => ({
        label: reason.label,
        fieldName: reason.fieldName,
        boundary: reason.boundary,
        place: reviewItemPlace(reason),
      })),
      // The line the main window shows above its fields, which the panel shows when the fields are here.
      waiting: awaitingUnderstanding(document)
        ? undefined
        : (whyDocumentWaits(document, job) ?? undefined),
      note: awaitingUnderstanding(document)
        ? undefined
        : (waitingNote(document, WAIT_REASON_WORDS) ?? undefined),
      learningStatus: teachingTypeProgress
        ? `${teachingTypeProgress.confirmed} type confirmations · ${teachingTypeProgress.recognisedFromConfirmedExamples} documents recognised from them`
        : undefined,
      learningReceipt: learningReceipt ?? undefined,
      canDelete: canDeleteDocuments,
      // The panel's Delete is off whenever this window would refuse it (another document's page turn or boundary
      // change, pages being read), not only while this one is busy, so a click there is never refused out of sight. Off
      // too while anything but typing holds a save on this document (a line item added, removed or accepted, a picked
      // value, value Undo, Find the table, Recheck): unlike typing, it moves the document past the revision the panel's
      // Delete is bound to, which the action handler then refuses. A value or line typed leaves it on. A line save that
      // is not typing lands only with the whole line-save lane, so it stays off until that lane lands (lineSaveUntyped),
      // even with only typing still saving behind it.
      deleteBusy:
        deleteUnavailable ||
        deleteRefusal(document.id) !== null ||
        untypedLineMutationAdmitted(document.id) ||
        lineSaveUntyped.current.has(document.id) ||
        (fieldSavingDocumentIdRef.current === document.id && !fieldSaveTypedRef.current),
      deletedNotice:
        workspaceNotice.action?.telemetry === DELETE_UNDO_TELEMETRY
          ? workspaceNotice.message
          : undefined,
    };
    reviewSnapshotRef.current.refusedLineRow = refusedLine;
    // A split or join being saved owns its documents; the detached editors wait with the docked ones.
    reviewSnapshotRef.current.fieldSaving ||=
      confirmingSelected ||
      wholeBatchMutationBusy ||
      schemaRefreshBusy ||
      structuralMutationBlocks(document.id);
  }
  return (
    <div
      ref={paneLayout.ref}
      className="capture-view"
      data-workspace-projection={batch.workspaceProjection ?? 'live'}
      data-loaded-document-count={loadedDocumentIds.size}
    >
      {deletedDialog}
      {shownError && (
        <div
          className="workspace-action-message"
          role="alert"
          data-telemetry={`error:${shownErrorCode}`}
        >
          <TriangleAlert size={20} />
          <OperatorErrorText message={shownError} />
          <TrialWallButton message={shownError} />
          {/* PAIGE: a confirmation that could not be saved is tried again from here (confirm-save.ts). */}
          {shownError === CONFIRM_SAVE_FAILED && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setError(null);
                runMainAction('button');
              }}
            >
              Try again
            </button>
          )}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
            <X size={18} />
          </button>
        </div>
      )}
      {!shownError && shownAcquisitionNotice && (
        <div className="workspace-action-message" role="status">
          <Info size={20} />
          <span>{shownAcquisitionNotice}</span>
          <button
            type="button"
            onClick={() => setAcquisitionNotice(null)}
            aria-label="Dismiss this notice"
          >
            <X size={18} />
          </button>
        </div>
      )}
      {detachedPanels.viewer && <JobProcessingButton jobId={job.id} compact />}
      {movedSurfaces.length > 0 && detachedPanels.viewer && (
        <div className="detached-surface-bar" aria-label="Surfaces on other screens">
          {movedSurfaces.map(([panel]) => (
            <div key={panel}>
              <ExternalLink size={11} />
              <span>
                <strong>{detachedSurfaceLabels[panel]}</strong>
                <small>On another screen</small>
              </span>
              <button
                onClick={() => redockPanel(panel)}
                aria-label={`Show ${detachedSurfaceLabels[panel]} here`}
              >
                Show here
              </button>
            </div>
          ))}
        </div>
      )}
      {(schemaRefreshBusy || boundaryRefreshing) && detachedPanels.viewer && (
        <div className="boundary-refresh-status" role="status">
          <LoaderCircle className="spin" size={13} />
          <span>
            {schemaRefreshBusy
              ? 'Applying the current job fields…'
              : 'Rechecking the documents either side · you can keep working'}
          </span>
        </div>
      )}
      {captureQueue.length > 0 && detachedPanels.viewer && (
        <div className="workspace-capture-status">
          <CaptureQueueStatus
            queue={captureQueue}
            processingId={captureProcessingId}
            progress={captureProgress}
            step={importStep}
            waves={captureWaves}
            folder={folderImport}
            learningPhaseLabel={learningLanguage.phaseLabel}
            onOpenCluster={onOpenCluster}
            onRetry={retryStagedCapture}
            onDiscard={(id) => void discardStagedCapture(id)}
          />
        </div>
      )}
      {paneLayout.narrow && (
        <div className="review-pane-switcher" role="group" aria-label="Review panes">
          {(
            [
              ['documents', 'Documents'],
              ['page', 'Page'],
              ['fields', 'Fields'],
              ['lines', 'Line items'],
            ] as const
          )
            .filter(
              ([pane]) =>
                (pane !== 'fields' || !detachedPanels.fields) &&
                (pane !== 'lines' || (expectsLineItems && !detachedPanels['line-items'])),
            )
            .map(([pane, label]) => (
              <button
                key={pane}
                type="button"
                aria-pressed={activeReviewPane === pane}
                onClick={() => paneLayout.revealPane(pane)}
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  event.preventDefault();
                  const buttons = [
                    ...event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>(
                      'button',
                    ),
                  ];
                  const at = buttons.indexOf(event.currentTarget);
                  const next =
                    event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? buttons.length - 1
                        : (at + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) %
                          buttons.length;
                  buttons[next].focus();
                  buttons[next].click();
                }}
              >
                {label}
              </button>
            ))}
        </div>
      )}
      <div
        className={`capture-layout has-review-toolbar ${fieldsAtTop ? 'fields-at-top' : ''} line-items-${lineItemsMode} interaction-text ${sourceReview ? 'source-review' : ''} ${comparingLines ? 'source-review-lines' : ''} ${paneLayout.narrow ? 'panes-single' : ''} ${resizingResults || resizingSpine ? 'resizing' : ''} ${detachedPanels.viewer ? 'viewer-detached' : ''} ${detachedPanels.fields ? 'fields-detached' : ''} ${detachedPanels['line-items'] ? 'lines-detached' : ''}`}
        data-active-pane={activeReviewPane}
        style={{ gridTemplateColumns: captureColumns }}
      >
        {!detachedPanels.viewer && (
          <aside className="thumbnail-strip" data-review-pane="documents">
            {/* PAIGE: files and folders dragged onto the documents are Add pages (PageDropZone). */}
            <PageDropZone
              heading="Drop a folder or files to add pages"
              disabled={acquiring}
              onPages={(source) => void acquireDocuments(source)}
            />
            <div className="thumbnail-heading">
              <div
                className="spine-title-row"
                data-documents={batch.documents.length}
                data-waiting={askedDocuments}
              >
                {/* The job's name is the list's title (owner, 2026-10-30): its selector, then what is true of
                  the job where everyone sees it, Calibration or the review work left. */}
                {workspaceJobSelector}
                {calibrating ? (
                  <TooltipButton
                    type="button"
                    className="spine-calibration-tag"
                    tooltip="Turns off by itself when this job is ready"
                    data-telemetry="calibration-tag"
                    onClick={() => window.dispatchEvent(new Event('capturepoint-open-learning'))}
                  >
                    <Gauge className="spine-calibration-icon" size={14} aria-hidden="true" />
                    <span className="spine-title-words">Calibration</span>
                  </TooltipButton>
                ) : (
                  reviewCount > 0 && (
                    <span
                      key={reviewCountTick}
                      className={`spine-review-count${reviewCountTickClass ? ` ${reviewCountTickClass}` : ''}`}
                      role="status"
                    >
                      <span className="review-waiting-dot" aria-hidden="true" />
                      <span className="spine-review-count-text">
                        {reviewCount.toLocaleString('en-US')}
                        <span className="spine-title-words">
                          {' '}
                          {reviewCount === 1 ? 'needs' : 'need'} review
                        </span>
                      </span>
                    </span>
                  )
                )}
                <div className="spine-filter-actions">
                  <TooltipButton
                    type="button"
                    className="spine-search-trigger"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setBatchSearchOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={batchSearchOpen}
                    tooltip="Search documents · Ctrl+Shift+F"
                    aria-label="Search documents"
                  >
                    <FileSearch size={20} />
                  </TooltipButton>
                </div>
              </div>
              {assistedReview && (
                <p className="assisted-review-status" role="status">
                  <Bot size={14} aria-hidden="true" />
                  {/* PAIGE: Cortexa, the product's name for the AI (CLAUDE.md, Names). */}
                  {documentCensus.aiReviewing > 0
                    ? `Autopilot: Cortexa reviews for you · ${documentCensus.aiReviewing} in progress`
                    : 'Autopilot: Cortexa reviews for you'}
                </p>
              )}
              {clearedNewsLine('list')}
              {/* PAIGE: the exported documents a later check flagged; never in Needs review. */}
              <FlaggedAfterExportFinder
                count={flaggedAfterExport.ids.size}
                onNext={() => {
                  const next = nextFlagged(
                    batch.documents,
                    flaggedAfterExport.ids,
                    selectedDocument,
                  );
                  if (next === null) return;
                  selectDocument(next, 0, true);
                }}
              />
              {/* The sample tour takes the top slot. On their own job the export line stays on top,
                with the tour card (or, once, the offer of it) under it (owner, 2026-10-04). */}
              {touring && !tour?.own && !tour?.ready && (
                <TourReadingReport
                  jobId={job.id}
                  stage={captureProgress?.stage ?? null}
                  reading={
                    Boolean(folderImport) ||
                    stillReading ||
                    captureQueue.some((item) => item.status === 'pending')
                  }
                  types={batch.documents.map((document) =>
                    tourDocumentType(document, stillReading),
                  )}
                  again={tourReadAgain(batch.documents)}
                  problem={tourReaderProblem(batch.documents)}
                />
              )}
              {touring && !tour?.own ? (
                <>
                  <TourCard jobId={job.id} />
                  {hostHas('desktop-export') ? (
                    <FirstExportCard
                      jobId={job.id}
                      documents={batch.documents}
                      onNotice={onNotice}
                      onRefresh={onRestored}
                      reading={
                        folderImport ||
                        stillReading ||
                        captureQueue.some((item) => item.status === 'pending')
                          ? {
                              stage: captureProgress?.stage,
                              completed: captureProgress?.completed,
                              total: captureProgress?.total,
                            }
                          : null
                      }
                    />
                  ) : (
                    hostExportPanel
                  )}
                </>
              ) : (
                <>
                  {/* Documents › Paige Exports is the desktop's folder; Paige sends results to its own destinations. */}
                  {hostHas('desktop-export') && (
                    <FirstExportCard
                      jobId={job.id}
                      documents={batch.documents}
                      onNotice={onNotice}
                      onRefresh={onRestored}
                      reading={
                        folderImport
                          ? { folder: true }
                          : stillReading || captureQueue.some((item) => item.status === 'pending')
                            ? {
                                stage: captureProgress?.stage,
                                completed: captureProgress?.completed,
                                total: captureProgress?.total,
                              }
                            : null
                      }
                    />
                  )}
                  {/* PAIGE: the same line on Paige's job, from its destinations (Dustin, 2026-11-03); hidden
                      while a batch is read, as CapturePoint's card is (exportCardState). */}
                  {!hostHas('desktop-export') &&
                    !touring &&
                    !(
                      folderImport ||
                      stillReading ||
                      captureQueue.some((item) => item.status === 'pending')
                    ) &&
                    hostExportPanel}
                  {touring ? (
                    <>
                      <TourCard jobId={job.id} />
                      {!hostHas('desktop-export') && hostExportPanel}
                    </>
                  ) : (
                    <>
                      <TourOffer
                        jobId={job.id}
                        documents={batch.documents.length}
                        sample={Boolean(sampleSetOfJob(job.id))}
                        reading={
                          Boolean(folderImport) ||
                          stillReading ||
                          captureQueue.some((item) => item.status === 'pending')
                        }
                      />
                      <DocumentHint
                        jobId={job.id}
                        documents={batch.documents}
                        pilot={job.reviewPolicy?.processingMode}
                        sample={Boolean(sampleSetOfJob(job.id))}
                        calibrating={isCalibrating(job.learningStance)}
                        reading={
                          Boolean(folderImport) ||
                          stillReading ||
                          captureQueue.some((item) => item.status === 'pending')
                        }
                      />
                    </>
                  )}
                </>
              )}
              {movedSurfaces.length > 0 && (
                <div className="spine-moved-panels">
                  {movedSurfaces.map(([panel]) => (
                    <button
                      key={panel}
                      onClick={() => redockPanel(panel)}
                      title="Show it here"
                      aria-label={`Show ${detachedSurfaceLabels[panel]} here`}
                    >
                      <ExternalLink size={13} />
                      {detachedSurfaceLabels[panel]}
                    </button>
                  ))}
                </div>
              )}
              {typeof batch.blankPagesRemoved === 'number' && batch.blankPagesRemoved > 0 && (
                <span className="batch-blanks-removed">
                  {counted(batch.blankPagesRemoved, 'blank sheet')} removed
                </span>
              )}
              {captureQueue.some((item) => item.status === 'failed') && (
                <div className="workspace-capture-status compact">
                  <CaptureQueueStatus
                    condensed
                    queue={captureQueue.filter((item) => item.status === 'failed')}
                    processingId=""
                    progress={null}
                    waves={new Map()}
                    onOpenCluster={onOpenCluster}
                    onRetry={retryStagedCapture}
                    onDiscard={(id) => void discardStagedCapture(id)}
                  />
                </div>
              )}
            </div>
            <div
              className="document-spine"
              ref={attachSpine}
              style={
                {
                  scrollPaddingBottom: SPINE_FLOATING_ADD_CLEARANCE,
                  paddingBottom: Math.max(
                    SPINE_FLOATING_ADD_CLEARANCE,
                    lastSpineMetric
                      ? spineViewport.height -
                          lastSpineMetric.height -
                          incomingPageEntries.length * incomingRowHeight
                      : 0,
                  ),
                  '--spine-page-row': `${spinePageRow}px`,
                  '--spine-thumbnail': `${spineThumbnailWidth}px`,
                } as CSSProperties
              }
              onScroll={(event) => {
                const target = event.currentTarget;
                if (spineScrollFrame.current) return;
                spineScrollFrame.current = window.requestAnimationFrame(() => {
                  spineScrollFrame.current = 0;
                  setSpineViewport((current) => {
                    const next = { top: target.scrollTop, height: target.clientHeight };
                    return Math.abs(next.top - current.top) < 40 &&
                      Math.abs(next.height - current.height) < 1
                      ? current
                      : next;
                  });
                });
              }}
            >
              <div className="document-spine-window" style={{ height: `${spineOffset}px` }}>
                {virtualSpineEntries.map(({ item, documentIndex, top, height }) => {
                  const outstanding = documentReviewReasons(item, documentIndex);
                  // The colour comes from the document's own recorded state, the same
                  // thing the queue filters on, so a thumbnail cannot say "settled"
                  // while sitting in the review list — or turn green the moment it is
                  // opened. The reason list is still what names the fault and carries
                  // the operator to it; it just no longer decides the colour.
                  const reviewReason = reasonsToCheck(outstanding)[0] ?? null;
                  // Asked about (review work, or a teaching example while calibrating), or nothing left to do.
                  const asked = askedWork(item, calibrating);
                  const ready = asked === null;
                  // A split or join already asked for here shows "Saving…" where its control was.
                  const opening = item.pages[0];
                  const pendingJoin = opening
                    ? boundaryPendingByPage.get(opening.number)
                    : undefined;
                  /* The state belongs to the document, not to a dot floating at the end of a
                 row. A short type name left it stranded in empty space with nothing
                 tying it to the pages above or below, so an operator could not tell
                 which document it was about. The whole block carries it now. */
                  return (
                    <Fragment key={item.id}>
                      {/* The join belongs in the eight-pixel inter-document gap, not
                    inside the paint-contained document header where it clips. */}
                      {joinableSpineDocumentIndexes.has(documentIndex) &&
                        (pendingJoin ? (
                          <BoundaryPendingMark
                            kind={pendingJoin}
                            pageNumber={opening?.number}
                            className="spine-pending"
                            style={{ top: `${top - 4}px` }}
                          />
                        ) : (
                          <TooltipButton
                            className="merge-boundary spine-gap-action"
                            data-telemetry="document-join"
                            data-boundary-document-index={documentIndex}
                            style={{ top: `${top - 4}px` }}
                            onClick={() => mergeWithPrevious(documentIndex)}
                            tooltip={joinTooltip}
                            aria-label={`Merge document ${documentIndex + 1} with previous`}
                          >
                            <Paperclip size={18} />
                          </TooltipButton>
                        ))}
                      <section
                        data-document-index={documentIndex}
                        className={`document-group ${awaitingUnderstanding(item) ? 'group-reading' : asked === null ? (exceptionsRouteOf(item) ? 'group-exceptions' : 'group-ready') : calibrating ? 'group-asked' : 'group-review'} ${arrivedBoundary === documentIndex ? 'boundary-arrived' : ''} ${arrivedBoundary !== null && arrivedBoundary - 1 === documentIndex ? 'boundary-before' : ''} ${selectedDocument === documentIndex ? 'active' : ''}`}
                        style={{ top: `${top}px`, height: `${height - 8}px` }}
                      >
                        <div className="document-marker-row">
                          <button
                            className="document-marker"
                            onClick={() => {
                              selectDocument(documentIndex);
                              if (reviewReason?.fieldId) setFocusFieldId(reviewReason.fieldId);
                              else if (reviewReason?.documentType) setFocusTypeControl(true);
                              else if (
                                reviewReason &&
                                (reviewReason.lineRow || reviewReason.table)
                              ) {
                                if (detachedPanels['line-items']) detachPanel('line-items');
                                else {
                                  paneLayout.revealPane('lines');
                                  setLineItemsMode('docked');
                                }
                              }
                            }}
                          >
                            <span className="spine-document-name">
                              <small>
                                Document {documentIndex + 1}
                                {/* PAIGE: exported, then flagged by a later check. */}
                                {flaggedAfterExport.ids.has(item.id) && <FlaggedAfterExportChip />}
                              </small>
                              <strong>
                                {awaitingUnderstanding(item)
                                  ? 'Reading…'
                                  : (filedDocumentType(item, job.documentTypes, typeNameHistory)
                                      ?.name ?? item.documentType)}
                              </strong>
                              {/* Paige: who holds this document (ADR-0009). */}
                              {reviewLease.held.get(item.id) && (
                                <HeldByChip holder={reviewLease.held.get(item.id)!} />
                              )}
                            </span>
                            {!awaitingUnderstanding(item) &&
                              asked === null &&
                              exceptionsRouteOf(item) && (
                                <span
                                  className="spine-exceptions-note"
                                  title={exceptionsRouteOf(item)!.reasonText}
                                >
                                  Goes to exceptions
                                </span>
                              )}
                            {awaitingUnderstanding(item) ? <i className="pending-dot" /> : null}
                          </button>
                        </div>
                        <div
                          className="document-pages-window"
                          style={{ height: `${item.pages.length * spinePageRow}px` }}
                        >
                          {/* Same visibility test as before, decided inline: the old
                      map→filter→map built two throwaway arrays and a wrapper
                      object per PAGE of every visible document per render,
                      which on a long document is thousands of allocations to
                      draw a dozen thumbnails. */}
                          {item.pages.map((page, pageIndex) => {
                            const pageTop = spinePageTop(top, pageIndex, spinePageRow);
                            if (
                              pageTop + spinePageRow < spineViewport.top - spineBuffer ||
                              pageTop > spineViewport.top + spineViewport.height + spineBuffer
                            )
                              return null;
                            const removeKey = `${item.id}:${page.number}`;
                            const removing = confirmingPageRemoval === removeKey;
                            const cancelRemoval = (target: HTMLElement) => {
                              setConfirmingPageRemoval(null);
                              target
                                .closest('.thumbnail-shell')
                                ?.querySelector<HTMLButtonElement>('.remove-page')
                                ?.focus({ preventScroll: true });
                            };
                            return (
                              <div
                                className={`thumbnail-shell${removing ? ' removing' : ''}`}
                                style={{ top: `${pageIndex * spinePageRow}px` }}
                                key={`${item.id}-${page.number}`}
                              >
                                <button
                                  data-document-index={documentIndex}
                                  data-page-index={pageIndex}
                                  onClick={() => selectDocument(documentIndex, pageIndex)}
                                  className={`thumbnail ${selectedDocument === documentIndex && selectedPage === pageIndex ? 'active' : ''}`}
                                >
                                  <LazyPageThumbnail
                                    previewPath={pageTurnImage(
                                      pageTurns.get(pageTurnKey(item.id, page.number)),
                                      page.previewPath,
                                    )}
                                    label={`Document ${documentIndex + 1}, page ${pageIndex + 1}`}
                                    transform={pageTurnTransform(
                                      pageTurnShown(
                                        pageTurns.get(pageTurnKey(item.id, page.number)),
                                        pageTurnImage(
                                          pageTurns.get(pageTurnKey(item.id, page.number)),
                                          page.previewPath,
                                        ),
                                      ),
                                      page.pixelWidth,
                                      page.pixelHeight,
                                    )}
                                  />
                                  <small>Page {pageIndex + 1}</small>
                                </button>
                                {pageIndex > 0 &&
                                  (boundaryPendingByPage.has(page.number) ? (
                                    <BoundaryPendingMark
                                      kind={boundaryPendingByPage.get(page.number)!}
                                      pageNumber={page.number}
                                      className="spine-pending"
                                    />
                                  ) : (
                                    <TooltipButton
                                      className="split-boundary spine-gap-action"
                                      data-telemetry="document-split"
                                      onClick={() => splitDocumentAt(documentIndex, pageIndex)}
                                      tooltip="Start a new document here · Ctrl+B"
                                      aria-label={`Start new document at page ${pageIndex + 1}`}
                                    >
                                      <Scissors size={18} />
                                    </TooltipButton>
                                  ))}
                                {/* Splitting and merging move a boundary and are the work; taking
                        a page out of the batch is the only control here that
                        destroys something, and it sat one hover-click away from
                        every thumbnail. It asks first now, in place and naming
                        the page, the way removing a field does. */}
                                {item.pages.length > 1 && (
                                  <>
                                    <TooltipButton
                                      className="remove-page"
                                      data-telemetry="page-remove"
                                      tabIndex={removing ? -1 : 0}
                                      aria-expanded={removing}
                                      onClick={() => setConfirmingPageRemoval(removeKey)}
                                      tooltip="Remove page"
                                      aria-label={`Remove page ${pageIndex + 1} from document ${documentIndex + 1}`}
                                    >
                                      <Trash2 size={15} />
                                    </TooltipButton>
                                    {removing && (
                                      <div
                                        className="remove-page-confirm"
                                        role="group"
                                        aria-label={`Remove page ${pageIndex + 1} from document ${documentIndex + 1}?`}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Escape') {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            cancelRemoval(event.currentTarget);
                                          }
                                        }}
                                      >
                                        <strong>Remove page {pageIndex + 1}?</strong>
                                        <small>Document {documentIndex + 1}</small>
                                        <span>
                                          <button
                                            className="remove-page-no"
                                            data-telemetry="page-remove-keep"
                                            autoFocus
                                            onClick={(event) => cancelRemoval(event.currentTarget)}
                                          >
                                            Keep it
                                          </button>
                                          <button
                                            className="remove-page-yes"
                                            data-telemetry="page-remove-confirm"
                                            onClick={() => {
                                              setConfirmingPageRemoval(null);
                                              void removePageAt(documentIndex, pageIndex);
                                            }}
                                          >
                                            Remove
                                          </button>
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    </Fragment>
                  );
                })}
                {firstSplitEntry && (
                  <i
                    className="spine-first-split"
                    aria-hidden="true"
                    style={{ top: `${spinePageTop(firstSplitEntry.top, 1, spinePageRow)}px` }}
                  />
                )}
              </div>
              {incomingPageEntries.length > 0 && (
                <section className="incoming-pages" aria-label="Incoming pages">
                  <div
                    style={{
                      position: 'relative',
                      height: incomingPageEntries.length * incomingRowHeight,
                    }}
                  >
                    {visibleIncomingPages.map(({ key, page, index }) => (
                      <div
                        className="incoming-page"
                        key={key}
                        style={{
                          position: 'absolute',
                          top: index * incomingRowHeight,
                          height: incomingRowHeight,
                          left: 0,
                          right: 0,
                        }}
                      >
                        <div className="incoming-page-image" style={{ height: spinePageRow - 12 }}>
                          <LazyPageThumbnail
                            previewPath={page.previewPath}
                            label={`Incoming page from ${page.label}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
            {/* The footer owns its space: acquisition must never cover the page
              label or deletion target at the bottom of the list. Source choice,
              review policy and routine learning detail live in its menu. */}
            {/* After its export the tour's job doesn't invite more pages (CapturePoint 94b04eb1).
                PAIGE: its documents stay in the list, so Add pages goes from the list's footer. */}
            <div
              className={`spine-actions${touring && !tour?.own && (tour?.step === 'files' || tour?.step === 'turn') ? ' tour-exported' : ''}`}
            >
              <div className="spine-add-split" role="group" aria-label="Add pages">
                <button
                  className="spine-add"
                  onClick={() => void acquireDocuments()}
                  disabled={acquiring || readOnlyView()}
                  title={readOnlyView() ? READ_ONLY_HINT : undefined}
                >
                  {acquiring ? <LoaderCircle className="spin" size={14} /> : sourceIcon(14)}
                  <span>{acquiring ? acquisitionWorkingVerb : 'Add pages'}</span>
                </button>
                <TooltipButton
                  className={`spine-more ${spineMenuOpen ? 'active' : ''}`}
                  onClick={() => setSpineMenuOpen((current) => !current)}
                  aria-haspopup="menu"
                  aria-expanded={spineMenuOpen}
                  aria-label="Page source options"
                  tooltip="More"
                >
                  <MoreHorizontal size={15} />
                </TooltipButton>
              </div>
              {/* The same way out as the capture screen has.
                This copy of the control had none: a scan started here disabled the
                button, showed "Scanning", and left the operator with nothing to
                click until the scanner answered or the eleven-minute timeout
                fired. Observed stuck exactly that way while walking the workspace.
                Two buttons that start the same work must both be able to stop it. */}
              {acquiring &&
                sourceKey !== 'files' &&
                sourceKey !== 'folder' &&
                sourceKey !== 'simulator' && (
                  <button
                    className="spine-stop"
                    onClick={() => void stopAcquiring()}
                    aria-label="Stop the scanner"
                  >
                    <Square size={13} />
                    <span>Stop</span>
                  </button>
                )}
              {spineMenuOpen && (
                <div
                  className="spine-menu"
                  role="menu"
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      setSpineMenuOpen(false);
                      event.currentTarget
                        .closest('.spine-actions')
                        ?.querySelector<HTMLButtonElement>('.spine-more')
                        ?.focus();
                    }
                  }}
                >
                  {boundaryUndoAvailable && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setSpineMenuOpen(false);
                        undoBoundaryCorrection();
                      }}
                      aria-label="Undo the last boundary change"
                    >
                      <Undo2 size={14} />
                      <span>Undo boundary change</span>
                    </button>
                  )}
                  {pageUndoCount > 0 && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setSpineMenuOpen(false);
                        void undoPageRemoval();
                      }}
                      aria-label="Put back the removed page"
                    >
                      <Undo2 size={14} />
                      <span>Put back removed page</span>
                    </button>
                  )}
                  {valueUndoOffered && valueUndo && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setSpineMenuOpen(false);
                        void undoValueEdit();
                      }}
                      aria-label={`Undo the change to ${valueUndo.fieldName}`}
                    >
                      <Undo2 size={14} />
                      <span>Undo change to {valueUndo.fieldName}</span>
                    </button>
                  )}
                  <>
                    <div className="spine-menu-label">Add pages from</div>
                    {/* A whole folder belongs here too. It is a first-class choice on the
                    intake screen's Page source and was missing from this menu, so a job
                    set up FROM a folder could never be handed another one: the operator
                    reaches this menu, finds Files and scanners only, and the source has
                    meanwhile fallen back to a TWAIN scanner nobody chose. Add pages then
                    calls stageScan against it and returns silently — measured 2026-08-08,
                    two clicks, no spinner, no error, no log line, the batch unchanged. */}
                    {[
                      ...(hostHas('folder-intake')
                        ? [{ key: 'folder', name: 'Folder (includes subfolders)' }]
                        : []),
                      { key: 'files', name: 'Files on this computer' },
                      ...(scanners?.sources ?? []).map((source, index) => ({
                        key: `scanner:${index}`,
                        name: source.name,
                        email: source.icon === 'email',
                        watch: source.icon === 'folder',
                      })),
                      ...(simulatorTray ? [{ key: 'simulator', name: 'Simulated scanner' }] : []),
                    ].map((source) => (
                      <button
                        key={source.key}
                        role="menuitemradio"
                        aria-checked={sourceKey === source.key}
                        className={sourceKey === source.key ? 'selected' : ''}
                        onClick={() => {
                          // Paige's email source shows the job's address; it is never the
                          // source Add pages remembers, which would reopen it on every click.
                          // Nor is its watched folder, which is a sheet too.
                          if (!('email' in source && (source.email || source.watch)))
                            chooseSource(source.key);
                          setSpineMenuOpen(false);
                          void acquireDocuments(source.key);
                        }}
                      >
                        {source.key === 'files' ? (
                          <FileUp size={13} />
                        ) : source.key === 'folder' ? (
                          <FolderOpen size={13} />
                        ) : 'email' in source && source.email ? (
                          <Mail size={13} />
                        ) : 'watch' in source && source.watch ? (
                          <FolderSync size={13} />
                        ) : (
                          <ScanLine size={13} />
                        )}
                        <span>{source.name}</span>
                      </button>
                    ))}
                    <div className="spine-menu-divider" />
                  </>
                  {hostHas('break-sheets') && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setBreakSheetsOpen(true);
                        setSpineMenuOpen(false);
                      }}
                    >
                      <QrCode size={15} />
                      <span>Print break sheets</span>
                    </button>
                  )}
                  {/* The job's Deleted view (ADR-0096), where its documents are. */}
                  {window.capturePointDesktop?.listDeletedDocuments && (
                    <>
                      <div className="spine-menu-divider" />
                      <button
                        role="menuitem"
                        data-telemetry="documents-deleted"
                        onClick={() => {
                          setDeletedOpen(true);
                          setSpineMenuOpen(false);
                        }}
                      >
                        <Trash2 size={14} />
                        <span>
                          Deleted documents
                          {deletedCount ? ` · ${deletedCount.toLocaleString()}` : ''}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* The rail's import cue (interface-principles: "A simple Processing
                indicator at the bottom"), no counts, for work the status bar does not
                already tell: a folder being read is told there, with its count. */}
              {captureProcessingId && !folderImport && (
                <span className="spine-processing" role="status">
                  <LoaderCircle className="spin" size={13} />
                  <span>Processing</span>
                </span>
              )}
            </div>
            <div
              className="spine-resizer"
              role="separator"
              tabIndex={0}
              aria-label="Resize document thumbnails"
              aria-orientation="vertical"
              aria-valuemin={132}
              aria-valuemax={paneLayout.spineMax}
              aria-valuenow={paneLayout.spine}
              onPointerDown={beginSpineResize}
              onPointerMove={moveSpineResize}
              onPointerUp={endSpineResize}
              onPointerCancel={endSpineResize}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                event.preventDefault();
                setSpineWidth(
                  Math.max(
                    132,
                    Math.min(
                      paneLayout.spineMax,
                      paneLayout.spine + (event.key === 'ArrowRight' ? 20 : -20),
                    ),
                  ),
                );
              }}
            />
          </aside>
        )}
        <div className="workbench-review-toolbar">
          <LearningPendingContext.Provider
            value={
              improvementBell.hasUnseen
                ? jobSuggestions.filter((suggestion) => suggestion.status === 'pending').length
                : 0
            }
          >
            <LearningImprovementActionContext.Provider
              value={() => {
                window.dispatchEvent(
                  new CustomEvent('capturepoint-open-learning', {
                    detail: { section: 'suggestions' },
                  }),
                );
                improvementBell.acknowledge();
              }}
            >
              {workspaceNavigation}
            </LearningImprovementActionContext.Provider>
          </LearningPendingContext.Provider>
          <div
            className="review-layout-switcher"
            role="group"
            aria-label="Document view"
            hidden={doneStage}
          >
            <button
              type="button"
              aria-pressed={!sourceReview}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setCompareSources(false);
                paneLayout.revealPane('page');
              }}
              aria-label="Full page"
            >
              <FileText size={15} />
              <span>Full page</span>
            </button>
            <TooltipButton
              type="button"
              aria-pressed={sourceReview}
              onMouseDown={(event) => event.preventDefault()}
              disabled={Boolean(
                detachedPanels.fields && (!expectsLineItems || detachedPanels['line-items']),
              )}
              onClick={() => {
                setCompareSources(true);
                basePaneLayout.revealPane(detachedPanels.fields ? 'lines' : 'fields');
              }}
              aria-label="Focus"
              tooltip="Values next to the page"
            >
              <ScanLine size={15} />
              <span>Focus</span>
            </TooltipButton>
          </div>

          {!detachedPanels.viewer && (
            <div
              className="viewer-toolbar"
              role="toolbar"
              aria-label="Review workspace"
              hidden={sourceReview || doneStage}
            >
              <WorkspaceStepper
                label="Page"
                index={selectedPage + 1}
                total={document.pages.length}
                previousDisabled={selectedPage === 0}
                nextDisabled={selectedPage === document.pages.length - 1}
                onPrevious={() => selectDocument(selectedDocument, selectedPage - 1)}
                onNext={() => selectDocument(selectedDocument, selectedPage + 1)}
                previousTitle="Previous page · Page Up"
                nextTitle="Next page · Page Down"
              />

              <div className="viewer-tools" role="group" aria-label="View">
                {(schemaRefreshBusy || boundaryRefreshing) && (
                  <span
                    className="toolbar-busy-chip"
                    role="status"
                    title={schemaRefreshBusy ? 'Saving…' : 'Checking…'}
                  >
                    <LoaderCircle className="spin" size={13} />
                  </span>
                )}
                <div className="batch-page-search">
                  <TooltipButton
                    className={`page-find-trigger ${searchOpen ? 'active' : ''}`}
                    onClick={() => setSearchOpen((current) => !current)}
                    tooltip="Shortcut: Ctrl+F"
                    aria-label="Find on this page"
                    aria-expanded={searchOpen}
                  >
                    <TextSearch size={16} />
                    <span>Find</span>
                  </TooltipButton>
                  {searchOpen && (
                    <div className="batch-search-popover page-find">
                      <div className="batch-search-input">
                        <TextSearch size={14} />
                        <input
                          autoFocus
                          value={searchQuery}
                          onChange={(event) => setSearchQuery(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              moveFind(event.shiftKey ? -1 : 1);
                            }
                            if (event.key === 'Escape') {
                              event.preventDefault();
                              closeFind();
                            }
                          }}
                          placeholder="Find on this page"
                          aria-label="Find on this page"
                        />
                        <small>
                          {searchQuery
                            ? `${findMatches.length ? findIndex + 1 : 0}/${findMatches.length}`
                            : ''}
                        </small>
                        <button
                          onClick={() => moveFind(-1)}
                          disabled={!findMatches.length}
                          aria-label="Previous match"
                        >
                          <ChevronLeft size={13} />
                        </button>
                        <button
                          onClick={() => moveFind(1)}
                          disabled={!findMatches.length}
                          aria-label="Next match"
                        >
                          <ChevronRight size={13} />
                        </button>
                        <button onClick={closeFind} aria-label="Close find">
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="zoom-control">
                  <TooltipButton
                    className="zoom-step"
                    onClick={() => applyZoom((readerMode ? readerScale : zoom) / 1.2)}
                    tooltip="Zoom out"
                    aria-label="Zoom out"
                  >
                    <Minus size={13} />
                  </TooltipButton>
                  <TooltipButton
                    className={zoomMenuOpen ? 'active' : ''}
                    onClick={() => setZoomMenuOpen((current) => !current)}
                    tooltip="Zoom and fit"
                    aria-label="Zoom and fit"
                    aria-expanded={zoomMenuOpen}
                  >
                    <strong className="zoom-readout">
                      {Math.round((readerMode ? readerScale : zoom) * 100)}%
                    </strong>
                  </TooltipButton>
                  <TooltipButton
                    className="zoom-step"
                    onClick={() => applyZoom((readerMode ? readerScale : zoom) * 1.2)}
                    tooltip="Zoom in"
                    aria-label="Zoom in"
                  >
                    <Plus size={13} />
                  </TooltipButton>
                  {zoomMenuOpen && (
                    <div className="viewer-menu-popover zoom-popover">
                      <label className="zoom-slider">
                        <input
                          type="range"
                          min={readerMode ? 35 : 8}
                          max={readerMode ? 300 : 500}
                          value={Math.round((readerMode ? readerScale : zoom) * 100)}
                          onChange={(event) => applyZoom(Number(event.target.value) / 100)}
                          aria-label="Zoom level"
                        />
                      </label>
                      <button
                        onClick={() => {
                          resetView();
                          setZoomMenuOpen(false);
                        }}
                      >
                        <Minimize2 size={14} /> Fit page <small>Ctrl+0</small>
                      </button>
                      <button
                        onClick={() => {
                          fitWidth();
                          setZoomMenuOpen(false);
                        }}
                      >
                        <MoveHorizontal size={14} /> Fit width
                      </button>
                      <button
                        onClick={() => {
                          fitHeight();
                          setZoomMenuOpen(false);
                        }}
                      >
                        <MoveVertical size={14} /> Fit height
                      </button>
                      <button
                        onClick={() => {
                          applyZoom(1);
                          setZoomMenuOpen(false);
                        }}
                      >
                        <Maximize2 size={14} /> Actual size
                      </button>
                    </div>
                  )}
                </div>
                {/* Turn page (owner-requested): the one image tool. No crop, straighten, brightness or flip, on purpose. */}
                <TooltipButton
                  className="turn-page"
                  data-telemetry="page-turn"
                  onClick={() => turnPageShortcutRef.current()}
                  disabled={batch.batchId.startsWith('live:') || !document.pages[selectedPage]}
                  tooltip="Turn page · R"
                  aria-label="Turn page"
                >
                  <RotateCwSquare size={14} />
                </TooltipButton>
                {/* One item behind an overflow is a click charged for nothing. */}
                {hostHas('detached-panels') && (
                  <TooltipButton
                    onClick={() => detachPanel('viewer')}
                    tooltip="Move to another window"
                    aria-label="Move the viewer to another window"
                  >
                    <ExternalLink size={14} />
                  </TooltipButton>
                )}
              </div>
            </div>
          )}
        </div>
        {!detachedPanels.viewer && !doneStage && (
          <section
            className={`document-stage${beatHere ? ' confirm-beat-on' : ''}`}
            data-review-pane="page"
          >
            {viewerRereading && (
              <span className="page-turn-rereading" role="status">
                <RotateCwSquare size={12} aria-hidden="true" />
                Re-reading
              </span>
            )}
            {beatHere && (
              <div className="confirm-beat" role="status">
                <span className="confirm-beat-pill">
                  <Check size={26} aria-hidden="true" />
                  Done
                </span>
              </div>
            )}
            <div
              ref={viewerRef}
              tabIndex={-1}
              role="region"
              aria-label="Document viewer"
              className={`paper-canvas ${readerMode ? 'reader-mode' : ''} ${isPanning ? 'panning' : ''}`}
              onPointerDown={beginPan}
              onPointerMove={movePan}
              onPointerUp={endPan}
              onPointerCancel={endPan}
              onContextMenu={(event) => {
                if (suppressContextMenu.current) {
                  event.preventDefault();
                  suppressContextMenu.current = false;
                }
              }}
            >
              {readerMode ? (
                <ReaderVirtualFlow
                  key={document.id}
                  paths={document.pages.map((page) =>
                    pageTurnImage(
                      pageTurns.get(pageTurnKey(document.id, page.number)),
                      page.previewPath,
                    ),
                  )}
                  selected={selectedPage}
                  scale={readerScale}
                  leading={readerLeading}
                  trailing={readerTrailing}
                  renderPage={(index, width, onSize, height) => {
                    const shownPage = document.pages[index];
                    const page = {
                      ...shownPage,
                      previewPath: pageTurnImage(
                        pageTurns.get(pageTurnKey(document.id, shownPage.number)),
                        shownPage.previewPath,
                      ),
                    };
                    return (
                      <Fragment>
                        {index > 0 && (
                          <ReaderPageBreak
                            pageNumber={index + 1}
                            batchPageNumber={page.number}
                            pending={boundaryPendingByPage.get(page.number)}
                            notice={joinNewsHere?.page === page.number ? joinNewsLine : undefined}
                            onSplit={() => splitDocumentAt(selectedDocument, index)}
                          />
                        )}
                        <ReaderPage
                          documentId={document.id}
                          documentMutationToken={currentDocumentMutationToken!}
                          page={page}
                          index={index}
                          width={width}
                          onSize={(w, h) => {
                            onSize(w, h);
                            const pending = pendingReaderEvidence.current;
                            if (
                              pending?.documentId === document.id &&
                              pending.pageIndex === index
                            ) {
                              pendingReaderEvidence.current = null;
                              if (pending.revision === readerSelectionSync.current.revision)
                                window.requestAnimationFrame(() => pending.apply(h));
                            }
                          }}
                          height={height}
                          evidenceLines={
                            index === selectedPage
                              ? displayedEvidenceLines
                              : (pinnedEvidenceByPage.get(page.number) ?? [])
                          }
                          evidenceWords={
                            index === selectedPage
                              ? displayedEvidenceWords
                              : citedWordIndexes(
                                  page.words,
                                  pinnedCitationsByPage.get(page.number) ?? [],
                                )
                          }
                          barcodeEvidence={index === selectedPage ? displayedBarcodeEvidence : []}
                          markEvidence={index === selectedPage ? displayedMarkEvidence : noMarks}
                          fieldLinkSignature={fieldLinkSignature}
                          defaultPageNumber={document.pages[0]?.number}
                          hoverEvidence={hoverEvidenceByPage.get(page.number) ?? []}
                          fieldIdForLine={fieldIdForLine}
                          fieldIdForBarcode={fieldIdForBarcode}
                          onHoverField={setHoveredFieldId}
                          onBarcodeMenu={openViewerSelectionMenu}
                          onMarkTable={
                            markingTableFor === document.id && document.lineItems.length === 0
                              ? (region) => void findTable(region)
                              : null
                          }
                          onCancelMarkTable={() => setMarkingTableFor(null)}
                          quarterTurns={pageTurnShown(
                            pageTurns.get(pageTurnKey(document.id, page.number)),
                            page.previewPath,
                          )}
                        />
                      </Fragment>
                    );
                  }}
                />
              ) : preview ? (
                <div
                  ref={pageStackRef}
                  className={`page-image-stack ${easingView ? 'easing' : ''}`}
                  style={{
                    transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})${viewerTurn ? ` ${viewerTurn}` : ''}`,
                  }}
                >
                  <img
                    className="page-preview"
                    src={preview}
                    draggable={false}
                    alt={`Page ${selectedPage + 1} of ${document.sourceName}`}
                    onLoad={(event) => {
                      const size = {
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                      };
                      // The same page swapped in at the same size (a turn's new image, ending where it was shown) keeps the view as it is.
                      if (
                        size.width === previewSizeRef.current.width &&
                        size.height === previewSizeRef.current.height
                      )
                        return;
                      setPreviewSize(size);
                      window.requestAnimationFrame(() => fitView(size));
                    }}
                  />
                  {previewSize.width > 1 && previewImagePath === previewPath && (
                    <>
                      <OcrTextLayer
                        documentId={document.id}
                        documentMutationToken={currentDocumentMutationToken!}
                        page={document.pages[selectedPage]}
                        size={previewSize}
                        evidenceLines={displayedEvidenceLines}
                        evidenceWords={displayedEvidenceWords}
                        barcodeEvidence={displayedBarcodeEvidence}
                        markEvidence={displayedMarkEvidence}
                        fieldLinkSignature={fieldLinkSignature}
                        fieldIdForLine={fieldIdForLine}
                        fieldIdForBarcode={fieldIdForBarcode}
                        onHoverField={setHoveredFieldId}
                        onBarcodeMenu={openViewerSelectionMenu}
                      />
                      <HoverEvidenceLayer
                        page={document.pages[selectedPage]}
                        size={previewSize}
                        defaultPageNumber={document.pages[0]?.number}
                        evidence={hoverEvidence}
                      />
                      {markingTableFor === document.id &&
                        document.lineItems.length === 0 &&
                        document.pages[selectedPage] && (
                          <TableMarkLayer
                            pageNumber={document.pages[selectedPage].number}
                            size={previewSize}
                            onMarked={(region) => void findTable(region)}
                            onCancel={() => setMarkingTableFor(null)}
                          />
                        )}
                    </>
                  )}
                </div>
              ) : (
                <LoaderCircle className="preview-loader spin" size={28} />
              )}
            </div>

            {correctionsOpen && (
              <CorrectionsPanel
                failed={correctionsFailed}
                onRetry={openCorrections}
                corrections={corrections}
                loading={correctionsLoading}
                documents={batch.documents}
                onClose={() => setCorrectionsOpen(false)}
                onJump={jumpToCorrection}
              />
            )}
            {selectionMenu && (
              <SelectionActions
                key={`${selectionMenu.documentId}:${selectionMenu.pageNumber}:${selectionMenu.text}:${selectionMenu.x}:${selectionMenu.y}`}
                className="viewer-selection-menu"
                x={selectionMenu.x}
                y={selectionMenu.y}
                text={selectionMenu.text}
                expanded={!selectionMenu.automatic}
                // Not while the document is being confirmed or retyped: a value picked then was dropped
                // and the old one sealed.
                fields={configuredFields.filter((item) => item.kind !== 'line-items')}
                disabled={
                  fieldSaving ||
                  schemaRefreshBusy ||
                  confirmingSelected ||
                  typeSavingDocumentId === document.id
                }
                onCopy={() => {
                  void window.capturePointDesktop?.copyText(
                    selectionMenu.copyText ?? selectionMenu.text,
                  );
                  setSelectionMenu(null);
                }}
                onAssign={(id) => {
                  const field = configuredFields.find((item) => item.id === id);
                  if (field) persistViewerSelection(field);
                }}
                onClose={() => setSelectionMenu(null)}
              />
            )}
          </section>
        )}
        {/* Nothing left asked about: the designed caught-up scene, not the last document as though it
            were still work (owner, 2026-10-05). Choosing any document opens it, the last one with its Undo. */}
        {doneStage && (
          <section className="review-done-stage" data-review-pane="page">
            <CaughtUpHero
              key={job.id}
              jobId={job.id}
              onNotice={onNotice}
              confirmed={confirmedInVisit.size}
            />
          </section>
        )}
        <div className="review-content" hidden={doneStage}>
          {!detachedPanels.fields && (
            <aside
              className={`results-panel field-workbench ${sourceReview ? 'source-review-fields' : ''}`}
              data-review-pane="fields"
            >
              {/* Paige: a colleague's document, or a save Paige refused (ADR-0009). */}
              <ReviewLeaseNotice lease={reviewLease} />
              {/* Paige: who sent this from a phone, when and where (docs/engineering/mobile-capture.md). */}
              <CaptureOrigin key={document?.id ?? 'none'} documentId={document?.id ?? null} />
              <ExportedOnceNotice />
              {/* PAIGE: already exported, and what a later check found (flagged-after-export.ts). */}
              <FlaggedAfterExportNotice
                document={document}
                schema={documentSchema}
                policy={job.reviewPolicy}
                exported={flaggedAfterExport.exported(document)}
                regrouped={flaggedAfterExport.regrouped(document)}
                sendsTo={flaggedAfterExport.sendsTo(document)}
              />
              <div
                className="results-resizer"
                role="separator"
                tabIndex={0}
                aria-label="Resize fields pane"
                aria-orientation="vertical"
                aria-valuemin={270}
                aria-valuemax={paneLayout.fieldsMax}
                aria-valuenow={paneLayout.fields}
                onPointerDown={beginResultsResize}
                onPointerMove={moveResultsResize}
                onPointerUp={endResultsResize}
                onPointerCancel={endResultsResize}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                  event.preventDefault();
                  setResultsWidth(
                    Math.max(
                      270,
                      Math.min(
                        paneLayout.fieldsMax,
                        paneLayout.fields + (event.key === 'ArrowLeft' ? 20 : -20),
                      ),
                    ),
                  );
                }}
              />
              {/* Why it waits heads the panel; what to check is counted on the Fields heading. */}
              {!awaitingUnderstanding(document) && toCheckItems.length === 0 && (
                <WaitingLine waiting={whyDocumentWaits(document, job)} />
              )}
              {/* On its way to the exceptions folder (owner, 2026-10-31): a neutral note with the reason, nothing to do. */}
              {!awaitingUnderstanding(document) &&
                toCheckItems.length === 0 &&
                exceptionsRouteOf(document) && (
                  <p className="review-exceptions-note" role="note">
                    <FolderOutput size={14} aria-hidden="true" />
                    <span>
                      <b>Goes to exceptions</b> · {exceptionsRouteOf(document)!.reasonText}
                    </span>
                  </p>
                )}
              <div className="results-heading">
                <div className="field-workbench-type">
                  <span className="field-workbench-label">Document type</span>
                  <div
                    className={`document-type-control ${typeReviewReasons.length > 0 ? 'needs-review' : ''} ${arrivedFieldId === 'document-type' ? 'review-arrived' : ''} ${typePromptDocumentId === document.id ? 'type-wanted' : ''}`}
                  >
                    <div>
                      <select
                        ref={documentTypeSelectRef}
                        value={shownType?.id ?? ''}
                        onChange={(event) =>
                          event.target.value === '__new__'
                            ? setAddingTypeName('')
                            : event.target.value.startsWith('__found__')
                              ? void addTypeAndAssign(event.target.value.slice('__found__'.length))
                              : void setDocumentType(event.target.value)
                        }
                        disabled={
                          schemaRefreshBusy ||
                          typeSavingDocumentId === document.id ||
                          confirmingSelected
                        }
                        aria-label="Document type"
                      >
                        {/* The reader has usually already said what this is. During a job's first read it says so on the left — "D1 Form W-9" — while the job itself still has no configured types, so this control said "Choose document type" over an empty list about a document whose kind was on screen a few inches away. Only while no type is chosen: once one is, its name is already in the list, and this entry showed it a second time, greyed out at the top. */}
                        {!shownType && (
                          <option value="" disabled hidden={readerTypeNameListed}>
                            {readerTypeName || 'Choose document type'}
                          </option>
                        )}
                        {job.documentTypes.map((type) => (
                          <option value={type.id} key={type.id}>
                            {type.name}
                          </option>
                        ))}
                        {foundTypeNames.map((name) => (
                          <option value={`__found__${name}`} key={`found-${name}`}>
                            {name}
                          </option>
                        ))}
                        <option value="__new__">+ Add a document type…</option>
                      </select>
                      {/* No Confirm type (single confirm, 2026-10-05): the type is confirmed with its
                      document. What it learned still says so. */}
                      {retypingDocumentId !== document.id &&
                        !document.documentTypeVerified &&
                        documentSchema &&
                        document.documentTypeTrust === TYPE_TRUST_AUTHORIZED_LEARNED && (
                          <span className="type-confirmation-state">Type recognized</span>
                        )}
                    </div>
                    {/* Why, always shown under the box, as a field says it (owner, 2026-10-14). */}
                    {typeReviewReasons.length > 0 && !awaitingUnderstanding(document) && (
                      <div className="type-review-reasons">
                        {typeReviewReasons.map((reason, index) => (
                          <small className="type-review-reason" key={`type-issue-${index}`}>
                            {reason.label}
                          </small>
                        ))}
                        {guessedTurnPage !== null && (
                          <button
                            type="button"
                            data-telemetry="page-turn-guessed"
                            disabled={pageTurnBusy(
                              pageTurns.get(pageTurnKey(document.id, guessedTurnPage)),
                            )}
                            onClick={() => {
                              const pageIndex = document.pages.findIndex(
                                (page) => page.number === guessedTurnPage,
                              );
                              if (pageIndex >= 0 && pageIndex !== selectedPage)
                                selectDocument(selectedDocument, pageIndex);
                              void turnPage(document.id, guessedTurnPage);
                            }}
                          >
                            Turn it
                          </button>
                        )}
                      </div>
                    )}
                    {addingTypeName !== null && (
                      <span className="add-type-inline">
                        <input
                          autoFocus
                          value={addingTypeName}
                          placeholder="e.g. Purchase Order"
                          aria-label="New document type name"
                          disabled={schemaRefreshBusy}
                          onChange={(event) => setAddingTypeName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && addingTypeName.trim()) {
                              const name = addingTypeName;
                              setAddingTypeName(null);
                              void addTypeAndAssign(name);
                            }
                            if (event.key === 'Escape') setAddingTypeName(null);
                          }}
                        />
                        <TooltipButton
                          className="icon-button"
                          tooltip="Add type"
                          aria-label="Add as a new type"
                          disabled={schemaRefreshBusy || !addingTypeName.trim()}
                          onClick={() => {
                            const name = addingTypeName;
                            setAddingTypeName(null);
                            void addTypeAndAssign(name ?? '');
                          }}
                        >
                          <Check size={13} />
                        </TooltipButton>
                        <button
                          className="icon-button"
                          aria-label="Cancel"
                          onClick={() => setAddingTypeName(null)}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    )}
                    {retypingDocumentId === document.id ? (
                      <small className="type-rereading">
                        <LoaderCircle className="spin" size={11} /> Re-reading as{' '}
                        {retypingAs ?? document.documentType}
                      </small>
                    ) : typePromptDocumentId === document.id ? (
                      <small className="type-wanted-note">Choose a type for the new document</small>
                    ) : extractionFailure ? null : (
                      !documentSchema && (
                        <small>
                          {job.documentTypes.length === 0 && document.documentType?.trim()
                            ? 'Fields arrive after setup'
                            : 'Choose the closest match'}
                        </small>
                      )
                    )}
                  </div>
                </div>
                {sourceReview && (
                  <DocumentTypePreview document={document} token={currentDocumentMutationToken!} />
                )}
                <div className="results-heading-tools">
                  <span className="field-history-tools">
                    {canDeleteDocuments && (
                      <TooltipButton
                        className="icon-button document-delete"
                        data-telemetry="document-delete"
                        disabled={deleteUnavailable}
                        aria-busy={deletingDocumentId === document.id || undefined}
                        onClick={() => void deleteDocument(document.id)}
                        aria-label="Delete document"
                        tooltip="Delete document"
                      >
                        {deletingDocumentId === document.id ? (
                          <LoaderCircle className="spin" size={17} />
                        ) : (
                          <Trash2 size={17} />
                        )}
                      </TooltipButton>
                    )}
                    <TooltipButton
                      className="icon-button"
                      onClick={openCorrections}
                      aria-label="Your corrections"
                      tooltip="Your corrections"
                    >
                      <History size={19} />
                    </TooltipButton>
                    {hostHas('detached-panels') && (
                      <TooltipButton
                        className="icon-button"
                        onClick={() => detachPanel('fields')}
                        aria-label="Move fields to another window"
                        tooltip="Move to another window"
                      >
                        <ExternalLink size={15} />
                      </TooltipButton>
                    )}
                  </span>
                </div>
              </div>
              <ManualExportAction
                key={job.id}
                jobId={job.id}
                onNotice={onNotice}
                onRefresh={onRestored}
              />
              <div className="recognized-fields">
                {pageAuditNotes.map((finding, index) => (
                  <div className="document-audit-note" key={`${finding.code}-${index}`}>
                    <Info size={13} />
                    <span>
                      {findingWords(finding.message) /* PAIGE: Paige's words (finding-words.ts) */}
                    </span>
                  </div>
                ))}
                {/* An unexplained saved review flag has a quiet footer recheck action.
              Do not insert a warning above these editors while confirmations settle. */}
                {!awaitingUnderstanding(document) &&
                  currentReviewReasons
                    .filter((reason) => reason.boundary)
                    .map((reason, index) => (
                      /* A yes-or-no question deserves one line, not an expander, a list
               item, and a separate confirmation row below it. */
                      <div
                        className="boundary-inline-question"
                        role="group"
                        aria-label="Document boundary question"
                        key={`boundary-${index}`}
                      >
                        <Scissors size={13} />
                        <button
                          className="boundary-question-peek"
                          onClick={() => goToReviewItem(reason)}
                        >
                          Does this document start here?
                        </button>
                        <button
                          className="boundary-deny"
                          data-telemetry="document-join"
                          onClick={() => void mergeWithPrevious(selectedDocument)}
                          disabled={selectedDocument === 0}
                        >
                          Join previous
                        </button>
                      </div>
                    ))}
                {/* ADR-0091: a file kept whole that looks like several documents. One line, two answers, both truth. */}
                {!awaitingUnderstanding(document) &&
                  (() => {
                    const suggestion = sourceSplitSuggestion(document);
                    if (!suggestion || suggestion.keptByLesson) return null;
                    return (
                      <div
                        className="boundary-inline-question source-split-suggestion"
                        role="group"
                        aria-label="Split suggestion"
                      >
                        <Scissors size={13} />
                        <span>This file looks like {suggestion.documents} documents.</span>
                        <button
                          className="boundary-split-accept"
                          data-telemetry="source-split-accept"
                          onClick={() => splitAtSuggestedStarts(selectedDocument)}
                        >
                          Split
                        </button>
                        <button
                          className="boundary-keep"
                          data-telemetry="source-split-keep"
                          onClick={() => void keepSuggestedFileWhole(selectedDocument)}
                        >
                          Keep as one
                        </button>
                      </div>
                    );
                  })()}
                {expectsLineItems && detachedPanels['line-items'] && tableReviewDetail(false)}
                {/* About the whole document: said, always shown, with the page one click away. */}
                {!awaitingUnderstanding(document) &&
                  otherReviewReasons.map((reason, index) => (
                    <div className="document-review-reason" key={`document-issue-${index}`}>
                      <p className="review-reason-line">{reason.label}</p>
                      <button
                        type="button"
                        className="review-reason-action"
                        onClick={() => {
                          paneLayout.revealPane('page');
                          window.requestAnimationFrame(() =>
                            window.document
                              .querySelector<HTMLElement>('.paper-canvas')
                              ?.scrollIntoView({ block: 'nearest' }),
                          );
                        }}
                      >
                        Review page
                      </button>
                    </div>
                  ))}
                {/* Where the checklist was.

              The checklist is the one thing the operator is looking at, and the
              moment they answer its last item it unmounts and leaves a hole.
              The emptiest place on the screen was also the highest-intent
              moment: they have just finished something, they are ready to work,
              and the only way on was a red thumbnail nine hundred pixels to the
              left that nothing had ever taught them to look at.

              So the hole becomes the door. Same rectangle, no eye travel, and it
              names the document it leads to rather than saying "next" — a
              doorway rather than a void. The count gives the session an end,
              which a spine full of red never does.

              Only while the operator is working the review list. With the filter
              off they are browsing, and offering to move them on is an answer to
              a question they did not ask. */}
                {/* Never says "done" over the flag's head.

              Gating this on locally computed reasons alone was the same mistake
              in a new place. The reasons are worked out from the document in
              hand, and a document in hand is not always the whole document — a
              projected copy carries no line items, so the arithmetic complaint
              cannot be found and the panel concluded there was nothing wrong. The
              engine said the document needed review, the spine drew it red, and
              this announced it was finished. needsReview has one owner and this
              is not it, so it asks. */}
                {retypingDocumentId === document.id && (
                  /* A retype leaves the panel between types for a few seconds. Say
               what is happening instead of inviting the operator onward —
               "done" over a panel of empty fields read as "accept whatever
               comes", which is not what anyone said. */
                  <div className="review-advance retype-pending" role="status">
                    <span className="review-advance-row">
                      <LoaderCircle className="spin" size={14} />
                      <span>
                        <strong>Re-reading as {retypingAs ?? document.documentType}</strong>
                        <small>
                          the fields below fill in as they are read · nothing is accepted for you
                        </small>
                      </span>
                    </span>
                  </div>
                )}

                {/* WHAT IT READ, BEFORE THE SETUP IS SAVED.
              A job's first documents are understood before the job has any
              configured types, so documentSchema is undefined and the field
              column — which is built from the job's schema — is empty. The
              operator sees "CreditMemo" beside a blank panel on every single
              new job, which is the first thing they ever see this product do.
              The values exist: they are on the document, they are what the
              proposal will be built from. They are shown here as a reading
              rather than as inputs, because until the setup is saved there is
              no field to write a correction back to. */}
                {!documentSchema &&
                  (document.fields ?? []).some(
                    (field) => field.name !== 'Document type' && String(field.value ?? '').trim(),
                  ) && (
                    <div className="proposed-reading">
                      <small>Read from this document · kept when you save the setup</small>
                      <dl>
                        {(document.fields ?? [])
                          .filter(
                            (field) =>
                              field.name !== 'Document type' && String(field.value ?? '').trim(),
                          )
                          .slice(0, 12)
                          .map((field) => (
                            <div key={field.name}>
                              <dt>{field.name}</dt>
                              <dd>{field.value}</dd>
                            </div>
                          ))}
                      </dl>
                    </div>
                  )}
                <div className="results-notices">
                  {supersededByMerge.length > 0 &&
                    supersededByMerge[0].documentId === document.id && (
                      <div className="merge-superseded" role="status">
                        <TriangleAlert size={14} />
                        <div>
                          <strong>
                            {supersededByMerge.length === 1
                              ? 'A correction was replaced by this merge'
                              : `${counted(supersededByMerge.length, 'correction')} were replaced by this merge`}
                          </strong>
                          {supersededByMerge.map((item) => (
                            <button
                              key={`${item.fieldName}-${item.discarded}`}
                              onClick={() => {
                                if (item.fieldId) setFocusFieldId(item.fieldId);
                              }}
                              title={item.fieldId ? 'Go to the field' : undefined}
                            >
                              {item.fieldName} kept “{item.kept}” · your “{item.discarded}” was
                              replaced
                            </button>
                          ))}
                        </div>
                        <button
                          className="icon-button"
                          onClick={() => setSupersededByMerge([])}
                          aria-label="Dismiss replaced correction notice"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    )}
                  {/* Said, not spun.

                This work retypes documents and takes them out of the review queue,
                and it ran behind a bare spinner. An operator watched their queue
                shorten by fourteen with no sentence anywhere explaining it, which
                is worse than the work still being there: work that vanishes without
                a reason is work you have to go and check. */}

                  {/* Only a DISAGREEMENT earns a card. When the suggestion matches the
                document's current type, this card was a second confirm control
                beside the header's — and its "Confirm" did not even verify, so
                the operator's click bought less than the one next to it. The
                agreement's evidence now rides the header CTA's own sub-line. */}
                  {typeSuggestion &&
                    !document.documentTypeVerified &&
                    document.documentType.localeCompare(typeSuggestion.suggestedType, undefined, {
                      sensitivity: 'accent',
                    }) !== 0 && (
                      <div className="type-suggestion">
                        <WandSparkles size={14} />
                        <span>
                          <small>Pattern noticed</small>
                          <strong>{typeSuggestion.suggestedType}</strong>
                        </span>
                        <div className="suggestion-actions">
                          <button
                            onClick={() => {
                              const suggested = job.documentTypes.find(
                                (type) =>
                                  type.name.localeCompare(typeSuggestion.suggestedType, undefined, {
                                    sensitivity: 'accent',
                                  }) === 0,
                              );
                              if (suggested) void setDocumentType(suggested.id);
                            }}
                            disabled={schemaRefreshBusy || typeSavingDocumentId === document.id}
                          >{`Change to ${typeSuggestion.suggestedType}`}</button>
                          <button
                            className="icon-button"
                            onClick={() => dismissSuggestion('now')}
                            title="Not now"
                            aria-label="Dismiss suggestion"
                          >
                            <X size={12} />
                          </button>
                          <div className="suggestion-menu">
                            <TooltipButton
                              className="icon-button"
                              onClick={() => setSuggestionMenuOpen((current) => !current)}
                              aria-label="Suggestion options"
                              tooltip="More"
                            >
                              <MoreHorizontal size={13} />
                            </TooltipButton>
                            {suggestionMenuOpen && (
                              <div>
                                <button onClick={() => dismissSuggestion('today')}>
                                  Do not suggest today
                                </button>
                                <button onClick={() => dismissSuggestion('never')}>
                                  Never suggest this change
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  {notReadReason && pagesNeverRead.length > 0 && (
                    <div className="extraction-failure document-not-read">
                      <TriangleAlert size={16} />
                      <span>
                        <strong>
                          {pagesNeverRead.length === 1
                            ? `Page ${pagesNeverRead[0].number} was not read`
                            : `${pagesNeverRead.length} pages were not read`}
                        </strong>
                        <small>
                          Its empty fields may not really be empty. Read it again first.
                        </small>
                        <TechnicalDetails detail={notReadReason} />
                      </span>
                      <button
                        data-telemetry="read-again"
                        disabled={processing}
                        onClick={() => void retryExtraction(document.id)}
                      >
                        <RotateCw size={14} /> Read it again
                      </button>
                    </div>
                  )}
                  {/* The field reader itself can't read (not installed, or failing its own test read): said plainly,
                the exact error behind Technical details, and no promise of retries that can't succeed. */}
                  {notReadReason &&
                    pagesNeverRead.length === 0 &&
                    retypingDocumentId !== document.id &&
                    // PAIGE: the reader is Paige's own, never installed here, and the desktop's background retry is
                    // not run (`not-read-retry`): Paige reads it again when its review unit comes back (NotReadReads),
                    // and keeps its Not read yet card with Read it again.
                    (hostHas('not-read-retry') && isReaderProblem(document.notReadCause) ? (
                      <div
                        className="extraction-type-needed document-not-read reader-problem"
                        role="status"
                      >
                        {!touring && (
                          <ReaderProblemSignal jobId={job.id} cause={document.notReadCause} />
                        )}
                        <TriangleAlert size={16} />
                        <span>
                          <strong>{readerProblemWords(document.notReadCause).title}</strong>
                          <small>{readerProblemWords(document.notReadCause).line}</small>
                          <TechnicalDetails detail={notReadReason} />
                        </span>
                      </div>
                    ) : hostHas('not-read-retry') ? (
                      <div className="extraction-type-needed document-not-read" role="status">
                        <RotateCw size={16} className="spin" />
                        <span>
                          <strong>Reading this document again automatically</strong>
                          <small>
                            The first reading didn’t finish. Paige retries on its own; your pages
                            and saved corrections are kept.
                          </small>
                        </span>
                      </div>
                    ) : (
                      // Paige reads it again from its review unit (NotReadReads), not CapturePoint's
                      // background pass: the document says so and offers the read now.
                      <div className="extraction-type-needed document-not-read" role="status">
                        <Info size={16} />
                        <span>
                          <strong>Not read yet</strong>
                          <small>
                            {notReadReason} Paige reads it again on its own; your pages and saved
                            corrections are kept.
                          </small>
                        </span>
                        {job.documentTypes.length > 0 ? (
                          <button
                            type="button"
                            disabled={processing}
                            onClick={() => void retryExtraction(document.id)}
                          >
                            {processing ? 'Reading…' : 'Read it again'}
                          </button>
                        ) : (
                          <small>
                            Cortexa reads it once the job’s setup has its document types.
                          </small>
                        )}
                      </div>
                    ))}
                  {extractionFailure &&
                    !notReadReason &&
                    (extractionNeedsType ? (
                      <div className="extraction-type-needed" role="status">
                        <Info size={20} />
                        <span>
                          <strong>This document needs another read</strong>
                          <small>
                            An earlier reading could not identify it. Retry this document to read it
                            again.
                          </small>
                        </span>
                        <button
                          type="button"
                          disabled={processing}
                          onClick={() => void retryExtraction(document.id)}
                        >
                          {processing ? 'Reading…' : 'Retry this document'}
                        </button>
                      </div>
                    ) : (
                      <div
                        className="extraction-failure"
                        role="alert"
                        data-telemetry="error:fields-not-read"
                      >
                        <TriangleAlert size={16} />
                        <span>
                          <strong>Fields could not be read</strong>
                          <small>Your pages are safe.</small>
                          <details>
                            <summary>What happened?</summary>
                            {extractionFailure.message}
                          </details>
                        </span>
                        <button onClick={() => void retryExtraction(document.id)}>
                          <RotateCw size={14} /> Retry
                        </button>
                      </div>
                    ))}
                </div>
                {/* Suggestion receipts retain their existing Undo/Redo actions in
                the workspace; they are independent of the retired learning dialog. */}
                {activeReceipt === 'improvement' && lastJobImprovement && (
                  <div
                    className={`job-improvement-applied ${receiptOffer && !jobImprovementUndone ? 'with-offer' : ''}`}
                  >
                    {jobImprovementUndone ? <Undo2 size={13} /> : <CheckCircle2 size={13} />}
                    <span>
                      <small>
                        {jobImprovementUndone ? 'Change undone' : 'Job improved'} ·{' '}
                        {lastJobImprovementOutcome === 'refreshed'
                          ? 'safe work refreshed'
                          : lastJobImprovementOutcome === 'future-only'
                            ? 'new pages only'
                            : lastJobImprovementOutcome === 'queued'
                              ? 'earlier files catch up automatically'
                              : 'corrections kept'}
                      </small>
                      <strong>{lastJobImprovement.title}</strong>
                      {/* The money case, closed. Since D10 a type applied from a suggestion
                    arrives with the proposed fields the person kept, and with Fields: [] only
                    when none were kept; then a receipt that stopped at "8 documents filed"
                    had moved the money from recorded with the wrong sign to not recorded at
                    all. For that fieldless case this names the next step in the operator's own language, and it is
                    drawn from the job as it stands rather than from the card, so it stops
                    saying it the moment the type has a field. */}
                      {!jobImprovementUndone &&
                        lastJobImprovement.kind === SUGGESTION.documentType &&
                        job.documentTypes.some(
                          (type) =>
                            type.name.localeCompare(
                              lastJobImprovement.proposedLabel ?? '',
                              undefined,
                              { sensitivity: 'accent' },
                            ) === 0 && type.fields.length === 0,
                        ) && (
                          <em className="job-improvement-next">
                            {pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed'
                              ? // Autopilot holds nothing for a person, so a type with no fields sends its documents out without values (D10).
                                'It has no fields yet, so its documents go out without values until you add some.'
                              : 'It has no fields yet. Confirm one and Paige will suggest them.'}
                          </em>
                        )}
                      {/* With the fields kept from its proposal (D10): its documents are read for them before they go on.
                    Only for a type this apply added: a name the engine filed under an existing type adds no fields. */}
                      {!jobImprovementUndone &&
                        lastJobImprovement.kind === SUGGESTION.documentType &&
                        (() => {
                          const addedTypeId = applyChoiceFor(lastJobImprovement.id)?.addedTypeId;
                          const added = addedTypeId
                            ? job.documentTypes.find((type) => type.id === addedTypeId)
                            : undefined;
                          return added &&
                            added.fields.length > 0 &&
                            applyChoiceFor(lastJobImprovement.id)?.keptFieldNames?.length ? (
                            <em className="job-improvement-next">
                              {newTypeFieldsReceipt(added.fields.map((field) => field.name))}
                            </em>
                          ) : null;
                        })()}
                    </span>
                    {isJobAdjustment(lastJobImprovement) && (
                      <button
                        onClick={() =>
                          void (jobImprovementUndone
                            ? applyJobSuggestion(
                                lastJobImprovement,
                                lastJobImprovementScope,
                                applyChoiceFor(lastJobImprovement.id)?.proposedName,
                                applyChoiceFor(lastJobImprovement.id)?.excludedDocumentIds,
                                applyChoiceFor(lastJobImprovement.id)?.keptFieldNames,
                              )
                            : undoJobSuggestion(lastJobImprovement, lastJobImprovementScope))
                        }
                        disabled={jobSuggestionBusy === lastJobImprovement.id}
                      >
                        {jobSuggestionBusy === lastJobImprovement.id ? (
                          <LoaderCircle className="spin" size={12} />
                        ) : jobImprovementUndone ? (
                          <RotateCw size={12} />
                        ) : (
                          <Undo2 size={12} />
                        )}{' '}
                        {jobImprovementUndone ? 'Redo' : 'Undo'}
                      </button>
                    )}
                    {receiptOffer && !jobImprovementUndone && (
                      <button
                        className="jobstance-offer"
                        onClick={() => void changeStance(receiptOffer, new Date().toISOString())}
                        disabled={stanceBusy}
                      >
                        {stanceOfferLabel(receiptOffer)}
                      </button>
                    )}
                    <button
                      className="icon-button"
                      onClick={() => {
                        declineStanceOffer();
                        setLastJobImprovement(null);
                        setJobImprovementUndone(false);
                      }}
                      aria-label="Dismiss job improvement notice"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                {activeReceipt === 'stance' &&
                  stanceReceipt &&
                  (() => {
                    const copy = stanceReceiptCopy(
                      job.learningStance,
                      stanceReceipt.area,
                      stanceReceipt.action,
                    );
                    return (
                      <div className="job-improvement-applied jobstance-receipt" role="status">
                        <CheckCircle2 size={13} />
                        <span>
                          <strong>{copy.title}</strong>
                          {copy.detail && <small>{copy.detail}</small>}
                        </span>
                        <button
                          className="icon-button"
                          onClick={() => setStanceReceipt(null)}
                          aria-label="Dismiss notice"
                        >
                          <X size={12} />
                        </button>
                        {/* The buttons take their own line under the sentence. Measured in
                    the running product: on one line, in a 319px results column,
                    the sentence the receipt exists to say was crushed to 47px. */}
                        <span className="jobstance-receipt-actions">
                          <button onClick={() => void undoStance()} disabled={stanceBusy}>
                            {stanceBusy ? (
                              <LoaderCircle className="spin" size={12} />
                            ) : (
                              <Undo2 size={12} />
                            )}{' '}
                            Undo
                          </button>
                          {copy.chain && !stanceStopped(job.learningStance, copy.chain) && (
                            <button
                              className="jobstance-offer"
                              onClick={() =>
                                void changeStance(copy.chain!, new Date().toISOString())
                              }
                              disabled={stanceBusy}
                            >
                              {STANCE_CHAIN_LABEL}
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                {loadingDocumentId === document.id && (
                  <div className="document-detail-loading" role="status">
                    <LoaderCircle className="spin" size={13} />
                    <span>Loading fields and evidence…</span>
                  </div>
                )}
                {/* A type applied from a suggestion arrives with no fields on
                purpose — its fields are the operator's to shape. A blank list
                said nothing about that and left the money case half-closed:
                the documents had been moved out of the wrong type and into one
                that collects nothing. Empty states teach. */}
                <RetainedFieldDrafts
                  store={fieldDraftStore}
                  documentId={document.id}
                  fields={configuredFields}
                  availableDocumentIds={batch.documents.map((item) => item.id)}
                />
                <FieldsHeading
                  items={toCheckItems}
                  note={waitingNote(document, WAIT_REASON_WORDS)}
                  onGo={(index) => {
                    reviewItemCursor.current = { documentId: document.id, index };
                    goToReviewItem(currentReviewWork[index]);
                  }}
                />
                {fieldOrder.error && <p role="status">{fieldOrder.error}</p>}
                {documentSchema &&
                  configuredFields.filter((item) => item.kind !== 'line-items').length === 0 &&
                  loadingDocumentId !== document.id && (
                    <div className="recognized-fields-empty">
                      <span>
                        No fields yet. Confirm one of these documents and Paige will suggest what to
                        collect - or add one now.
                      </span>
                      {!inlineFieldSetupHere && (
                        <button
                          className="quiet-button"
                          onClick={() =>
                            setInlineFieldSetup({
                              documentId: document.id,
                              typeId: documentSchema.id,
                            })
                          }
                        >
                          <Plus size={13} /> Add field
                        </button>
                      )}
                    </div>
                  )}
                <FieldHoverRowEcho />
                {configuredFields
                  .filter((item) => item.kind !== 'line-items')
                  .map((item) => {
                    const fieldsArriving =
                      loadingDocumentId === document.id ||
                      (batch.workspaceProjection === 'paged' &&
                        !loadedDocumentIds.has(document.id));
                    if (retypingDocumentId === document.id)
                      return (
                        <label key={item.id} className="field-pending-row">
                          <span>
                            {item.name}
                            {item.required && <sup>*</sup>}
                          </span>
                          <div className="field-pending-slot">
                            <LoaderCircle className="spin" size={12} />
                            <span>Reading…</span>
                          </div>
                        </label>
                      );
                    // A paged document's fields arrive after it opens. A box shown before then took typing that the
                    // arriving values overwrote (found by Paige's review tests under load), so it waits for them.
                    if (fieldsArriving)
                      return (
                        <label key={item.id} className="field-pending-row">
                          <span>
                            {item.name}
                            {item.required && <sup>*</sup>}
                          </span>
                          <div className="field-pending-slot">
                            <LoaderCircle className="spin" size={12} />
                            <span>Loading…</span>
                          </div>
                        </label>
                      );
                    const recognized = values.get(item.name);
                    const barcodeValue = Boolean(
                      recognized?.barcodeEvidence ||
                      recognized?.source.toLowerCase().includes('barcode'),
                    );
                    const evidenceLinked = Boolean(
                      recognized?.value && (recognized.evidenceLineIndexes?.length || barcodeValue),
                    );
                    const operatorValue = recognized?.source.startsWith('operator');
                    const validationWarning = recognized?.validationStatus === 'warning';
                    const reviewReason = currentReviewReasons.find(
                      (reason) => reason.fieldId === item.id,
                    );
                    const withheld = isWithheldField(recognized);
                    // Amber is what needs a look, the things "N to check" counts (owner, 2026-10-14), its
                    // reason said under the box. A value the screen cannot point to on the page is said
                    // quietly, and fields that could not be read are said once, above them.
                    const fieldIssue = Boolean(validationWarning || reviewReason || withheld);
                    const unlocated =
                      !fieldIssue &&
                      !extractionFailure &&
                      Boolean(
                        recognized?.value &&
                        !evidenceLinked &&
                        !operatorValue &&
                        !recognized.verified,
                      );
                    const status =
                      recognized?.validationMessage ??
                      (withheld ? SET_ASIDE_REASON : undefined) ??
                      reviewReason?.label ??
                      'Check this value';
                    const pinned = pinnedFieldIds.includes(item.id);
                    return (
                      <FieldReviewRow
                        key={`${document.id}:${item.id}`}
                        reorder={fieldOrder.reorder(item)}
                        store={fieldDraftStore}
                        binding={fieldDraftBinding(item, recognized)}
                        value={recognized?.value ?? ''}
                        verified={recognized?.verified}
                        required={item.required}
                        source={
                          sourceReview && (
                            <FieldSourceCrop
                              document={document}
                              field={recognized}
                              documentMutationToken={currentDocumentMutationToken!}
                              onShowPage={(pageNumber) => {
                                setCompareSources(false);
                                paneLayout.revealPane('page');
                                window.requestAnimationFrame(() => {
                                  showEvidence(
                                    pageNumber,
                                    recognized?.evidenceLineIndexes,
                                    recognized?.barcodeEvidence,
                                  );
                                  citeActiveEvidence(recognized?.evidenceLineIndexes, recognized);
                                });
                              }}
                            />
                          )
                        }
                        className={`${arrivedFieldId === item.id ? 'review-arrived' : ''} ${pinned ? 'evidence-pinned' : ''}`}
                        onMouseEnter={() => {
                          if (recognized?.value) setHoveredFieldId(item.id);
                        }}
                        onMouseLeave={() => setHoveredFieldId(null)}
                        labelActions={
                          recognized?.value && (
                            <TooltipButton
                              type="button"
                              className="field-evidence-pin"
                              onClick={(event) => {
                                event.preventDefault();
                                togglePinnedField(item.id);
                              }}
                              tooltip={pinned ? 'Stop highlighting' : 'Keep highlighted'}
                              aria-label={
                                pinned ? `Unpin ${item.name} evidence` : `Pin ${item.name} evidence`
                              }
                              aria-pressed={pinned}
                            >
                              <Eye size={11} />
                            </TooltipButton>
                          )
                        }
                        adornment={
                          barcodeValue && (
                            <Barcode
                              size={15}
                              className="field-barcode"
                              aria-label="Read from a barcode"
                            />
                          )
                        }
                        detail={
                          recognized?.lookupEvidence ? (
                            <span className="field-lookup-source">
                              <Database size={12} /> {recognized.lookupEvidence.explanation}
                              {recognized.lookupEvidence.rowNumber
                                ? ` · row ${recognized.lookupEvidence.rowNumber}`
                                : ''}{' '}
                              · from {recognized.lookupEvidence.sourceValue}
                            </span>
                          ) : confidenceBasisText(recognized) ? (
                            <span className="field-confidence-basis">
                              {confidenceBasisText(recognized)}
                            </span>
                          ) : (
                            unlocated && (
                              <span className="field-unlocated">
                                Couldn’t find this on the page
                              </span>
                            )
                          )
                        }
                        choices={tieChoices(document, item.name).map((choice) => ({
                          rowNumber: choice.rowNumber,
                          value: choice.value,
                          onPick: () =>
                            void pickLookupChoice(document.id, item.name, choice.rowNumber),
                        }))}
                        warning={fieldIssue ? status : undefined}
                        choicesDisabled={
                          !loadedDocumentIds.has(document.id) ||
                          typeSavingDocumentId === document.id ||
                          confirmingSelected
                        }
                      >
                        {(recognized?.value ?? '').includes('\n') ||
                        item.kind === 'postal-address' ? (
                          /* A single-line input strips the newlines out of a multi-line value — an
                   address block displayed as one glued run of words, and, worse, EDITING
                   the field wrote the glued line back and destroyed the real breaks the
                   reader had captured. Values that carry lines get an element that can
                   hold them. */
                          <FieldDraftEditor
                            key={`${document.id}:${item.id}`}
                            store={fieldDraftStore}
                            suspendBlurCommit={batchSearchOpen}
                            binding={fieldDraftBinding(item, recognized)}
                            multiline
                            value={recognized?.value ?? ''}
                            disabled={
                              !loadedDocumentIds.has(document.id) ||
                              fieldSaving ||
                              lineSaving ||
                              typeSavingDocumentId === document.id ||
                              confirmingSelected
                            }
                            onBegin={() => beginFieldEdit(item, recognized)}
                            onEnd={() => finishFieldEdit(item)}
                          />
                        ) : (
                          <FieldDraftEditor
                            key={`${document.id}:${item.id}`}
                            store={fieldDraftStore}
                            suspendBlurCommit={batchSearchOpen}
                            binding={fieldDraftBinding(item, recognized)}
                            value={recognized?.value ?? ''}
                            placeholder={
                              !loadedDocumentIds.has(document.id)
                                ? ''
                                : extractionFailure
                                  ? 'Retry extraction'
                                  : isWithheldField(recognized)
                                    ? 'Empty'
                                    : 'Not found'
                            }
                            disabled={
                              !loadedDocumentIds.has(document.id) ||
                              fieldSaving ||
                              lineSaving ||
                              typeSavingDocumentId === document.id ||
                              confirmingSelected
                            }
                            decimal={item.kind === 'currency' || item.kind === 'number'}
                            currency={item.kind === 'currency'}
                            onBegin={() => beginFieldEdit(item, recognized)}
                            onEnd={() => finishFieldEdit(item)}
                            onEnterKey={() => advanceAfterFieldSave(item, document.id)}
                            onSavedEnter={() => advanceAfterFieldSave(item, document.id)}
                          />
                        )}
                      </FieldReviewRow>
                    );
                  })}
                {/* Where the next field goes while a new type is set up: after the last one, and it
                stays until Done (owner, 2026-10-05: a new type needs three to five fields, and
                the add row closed after one). */}
                {inlineFieldSetupHere && (
                  <div className="inline-add-field">
                    <input
                      ref={inlineFieldInputRef}
                      autoFocus
                      disabled={inlineFieldSaving || schemaRefreshBusy}
                      value={inlineFieldName}
                      onChange={(event) => setInlineFieldName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') addInlineFieldHere();
                        if (event.key === 'Escape') setInlineFieldName('');
                      }}
                      placeholder={
                        configuredFields.some((item) => item.kind !== 'line-items')
                          ? 'Add another field'
                          : 'Add a field'
                      }
                      aria-label="New field name"
                    />
                    {inlineFieldName && (
                      <button
                        onClick={() => {
                          setInlineFieldName('');
                          inlineFieldInputRef.current?.focus();
                        }}
                        disabled={inlineFieldSaving}
                        aria-label="Clear field name"
                      >
                        <X size={14} />
                      </button>
                    )}
                    <button
                      onClick={addInlineFieldHere}
                      disabled={inlineFieldSaving || !inlineFieldName.trim()}
                    >
                      Add
                    </button>
                    <button onClick={finishInlineFieldSetup} disabled={inlineFieldSaving}>
                      Done
                    </button>
                  </div>
                )}
              </div>
            </aside>
          )}
          {expectsLineItems &&
            (lineItemsMode !== 'hidden' || paneLayout.narrow || sourceReview) &&
            !detachedPanels['line-items'] && (
              <section
                data-review-pane="lines"
                className={`line-item-ledger ${lineItemsMode} ${document.lineItems.length === 0 ? 'empty' : ''}`}
                aria-label="Line items"
                aria-busy={lineSaving || fieldSaving}
                style={
                  document.lineItems.length === 0 || paneLayout.narrow
                    ? undefined
                    : { height: clampLineItemsHeight(lineItemsHeight) }
                }
              >
                {document.lineItems.length > 0 && (
                  <div
                    className={`lines-resizer ${resizingLines ? 'resizing' : ''}`}
                    role="separator"
                    tabIndex={0}
                    aria-label="Resize line items"
                    aria-orientation="horizontal"
                    aria-valuemin={LINE_ITEMS_MIN_HEIGHT}
                    aria-valuemax={LINE_ITEMS_MAX_HEIGHT}
                    aria-valuenow={Math.round(lineItemsHeight)}
                    onPointerDown={beginLinesResize}
                    onPointerMove={moveLinesResize}
                    onPointerUp={endLinesResize}
                    onPointerCancel={endLinesResize}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowUp')
                        setLineItemsHeight((current) => clampLineItemsHeight(current + 24));
                      if (event.key === 'ArrowDown')
                        setLineItemsHeight((current) => clampLineItemsHeight(current - 24));
                    }}
                  />
                )}
                {document.lineItems.length === 0 && (
                  <header>
                    <div>
                      <Table2 size={15} />
                      <strong>Line items</strong>
                      <small>{document.lineItems.length}</small>
                    </div>
                    <div className="ledger-tools">
                      {hostHas('detached-panels') && (
                        <TooltipButton
                          onClick={() => detachPanel('line-items')}
                          tooltip="Move to another window"
                          aria-label="Move line items to another window"
                        >
                          <ExternalLink size={13} />
                        </TooltipButton>
                      )}
                      <TooltipButton
                        onClick={() =>
                          paneLayout.narrow
                            ? paneLayout.revealPane('page')
                            : setLineItemsMode('hidden')
                        }
                        tooltip={paneLayout.narrow ? 'Back to page' : 'Collapse line items'}
                        aria-label={paneLayout.narrow ? 'Back to page' : 'Collapse line items'}
                      >
                        <ChevronDown size={14} />
                      </TooltipButton>
                    </div>
                  </header>
                )}
                {document.lineItems.length === 0 ? (
                  <div className="ledger-scroll">
                    <div className="line-item-empty">
                      <span>
                        <Table2 size={18} />
                        <span>
                          <strong>
                            {markingTableFor === document.id
                              ? 'Drag a box around the table'
                              : 'No rows recognized'}
                          </strong>
                          <small>
                            {markingTableFor === document.id
                              ? 'Only the words inside the box are read. Esc to stop.'
                              : 'The page is still available for review.'}
                          </small>
                        </span>
                      </span>
                      <span className="line-item-empty-actions">
                        {markingTableFor === document.id ? (
                          <button
                            onClick={() => setMarkingTableFor(null)}
                            disabled={findingTableFor === document.id}
                          >
                            {findingTableFor === document.id ? (
                              <LoaderCircle className="spin" size={13} />
                            ) : (
                              <BoxSelect size={13} />
                            )}{' '}
                            Stop marking
                          </button>
                        ) : (
                          <button
                            className="primary"
                            onClick={() => void findTable()}
                            disabled={
                              findingTableFor !== null ||
                              lineSaving ||
                              fieldSaving ||
                              typeSavingDocumentId === document.id
                            }
                          >
                            {findingTableFor === document.id ? (
                              <LoaderCircle className="spin" size={13} />
                            ) : (
                              <ScanEye size={13} />
                            )}{' '}
                            {findingTableFor === document.id
                              ? 'Reading the table'
                              : 'Find the table'}
                          </button>
                        )}
                        <button
                          onClick={addLineItem}
                          disabled={
                            findingTableFor === document.id ||
                            lineSaving ||
                            fieldSaving ||
                            typeSavingDocumentId === document.id
                          }
                        >
                          {lineSaving || fieldSaving || typeSavingDocumentId === document.id ? (
                            <LoaderCircle className="spin" size={13} />
                          ) : (
                            <Plus size={13} />
                          )}{' '}
                          Add first row
                        </button>
                      </span>
                    </div>
                    {tableReviewDetail(true)}
                  </div>
                ) : (
                  <div className="ledger-scroll">
                    {tableReviewDetail(true)}
                    <LineItemLedger
                      document={document}
                      draftStore={lineDraftStore}
                      suspendBlurCommit={batchSearchOpen}
                      toolbarStart={comparingLines ? <strong>Line items</strong> : undefined}
                      toolbarStatus={
                        <LineItemArithmeticStatus
                          rows={document.lineItems}
                          fields={document.fields}
                          held={tableHoldWords(document, WAIT_REASON_WORDS)}
                          compact
                        />
                      }
                      toolbarActions={[
                        ...(shiftedRows.length > 0
                          ? [
                              {
                                id: 'fix-shifted',
                                label: `Fix ${shiftedRows.length} shifted ${shiftedRows.length === 1 ? 'row' : 'rows'}`,
                                tooltip: 'Fix shifted rows',
                                icon: <Sigma size={16} />,
                                onSelect: fixShiftedRows,
                                disabled:
                                  lineSaving || fieldSaving || typeSavingDocumentId === document.id,
                              },
                            ]
                          : []),
                        {
                          id: 'add',
                          label: 'Add line item',
                          icon: <Plus size={16} />,
                          onSelect: addLineItem,
                          disabled:
                            lineSaving || fieldSaving || typeSavingDocumentId === document.id,
                        },
                        ...(hostHas('detached-panels')
                          ? [
                              {
                                id: 'detach',
                                label: 'Move line items to another window',
                                tooltip: 'Move to another window',
                                icon: <ExternalLink size={16} />,
                                onSelect: () => detachPanel('line-items'),
                              },
                            ]
                          : []),
                      ]}
                      onCollapse={
                        sourceReview
                          ? undefined
                          : () =>
                              paneLayout.narrow
                                ? paneLayout.revealPane('page')
                                : setLineItemsMode('hidden')
                      }
                      collapseLabel={paneLayout.narrow ? 'Back to page' : 'Collapse line items'}
                      compareSources={comparingLines}
                      onShowSource={showRowSource}
                      activeRow={activeLineItemRow}
                      arrivedRow={arrivedLineRow}
                      arrivalRevision={imageSelectionSerial}
                      flashRow={refusedLine}
                      saving={
                        lineSaving ||
                        fieldSaving ||
                        typeSavingDocumentId === document.id ||
                        confirmingSelected ||
                        wholeBatchMutationBusy
                      }
                      confirmationDisabled={
                        fieldSaving ||
                        typeSavingDocumentId === document.id ||
                        confirmingSelected ||
                        wholeBatchMutationBusy ||
                        Boolean(lineStructureMutationDocumentId.current)
                      }
                      confirmingRows={
                        new Set(
                          document.lineItems
                            .filter((row) =>
                              rowConfirmationKeys.has(`${document.id}:${row.rowNumber}`),
                            )
                            .map((row) => row.rowNumber),
                        )
                      }
                      actions={{
                        onEnterRow: beginLineItemEdit,
                        onAcceptRow: acceptLineItem,
                        onUseSum: useCalculatedAmount,
                        onDeleteRow: deleteLineItem,
                      }}
                    />
                  </div>
                )}
                {visibleFindings.length > 0 && (
                  <footer>
                    <TriangleAlert size={13} />{' '}
                    {visibleFindings.length === 1
                      ? `Line ${visibleFindings[0].rowNumber} does not add up. Correct it, or mark it right as printed.`
                      : `${visibleFindings.length} lines do not add up: ${visibleFindings.map((row) => row.rowNumber).join(', ')}. Correct them, or mark them right as printed.`}
                  </footer>
                )}
              </section>
            )}
          {expectsLineItems &&
            !sourceReview &&
            lineItemsMode === 'hidden' &&
            !paneLayout.narrow &&
            !detachedPanels['line-items'] && (
              <button
                className="line-item-peek"
                onClick={() => {
                  paneLayout.revealPane('lines');
                  setLineItemsMode('docked');
                }}
              >
                <Table2 size={14} />
                <strong>Show line items</strong>
                <small>{document.lineItems.length}</small>
                {currentReviewReasons.some(isTableReviewReason) && (
                  <span className="table-review-cue">
                    <TriangleAlert size={13} /> Review table
                  </span>
                )}
                <ChevronDown size={14} />
              </button>
            )}
        </div>
        {documentCompletion}
      </div>
      {batchSearchOpen && (
        <DocumentSearchDialog
          key={`${job.id}:${batch.batchId}`}
          jobId={job.id}
          batchId={batch.batchId}
          jobName={job.name}
          onChoose={jumpToBatchSearchResult}
          onClose={() => setBatchSearchOpen(false)}
        />
      )}
      <RetainedLineDrafts
        store={lineDraftStore}
        document={document}
        availableDocumentIds={batch.documents.map((item) => item.id)}
      />
      {capturePanels}
    </div>
  );
}

/**
 * A catalog definition can legitimately contain hundreds of document types and
 * thousands of fields. Native <details> elements still build every descendant
 * while they are closed, so painting the entire definition made merely choosing
 * a large catalog feel like opening it. Keep the searchable definition in
 * memory, but only mount the rows the operator has actually asked to inspect.
 */
function CatalogSchemaTypePreview({
  type,
  query,
  first,
}: {
  type: ContentCentralCatalogDefinition['documentTypes'][number];
  query: string;
  first: boolean;
}) {
  const [open, setOpen] = useState(first);
  const [fieldLimit, setFieldLimit] = useState(CONTENT_CENTRAL_SCHEMA_FIELD_PAGE_SIZE);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const typeMatches =
    normalizedQuery.length > 0 && type.name.toLocaleLowerCase().includes(normalizedQuery);
  const matchingFields =
    normalizedQuery.length === 0 || typeMatches
      ? type.fields
      : type.fields.filter((field) => field.name.toLocaleLowerCase().includes(normalizedQuery));
  const forcedOpen = normalizedQuery.length > 0;
  const visibleFields = matchingFields.slice(0, fieldLimit);
  useEffect(() => {
    setFieldLimit(CONTENT_CENTRAL_SCHEMA_FIELD_PAGE_SIZE);
  }, [normalizedQuery, type.id]);
  return (
    <details
      open={forcedOpen || open}
      onToggle={(event) => {
        if (!forcedOpen) setOpen(event.currentTarget.open);
      }}
    >
      <summary>
        <span>
          <Files size={15} />
        </span>
        <strong title={type.name}>{type.name}</strong>
        <small>
          {counted(
            type.fields.filter(
              (field) => field.extractable && field.valueOwner.toLowerCase() === 'capturepoint',
            ).length,
            'field',
          )}
        </small>
        <ChevronDown size={14} />
      </summary>
      {(forcedOpen || open) && (
        <>
          <ul>
            {visibleFields.map((field) => (
              <li
                key={field.id}
                className={
                  field.valueOwner.toLowerCase() !== 'capturepoint' ? 'destination-owned' : ''
                }
              >
                <TextCursorInput size={12} />
                <span title={field.name}>{field.name}</span>
                <em>
                  {field.required
                    ? 'Required'
                    : field.valueOwner.toLowerCase() !== 'capturepoint'
                      ? 'Content Central'
                      : field.valueType}
                </em>
              </li>
            ))}
          </ul>
          {matchingFields.length > visibleFields.length && (
            <button
              type="button"
              className="catalog-schema-fields-more"
              onClick={() =>
                setFieldLimit((current) => current + CONTENT_CENTRAL_SCHEMA_FIELD_PAGE_SIZE)
              }
            >
              Show{' '}
              {Math.min(
                CONTENT_CENTRAL_SCHEMA_FIELD_PAGE_SIZE,
                matchingFields.length - visibleFields.length,
              )}{' '}
              more fields
            </button>
          )}
          {type.warnings.map((warning) => (
            <p key={warning} className="catalog-job-warning">
              <TriangleAlert size={12} /> {warning}
            </p>
          ))}
        </>
      )}
    </details>
  );
}

/**
 * New job's first step, "How do you want to start?" (owner-approved "One front
 * door"): every way to start a job is a picture card that goes on with one click.
 * Your own documents and Content Central go on to naming the job; a saved job
 * hands over to its own window. The tour waits in the footer.
 */
function NewJobStartChoices({
  onChoose,
  onImport,
}: {
  onChoose: (intent: JobBootstrapChoice) => void;
  onImport?: () => void;
}) {
  // Every card is a way to start the operator's own job. The ready-made job became the tour
  // (owner, 2026-10-03), which waits in the footer rather than among the ways to start.
  // Which start was chosen is sent as its code (telemetry catalog job.start-choice), never the card's words.
  const choices = [
    {
      key: 'scan',
      telemetry: 'start-own-documents',
      title: 'My own documents',
      copy: 'Scan paper or pick files. Paige works out the setup from your pages.',
      icon: <ScanLine size={24} />,
      choose: () => {
        reportEvent('job.start-choice:own-documents');
        onChoose('scan');
      },
    },
    // PAIGE: starting from a Content Central catalog needs a connection: the desktop's saved one, or
    // Paige's Content Central destination (`content-central-start`, cpv6-parity S-22).
    ...(hostHas('saved-connections') || hostHas('content-central-start')
      ? [
          {
            key: 'content-central',
            telemetry: 'start-content-central',
            title: 'From Content Central',
            copy: 'Bring in the document types and fields you already use there.',
            icon: <Database size={24} />,
            choose: () => {
              reportEvent('job.start-choice:content-central');
              onChoose('content-central');
            },
          },
        ]
      : []),
    ...(onImport
      ? [
          {
            key: 'saved',
            telemetry: 'start-saved-job',
            title: 'A saved job',
            copy: hostHas('legacy-import')
              ? 'A job file from another computer, or your jobs from earlier Paige.'
              : 'A job setup file, with everything its job learned.',
            icon: <FileUp size={24} />,
            choose: () => {
              reportEvent('job.start-choice:saved-job');
              onImport();
            },
          },
        ]
      : []),
  ];
  return (
    <>
      <h2 id="create-title">How do you want to start?</h2>
      <div className={`job-start-choices${choices.length === 3 ? ' three' : ''}`}>
        {choices.map((choice, index) => (
          <button
            key={choice.key}
            type="button"
            className={`job-start-choice${index === 0 ? ' primary' : ''}`}
            data-telemetry={choice.telemetry}
            onClick={choice.choose}
          >
            <span className="job-start-choice-art" aria-hidden="true">
              {choice.icon}
            </span>
            <strong>{choice.title}</strong>
            <small>{choice.copy}</small>
          </button>
        ))}
      </div>
      <button
        type="button"
        className="job-start-manual"
        data-telemetry="start-manual"
        onClick={() => {
          reportEvent('job.start-choice:manual');
          onChoose('manual');
        }}
      >
        Set up document types and fields yourself
      </button>
    </>
  );
}

export function CreateJobModal({
  initialName = '',
  connectionSettings,
  existingNames,
  intent,
  start = 'choice',
  cudaActive,
  discardsJob = false,
  onIntentChange,
  onClose,
  onBack,
  onOpenExisting,
  onCreate,
  onTour,
  onImport,
}: {
  initialName?: string;
  connectionSettings?: React.ReactNode;
  existingJobs: Job[];
  existingNames: string[];
  intent: JobBootstrapChoice;
  start?: NewJobStep;
  cudaActive: boolean;
  /** Reopened from the empty job New job just made (Back on its page sources): closing discards that job. */
  discardsJob?: boolean;
  onIntentChange: (intent: JobBootstrapChoice) => void;
  onClose: () => void;
  onBack?: () => void;
  onOpenExisting: (jobId: string) => void;
  onCreate: (intent: JobCreationIntent) => Promise<void>;
  onTour?: () => void;
  onImport?: () => void;
}) {
  /* New job begins with "How do you want to start?"; each start is then its own
     stage rather than switches below the action: the fast name-and-create step,
     or the roomy catalog workspace Content Central's information needs. */
  const mode: Job['bootstrapMode'] =
    intent === 'folder'
      ? 'folder'
      : intent === 'manual'
        ? 'manual'
        : intent === 'content-central'
          ? 'content-central'
          : 'scan';
  const [step, setStep] = useState<NewJobStep>(start);
  // New job closed without a job (telemetry catalog job.new.closed): at which step, or after its empty job was made.
  const closedAt = useRef({ step, discardsJob });
  closedAt.current = { step, discardsJob };
  const reportClosed = () =>
    reportEvent(
      closedAt.current.discardsJob
        ? 'job.new.closed:source'
        : closedAt.current.step === 'choice'
          ? 'job.new.closed:start-choice'
          : 'job.new.closed:name',
    );
  const close = () => {
    reportClosed();
    onClose();
  };
  const [name, setName] = useState(initialName);
  const draftJobId = useRef(crypto.randomUUID());
  const nameTouchedRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [managingConnections, setManagingConnections] = useState(false);
  const addedConnectionId = useRef<string | null>(null);
  useEffect(() => {
    if (!managingConnections) return;
    const added = (event: Event) => {
      addedConnectionId.current = (event as CustomEvent<string>).detail ?? null;
    };
    window.addEventListener(CONNECTION_ADDED_EVENT, added);
    return () => window.removeEventListener(CONNECTION_ADDED_EVENT, added);
  }, [managingConnections]);
  const [connections, setConnections] = useState<DestinationConnectionSummary[]>([]);
  const [connectionLoadError, setConnectionLoadError] = useState('');
  const [connectionLoadRetry, setConnectionLoadRetry] = useState(0);
  const [connectionId, setConnectionId] = useState('');
  const [catalogs, setCatalogs] = useState<ContentCentralCatalogSummary[]>([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogId, setCatalogId] = useState('');
  const [catalogDefinition, setCatalogDefinition] =
    useState<ContentCentralCatalogDefinition | null>(null);
  const [catalogListRetry, setCatalogListRetry] = useState(0);
  const [catalogListError, setCatalogListError] = useState('');
  const [catalogNextCursor, setCatalogNextCursor] = useState<string | null>(null);
  const [catalogMoreLoading, setCatalogMoreLoading] = useState(false);
  const [catalogMoreError, setCatalogMoreError] = useState('');
  const [catalogRetry, setCatalogRetry] = useState(0);
  const [catalogReadError, setCatalogReadError] = useState('');
  const [catalogSchemaQuery, setCatalogSchemaQuery] = useState('');
  const [catalogSchemaTypeLimit, setCatalogSchemaTypeLimit] = useState(
    CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE,
  );
  // Below the master/detail breakpoint, choosing a catalog becomes a deliberate
  // second step. Keeping both tall panes painted on top of the same 250px grid
  // row hid every catalog after the first few behind the schema surface.
  const [compactCatalogBrowserOpen, setCompactCatalogBrowserOpen] = useState(true);
  const [boundJob, setBoundJob] = useState<BoundCatalogJobSummary | null>(null);
  const [acknowledgedBoundJobId, setAcknowledgedBoundJobId] = useState<string | null>(null);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [connectionDraft, setConnectionDraft] = useState({ name: '', url: '' });
  const dialogRef = useRef<HTMLElement | null>(null);
  const catalogSchemaRef = useRef<HTMLElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  // React state disables the controls on the next render. This ref closes the
  // smaller same-turn window as well, so Enter and the action button can never
  // create two jobs or open two folder pickers for one decision.
  const createInFlight = useRef(false);
  const connectionInFlight = useRef(false);
  const catalogListRequest = useRef(0);
  const catalogMoreInFlight = useRef(false);
  const catalogListContext = `${intent}\u0000${connectionId}\u0000${catalogQuery.trim()}`;
  const catalogListContextRef = useRef(catalogListContext);
  catalogListContextRef.current = catalogListContext;
  const normalizedName = name.trim().toLocaleLowerCase();
  const duplicateName =
    normalizedName.length > 0 &&
    existingNames.some((existing) => existing.trim().toLocaleLowerCase() === normalizedName);
  const secondaryCatalogAcknowledged = Boolean(
    boundJob && acknowledgedBoundJobId === boundJob.jobId,
  );
  const normalizedCatalogSchemaQuery = catalogSchemaQuery.trim().toLocaleLowerCase();
  const matchingCatalogDocumentTypes = useMemo(
    () =>
      catalogDefinition?.documentTypes.filter(
        (type) =>
          normalizedCatalogSchemaQuery.length === 0 ||
          type.name.toLocaleLowerCase().includes(normalizedCatalogSchemaQuery) ||
          type.fields.some((field) =>
            field.name.toLocaleLowerCase().includes(normalizedCatalogSchemaQuery),
          ),
      ) ?? [],
    [catalogDefinition, normalizedCatalogSchemaQuery],
  );
  const visibleCatalogDocumentTypes = matchingCatalogDocumentTypes.slice(0, catalogSchemaTypeLimit);
  useEffect(() => {
    const compact = window.matchMedia('(max-width: 800px)');
    const beginCompactFlow = () => {
      if (compact.matches) setCompactCatalogBrowserOpen(true);
    };
    beginCompactFlow();
    compact.addEventListener('change', beginCompactFlow);
    return () => compact.removeEventListener('change', beginCompactFlow);
  }, []);
  useEffect(() => {
    previousFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      previousFocus.current?.focus();
    };
  }, []);
  useEffect(() => {
    window.requestAnimationFrame(() => {
      const target = dialogRef.current?.querySelector<HTMLElement>(
        step === 'choice'
          ? '.job-start-choice:not([disabled])'
          : showConnectionForm
            ? '.catalog-job-connect-panel input:not([disabled])'
            : intent === 'content-central'
              ? '.catalog-connection-bar select:not([disabled]),.catalog-browser-search input:not([disabled]),.catalog-connection-bar button:not([disabled])'
              : '.job-start-name input:not([disabled])',
      );
      (
        target ?? dialogRef.current?.querySelector<HTMLElement>('.one-path-close:not([disabled])')
      )?.focus();
    });
  }, [connections.length, intent, showConnectionForm, step]);
  useEffect(() => {
    if (!creating) return;
    window.requestAnimationFrame(() => dialogRef.current?.focus());
  }, [creating]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (managingConnections) return;
      if (event.key === 'Escape' && !creating) {
        reportClosed();
        onClose();
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = dialogTabStops(dialogRef.current);
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [creating, onClose, managingConnections]);
  useEffect(() => {
    if (intent !== 'content-central' || !window.capturePointDesktop) {
      setLoadingRemote(false);
      return;
    }
    let live = true;
    setLoadingRemote(true);
    setConnectionLoadError('');
    window.capturePointDesktop
      .listDestinationConnections()
      .then((items) => {
        if (!live) return;
        setConnections(items);
        setConnectionLoadError('');
        // A connection just added in Connections is the one the operator went
        // there to make, so it comes back already chosen. One removed there is
        // not kept: its catalogs are gone and Create would fail against it.
        const added = addedConnectionId.current;
        addedConnectionId.current = null;
        const listed = (id: string | null) =>
          Boolean(id) && items.some((item) => item.connectionId === id);
        setConnectionId(
          (current) =>
            (listed(added) ? added! : listed(current) ? current : items[0]?.connectionId) || '',
        );
      })
      .catch((reason) => {
        if (live) {
          setConnections([]);
          setConnectionId('');
          setConnectionLoadError(messageFor(reason, 'Connections could not be loaded'));
        }
      })
      .finally(() => {
        if (live) setLoadingRemote(false);
      });
    return () => {
      live = false;
    };
  }, [intent, connectionLoadRetry]);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    const request = ++catalogListRequest.current;
    const context = catalogListContext;
    if (intent !== 'content-central' || !connectionId || !desktop) {
      setCatalogs([]);
      setCatalogId('');
      setCatalogDefinition(null);
      setCatalogListError('');
      setCatalogReadError('');
      setAcknowledgedBoundJobId(null);
      setCatalogNextCursor(null);
      setCatalogMoreLoading(false);
      setCatalogMoreError('');
      catalogMoreInFlight.current = false;
      return;
    }
    let live = true;
    // Results belong to the connection and query that produced them. Clear
    // them before the debounced request so a fast click cannot bind a catalog
    // from the previous server (or from a search that is no longer visible).
    setLoadingRemote(true);
    setError('');
    setCatalogListError('');
    setCatalogs([]);
    setCatalogId('');
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    catalogMoreInFlight.current = false;
    setCatalogDefinition(null);
    setBoundJob(null);
    setCatalogReadError('');
    setAcknowledgedBoundJobId(null);
    const timer = window.setTimeout(
      () =>
        desktop
          .listContentCentralCatalogs(connectionId, catalogQuery.trim() || undefined)
          .then((page) => {
            if (
              live &&
              request === catalogListRequest.current &&
              context === catalogListContextRef.current
            ) {
              const items = page.items.slice(0, CONTENT_CENTRAL_CATALOG_RENDER_LIMIT);
              setCatalogs(items);
              setCatalogNextCursor(page.nextCursor ?? null);
              setCatalogId((current) => (items.some((item) => item.id === current) ? current : ''));
            }
          })
          .catch((reason) => {
            if (
              live &&
              request === catalogListRequest.current &&
              context === catalogListContextRef.current
            )
              setCatalogListError(messageFor(reason, 'Catalogs could not be loaded'));
          })
          .finally(() => {
            if (
              live &&
              request === catalogListRequest.current &&
              context === catalogListContextRef.current
            )
              setLoadingRemote(false);
          }),
      250,
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [intent, connectionId, catalogQuery, catalogListRetry]);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const loadMoreJobCatalogs = async () => {
    const desktop = window.capturePointDesktop;
    const cursor = catalogNextCursor;
    if (
      !desktop ||
      !cursor ||
      catalogMoreInFlight.current ||
      intent !== 'content-central' ||
      !connectionId ||
      catalogs.length >= CONTENT_CENTRAL_CATALOG_RENDER_LIMIT
    )
      return;
    catalogMoreInFlight.current = true;
    const request = ++catalogListRequest.current;
    const context = catalogListContextRef.current;
    setCatalogMoreLoading(true);
    setCatalogMoreError('');
    try {
      const page = await desktop.listContentCentralCatalogs(
        connectionId,
        catalogQuery.trim() || undefined,
        cursor,
      );
      if (request !== catalogListRequest.current || context !== catalogListContextRef.current)
        return;
      setCatalogs((current) => mergeContentCentralCatalogs(current, page.items));
      setCatalogNextCursor(page.nextCursor ?? null);
    } catch (reason) {
      if (request !== catalogListRequest.current || context !== catalogListContextRef.current)
        return;
      setCatalogMoreError(messageFor(reason, 'More Content Central catalogs could not be loaded'));
    } finally {
      if (request === catalogListRequest.current && context === catalogListContextRef.current) {
        catalogMoreInFlight.current = false;
        setCatalogMoreLoading(false);
      }
    }
  };

  const refreshJobCatalogSearch = () => {
    catalogListRequest.current++;
    catalogMoreInFlight.current = false;
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
    setCatalogNextCursor(null);
    setCatalogListRetry((value) => value + 1);
  };

  const changeJobCatalogQuery = (nextQuery: string) => {
    catalogListRequest.current++;
    catalogMoreInFlight.current = false;
    setCatalogQuery(nextQuery);
    setCatalogNextCursor(null);
    setCatalogMoreLoading(false);
    setCatalogMoreError('');
  };
  useEffect(() => {
    if (
      intent !== 'content-central' ||
      !connectionId ||
      !catalogId ||
      !window.capturePointDesktop
    ) {
      setCatalogDefinition(null);
      setBoundJob(null);
      setCatalogReadError('');
      setAcknowledgedBoundJobId(null);
      return;
    }
    let live = true;
    setLoadingRemote(true);
    setError('');
    setCatalogReadError('');
    setCatalogDefinition(null);
    setBoundJob(null);
    setAcknowledgedBoundJobId(null);
    Promise.all([
      window.capturePointDesktop.getContentCentralCatalog(connectionId, catalogId),
      window.capturePointDesktop.findContentCentralJob(connectionId, catalogId),
    ])
      .then(([definition, existing]) => {
        if (!live) return;
        setCatalogDefinition(definition);
        setBoundJob(existing);
        if (!nameTouchedRef.current) setName(definition.catalog.name);
      })
      .catch((reason) => {
        if (live) {
          const message = messageFor(reason, 'The catalog could not be read');
          setError(message);
          setCatalogReadError(message);
        }
      })
      .finally(() => {
        if (live) setLoadingRemote(false);
      });
    return () => {
      live = false;
    };
  }, [intent, connectionId, catalogId, catalogRetry]);
  useEffect(() => {
    if (!catalogId || !window.matchMedia('(max-width: 800px)').matches) return;
    const frame = window.requestAnimationFrame(() => {
      catalogSchemaRef.current?.scrollIntoView({ block: 'start', inline: 'nearest' });
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [catalogId, catalogRetry]);
  useEffect(() => {
    setCatalogSchemaQuery('');
    setCatalogSchemaTypeLimit(CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE);
  }, [catalogDefinition?.catalog.id]);
  const addConnection = async () => {
    if (
      creating ||
      connectionInFlight.current ||
      !window.capturePointDesktop ||
      !connectionDraft.url.trim()
    )
      return;
    connectionInFlight.current = true;
    setLoadingRemote(true);
    setError('');
    try {
      const connection = await window.capturePointDesktop.connectContentCentral({
        displayName: connectionDraft.name.trim() || 'Content Central',
        baseUrl: connectionDraft.url.trim(),
      });
      if (!connection) return;
      setConnections((current) => [
        ...current.filter((item) => item.connectionId !== connection.connectionId),
        connection,
      ]);
      catalogListRequest.current++;
      catalogMoreInFlight.current = false;
      setConnectionId(connection.connectionId);
      setShowConnectionForm(false);
      setConnectionDraft({ name: '', url: '' });
    } catch (reason) {
      setError(messageFor(reason, 'Content Central could not be connected'));
    } finally {
      connectionInFlight.current = false;
      setLoadingRemote(false);
    }
  };
  const chooseMethod = (choice: JobBootstrapChoice) => {
    if (creating) return;
    setShowConnectionForm(false);
    setCompactCatalogBrowserOpen(true);
    setAcknowledgedBoundJobId(null);
    setError('');
    onIntentChange(choice);
  };
  // A start chosen on the first step goes on to naming the job; Back returns to that step.
  const chooseStart = (choice: JobBootstrapChoice) => {
    chooseMethod(choice);
    setStep('name');
  };
  const backToStart = () => {
    if (creating) return;
    chooseMethod('scan');
    setStep('choice');
  };
  const chooseAnotherLocalJob = () => {
    if (creating || !boundJob || !catalogDefinition) return;
    setAcknowledgedBoundJobId(boundJob.jobId);
    const stem = `${catalogDefinition.catalog.name} intake`;
    let candidate = stem.slice(0, 160);
    let suffix = 2;
    const taken = new Set(existingNames.map((value) => value.trim().toLocaleLowerCase()));
    while (taken.has(candidate.trim().toLocaleLowerCase())) {
      const ending = ` ${suffix++}`;
      candidate = `${stem.slice(0, 160 - ending.length)}${ending}`;
    }
    nameTouchedRef.current = true;
    setName(candidate);
    window.requestAnimationFrame(() =>
      dialogRef.current
        ?.querySelector<HTMLInputElement>('.catalog-job-footer .job-start-name input')
        ?.focus(),
    );
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const create = async () => {
    if (
      !name.trim() ||
      duplicateName ||
      creating ||
      createInFlight.current ||
      loadingRemote ||
      (intent === 'content-central' &&
        ((Boolean(boundJob) && !secondaryCatalogAcknowledged) ||
          !connectionId ||
          !catalogId ||
          !catalogDefinition ||
          catalogBootstrapProblems(catalogDefinition).length > 0))
    )
      return;
    // Guard BEFORE the folder picker, not after: the picker holds this
    // function open for as long as the operator keeps the dialog up, and a
    // second press in that window would sail past a guard set later — two
    // jobs, one name, two folder dialogs.
    createInFlight.current = true;
    setCreating(true);
    setError('');
    if (intent === 'content-central') {
      try {
        const result = await window.capturePointDesktop?.createContentCentralJob(
          connectionId,
          catalogId,
          name.trim(),
          acknowledgedBoundJobId ?? undefined,
        );
        if (!result) throw new Error('The Content Central job could not be created.');
        // The job's setup comes from Content Central: its source (telemetry catalog source.chosen).
        reportEvent('source.chosen:content-central', { jobId: result.job.id });
        announceExportDestinationChanged();
        await onCreate({ job: migrateJob(result.job as Job), catalogResult: result });
      } catch (reason) {
        setError(messageFor(reason, 'The Content Central job could not be created'));
        createInFlight.current = false;
        setCreating(false);
      }
      return;
    }
    // The folder promise starts with the folder: pick it before the job
    // exists, so cancelling the picker cancels nothing but the picker.
    let folderToken: string | undefined;
    let folderFileCount: number | undefined;
    let folderAnalysisCount: number | undefined;
    if (intent === 'folder' && window.capturePointDesktop) {
      try {
        const selection = await window.capturePointDesktop.chooseDocumentFolder();
        if (selection.canceled || !selection.token) {
          createInFlight.current = false;
          setCreating(false);
          return;
        }
        folderToken = selection.token;
        folderFileCount = selection.supportedFileCount;
        folderAnalysisCount = selection.analysisFileCount;
      } catch (reason) {
        // The dialog can close cleanly and the folder still fail to read —
        // unmounted share, permissions pulled between choosing and listing.
        setError(messageFor(reason, 'The folder could not be read'));
        createInFlight.current = false;
        setCreating(false);
        return;
      }
    }
    const now = new Date().toISOString();
    const job: Job = {
      id: draftJobId.current,
      name: name.trim(),
      description:
        intent === 'folder'
          ? 'Proposed from a folder of representative documents'
          : intent === 'manual'
            ? 'Configured by hand, refined by what arrives'
            : 'Built from documents as they are scanned or imported',
      accent: '#087f9c',
      bootstrapMode: mode,
      status: 'provisional',
      schemaVersion: 0,
      createdAt: now,
      updatedAt: now,
      documentTypes: [],
      separationMode: 'automatic',
      separationBarcodeText: null,
      boundaryOpeningProfiles: [],
      boundaryContinuationProfiles: [],
      suppressedSuggestions: [],
      suggestionsPausedUntil: null,
    };
    try {
      await onCreate({
        job,
        openSetup: intent === 'manual',
        folderToken,
        folderFileCount,
        folderAnalysisCount,
      });
    } catch (reason) {
      setError(messageFor(reason, 'The job could not be created'));
      createInFlight.current = false;
      setCreating(false);
    }
  };
  const methodCopy = jobBootstrapCopy[intent];
  const catalogProblems = catalogDefinition ? catalogBootstrapProblems(catalogDefinition) : [];
  const createDisabled =
    !name.trim() ||
    duplicateName ||
    creating ||
    (intent === 'content-central' &&
      ((Boolean(boundJob) && !secondaryCatalogAcknowledged) ||
        !connectionId ||
        !catalogId ||
        !catalogDefinition ||
        loadingRemote ||
        catalogProblems.length > 0));
  const catalogCreateAction = secondaryCatalogAcknowledged
    ? 'Create separate job'
    : methodCopy.action;
  const methodIcon = (choice: JobBootstrapChoice, size = 20) =>
    choice === 'scan' ? (
      <ScanLine size={size} />
    ) : choice === 'folder' ? (
      <FolderOpen size={size} />
    ) : choice === 'manual' ? (
      <SlidersHorizontal size={size} />
    ) : (
      <Database size={size} />
    );
  const returnFromConnections = () => {
    setManagingConnections(false);
    setConnectionLoadRetry((value) => value + 1);
  };
  if (managingConnections && connectionSettings)
    return (
      <WorkspaceDialog
        telemetry="new-job-connections"
        title="Connections"
        actions={
          <button type="button" className="secondary-button" onClick={returnFromConnections}>
            <ChevronLeft size={16} /> Back to new job
          </button>
        }
        onClose={returnFromConnections}
      >
        {connectionSettings}
      </WorkspaceDialog>
    );
  return (
    <div
      className="modal-backdrop job-start-backdrop"
      onMouseDown={(event) => !creating && event.target === event.currentTarget && close()}
    >
      <section
        ref={dialogRef}
        className={`create-modal one-path-modal job-start-modal${step === 'choice' ? ' job-start-chooser' : intent === 'content-central' ? ' catalog-job-modal' : ' local-job-modal'}`}
        role="dialog"
        data-telemetry-dialog="new-job"
        aria-modal="true"
        aria-labelledby="create-title"
        aria-busy={creating || loadingRemote}
        tabIndex={-1}
      >
        <button
          className="icon-button one-path-close"
          disabled={creating}
          onClick={close}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        {step === 'choice' ? (
          <>
            <NewJobStartChoices onChoose={chooseStart} onImport={onImport} />
            <footer className="job-start-footer">
              <button
                type="button"
                className="secondary-button new-job-back"
                onClick={() => {
                  reportClosed();
                  (onBack ?? onClose)();
                }}
              >
                <ChevronLeft size={16} /> Back
              </button>
              {onTour && (
                <button
                  type="button"
                  data-telemetry="tour-new-job"
                  className="secondary-button job-start-tour"
                  onClick={() => {
                    reportEvent('job.start-choice:tour');
                    onTour();
                  }}
                >
                  <Play size={15} aria-hidden="true" /> {tourOfferLabel('Take a 2-minute tour')}
                </button>
              )}
            </footer>
          </>
        ) : intent === 'content-central' ? (
          <>
            <button
              type="button"
              className="quiet-button job-start-back"
              disabled={creating}
              onClick={backToStart}
            >
              <ChevronLeft size={14} /> Back to new job
            </button>
            <div className="job-start-heading catalog-heading">
              <span className="job-start-mark catalog">
                <Database size={23} />
                <Link2 size={12} />
              </span>
              <div>
                <h2 id="create-title">Start from a connected app</h2>
                <p>Choose a connection, then the setup to bring into your job.</p>
              </div>
            </div>
            <div className="catalog-job-workspace" aria-busy={loadingRemote}>
              <div className="catalog-connection-bar">
                {loadingRemote && connections.length === 0 ? (
                  <div className="catalog-job-loading" role="status">
                    <LoaderCircle className="spin" size={15} /> Loading connections…
                  </div>
                ) : connectionLoadError ? (
                  <div
                    className="catalog-connection-load-error"
                    role="alert"
                    data-telemetry="error:connections-load-failed"
                  >
                    <TriangleAlert size={16} />
                    <span>
                      <strong>Connections could not be loaded</strong>
                      <small>{connectionLoadError}</small>
                    </span>
                    <button
                      type="button"
                      className="secondary-button small"
                      disabled={creating}
                      onClick={() => setConnectionLoadRetry((value) => value + 1)}
                    >
                      Try again
                    </button>
                  </div>
                ) : connections.length === 0 ? (
                  <div className="catalog-connection-empty">
                    <Database size={17} />
                    <span>
                      <strong>Connect your first app</strong>
                      <small>
                        Choose Add connection to set up Content Central, then return here to choose
                        a catalog.
                      </small>
                    </span>
                  </div>
                ) : (
                  <div className="job-connection-choices" role="group" aria-label="Connections">
                    {connections.map((connection) => (
                      <button
                        type="button"
                        key={connection.connectionId}
                        disabled={creating}
                        aria-pressed={connectionId === connection.connectionId}
                        onClick={() => {
                          if (connectionId === connection.connectionId) return;
                          catalogListRequest.current++;
                          catalogMoreInFlight.current = false;
                          setConnectionId(connection.connectionId);
                          setCatalogs([]);
                          setCatalogQuery('');
                          setCatalogId('');
                          setCatalogDefinition(null);
                          setCatalogListError('');
                          setCatalogNextCursor(null);
                          setCatalogMoreLoading(false);
                          setCatalogMoreError('');
                          setBoundJob(null);
                          setAcknowledgedBoundJobId(null);
                        }}
                      >
                        <Database size={24} />
                        <span>
                          <strong>{connection.displayName}</strong>
                          <small>Content Central</small>
                        </span>
                        {connectionId === connection.connectionId && <CheckCircle2 size={18} />}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="secondary-button small"
                  disabled={creating}
                  aria-expanded={showConnectionForm}
                  onClick={() =>
                    // PAIGE: Settings > Connections is the desktop's; Paige adds one here, its key in Paige's window.
                    connectionSettings && hostHas('saved-connections')
                      ? setManagingConnections(true)
                      : setShowConnectionForm((value) => !value)
                  }
                >
                  <Plus size={13} /> {showConnectionForm ? 'Close' : 'Add connection'}
                </button>
              </div>
              {showConnectionForm && (
                <div className="catalog-job-connect-panel">
                  <div>
                    <strong>Add connection · Content Central</strong>
                    <small>The connection key is entered in a protected system window.</small>
                  </div>
                  <label>
                    <span>Connection name</span>
                    <input
                      value={connectionDraft.name}
                      disabled={creating}
                      onChange={(event) =>
                        setConnectionDraft((current) => ({ ...current, name: event.target.value }))
                      }
                      placeholder="Content Central"
                    />
                  </label>
                  <label>
                    <span>Server address</span>
                    <input
                      type="url"
                      value={connectionDraft.url}
                      disabled={creating}
                      onChange={(event) =>
                        setConnectionDraft((current) => ({ ...current, url: event.target.value }))
                      }
                      placeholder="https://documents.example.com/ContentCentral"
                    />
                  </label>
                  <button
                    type="button"
                    className="primary-button small"
                    data-telemetry="new-job-content-central-connect"
                    onClick={() => void addConnection()}
                    disabled={creating || loadingRemote || !connectionDraft.url.trim()}
                  >
                    <LockKeyhole size={13} /> Enter key securely
                  </button>
                </div>
              )}
              <div
                className={`catalog-job-columns ${catalogId && !compactCatalogBrowserOpen ? 'catalog-reviewing' : 'catalog-browsing'}`}
              >
                <section className="catalog-browser" aria-label="Content Central catalogs">
                  <div className="catalog-browser-heading">
                    <div>
                      <strong>Catalogs</strong>
                      <small role="status" aria-live="polite">
                        {catalogListError
                          ? 'Couldn’t load'
                          : loadingRemote && connectionId && catalogs.length === 0
                            ? 'Loading…'
                            : connectionId
                              ? `${catalogs.length.toLocaleString()} ${catalogNextCursor ? 'shown' : catalogs.length === 1 ? 'match' : 'matches'}`
                              : 'Choose a connection'}
                      </small>
                    </div>
                  </div>
                  <label className="catalog-browser-search">
                    <Search size={15} />
                    <input
                      value={catalogQuery}
                      onChange={(event) => changeJobCatalogQuery(event.target.value)}
                      placeholder="Search catalogs"
                      disabled={creating || !connectionId}
                      aria-label="Find catalog"
                    />
                  </label>
                  <div className="catalog-card-list">
                    {loadingRemote && connectionId && catalogs.length === 0 ? (
                      <div className="catalog-browser-state" role="status">
                        <LoaderCircle className="spin" size={16} /> Finding catalogs…
                      </div>
                    ) : catalogListError ? (
                      <div
                        className="catalog-browser-state catalog-browser-error"
                        role="alert"
                        data-telemetry="error:catalog-load-failed"
                      >
                        <TriangleAlert size={20} />
                        <strong>Couldn’t load catalogs</strong>
                        <small>{catalogListError}</small>
                        <button
                          type="button"
                          className="secondary-button small"
                          disabled={creating}
                          onClick={() => setCatalogListRetry((value) => value + 1)}
                        >
                          Try again
                        </button>
                      </div>
                    ) : catalogs.length === 0 ? (
                      <div className="catalog-browser-state">
                        <Database size={19} />
                        <strong>
                          {connectionId ? 'No catalogs found' : 'Choose a connection'}
                        </strong>
                        <small>
                          {catalogQuery.trim()
                            ? 'Try a different name.'
                            : 'Available catalogs will appear here.'}
                        </small>
                      </div>
                    ) : (
                      catalogs.map((catalog) => {
                        const access = !catalog.canAddDocuments
                          ? {
                              className: 'view-only',
                              label: 'View only',
                              title: "Can't add documents here",
                            }
                          : catalog.documentTypeCount === 0
                            ? {
                                className: 'unavailable',
                                label: 'Unavailable',
                                title: 'Has no document types',
                              }
                            : { className: '', label: 'Ready', title: undefined };
                        return (
                          <button
                            type="button"
                            key={catalog.id}
                            disabled={creating}
                            className={`catalog-card${sameContentCentralId(catalogId, catalog.id) ? ' selected' : ''}${access.className ? ` ${access.className}` : ''}`}
                            aria-pressed={sameContentCentralId(catalogId, catalog.id)}
                            title={access.title}
                            onClick={() => {
                              setCompactCatalogBrowserOpen(false);
                              if (sameContentCentralId(catalogId, catalog.id)) {
                                if (!catalogDefinition && !loadingRemote)
                                  setCatalogRetry((value) => value + 1);
                                return;
                              }
                              setCatalogId(catalog.id);
                              setCatalogDefinition(null);
                              setAcknowledgedBoundJobId(null);
                            }}
                          >
                            <span className="catalog-card-icon">
                              <Database size={16} />
                            </span>
                            <span>
                              <strong>{catalog.name}</strong>
                              <small>
                                {catalog.documentTypeCount} document type
                                {catalog.documentTypeCount === 1 ? '' : 's'}
                              </small>
                            </span>
                            <span
                              className={`catalog-card-access${access.className ? ` ${access.className}` : ''}`}
                            >
                              {access.label}
                            </span>
                            <ChevronRight size={15} />
                          </button>
                        );
                      })
                    )}
                  </div>
                  {catalogMoreError && (
                    <div
                      className="catalog-page-error"
                      role="alert"
                      data-telemetry="error:catalog-load-failed"
                    >
                      <TriangleAlert size={13} />
                      <span>
                        <strong>Couldn’t load more</strong>
                        <small>{catalogMoreError}</small>
                      </span>
                      <button
                        type="button"
                        className="quiet-button small"
                        disabled={creating || catalogMoreLoading}
                        onClick={refreshJobCatalogSearch}
                      >
                        Refresh results
                      </button>
                    </div>
                  )}
                  {!catalogMoreError &&
                    catalogNextCursor &&
                    catalogs.length < CONTENT_CENTRAL_CATALOG_RENDER_LIMIT && (
                      <button
                        type="button"
                        className="catalog-show-more"
                        disabled={creating || catalogMoreLoading}
                        onClick={() => void loadMoreJobCatalogs()}
                      >
                        {catalogMoreLoading ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                        <span>
                          <strong>{catalogMoreLoading ? 'Loading…' : 'Show more'}</strong>
                          <small>Keep this search and load the next results</small>
                        </span>
                      </button>
                    )}
                  {catalogNextCursor && catalogs.length >= CONTENT_CENTRAL_CATALOG_RENDER_LIMIT && (
                    <small className="catalog-result-limit">
                      Search by name to find catalogs beyond these{' '}
                      {CONTENT_CENTRAL_CATALOG_RENDER_LIMIT.toLocaleString()} results.
                    </small>
                  )}
                </section>
                <section
                  ref={catalogSchemaRef}
                  className="catalog-schema"
                  aria-label="Selected catalog setup"
                >
                  {catalogId && (
                    <button
                      type="button"
                      className="catalog-review-back"
                      disabled={creating}
                      onClick={() => {
                        setCompactCatalogBrowserOpen(true);
                        window.requestAnimationFrame(() =>
                          dialogRef.current
                            ?.querySelector<HTMLButtonElement>('.catalog-card[aria-pressed="true"]')
                            ?.focus(),
                        );
                      }}
                    >
                      <ChevronLeft size={13} /> Back to catalogs
                    </button>
                  )}
                  {loadingRemote && catalogId && !catalogDefinition ? (
                    <div className="catalog-schema-state" role="status">
                      <LoaderCircle className="spin" size={17} />
                      <strong>Reading catalog setup</strong>
                    </div>
                  ) : catalogReadError && catalogId ? (
                    <div
                      className="catalog-schema-state catalog-schema-error"
                      role="alert"
                      data-telemetry="error:catalog-load-failed"
                    >
                      <TriangleAlert size={23} />
                      <strong>Couldn’t read this catalog</strong>
                      <small>{catalogReadError}</small>
                      <button
                        type="button"
                        className="secondary-button small"
                        disabled={creating}
                        onClick={() => setCatalogRetry((value) => value + 1)}
                      >
                        Try again
                      </button>
                    </div>
                  ) : !catalogDefinition ? (
                    <div className="catalog-schema-state">
                      <FolderTree size={27} />
                      <strong>Choose a catalog</strong>
                      <small>
                        You’ll review its document types and fields before creating the job.
                      </small>
                    </div>
                  ) : (
                    <>
                      <div className="catalog-schema-heading">
                        <span>
                          <CheckCircle2 size={17} />
                        </span>
                        <div>
                          <strong>{catalogDefinition.catalog.name}</strong>
                          <small>
                            {catalogDefinition.documentTypes.length} document type
                            {catalogDefinition.documentTypes.length === 1 ? '' : 's'} will become
                            the starting setup
                          </small>
                        </div>
                      </div>
                      {boundJob && (
                        <div
                          className={`catalog-bound-job${secondaryCatalogAcknowledged ? ' secondary-approved' : ''}`}
                        >
                          <Link2 size={17} />
                          <span>
                            <strong>
                              {secondaryCatalogAcknowledged
                                ? 'Creating a separate local job'
                                : `Already connected to ${boundJob.jobName}`}
                            </strong>
                            <small>
                              {secondaryCatalogAcknowledged
                                ? `Both jobs will use ${catalogDefinition.catalog.name}, but confirmations and learning stay independent.`
                                : 'Open the existing job, or deliberately create another one that learns independently.'}
                            </small>
                          </span>
                          <div className="catalog-bound-job-actions">
                            <button
                              type="button"
                              className="secondary-button small"
                              disabled={creating}
                              onClick={() => onOpenExisting(boundJob.jobId)}
                            >
                              Open job
                            </button>
                            {secondaryCatalogAcknowledged ? (
                              <button
                                type="button"
                                className="quiet-button small"
                                disabled={creating}
                                onClick={() => setAcknowledgedBoundJobId(null)}
                              >
                                Cancel separate job
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="secondary-button small"
                                disabled={creating}
                                onClick={chooseAnotherLocalJob}
                              >
                                Create another
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="catalog-schema-tools">
                        <label>
                          <Search size={14} />
                          <input
                            value={catalogSchemaQuery}
                            onChange={(event) => {
                              setCatalogSchemaQuery(event.target.value);
                              setCatalogSchemaTypeLimit(CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE);
                            }}
                            placeholder="Find a document type or field"
                            aria-label="Find document type or field"
                          />
                        </label>
                        <small role="status">
                          {normalizedCatalogSchemaQuery
                            ? counted(matchingCatalogDocumentTypes.length, 'matching type')
                            : `${Math.min(catalogSchemaTypeLimit, matchingCatalogDocumentTypes.length).toLocaleString()} of ${matchingCatalogDocumentTypes.length.toLocaleString()} shown`}
                        </small>
                      </div>
                      <div className="catalog-schema-tree">
                        {visibleCatalogDocumentTypes.map((type, index) => (
                          <CatalogSchemaTypePreview
                            key={`${type.id}\u0000${normalizedCatalogSchemaQuery}`}
                            type={type}
                            query={normalizedCatalogSchemaQuery}
                            first={index === 0 && normalizedCatalogSchemaQuery.length === 0}
                          />
                        ))}
                        {matchingCatalogDocumentTypes.length === 0 && (
                          <div className="catalog-schema-no-match">
                            <Search size={19} />
                            <strong>No setup items match</strong>
                            <small>Try a document type or field name.</small>
                          </div>
                        )}
                        {matchingCatalogDocumentTypes.length >
                          visibleCatalogDocumentTypes.length && (
                          <button
                            type="button"
                            className="catalog-schema-more"
                            aria-label={`Show ${Math.min(CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE, matchingCatalogDocumentTypes.length - visibleCatalogDocumentTypes.length)} more document types`}
                            onClick={() =>
                              setCatalogSchemaTypeLimit(
                                (current) => current + CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE,
                              )
                            }
                          >
                            <ChevronDown size={14} />
                            <span>
                              <strong>
                                Show{' '}
                                {Math.min(
                                  CONTENT_CENTRAL_SCHEMA_TYPE_PAGE_SIZE,
                                  matchingCatalogDocumentTypes.length -
                                    visibleCatalogDocumentTypes.length,
                                )}{' '}
                                more document types
                              </strong>
                              <small>
                                {visibleCatalogDocumentTypes.length.toLocaleString()} of{' '}
                                {matchingCatalogDocumentTypes.length.toLocaleString()} shown
                              </small>
                            </span>
                          </button>
                        )}
                      </div>
                      {catalogProblems.length > 0 && (
                        <div className="catalog-problems">
                          <strong>
                            <TriangleAlert size={14} /> Needs attention
                          </strong>
                          {catalogProblems.slice(0, 6).map((problem) => (
                            <p key={problem}>{problem}</p>
                          ))}
                          {catalogProblems.length > 6 && (
                            <small>{catalogProblems.length - 6} more compatibility issues</small>
                          )}
                        </div>
                      )}
                      <p className="catalog-filing-authority">
                        <ShieldCheck size={13} /> Content Central’s filing rules stay in control.
                      </p>
                    </>
                  )}
                </section>
              </div>
            </div>
            <NewJobLearningIntroduction intent={intent} compact />
            <div className="catalog-job-footer">
              <label className="job-start-name">
                <span>Job name</span>
                <input
                  className={duplicateName ? 'invalid' : ''}
                  disabled={creating}
                  maxLength={160}
                  value={name}
                  onChange={(event) => {
                    nameTouchedRef.current = true;
                    setName(event.target.value);
                  }}
                  onKeyDown={(event) => event.key === 'Enter' && !createDisabled && void create()}
                  placeholder="Accounts payable intake"
                  aria-invalid={duplicateName}
                  aria-label="Job name"
                />
                {duplicateName && (
                  <small
                    className="name-conflict"
                    data-telemetry="error:job-name-taken"
                    role="status"
                  >
                    <TriangleAlert size={12} /> A job with this name already exists
                  </small>
                )}
              </label>
              {error && (
                <div
                  className="modal-error"
                  role="alert"
                  data-telemetry={
                    /already exists/i.test(error)
                      ? 'error:job-name-taken'
                      : 'error:job-create-failed'
                  }
                >
                  <TriangleAlert size={14} /> {error}
                </div>
              )}
              <button
                className="primary-button catalog-job-create"
                disabled={createDisabled}
                onClick={() => void create()}
              >
                {creating ? <LoaderCircle className="spin" size={17} /> : <Link2 size={17} />}
                {creating ? 'Creating…' : catalogCreateAction}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="local-job-body">
              <div className="new-job-artwork-header">
                <img
                  className="new-job-artwork"
                  src={newJobLearningUrl}
                  alt="Paper documents move from a filing cabinet through an intelligent learning core into organized digital records."
                />
              </div>
              <header className="local-job-heading">
                <h2 id="create-title">Name your new job</h2>
              </header>
              <label className="job-start-name">
                <input
                  autoFocus
                  className={duplicateName ? 'invalid' : ''}
                  disabled={creating}
                  maxLength={160}
                  value={name}
                  onChange={(event) => {
                    nameTouchedRef.current = true;
                    setName(event.target.value);
                  }}
                  onKeyDown={(event) => event.key === 'Enter' && !createDisabled && void create()}
                  placeholder="Accounts payable intake"
                  aria-invalid={duplicateName}
                  aria-label="Job name"
                />
                {duplicateName && (
                  <small
                    className="name-conflict"
                    data-telemetry="error:job-name-taken"
                    role="status"
                  >
                    <TriangleAlert size={12} /> A job with this name already exists
                  </small>
                )}
              </label>
              {error && (
                <div
                  className="modal-error"
                  role="alert"
                  data-telemetry={
                    /already exists/i.test(error)
                      ? 'error:job-name-taken'
                      : 'error:job-create-failed'
                  }
                >
                  <TriangleAlert size={14} /> {error}
                </div>
              )}
            </div>
            <footer className="local-job-footer">
              <button
                type="button"
                className="secondary-button new-job-back"
                disabled={creating}
                onClick={backToStart}
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button
                className="primary-button one-path-start"
                disabled={createDisabled}
                onClick={() => void create()}
              >
                {creating ? <LoaderCircle className="spin" size={17} /> : methodIcon(intent, 17)}
                {creating ? 'Creating…' : 'Create job'}
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function catalogBootstrapProblems(definition: ContentCentralCatalogDefinition): string[] {
  const problems: string[] = [];
  if (!definition.catalog.canAddDocuments)
    problems.push(`${definition.catalog.name} is view only for this connection.`);
  if (definition.documentTypes.length === 0)
    problems.push(`${definition.catalog.name} has no document types to use as a scan-job setup.`);
  for (const type of definition.documentTypes) {
    if (!type.canAddDocuments)
      problems.push(`${type.name} does not allow this connection to add documents.`);
    const columns = new Set<string>();
    for (const field of type.fields.filter(
      (value) => value.extractable && value.valueOwner.toLowerCase() === 'capturepoint',
    )) {
      if (field.multiple)
        problems.push(
          `${type.name} · ${field.name} accepts multiple values, which this Paige release cannot safely export.`,
        );
      if (field.warnings.length > 0)
        problems.push(
          `${type.name} · ${field.name} needs manual handling in Content Central: ${field.warnings[0]}`,
        );
      if (!field.lineItemGroupId) continue;
      const normalized = field.name.replace(/[^a-z0-9]/gi, '').toLowerCase();
      const column = ['description', 'itemdescription', 'lineitemdescription', 'item'].includes(
        normalized,
      )
        ? 'description'
        : ['quantity', 'qty'].includes(normalized)
          ? 'quantity'
          : ['unitprice', 'price', 'rate'].includes(normalized)
            ? 'unit price'
            : ['amount', 'lineamount', 'extendedamount', 'total'].includes(normalized)
              ? 'amount'
              : null;
      if (!column)
        problems.push(
          `${type.name} · ${field.name} is a line-item column Paige cannot represent safely.`,
        );
      else if (columns.has(column))
        problems.push(`${type.name} has more than one line-item column that maps to ${column}.`);
      else columns.add(column);
    }
  }
  return [...new Set(problems)];
}

export default App;
